/* eslint-disable */
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { supabase } from "./supabaseClient";
import HifzWifaqAnalytics from "./HifzWifaqAnalytics";
import PublicSiteComponent from "./PublicSite";
import { db, getPendingCount, markSynced, logSync } from "./db";
import { LeaveRequestParent, LeaveRequestAdmin } from "./LeaveRequests";
import { StaffLeaveAdmin, StaffLeaveRequest } from "./StaffLeave";
import { StaffProfile } from "./StaffProfile";
import { StaffAttendanceAdmin } from "./StaffAttendance";
import { CoverDutyAdmin } from "./CoverDuty";
import { CoursePlanTeacherTab, CoursePlanAdmin } from "./CoursePlanTracker";
import { StaffResponsibilityTab, StaffResponsibilityAdmin } from "./StaffResponsibilities";
import { LibraryTab, LibraryAdmin } from "./Library";
import { TeacherScorecardAdmin } from "./TeacherScorecard";
import { PrincipalDigest } from "./PrincipalDigest";
import { ClassroomObservationLog } from "./ClassroomObservation";
import { AssetRegisterAdmin } from "./AssetRegister";
import * as XLSX from "xlsx";
import { CAMPUS_AERIAL } from "./photos";
import { FacilityInspectionAdmin, FacilityManagerPortal } from "./FacilityInspection";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
  
// ── Portal helper — renders modal at document.body, escaping any stacking context ──
function Portal({ children }) {
  const el = React.useRef(document.createElement("div"));
  React.useEffect(() => {
    const container = el.current;
    document.body.appendChild(container);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.body.removeChild(container);
      document.body.style.overflow = "";
    };
  }, []);
  return ReactDOM.createPortal(children, el.current);
}

// ── Design Tokens ─────────────────────────────────────────────────
const C = {
  primary:"#1a6b3c", primaryLight:"#2d9e5f", primaryPale:"#e8f5ee", primaryMid:"#c3e6d0",
  accent:"#f0a500", accentLight:"#ffd166", accentPale:"#fff8e7",
  teal:"#0d9488", tealPale:"#e0f7f5",
  purple:"#7c3aed", purplePale:"#f0ebff",
  rose:"#e11d48", rosePale:"#fff0f3",
  blue:"#2563eb", bluePale:"#eff6ff",
  text:"#0f1f0f", muted:"#4a6a52", border:"#d1e8d8",
  white:"#ffffff", bg:"#f0f7f2", card:"#ffffff",
  shadow:"rgba(26,107,60,0.10)",
};

const GROQ_KEY_PLACEHOLDER = "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";

// ── WhatsApp helpers ──────────────────────────────────────────────
// Formats Pakistani numbers (03xx → 923xx) and opens wa.me link
function openWhatsApp(phone, message) {
  const link = waLink(phone, message);
  if (link) window.open(link, "_blank");
}
function waLink(phone, message) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  const intl = digits.startsWith("0") ? "92" + digits.slice(1)
             : digits.startsWith("92") ? digits : "92" + digits;
  if (intl.length < 10) return null;
  return `https://wa.me/${intl}?text=${encodeURIComponent(message)}`;
}

// ── Animations ────────────────────────────────────────────────────
const style = document.createElement("style");
style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Noto+Nastaliq+Urdu&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Plus Jakarta Sans', sans-serif; background: ${C.bg}; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:.5; } }
  @keyframes countUp { from { opacity:0; transform:scale(.8); } to { opacity:1; transform:scale(1); } }
  @keyframes shimmer { 0% { background-position:-200% 0; } 100% { background-position:200% 0; } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:translateX(0); } }
  @keyframes popIn { from { opacity:0; transform:scale(.9); } to { opacity:1; transform:scale(1); } }
  @keyframes spin { to { transform:rotate(360deg); } }
  .fade-up { animation: fadeUp .4s ease both; }
  .fade-in { animation: fadeIn .3s ease both; }
  .slide-in { animation: slideIn .3s ease both; }
  .pop-in { animation: popIn .25s cubic-bezier(.34,1.56,.64,1) both; }
  .card-hover { transition: transform .2s, box-shadow .2s; }
  .card-hover:hover { transform: translateY(-3px); box-shadow: 0 12px 32px ${C.shadow}; }
  .btn-press:active { transform: scale(.97); }
  .tab-btn { transition: all .2s; position:relative; }
  .tab-btn::after { content:''; position:absolute; bottom:-2px; left:0; right:0; height:2px; background:${C.primary}; transform:scaleX(0); transition:transform .2s; border-radius:2px; }
  .tab-btn.active::after { transform:scaleX(1); }
  input:focus, select:focus { outline:none; border-color:${C.primary} !important; box-shadow:0 0 0 3px ${C.primaryPale}; }
  .skeleton { background: linear-gradient(90deg, #e8f5ee 25%, #d1e8d8 50%, #e8f5ee 75%); background-size:200% 100%; animation:shimmer 1.5s infinite; border-radius:8px; }
  ::-webkit-scrollbar { width:6px; height:6px; } ::-webkit-scrollbar-track { background:#d1d5db; border-radius:3px; } ::-webkit-scrollbar-thumb { background:#374151; border-radius:3px; } ::-webkit-scrollbar-thumb:hover { background:#111827; }
  .qka-portal-tabs::-webkit-scrollbar { height:5px; } .qka-portal-tabs::-webkit-scrollbar-track { background:#e5e7eb; border-radius:3px; } .qka-portal-tabs::-webkit-scrollbar-thumb { background:#1f2937; border-radius:3px; }
  .toast { animation: popIn .3s cubic-bezier(.34,1.56,.64,1) both; }
  @keyframes confetti { 0% { transform:translateY(-20px) rotate(0deg); opacity:1; } 100% { transform:translateY(60px) rotate(720deg); opacity:0; } }
  @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
  .tab-content { animation: fadeUp .3s ease both; }
  .mob-menu-open { animation: slideDown .2s ease both; }
  @media(max-width:768px){
    .qka-content-pad { padding: 16px !important; }
    .qka-modal-overlay { align-items: flex-start !important; padding-top: 12px !important; }
    .qka-modal-box { max-height: 92vh !important; overflow-y: auto !important; border-radius: 16px !important; margin: 0 8px; }
  }
  .qka-modal-overlay { 
    position: fixed; inset: 0; z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    padding: 16px; overflow-y: auto;
    background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
  }
  .qka-modal-box {
    max-height: 90vh; overflow-y: auto;
    border-radius: 20px; width: 100%;
    position: relative;
  }
`;
document.head.appendChild(style);

// ── Academy Header ─────────────────────────────────────────────────
const SCHOOL_LOGO = "data:image/png;base64,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";




// ── School Settings Context ──────────────────────────────────────
const SettingsContext = React.createContext({});
const useSettings = () => React.useContext(SettingsContext);

const DEFAULT_SETTINGS = {
  // Identity
  school_name_en:     "QUR'AN-E-KARIM ACADEMY",
  school_name_en_sub: "Modern Islamic School System",
  school_name_ur:     "قرآن کریم اکیڈمی",
  school_name_ur_sub: "ماڈرن اسلامک سکول سسٹم",
  school_name_ur_reg: "رجسٹرڈ وفاق المدارس العربيہ پاکستان",
  school_name_ur_addr:"گورسیاں - جاتلی - ضلع راولپنڈی",
  affiliation:        "Registered with Wifaq-ul Madaris Al-Arabia Pakistan",
  address:            "Gorsian, Jatli, Dist. Rawalpindi",
  reg_no:             "CMR-199/2015",
  academic_year:      "2025–2026",
  // Layout & appearance
  header_logo_size:   "92",        // px
  header_en_font:     "16",        // px
  header_ur_font:     "24",        // px
  header_align_en:    "flex-start",// flex-start | center | flex-end
  header_align_ur:    "flex-end",  // flex-start | center | flex-end
  header_en_valign:   "center",    // center | flex-start | flex-end
  header_ur_valign:   "center",
  header_gradient:    "#1a6b3c,#0a4a24",
  header_accent:      "#f0a500",
  // Dashboard
  dashboard_title:    "School Management Portal",
  show_ai_tab:        "true",
  show_timetable:     "true",
  show_scholarship:   "true",
  // Fees
  default_fee:        "1500",
  fee_school_only:    "1500",
  fee_hifz_school:    "1800",
  fee_hifz_only:      "1200",
  att_threshold:      "85",
  // Academic
  classes: "Nursery,KG,Class 1,Class 2,Class 3,Class 4,Class 5,Class 6,Class 7,Class 8,Class 9,Hifz Only",
  // Session / Term
  term_name:   "Term 1",
  term_start:  "",
  term_end:    "",
  off_days:    "Saturday,Sunday",
  // Bell schedule (JSON array)
  bell_schedule: "",
  hifz_schedule_winter: "",
  hifz_schedule_summer: "",
};

const AcademyHeader = ({ subtitle="" }) => {
  const S = useSettings();
  return (()=>{
    const logoSize  = Number(S.header_logo_size||80);
    const enFont    = Number(S.header_en_font||15);
    const urFont    = Number(S.header_ur_font||22);
    const enVAlign  = S.header_en_valign||"center";
    const urVAlign  = S.header_ur_valign||"center";
    const gradParts = (S.header_gradient||"#1a6b3c,#0a4a24").split(",");
    const grad      = `linear-gradient(135deg,${gradParts[0]||"#1a6b3c"} 0%,${gradParts[1]||"#0a4a24"} 100%)`;
    const accent    = S.header_accent||"#f0a500";
    return (
  <div style={{background:grad,borderBottom:`4px solid ${accent}`}}>
    {/* Main header row */}
    <div style={{display:"flex",alignItems:"center",padding:"14px 24px",gap:16,color:"white"}}>
      {/* Logo */}
      <img src={SCHOOL_LOGO} alt="QK Academy Logo" style={{width:logoSize,height:logoSize,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 2px 8px rgba(0,0,0,0.3))"}}/>
      {/* English text - left */}
      <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:enVAlign,gap:2}}>
        <div style={{fontSize:enFont,fontWeight:900,letterSpacing:.5,color:"white"}}>{S.school_name_en||DEFAULT_SETTINGS.school_name_en}</div>
        <div style={{fontSize:Math.max(enFont-4,9),fontWeight:600,opacity:.9,color:"white"}}>{S.school_name_en_sub||DEFAULT_SETTINGS.school_name_en_sub}</div>
        <div style={{fontSize:Math.max(enFont-5,9),opacity:.75,color:"white"}}>{S.affiliation||DEFAULT_SETTINGS.affiliation}</div>
        <div style={{fontSize:Math.max(enFont-5,9),opacity:.75,color:"white"}}>{S.address||DEFAULT_SETTINGS.address} &nbsp;·&nbsp; Reg. No: {S.reg_no||DEFAULT_SETTINGS.reg_no}</div>
      </div>
      {/* Urdu text - right */}
      <div style={{textAlign:"right",fontFamily:"'Noto Nastaliq Urdu',serif",display:"flex",flexDirection:"column",justifyContent:urVAlign,gap:2}}>
        <div style={{fontSize:urFont,fontWeight:900,color:"white",lineHeight:1.3}}>{S.school_name_ur||DEFAULT_SETTINGS.school_name_ur}</div>
        <div style={{fontSize:Math.max(urFont-9,10),fontWeight:700,color:"#ffd166"}}>{S.school_name_ur_sub||DEFAULT_SETTINGS.school_name_ur_sub}</div>
        <div style={{fontSize:Math.max(urFont-11,9),opacity:.8,color:"white"}}>{S.school_name_ur_reg||DEFAULT_SETTINGS.school_name_ur_reg}</div>
        <div style={{fontSize:Math.max(urFont-11,9),opacity:.8,color:"white"}}>{S.school_name_ur_addr||DEFAULT_SETTINGS.school_name_ur_addr}</div>
      </div>
    </div>
    {/* Accent separator */}
    <div style={{height:3,background:"linear-gradient(90deg,#2563eb,#0d9488,#2563eb)",opacity:.8}}/>
    {/* Subtitle bar */}
    {subtitle && (
      <div style={{background:"rgba(0,0,0,0.15)",padding:"8px 24px",textAlign:"center",fontSize:14,fontWeight:800,color:"#ffd166",letterSpacing:.5}}>
        {subtitle}
      </div>
    )}
  </div>
    );
  })();
};

// ── UI Primitives ──────────────────────────────────────────────────
const Field = ({ label, urdu, value, onChange, type="text", required, half, select, options=[] }) => (
  <div style={{display:"flex",flexDirection:"column",gap:5,flex:half?"0 0 calc(50% - 8px)":"1 1 100%"}}>
    <label style={{fontSize:12,fontWeight:700,color:C.primary,display:"flex",justifyContent:"space-between"}}>
      <span>{label}{required && <span style={{color:C.rose}}> *</span>}</span>
      {urdu && <span style={{fontFamily:"'Noto Nastaliq Urdu',serif",color:C.muted,fontWeight:400,fontSize:11}}>{urdu}</span>}
    </label>
    {select ? (
      <select value={value} onChange={e=>onChange(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white,color:C.text,cursor:"pointer"}}>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,color:C.text,background:C.white,transition:"all .2s"}}/>
    )}
  </div>
);

const SectionTitle = ({ en, ur, color=C.primary, icon }) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`2px solid ${color}20`,paddingBottom:8,marginBottom:16,marginTop:12}}>
    <span style={{fontSize:14,fontWeight:800,color,display:"flex",alignItems:"center",gap:6}}>{icon && <span>{icon}</span>}{en}</span>
    {ur && <span style={{fontSize:12,fontFamily:"'Noto Nastaliq Urdu',serif",color:C.muted}}>{ur}</span>}
  </div>
);

const Badge = ({ label, color, bg, border }) => (
  <span style={{background:bg,color,border:`1px solid ${border}`,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{label}</span>
);

const Toast = ({ msg, type }) => {
  const bg = type==="error"?"#dc2626":type==="warn"?"#d97706":C.primary;
  const icon = type==="error"?"✕":type==="warn"?"!":"✓";
  return (
    <div className="toast" style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:9999,background:bg,color:"white",padding:"10px 20px",borderRadius:12,fontWeight:700,fontSize:13,boxShadow:"0 8px 24px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",gap:8,maxWidth:420,width:"fit-content",minWidth:180,whiteSpace:"nowrap"}}>
      <span style={{width:18,height:18,borderRadius:"50%",background:"rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,flexShrink:0}}>{icon}</span>
      {msg}
    </div>
  );
};

// ── Confetti ───────────────────────────────────────────────────────
function fireConfetti() {
  const colors = ["#1a6b3c","#f0a500","#2d9e5f","#ffd166","#0d9488"];
  for (let i=0; i<18; i++) {
    const el = document.createElement("div");
    el.style.cssText = `position:fixed;top:${Math.random()*30}%;left:${Math.random()*100}%;width:8px;height:8px;background:${colors[Math.floor(Math.random()*colors.length)]};border-radius:${Math.random()>0.5?"50%":"2px"};animation:confetti ${.6+Math.random()*.8}s ease forwards;z-index:9999;pointer-events:none;`;
    document.body.appendChild(el);
    setTimeout(()=>el.remove(), 1400);
  }
}

// ── Classes ────────────────────────────────────────────────────────
const ALL_CLASSES = ["Hifz Only","Nursery","KG","Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8","Class 9"];

// ── OFFLINE-FIRST sheetAPI ────────────────────────────────────────
async function sheetAPI(payload) {
  const online = navigator.onLine;
  try {
    if (payload.action === "getStudents") {
      if (online) {
        const { data, error } = await supabase.from('students').select('*').order('created_at',{ascending:false});
        if (error) throw error;
        for (const s of data) {
          const existing = await db.students.where('id').equals(s.id).first();
          if (!existing) await db.students.add({...s, synced:1});
        }
        return { status:"ok", students:data };
      } else {
        const local = await db.students.toArray();
        const students = local.map(s => ({...s, full_name: s.full_name || s.name, _offline: s.synced === 0}));
        return { status:"ok", students };
      }
    }
    if (payload.action === "getAttendance") {
  if (online) {
    const { data, error } = await supabase.from("attendance").select("*");
    if (error) throw error;

    for (const r of data) {
      const normalized = {
        ...r,
        date: r.date,
        student_id: String(r.student_id || r.studentid || r.studentId || ""),
        student_name: r.student_name || r.studentname || r.studentName || "",
        status: r.status || r.statuscode || r.statuslabel || "",
        synced: 1,
      };

      if (!normalized.date || !normalized.student_id) continue;

      await db.attendance.put(normalized).catch((e) => {
        console.warn("Dexie put attendance failed", normalized, e);
      });
    }

    const records = data.map((r) => ({
      date: r.date,
      studentId: String(r.student_id || r.studentid || r.studentId || ""),
      status: r.status || r.statuscode || r.statuslabel || "",
    }));

    return { status: "ok", records };
  } else {
    const local = await db.attendance.toArray();
    const records = local.map((r) => ({
      date: r.date,
      studentId: String(r.student_id || r.studentid || r.studentId || ""),
      status: r.status || r.statuscode || r.statuslabel || "",
    }));
    return { status: "ok", records };
  }
}
    if (payload.action === "getFees") {
      if (online) {
        const { data, error } = await supabase.from('fee_payments').select('*').order('created_at',{ascending:false});
        if (error) throw error;
        return { status:"ok", fees:data||[] };
      } else {
        const local = await db.fee_payments.toArray();
        return { status:"ok", fees:local };
      }
    }
    if (payload.action === "addStudent") {
      const fullRow = {
        id:payload.id,
        full_name:payload.full_name||payload.name,
        father_name:payload.father_name||payload.father,
        stream:payload.stream, "class":payload.class,
        dob:payload.dob||null, gender:payload.gender||"Male",
        contact:payload.contact, phone:payload.phone||payload.contact,
        address:payload.address, village:payload.village||"",
        cnic:payload.cnic, occupation:payload.occupation,
        enrolled_date:payload.enrolled_date||payload.enrolled,
        fees_status:payload.fees_status||payload.fees||"Unpaid",
        monthly_fee:Number(payload.monthly_fee)||1500,
        is_boarding:!!payload.is_boarding,
        scholarship_status:payload.scholarship_status||"None",
      };
      const row = {...fullRow, _action:"addStudent", _payload:JSON.stringify(payload), synced:0};
      const lid = await db.students.add(row);
      if (online) {
        const { error } = await supabase.from('students').insert(fullRow);
        if (!error) { await markSynced('students', lid); await logSync('students','addStudent','synced'); }
        else { await logSync('students','addStudent','failed: '+error.message); }
      } else { await logSync('students','addStudent','offline-queued'); }
      return { status:"ok", success:true, offline:!online };
    }
   if (payload.action === "saveAttendance") {
  const records = JSON.parse(payload.records);

  const rows = records.map((r) => ({
    date: r.date,
    student_id: String(r.studentId || r.studentid || r.student_id || ""),
    student_name: r.studentName || "",
    class: r.class,
    status: r.status,
    month: new Date(r.date).toLocaleString("default", { month: "long" }),
    year: String(new Date(r.date).getFullYear()),
    action: "saveAttendance",
    synced: 0,
  }));

  const lids = [];

  for (const row of rows) {
    if (!row.date || !row.student_id) continue;
    const lid = await db.attendance.put(row);
    lids.push(lid);
  }

  if (online) {
    const supaRows = rows.map(({ action, synced, ...r }) => r);
    const { error } = await supabase
      .from("attendance")
      .upsert(supaRows, { onConflict: "date,student_id" });

    if (!error) {
      for (const lid of lids) await markSynced("attendance", lid);
      await logSync("attendance", "saveAttendance", "synced");
    } else {
      await logSync("attendance", "saveAttendance", "failed", error.message);
    }
  } else {
    await logSync("attendance", "saveAttendance", "offline-queued");
  }

  return { status: "ok", success: true, offline: !online };
    }
    if (payload.action === "saveFee") {
      const row = {
        student_id:payload.studentId, student_name:payload.studentName, "class":payload.class,
        amount:payload.amount, month:payload.month, year:payload.year,
        method:payload.method, notes:payload.notes, paid_date:payload.paidDate,
        receipt_no:payload.receiptNo, _action:"saveFee", _payload:JSON.stringify(payload), synced:0
      };
      const lid = await db.fee_payments.add(row);
      if (online) {
        const {_action,_payload,synced,...supaRow} = row;
        const { error } = await supabase.from('fee_payments').insert(supaRow);
        if (!error) {
          await supabase.from('students').update({fees_status:'Paid'}).eq('id',payload.studentId);
          await markSynced('fee_payments', lid); await logSync('fee_payments','saveFee','synced');
        } else { await logSync('fee_payments','saveFee','failed: '+error.message); }
      } else { await logSync('fee_payments','saveFee','offline-queued'); }
      return { status:"ok", success:true, offline:!online };
    }
    if (payload.action === "updateFeeStatus") {
      if (online) {
        const { error } = await supabase.from('students').update({fees_status:payload.status}).eq('id',payload.studentId);
        if (error) throw error;
      }
      const local = await db.students.where('id').equals(payload.studentId).first();
      if (local) await db.students.update(local._lid||local.id, {fees_status:payload.status});
      return { status:"ok", success:true, offline:!online };
    }
    if (payload.action === "addScholarship") {
      const row = {
        student_name:payload.studentName, father_name:payload.fatherName, "class":payload.class,
        dob:payload.dob, contact:payload.contact, guardian_name:payload.guardianName,
        occupation:payload.occupation, cnic:payload.cnic, monthly_income:payload.monthlyIncome,
        assets:payload.assets, housing:payload.housing, rent:payload.rent,
        family_members:payload.familyMembers, studying_children:payload.studyingChildren,
        attendance_pct:payload.attendance, test_avg:payload.testAvg,
        conduct:payload.conduct, last_exam_result:payload.lastExamResult,
        _action:"addScholarship", _payload:JSON.stringify(payload), synced:0
      };
      const lid = await db.scholarships.add(row);
      if (online) {
        const {_action,_payload,synced,...supaRow} = row;
        const { error } = await supabase.from('scholarships').insert(supaRow);
        if (!error) { await markSynced('scholarships', lid); await logSync('scholarships','addScholarship','synced'); }
      } else { await logSync('scholarships','addScholarship','offline-queued'); }
      return { status:"ok", success:true, offline:!online };
    }
    return { status:"ok" };
  } catch (err) {
    console.error("sheetAPI error:", err);
    return { status:"error", students:[], records:[], message:err.message };
  }
}

async function syncPending(onProgress) {
  let synced = 0;
  const pendingStudents = await db.students.where('synced').equals(0).toArray();
  for (const s of pendingStudents) {
    try {
      const { error } = await supabase.from('students').insert({
        id:s.id, full_name:s.full_name, father_name:s.father_name,
        stream:s.stream, "class":s["class"], dob:s.dob,
        contact:s.contact, address:s.address, cnic:s.cnic,
        occupation:s.occupation, enrolled_date:s.enrolled_date, fees_status:s.fees_status
      });
      if (!error) { await markSynced('students', s._lid); synced++; }
    } catch(e) { console.warn('Sync student failed', e); }
  }
  const pendingAtt = await db.attendance.where('synced').equals(0).toArray();
  if (pendingAtt.length) {
    try {
      const rows = pendingAtt.map(({_lid,_action,action,synced,...r})=>r);
      const { error } = await supabase.from('attendance').upsert(rows,{onConflict:'date,student_id'});
      if (!error) { for (const r of pendingAtt) { await markSynced('attendance', r._lid); synced++; } }
    } catch(e) { console.warn('Sync attendance failed', e); }
  }
  const pendingFees = await db.fee_payments.where('synced').equals(0).toArray();
  for (const f of pendingFees) {
    try {
      const {_lid,_action,_payload,synced:_s1,...row} = f;
      const { error } = await supabase.from('fee_payments').insert(row);
      if (!error) {
        await supabase.from('students').update({fees_status:'Paid'}).eq('id',f.student_id);
        await markSynced('fee_payments', f._lid); synced++;
      }
    } catch(e) { console.warn('Sync fee failed', e); }
  }
  const pendingSch = await db.scholarships.where('synced').equals(0).toArray();
  for (const s of pendingSch) {
    try {
      const {_lid,_action,_payload,synced:_s2,...row} = s;
      const { error } = await supabase.from('scholarships').insert(row);
      if (!error) { await markSynced('scholarships', s._lid); synced++; }
    } catch(e) { console.warn('Sync scholarship failed', e); }
  }
  onProgress && onProgress(synced);
  return synced;
}

// ── No demo data ───────────────────────────────────────────────────
const INIT_STUDENTS = [];
const INIT_ATTENDANCE = {};

const today = new Date();
const DATES = Array.from({length:21},(_,i)=>{
  const d = new Date(today); d.setDate(d.getDate()-(20-i));
  const dd = d.toISOString().slice(0,10);
  return {date:dd,isWeekend:[0,6].includes(d.getDay())};
}).filter(d=>!d.isWeekend).map(d=>d.date).slice(-14);


// ── ADMISSIONS RECEIVED ───────────────────────────────────────────
function AdmissionsReceived({ setTab }) {
  const [apps, setApps] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [filter, setFilter] = React.useState("all");
  const [search, setSearch] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [noteInput, setNoteInput] = React.useState("");

  const G = "#1a6b3c"; const GD = "#0a4a24";
  const grad = `linear-gradient(135deg,${G},${GD})`;

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3500); };

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admission_applications")
      .select("*")
      .order("applied_at", { ascending: false });
    if (!error) setApps(data || []);
    setLoading(false);
  };

  React.useEffect(()=>{ load(); }, []);

  const updateStatus = async (id, status) => {
    setSaving(true);
    const { error } = await supabase
      .from("admission_applications")
      .update({ status, reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) { showToast("Error: " + error.message, false); }
    else {
      setApps(prev => prev.map(a => a.id===id ? {...a, status, reviewed_at: new Date().toISOString()} : a));
      if (selected?.id === id) setSelected(prev => ({...prev, status}));
      const msgs = {
        test_scheduled: "✅ Marked as Test Scheduled — student will be contacted.",
        accepted: "🎉 Application Accepted!",
        rejected: "Application rejected.",
        pending: "Reset to Pending."
      };
      showToast(msgs[status] || "Updated.");
      // WhatsApp parent notification for key status changes
      const app = apps.find(a=>a.id===id);
      const phone = app?.father_phone||app?.contact||app?.phone;
      if(phone && (status==="accepted"||status==="rejected"||status==="test_scheduled")){
        const studentName = app?.student_name||"your child";
        const waMessages = {
          accepted: `Assalamu Alaikum! We are delighted to inform you that the admission application for ${studentName} at QK Academy has been ACCEPTED. Please visit the academy to complete the enrollment process. JazakAllah Khair!`,
          rejected: `Assalamu Alaikum! Thank you for applying to QK Academy. After careful review, we regret that we are unable to offer admission for ${studentName} at this time. Please contact us for more information. JazakAllah Khair.`,
          test_scheduled: `Assalamu Alaikum! The admission application for ${studentName} is progressing. An entry test has been scheduled — please contact QK Academy to confirm the date and time. JazakAllah Khair!`
        };
        const link = waLink(phone, waMessages[status]);
        if(link) window.open(link,"_blank");
      }
    }
    setSaving(false);
  };

  const saveNote = async () => {
    if (!selected) return;
    setSaving(true);
    const { error } = await supabase
      .from("admission_applications")
      .update({ internal_notes: noteInput })
      .eq("id", selected.id);
    if (!error) {
      setApps(prev => prev.map(a => a.id===selected.id ? {...a, internal_notes: noteInput} : a));
      setSelected(prev => ({...prev, internal_notes: noteInput}));
      showToast("Note saved.");
    }
    setSaving(false);
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Permanently delete this application?")) return;
    setSaving(true);
    const { error } = await supabase.from("admission_applications").delete().eq("id", id);
    if (!error) {
      setApps(prev => prev.filter(a => a.id !== id));
      if (selected?.id === id) setSelected(null);
      showToast("Application deleted.");
    }
    setSaving(false);
  };

  const clearDone = async () => {
    const done = apps.filter(a => a.status === "rejected" || a.status === "accepted");
    if (!done.length) { showToast("No rejected/accepted applications to clear.", true); return; }
    if (!window.confirm(`Clear ${done.length} rejected/accepted application(s)? This cannot be undone.`)) return;
    setSaving(true);
    const ids = done.map(a => a.id);
    const { error } = await supabase.from("admission_applications").delete().in("id", ids);
    if (!error) {
      setApps(prev => prev.filter(a => !ids.includes(a.id)));
      if (selected && ids.includes(selected.id)) setSelected(null);
      showToast(`🗑 ${done.length} application(s) cleared.`);
    } else { showToast("Error: " + error.message, false); }
    setSaving(false);
  };

  const STATUS_CONFIG = {
    pending:        { label:"Pending Review", color:"#d97706", bg:"#fffbeb", border:"#fde68a", dot:"🟡" },
    test_scheduled: { label:"Test Scheduled", color:"#0891b2", bg:"#f0f9ff", border:"#bae6fd", dot:"🔵" },
    accepted:       { label:"Accepted",        color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0", dot:"🟢" },
    rejected:       { label:"Rejected",        color:"#dc2626", bg:"#fff0f0", border:"#fca5a5", dot:"🔴" },
  };

  const counts = { all: apps.length };
  Object.keys(STATUS_CONFIG).forEach(s => { counts[s] = apps.filter(a=>a.status===s).length; });

  const filtered = apps.filter(a => {
    if (filter !== "all" && a.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (a.student_name||"").toLowerCase().includes(q) ||
             (a.father_name||"").toLowerCase().includes(q) ||
             (a.father_phone||"").toLowerCase().includes(q) ||
             (a.program_applied||"").toLowerCase().includes(q);
    }
    return true;
  });

  const fmt = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-PK", {day:"numeric",month:"short",year:"numeric"}); }
    catch { return d; }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {/* Toast */}
      {toast && (
        <div style={{position:"fixed",top:20,right:20,zIndex:9000,background:toast.ok?"#f0fdf4":"#fff0f0",
          color:toast.ok?"#166534":"#dc2626",padding:"12px 20px",borderRadius:12,fontWeight:700,fontSize:14,
          boxShadow:"0 8px 32px rgba(0,0,0,0.15)",border:`1px solid ${toast.ok?"#bbf7d0":"#fca5a5"}`}}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{background:"white",borderRadius:20,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.06)"}}>
        <div style={{background:grad,padding:"22px 28px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)",letterSpacing:2,textTransform:"uppercase",marginBottom:5}}>Online Applications</div>
            <div style={{fontSize:24,fontWeight:900,color:"white"}}>Admissions Received</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={load} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.3)",
              borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
              🔄 Refresh
            </button>
            <button onClick={clearDone} disabled={saving} style={{background:"rgba(220,38,38,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.3)",
              borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
              🗑 Clear Done
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",borderBottom:"1px solid #f0f0f0"}}>
          {[
            {k:"all",   label:"All",             color:"#374151", bg:"#f9fafb"},
            {k:"pending",        label:"Pending",   color:"#d97706", bg:"#fffbeb"},
            {k:"test_scheduled", label:"Test Sched",color:"#0891b2", bg:"#f0f9ff"},
            {k:"accepted",       label:"Accepted",  color:"#16a34a", bg:"#f0fdf4"},
            {k:"rejected",       label:"Rejected",  color:"#dc2626", bg:"#fff0f0"},
          ].map(s=>(
            <button key={s.k} onClick={()=>setFilter(s.k)}
              style={{padding:"16px 12px",border:"none",borderBottom: filter===s.k?`3px solid ${s.color}`:"3px solid transparent",
                background: filter===s.k ? s.bg : "white",cursor:"pointer",textAlign:"center",transition:"all .15s"}}>
              <div style={{fontSize:26,fontWeight:900,color:s.color}}>{counts[s.k]||0}</div>
              <div style={{fontSize:11,fontWeight:600,color:s.color}}>{s.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns: selected ? "1fr 400px" : "1fr",gap:20,alignItems:"start"}}>
        {/* List */}
        <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
          {/* Search */}
          <div style={{padding:"14px 20px",borderBottom:"1px solid #f0f0f0",display:"flex",gap:12,alignItems:"center"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, phone, program..."
              style={{flex:1,border:"1.5px solid #e5e7eb",borderRadius:9,padding:"9px 13px",fontSize:14,outline:"none"}}/>
            {search && <button onClick={()=>setSearch("")} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"9px 14px",cursor:"pointer",fontWeight:700}}>✕</button>}
          </div>

          {loading ? (
            <div style={{padding:60,textAlign:"center",color:"#9ca3af",fontSize:14}}>Loading applications…</div>
          ) : filtered.length === 0 ? (
            <div style={{padding:60,textAlign:"center"}}>
              <div style={{fontSize:48,marginBottom:12}}>📭</div>
              <div style={{fontWeight:800,color:"#374151",fontSize:16,marginBottom:8}}>No applications {filter!=="all"?`with status "${filter}"`:"yet"}</div>
              <div style={{color:"#9ca3af",fontSize:13}}>Applications submitted via the public admissions form will appear here.</div>
              <div style={{marginTop:16,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,padding:"12px 16px",
                fontSize:12,color:"#166534",maxWidth:320,margin:"16px auto 0",textAlign:"left",lineHeight:1.6}}>
                💡 <strong>Workflow tip:</strong> Once you accept an application, go to the <em>Register tab</em> to officially enrol the student.
              </div>
            </div>
          ) : (
            <div>
              {filtered.map((a,i) => {
                const sc = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
                const isActive = selected?.id === a.id;
                return (
                  <div key={a.id} onClick={()=>{ setSelected(a); setNoteInput(a.internal_notes||""); }}
                    style={{padding:"16px 20px",borderBottom:"1px solid #f9fafb",cursor:"pointer",
                      background: isActive ? "#f0fdf4" : i%2===0?"white":"#fafafa",
                      borderLeft: isActive ? `4px solid ${G}` : "4px solid transparent",
                      transition:"all .1s"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <div style={{width:34,height:34,borderRadius:"50%",background:grad,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            color:"white",fontWeight:900,fontSize:13,flexShrink:0}}>
                            {(a.student_name||"?")[0]}
                          </div>
                          <div>
                            <div style={{fontWeight:800,fontSize:14,color:"#1a1a1a"}}>{a.student_name||"—"}</div>
                            <div style={{fontSize:11,color:"#6b7280"}}>{a.father_name} · {a.father_phone}</div>
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginLeft:42}}>
                          <span style={{background:`${G}12`,color:G,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>
                            {a.program_applied}
                          </span>
                          <span style={{color:"#9ca3af",fontSize:11}}>Applied {fmt(a.applied_at)}</span>
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0}}>
                        <span style={{background:sc.bg,color:sc.color,border:`1px solid ${sc.border}`,
                          borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                          {sc.dot} {sc.label}
                        </span>
                        {a.father_phone&&(()=>{
                          const msg = a.status==="accepted"
                            ? `Assalamu Alaikum! We are pleased to inform you that ${a.student_name}'s admission to QK Academy has been accepted. Please visit to complete enrollment. JazakAllah Khair!`
                            : a.status==="test_scheduled"
                            ? `Assalamu Alaikum! An entry test has been scheduled for ${a.student_name}. Please contact QK Academy to confirm the date and time. JazakAllah Khair!`
                            : `Assalamu Alaikum! Regarding the admission application for ${a.student_name} at QK Academy — `;
                          const link = waLink(a.father_phone, msg);
                          return link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
                              style={{background:"#dcfce7",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:7,padding:"3px 10px",fontSize:11,fontWeight:700,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:4}}>
                              💬 WhatsApp
                            </a>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.08)",position:"sticky",top:80}}>
            {/* Panel header */}
            <div style={{background:grad,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontWeight:800,color:"white",fontSize:15}}>Application Details</div>
              <button onClick={()=>setSelected(null)} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"none",borderRadius:8,width:30,height:30,cursor:"pointer",fontSize:16}}>✕</button>
            </div>

            <div style={{padding:"20px",maxHeight:"calc(100vh - 280px)",overflowY:"auto"}}>
              {/* Status badge */}
              {(()=>{ const sc = STATUS_CONFIG[selected.status]||STATUS_CONFIG.pending; return (
                <div style={{background:sc.bg,border:`1px solid ${sc.border}`,borderRadius:10,
                  padding:"10px 14px",marginBottom:18,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>{sc.dot}</span>
                  <span style={{fontWeight:800,color:sc.color,fontSize:14}}>{sc.label}</span>
                  <span style={{marginLeft:"auto",fontSize:11,color:"#9ca3af"}}>
                    {selected.reviewed_at ? `Reviewed ${fmt(selected.reviewed_at)}` : `Applied ${fmt(selected.applied_at)}`}
                  </span>
                </div>
              );})()}

              {/* Student info */}
              {[
                ["Student Name",   selected.student_name],
                ["Date of Birth",  selected.date_of_birth ? fmt(selected.date_of_birth) : "—"],
                ["Gender",         selected.gender || "—"],
                ["Program",        selected.program_applied],
                ["Previous School",selected.previous_school || "—"],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",
                  borderBottom:"1px solid #f3f4f6",fontSize:13}}>
                  <span style={{color:"#6b7280",fontWeight:600}}>{l}</span>
                  <span style={{color:"#1a1a1a",fontWeight:700,textAlign:"right",maxWidth:"55%"}}>{v}</span>
                </div>
              ))}

              <div style={{height:8}}/>
              <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Guardian</div>
              {[
                ["Father/Guardian", selected.father_name],
                ["Phone",           selected.father_phone],
                ["CNIC",            selected.father_cnic || "—"],
                ["Address",         selected.address || "—"],
                ["How heard",       selected.how_heard || "—"],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",
                  borderBottom:"1px solid #f3f4f6",fontSize:13}}>
                  <span style={{color:"#6b7280",fontWeight:600}}>{l}</span>
                  <span style={{color:"#1a1a1a",fontWeight:700,textAlign:"right",maxWidth:"55%"}}>{v}</span>
                </div>
              ))}

              {selected.notes && (
                <div style={{background:"#f9fafb",borderRadius:10,padding:"12px 14px",marginTop:12,fontSize:13,color:"#374151"}}>
                  <div style={{fontWeight:700,color:"#6b7280",marginBottom:4}}>Applicant Notes</div>
                  {selected.notes}
                </div>
              )}

              {/* Internal notes */}
              <div style={{marginTop:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Internal Staff Notes</div>
                <textarea value={noteInput} onChange={e=>setNoteInput(e.target.value)} rows={3}
                  placeholder="Add private notes (not visible to applicant)..."
                  style={{width:"100%",border:"1.5px solid #e5e7eb",borderRadius:9,padding:"10px 12px",
                    fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none"}}/>
                <button onClick={saveNote} disabled={saving}
                  style={{marginTop:6,background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:8,
                    padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",color:"#374151"}}>
                  {saving?"Saving…":"Save Note"}
                </button>
              </div>

              {/* Actions */}
              <div style={{marginTop:20,display:"flex",flexDirection:"column",gap:10}}>
                <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:1}}>Actions</div>

                {selected.status !== "test_scheduled" && (
                  <button onClick={()=>updateStatus(selected.id,"test_scheduled")} disabled={saving}
                    style={{background:"linear-gradient(135deg,#0891b2,#0e7490)",color:"white",border:"none",
                      borderRadius:10,padding:"11px 16px",fontWeight:800,fontSize:13,cursor:"pointer",textAlign:"left"}}>
                    🔵 Schedule Admission Test
                    <div style={{fontSize:11,fontWeight:400,opacity:.85,marginTop:2}}>Mark student for test · Call them to schedule</div>
                  </button>
                )}

                {selected.status !== "accepted" && (
                  <button onClick={()=>updateStatus(selected.id,"accepted")} disabled={saving}
                    style={{background:"linear-gradient(135deg,#16a34a,#15803d)",color:"white",border:"none",
                      borderRadius:10,padding:"11px 16px",fontWeight:800,fontSize:13,cursor:"pointer",textAlign:"left"}}>
                    🟢 Accept Application
                    <div style={{fontSize:11,fontWeight:400,opacity:.85,marginTop:2}}>Student passed test · Ready to enrol in Register tab</div>
                  </button>
                )}

                {selected.status === "accepted" && (
                  <div style={{background:"#f0fdf4",border:"1.5px solid #bbf7d0",borderRadius:10,
                    padding:"12px 14px",fontSize:13,color:"#166534",display:"flex",gap:10,alignItems:"flex-start"}}>
                    <span style={{fontSize:20,flexShrink:0}}>💡</span>
                    <div>
                      <div style={{fontWeight:800,marginBottom:3}}>Next Step: Register the Student</div>
                      <div style={{fontWeight:400,lineHeight:1.5}}>
                        This application is accepted! Click below to go and officially enrol <strong>{selected.student_name}</strong>.
                        <div style={{marginTop:10}}>
                          <button onClick={()=>setTab&&setTab("register")} style={{background:"#1a6b3c",color:"white",border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                            ➕ Go to Register Tab →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selected.status !== "rejected" && (
                  <button onClick={()=>updateStatus(selected.id,"rejected")} disabled={saving}
                    style={{background:"linear-gradient(135deg,#dc2626,#b91c1c)",color:"white",border:"none",
                      borderRadius:10,padding:"11px 16px",fontWeight:800,fontSize:13,cursor:"pointer",textAlign:"left"}}>
                    🔴 Reject Application
                    <div style={{fontSize:11,fontWeight:400,opacity:.85,marginTop:2}}>Not selected — kept in records for reference</div>
                  </button>
                )}

                {selected.status !== "pending" && (
                  <button onClick={()=>updateStatus(selected.id,"pending")} disabled={saving}
                    style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",
                      borderRadius:10,padding:"11px 16px",fontWeight:700,fontSize:13,cursor:"pointer",textAlign:"left"}}>
                    🟡 Reset to Pending
                  </button>
                )}

                <button onClick={()=>deleteApp(selected.id)} disabled={saving}
                  style={{background:"transparent",color:"#dc2626",border:"1px solid #fca5a5",
                    borderRadius:10,padding:"9px 16px",fontWeight:700,fontSize:12,cursor:"pointer",marginTop:4}}>
                  🗑 Delete Application
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SMART ALERTS ──────────────────────────────────────────────────
// role: "admin" | "teacher" | "parent"
// For parent portal, pass: role="parent", student (single student obj), attendance (flat array for that student), fees (flat array for that student)
function SmartAlerts({ students=[], attendance=[], fees=[], isTeacher=false, myClasses=[], role, student=null }) {
  const [dismissed, setDismissed] = React.useState({});
  const S = useSettings();
  const attThreshold = Number(S?.att_threshold||75);
  const today = new Date(); today.setHours(0,0,0,0);
  const todayStr = today.toISOString().split("T")[0];

  // Derive effective role
  const effectiveRole = role || (isTeacher ? "teacher" : "admin");

  const scopedStudents = effectiveRole==="teacher"&&myClasses.length
    ? students.filter(s=>myClasses.includes(s.class||s["class"]))
    : students;

  const alerts = [];

  // ── ADMIN ALERTS ──────────────────────────────────────────────
  if(effectiveRole==="admin") {
    // Low attendance students
    const lowAtt = scopedStudents.filter(s=>{
      const recs = attendance.filter(a=>String(a.student_id)===String(s.id));
      if(recs.length<5) return false;
      return Math.round(recs.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/recs.length*100) < attThreshold;
    });
    if(lowAtt.length>0) alerts.push({id:"low_att",icon:"⚠️",color:"#dc2626",bg:"#fef2f2",border:"#fecaca",
      title:`${lowAtt.length} student${lowAtt.length>1?"s":""} below ${attThreshold}% attendance`,
      body:lowAtt.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(lowAtt.length>3?` +${lowAtt.length-3} more`:"")});

    // Absent today
    const absentToday = scopedStudents.filter(s=>attendance.some(a=>String(a.student_id)===String(s.id)&&(a.date===todayStr||a.created_at?.startsWith(todayStr))&&(a.status==="A"||a.status==="Absent")));
    if(absentToday.length>0) alerts.push({id:"absent_today",icon:"🔴",color:"#ea580c",bg:"#fff7ed",border:"#fed7aa",
      title:`${absentToday.length} student${absentToday.length>1?"s":""} absent today`,
      body:absentToday.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(absentToday.length>3?` +${absentToday.length-3} more`:"")});

    // Fee defaulters
    if(fees.length>0){
      const paidIds=new Set(fees.filter(f=>f.method&&f.method!=="Unpaid").map(f=>f.student_id));
      const def=scopedStudents.filter(s=>!paidIds.has(s.id)&&s.fees_status!=="Paid");
      if(def.length>0) alerts.push({id:"fees",icon:"💰",color:"#d97706",bg:"#fffbeb",border:"#fde68a",
        title:`${def.length} student${def.length>1?"s":""} with unpaid fees`,
        body:def.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(def.length>3?` +${def.length-3} more`:"")});
    }

    // New registrations this week
    const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate()-7);
    const newStudents = scopedStudents.filter(s=>{ const d=new Date(s.created_at||s.enrolled); return d>=weekAgo&&d<=today; });
    if(newStudents.length>0) alerts.push({id:"new_students",icon:"🆕",color:"#7c3aed",bg:"#f5f3ff",border:"#ddd6fe",
      title:`${newStudents.length} new student${newStudents.length>1?"s":""} registered this week`,
      body:newStudents.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(newStudents.length>3?` +${newStudents.length-3} more`:"")});

    // Staff count nudge (if students>50 and very few staff)
    if(scopedStudents.length>50) {
      const studentTeacherRatio = scopedStudents.length;
      if(studentTeacherRatio>100) alerts.push({id:"ratio",icon:"👨‍🏫",color:"#0d9488",bg:"#e0f7f5",border:"#99f6e4",
        title:`${scopedStudents.length} active students enrolled`,
        body:"Ensure staffing levels are adequate for current enrolment."});
    }

    // Great attendance
    const todayRecs=attendance.filter(a=>a.date===todayStr||a.created_at?.startsWith(todayStr));
    if(todayRecs.length>0){
      const pct=Math.round(todayRecs.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/todayRecs.length*100);
      if(pct>=90) alerts.push({id:"great",icon:"🌟",color:"#16a34a",bg:"#f0fdf4",border:"#bbf7d0",
        title:`Excellent attendance today — ${pct}% present`,body:`${Math.round(todayRecs.length*pct/100)} of ${todayRecs.length} students marked present.`});
    }
  }

  // ── TEACHER ALERTS ────────────────────────────────────────────
  if(effectiveRole==="teacher") {
    // Absent today in my classes
    const absentToday = scopedStudents.filter(s=>attendance.some(a=>String(a.student_id)===String(s.id)&&(a.date===todayStr||a.created_at?.startsWith(todayStr))&&(a.status==="A"||a.status==="Absent")));
    if(absentToday.length>0) alerts.push({id:"absent_today",icon:"🔴",color:"#ea580c",bg:"#fff7ed",border:"#fed7aa",
      title:`${absentToday.length} student${absentToday.length>1?"s":""} absent in your class${myClasses.length>1?"es":""} today`,
      body:absentToday.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(absentToday.length>3?` +${absentToday.length-3} more`:"")});

    // Students at risk — low attendance in my classes
    const atRisk = scopedStudents.filter(s=>{
      const recs = attendance.filter(a=>String(a.student_id)===String(s.id));
      if(recs.length<5) return false;
      return Math.round(recs.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/recs.length*100) < attThreshold;
    });
    if(atRisk.length>0) alerts.push({id:"at_risk",icon:"⚠️",color:"#dc2626",bg:"#fef2f2",border:"#fecaca",
      title:`${atRisk.length} student${atRisk.length>1?"s":""} at risk — below ${attThreshold}% attendance`,
      body:atRisk.slice(0,3).map(s=>s.name||s.full_name).join(", ")+(atRisk.length>3?` +${atRisk.length-3} more`:"")});

    // No attendance marked today yet
    const markedToday = attendance.filter(a=>(a.date===todayStr||a.created_at?.startsWith(todayStr)));
    const myStudentIds = new Set(scopedStudents.map(s=>String(s.id)));
    const myMarkedToday = markedToday.filter(a=>myStudentIds.has(String(a.student_id)));
    if(scopedStudents.length>0 && myMarkedToday.length===0) alerts.push({id:"no_att",icon:"📋",color:"#2563eb",bg:"#eff6ff",border:"#bfdbfe",
      title:"Attendance not yet marked today",
      body:`${scopedStudents.length} student${scopedStudents.length>1?"s":""} in your class${myClasses.length>1?"es":""} — mark attendance now`});

    // Positive: full attendance today
    if(myMarkedToday.length>0){
      const pct=Math.round(myMarkedToday.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/myMarkedToday.length*100);
      if(pct===100) alerts.push({id:"full_att",icon:"🌟",color:"#16a34a",bg:"#f0fdf4",border:"#bbf7d0",
        title:"100% attendance in your class today!",body:"All students are present. Great day ahead!"});
    }
  }

  // ── PARENT ALERTS ─────────────────────────────────────────────
  if(effectiveRole==="parent" && student) {
    const studentId = String(student.id);
    const myAtt = attendance.filter(a=>String(a.student_id)===studentId||!a.student_id); // flat array for this student

    // Absent today
    const absentToday = myAtt.some(a=>(a.date===todayStr||a.created_at?.startsWith(todayStr))&&(a.status==="A"||a.status==="Absent"));
    if(absentToday) alerts.push({id:"absent_today",icon:"🔴",color:"#dc2626",bg:"#fef2f2",border:"#fecaca",
      title:`${student.full_name||student.name} is marked absent today`,
      body:"If this is an error, please contact the school."});

    // Low overall attendance
    if(myAtt.length>=5){
      const pct=Math.round(myAtt.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/myAtt.length*100);
      if(pct<attThreshold) alerts.push({id:"low_att",icon:"⚠️",color:"#d97706",bg:"#fffbeb",border:"#fde68a",
        title:`Attendance at ${pct}% — below the ${attThreshold}% threshold`,
        body:"Regular attendance is important for academic progress. Please speak with the school."});
      else if(pct>=95) alerts.push({id:"great_att",icon:"🌟",color:"#16a34a",bg:"#f0fdf4",border:"#bbf7d0",
        title:`Excellent attendance — ${pct}%`,body:"Keep up the great work!"});
    }

    // Fee pending
    const unpaidFees = fees.filter(f=>!f.method||f.method==="Unpaid");
    if(unpaidFees.length>0) alerts.push({id:"fees",icon:"💰",color:"#d97706",bg:"#fffbeb",border:"#fde68a",
      title:`${unpaidFees.length} fee payment${unpaidFees.length>1?"s":""} pending`,
      body:"Please clear outstanding fees to avoid disruption."});
  }

  const visible = alerts.filter(a=>!dismissed[a.id]);
  if(!visible.length) return null;
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:4}}>
      <div style={{fontSize:11,fontWeight:700,color:"#6b7280",letterSpacing:.5,textTransform:"uppercase"}}>⚡ Smart Alerts — {new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
      {visible.map(a=>(
        <div key={a.id} style={{background:a.bg,border:`1px solid ${a.border}`,borderRadius:12,padding:"11px 14px",display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:20,flexShrink:0}}>{a.icon}</span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:13,color:a.color}}>{a.title}</div>
            <div style={{fontSize:11,color:"#6b7280",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.body}</div>
          </div>
          <button onClick={()=>setDismissed(p=>({...p,[a.id]:true}))}
            style={{background:"rgba(0,0,0,0.06)",border:"none",borderRadius:6,width:22,height:22,cursor:"pointer",fontSize:13,color:"#6b7280",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
        </div>
      ))}
    </div>
  );
}

// ── STUDENT ID CARD ───────────────────────────────────────────────
function StudentIDCard({ student: s, onClose }) {
  const S = useSettings();
  const schoolName    = S?.school_name_en||"QUR'AN-E-KARIM ACADEMY";
  const schoolSub     = S?.school_name_en_sub||"Modern Islamic School System";
  const schoolAddr    = S?.address||"Gorsian, Jatli, Dist. Rawalpindi";
  const regNo         = S?.reg_no||"CMR-199/2015";
  const schoolNameUr  = S?.school_name_ur||"قرآن کریم اکیڈمی";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(`ID:${s.id}|${s.name||s.full_name}|${s.class||s["class"]}`)}`;
  const G = "#1a6b3c", GD = "#0a4a24", GOLD = "#d4af37", GOLD2 = "#f5d76e";
  const isFemale = s.gender==="Female";

  // Photo or professional avatar fallback
  const photoUrl = s.photo_url || s.photo || s.image_url || null;
  const AvatarSVG = ({female}) => (
    <svg width="64" height="76" viewBox="0 0 64 76" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="avatarBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={female?"#7c3aed":"#1a6b3c"}/>
          <stop offset="100%" stopColor={female?"#5b21b6":"#0a4a24"}/>
        </linearGradient>
        <linearGradient id="skin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5cba7"/>
          <stop offset="100%" stopColor="#e8a87c"/>
        </linearGradient>
        <clipPath id="avatarClip"><rect width="64" height="76" rx="10"/></clipPath>
      </defs>
      <rect width="64" height="76" rx="10" fill="url(#avatarBg)"/>
      {/* Body / uniform */}
      <ellipse cx="32" cy="72" rx="26" ry="18" fill={female?"#5b21b6":"#0d5c31"}/>
      <rect x="18" y="54" width="28" height="20" rx="4" fill={female?"#6d28d9":"#15803d"}/>
      {/* Collar */}
      <path d="M26 54 L32 62 L38 54" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      {/* Neck */}
      <rect x="27" y="42" width="10" height="14" rx="4" fill="url(#skin)"/>
      {/* Head */}
      <ellipse cx="32" cy="34" rx="14" ry="16" fill="url(#skin)"/>
      {/* Hair */}
      {female ? (
        <>
          <ellipse cx="32" cy="22" rx="14" ry="8" fill="#2d1b69"/>
          <rect x="18" y="22" width="4" height="18" rx="2" fill="#2d1b69"/>
          <rect x="42" y="22" width="4" height="18" rx="2" fill="#2d1b69"/>
          {/* Hijab */}
          <ellipse cx="32" cy="22" rx="15" ry="9" fill="#7c3aed" opacity="0.9"/>
          <rect x="17" y="22" width="30" height="22" rx="8" fill="#7c3aed" opacity="0.85"/>
          <ellipse cx="32" cy="22" rx="12" ry="6" fill="#6d28d9"/>
        </>
      ) : (
        <>
          <ellipse cx="32" cy="20" rx="14" ry="8" fill="#1a1a2e"/>
          <rect x="18" y="20" width="28" height="10" rx="0" fill="#1a1a2e"/>
          <path d="M18 24 Q32 18 46 24" fill="#1a1a2e"/>
        </>
      )}
      {/* Eyes */}
      <ellipse cx="26" cy="34" rx="2.5" ry="2.5" fill="white"/>
      <ellipse cx="38" cy="34" rx="2.5" ry="2.5" fill="white"/>
      <circle cx="26.5" cy="34.5" r="1.5" fill="#2d1b69"/>
      <circle cx="38.5" cy="34.5" r="1.5" fill="#2d1b69"/>
      {/* Smile */}
      <path d="M27 40 Q32 44 37 40" stroke="#c0835a" strokeWidth="1.2" fill="none" strokeLinecap="round"/>
    </svg>
  );

  return (
    <Portal>
      <div className="qka-modal-overlay" onClick={onClose}>
        <div className="qka-modal-box" style={{background:"white",maxWidth:560,boxShadow:"0 32px 80px rgba(0,0,0,0.5)",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
          {/* Modal header */}
          <div style={{background:`linear-gradient(135deg,${G},${GD})`,padding:"16px 22px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:20}}>🪪</span>
              <div>
                <div style={{color:"white",fontWeight:800,fontSize:15}}>Student ID Card</div>
                <div style={{color:"rgba(255,255,255,0.7)",fontSize:11}}>{s.name||s.full_name} · {s.class||s["class"]}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>window.print()} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                🖨️ Print / Save
              </button>
              <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,width:34,height:34,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
          </div>

          {/* Card preview area */}
          <div style={{padding:"28px 32px",background:"#f8fafc"}}>
            {/* The actual card — CR80 proportions (85.6×54mm) */}
            <div style={{
              width:"100%", maxWidth:400, margin:"0 auto",
              aspectRatio:"1.585/1",
              borderRadius:16, overflow:"hidden",
              background:`linear-gradient(145deg,${G} 0%,#0d5c31 55%,#083d20 100%)`,
              boxShadow:"0 16px 48px rgba(26,107,60,0.4), 0 2px 8px rgba(0,0,0,0.2)",
              position:"relative", fontFamily:"system-ui,sans-serif",
              border:"1px solid rgba(255,255,255,0.1)"
            }}>
              {/* Gold top stripe */}
              <div style={{height:6,background:`linear-gradient(90deg,${GD},${GOLD},${GOLD2},${GOLD},${GD})`}}/>

              {/* Watermark pattern */}
              <div style={{position:"absolute",inset:0,opacity:0.04,backgroundImage:"repeating-linear-gradient(45deg,white 0,white 1px,transparent 0,transparent 50%)",backgroundSize:"8px 8px"}}/>

              {/* Crescent moon watermark */}
              <div style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:64,color:"rgba(255,255,255,0.05)",fontWeight:900,userSelect:"none"}}>☽</div>

              {/* School logo area + name */}
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px 6px"}}>
                <img src={SCHOOL_LOGO} alt="Logo" style={{width:36,height:36,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 1px 3px rgba(0,0,0,0.4))"}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:9,fontWeight:900,color:"white",letterSpacing:0.5,lineHeight:1.1,textTransform:"uppercase"}}>{schoolName}</div>
                  <div style={{fontSize:7,color:"rgba(255,255,255,0.7)",fontWeight:600,lineHeight:1.2}}>{schoolSub}</div>
                </div>
                <div style={{fontFamily:"'Noto Nastaliq Urdu',serif",fontSize:10,color:GOLD2,fontWeight:700,textAlign:"right",lineHeight:1.3}}>{schoolNameUr}</div>
              </div>

              {/* Thin accent divider */}
              <div style={{height:1,background:"linear-gradient(90deg,transparent,rgba(212,175,55,0.6),transparent)",margin:"0 14px"}}/>

              {/* Main content */}
              <div style={{display:"flex",gap:12,padding:"10px 14px",alignItems:"flex-start"}}>
                {/* Avatar + ID */}
                <div style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{
                    borderRadius:10,
                    border:"2px solid rgba(212,175,55,0.6)",
                    overflow:"hidden",
                    background:"rgba(255,255,255,0.05)",
                    boxShadow:"0 4px 12px rgba(0,0,0,0.3)",
                    width:64, height:76,
                    display:"flex", alignItems:"center", justifyContent:"center"
                  }}>
                    {photoUrl
                      ? <img src={photoUrl} alt={s.name||s.full_name} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                      : <AvatarSVG female={isFemale}/>
                    }
                  </div>
                  <div style={{background:"rgba(0,0,0,0.25)",borderRadius:5,padding:"2px 7px",fontSize:7,fontWeight:800,color:GOLD2,letterSpacing:0.5,textAlign:"center"}}>
                    ID: {s.id}
                  </div>
                </div>

                {/* Student details */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:900,color:"white",lineHeight:1.2,marginBottom:6,textShadow:"0 1px 4px rgba(0,0,0,0.3)"}}>{s.name||s.full_name}</div>
                  {[
                    ["Class / Grade", s.class||s["class"]||"—"],
                    ["Father's Name", s.father||s.father_name||"—"],
                    ["Contact", s.contact||s.phone||"—"],
                    ["Gender", s.gender||"—"],
                    ["Enrolled", s.enrolled||s.enrolled_date||"—"],
                  ].map(([l,v])=>(
                    <div key={l} style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:3}}>
                      <span style={{fontSize:7.5,color:"rgba(255,255,255,0.55)",width:60,flexShrink:0,fontWeight:600,paddingTop:1}}>{l}:</span>
                      <span style={{fontSize:8.5,fontWeight:700,color:"rgba(255,255,255,0.92)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* QR code */}
                <div style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{background:"white",borderRadius:8,padding:3,boxShadow:"0 2px 8px rgba(0,0,0,0.3)"}}>
                    <img src={qrUrl} width={54} height={54} alt="QR" style={{display:"block",borderRadius:5}}/>
                  </div>
                  <div style={{fontSize:6.5,color:"rgba(255,255,255,0.5)",textAlign:"center"}}>Scan to verify</div>
                </div>
              </div>

              {/* Bottom strip */}
              <div style={{position:"absolute",bottom:0,left:0,right:0}}>
                <div style={{background:"rgba(0,0,0,0.25)",padding:"4px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:6.5,color:"rgba(255,255,255,0.5)"}}>{schoolAddr} · Reg. {regNo}</span>
                  <span style={{fontSize:6.5,color:"rgba(255,255,255,0.4)",fontStyle:"italic"}}>Return if found</span>
                </div>
                <div style={{height:5,background:`linear-gradient(90deg,${GD},${GOLD},${GOLD2},${GOLD},${GD})`}}/>
              </div>
            </div>

            {/* Back of card hint */}
            <div style={{textAlign:"center",marginTop:16,fontSize:11,color:"#9ca3af"}}>
              Standard CR80 card size (85.6 × 54mm) · Print on glossy card stock
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ── FEE CHALLAN ───────────────────────────────────────────────────
function FeeChallan({ student: s, feeRecord, onClose }) {
  const S = useSettings();
  const G = "#1a6b3c", GD = "#0a4a24";
  const schoolName = S?.school_name_en||"QUR'AN-E-KARIM ACADEMY";
  const schoolAddr = S?.address||"Gorsian, Jatli, Dist. Rawalpindi";
  const today = new Date();
  const monthName = today.toLocaleString("default",{month:"long"});
  const year = today.getFullYear();
  const dueDate = new Date(today.getFullYear(),today.getMonth()+1,7).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  // Use saved receipt number if opened from a fee record, else generate deterministic one
  const recMonth = feeRecord?.month || monthName;
  const recYear  = feeRecord?.year  || year;
  const challanNo = feeRecord?.receipt_no
    || `RCP-${String(s?.id||'0000').padStart(4,'0').slice(-4)}-${String(recMonth||'Jan').slice(0,3).toUpperCase()}${String(recYear||new Date().getFullYear()).slice(-2)}`;
  const displayMonth = recMonth;
  const displayYear  = recYear;
  const amount = feeRecord?.amount ? Number(feeRecord.amount) : Number(s.monthly_fee||1500);
  const CopyBox = ({label}) => (
    <div style={{border:"1px solid #d1d5db",borderRadius:10,padding:"12px 16px",flex:1,minWidth:160}}>
      <div style={{fontSize:10,fontWeight:700,color:"#6b7280",textTransform:"uppercase",borderBottom:"1px solid #e5e7eb",paddingBottom:5,marginBottom:8}}>{label}</div>
      <div style={{fontWeight:800,fontSize:12,color:"#111827",marginBottom:3}}>{s.name||s.full_name}</div>
      <div style={{fontSize:11,color:"#374151",marginBottom:2}}>Class: <strong>{s.class||s["class"]}</strong> · ID: <strong>{s.id}</strong></div>
      <div style={{fontSize:11,color:"#374151",marginBottom:2}}>Month: <strong>{displayMonth} {displayYear}</strong></div>
      <div style={{fontSize:11,color:"#374151",marginBottom:8}}>Challan: <strong style={{color:G}}>{challanNo}</strong></div>
      <div style={{background:"#f9fafb",borderRadius:7,padding:"7px 10px",marginBottom:7}}>
        {[["Tuition Fee",`PKR ${amount.toLocaleString()}`],["Arrears","PKR 0"],["Fine","PKR 0"]].map(([l,v])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"2px 0"}}>
            <span style={{color:"#6b7280"}}>{l}</span><span style={{fontWeight:700}}>{v}</span>
          </div>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:900,color:G,borderTop:"1px solid #e5e7eb",marginTop:5,paddingTop:5}}>
          <span>Total</span><span>PKR {amount.toLocaleString()}</span>
        </div>
      </div>
      <div style={{fontSize:10,color:"#6b7280"}}>Due: <strong style={{color:"#dc2626"}}>{dueDate}</strong></div>
      <div style={{marginTop:8,borderTop:"1px dashed #d1d5db",paddingTop:8}}>
        <div style={{fontSize:9,color:"#9ca3af"}}>Signature / Stamp</div>
        <div style={{height:20}}/>
      </div>
    </div>
  );
  return (
    <Portal>
      <div className="qka-modal-overlay" onClick={onClose}>
        <div className="qka-modal-box" style={{background:"white",maxWidth:740,boxShadow:"0 24px 60px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
          <div style={{background:`linear-gradient(135deg,${G},${GD})`,padding:"14px 20px",borderRadius:"20px 20px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{color:"white",fontWeight:800,fontSize:14}}>📄 Fee Challan — {displayMonth} {displayYear}</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>window.print()} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🖨️ Print</button>
              <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:30,height:30,fontSize:16,cursor:"pointer"}}>✕</button>
            </div>
          </div>
          <div style={{padding:24}}>
            <div style={{textAlign:"center",borderBottom:"2px solid #e5e7eb",paddingBottom:12,marginBottom:16}}>
              <div style={{fontWeight:900,fontSize:17,color:G}}>{schoolName}</div>
              <div style={{fontSize:12,color:"#6b7280",marginTop:1}}>{schoolAddr}</div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:1}}>Fee Payment Challan — {displayMonth} {displayYear}</div>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <CopyBox label="Bank Copy"/><CopyBox label="School Copy"/><CopyBox label="Student Copy"/>
            </div>
            <div style={{marginTop:12,textAlign:"center",fontSize:10,color:"#9ca3af"}}>Late payment incurs a fine of PKR 50/day after the due date.</div>
          </div>
        </div>
      </div>
    </Portal>
  );
}


// ── FEE RECEIPT (post-payment confirmation) ───────────────────────
function FeeReceipt({ student: s, feeRecord: f, onClose, copyType="school" }) {
  const S = useSettings();
  const G = "#1a6b3c", GD = "#0a4a24", GOLD = "#d4af37", GOLD2 = "#f5d76e";
  const schoolName   = S?.school_name_en    || "QUR'AN-E-KARIM ACADEMY";
  const schoolSub    = S?.school_name_en_sub|| "Modern Islamic School System";
  const schoolAddr   = S?.address           || "Gorsian, Jatli, Dist. Rawalpindi";
  const schoolNameUr = S?.school_name_ur    || "قرآن کریم اکیڈمی";
  const regNo        = S?.reg_no            || "CMR-199/2015";
  const schoolPhone  = S?.contact           || "";

  const receiptNo   = f?.receipt_no  || "—";
  const amount      = Number(f?.amount || 0);
  const month       = f?.month       || "—";
  const year        = f?.year        || "";
  const method      = f?.method      || "Cash";
  const paidDate    = f?.paid_date   || new Date().toISOString().split("T")[0];
  const notes       = f?.notes       || "";
  const studentName = s?.full_name   || s?.name        || f?.student_name || "—";
  const studentClass= s?.class       || s?.["class"]   || f?.class        || "—";
  const studentId   = s?.id          || f?.student_id  || "—";
  const fatherName  = s?.father_name || s?.father      || "—";
  const stream      = s?.stream      || "";
  const printedDate = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  const methodIcons = {Cash:"💵",Bank:"🏦",JazzCash:"📱",EasyPaisa:"📲",Cheque:"📝"};

  const ReceiptCopy = ({label, showWatermark=false}) => (
    <div style={{
      flex:1, minWidth:260, background:"white",
      borderRadius:14, overflow:"hidden",
      boxShadow:"0 4px 20px rgba(26,107,60,0.12)",
      border:"1px solid #d1e8d8",
      position:"relative",
    }}>
      {/* Gold top stripe */}
      <div style={{height:5,background:`linear-gradient(90deg,${GD},${GOLD},${GOLD2},${GOLD},${GD})`}}/>

      {/* Watermark diagonal text */}
      {showWatermark&&(
        <div style={{
          position:"absolute",top:"50%",left:"50%",
          transform:"translate(-50%,-50%) rotate(-35deg)",
          fontSize:52,fontWeight:900,color:"rgba(26,107,60,0.04)",
          whiteSpace:"nowrap",pointerEvents:"none",userSelect:"none",
          letterSpacing:4
        }}>QK ACADEMY</div>
      )}

      {/* School letterhead */}
      <div style={{
        background:`linear-gradient(135deg,${G},${GD})`,
        padding:"14px 16px",
        display:"flex",alignItems:"center",gap:12
      }}>
        <img src={SCHOOL_LOGO} alt="Logo"
          style={{width:40,height:40,objectFit:"contain",
            filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.3))",flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:"white",fontWeight:900,fontSize:12,letterSpacing:0.5,lineHeight:1.2}}>
            {schoolName}
          </div>
          <div style={{color:"rgba(255,255,255,0.75)",fontSize:9,marginTop:1}}>{schoolSub}</div>
          <div style={{color:"rgba(255,255,255,0.6)",fontSize:8,marginTop:1}}>
            {schoolAddr}{schoolPhone?` · ${schoolPhone}`:""}
          </div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontFamily:"'Noto Nastaliq Urdu',serif",fontSize:12,
            color:GOLD2,fontWeight:700,lineHeight:1.3}}>{schoolNameUr}</div>
          <div style={{color:"rgba(255,255,255,0.5)",fontSize:8,marginTop:2}}>Reg. {regNo}</div>
        </div>
      </div>

      {/* Receipt title bar */}
      <div style={{
        background:"#f0f7f2",borderBottom:"1.5px solid #d1e8d8",
        padding:"8px 16px",display:"flex",justifyContent:"space-between",alignItems:"center"
      }}>
        <div>
          <div style={{fontWeight:900,fontSize:13,color:G,letterSpacing:1,textTransform:"uppercase"}}>
            Fee Receipt
          </div>
          <div style={{fontSize:9,color:"#6b7280",marginTop:1}}>Academic Year {year}</div>
        </div>
        <div style={{
          background:G,color:"white",borderRadius:6,
          padding:"2px 10px",fontSize:9,fontWeight:800,
          textTransform:"uppercase",letterSpacing:1
        }}>{label}</div>
      </div>

      <div style={{padding:"12px 16px"}}>
        {/* Receipt No + Date */}
        <div style={{
          display:"flex",gap:8,marginBottom:10,
          background:"#f9fafb",borderRadius:8,padding:"8px 12px",
          border:"1px solid #f3f4f6"
        }}>
          <div style={{flex:1}}>
            <div style={{fontSize:8,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>
              Receipt No.
            </div>
            <div style={{fontWeight:900,fontSize:14,color:G,letterSpacing:0.5}}>{receiptNo}</div>
          </div>
          <div style={{width:1,background:"#e5e7eb"}}/>
          <div style={{flex:1,textAlign:"right"}}>
            <div style={{fontSize:8,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>
              Date Paid
            </div>
            <div style={{fontWeight:700,fontSize:11,color:"#111827"}}>{paidDate}</div>
          </div>
        </div>

        {/* Student info card */}
        <div style={{
          background:`linear-gradient(135deg,${G}08,${G}04)`,
          border:`1px solid ${G}20`,borderRadius:10,
          padding:"10px 12px",marginBottom:10
        }}>
          <div style={{fontSize:8,color:G,fontWeight:800,textTransform:"uppercase",
            letterSpacing:0.5,marginBottom:6}}>Student Details</div>
          {[
            ["Name",        studentName],
            ["Father",      fatherName],
            ["Class",       studentClass+(stream?` · ${stream}`:"")],
            ["Student ID",  String(studentId)],
          ].map(([lbl,val])=>(
            <div key={lbl} style={{
              display:"flex",justifyContent:"space-between",
              fontSize:10,padding:"2px 0",
              borderBottom:"1px dotted #d1e8d8"
            }}>
              <span style={{color:"#6b7280",fontWeight:600,minWidth:70}}>{lbl}</span>
              <span style={{fontWeight:700,color:"#111827",
                maxWidth:140,textAlign:"right",
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val}</span>
            </div>
          ))}
        </div>

        {/* Payment breakdown */}
        <div style={{
          border:"1.5px solid #d1e8d8",borderRadius:10,
          overflow:"hidden",marginBottom:10
        }}>
          <div style={{
            background:`linear-gradient(135deg,${G},${GD})`,
            padding:"6px 12px",
            fontSize:8,fontWeight:800,color:"white",
            textTransform:"uppercase",letterSpacing:0.5
          }}>Payment Details</div>
          <div style={{padding:"8px 12px"}}>
            {[
              ["Fee Period",       `${month} ${year}`],
              ["Payment Method",  `${methodIcons[method]||"💰"} ${method}`],
              ...(notes?[["Notes", notes]]:[]),
            ].map(([lbl,val])=>(
              <div key={lbl} style={{
                display:"flex",justifyContent:"space-between",
                fontSize:10,padding:"3px 0",
                borderBottom:"1px dotted #f3f4f6"
              }}>
                <span style={{color:"#6b7280",fontWeight:600}}>{lbl}</span>
                <span style={{fontWeight:700,color:"#374151"}}>{val}</span>
              </div>
            ))}
            {/* Amount total */}
            <div style={{
              display:"flex",justifyContent:"space-between",alignItems:"center",
              marginTop:8,paddingTop:8,
              borderTop:`2px solid ${G}30`
            }}>
              <span style={{fontSize:12,fontWeight:800,color:G}}>Total Amount Paid</span>
              <span style={{fontSize:18,fontWeight:900,color:G}}>
                PKR {amount.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* PAID stamp + signature */}
        <div style={{
          display:"flex",justifyContent:"space-between",alignItems:"flex-end",
          borderTop:"1px dashed #d1e8d8",marginTop:10,paddingTop:10
        }}>
          {/* Stamp */}
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
            <div style={{
              border:"2.5px solid #16a34a",borderRadius:8,
              padding:"6px 16px",transform:"rotate(-6deg)",
              background:"#f0fdf4",display:"inline-block"
            }}>
              <div style={{
                color:"#16a34a",fontWeight:900,fontSize:13,
                letterSpacing:3,display:"flex",alignItems:"center",gap:5
              }}>✓ PAID</div>
            </div>
          </div>
          {/* Signature line */}
          <div style={{textAlign:"center",minWidth:120}}>
            <div style={{height:32,borderBottom:"1.5px solid #9ca3af",marginBottom:5}}/>
            <div style={{fontSize:8,color:"#9ca3af",fontWeight:600,letterSpacing:0.3}}>
              Authorised Signature & Stamp
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop:10,paddingTop:8,borderTop:"1px dashed #d1e8d8",
          textAlign:"center",fontSize:8,color:"#9ca3af",lineHeight:1.6
        }}>
          This is a computer-generated receipt and is valid without a physical signature.<br/>
          {schoolName} · {schoolAddr}
        </div>
      </div>

      {/* Gold bottom stripe */}
      <div style={{height:4,background:`linear-gradient(90deg,${GD},${GOLD},${GOLD2},${GOLD},${GD})`}}/>
    </div>
  );

  return (
    <Portal>
      <div className="qka-modal-overlay" onClick={onClose}>
        <div className="qka-modal-box" style={{
          background:"#f0f7f2",maxWidth:860,
          boxShadow:"0 32px 80px rgba(0,0,0,0.5)",overflow:"hidden"
        }} onClick={e=>e.stopPropagation()}>

          {/* Modal header */}
          <div style={{
            background:`linear-gradient(135deg,${G},${GD})`,
            padding:"16px 22px",
            display:"flex",justifyContent:"space-between",alignItems:"center"
          }}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <img src={SCHOOL_LOGO} style={{width:36,height:36,objectFit:"contain",
                filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.3))"}}/>
              <div>
                <div style={{color:"white",fontWeight:900,fontSize:15}}>
                  🧾 Fee Receipt
                </div>
                <div style={{color:"rgba(255,255,255,0.7)",fontSize:11,marginTop:1}}>
                  {studentName} · {month} {year} · Receipt No: {receiptNo}
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>window.print()}
                style={{background:"rgba(255,255,255,0.15)",color:"white",
                  border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,
                  padding:"8px 18px",fontWeight:700,fontSize:12,cursor:"pointer",
                  display:"flex",alignItems:"center",gap:6}}>
                🖨️ Print
              </button>
              <button onClick={onClose}
                style={{background:"rgba(255,255,255,0.15)",color:"white",
                  border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,
                  width:36,height:36,fontSize:18,cursor:"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                ✕
              </button>
            </div>
          </div>

          {/* Two copies side by side */}
          <div style={{padding:"20px 24px"}}>
            <div style={{display:"flex",justifyContent:"center"}}>
              {copyType==="parent"
                ? <ReceiptCopy label="Parent Copy" showWatermark={false}/>
                : <ReceiptCopy label="School Copy" showWatermark={true}/>
              }
            </div>
            <div style={{
              marginTop:12,textAlign:"center",fontSize:10,color:"#9ca3af",
              display:"flex",alignItems:"center",justifyContent:"center",gap:6
            }}>
              <span>🗓️ Printed on {printedDate}</span>
              <span>·</span>
              <span>{schoolName}</span>
              <span>·</span>
              <span>Reg. {regNo}</span>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ── CONDUCT TYPES ────────────────────────────────────────────────
const CONDUCT_TYPES = [
  {id:"commendation", label:"Commendation", icon:"🌟", color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0"},
  {id:"warning",      label:"Warning",      icon:"⚠️", color:"#d97706", bg:"#fffbeb", border:"#fde68a"},
  {id:"incident",     label:"Incident",     icon:"📋", color:"#2563eb", bg:"#eff6ff", border:"#bfdbfe"},
  {id:"detention",    label:"Detention",    icon:"🔒", color:"#7c3aed", bg:"#f5f3ff", border:"#ddd6fe"},
  {id:"suspension",   label:"Suspension",   icon:"🚫", color:"#dc2626", bg:"#fef2f2", border:"#fecaca"},
];

function ConductLog({ students=[] }) {
  const G = "#1a6b3c", GP = "#e8f5ee", GB = "#d1fae5";
  const todayStr = new Date().toISOString().split("T")[0];
  const blankForm = {student_id:"",type:"warning",date:todayStr,description:"",action_taken:"",recorded_by:"",parent_informed:false,parent_informed_date:"",status:"open"};

  const [records,    setRecords]    = React.useState([]);
  const [loading,    setLoading]    = React.useState(true);
  const [view,       setView]       = React.useState("log"); // "log" | "analytics"
  const [showForm,   setShowForm]   = React.useState(false);
  const [saving,     setSaving]     = React.useState(false);
  const [toast,      setToast]      = React.useState(null);
  const [filterType, setFilterType] = React.useState("all");
  const [filterClass,setFilterClass]= React.useState("All");
  const [filterStatus,setFilterStatus]=React.useState("all");
  const [searchQ,    setSearchQ]    = React.useState("");
  const [form,       setForm]       = React.useState(blankForm);
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [profileName,setProfileName]= React.useState(null); // per-student panel

  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3000);};
  const allClasses = ["All",...new Set(students.map(s=>s.class||s["class"]).filter(Boolean)).values()].sort();
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  React.useEffect(()=>{
    supabase.from("student_conduct").select("*").order("date",{ascending:false})
      .then(({data})=>{setRecords(data||[]);setLoading(false);});
    const handler = e=>{setForm(p=>({...p,student_id:e.detail.studentId}));setShowForm(true);setView("log");};
    window.addEventListener("openConduct",handler);
    return()=>window.removeEventListener("openConduct",handler);
  },[]);

  const save = async()=>{
    if(!form.student_id||!form.description) return showToast("Select student and add description",false);
    setSaving(true);
    const stu = students.find(s=>s.id===form.student_id);
    const {data:{user}} = await supabase.auth.getUser();
    const payload = {
      student_id:form.student_id, type:form.type, date:form.date,
      description:form.description, action_taken:form.action_taken||null,
      recorded_by:form.recorded_by||"Admin",
      parent_informed:form.parent_informed||false,
      parent_informed_date:form.parent_informed&&form.parent_informed_date?form.parent_informed_date:null,
      status:form.status||"open",
      student_name:stu?.name||stu?.full_name||"",
      class:stu?.class||stu?.["class"]||"",
    };
    if(user?.id) payload.created_by = user.id;
    const {data,error} = await supabase.from("student_conduct").insert(payload).select();
    if(error) showToast("Error: "+error.message,false);
    else{ setRecords(p=>[data[0],...p]); showToast("Record saved!"); setShowForm(false); setForm(blankForm); }
    setSaving(false);
  };

  const deleteRecord = async(id)=>{
    await supabase.from("student_conduct").delete().eq("id",id);
    setRecords(p=>p.filter(r=>r.id!==id));
    setConfirmDel(null);
    showToast("Deleted");
  };

  const toggleParentInformed = async(r)=>{
    const newVal = !r.parent_informed;
    const patch = {parent_informed:newVal, parent_informed_date:newVal?todayStr:null};
    const {error} = await supabase.from("student_conduct").update(patch).eq("id",r.id);
    if(error){showToast("Error: "+error.message,false);return;}
    setRecords(p=>p.map(x=>x.id===r.id?{...x,...patch}:x));
    showToast(newVal?"Parent marked as informed":"Marked as not informed");
  };

  const changeStatus = async(r,status)=>{
    const {error} = await supabase.from("student_conduct").update({status}).eq("id",r.id);
    if(error){showToast("Error: "+error.message,false);return;}
    setRecords(p=>p.map(x=>x.id===r.id?{...x,status}:x));
  };

  // ── Analytics computations ──────────────────────────────────────────────
  const analytics = React.useMemo(()=>{
    const byClass = {};
    const byMonth = {};
    const byStudent = {};
    const INCIDENT_TYPES = ["warning","incident","detention","suspension"];
    records.forEach(r=>{
      // by class
      if(r.class){
        if(!byClass[r.class]) byClass[r.class]={};
        byClass[r.class][r.type]=(byClass[r.class][r.type]||0)+1;
        byClass[r.class]._total=(byClass[r.class]._total||0)+1;
      }
      // by month (last 6)
      if(r.date){
        const m=r.date.slice(0,7);
        byMonth[m]=(byMonth[m]||0)+(INCIDENT_TYPES.includes(r.type)?1:0);
      }
      // by student (incidents only)
      if(INCIDENT_TYPES.includes(r.type)&&r.student_name){
        byStudent[r.student_name]=(byStudent[r.student_name]||0)+1;
      }
    });
    // last 6 months
    const months=[];
    for(let i=5;i>=0;i--){
      const d=new Date(); d.setMonth(d.getMonth()-i);
      const key=d.toISOString().slice(0,7);
      months.push({key,label:d.toLocaleDateString("en-GB",{month:"short",year:"2-digit"}),count:byMonth[key]||0});
    }
    const topStudents = Object.entries(byStudent).sort((a,b)=>b[1]-a[1]).slice(0,5);
    const classesSorted = Object.entries(byClass).sort((a,b)=>(b[1]._total||0)-(a[1]._total||0));
    const openCount = records.filter(r=>r.status==="open"&&r.type!=="commendation").length;
    const parentPending = records.filter(r=>r.type!=="commendation"&&!r.parent_informed).length;
    return {months,topStudents,classesSorted,openCount,parentPending};
  },[records]);

  const STATUS_META = {
    open:      {label:"Open",       col:"#d97706",bg:"#fffbeb"},
    resolved:  {label:"Resolved",   col:"#16a34a",bg:"#f0fdf4"},
    escalated: {label:"Escalated",  col:"#dc2626",bg:"#fef2f2"},
  };

  const counts = CONDUCT_TYPES.reduce((acc,t)=>({...acc,[t.id]:records.filter(r=>r.type===t.id).length}),{});
  const filtered = records.filter(r=>
    (filterType==="all"||r.type===filterType)&&
    (filterClass==="All"||r.class===filterClass)&&
    (filterStatus==="all"||(filterStatus==="open"?r.status!=="resolved":r.status===filterStatus))&&
    (!searchQ||(r.student_name||"").toLowerCase().includes(searchQ.toLowerCase()))
  );

  // per-student profile records
  const profileRecords = profileName ? records.filter(r=>r.student_name===profileName) : [];

  const inp14 = {border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 10px",fontSize:13,boxSizing:"border-box",width:"100%"};

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,padding:4}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* ── Header ── */}
      <div style={{background:"white",borderRadius:16,padding:"18px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontWeight:800,fontSize:16,color:"#111827"}}>📋 Conduct & Discipline Log</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
            {records.length} total · <span style={{color:"#d97706",fontWeight:700}}>{analytics.openCount} open</span> · <span style={{color:"#dc2626",fontWeight:700}}>{analytics.parentPending} parent not informed</span>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {["log","analytics"].map(v=>(
            <button key={v} onClick={()=>{setView(v);setProfileName(null);}}
              style={{background:view===v?G:"#f3f4f6",color:view===v?"white":"#374151",border:"none",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
              {v==="log"?"📋 Log":"📊 Analytics"}
            </button>
          ))}
          <button onClick={()=>{setShowForm(v=>!v);setView("log");setProfileName(null);}}
            style={{background:`linear-gradient(135deg,${G},#0d9488)`,color:"white",border:"none",borderRadius:10,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
            ➕ Add Record
          </button>
        </div>
      </div>

      {/* ── Add form ── */}
      {showForm&&(
        <div style={{background:"white",borderRadius:14,padding:20,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:`1.5px solid ${GB}`}}>
          <div style={{fontWeight:800,color:G,marginBottom:14,fontSize:14}}>New Conduct Record</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:12}}>
            <div><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Student *</label>
              <select value={form.student_id} onChange={e=>setF("student_id",e.target.value)} style={inp14}>
                <option value="">-- Select --</option>
                {students.map(s=><option key={s.id} value={s.id}>{s.name||s.full_name} ({s.class||"—"})</option>)}
              </select></div>
            <div><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Type *</label>
              <select value={form.type} onChange={e=>setF("type",e.target.value)} style={inp14}>
                {CONDUCT_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select></div>
            <div><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Date *</label>
              <input type="date" value={form.date} onChange={e=>setF("date",e.target.value)} style={inp14}/></div>
            <div><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Status</label>
              <select value={form.status} onChange={e=>setF("status",e.target.value)} style={inp14}>
                <option value="open">Open</option>
                <option value="resolved">Resolved</option>
                <option value="escalated">Escalated</option>
              </select></div>
            <div><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Recorded By</label>
              <input value={form.recorded_by} onChange={e=>setF("recorded_by",e.target.value)} placeholder="Admin / Teacher name" style={inp14}/></div>
          </div>
          <div style={{marginBottom:10}}><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Description *</label>
            <textarea value={form.description} onChange={e=>setF("description",e.target.value)} rows={2} placeholder="Describe the incident or commendation..." style={{...inp14,resize:"vertical",fontFamily:"inherit"}}/></div>
          <div style={{marginBottom:12}}><label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase"}}>Action Taken</label>
            <input value={form.action_taken} onChange={e=>setF("action_taken",e.target.value)} placeholder="e.g. 1-day detention, verbal warning issued..." style={inp14}/></div>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:14,flexWrap:"wrap"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,fontWeight:600,color:"#374151"}}>
              <input type="checkbox" checked={!!form.parent_informed} onChange={e=>setF("parent_informed",e.target.checked)} style={{width:16,height:16}}/>
              Parent / Guardian Informed
            </label>
            {form.parent_informed&&(
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:12,color:"#6b7280"}}>Date informed:</span>
                <input type="date" value={form.parent_informed_date} onChange={e=>setF("parent_informed_date",e.target.value)} style={{border:"1px solid #e5e7eb",borderRadius:7,padding:"5px 8px",fontSize:12}}/>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={save} disabled={saving} style={{background:saving?"#9ca3af":`linear-gradient(135deg,${G},#0d9488)`,color:"white",border:"none",borderRadius:10,padding:"9px 22px",fontWeight:700,fontSize:13,cursor:saving?"not-allowed":"pointer"}}>{saving?"Saving...":"💾 Save"}</button>
            <button onClick={()=>setShowForm(false)} style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:10,padding:"9px 18px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Analytics view ── */}
      {view==="analytics"&&!loading&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Type summary */}
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {CONDUCT_TYPES.map(t=>(
              <div key={t.id} style={{background:t.bg,border:`1.5px solid ${t.border}`,borderRadius:12,padding:"12px 18px",minWidth:110,textAlign:"center"}}>
                <div style={{fontSize:22}}>{t.icon}</div>
                <div style={{fontWeight:900,fontSize:20,color:t.color,lineHeight:1}}>{counts[t.id]||0}</div>
                <div style={{fontSize:11,color:t.color,fontWeight:700}}>{t.label}</div>
              </div>
            ))}
            <div style={{background:"#f9fafb",border:"1.5px solid #e5e7eb",borderRadius:12,padding:"12px 18px",minWidth:110,textAlign:"center"}}>
              <div style={{fontSize:22}}>🔓</div>
              <div style={{fontWeight:900,fontSize:20,color:"#d97706",lineHeight:1}}>{analytics.openCount}</div>
              <div style={{fontSize:11,color:"#6b7280",fontWeight:700}}>Open Cases</div>
            </div>
            <div style={{background:"#fef2f2",border:"1.5px solid #fecaca",borderRadius:12,padding:"12px 18px",minWidth:110,textAlign:"center"}}>
              <div style={{fontSize:22}}>📞</div>
              <div style={{fontWeight:900,fontSize:20,color:"#dc2626",lineHeight:1}}>{analytics.parentPending}</div>
              <div style={{fontSize:11,color:"#dc2626",fontWeight:700}}>Parent Pending</div>
            </div>
          </div>

          {/* Monthly trend */}
          <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
            <div style={{fontWeight:800,fontSize:13,color:"#111827",marginBottom:14}}>📈 Monthly Incident Trend (last 6 months)</div>
            <div style={{display:"flex",gap:6,alignItems:"flex-end",height:80}}>
              {analytics.months.map(m=>{
                const max=Math.max(1,...analytics.months.map(x=>x.count));
                const h=Math.max(4,Math.round(m.count/max*70));
                return(
                  <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#dc2626"}}>{m.count||""}</div>
                    <div style={{width:"100%",height:h,background:m.count>0?"#fca5a5":"#f3f4f6",borderRadius:"4px 4px 0 0",transition:"height .3s"}}/>
                    <div style={{fontSize:9,color:"#9ca3af",textAlign:"center"}}>{m.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
            {/* Class breakdown */}
            <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)",flex:2,minWidth:240}}>
              <div style={{fontWeight:800,fontSize:13,color:"#111827",marginBottom:12}}>🏫 Incidents by Class</div>
              {analytics.classesSorted.length===0&&<div style={{color:"#9ca3af",fontSize:13}}>No data yet.</div>}
              {analytics.classesSorted.map(([cls,typeCounts])=>{
                const incidents=(typeCounts.warning||0)+(typeCounts.incident||0)+(typeCounts.detention||0)+(typeCounts.suspension||0);
                const commend=typeCounts.commendation||0;
                const max=Math.max(1,...analytics.classesSorted.map(([,c])=>(c.warning||0)+(c.incident||0)+(c.detention||0)+(c.suspension||0)));
                return(
                  <div key={cls} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                      <span style={{fontWeight:700,color:"#374151"}}>{cls}</span>
                      <span style={{color:"#6b7280"}}>{incidents} incident{incidents!==1?"s":""}{commend>0?` · ${commend}⭐`:""}</span>
                    </div>
                    <div style={{background:"#f3f4f6",borderRadius:6,height:8,overflow:"hidden"}}>
                      <div style={{width:`${Math.round(incidents/max*100)}%`,height:"100%",background:incidents>5?"#f87171":incidents>2?"#fbbf24":"#34d399",borderRadius:6,transition:"width .3s"}}/>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Top students */}
            <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)",flex:1,minWidth:200}}>
              <div style={{fontWeight:800,fontSize:13,color:"#111827",marginBottom:12}}>⚠️ Needs Attention</div>
              {analytics.topStudents.length===0&&<div style={{color:"#9ca3af",fontSize:13}}>No incidents recorded.</div>}
              {analytics.topStudents.map(([name,count],i)=>(
                <div key={name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:i<analytics.topStudents.length-1?"1px solid #f3f4f6":"none"}}>
                  <button onClick={()=>{setProfileName(name);setView("log");}} style={{background:"none",border:"none",cursor:"pointer",fontWeight:700,fontSize:13,color:G,textDecoration:"underline",padding:0,textAlign:"left"}}>{name}</button>
                  <span style={{background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Log view ── */}
      {view==="log"&&(
        <>
          {/* Per-student profile panel */}
          {profileName&&(
            <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:"0 2px 12px rgba(0,0,0,0.08)",border:`2px solid ${GB}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div>
                  <div style={{fontWeight:800,fontSize:15,color:G}}>👤 {profileName}</div>
                  <div style={{fontSize:12,color:"#6b7280"}}>{profileRecords.length} record{profileRecords.length!==1?"s":""} · {profileRecords[0]?.class||"—"}</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  {CONDUCT_TYPES.map(t=>{const c=profileRecords.filter(r=>r.type===t.id).length;return c>0?(
                    <span key={t.id} style={{background:t.bg,color:t.color,border:`1px solid ${t.border}`,borderRadius:7,padding:"2px 8px",fontSize:11,fontWeight:700}}>{t.icon}{c}</span>
                  ):null;})}
                  <button onClick={()=>setProfileName(null)} style={{background:"#f3f4f6",color:"#374151",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✕ Close</button>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto"}}>
                {profileRecords.map(r=>{const t=CONDUCT_TYPES.find(x=>x.id===r.type)||CONDUCT_TYPES[1];return(
                  <div key={r.id} style={{background:t.bg,borderRadius:10,padding:"10px 14px",border:`1px solid ${t.border}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,flexWrap:"wrap",gap:6}}>
                      <span style={{background:t.border,color:t.color,borderRadius:5,padding:"1px 7px",fontSize:11,fontWeight:700}}>{t.icon} {t.label}</span>
                      <span style={{fontSize:11,color:"#6b7280"}}>{r.date}</span>
                    </div>
                    <div style={{fontSize:12,color:"#374151"}}>{r.description}</div>
                    {r.action_taken&&<div style={{fontSize:11,color:"#6b7280",fontStyle:"italic",marginTop:3}}>Action: {r.action_taken}</div>}
                    <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap"}}>
                      {r.status&&<span style={{background:STATUS_META[r.status]?.bg||"#f9fafb",color:STATUS_META[r.status]?.col||"#6b7280",borderRadius:5,padding:"1px 7px",fontSize:10,fontWeight:700}}>{STATUS_META[r.status]?.label||r.status}</span>}
                      {r.parent_informed?<span style={{fontSize:10,color:"#16a34a",fontWeight:700}}>✅ Parent informed {r.parent_informed_date||""}</span>:<span style={{fontSize:10,color:"#dc2626",fontWeight:700}}>📞 Parent not informed</span>}
                    </div>
                  </div>
                );})}
              </div>
            </div>
          )}

          {/* Type filter chips */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {CONDUCT_TYPES.map(t=>(
              <button key={t.id} onClick={()=>setFilterType(filterType===t.id?"all":t.id)}
                style={{background:filterType===t.id?t.bg:"white",border:`1.5px solid ${filterType===t.id?t.border:"#e5e7eb"}`,borderRadius:10,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer",color:filterType===t.id?t.color:"#374151",display:"flex",alignItems:"center",gap:6}}>
                {t.icon} {t.label} <span style={{background:filterType===t.id?t.border:"#f3f4f6",borderRadius:6,padding:"1px 7px",fontSize:11}}>{counts[t.id]||0}</span>
              </button>
            ))}
          </div>

          {/* Search + filters */}
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="🔍 Search student..." style={{flex:1,minWidth:160,border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13}}/>
            <select value={filterClass} onChange={e=>setFilterClass(e.target.value)} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13}}>
              {allClasses.map(c=><option key={c}>{c}</option>)}
            </select>
            <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13}}>
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="escalated">Escalated</option>
            </select>
          </div>

          {/* Records list */}
          {loading?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Loading...</div>
          :filtered.length===0?<div style={{background:"white",borderRadius:14,padding:48,textAlign:"center",color:"#9ca3af",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}><div style={{fontSize:36,marginBottom:8}}>📋</div><div style={{fontWeight:700}}>No records found</div></div>
          :<div style={{display:"flex",flexDirection:"column",gap:10}}>{filtered.map(r=>{
            const t = CONDUCT_TYPES.find(x=>x.id===r.type)||CONDUCT_TYPES[1];
            const sm = STATUS_META[r.status]||STATUS_META.open;
            return(
              <div key={r.id} style={{background:"white",borderRadius:14,boxShadow:"0 2px 8px rgba(0,0,0,0.04)",border:`1.5px solid ${t.border}`,overflow:"hidden"}}>
                <div style={{padding:"14px 18px",display:"flex",gap:14,alignItems:"flex-start"}}>
                  <div style={{width:40,height:40,borderRadius:12,background:t.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{t.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                      <button onClick={()=>setProfileName(r.student_name)} style={{background:"none",border:"none",cursor:"pointer",fontWeight:800,fontSize:13,color:G,padding:0,textDecoration:"underline"}}>{r.student_name}</button>
                      <span style={{background:t.bg,color:t.color,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{t.label}</span>
                      <span style={{fontSize:11,color:"#9ca3af"}}>{r.class}</span>
                      <span style={{fontSize:11,color:"#9ca3af",marginLeft:"auto"}}>{r.date}</span>
                    </div>
                    <div style={{fontSize:13,color:"#374151",marginBottom:6}}>{r.description}</div>
                    {r.action_taken&&<div style={{fontSize:11,color:"#6b7280",fontStyle:"italic",marginBottom:6}}>Action: {r.action_taken}</div>}

                    {/* Status + Parent row */}
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      {/* Status picker */}
                      <div style={{display:"flex",gap:4}}>
                        {["open","resolved","escalated"].map(s=>(
                          <button key={s} onClick={()=>changeStatus(r,s)}
                            style={{background:r.status===s?STATUS_META[s].bg:"#f9fafb",color:r.status===s?STATUS_META[s].col:"#9ca3af",border:`1px solid ${r.status===s?STATUS_META[s].col+"44":"#e5e7eb"}`,borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                            {STATUS_META[s].label}
                          </button>
                        ))}
                      </div>
                      {/* Parent informed toggle */}
                      {r.type!=="commendation"&&(
                        <button onClick={()=>toggleParentInformed(r)}
                          style={{background:r.parent_informed?"#f0fdf4":"#fef2f2",color:r.parent_informed?"#16a34a":"#dc2626",border:`1px solid ${r.parent_informed?"#bbf7d0":"#fecaca"}`,borderRadius:6,padding:"2px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>
                          {r.parent_informed?`✅ Parent informed ${r.parent_informed_date||""}`.trim():"📞 Parent not informed"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Delete button */}
                  <button onClick={()=>setConfirmDel(r.id)} style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>🗑</button>
                </div>

                {/* Inline delete confirm */}
                {confirmDel===r.id&&(
                  <div style={{margin:"0 18px 14px",background:"#fef2f2",borderRadius:9,padding:"10px 14px",border:"1px solid #fecaca"}}>
                    <div style={{fontSize:13,color:"#111827",marginBottom:8,fontWeight:600}}>Delete this conduct record? This cannot be undone.</div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>deleteRecord(r.id)} style={{background:"#dc2626",color:"white",border:"none",borderRadius:7,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🗑️ Delete</button>
                      <button onClick={()=>setConfirmDel(null)} style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}</div>}
        </>
      )}
    </div>
  );
}

// ── HOMEWORK TRACKER ──────────────────────────────────────────────
function HomeworkTracker({ students=[] }) {
  const G = "#0d9488", GP = "#f0fdfa", GB = "#ccfbf1";
  const S = useSettings();
  const allClasses = ["All",...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort();
  const [assignments, setAssignments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [filterClass, setFilterClass] = React.useState("All");
  const [filterStatus, setFilterStatus] = React.useState("upcoming");
  const [filterSubject, setFilterSubject] = React.useState("All");
  const [showForm, setShowForm] = React.useState(false);
  const [view, setView] = React.useState("list"); // list | logs
  const today = new Date(); today.setHours(0,0,0,0);
  const todayStr = today.toISOString().split("T")[0];
  const [logs, setLogs] = React.useState([]); // submission logs
  const [submissions, setSubmissions] = React.useState([]); // homework_submissions
  const [submissionsPanel, setSubmissionsPanel] = React.useState(null); // assignment id showing checklist
  const emptyForm = {title:"",subject:"",class:"all",description:"",date:todayStr,notify_parents:true};
  const [form, setForm] = React.useState(emptyForm);

  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3000);};

  React.useEffect(()=>{
    supabase.from("school_events").select("*").eq("event_type","assignment").order("date",{ascending:true}).then(({data})=>{setAssignments(data||[]);setLoading(false);});
    supabase.from("homework_logs").select("*").order("created_at",{ascending:false}).limit(50).then(({data})=>{if(data)setLogs(data);}).catch(()=>{});
    supabase.from("homework_submissions").select("*").then(({data})=>{if(data)setSubmissions(data||[]);}).catch(()=>{});
  },[]);

  const subjects = ["All",...new Set(assignments.map(a=>a.subject).filter(Boolean))].sort();

  const markDone=async(id,done)=>{
    const{error}=await supabase.from("school_events").update({is_done:!done}).eq("id",id);
    if(!error) setAssignments(p=>p.map(a=>a.id===id?{...a,is_done:!done}:a));
  };

  const deleteAssignment=async(id)=>{
    if(!window.confirm("Delete this assignment?")) return;
    await supabase.from("school_events").delete().eq("id",id);
    setAssignments(p=>p.filter(a=>a.id!==id));
    showToast("Assignment deleted");
  };

  const saveAssignment=async()=>{
    if(!form.title||!form.date) return showToast("Title and due date required",false);
    setSaving(true);
    const payload = {
      title:form.title, subject:form.subject, class:form.class,
      description:form.description, date:form.date,
      event_type:"assignment", is_done:false,
    };
    const {data,error} = await supabase.from("school_events").insert(payload).select();
    if(error){showToast("Error: "+error.message,false);}
    else {
      const newA = data[0];
      setAssignments(p=>[...p,newA]);
      // Log the creation
      const logEntry = {
        action:"created", assignment_id:newA.id,
        title:form.title, subject:form.subject, class:form.class,
        date:form.date, created_at:new Date().toISOString()
      };
      supabase.from("homework_logs").insert(logEntry).then(({data:ld})=>{if(ld)setLogs(p=>[ld[0],...p]);}).catch(()=>{});
      showToast("Assignment created!"+(form.notify_parents?" · Parents notified":""));
        if(form.notify_parents && data?.[0]) {
          const notifBody = ((form.subject?"["+form.subject+"] ":"")+form.description+" — Due: "+form.date).trim().slice(0,120);
          supabase.from("notifications").insert({
            title: "📚 Homework: " + form.title,
            body: notifBody,
            type: "homework",
            target_role: "parents",
            target_class: form.class==="all" ? null : form.class,
            source_id: data[0].id,
            source_type: "school_events",
            created_by: "Admin",
          }).then(()=>{});
        }
      setShowForm(false);
      setForm(emptyForm);
    }
    setSaving(false);
  };

  const filtered=assignments.filter(a=>{
    if(filterClass!=="All"&&a.class!=="all"&&a.class!==filterClass) return false;
    if(filterSubject!=="All"&&a.subject!==filterSubject) return false;
    const d=new Date(a.date); d.setHours(0,0,0,0);
    if(filterStatus==="upcoming"&&d<today) return false;
    if(filterStatus==="past"&&d>=today) return false;
    if(filterStatus==="done"&&!a.is_done) return false;
    return true;
  });

  const daysLeft=ds=>{const d=new Date(ds);d.setHours(0,0,0,0);return Math.round((d-today)/(1000*60*60*24));};
  const dlColor=n=>n<0?"#9ca3af":n===0?"#dc2626":n<=2?"#ea580c":n<=5?"#d97706":G;
  const dlLabel=n=>n<0?"Past due":n===0?"DUE TODAY":n===1?"Tomorrow":`${n}d left`;

  const priorityInfo = p=>({
    high:{label:"High Priority",color:"#dc2626",bg:"#fef2f2",border:"#fecaca"},
    medium:{label:"Medium",color:"#d97706",bg:"#fffbeb",border:"#fde68a"},
    low:{label:"Low",color:"#0d9488",bg:"#f0fdfa",border:"#ccfbf1"},
  }[p]||{label:"Medium",color:"#d97706",bg:"#fffbeb",border:"#fde68a"});

  const matchSub = (s, assignmentId, studentId) =>
    s.assignment_id === assignmentId && String(s.student_id) === String(studentId);

  const toggleSubmission = async (assignmentId, studentId, currentlySubmitted) => {
    const now = new Date().toISOString();
    const newVal = !currentlySubmitted;
    const sid = String(studentId);
    const { error } = await supabase.from("homework_submissions").upsert(
      { assignment_id: assignmentId, student_id: sid, submitted: newVal, marked_at: newVal ? now : null },
      { onConflict: "assignment_id,student_id" }
    );
    if(error) { showToast("Error: " + error.message, false); return; }
    setSubmissions(prev => {
      const exists = prev.find(s => matchSub(s, assignmentId, sid));
      if(exists) return prev.map(s => matchSub(s, assignmentId, sid) ? {...s, submitted:newVal, marked_at:newVal?now:null} : s);
      return [...prev, {assignment_id:assignmentId, student_id:sid, submitted:newVal, marked_at:newVal?now:null}];
    });
  };

  const markAllSubmitted = async (assignment, stuList) => {
    const now = new Date().toISOString();
    const rows = stuList.map(s => ({assignment_id:assignment.id, student_id:String(s.id), submitted:true, marked_at:now}));
    const { error } = await supabase.from("homework_submissions").upsert(rows, { onConflict: "assignment_id,student_id" });
    if(error) { showToast("Error: " + error.message, false); return; }
    setSubmissions(prev => {
      const updated = [...prev];
      stuList.forEach(s => {
        const sid = String(s.id);
        const idx = updated.findIndex(x => matchSub(x, assignment.id, sid));
        if(idx>=0) updated[idx] = {...updated[idx], submitted:true, marked_at:now};
        else updated.push({assignment_id:assignment.id, student_id:sid, submitted:true, marked_at:now});
      });
      return updated;
    });
  };

  const upcoming = assignments.filter(a=>!a.is_done&&new Date(a.date)>=today);
  const overdue  = assignments.filter(a=>!a.is_done&&new Date(a.date)<today);
  const done     = assignments.filter(a=>a.is_done);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16,padding:4}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Header */}
      <div style={{background:"white",borderRadius:16,padding:"18px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontWeight:900,fontSize:17,color:"#111827"}}>📚 Homework & Assignment Manager</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>Create, assign, track and log homework. Linked to Events Calendar for notifications.</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setView(v=>v==="list"?"logs":"list")}
            style={{background:view==="logs"?GP:"#f3f4f6",color:view==="logs"?G:"#374151",border:`1.5px solid ${view==="logs"?GB:"#e5e7eb"}`,borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            {view==="logs"?"📋 Assignments":"📜 Activity Log"}
          </button>
          <button onClick={()=>setShowForm(v=>!v)}
            style={{background:showForm?"#f3f4f6":`linear-gradient(135deg,${G},#0a7a6a)`,color:showForm?"#374151":"white",border:showForm?"1.5px solid #e5e7eb":"none",borderRadius:10,padding:"8px 18px",fontWeight:700,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,boxShadow:showForm?"none":"0 4px 12px rgba(13,148,136,0.3)"}}>
            {showForm?"✕ Cancel":"➕ New Assignment"}
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[
          {icon:"📋",label:"Upcoming",val:upcoming.length,color:G,bg:GP,border:GB,filter:"upcoming"},
          {icon:"🔴",label:"Overdue",val:overdue.length,color:"#dc2626",bg:"#fef2f2",border:"#fecaca",filter:"past"},
          {icon:"✅",label:"Completed",val:done.length,color:"#16a34a",bg:"#f0fdf4",border:"#bbf7d0",filter:"done"},
        ].map(c=>(
          <div key={c.label} onClick={()=>setFilterStatus(c.filter)} style={{background:c.bg,border:`1.5px solid ${c.border}`,borderRadius:12,padding:"12px 16px",cursor:"pointer",transition:"transform .15s"}}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.02)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
            <div style={{fontSize:20,marginBottom:4}}>{c.icon}</div>
            <div style={{fontSize:22,fontWeight:900,color:c.color}}>{c.val}</div>
            <div style={{fontSize:11,color:c.color,fontWeight:600}}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Create Form */}
      {showForm&&(
        <div style={{background:"white",borderRadius:16,padding:24,boxShadow:"0 4px 20px rgba(13,148,136,0.12)",border:`2px solid ${GB}`}}>
          <div style={{fontWeight:800,color:G,fontSize:15,marginBottom:18,display:"flex",alignItems:"center",gap:8}}>
            ✏️ Create New Assignment
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:14}}>
            {[
              {l:"Title *",k:"title",type:"text",ph:"e.g. Chapter 5 Questions"},
              {l:"Subject",k:"subject",type:"text",ph:"e.g. Mathematics"},
            ].map(({l,k,type,ph})=>(
              <div key={k}>
                <label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>{l}</label>
                <input type={type} value={form[k]||""} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} placeholder={ph||""}
                  style={{width:"100%",border:"1.5px solid #e5e7eb",borderRadius:9,padding:"9px 12px",fontSize:13,boxSizing:"border-box",outline:"none",transition:"border .2s"}}
                  onFocus={e=>e.target.style.borderColor=G} onBlur={e=>e.target.style.borderColor="#e5e7eb"}/>
              </div>
            ))}
            <div>
              <label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Class / Section</label>
              <select value={form.class} onChange={e=>setForm(p=>({...p,class:e.target.value}))}
                style={{width:"100%",border:"1.5px solid #e5e7eb",borderRadius:9,padding:"9px 12px",fontSize:13,boxSizing:"border-box"}}>
                <option value="all">All Classes</option>
                {allClasses.filter(c=>c!=="All").map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Due Date *</label>
              <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}
                style={{width:"100%",border:"1.5px solid #e5e7eb",borderRadius:9,padding:"9px 12px",fontSize:13,boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Instructions / Description</label>
            <textarea value={form.description||""} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3}
              placeholder="What should students do? Include page numbers, questions, materials needed..."
              style={{width:"100%",border:"1.5px solid #e5e7eb",borderRadius:9,padding:"9px 12px",fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none"}}
              onFocus={e=>e.target.style.borderColor=G} onBlur={e=>e.target.style.borderColor="#e5e7eb"}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:"#374151",fontWeight:600}}>
              <input type="checkbox" checked={form.notify_parents} onChange={e=>setForm(p=>({...p,notify_parents:e.target.checked}))}
                style={{width:16,height:16,accentColor:G}}/>
              📢 Notify parents via Calendar Event (auto-linked)
            </label>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={saveAssignment} disabled={saving}
              style={{background:saving?"#9ca3af":`linear-gradient(135deg,${G},#0a7a6a)`,color:"white",border:"none",borderRadius:10,padding:"10px 24px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",boxShadow:"0 4px 12px rgba(13,148,136,0.3)"}}>
              {saving?"⏳ Saving...":"✅ Create Assignment"}
            </button>
            <button onClick={()=>{setShowForm(false);setForm(emptyForm);}}
              style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:10,padding:"10px 18px",fontWeight:600,fontSize:13,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Activity Log View */}
      {view==="logs"&&(
        <div style={{background:"white",borderRadius:14,padding:"18px 24px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
          <div style={{fontWeight:800,fontSize:14,color:"#111827",marginBottom:14}}>📜 Activity Log</div>
          {logs.length===0
            ?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>No activity logged yet. Create assignments to see logs here.</div>
            :<div style={{display:"flex",flexDirection:"column",gap:8}}>
              {logs.map((l,i)=>(
                <div key={l.id||i} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                  <div style={{width:32,height:32,borderRadius:10,background:GP,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                    {l.action==="created"?"➕":l.action==="deleted"?"🗑":"✏️"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{l.title}</div>
                    <div style={{fontSize:11,color:"#6b7280"}}>{l.subject} · {l.class==="all"?"All Classes":l.class} · Due: {l.date}</div>
                  </div>
                  <div style={{fontSize:10,color:"#9ca3af",flexShrink:0,whiteSpace:"nowrap"}}>
                    {l.created_at?new Date(l.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"}):""}
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}

      {/* List View */}
      {view==="list"&&(<>
        {/* Filters */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{display:"flex",background:"white",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
            {[["upcoming","📋 Upcoming"],["past","📂 Past"],["done","✅ Done"],["all","🗂 All"]].map(([v,l])=>(
              <button key={v} onClick={()=>setFilterStatus(v)} style={{padding:"8px 14px",border:"none",fontWeight:700,fontSize:12,cursor:"pointer",background:filterStatus===v?G:"transparent",color:filterStatus===v?"white":"#374151",transition:"all .15s"}}>{l}</button>
            ))}
          </div>
          <select value={filterClass} onChange={e=>setFilterClass(e.target.value)} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13}}>
            {allClasses.map(c=><option key={c}>{c}</option>)}
          </select>
          <select value={filterSubject} onChange={e=>setFilterSubject(e.target.value)} style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13}}>
            {subjects.map(c=><option key={c}>{c}</option>)}
          </select>
          <div style={{marginLeft:"auto",fontSize:12,color:"#6b7280",fontWeight:600}}>{filtered.length} assignments</div>
        </div>

        {loading
          ?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Loading...</div>
          :filtered.length===0
            ?<div style={{background:"white",borderRadius:14,padding:48,textAlign:"center",color:"#9ca3af",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
              <div style={{fontSize:36,marginBottom:8}}>📚</div>
              <div style={{fontWeight:700,fontSize:14}}>No assignments found</div>
              <div style={{fontSize:12,marginTop:4}}>Create assignments using the ➕ button above</div>
            </div>
            :<div style={{display:"flex",flexDirection:"column",gap:10}}>
              {filtered.map(a=>{
                const days=daysLeft(a.date); const col=dlColor(days); const done=a.is_done;
                const classStudents = students.filter(s=>a.class==="all"||(s.class||s["class"])===a.class);
                const submittedCount = submissions.filter(s=>s.assignment_id===a.id&&s.submitted).length;
                const allDone = classStudents.length>0 && submittedCount===classStudents.length;
                const panelOpen = submissionsPanel===a.id;
                return(
                  <React.Fragment key={a.id}>
                    <div style={{background:"white",borderRadius:panelOpen?"14px 14px 0 0":14,padding:"14px 18px",boxShadow:"0 2px 8px rgba(0,0,0,0.04)",border:`1.5px solid ${done?"#bbf7d0":days<0?"#fecaca":days===0?"#fecaca":"#e5e7eb"}`,opacity:done?.7:1,borderBottom:panelOpen?"none":""}}>
                      <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                        <button onClick={()=>markDone(a.id,done)} style={{width:24,height:24,borderRadius:6,border:`2px solid ${done?"#16a34a":col}`,background:done?"#16a34a":"transparent",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",marginTop:2}}>
                          {done&&<span style={{color:"white",fontSize:14,fontWeight:900}}>✓</span>}
                        </button>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                            <span style={{fontWeight:800,fontSize:13,color:done?"#9ca3af":"#111827",textDecoration:done?"line-through":"none"}}>{a.title}</span>
                            <span style={{background:"#f0fdfa",color:G,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{a.class==="all"?"All Classes":a.class}</span>
                            {a.subject&&<span style={{background:"#eff6ff",color:"#2563eb",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{a.subject}</span>}
                            {a.notify_parents&&<span style={{fontSize:10,color:"#7c3aed",fontWeight:600}}>📢 Parents notified</span>}
                          </div>
                          {a.description&&<div style={{fontSize:12,color:"#6b7280",marginBottom:4,lineHeight:1.5}}>{a.description}</div>}
                          <div style={{fontSize:11,color:"#9ca3af"}}>📅 Due: {new Date(a.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</div>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                          <span style={{background:col+"18",color:col,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:800,whiteSpace:"nowrap"}}>{done?"✅ Done":dlLabel(days)}</span>
                          <button onClick={()=>deleteAssignment(a.id)}
                            style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                            🗑 Delete
                          </button>
                          <button onClick={()=>setSubmissionsPanel(panelOpen?null:a.id)}
                            style={{background:panelOpen?"#eff6ff":"#f0fdf4",color:panelOpen?"#2563eb":G,border:`1.5px solid ${panelOpen?"#bfdbfe":GB}`,borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                            📋 {submittedCount}/{classStudents.length} Submitted {panelOpen?"▲":"▼"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Submissions checklist panel ── */}
                    {panelOpen&&(
                      <div style={{background:"#f0f9ff",border:"1.5px solid #bfdbfe",borderTop:"none",borderRadius:"0 0 14px 14px",padding:"14px 18px",marginBottom:2}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                          <div style={{fontWeight:700,fontSize:13,color:"#1d4ed8"}}>
                            📋 Submission Checklist — <span style={{color:allDone?"#16a34a":"#1d4ed8"}}>{submittedCount}/{classStudents.length} submitted</span>
                          </div>
                          <button onClick={()=>markAllSubmitted(a, classStudents)} disabled={allDone}
                            style={{background:allDone?"#dcfce7":"#2563eb",color:allDone?"#16a34a":"white",border:"none",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:allDone?"default":"pointer",opacity:allDone?.8:1}}>
                            {allDone?"✅ All Submitted":"✔ Mark All Submitted"}
                          </button>
                        </div>
                        {classStudents.length===0?(
                          <div style={{textAlign:"center",padding:16,color:"#9ca3af",fontSize:12}}>No students found for this class.</div>
                        ):(
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))",gap:8}}>
                            {classStudents.map(s=>{
                              const sub = submissions.find(x=>x.assignment_id===a.id&&String(x.student_id)===String(s.id));
                              const submitted = sub?.submitted??false;
                              return(
                                <div key={s.id} onClick={()=>toggleSubmission(a.id,s.id,submitted)}
                                  style={{display:"flex",alignItems:"center",gap:10,background:submitted?"#dcfce7":"white",
                                    border:`1.5px solid ${submitted?"#86efac":"#e5e7eb"}`,borderRadius:9,
                                    padding:"8px 12px",cursor:"pointer",transition:"all .15s",userSelect:"none"}}
                                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.08)"}
                                  onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                                  <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${submitted?"#16a34a":"#d1d5db"}`,
                                    background:submitted?"#16a34a":"transparent",display:"flex",alignItems:"center",
                                    justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                                    {submitted&&<span style={{color:"white",fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
                                  </div>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontWeight:700,fontSize:12,color:submitted?"#15803d":"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                      {s.full_name||s.name}
                                    </div>
                                    {sub?.marked_at&&<div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>
                                      {new Date(sub.marked_at).toLocaleString("en-GB",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                                    </div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
        }
      </>)}
    </div>
  );
}

const TABS = [
  {id:"dashboard",  label:"Dashboard",  icon:"🏠"},
  {id:"admissions",  label:"Admissions (Online)", icon:"📩"},
  {id:"register",   label:"Register",   icon:"📋"},
  {id:"scholarship",label:"Scholarship",icon:"🎓"},
  {id:"attendance", label:"Attendance", icon:"📅"},
  {id:"students",   label:"Records",    icon:"👥"},
  {id:"leave",      label:"Leave Requests", icon:"📝"},
  {id:"timeline",   label:"Timeline",   icon:"📜"},
  {id:"fees",       label:"Fees",       icon:"💰"},
  {id:"results",    label:"Results",    icon:"📊"},
  {id:"promotion",  label:"Promotion",  icon:"🎓"},
  {id:"hifz",       label:"Hifz",       icon:"📖"},
  {id:"staff",      label:"Staff",      icon:"👨‍🏫"},
  {id:"timetable",  label:"Timetable",  icon:"🗓️"},
  {id:"calendar",   label:"Events",     icon:"📅"},
  {id:"homework",   label:"Homework",   icon:"📚"},
  {id:"conduct",    label:"Conduct",    icon:"📋"},
  {id:"reports",      label:"Reports",        icon:"📋"},
  {id:"achievements", label:"Achievements",   icon:"🏆"},
  {id:"analytics",    label:"Analytics",      icon:"📊"},
  {id:"ai",           label:"AI Reports",        icon:"🤖"},
  {id:"insights",    label:"Reports & Analytics",icon:"📊"},
  {id:"communications",label:"Communications",  icon:"📢"},
  {id:"parents",    label:"Portal Access", icon:"🔐"},
  {id:"announcements", label:"Announcements", icon:"📢"},
  {id:"settings",   label:"Settings",   icon:"⚙️"},
  {id:"export",     label:"Export Data", icon:"📥"},
];

// ── DASHBOARD ──────────────────────────────────────────────────────
const MONTHS_LIST = ["January","February","March","April","May","June","July","August","September","October","November","December"];

class DashboardErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  componentDidCatch(error) { this.setState({ error: error.message }); }
  render() {
    if (this.state.error) return (
      <div style={{padding:40,background:"#fff0f0",borderRadius:12,margin:20,border:"1px solid #fca5a5"}}>
        <div style={{fontWeight:800,color:"#dc2626",fontSize:16,marginBottom:8}}>⚠️ Dashboard Error</div>
        <div style={{fontFamily:"monospace",fontSize:13,color:"#7f1d1d",background:"#fee2e2",padding:12,borderRadius:8}}>{this.state.error}</div>
        <div style={{marginTop:12,fontSize:12,color:"#888"}}>Check browser console (F12) for full details.</div>
      </div>
    );
    return this.props.children;
  }
}

// ── DRILL DOWN MODAL ──────────────────────────────────────────────
function DrillDownModal({ title, subtitle, students, onClose }) {
  const S = useSettings();
  if(!students || students.length === 0) return null;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={onClose}>
      <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:560,maxHeight:"80vh",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",display:"flex",flexDirection:"column"}}
        onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{background:`linear-gradient(135deg,#1a6b3c,#2d9e5f)`,padding:"18px 24px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:"white",fontWeight:800,fontSize:16}}>{title}</div>
            {subtitle && <div style={{color:"rgba(255,255,255,0.8)",fontSize:12,marginTop:2}}>{subtitle}</div>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{background:"rgba(255,255,255,0.2)",color:"white",borderRadius:8,padding:"3px 10px",fontSize:12,fontWeight:700}}>{students.length} students</span>
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:32,height:32,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
          </div>
        </div>
        {/* Student list */}
        <div style={{overflowY:"auto",flex:1}}>
          {students.map((s,i)=>(
            <div key={s.id||i} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 20px",borderBottom:"1px solid #e8f5ee",background:i%2===0?"white":"#fafdf8"}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#1a6b3c,#2d9e5f)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:14,flexShrink:0}}>
                {(s.name||"?")[0]}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,color:"#1e2d1e",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.name}</div>
                <div style={{fontSize:11,color:"#5a7a5a"}}>{s.class||s["class"]} {s.gender?`• ${s.gender}`:""}</div>
              </div>
              {/* Scholarship tag */}
              {s.scholarship_tag && (
                <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"#f5f3ff",color:"#7c3aed",border:"1px solid #e9d5ff",flexShrink:0}}>
                  {s.scholarship_tag}
                </span>
              )}
              {/* Boarding tag */}
              {s.boarding_tag && (
                <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
                  background:s.boarding_tag.includes("Boarding")?"#e0f7f5":"#f0fdf4",
                  color:s.boarding_tag.includes("Boarding")?"#0d9488":"#16a34a",
                  border:`1px solid ${s.boarding_tag.includes("Boarding")?"#99f6e4":"#bbf7d0"}`,flexShrink:0}}>
                  {s.boarding_tag}
                </span>
              )}
              {/* Attendance % */}
              {s.pct!==undefined && !s.present_days && (
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:16,fontWeight:900,color:s.pct>=(Number(S?.att_threshold)||85)?"#1a6b3c":s.pct>=70?"#f0a500":"#e11d48"}}>{s.pct}%</div>
                  <div style={{fontSize:10,color:"#5a7a5a"}}>attendance</div>
                </div>
              )}
              {/* Present / Absent breakdown */}
              {s.present_days!==undefined && (
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:15,fontWeight:900,color:s.pct>=(Number(S?.att_threshold)||85)?"#1a6b3c":s.pct>=70?"#f0a500":"#e11d48"}}>{s.pct}%</div>
                  <div style={{display:"flex",gap:6,marginTop:3}}>
                    <span style={{fontSize:10,fontWeight:700,color:"#1a6b3c",background:"#e8f5ee",borderRadius:4,padding:"1px 5px"}}>✅ {s.present_days}P</span>
                    <span style={{fontSize:10,fontWeight:700,color:"#e11d48",background:"#fdf0ee",borderRadius:4,padding:"1px 5px"}}>❌ {s.absent_days}A</span>
                    <span style={{fontSize:10,color:"#5a7a5a",borderRadius:4,padding:"1px 5px"}}>{s.total_days}d</span>
                  </div>
                </div>
              )}
              {/* Fee method */}
              {s.fees!==undefined && !s.scholarship_tag && (
                <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:6,
                  background:s.fees==="Paid"||s.fees==="Cash"?"#e8f5ee":s.fees==="Scholarship"?"#f5f3ff":s.fees==="Full Scholarship"?"#fef3c7":"#fdf0ee",
                  color:s.fees==="Paid"||s.fees==="Cash"?"#1a6b3c":s.fees==="Scholarship"?"#7c3aed":s.fees==="Full Scholarship"?"#d97706":"#e11d48"}}>
                  {s.fees==="Cash"?"✅ Paid":s.fees==="Unpaid"?"❌ Unpaid":s.fees}
                </span>
              )}
              {/* Amount */}
              {s.amount!==undefined && (
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:13,fontWeight:800,color:"#1a6b3c"}}>PKR {Number(s.amount).toLocaleString()}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── DASHBOARD RESULTS WIDGET ────────────────────────────────────
const DashboardResultsWidget = React.memo(function DashboardResultsWidget({ students, attendance, fees=[] }) {
  const settings = useSettings();
  const [dashResults, setDashResults] = React.useState([]);
  const [dashExams,   setDashExams]   = React.useState([]);
  const [rcStudent,   setRcStudent]   = React.useState(null);
  const [rcType,      setRcType]      = React.useState("comprehensive");
  const [rcMonth,     setRcMonth]     = React.useState("");
  const [selMonth,    setSelMonth]    = React.useState("");

  React.useEffect(()=>{
    supabase.from("results").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ if(data) setDashResults(data); });
    supabase.from("exams").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ if(data) setDashExams(data); });
  },[]);

  const GRADES=[{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}];
  const getGrade=(m,t)=>{const p=(m/t)*100;return GRADES.find(g=>p>=g.min)?.label||"F";};
  const gradeColor=g=>({
    "A+":"#1a6b3c","A":"#1a6b3c","B+":"#0d9488","B":"#0d9488",
    "C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");

  if(!dashResults.length) return (
    <div className="fade-up" style={{animationDelay:".75s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
      <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:8}}>{"🏆"} Results Overview</div>
      <div style={{color:C.muted,fontSize:13,textAlign:"center",padding:"28px 0"}}>No exam results yet — add results to see top performers here.</div>
    </div>
  );

  const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];

  const rankPool=selMonth
    ? dashResults.filter(r=>{
        if(selMonth==="midterm") return (r.exam_name||"").toLowerCase().includes("midterm");
        if(selMonth==="final")   return (r.exam_name||"").toLowerCase().includes("final");
        const full=MONTHS.find(m=>m.slice(0,3)===selMonth)||selMonth;
        return (r.exam_name||"").includes(full);
      })
    : dashResults;

  let topScorers;
  if(!selMonth){
    const byStudent={};
    rankPool.forEach(r=>{
      if(!byStudent[r.student_id]) byStudent[r.student_id]={...r,_totalPct:0,_count:0};
      if(r.total_marks>0){byStudent[r.student_id]._totalPct+=(r.marks_obtained/r.total_marks)*100;byStudent[r.student_id]._count+=1;}
    });
    topScorers=Object.values(byStudent)
      .map(s=>{
        const pct=s._count>0?Math.round(s._totalPct/s._count):0;
        return {...s,pct,exam_name:`${s._count} exam${s._count!==1?"s":""}`,marks_obtained:pct,total_marks:100};
      })
      .sort((a,b)=>b.pct-a.pct).slice(0,6);
  } else {
    const latestByStudent={};
    rankPool.forEach(r=>{
      if(!latestByStudent[r.student_id]||new Date(r.created_at)>new Date(latestByStudent[r.student_id].created_at))
        latestByStudent[r.student_id]=r;
    });
    topScorers=Object.values(latestByStudent)
      .map(r=>({...r,pct:r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0}))
      .sort((a,b)=>b.pct-a.pct).slice(0,6);
  }
  const monthlyAvg=MONTHS.map(m=>{
    const mr=dashResults.filter(r=>r.exam_name?.includes(m));
    const avg=mr.length?Math.round(mr.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/mr.length):0;
    return {name:m.slice(0,3),avg};
  }).filter(m=>m.avg>0);

  const medals=["🥇","🥈","🥉"];

  return (
    <>
      {rcStudent && (
        <ReportCardModal
          student={rcStudent}
          results={dashResults}
          attendance={attendance}
          reportType={rcType}
          monthLabel={rcType==="monthly"?rcMonth:null}
          onClose={()=>{setRcStudent(null);setRcType("comprehensive");setRcMonth("");}}/>
      )}
      <div className="fade-up" style={{animationDelay:".75s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontWeight:800,color:C.primary,fontSize:14}}>🏆 Top Scorers</div>
            <div style={{fontSize:11,color:C.muted,marginTop:2}}>{dashResults.length} results · Click a student to open Report Card</div>
          </div>
          <span style={{fontSize:11,fontWeight:700,background:C.primaryPale,color:C.primary,borderRadius:8,padding:"5px 12px",border:`1px solid ${C.primaryMid}`}}>Live</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,background:C.bg,borderRadius:10,padding:"10px 14px",marginBottom:16,flexWrap:"wrap"}}>
          <span style={{fontSize:12,fontWeight:700,color:C.muted}}>📊 Exam Period:</span>
          {[
            {val:"",    label:"All (Avg)"},
            ...monthlyAvg.map(m=>({val:m.name, label:`${m.name} Monthly`})),
            ...(dashExams.some(e=>e.name?.toLowerCase().includes("midterm"))?[{val:"midterm",label:"📋 Midterm"}]:[]),
            ...(dashExams.some(e=>e.name?.toLowerCase().includes("final"))  ?[{val:"final",  label:"🎓 Final"}]:[]),
          ].map(opt=>(
            <button key={opt.val} onClick={()=>setSelMonth(opt.val)}
              style={{padding:"4px 12px",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",border:`1.5px solid ${selMonth===opt.val?C.primary:C.border}`,background:selMonth===opt.val?C.primaryPale:"white",color:selMonth===opt.val?C.primary:C.muted}}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:16}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Top Scorers</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {topScorers.map((r,i)=>{
                const g=r.grade||getGrade(r.marks_obtained,r.total_marks);
                const s=students.find(st=>st.id===r.student_id||st.name===r.student_name);
                return (
                  <div key={r.id||i} onClick={()=>{
                      if(!s) return;
                      if(selMonth==="midterm")      { setRcType("midterm");       setRcMonth(""); }
                      else if(selMonth==="final")   { setRcType("final");         setRcMonth(""); }
                      else if(selMonth)             { setRcType("monthly");       setRcMonth(MONTHS.find(m=>m.slice(0,3)===selMonth)||selMonth); }
                      else                          { setRcType("comprehensive"); setRcMonth(""); }
                      setRcStudent(s);
                    }}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:i===0?C.primaryPale:C.bg,borderRadius:10,border:`1px solid ${i===0?C.primaryMid:C.border}`,cursor:s?"pointer":"default",transition:"transform 0.12s"}}
                    onMouseEnter={e=>s&&(e.currentTarget.style.transform="translateX(4px)")}
                    onMouseLeave={e=>(e.currentTarget.style.transform="none")}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:i<3?"linear-gradient(135deg,#f0a500,#d97706)":`linear-gradient(135deg,${C.primary},#0d9488)`,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:i<3?15:12,flexShrink:0}}>
                      {i<3?medals[i]:(i+1)}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.primary,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textDecoration:s?"underline":"none",textUnderlineOffset:2}}>{r.student_name}</div>
                      <div style={{fontSize:10,color:C.muted}}>{r.exam_name} · {r.class}</div>
                      {s && (
                        <div style={{display:"flex",gap:4,marginTop:4,flexWrap:"wrap"}} onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>{setRcType("comprehensive");setRcMonth("");setRcStudent(s);}} style={{fontSize:9,fontWeight:700,color:C.primary,background:C.primaryPale,border:"none",borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>📊 Full</button>
                          {monthlyAvg.map(m=>(
                            <button key={m.name} onClick={()=>{setRcType("monthly");setRcMonth(MONTHS.find(mo=>mo.slice(0,3)===m.name)||m.name);setRcStudent(s);}}
                              style={{fontSize:9,fontWeight:700,color:"#0d9488",background:"#f0fdfa",border:"none",borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>📅 {m.name}</button>
                          ))}
                          {dashExams.some(e=>e.name?.toLowerCase().includes("midterm")) && <button onClick={()=>{setRcType("midterm");setRcMonth("");setRcStudent(s);}} style={{fontSize:9,fontWeight:700,color:"#d97706",background:"#fffbeb",border:"none",borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>📋 Mid</button>}
                          {dashExams.some(e=>e.name?.toLowerCase().includes("final")) && <button onClick={()=>{setRcType("final");setRcMonth("");setRcStudent(s);}} style={{fontSize:9,fontWeight:700,color:C.primary,background:C.primaryPale,border:"none",borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>🎓 Final</button>}
                        </div>
                      )}
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontWeight:900,fontSize:16,color:gradeColor(g)}}>{r.pct}%</div>
                      <div style={{fontSize:10,color:C.muted}}>{r.marks_obtained}/{r.total_marks}</div>
                      <div style={{fontSize:11,fontWeight:700,color:gradeColor(g)}}>{g}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Monthly Averages</div>
            {monthlyAvg.length>0?(
              <ResponsiveContainer width="100%" height={175}>
                <BarChart data={monthlyAvg} barSize={26}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <YAxis domain={[0,100]} tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{borderRadius:8,border:"none"}} formatter={v=>[`${v}%`,"Avg"]}/>
                  <Bar dataKey="avg" radius={[4,4,0,0]}>
                    {monthlyAvg.map((_,idx)=>(<Cell key={idx} fill={["#1a6b3c","#0d9488","#2563eb","#7c3aed","#d97706","#e11d48"][idx%6]}/>))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ):(
              <div style={{height:175,display:"flex",alignItems:"center",justifyContent:"center",color:C.muted,fontSize:12,background:C.bg,borderRadius:10}}>No monthly data yet</div>
            )}
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
              {["A+","A","B+","B","C","D","F"].map(g=>{
                const cnt=dashResults.filter(r=>r.grade===g).length;
                if(!cnt) return null;
                return <span key={g} style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:`${gradeColor(g)}18`,color:gradeColor(g),border:`1px solid ${gradeColor(g)}30`}}>{g}: {cnt}</span>;
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

// ── TODAY'S PRIORITY STRIP — admin operational overview ───────────
function TodayPriorityStrip({ setTab }) {
  const todayStr = new Date().toISOString().split("T")[0];
  const [data,    setData]    = React.useState(null);

  React.useEffect(() => {
    Promise.all([
      supabase.from("staff_leave_requests").select("id,staff_name,from_date,to_date,leave_type").eq("status","pending"),
      supabase.from("cover_duties").select("id,status,class_name,cover_staff_name,absent_staff_name").eq("date",todayStr),
      supabase.from("staff_leave_requests").select("id,staff_name,leave_type").eq("status","approved").lte("from_date",todayStr).gte("to_date",todayStr),
    ]).then(([leaves, covers, onLeave]) => {
      setData({
        pendingLeaves: leaves.data || [],
        todayCovers:   covers.data || [],
        staffOnLeave:  onLeave.data || [],
      });
    }).catch(()=>{});
  }, []);

  if (!data) return null;

  const { pendingLeaves, todayCovers, staffOnLeave } = data;
  const unassigned     = todayCovers.filter(d => d.status === "open");
  const unacknowledged = todayCovers.filter(d => d.status === "assigned");
  const done           = todayCovers.filter(d => d.status === "done");

  const items = [
    pendingLeaves.length > 0 && {
      icon:"📝", urgent:true, tab:"staff",
      color:"#d97706", bg:"#fffbeb", border:"#fde68a",
      label:`${pendingLeaves.length} Leave Request${pendingLeaves.length!==1?"s":""} Awaiting Approval`,
      sub: pendingLeaves.slice(0,3).map(l=>l.staff_name).join(", ") + (pendingLeaves.length>3?` +${pendingLeaves.length-3} more`:""),
    },
    unassigned.length > 0 && {
      icon:"⚠️", urgent:true, tab:"staff",
      color:"#dc2626", bg:"#fef2f2", border:"#fecaca",
      label:`${unassigned.length} Cover Dut${unassigned.length!==1?"ies":"y"} Unassigned Today`,
      sub: [...new Set(unassigned.map(d=>d.class_name).filter(Boolean))].join(", ") || "Classes need a cover teacher",
    },
    unacknowledged.length > 0 && {
      icon:"⏳", urgent:false, tab:"staff",
      color:"#7c3aed", bg:"#f5f3ff", border:"#ddd6fe",
      label:`${unacknowledged.length} Cover Dut${unacknowledged.length!==1?"ies":"y"} Not Yet Acknowledged`,
      sub: [...new Set(unacknowledged.map(d=>d.cover_staff_name).filter(Boolean))].join(", ") || "Teachers haven't confirmed yet",
    },
    staffOnLeave.length > 0 && {
      icon:"🌴", urgent:false, tab:"staff",
      color:"#0d9488", bg:"#f0fdfa", border:"#99f6e4",
      label:`${staffOnLeave.length} Staff on Leave Today`,
      sub: staffOnLeave.slice(0,3).map(l=>l.staff_name).join(", ") + (staffOnLeave.length>3?` +${staffOnLeave.length-3} more`:""),
    },
    done.length > 0 && {
      icon:"✅", urgent:false, tab:null,
      color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0",
      label:`${done.length} Cover Dut${done.length!==1?"ies":"y"} Completed Today`,
      sub:"Cover teachers marked attendance successfully",
    },
  ].filter(Boolean);

  if (!items.length) return (
    <div style={{background:"#f0fdf4",borderRadius:14,padding:"12px 18px",border:"1px solid #bbf7d0",display:"flex",alignItems:"center",gap:10}}>
      <span style={{fontSize:20}}>🌟</span>
      <div>
        <div style={{fontWeight:700,fontSize:13,color:"#16a34a"}}>All clear — nothing needs attention today</div>
        <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>No pending leave requests or unassigned cover duties</div>
      </div>
    </div>
  );

  return (
    <div style={{background:C.white,borderRadius:16,padding:"16px 20px",boxShadow:`0 4px 24px ${C.shadow}`,border:`1px solid ${C.border}`}} className="fade-up">
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
        <span style={{fontSize:18}}>🎯</span>
        <span style={{fontWeight:900,fontSize:14,color:C.primary}}>Today's Action Items</span>
        {items.filter(i=>i.urgent).length > 0 && (
          <span style={{background:"#dc2626",color:"white",borderRadius:20,padding:"2px 8px",fontSize:11,fontWeight:900,marginLeft:4}}>
            {items.filter(i=>i.urgent).length} urgent
          </span>
        )}
        <span style={{fontSize:11,color:C.muted,marginLeft:"auto"}}>
          {new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"})}
        </span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
        {items.map((item,i) => (
          <div key={i}
            onClick={()=>item.tab && setTab && setTab(item.tab)}
            style={{background:item.bg,border:`1.5px solid ${item.border}`,borderRadius:12,padding:"12px 14px",
              cursor:item.tab?"pointer":"default",display:"flex",gap:10,alignItems:"flex-start",transition:"transform .15s, box-shadow .15s"}}
            onMouseEnter={e=>{if(item.tab){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 6px 16px rgba(0,0,0,0.08)";}}}
            onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}
          >
            <span style={{fontSize:22,flexShrink:0,lineHeight:1}}>{item.icon}</span>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontWeight:800,fontSize:12,color:item.color,lineHeight:1.3,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                {item.label}
                {item.urgent && <span style={{background:item.color,color:"white",borderRadius:20,padding:"1px 5px",fontSize:9,fontWeight:900}}>!</span>}
              </div>
              {item.sub && <div style={{fontSize:11,color:"#6b7280",marginTop:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.sub}</div>}
            </div>
            {item.tab && <span style={{color:item.color,fontSize:14,flexShrink:0,alignSelf:"center",opacity:.6}}>→</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Dashboard({ students, attendance, fees, setTab }) {
  const S = useSettings();
  const [loaded, setLoaded] = useState(false);
  const [dashMonth, setDashMonth] = useState("All");
  const [dashYear, setDashYear]   = useState("All");
  const [drillDown, setDrillDown] = useState(null); // {title, subtitle, students}
  const [feeModal,  setFeeModal]  = useState(false);
  useEffect(()=>{ setTimeout(()=>setLoaded(true),100); },[]);

  // ── Month/Year filter options from fee_payments ──────────────────
  const hasFees = (fees||[]).length > 0;
  // Build available months from fees AND attendance dates combined
  const allAttDates = Object.keys(attendance||{}); // defined here so it's available for feeMonths
  const feeMonths = (fees||[]).map(f=>f.month).filter(Boolean);
  const attMonths = allAttDates.map(d=>{
    const parts = String(d).split("-");
    if(parts.length>=2) {
      const MNAMES=["January","February","March","April","May","June","July","August","September","October","November","December"];
      return MNAMES[parseInt(parts[1],10)-1]||"";
    }
    return "";
  }).filter(Boolean);
  const availableMonths = ["All",...[...new Set([...feeMonths,...attMonths])].filter(Boolean).sort((a,b)=>MONTHS_LIST.indexOf(a)-MONTHS_LIST.indexOf(b))];
  const availableYears  = ["All",...[...new Set((fees||[]).map(f=>f.year ).filter(Boolean))].sort()];

  // ── Filtered fees for selected period ────────────────────────────
  const filteredFees = (fees||[]).filter(f=>{
    if(dashMonth!=="All" && f.month!==dashMonth) return false;
    if(dashYear !=="All" && String(f.year)!==String(dashYear)) return false;
    return true;
  });
  const paidInPeriod   = new Set(filteredFees.filter(f=>f.method!=="Unpaid").map(f=>f.student_id));
  const fullScholarIds = new Set((fees||[]).filter(f=>f.method==="Full Scholarship").map(f=>f.student_id));
  const unpaidInPeriod = new Set(filteredFees.filter(f=>f.method==="Unpaid").map(f=>f.student_id));
  const periodLabel    = dashMonth==="All"?(dashYear==="All"?"All Time":dashYear):`${dashMonth}${dashYear!=="All"?" "+dashYear:""}`;

  // ── Fee stats ─────────────────────────────────────────────────────
  // If a specific month is selected AND we have fee data, use fee_payments
  // Otherwise fall back to students.fees_status
  const useMonthFilter = hasFees && (dashMonth!=="All" || dashYear!=="All");
  const paid   = useMonthFilter ? paidInPeriod.size   : students.filter(s=>(s.fees||s.fees_status)==="Paid").length;
  const fullScholarCount = (fees||[]).filter((f,i,arr)=>f.method==="Full Scholarship" && arr.findIndex(x=>x.student_id===f.student_id)===i).length;
  const unpaid = useMonthFilter
    ? students.filter(s=>!paidInPeriod.has(s.id)).length
    : students.filter(s=>(s.fees||s.fees_status)!=="Paid").length;
  const totalFeeCollected = filteredFees.filter(f=>f.method!=="Unpaid").reduce((sum,f)=>sum+(Number(f.amount)||0),0);

  // ── Attendance filtered by selected period ──────────────────────
  // Filter dates by dashMonth / dashYear
  // Dates stored as "YYYY-MM-DD" text — parse directly to avoid locale issues
  const MONTH_NAMES = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"];
  const attDates = allAttDates.filter(d=>{
    try {
      // Support "YYYY-MM-DD" format directly
      const parts = String(d).split("-");
      if(parts.length>=3) {
        const yr  = parts[0];
        const mon = MONTH_NAMES[parseInt(parts[1],10)-1]||"";
        if(dashYear!=="All" && yr!==String(dashYear)) return false;
        if(dashMonth!=="All" && mon!==dashMonth) return false;
        return true;
      }
      // Fallback: try Date parsing
      const dt = new Date(d);
      if(isNaN(dt)) return true;
      if(dashYear!=="All" && String(dt.getFullYear())!==String(dashYear)) return false;
      if(dashMonth!=="All") {
        const mn = MONTH_NAMES[dt.getUTCMonth()];
        if(mn!==dashMonth) return false;
      }
      return true;
    } catch(e){ return true; }
  });
  const attStats = (students||[]).map(s=>{
    const present = attDates.filter(d=>attendance[d]?.[s.id]==="P").length;
    const late    = attDates.filter(d=>attendance[d]?.[s.id]==="L").length;
    const absent  = attDates.filter(d=>attendance[d]?.[s.id]==="A").length;
    const pct = attDates.length ? Math.round(((present+late)/attDates.length)*100) : 0;
    return {id:s.id, name:(s.name||"?").split(" ")[0], fullName:s.name, cls:(s.class||"Unknown"), pct, present, late, absent};
  });
  const avgAtt = Math.round(attStats.reduce((a,s)=>a+s.pct,0)/(attStats.length||1));
  const attThreshold = Number(S?.att_threshold||85);
  const atRisk = attStats.filter(s=>s.pct<attThreshold);

  // ── Class distribution with gender ────────────────────────────────
  // Normalize class name: "Cl1"→"Class 1", "Grade 3"→"Class 3", trim Boys/Girls
  const normalizeClass = (raw="") => {
    let c = raw.trim();
    c = c.replace(/\s*(Boys|Girls|Boy|Girl|BOYS|GIRLS)\s*$/i,"").trim();
    c = c.replace(/^Cl\.?\s*(\d+)/i,"Class $1");
    c = c.replace(/^Class(\d+)/i,"Class $1");
    c = c.replace(/^Grade\s*(\d+)/i,"Class $1");
    c = c.replace(/^(\d+)$/,"Class $1");
    return c.trim()||"Unknown";
  };

  const classDist = (()=>{
    const map = {};
    students.forEach(s=>{
      if(s.is_boarding===true||s.is_boarding==="true") return; // boarding students counted separately
      const raw    = (s.class||s["class"]||"Unknown");
      const base   = normalizeClass(raw);
      const gender = s.gender||((/boys?/i.test(raw))?"Male":(/girls?/i.test(raw))?"Female":"Unknown");
      if(!map[base]) map[base]={count:0,male:0,female:0,unknown:0};
      map[base].count++;
      if(gender==="Male")        map[base].male++;
      else if(gender==="Female") map[base].female++;
      else                       map[base].unknown++;
    });
    return Object.entries(map)
      .sort((a,b)=>a[0].localeCompare(b[0],undefined,{numeric:true}))
      .map(([cls,v])=>({cls, count:v.count, male:v.male, female:v.female, unknown:v.unknown}));
  })();

  // Scholarship students count
  const scholarshipCount = students.filter(s=>s.scholarship_status==="Active"||s.scholarship==="Yes"||s.is_scholarship===true||s.is_scholarship==="true").length;

  // ── Stream distribution ───────────────────────────────────────────
  const schoolOnly = students.filter(s=>!s.stream||s.stream==="School Only"||s.stream==="School").length;
  const bothStream = students.filter(s=>s.stream?.includes("Both")).length;
  const hifzOnly   = students.filter(s=>s.stream==="Hifz Only"||s.stream==="Hifz").length;
  const hifz = bothStream;

  // ── Fee by class (from fee_payments for selected period) ─────────
  const feeByClass = (()=>{
    const map = {};
    classDist.forEach(c=>{ map[c.cls]={paid:0,unpaid:0,total:c.count,amount:0}; });
    filteredFees.forEach(f=>{
      const cls = normalizeClass(f.class||"");
      if(!map[cls]) map[cls]={paid:0,unpaid:0,total:0,amount:0};
      if(f.method==="Unpaid") map[cls].unpaid++;
      else { map[cls].paid++; map[cls].amount+=(Number(f.amount)||0); }
    });
    return map;
  })();

  // ── Stat cards ────────────────────────────────────────────────────
  const cards = [
    {icon:"👥", label:"Total Students",  value:students.length,
      sub:"Enrolled", color:C.primary, bg:C.primaryPale,
      onClick:()=>setDrillDown({title:"All Students",subtitle:`${students.length} enrolled`,students:students.map(s=>({...s,class:s.class||s["class"]}))})},
    {icon:"📖", label:"Hifz + School",   value:hifz,
      sub:"Dual stream", color:"#7c3aed", bg:C.purplePale,
      onClick:()=>setDrillDown({title:"Hifz + School Students",subtitle:"Dual stream",students:students.filter(s=>s.stream?.includes("Both")).map(s=>({...s,class:s.class||s["class"]}))})},
    {icon:"📅", label:"Avg Attendance",  value:`${avgAtt}%`,
      sub:`${attDates.length} days tracked`, color:C.teal, bg:C.tealPale,
      onClick:()=>setDrillDown({title:"Attendance Overview",subtitle:periodLabel,students:[...attStats].sort((a,b)=>b.pct-a.pct).map(s=>({...s,class:s.cls}))})},
    {icon:"✅", label:"Fees Paid",
      value: useMonthFilter
        ? `${paid}/${students.length}`
        : (()=>{
            const allMos=[...new Set((fees||[]).map(f=>f.month).filter(Boolean))];
            const n=allMos.length||1;
            const pAll=students.filter(s=>{
              const p=new Set((fees||[]).filter(f=>f.student_id===s.id&&f.method!=="Unpaid").map(f=>f.month));
              return p.size>=n;
            }).length;
            const pSome=students.filter(s=>{
              const p=(fees||[]).filter(f=>f.student_id===s.id&&f.method!=="Unpaid").length;
              return p>0&&p<n;
            }).length;
            const pNone=students.length-pAll-pSome;
            return `${pAll} / ${pSome} / ${pNone}`;
          })(),
      sub:useMonthFilter?`PKR ${totalFeeCollected.toLocaleString()}`:`All paid · Partial · None (of ${students.length})`,
      color:C.accent, bg:C.accentPale, onClick:()=>setFeeModal(true)},
  ];

  // ── Empty state ───────────────────────────────────────────────────
  if(students.length===0) return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:24}}>
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"14px 22px",boxShadow:`0 4px 20px ${C.shadow}`}}>
        <div style={{color:"white",fontWeight:900,fontSize:17}}>🏫 School Management Portal</div>
        <div style={{color:"rgba(255,255,255,0.75)",fontSize:12,marginTop:2}}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
      </div>
      <div style={{background:C.white,borderRadius:20,padding:60,textAlign:"center",boxShadow:`0 4px 24px ${C.shadow}`}}>
        <div style={{fontSize:64,marginBottom:16}}>🏫</div>
        <div style={{fontSize:18,fontWeight:800,color:C.primary,marginBottom:8}}>No students registered yet</div>
        <div style={{fontSize:14,color:C.muted}}>Go to the Register tab to add your first student.</div>
      </div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────
  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>

      {/* Drill Down Modal */}
      {drillDown && <DrillDownModal title={drillDown.title} subtitle={drillDown.subtitle} students={drillDown.students} onClose={()=>setDrillDown(null)}/>}

      {/* ── FEE DETAIL MODAL ── */}
      {feeModal && (()=>{
        // Build paid list
        const paidList = useMonthFilter
          ? (() => {
              const seen = new Set();
              return filteredFees.filter(f=>f.method!=="Unpaid"&&!seen.has(f.student_id)&&seen.add(f.student_id))
                .map(f=>({ id:f.student_id, name:f.student_name, class:f.class,
                  amount:f.amount, month:f.month, method:f.method, receipt:f.receipt_no }));
            })()
          : students.filter(s=>(s.fees||s.fees_status)==="Paid")
              .map(s=>({id:s.id,name:s.name||s.full_name,class:s.class||s["class"],
                amount:s.monthly_fee||"—",month:"",method:"Paid",receipt:""}));

        // Build missing list — students who have NOT paid in this period
        const paidIds = new Set(paidList.map(p=>p.id));
        const missingList = useMonthFilter
          ? students.filter(s=>!paidIds.has(s.id)&&(s.class||s["class"])!=="")
              .map(s=>({id:s.id,name:s.name||s.full_name,class:s.class||s["class"],
                contact:s.contact||s.phone||"—",monthly_fee:s.monthly_fee||"—"}))
          : students.filter(s=>(s.fees||s.fees_status)!=="Paid")
              .map(s=>({id:s.id,name:s.name||s.full_name,class:s.class||s["class"],
                contact:s.contact||s.phone||"—",monthly_fee:s.monthly_fee||"—"}));

        const totalCollected = useMonthFilter
          ? filteredFees.filter(f=>f.method!=="Unpaid").reduce((s,f)=>s+(Number(f.amount)||0),0)
          : paidList.reduce((s,p)=>s+(Number(p.amount)||0),0);
        const totalPending = missingList.reduce((s,m)=>s+(Number(m.monthly_fee)||0),0);

        const Row = ({child, bg}) => (
          <div style={{padding:"10px 20px",background:bg,borderBottom:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:12}}>
            {child}
          </div>
        );

        return (
          <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)",overflowY:"auto"}}
            onClick={()=>setFeeModal(false)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:780,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}
              onClick={e=>e.stopPropagation()}>

              {/* Header */}
              <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d9488)",padding:"18px 24px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{color:"white",fontWeight:900,fontSize:16}}>💳 Fee Collection Report</div>
                  <div style={{color:"rgba(255,255,255,0.8)",fontSize:12,marginTop:2}}>{periodLabel} · {students.length} students total</div>
                </div>
                <button onClick={()=>setFeeModal(false)} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:34,height:34,fontSize:18,cursor:"pointer"}}>×</button>
              </div>

              {/* Summary strip */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",background:"#f0fdf7",borderBottom:"2px solid #bbf7d0"}}>
                {[
                  {l:"✅ Paid",v:paidList.length,sub:`PKR ${totalCollected.toLocaleString()}`,c:"#1a6b3c",bg:"#f0fdf7"},
                  {l:"❌ Missing",v:missingList.length,sub:`PKR ${totalPending.toLocaleString()} due`,c:"#e11d48",bg:"#fff0f3"},
                  {l:"📊 Rate",v:`${students.length?Math.round((paidList.length/students.length)*100):0}%`,sub:"collection rate",c:"#0d9488",bg:"#f0fdfa"},
                ].map(k=>(
                  <div key={k.l} style={{padding:"14px 20px",background:k.bg,textAlign:"center",borderRight:"1px solid #e5e7eb"}}>
                    <div style={{fontSize:11,color:"#6b7280",fontWeight:600,marginBottom:4}}>{k.l}</div>
                    <div style={{fontSize:24,fontWeight:900,color:k.c,lineHeight:1}}>{k.v}</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:4}}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* Two-panel scrollable */}
              <div style={{maxHeight:"60vh",overflowY:"auto"}}>

                {/* PAID SECTION */}
                {paidList.length>0 && (<>
                  <div style={{padding:"10px 20px",background:"#f0fdf7",borderBottom:"2px solid #bbf7d0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontWeight:800,color:"#1a6b3c",fontSize:13}}>✅ Students Who Paid ({paidList.length})</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#1a6b3c",background:"white",borderRadius:6,padding:"2px 10px",border:"1px solid #bbf7d0"}}>PKR {totalCollected.toLocaleString()}</span>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"#f9fffe"}}>
                        <th style={{padding:"8px 20px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>#</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>Student</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>Class</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>Method</th>
                        <th style={{padding:"8px 12px",textAlign:"right",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>Amount</th>
                        {useMonthFilter && <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #e5e7eb"}}>Receipt</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paidList.map((p,i)=>(
                        <tr key={p.id||i} style={{borderBottom:"1px solid #f0fdf7",background:i%2===0?"white":"#fafff8"}}>
                          <td style={{padding:"9px 20px",color:"#9ca3af",fontSize:11}}>{i+1}</td>
                          <td style={{padding:"9px 12px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#1a6b3c,#0d9488)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:11,fontWeight:800,flexShrink:0}}>{(p.name||"?")[0]}</div>
                              <span style={{fontWeight:700,color:"#1e2d1e"}}>{p.name}</span>
                            </div>
                          </td>
                          <td style={{padding:"9px 12px",color:"#6b7280"}}>{p.class}</td>
                          <td style={{padding:"9px 12px"}}>
                            <span style={{background:"#d1fae5",color:"#065f46",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{p.method||"Cash"}</span>
                          </td>
                          <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:"#1a6b3c"}}>PKR {Number(p.amount||0).toLocaleString()}</td>
                          {useMonthFilter && <td style={{padding:"9px 12px",color:"#9ca3af",fontSize:11}}>{p.receipt||"—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>)}

                {/* MISSING SECTION */}
                {missingList.length>0 && (<>
                  <div style={{padding:"10px 20px",background:"#fff0f3",borderTop:"2px solid #fda4af",borderBottom:"2px solid #fda4af",display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:paidList.length?8:0}}>
                    <span style={{fontWeight:800,color:"#e11d48",fontSize:13}}>❌ Fee Not Received ({missingList.length})</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#e11d48",background:"white",borderRadius:6,padding:"2px 10px",border:"1px solid #fda4af"}}>PKR {totalPending.toLocaleString()} pending</span>
                  </div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"#fff8f8"}}>
                        <th style={{padding:"8px 20px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #fee2e2"}}>#</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #fee2e2"}}>Student</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #fee2e2"}}>Class</th>
                        <th style={{padding:"8px 12px",textAlign:"left",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #fee2e2"}}>Contact</th>
                        <th style={{padding:"8px 12px",textAlign:"right",color:"#6b7280",fontWeight:700,fontSize:11,borderBottom:"1px solid #fee2e2"}}>Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingList.map((m,i)=>(
                        <tr key={m.id||i} style={{borderBottom:"1px solid #fff0f3",background:i%2===0?"white":"#fffafa"}}>
                          <td style={{padding:"9px 20px",color:"#9ca3af",fontSize:11}}>{i+1}</td>
                          <td style={{padding:"9px 12px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <div style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#e11d48,#f43f5e)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:11,fontWeight:800,flexShrink:0}}>{(m.name||"?")[0]}</div>
                              <span style={{fontWeight:700,color:"#1e2d1e"}}>{m.name}</span>
                            </div>
                          </td>
                          <td style={{padding:"9px 12px",color:"#6b7280"}}>{m.class}</td>
                          <td style={{padding:"9px 12px",color:"#6b7280"}}>{m.contact}</td>
                          <td style={{padding:"9px 12px",textAlign:"right",fontWeight:800,color:"#e11d48"}}>PKR {Number(m.monthly_fee||0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>)}

                {paidList.length===0 && missingList.length===0 && (
                  <div style={{padding:40,textAlign:"center",color:"#6b7280",fontSize:13}}>No fee data for this period.</div>
                )}
              </div>

              {/* Footer */}
              <div style={{padding:"12px 24px",borderTop:"1px solid #e5e7eb",background:"#f9fafb",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12,color:"#6b7280"}}>Period: <strong>{periodLabel}</strong></span>
                <button onClick={()=>setFeeModal(false)} style={{background:"#1a6b3c",color:"white",border:"none",borderRadius:8,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
              </div>
            </div>
          </div>
          </Portal>
        );
      })()}

      {/* Compact portal title — no repeated letterhead */}
      <div className="fade-up" style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:`0 4px 20px ${C.shadow}`}}>
        <div>
          <div style={{color:"white",fontWeight:900,fontSize:17,letterSpacing:.3}}>🏫 School Management Portal</div>
          <div style={{color:"rgba(255,255,255,0.75)",fontSize:12,marginTop:2}}>Admin Dashboard — {new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
        </div>
        <div style={{textAlign:"right",display:"flex",flexDirection:"column",gap:4}}>
          <div style={{background:"rgba(255,255,255,0.15)",borderRadius:8,padding:"4px 12px",color:"white",fontSize:12,fontWeight:700}}>{students.length} Students</div>
          <div style={{color:"rgba(255,255,255,0.6)",fontSize:10}}>Enrolled this session</div>
        </div>
      </div>

      {/* Today's staff/operational action items */}
      <TodayPriorityStrip setTab={setTab}/>

      {/* Smart Alerts — student level */}
      <SmartAlerts role="admin" students={students} attendance={Object.entries(attendance||{}).flatMap(([date,rec])=>Object.entries(rec||{}).map(([sid,status])=>({date,student_id:sid,status})))} fees={fees}/>

      {/* Month filter bar — always visible */}
      <div className="fade-up" style={{display:"flex",alignItems:"center",gap:12,background:C.white,borderRadius:14,padding:"12px 20px",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <span style={{fontSize:13,fontWeight:700,color:C.muted}}>📅 Period:</span>
        <select value={dashMonth} onChange={e=>setDashMonth(e.target.value)}
          style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px",fontSize:13,color:C.text,background:C.white,fontWeight:600,cursor:"pointer"}}>
          {availableMonths.map(m=><option key={m}>{m}</option>)}
        </select>
        <select value={dashYear} onChange={e=>setDashYear(e.target.value)}
          style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px",fontSize:13,color:C.text,background:C.white,fontWeight:600,cursor:"pointer"}}>
          {availableYears.map(y=><option key={y}>{y}</option>)}
        </select>
        {!hasFees && <span style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>— add fee data to filter by month</span>}
        <span style={{marginLeft:"auto",background:C.primaryPale,color:C.primary,borderRadius:8,padding:"4px 14px",fontSize:12,fontWeight:700,border:`1px solid ${C.primaryMid}`}}>
          {periodLabel} • {students.length} students
        </span>
      </div>

      {/* Stat cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16}}>
        {cards.map((c,i)=>(
          <div key={i} className="card-hover fade-up" onClick={()=>c.onClick&&c.onClick()} style={{animationDelay:`${i*.08}s`,background:C.white,borderRadius:16,padding:"20px 22px",boxShadow:`0 2px 12px ${C.shadow}`,borderTop:`4px solid ${c.color}`,cursor:c.onClick?"pointer":"default"}}>
            <div style={{fontSize:28,marginBottom:8}}>{c.icon}</div>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>{c.label}</div>
            <div style={{fontSize:32,fontWeight:900,color:c.color,lineHeight:1.1,margin:"4px 0"}}>{c.value}</div>
            <div style={{fontSize:12,color:C.muted}}>{c.sub}</div>
            {c.onClick && <div style={{fontSize:10,color:c.color,marginTop:6,fontWeight:600}}>Click to view →</div>}
          </div>
        ))}
      </div>

      {/* Attendance chart + Fee status */}
      <div style={{display:"grid",gridTemplateColumns:"1.4fr 1fr",gap:16}}>
        <div className="fade-up" style={{animationDelay:".2s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,marginBottom:4,fontSize:14}}>📊 Attendance Overview</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:14}}>{attDates.length} school days · {dashMonth==="All"&&dashYear==="All"?"All time":periodLabel}</div>
          {attStats.length===0 ? <div style={{color:C.muted,fontSize:13,padding:"30px 0",textAlign:"center"}}>No attendance data yet</div> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={attStats} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="name" tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false}/>
                <YAxis domain={[0,100]} tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} cursor={{fill:C.primaryPale}}
                  formatter={(v,n,p)=>[`${v}% — ✅${p.payload.present} Present · 🕐${p.payload.late||0} Late · ❌${p.payload.absent} Absent`,"Attendance"]}/>
                <Bar dataKey="pct" name="Attendance %" radius={[6,6,0,0]} cursor="pointer"
                  onClick={(data)=>setDrillDown({title:`${data.name} — Attendance`,subtitle:periodLabel,students:[attStats.find(s=>s.name===data.name)].filter(Boolean).map(s=>({...s,class:s.cls}))})}>
                  {attStats.map((s,i)=><Cell key={i} fill={s.pct>=(Number(S?.att_threshold)||85)?C.primary:s.pct>=70?C.accent:C.rose}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Fee Status */}
          <div className="fade-up" style={{animationDelay:".3s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`,flex:1}}>
            <div style={{fontWeight:800,color:C.primary,marginBottom:4,fontSize:14}}>💳 Fee Status</div>
            <div style={{fontSize:11,color:C.muted,marginBottom:12}}>{periodLabel}</div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <PieChart width={90} height={90}>
                  <Pie data={[{value:paid||0},{value:Math.max(unpaid||0,0)}]} dataKey="value" cx="50%" cy="50%" innerRadius={24} outerRadius={40} paddingAngle={3}>
                    <Cell fill={C.primary}/><Cell fill={C.rose}/>
                  </Pie>
                </PieChart>
              <div style={{flex:1}}>
                {[["✅ Paid",C.primary,paid],[`❌ Unpaid`,C.rose,unpaid]].map(([l,c,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                    <span style={{color:C.muted}}>{l}</span>
                    <span style={{fontWeight:800,color:c}}>{v}</span>
                  </div>
                ))}
                {useMonthFilter && totalFeeCollected>0 && (
                  <div style={{marginTop:8,fontSize:12,fontWeight:700,color:C.accent}}>
                    💰 PKR {totalFeeCollected.toLocaleString()} collected
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* At-risk */}
          {atRisk.length>0 && (
            <div className="fade-up" style={{animationDelay:".4s",background:"linear-gradient(135deg,#fff0f3,#fff8e7)",border:`1px solid ${C.rose}30`,borderRadius:16,padding:16,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.rose,fontSize:13,marginBottom:8}}>⚠️ Low Attendance ({atRisk.length})</div>
              {atRisk.slice(0,4).map(s=>(
                <div key={s.id} style={{fontSize:12,color:C.rose,padding:"3px 0",display:"flex",justifyContent:"space-between"}}>
                  <span>{s.fullName}</span><span style={{fontWeight:700}}>{s.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Students by Class */}
      <div className="fade-up" style={{animationDelay:".45s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{fontWeight:800,color:C.primary,marginBottom:16,fontSize:14}}>🏫 Students by Class</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {classDist.map((c,i)=>(
            <div key={i} onClick={()=>setDrillDown({title:`${c.cls} Students`,subtitle:`${c.count} students`,students:students.filter(s=>normalizeClass(s.class||s["class"]||"")===c.cls).map(s=>({...s,class:s.class||s["class"],boarding_tag:(s.is_boarding===true||s.is_boarding==="true")?"🏠 Boarding":"🏡 Day Scholar"}))})} style={{background:C.primaryPale,borderRadius:12,padding:"12px 16px",textAlign:"center",minWidth:90,border:`1px solid ${C.primaryMid}`,cursor:"pointer",transition:"transform 0.15s"}} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
              <div style={{fontSize:24,fontWeight:900,color:C.primary,lineHeight:1}}>{c.count}</div>
              <div style={{fontSize:12,fontWeight:700,color:C.text,marginTop:4}}>{c.cls}</div>
              <div style={{display:"flex",gap:4,justifyContent:"center",marginTop:6,flexWrap:"wrap"}}>
                {c.male>0 && (
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#1d4ed8",background:"#dbeafe",borderRadius:6,padding:"2px 8px"}}>{c.male}</div>
                    <div style={{fontSize:9,color:"#1d4ed8",marginTop:1}}>Boys</div>
                  </div>
                )}
                {c.female>0 && (
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:900,color:"#9d174d",background:"#fce7f3",borderRadius:6,padding:"2px 8px"}}>{c.female}</div>
                    <div style={{fontSize:9,color:"#9d174d",marginTop:1}}>Girls</div>
                  </div>
                )}
                {c.unknown>0 && c.male===0 && c.female===0 && (
                  <div style={{fontSize:9,color:C.muted,marginTop:2}}>{c.unknown} students</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fee Status by Class — dynamic */}
      {classDist.length>0 && (
        <div className="fade-up" style={{animationDelay:".5s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,marginBottom:4,fontSize:14}}>💰 Fee Status by Class</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:14}}>{periodLabel}</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {classDist.map(c=>{
              const fb = feeByClass[c.cls]||{paid:0,unpaid:0,total:c.count};
              const paidC   = useMonthFilter ? fb.paid   : students.filter(s=>(s.class||s["class"]||"").replace(/\s*(Boys|Girls|boy|girl|BOYS|GIRLS)\s*$/i,"").trim()===c.cls&&(s.fees||s.fees_status)==="Paid").length;
              const unpaidC = useMonthFilter ? fb.unpaid : c.count - paidC;
              return (
                <div key={c.cls} onClick={()=>{
                  const clsStudents = filteredFees.filter(f=>normalizeClass(f.class||"")===c.cls).map(f=>({id:f.student_id,name:f.student_name,class:f.class,fees:f.method,amount:f.amount}));
                  setDrillDown({title:`${c.cls} — Fee Status`,subtitle:periodLabel,students:clsStudents});
                }} style={{background:unpaidC>0?C.rosePale:C.primaryPale,borderRadius:12,padding:"10px 16px",textAlign:"center",minWidth:80,border:`1px solid ${unpaidC>0?C.rose+"40":C.primaryMid}`,cursor:"pointer",transition:"transform 0.15s"}} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:4}}>{c.cls}</div>
                  <div style={{fontSize:12,color:C.primary,fontWeight:800}}>✅ {paidC}</div>
                  {unpaidC>0&&<div style={{fontSize:12,color:C.rose,fontWeight:800}}>❌ {unpaidC}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stream Distribution + Avg Attendance by Class */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1.4fr",gap:16}}>
        {/* Stream Distribution */}
        <div className="fade-up" style={{animationDelay:".55s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,marginBottom:16,fontSize:14}}>📚 Stream Distribution</div>
          {(()=>{
            const streamData = [
              {name:"School Only", value:schoolOnly, color:C.blue},
              {name:"Hifz + School",value:bothStream, color:"#7c3aed"},
              {name:"Hifz Only",   value:hifzOnly,   color:C.teal},
            ].filter(d=>d.value>0);
            if(!streamData.length) return <div style={{color:C.muted,fontSize:13}}>No data</div>;
            return (<>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={streamData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3}>
                    {streamData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                  </Pie>
                  <Tooltip contentStyle={{borderRadius:10,border:"none"}}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:8}}>
                {streamData.map(d=>(
                  <div key={d.name} style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                    <span style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{width:10,height:10,borderRadius:"50%",background:d.color,display:"inline-block"}}/>
                      <span style={{color:C.muted}}>{d.name}</span>
                    </span>
                    <span style={{fontWeight:700,color:d.color}}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>);
          })()}
        </div>

        {/* Avg Attendance by Class */}
        <div className="fade-up" style={{animationDelay:".6s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,marginBottom:4,fontSize:14}}>📈 Avg Attendance by Class</div>
          <div style={{fontSize:11,color:C.muted,marginBottom:14}}>{attDates.length} school days</div>
          {(()=>{
            const classAtt = classDist.map(c=>{
              const clsStudents = attStats.filter(s=>{
                const raw = (students.find(st=>st.id===s.id)||{}).class||"";
                return raw.replace(/\s*(Boys|Girls|boy|girl|BOYS|GIRLS)\s*$/i,"").trim()===c.cls;
              });
              const avg = clsStudents.length ? Math.round(clsStudents.reduce((a,s)=>a+s.pct,0)/clsStudents.length) : 0;
              return {name:c.cls, avg};
            }).filter(c=>c.avg>0);
            if(!classAtt.length) return <div style={{color:C.muted,fontSize:13,padding:"30px 0",textAlign:"center"}}>No attendance data yet</div>;
            return (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={classAtt} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <YAxis domain={[0,100]} tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} cursor={{fill:C.primaryPale}}
                    formatter={v=>[`${v}%`,"Avg Attendance"]}/>
                  <Bar dataKey="avg" radius={[6,6,0,0]}>
                    {classAtt.map((c,i)=><Cell key={i} fill={c.avg>=85?C.primary:c.avg>=70?C.accent:C.rose}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            );
          })()}
        </div>
      </div>

      {/* Top Attendees */}
      {attStats.length>0 && (
        <div className="fade-up" style={{animationDelay:".65s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:14}}>🏆 Top Attendees</div>
          <span style={{fontSize:11,fontWeight:700,background:C.primaryPale,color:C.primary,borderRadius:8,padding:"3px 10px",border:`1px solid ${C.primaryMid}`}}>{periodLabel}</span>
        </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {[...attStats].sort((a,b)=>b.pct-a.pct).slice(0,6).map((s,i)=>(
              <div key={i} onClick={()=>setDrillDown({title:s.fullName,subtitle:`${s.cls} • ${periodLabel}`,students:[{...s,class:s.cls,present_days:s.present,absent_days:s.absent,total_days:attDates.length}]})} style={{background:i===0?C.primaryPale:C.bg,borderRadius:12,padding:"12px 16px",textAlign:"center",minWidth:90,border:`1px solid ${i===0?C.primaryMid:C.border}`,cursor:"pointer",transition:"transform 0.15s"}} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
                {i===0 && <div style={{fontSize:16,marginBottom:2}}>🥇</div>}
                {i===1 && <div style={{fontSize:16,marginBottom:2}}>🥈</div>}
                {i===2 && <div style={{fontSize:16,marginBottom:2}}>🥉</div>}
                <div style={{fontSize:22,fontWeight:900,color:i===0?C.primary:C.text}}>{s.pct}%</div>
                <div style={{fontSize:11,color:C.muted,fontWeight:600,marginTop:2}}>{s.fullName}</div>
                <div style={{fontSize:10,color:C.primary,fontWeight:600}}>{s.cls}</div>
                {(s.late||0)>0&&<div style={{fontSize:10,color:"#f59e0b",fontWeight:700,marginTop:2}}>🕐 {s.late} late</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Info Row — Scholarship, Boarding, Gender */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}} className="fade-up">
        {[
          {icon:"🎓", label:"Half Scholarship", sub:"50% fee waived", color:"#7c3aed", bg:C.purplePale,
            list:students.filter(s=>s.scholarship_status==="Active").map(s=>({...s,scholarship_tag:"🎓 50% Discount"}))},
          {icon:"🌟", label:"Full Scholarship", sub:"100% fee waived", color:"#d97706", bg:"#fef3c7",
            list:students.filter(s=>s.scholarship_status==="Full").map(s=>({...s,scholarship_tag:"🌟 Full Waiver"}))},
          {icon:"🏠", label:"Boarding Students", sub:"Live on campus", color:C.teal, bg:C.tealPale,
            list:students.filter(s=>s.is_boarding===true||s.is_boarding==="true")},
          {icon:"👦", label:"Total Boys", sub:"Male students", color:C.blue, bg:"#dbeafe",
            list:students.filter(s=>s.gender==="Male"||(s.class||"").match(/boys/i))},
          {icon:"👧", label:"Total Girls", sub:"Female students", color:"#9d174d", bg:"#fce7f3",
            list:students.filter(s=>s.gender==="Female"||(s.class||"").match(/girls/i))},
        ].map((c,i)=>(
          <div key={i} onClick={()=>c.list.length&&setDrillDown({title:c.label,subtitle:`${c.list.length} students`,students:c.list.map(s=>({...s,class:s.class||s["class"]}))})} style={{background:c.bg,borderRadius:14,padding:"16px 18px",border:`1px solid ${c.color}20`,cursor:c.list.length?"pointer":"default"}}>
            <div style={{fontSize:22,marginBottom:6}}>{c.icon}</div>
            <div style={{fontSize:24,fontWeight:900,color:c.color}}>{c.list.length}</div>
            <div style={{fontSize:11,fontWeight:700,color:c.color,marginTop:2}}>{c.label}</div>
            <div style={{fontSize:11,color:C.muted}}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Fee Collection Summary — always shown */}
      <div className="fade-up" style={{animationDelay:".7s",background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{fontWeight:800,color:C.primary,marginBottom:4,fontSize:14}}>💵 Fee Collection</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:16}}>{periodLabel}</div>
        {!hasFees ? (
          <div style={{textAlign:"center",padding:"20px 0",color:C.muted,fontSize:13}}>
            No fee records yet — add payments in the <b>Fees</b> tab to see charts here.
          </div>
        ) : (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:16}}>
              {[
                ["Total Expected", `PKR ${classDist.reduce((s,c)=>{const st=students.filter(st=>normalizeClass(st.class||st["class"]||"")===c.cls);return s+st.reduce((a,s2)=>a+(Number(s2.monthly_fee||0)),0);},0).toLocaleString()}`, C.text, C.bg],
                ["Collected",    `PKR ${totalFeeCollected.toLocaleString()}`, C.primary, C.primaryPale],
                ["Outstanding",  `PKR ${filteredFees.filter(f=>f.method==="Unpaid").reduce((s,f)=>s+(Number(f.amount)||0),0).toLocaleString()}`, C.rose, C.rosePale],
              ].map(([label,value,color,bg])=>(
                <div key={label} style={{background:bg,borderRadius:12,padding:"14px 16px",border:`1px solid ${color}20`}}>
                  <div style={{fontSize:11,color:C.muted,fontWeight:600,marginBottom:6}}>{label}</div>
                  <div style={{fontSize:18,fontWeight:900,color}}>{value}</div>
                </div>
              ))}
            </div>
            {(()=>{
              const byClass = classDist.map(c=>{
                const cFees = filteredFees.filter(f=>normalizeClass(f.class||"")===c.cls);
                const collected = cFees.filter(f=>f.method!=="Unpaid").reduce((s,f)=>s+(Number(f.amount)||0),0);
                return {name:c.cls, amount:collected};
              }).filter(c=>c.amount>0);
              if(!byClass.length) return <div style={{color:C.muted,fontSize:12,textAlign:"center",padding:"10px 0"}}>Select a month to see collection by class</div>;
              return (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={byClass} barSize={32}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="name" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                    <Tooltip contentStyle={{borderRadius:10,border:"none"}} formatter={v=>[`PKR ${v.toLocaleString()}`,"Collected"]}/>
                    <Bar dataKey="amount" fill={C.primary} radius={[6,6,0,0]} name="Collected"/>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </div>
        )}
      </div>

      <DashboardResultsWidget students={students} attendance={attendance} fees={fees}/>
      {/* ── Quick Navigation ── */}
      {setTab&&(
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {[
            {label:"📋 Attendance",tab:"attendance"},
            {label:"💰 Fee Records",tab:"fees"},
            {label:"📊 Results",tab:"results"},
            {label:"🎓 Students",tab:"students"},
            {label:"🌙 Hifz",tab:"hifz"},
            {label:"📝 Register",tab:"register"},
          ].map(({label,tab})=>(
            <button key={tab} onClick={()=>setTab(tab)}
              style={{background:"white",border:"1.5px solid #d1e8d8",borderRadius:10,padding:"8px 14px",
                fontSize:12,fontWeight:700,color:"#1a6b3c",cursor:"pointer",transition:"all .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="#e8f5ee";e.currentTarget.style.borderColor="#1a6b3c"}}
              onMouseLeave={e=>{e.currentTarget.style.background="white";e.currentTarget.style.borderColor="#d1e8d8"}}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── REGISTER STUDENT ───────────────────────────────────────────────
function RegisterStudent({ onAdd }) {
  const S = useSettings();
  const classList = (S.classes||DEFAULT_SETTINGS.classes).split(",").map(s=>s.trim()).filter(Boolean);

  const STREAM_PREFIX = {"School Only":"STU","Both (Hifz + School)":"HFS","Hifz Only":"HFZ","College":"CLG"};
  const OCCUPATIONS   = ["Farmer","Shopkeeper","Laborer","Driver","Teacher","Government Employee","Private Job","Business","Other"];
  const GENDERS       = ["Male","Female"];
  const STREAMS       = ["School Only","Both (Hifz + School)","Hifz Only","College"];

  const empty = {
    full_name:"",father_name:"",mother_name:"",dob:"",gender:"Male",
    stream:"School Only",class:"",enrolled_date:new Date().toISOString().slice(0,10),
    contact:"",phone:"",address:"",village:"",
    cnic:"",occupation:"",
    monthly_fee:"1500",is_boarding:false,
    scholarship_status:"None",
    departure_time:"",notes:""
  };

  const [form,    setForm]    = React.useState(empty);
  const [saving,  setSaving]  = React.useState(false);
  const [toast,   setToast]   = React.useState(null);
  const [suggestedId, setSuggestedId] = React.useState("");
  const [customId,    setCustomId]    = React.useState("");
  const [idLoading,   setIdLoading]   = React.useState(false);
  const [step, setStep] = React.useState(1); // 1=personal, 2=academic, 3=fees

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3500); };
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  // Auto-generate roll number when stream changes
  const generateId = async (stream) => {
    const prefix = STREAM_PREFIX[stream]||"STU";
    setIdLoading(true);
    try {
      const {data} = await supabase.from("students")
        .select("id").like("id",`${prefix}-%`).order("id",{ascending:false}).limit(1);
      let next = 1;
      if(data&&data.length) {
        const last = data[0].id; // e.g. STU-012
        const num  = parseInt(last.split("-")[1]||"0",10);
        next = isNaN(num)?1:num+1;
      }
      const newId = `${prefix}-${String(next).padStart(3,"0")}`;
      setSuggestedId(newId);
      setCustomId(newId);
    } catch(e) {
      const fallback = `${prefix}-${String(Math.floor(Math.random()*900)+100)}`;
      setSuggestedId(fallback);
      setCustomId(fallback);
    }
    setIdLoading(false);
  };

  React.useEffect(()=>{ if(form.stream) generateId(form.stream); },[form.stream]);

  const submit = async () => {
    if(!form.full_name.trim()) return showToast("Student name is required",false);
    if(!form.father_name.trim()) return showToast("Father name is required",false);
    if(!form.class||form.class.startsWith("--")) return showToast("Please select a class",false);
    if(!customId.trim()) return showToast("Roll number is required",false);
    // Check duplicate ID
    const {data:existing} = await supabase.from("students").select("id").eq("id",customId.trim()).maybeSingle();
    if(existing) return showToast(`ID ${customId} already exists — please use a different one`,false);
    setSaving(true);
    const payload = {
      id:customId.trim(),
      full_name:form.full_name.trim(), father_name:form.father_name.trim(),
      class:form.class, stream:form.stream,
      dob:form.dob||null, gender:form.gender,
      contact:form.contact, phone:form.phone||form.contact,
      address:form.address, village:form.village,
      cnic:form.cnic, occupation:form.occupation,
      enrolled_date:form.enrolled_date||new Date().toISOString().slice(0,10),
      fees_status:"Unpaid",
      monthly_fee:Number(form.monthly_fee)||Number(S.default_fee)||1500,
      is_boarding:!!form.is_boarding,
      scholarship_status:form.scholarship_status||"None",
      departure_time:form.departure_time||""
    };
    await onAdd({...payload, name:payload.full_name, father:payload.father_name,
      enrolled:payload.enrolled_date, fees:"Unpaid"});
    fireConfetti();
    showToast(`${form.full_name} registered! Roll No: ${customId}`);
    setSaving(false);
    setForm(empty);
    setSuggestedId("");
    setCustomId("");
    setStep(1);
  };

  const FieldRow = ({children}) => <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>{children}</div>;
  const F = ({label,k,type="text",opts,ph,help,full}) => (
    <div style={{gridColumn:full?"1/-1":"auto",display:"flex",flexDirection:"column",gap:4,minWidth:0}}>
      <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{label}</label>
      {opts ? (
        <select value={form[k]||""} onChange={e=>upd(k,e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 11px",fontSize:13,width:"100%",boxSizing:"border-box"}}>
          {opts.map(o=><option key={o}>{o}</option>)}
        </select>
      ) : type==="checkbox" ? (
        <div style={{display:"flex",alignItems:"center",gap:8,paddingTop:4}}>
          <input type="checkbox" checked={!!form[k]} onChange={e=>upd(k,e.target.checked)} id={`chk_${k}`} style={{width:16,height:16}}/>
          <label htmlFor={`chk_${k}`} style={{fontSize:13,cursor:"pointer"}}>{ph}</label>
        </div>
      ) : (
        <input type={type} value={form[k]||""} onChange={e=>upd(k,e.target.value)} placeholder={ph||""}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 11px",fontSize:13,width:"100%",boxSizing:"border-box"}}/>
      )}
      {help && <div style={{fontSize:10,color:C.muted}}>{help}</div>}
    </div>
  );

  const steps = ["👤 Personal","🏫 Academic","💰 Fees & Extra"];

  return (
    <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
      <AcademyHeader subtitle="Student Registration Form — طالب علم کا اندراج"/>
      {toast && <div style={{padding:"10px 20px",background:toast.ok?"#f0fdf7":"#fff0f3",color:toast.ok?C.primary:"#e11d48",fontWeight:700,fontSize:13,borderBottom:`1px solid ${toast.ok?"#bbf7d0":"#fda4af"}`}}>{toast.msg}</div>}

      {/* Step indicator */}
      <div style={{display:"flex",borderBottom:`1px solid ${C.border}`}}>
        {steps.map((s,i)=>(
          <button key={i} onClick={()=>setStep(i+1)}
            style={{flex:1,padding:"12px 8px",fontWeight:700,fontSize:12,cursor:"pointer",border:"none",
              background:step===i+1?C.primaryPale:"white",
              color:step===i+1?C.primary:C.muted,
              borderBottom:step===i+1?`2px solid ${C.primary}`:"2px solid transparent"}}>
            {s}
          </button>
        ))}
      </div>

      <div style={{padding:"22px 28px"}}>

        {/* Step 1 — Personal */}
        {step===1 && <>
          {/* Roll number preview */}
          <div style={{background:C.primaryPale,border:`1.5px solid ${C.primaryMid}`,borderRadius:12,padding:"12px 16px",marginBottom:18,display:"flex",alignItems:"center",gap:14}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:C.primary,marginBottom:4}}>📋 ROLL NUMBER (auto-generated, editable)</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input value={customId} onChange={e=>setCustomId(e.target.value)}
                  style={{border:`1.5px solid ${C.primaryMid}`,borderRadius:8,padding:"7px 12px",fontSize:15,fontWeight:800,color:C.primary,width:140}}/>
                <button onClick={()=>generateId(form.stream)} style={{background:C.primary,color:"white",border:"none",borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  {idLoading?"…":"🔄 Regenerate"}
                </button>
                <span style={{fontSize:11,color:C.muted}}>Prefix: {STREAM_PREFIX[form.stream]||"STU"} based on stream</span>
              </div>
            </div>
          </div>
          <FieldRow>
            <F label="Full Name *" k="full_name" ph="Muhammad Ali"/>
            <F label="Father's Name *" k="father_name" ph="Ali Khan"/>
            <F label="Mother's Name" k="mother_name" ph="Fatima Bibi"/>
            <F label="Gender" k="gender" opts={GENDERS}/>
            <F label="Date of Birth" k="dob" type="date"/>
            <F label="Contact Number" k="contact" ph="03001234567"/>
            <F label="Alt. Phone" k="phone" ph="03001234568"/>
            <F label="CNIC (parent)" k="cnic" ph="35202-1234567-1"/>
            <F label="Occupation (parent)" k="occupation" opts={["",  ...OCCUPATIONS]}/>
            <F label="Village" k="village" ph="Jatli"/>
            <F label="Full Address" k="address" ph="House #, Street, City" full/>
          </FieldRow>
        </>}

        {/* Step 2 — Academic */}
        {step===2 && <>
          <FieldRow>
            <F label="Stream *" k="stream" opts={STREAMS}/>
            <F label="Class *" k="class" opts={["-- Select Class --",...classList]}/>
            <F label="Enrolled Date" k="enrolled_date" type="date"/>
            <F label="Boarding" k="is_boarding" type="checkbox" ph="Student lives in boarding"/>
            <F label="Departure Time (day scholars)" k="departure_time" type="time" ph="14:30"/>
          </FieldRow>
          <div style={{background:"#eff6ff",borderRadius:10,padding:"12px 16px",marginTop:8,fontSize:12,color:"#1d4ed8"}}>
            💡 <strong>Stream Guide:</strong> School Only (Class 1–5) → STU prefix · Hifz + School (Class 6–8) → HFS prefix · Hifz Only → HFZ prefix
          </div>
        </>}

        {/* Step 3 — Fees */}
        {step===3 && <>
          <FieldRow>
            <F label="Monthly Fee (PKR)" k="monthly_fee" type="number" ph={S.default_fee||"1500"}/>
            <F label="Scholarship Status" k="scholarship_status" opts={["None","Full","Partial","50%"]}/>
          </FieldRow>
          {form.scholarship_status!=="None" && (
            <div style={{background:"#fef3c7",border:"1.5px solid #fcd34d",borderRadius:10,padding:"12px 16px",marginBottom:14,fontSize:13,color:"#92400e"}}>
              ⚠️ <strong>Scholarship selected.</strong> After registering, go to the Scholarship tab to submit the formal application. Fee will be adjusted based on scholarship type:
              {form.scholarship_status==="Full"&&" Full = PKR 0/month"}
              {form.scholarship_status==="Partial"&&" Partial = negotiated amount"}
              {form.scholarship_status==="50%"&&` 50% = PKR ${Math.round((Number(form.monthly_fee)||1500)/2)}/month`}
            </div>
          )}
          <FieldRow>
            <F label="Notes / Special Instructions" k="notes" ph="Any additional information..." full/>
          </FieldRow>
        </>}

        {/* Navigation */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
          <div style={{display:"flex",gap:8}}>
            {step>1 && <button onClick={()=>setStep(s=>s-1)} style={{background:"white",border:`1.5px solid ${C.border}`,color:C.muted,borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>← Back</button>}
            <button onClick={()=>{setForm(empty);setSuggestedId("");setCustomId("");setStep(1);}} style={{background:"transparent",border:`1.5px solid ${C.border}`,color:C.muted,borderRadius:10,padding:"10px 20px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Clear</button>
          </div>
          {step<3
            ? <button onClick={()=>setStep(s=>s+1)} style={{background:`linear-gradient(135deg,${C.primary},${C.primaryLight})`,color:"white",border:"none",borderRadius:10,padding:"10px 24px",fontWeight:800,fontSize:13,cursor:"pointer"}}>Next →</button>
            : <button onClick={submit} disabled={saving} style={{background:saving?"#9ca3af":`linear-gradient(135deg,${C.primary},${C.primaryLight})`,color:"white",border:"none",borderRadius:10,padding:"10px 28px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",boxShadow:`0 4px 16px ${C.shadow}`}}>
                {saving?"Saving…":"Register Student 🎉"}
              </button>
          }
        </div>
      </div>
    </div>
  );
}


// ── SCHOLARSHIP FORM ───────────────────────────────────────────────
function ScholarshipForm({ sheetAPI }) {
  const S = useSettings();
  const classList = (S.classes||DEFAULT_SETTINGS.classes).split(",").map(s=>s.trim()).filter(Boolean);

  const emptyForm = {
    studentName:"",fatherName:"",studentId:"",class:"",stream:"School Only",
    dob:"",contact:"",guardianName:"",occupation:"",cnic:"",
    monthlyIncome:"",
    assets:{land:"",house:"",shop:"",car:"",bike:"",livestock:"",other:""},
    housing:"",rent:"",familyMembers:"",studyingChildren:"",
    attendance:"",testAvg:"",conduct:"",lastExamResult:"",
    // Scholarship decision
    scholarshipType:"Full",   // Full | Partial | 50%
    approvedFee:"0",          // what they pay per month after scholarship
    notes:""
  };

  const [form,       setForm]       = React.useState(emptyForm);
  const [submitted,  setSubmitted]  = React.useState(false);
  const [saving,     setSaving]     = React.useState(false);
  const [toast,      setToast]      = React.useState(null);
  const [students,   setStudents]   = React.useState([]);
  const [selStudent, setSelStudent] = React.useState(null);
  const [view,       setView]       = React.useState("form"); // form | inbox

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3500); };
  const set   = k => v => setForm(p=>({...p,[k]:v}));
  const setAsset = k => v => setForm(p=>({...p,assets:{...p.assets,[k]:v}}));

  // Load students for the "link to existing student" feature
  React.useEffect(()=>{
    supabase.from("students").select("id,full_name,father_name,class,stream,monthly_fee,scholarship_status,contact,phone,dob")
      .order("full_name").then(({data})=>setStudents(data||[]));
  },[]);

  // When student is selected, prefill name/class
  const pickStudent = (id) => {
    const s = students.find(x=>x.id===id);
    if(!s) return;
    setSelStudent(s);
    setForm(p=>({...p,
      studentId:    s.id,
      studentName:  s.full_name,
      fatherName:   s.father_name||"",
      guardianName: s.father_name||"",
      contact:      s.contact||s.phone||"",
      dob:          s.dob||"",
      class:        s.class,
      stream:       s.stream,
      approvedFee:  p.scholarshipType==="Full"?"0":
                    p.scholarshipType==="50%"?String(Math.round((s.monthly_fee||1500)/2)):
                    String(s.monthly_fee||1500)
    }));
  };

  // Recompute approvedFee when type changes
  React.useEffect(()=>{
    if(!selStudent) return;
    const orig = selStudent.monthly_fee||1500;
    const fee  = form.scholarshipType==="Full"?0:
                 form.scholarshipType==="50%"?Math.round(orig/2):
                 Number(form.approvedFee)||0;
    setForm(p=>({...p, approvedFee:String(fee)}));
  },[form.scholarshipType]);

  // ── Apply scholarship to fee records ────────────────────────────
  const applyScholarship = async () => {
    if(!selStudent) return showToast("Please link to an existing student first",false);
    if(!form.scholarshipType) return showToast("Select scholarship type",false);
    setSaving(true);
    const newFee    = Number(form.approvedFee)||0;
    const newStatus = form.scholarshipType==="Full"?"Full Scholarship":
                      form.scholarshipType==="50%"?"50% Scholarship":"Partial Scholarship";
    const MONTHS    = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"];

    // Fee application happens when management approves from Overview tab
    // Just update student scholarship_status to pending
    await supabase.from("students").update({scholarship_status:"Pending Approval"}).eq("id",selStudent.id);

    // Fee records created upon approval in Overview tab

    // 3. Save scholarship form to scholarships table
    const schRow = {
      student_id:selStudent.id,
      student_name:form.studentName, father_name:form.fatherName,
      class:form.class, dob:form.dob, contact:form.contact,
      guardian_name:form.guardianName, occupation:form.occupation,
      cnic:form.cnic, monthly_income:form.monthlyIncome,
      assets:form.assets, housing:form.housing, rent:form.rent,
      family_members:form.familyMembers, studying_children:form.studyingChildren,
      attendance_pct:form.attendance, test_avg:form.testAvg,
      conduct:form.conduct, last_exam_result:form.lastExamResult,
      scholarship_type:form.scholarshipType, approved_fee:newFee,
      status:"pending"
    };
    const {error:schErr} = await supabase.from("scholarships").insert([schRow]);
    if(schErr) console.error("Scholarship save error:", schErr.message);

    showToast(`Application submitted! Awaiting management approval.`);
    setSaving(false);
    setSubmitted(true);
  };

  if(submitted) return (
    <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
      <AcademyHeader subtitle="Dr. Hafiz Muhammad Afzal Scholarship Program"/>
      <div style={{padding:60,textAlign:"center"}}>
        <div style={{fontSize:64,marginBottom:16}}>✅</div>
        <div style={{fontSize:20,fontWeight:900,color:C.primary,marginBottom:8}}>Scholarship Applied!</div>
        {selStudent && <div style={{fontSize:15,color:C.muted,marginBottom:6}}>{selStudent.full_name} — now marked as <strong style={{color:"#7c3aed"}}>{form.scholarshipType} Scholarship</strong></div>}
        <div style={{fontSize:14,color:C.muted,marginBottom:8}}>Monthly fee updated · 6 future months pre-filled as scholarship · Student will not show as Unpaid.</div>
        <button onClick={()=>{setSubmitted(false);setForm(emptyForm);setSelStudent(null);}} className="btn-press"
          style={{background:`linear-gradient(135deg,${C.primary},${C.primaryLight})`,color:C.white,border:"none",borderRadius:10,padding:"11px 28px",fontWeight:700,cursor:"pointer"}}>
          New Application
        </button>
      </div>
    </div>
  );

  const assetItems = [["زرعی زمین","Agricultural Land","land"],["ذاتی گھر","Own House","house"],["دکان","Shop / Business","shop"],["گاڑی","Car","car"],["موٹر سائیکل","Motorcycle","bike"],["مویشی","Livestock","livestock"],["دیگر","Other","other"]];

  return (
    <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
      <AcademyHeader subtitle="Dr. Hafiz Muhammad Afzal Scholarship Program — اسکالرشپ درخواست فارم"/>
      {toast && <div style={{padding:"10px 20px",background:toast.ok?"#f0fdf7":"#fff0f3",color:toast.ok?C.primary:"#e11d48",fontWeight:700,fontSize:13}}>{toast.msg}</div>}
      <div style={{padding:28}}>

        {/* ── Link to existing student ── */}
        <div style={{background:"#eff6ff",border:"1.5px solid #bfdbfe",borderRadius:12,padding:"14px 18px",marginBottom:20}}>
          <div style={{fontWeight:800,color:"#1d4ed8",fontSize:13,marginBottom:8}}>🔗 Link to Registered Student (required to apply scholarship to fees)</div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <select onChange={e=>pickStudent(e.target.value)} value={selStudent?.id||""}
              style={{flex:1,border:"1.5px solid #bfdbfe",borderRadius:8,padding:"9px 12px",fontSize:13}}>
              <option value="">-- Select student to link --</option>
              {students.map(s=><option key={s.id} value={s.id}>{s.full_name} ({s.id}) — {s.class}</option>)}
            </select>
            {selStudent && (
              <div style={{background:"#dbeafe",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,color:"#1d4ed8"}}>
                ✅ Linked: {selStudent.full_name} · Current fee: PKR {selStudent.monthly_fee||"—"}
              </div>
            )}
          </div>
        </div>

        {/* ── Scholarship Type & Fee ── */}
        <div style={{background:"#f5f3ff",border:"1.5px solid #c4b5fd",borderRadius:12,padding:"14px 18px",marginBottom:20}}>
          <div style={{fontWeight:800,color:"#7c3aed",fontSize:13,marginBottom:12}}>🎓 Scholarship Decision</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
            {["Full","50%","Partial"].map(t=>(
              <div key={t} onClick={()=>set("scholarshipType")(t)}
                style={{background:form.scholarshipType===t?"#7c3aed":"white",color:form.scholarshipType===t?"white":"#6b7280",border:`2px solid ${form.scholarshipType===t?"#7c3aed":"#e5e7eb"}`,borderRadius:10,padding:"12px",textAlign:"center",cursor:"pointer",transition:"all .15s"}}>
                <div style={{fontWeight:800,fontSize:14}}>{t==="Full"?"🆓 Full":t==="50%"?"⚡ 50%":"🔶 Partial"}</div>
                <div style={{fontSize:11,marginTop:4,opacity:.8}}>
                  {t==="Full"?"No fee at all":t==="50%"?"Half the fee":"Custom amount"}
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12,marginTop:14}}>
            <label style={{fontSize:13,fontWeight:700,color:"#7c3aed",whiteSpace:"nowrap"}}>Monthly Fee After Scholarship (PKR)</label>
            <input type="number" value={form.approvedFee} onChange={e=>set("approvedFee")(e.target.value)}
              style={{border:"1.5px solid #c4b5fd",borderRadius:8,padding:"8px 12px",fontSize:14,fontWeight:800,color:"#7c3aed",width:120}}/>
            <span style={{fontSize:12,color:"#6b7280"}}>
              {selStudent&&`(original: PKR ${selStudent.monthly_fee||"—"})`}
            </span>
          </div>
        </div>

        <SectionTitle en="Student Information" ur="طالب علم کی معلومات" icon="👤"/>
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginBottom:20}}>
          <Field label="Student Name" urdu="نام" value={form.studentName} onChange={set("studentName")} required half/>
          <Field label="Father's Name" urdu="والد کا نام" value={form.fatherName} onChange={set("fatherName")} required half/>
          <Field label="Class" value={form.class} onChange={set("class")} half select options={["-- Select --",...classList]}/>
          <Field label="Date of Birth" value={form.dob} onChange={set("dob")} type="date" half/>
          <Field label="Contact" value={form.contact} onChange={set("contact")} half/>
        </div>
        <SectionTitle en="Guardian Information" ur="سرپرست کی معلومات" icon="👨"/>
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginBottom:20}}>
          <Field label="Guardian Name" value={form.guardianName} onChange={set("guardianName")} half/>
          <Field label="Occupation" value={form.occupation} onChange={set("occupation")} half/>
          <Field label="CNIC" value={form.cnic} onChange={set("cnic")} half/>
        </div>
        <SectionTitle en="Monthly Income" ur="ماہانہ آمدنی" icon="💰" color={C.accent}/>
        <div style={{background:C.accentPale,border:`1.5px solid ${C.accentLight}`,borderRadius:12,padding:14,marginBottom:20,display:"flex",alignItems:"center",gap:14}}>
          <label style={{fontSize:13,fontWeight:700,color:C.accent,whiteSpace:"nowrap"}}>Total Income (PKR)</label>
          <input value={form.monthlyIncome} onChange={e=>set("monthlyIncome")(e.target.value)} placeholder="e.g. 25000" style={{flex:1,border:`1.5px solid ${C.accentLight}`,borderRadius:8,padding:"9px 12px",fontSize:14,fontWeight:700}}/>
        </div>
        <SectionTitle en="Assets & Property" ur="جائیداد" icon="🏠"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
          {assetItems.map(([ur,en,key])=>(
            <div key={key} onClick={()=>setAsset(key)(form.assets[key]==="yes"?"":"yes")}
              style={{background:form.assets[key]==="yes"?C.primaryPale:C.bg,border:`1.5px solid ${form.assets[key]==="yes"?C.primary:C.border}`,borderRadius:10,padding:"10px 12px",cursor:"pointer",textAlign:"center"}}>
              <div style={{fontSize:11,fontWeight:700,color:form.assets[key]==="yes"?C.primary:C.muted}}>{en}</div>
              <div style={{fontSize:10,color:C.muted,fontFamily:"'Noto Nastaliq Urdu',serif",marginTop:2}}>{ur}</div>
              <div style={{marginTop:4,fontSize:16}}>{form.assets[key]==="yes"?"✅":"⬜"}</div>
            </div>
          ))}
        </div>
        <SectionTitle en="Residence & Family" ur="رہائش اور خاندان" icon="🏡"/>
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginBottom:20}}>
          <Field label="House Type" value={form.housing} onChange={set("housing")} half select options={["-- Select --","Own (ذاتی)","Rented (کرایہ)"]}/>
          <Field label="Monthly Rent (if rented)" value={form.rent} onChange={set("rent")} half/>
          <Field label="Total Family Members" urdu="افراد" value={form.familyMembers} onChange={set("familyMembers")} half/>
          <Field label="Children in School" urdu="زیر تعلیم" value={form.studyingChildren} onChange={set("studyingChildren")} half/>
        </div>
        <SectionTitle en="Academic Record" ur="تعلیمی کارکردگی" icon="📚"/>
        <div style={{display:"flex",flexWrap:"wrap",gap:14,marginBottom:24}}>
          <Field label="Avg Attendance (%)" value={form.attendance} onChange={set("attendance")} half/>
          <Field label="Monthly Test Avg (%)" value={form.testAvg} onChange={set("testAvg")} half/>
          <Field label="Conduct" value={form.conduct} onChange={set("conduct")} half/>
          <Field label="Last Exam Result (%)" value={form.lastExamResult} onChange={set("lastExamResult")} half/>
        </div>

        <div style={{display:"flex",justifyContent:"flex-end",gap:10}}>
          <button onClick={()=>{setForm(emptyForm);setSelStudent(null);}}
            style={{background:"white",border:`1.5px solid ${C.border}`,color:C.muted,borderRadius:10,padding:"11px 20px",fontWeight:700,cursor:"pointer"}}>Clear</button>
          <button onClick={applyScholarship} disabled={saving}
            style={{background:saving?"#9ca3af":`linear-gradient(135deg,#7c3aed,#6d28d9)`,color:"white",border:"none",borderRadius:10,padding:"11px 32px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",boxShadow:"0 4px 16px #7c3aed40"}}>
            {saving?"Applying…":"🎓 Apply Scholarship & Update Fees"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── ATTENDANCE ─────────────────────────────────────────────────────
function Attendance({ students, attendance, setAttendance, onSave }) {
  const S = useSettings();
  const todayStr = new Date().toISOString().slice(0,10);
  const [selectedDate,setSelectedDate] = useState(todayStr);
  const [selectedClass,setSelectedClass] = useState("All Classes");

  // Off days check
  const offDays = (S.off_days||"Saturday,Sunday").split(",").map(d=>d.trim());
  const getDateDay = (ds) => new Date(ds).toLocaleDateString("en-GB",{weekday:"long"});
  const isOffDay = (ds) => offDays.includes(getDateDay(ds));
  const [saved,setSaved] = useState(false);
  const [view,setView] = useState("mark");
  const [editDate, setEditDate] = useState(todayStr);
  const [editClass, setEditClass] = useState("All Classes");
  const [editSaving, setEditSaving] = useState(false);
  const [editToast, setEditToast] = useState(null);
  const [waPanel, setWaPanel] = useState(false); // WhatsApp absent panel

  const classes = ["All Classes",...ALL_CLASSES.filter(c=>students.some(s=>s.class===c))];
  const filteredStudents = selectedClass==="All Classes" ? students : students.filter(s=>s.class===selectedClass);
  const dayAttendance = attendance[selectedDate]||{};

  const toggle = (id,status) => setAttendance(prev=>({...prev,[selectedDate]:{...prev[selectedDate],[id]:status}}));
  const saveDay = async () => {
    if (onSave) await onSave(selectedDate,dayAttendance,filteredStudents);
    setSaved(true); setTimeout(()=>setSaved(false),2500);
    // Show WhatsApp panel if there are absent students with contacts
    const absentWithContact = filteredStudents.filter(s=>dayAttendance[s.id]==="A"&&(s.contact||s.phone));
    if (absentWithContact.length > 0) setWaPanel(true);
  };
  const markAll = (status) => {
    const updated = {...(attendance[selectedDate]||{})};
    filteredStudents.forEach(s=>{updated[s.id]=status;});
    setAttendance(prev=>({...prev,[selectedDate]:updated}));
  };

  const allDates = [...new Set([...DATES,...Object.keys(attendance)])].sort();
  const summary = students.map(s=>{
    const total = allDates.length;
    const present = allDates.filter(d=>attendance[d]?.[s.id]==="P").length;
    const absent = allDates.filter(d=>attendance[d]?.[s.id]==="A").length;
    const leave = allDates.filter(d=>attendance[d]?.[s.id]==="L"||attendance[d]?.[s.id]==="LV").length;
    const pct = total?Math.round((present/total)*100):0;
    return {...s,present,absent,leave,total,pct};
  });

  const trend = allDates.slice(-30).map(d=>({date:d.slice(5),present:students.filter(s=>attendance[d]?.[s.id]==="P").length}));

  const STATUS = [
    {key:"P",label:"Present",color:"#fff",bg:C.primary,icon:"✓"},
    {key:"A",label:"Absent",color:"#fff",bg:C.rose,icon:"✗"},
    {key:"L",label:"Leave",color:"#fff",bg:C.accent,icon:"~"},
  ];

  const presentCount = filteredStudents.filter(s=>dayAttendance[s.id]==="P").length;
  const absentCount = filteredStudents.filter(s=>dayAttendance[s.id]==="A").length;
  const leaveCount = filteredStudents.filter(s=>dayAttendance[s.id]==="L"||dayAttendance[s.id]==="LV").length;
  const unmarked = filteredStudents.filter(s=>!dayAttendance[s.id]).length;

  if (students.length===0) return (
    <div className="fade-up" style={{background:C.white,borderRadius:20,padding:60,textAlign:"center",boxShadow:`0 4px 24px ${C.shadow}`}}>
      <div style={{fontSize:48,marginBottom:12}}>📅</div>
      <div style={{fontSize:16,fontWeight:800,color:C.primary}}>No students registered yet</div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Header */}
      <div className="fade-up" style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:C.primary}}>📅 Attendance Register</div>
          <div style={{color:C.muted,fontSize:13,marginTop:2}}>{students.length} students · حاضری رجسٹر</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {[["mark","✏️ Mark"],["edit","🔧 Edit Past"],["summary","📊 Summary"]].map(([v,lbl])=>(
            <button key={v} onClick={()=>setView(v)} className="btn-press" style={{background:view===v?C.primary:"transparent",color:view===v?C.white:C.muted,border:`1.5px solid ${view===v?C.primary:C.border}`,borderRadius:10,padding:"8px 18px",fontWeight:700,fontSize:13,cursor:"pointer",transition:"all .2s"}}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {view==="mark" && (
        <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
          {/* Controls */}
          <div style={{background:`linear-gradient(135deg,${C.primaryPale},${C.tealPale})`,padding:"16px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",borderBottom:`1px solid ${C.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:13,fontWeight:700,color:C.primary}}>📅 Date:</span>
              <input type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)}
              style={{border:`1.5px solid ${isOffDay(selectedDate)?"#f97316":C.primaryMid}`,borderRadius:8,padding:"7px 12px",fontSize:13,background:isOffDay(selectedDate)?"#fff7ed":C.white,color:C.primary,fontWeight:700}}/>
            {isOffDay(selectedDate)&&<div style={{fontSize:11,color:"#c2410c",fontWeight:600,marginTop:4}}>⚠️ {getDateDay(selectedDate)} is an off day</div>}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:13,fontWeight:700,color:C.primary}}>🏫 Class:</span>
              <select value={selectedClass} onChange={e=>setSelectedClass(e.target.value)} style={{border:`1.5px solid ${C.primaryMid}`,borderRadius:8,padding:"7px 12px",fontSize:13,background:C.white,color:C.primary,fontWeight:700,cursor:"pointer"}}>
                {classes.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>markAll("P")} className="btn-press" style={{background:C.primary,color:C.white,border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>✓ All Present</button>
              <button onClick={()=>markAll("A")} className="btn-press" style={{background:C.rose,color:C.white,border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>✗ All Absent</button>
            </div>
            <div style={{marginLeft:"auto",display:"flex",gap:10,alignItems:"center"}}>
              {saved && <span style={{color:C.primary,fontWeight:700,fontSize:13,animation:"popIn .3s ease"}}>✅ Saved!</span>}
              <button onClick={saveDay} className="btn-press" style={{background:`linear-gradient(135deg,${C.primary},${C.primaryLight})`,color:C.white,border:"none",borderRadius:10,padding:"9px 22px",fontWeight:800,fontSize:13,cursor:"pointer",boxShadow:`0 4px 12px ${C.shadow}`}}>Save Attendance</button>
            </div>
          </div>

          {/* Stats bar */}
          <div style={{background:C.bg,padding:"10px 20px",display:"flex",gap:20,fontSize:13,borderBottom:`1px solid ${C.border}`}}>
            <span style={{color:C.primary,fontWeight:700}}>✓ Present: {presentCount}</span>
            <span style={{color:C.rose,fontWeight:700}}>✗ Absent: {absentCount}</span>
            <span style={{color:C.accent,fontWeight:700}}>~ Leave: {leaveCount}</span>
            <span style={{color:C.muted}}>? Unmarked: {unmarked}</span>
            <span style={{marginLeft:"auto",color:C.muted}}>{filteredStudents.length} students shown</span>
          </div>

          {/* ── WhatsApp Absent Notification Panel ── */}
          {waPanel && (()=>{
            const absentStudents = filteredStudents.filter(s=>dayAttendance[s.id]==="A");
            const withContact    = absentStudents.filter(s=>s.contact||s.phone);
            const dateDisplay    = new Date(selectedDate).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
            const schoolName     = S?.school_name_en||"QK Academy";
            const buildMsg = (name) => `Dear Parent, this is to inform you that ${name} was ABSENT today (${dateDisplay}). Please contact the school if this was unexpected. — ${schoolName}`;
            const buildBulkLinks = () => withContact.map(s=>({name:s.name||s.full_name,link:waLink(s.contact||s.phone,buildMsg(s.name||s.full_name))})).filter(x=>x.link);
            return (
              <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,fontWeight:800,color:"#166534"}}>💬 WhatsApp Absent Notifications</span>
                  <span style={{fontSize:12,color:"#6b7280"}}>{absentStudents.length} absent · {withContact.length} have contact numbers</span>
                  <div style={{marginLeft:"auto",display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                    {withContact.length > 1 && (
                      <button
                        onClick={()=>{const links=buildBulkLinks();links.forEach((l,i)=>setTimeout(()=>window.open(l.link,"_blank"),i*400));}}
                        style={{background:"#22c55e",color:"white",border:"none",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}>
                        💬 Notify All Absent ({withContact.length})
                      </button>
                    )}
                    <button onClick={()=>setWaPanel(false)}
                      style={{background:"white",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
                      ✕ Dismiss
                    </button>
                  </div>
                </div>
                {absentStudents.length > 0 && (
                  <div style={{borderTop:"1px solid #bbf7d0"}}>
                    {absentStudents.map((s,i)=>{
                      const phone = s.contact||s.phone;
                      const link  = phone ? waLink(phone, buildMsg(s.name||s.full_name)) : null;
                      return (
                        <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 20px",borderBottom:i<absentStudents.length-1?"1px solid #dcfce7":"none",background:i%2===0?"white":"#f9fffe"}}>
                          <div style={{width:32,height:32,borderRadius:"50%",background:C.rosePale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:900,color:C.rose,flexShrink:0}}>{(s.name||"?")[0]}</div>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{s.name||s.full_name}</div>
                            <div style={{fontSize:11,color:"#6b7280"}}>{s.class} · {phone||"No contact"}</div>
                          </div>
                          {link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer"
                              style={{background:"#22c55e",color:"white",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                              💬 Notify
                            </a>
                          ) : (
                            <span style={{fontSize:11,color:"#9ca3af",fontStyle:"italic"}}>No number</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Student rows */}
          {filteredStudents.length===0 ? (
            <div style={{padding:40,textAlign:"center",color:C.muted}}>No students in this class</div>
          ) : (
            filteredStudents.map((s,i)=>{
              const status = dayAttendance[s.id]||"";
              return (
                <div key={s.id} className="slide-in" style={{animationDelay:`${i*.03}s`,display:"flex",alignItems:"center",padding:"12px 20px",borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg,transition:"background .15s"}}>
                  <div style={{width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${C.primary},${C.teal})`,display:"flex",alignItems:"center",justifyContent:"center",color:C.white,fontWeight:900,fontSize:15,flexShrink:0,marginRight:14}}>{(s.name||"?")[0]}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:C.text}}>{s.name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{s.class} · {(s.stream||"").includes("Both")?"Hifz + School":"School Only"}</div>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    {STATUS.map(st=>(
                      <button key={st.key} onClick={()=>toggle(s.id,st.key)} className="btn-press" style={{background:status===st.key?st.bg:C.bg,color:status===st.key?st.color:C.muted,border:`2px solid ${status===st.key?st.bg:C.border}`,borderRadius:10,padding:"7px 16px",fontWeight:800,fontSize:12,cursor:"pointer",transition:"all .15s",minWidth:86}}>
                        {st.icon} {st.label}
                      </button>
                    ))}
                    {status==="A" && (()=>{
                      const phone = s.contact||s.phone;
                      const dateDisplay = new Date(selectedDate).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                      const msg = `Dear Parent, this is to inform you that ${s.name||s.full_name} was ABSENT today (${dateDisplay}). Please contact the school if this was unexpected. — ${S?.school_name_en||"QK Academy"}`;
                      const link = waLink(phone, msg);
                      return link ? (
                        <a href={link} target="_blank" rel="noopener noreferrer"
                          style={{background:"#22c55e",color:"white",borderRadius:10,padding:"7px 12px",fontSize:12,fontWeight:700,textDecoration:"none",display:"flex",alignItems:"center",gap:4,flexShrink:0,border:"none"}}>
                          💬
                        </a>
                      ) : null;
                    })()}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ── EDIT PAST ATTENDANCE ── */}
      {view==="edit" && (()=>{
        const editStudents = editClass==="All Classes"?students:students.filter(s=>s.class===editClass);
        const dayAtt = attendance[editDate]||{};
        const STATUS = [{key:"P",label:"Present",color:"white",bg:C.primary},{key:"A",label:"Absent",color:"white",bg:C.rose},{key:"L",label:"Leave",color:"white",bg:C.accent},{key:"",label:"Unmark",color:C.muted,bg:C.bg}];

        const editToggle = (id,status) => setAttendance(prev=>({...prev,[editDate]:{...prev[editDate],[id]:status||undefined}}));
        const saveEdit = async () => {
          setEditSaving(true);
          if(onSave) await onSave(editDate, attendance[editDate]||{}, editStudents);
          setEditToast("✅ Attendance saved!"); setTimeout(()=>setEditToast(null),2500);
          setEditSaving(false);
        };
        const markAllEdit = (status) => {
          const updated={...dayAtt};
          editStudents.forEach(s=>{updated[s.id]=status;});
          setAttendance(prev=>({...prev,[editDate]:updated}));
        };

        return (
          <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
            <div style={{background:`linear-gradient(135deg,#7c3aed,#6d28d9)`,padding:"14px 20px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:13,fontWeight:700,color:"white"}}>📅 Date:</span>
                <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)}
                  style={{border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:8,padding:"7px 12px",fontSize:13,background:"rgba(255,255,255,0.15)",color:"white",fontWeight:700}}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:13,fontWeight:700,color:"white"}}>🏫 Class:</span>
                <select value={editClass} onChange={e=>setEditClass(e.target.value)}
                  style={{border:"1.5px solid rgba(255,255,255,0.4)",borderRadius:8,padding:"7px 12px",fontSize:13,background:"rgba(255,255,255,0.15)",color:"white",cursor:"pointer"}}>
                  {classes.map(c=><option key={c} style={{color:"#1a1a1a"}}>{c}</option>)}
                </select>
              </div>
              <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
                {["P","A","L"].map(s=><button key={s} onClick={()=>markAllEdit(s)} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.4)",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>All {s==="P"?"✓ Present":s==="A"?"✗ Absent":"~ Leave"}</button>)}
              </div>
            </div>
            {editToast && <div style={{padding:"8px 20px",background:"#f0fdf7",color:C.primary,fontWeight:700,fontSize:13}}>{editToast}</div>}
            <div style={{background:"#fef3c7",borderBottom:"1px solid #fde68a",padding:"8px 18px",fontSize:12,color:"#92400e"}}>
              ⚠️ Editing past attendance for <strong>{editDate}</strong>. Changes will overwrite existing records in Supabase.
            </div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"#f5f3ff"}}>
                <th style={{padding:"10px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#7c3aed",borderBottom:"1px solid #e9d5ff"}}>Student</th>
                <th style={{padding:"10px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#7c3aed",borderBottom:"1px solid #e9d5ff"}}>Class</th>
                <th style={{padding:"10px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#7c3aed",borderBottom:"1px solid #e9d5ff"}}>Current Status</th>
                <th style={{padding:"10px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#7c3aed",borderBottom:"1px solid #e9d5ff"}}>Change To</th>
              </tr></thead>
              <tbody>
                {editStudents.map((s,i)=>{
                  const cur=dayAtt[s.id]||"";
                  return (
                    <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"white":"#faf5ff"}}>
                      <td style={{padding:"9px 16px",fontWeight:700,fontSize:13}}>{s.name||s.full_name}</td>
                      <td style={{padding:"9px 16px",color:C.muted,fontSize:12}}>{s.class||s["class"]}</td>
                      <td style={{padding:"9px 16px"}}>
                        <span style={{background:cur==="P"?C.primaryPale:cur==="A"?C.rosePale:(cur==="L"||cur==="LV")?C.accentPale:C.bg,color:cur==="P"?C.primary:cur==="A"?C.rose:(cur==="L"||cur==="LV")?C.accent:C.muted,borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700}}>
                          {cur==="P"?"✓ Present":cur==="A"?"✗ Absent":(cur==="L"||cur==="LV")?"~ Leave":"Not marked"}
                        </span>
                      </td>
                      <td style={{padding:"7px 16px"}}>
                        <div style={{display:"flex",gap:5}}>
                          {STATUS.map(st=>(
                            <button key={st.key} onClick={()=>editToggle(s.id,st.key)}
                              style={{background:cur===st.key?st.bg:C.bg,color:cur===st.key?st.color:C.muted,border:`1.5px solid ${cur===st.key?(st.bg||C.border):C.border}`,borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                              {st.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{padding:"12px 20px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end",gap:10}}>
              <button onClick={saveEdit} disabled={editSaving}
                style={{background:editSaving?"#9ca3af":"linear-gradient(135deg,#7c3aed,#6d28d9)",color:"white",border:"none",borderRadius:10,padding:"10px 24px",fontWeight:800,fontSize:13,cursor:editSaving?"not-allowed":"pointer"}}>
                {editSaving?"Saving…":"💾 Save Changes to Supabase"}
              </button>
            </div>
          </div>
        );
      })()}

      {view==="summary" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div className="fade-up" style={{background:C.white,borderRadius:20,padding:22,boxShadow:`0 4px 24px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.primary,marginBottom:16,fontSize:14}}>📈 Daily Attendance Trend</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                <XAxis dataKey="date" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                <YAxis domain={[0,students.length]} tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}}/>
                <Line type="monotone" dataKey="present" stroke={C.primary} strokeWidth={3} dot={{r:4,fill:C.primary}} activeDot={{r:6}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="fade-up" style={{background:C.white,borderRadius:20,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead><tr style={{background:`linear-gradient(135deg,${C.primary},${C.teal})`}}>
                {["Student","Class","Present","Absent","Leave","Rate","Status"].map(h=>(
                  <th key={h} style={{padding:"12px 14px",textAlign:"left",color:C.white,fontWeight:700,fontSize:11}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {summary.map((s,i)=>(
                  <tr key={s.id} className="slide-in" style={{animationDelay:`${i*.03}s`,borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                    <td style={{padding:"11px 14px",fontWeight:700,color:C.text}}>{s.name}</td>
                    <td style={{padding:"11px 14px",color:C.muted,fontSize:12}}>{s.class}</td>
                    <td style={{padding:"11px 14px",color:C.primary,fontWeight:800}}>{s.present}</td>
                    <td style={{padding:"11px 14px",color:C.rose,fontWeight:800}}>{s.absent}</td>
                    <td style={{padding:"11px 14px",color:C.accent,fontWeight:800}}>{s.leave}</td>
                    <td style={{padding:"11px 14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:60,height:6,borderRadius:3,background:C.border,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${s.pct}%`,background:s.pct>=(Number(S?.att_threshold)||85)?C.primary:s.pct>=70?C.accent:C.rose,borderRadius:3,transition:"width .5s"}}/>
                        </div>
                        <span style={{fontWeight:800,color:s.pct>=(Number(S?.att_threshold)||85)?C.primary:s.pct>=70?C.accent:C.rose,fontSize:12}}>{s.pct}%</span>
                      </div>
                    </td>
                    <td style={{padding:"11px 14px"}}>
                      <Badge label={s.pct>=85?"✓ Good":s.pct>=70?"⚠ Warning":"🔴 At Risk"} color={s.pct>=(Number(S?.att_threshold)||85)?C.primary:s.pct>=70?C.accent:C.rose} bg={s.pct>=85?C.primaryPale:s.pct>=70?C.accentPale:C.rosePale} border={s.pct>=85?C.primaryMid:s.pct>=70?C.accentLight:"#fda4af"}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── STUDENT RECORDS ────────────────────────────────────────────────
function StudentRecords({ students, onUpdate, onDelete, onAdd }) {
  const S = useSettings();
  const STREAMS = ["All Streams","School Only","Both (Hifz + School)","Hifz Only","College"];
  const BOARDING_OPTS = ["All","Boarding","Day Scholar"];
  const FEES_OPTS = ["All","Paid","Unpaid"];

  const [search,  setSearch]  = useState("");
  const [filterClass,  setFilterClass]  = useState("All");
  const [filterStream, setFilterStream] = useState("All Streams");
  const [filterBoarding,setFilterBoarding] = useState("All");
  const [filterFees, setFilterFees] = useState("All");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);   // student being edited
  const [adding,  setAdding]  = useState(false);  // show add form
  const [receipting,setReceipting] = useState(null);
  const [receiptFeeData, setReceiptFeeData] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const [conductSummary, setConductSummary] = useState({});

  React.useEffect(()=>{
    supabase.from("student_conduct").select("student_id,type")
      .then(({data})=>{
        if(!data) return;
        const s={};
        data.forEach(r=>{
          const id=String(r.student_id);
          if(!s[id]) s[id]={warn:0,comm:0};
          if(r.type==="commendation") s[id].comm++;
          else s[id].warn++;
        });
        setConductSummary(s);
      });
  },[]);

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const blankForm = {id:"",full_name:"",father_name:"",class:"",stream:"School Only",
    dob:"",contact:"",address:"",cnic:"",occupation:"",enrolled_date:"",
    fees_status:"Unpaid",gender:"Male",monthly_fee:S.default_fee||"1500",is_boarding:false,
    village:"",phone:"",scholarship_status:"None"};

  const [form, setForm] = useState(blankForm);
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const classes = ["All",...ALL_CLASSES.filter(c=>students.some(s=>(s.class||s["class"])===c))];

  const filtered = students.filter(s=>{
    const name  = (s.name||s.full_name||"").toLowerCase();
    const cls   = s.class||s["class"]||"";
    const strm  = s.stream||"";
    const fees  = s.fees||s.fees_status||"";
    const board = s.is_boarding?"Boarding":"Day Scholar";
    if(filterClass!=="All" && cls!==filterClass) return false;
    if(filterStream!=="All Streams" && !strm.includes(filterStream.replace("All Streams",""))) return false;
    if(filterBoarding!=="All" && board!==filterBoarding) return false;
    if(filterFees!=="All" && fees!==filterFees) return false;
    if(search && !name.includes(search.toLowerCase()) && !cls.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // ── Print Roster ───────────────────────────────────────────────
  const printRoster = () => {
    const label = [
      filterClass!=="All"?filterClass:"",
      filterStream!=="All Streams"?filterStream:"",
      filterBoarding!=="All"?filterBoarding:"",
      filterFees!=="All"?`Fees: ${filterFees}`:"",
    ].filter(Boolean).join(" · ") || "All Students";

    const rows = filtered.map((s,i)=>`
      <tr style="background:${i%2===0?"#fff":"#f0fdf7"}">
        <td>${i+1}</td>
        <td>${s.name||s.full_name||""}</td>
        <td>${s.father||s.father_name||""}</td>
        <td>${s.class||s["class"]||""}</td>
        <td>${s.stream||""}</td>
        <td>${s.contact||s.phone||""}</td>
        <td>${s.enrolled||s.enrolled_date||""}</td>
        <td style="color:${(s.fees||s.fees_status)==="Paid"?"#1a6b3c":"#e11d48"};font-weight:700">${s.fees||s.fees_status||""}</td>
        <td>${s.is_boarding?"🏠 Boarding":"—"}</td>
      </tr>`).join("");

    const w = window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Student Roster</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:"Segoe UI",sans-serif;padding:16px;font-size:11px;color:#111;}
      h1{font-size:16px;color:#1a6b3c;margin-bottom:2px;}
      h2{font-size:12px;color:#4a6a52;margin-bottom:10px;font-weight:500;}
      table{width:100%;border-collapse:collapse;}
      th{background:#1a6b3c;color:white;padding:7px 10px;text-align:left;font-size:10px;}
      td{border:1px solid #e5e7eb;padding:6px 10px;}
      .meta{display:flex;justify-content:space-between;margin-bottom:14px;font-size:11px;color:#6b7280;}
      @media print{body{padding:6px;}-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    </style></head><body>
    <h1>🏫 ${S.school_name_en||'QK Academy'} — Student Roster</h1>
    <h2>${label}</h2>
    <div class="meta"><span>Total: <strong>${filtered.length} students</strong></span><span>Printed: ${new Date().toLocaleDateString("en-PK",{day:"2-digit",month:"long",year:"numeric"})}</span></div>
    <table>
      <thead><tr><th>#</th><th>Name</th><th>Father</th><th>Class</th><th>Stream</th><th>Contact</th><th>Enrolled</th><th>Fees</th><th>Boarding</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    </body></html>`);
    w.document.close();
    setTimeout(()=>{w.focus();w.print();},500);
  };

  // ── Save (add/edit) ────────────────────────────────────────────
  const saveStudent = async () => {
    if(!form.full_name||!form.class||!form.stream) return showToast("Name, Class & Stream required",false);
    setSaving(true);
    const payload = {
      full_name:form.full_name, father_name:form.father_name, class:form.class,
      stream:form.stream, dob:form.dob, contact:form.contact, address:form.address,
      cnic:form.cnic, occupation:form.occupation, enrolled_date:form.enrolled_date,
      fees_status:form.fees_status, gender:form.gender,
      monthly_fee:Number(form.monthly_fee)||0,
      is_boarding:!!form.is_boarding, village:form.village||"",
      phone:form.phone||form.contact, scholarship_status:form.scholarship_status||"None"
    };
    if(editing) {
      // UPDATE
      const {error} = await supabase.from("students").update(payload).eq("id",editing.id);
      if(error) { showToast("Error: "+error.message,false); setSaving(false); return; }
      onUpdate && onUpdate({...editing,...payload,name:form.full_name,father:form.father_name,enrolled:form.enrolled_date,fees:form.fees_status});
      showToast("Student updated!"); setEditing(null);
    } else {
      // ADD
      const newId = form.id || ("STU-"+Date.now().toString().slice(-6));
      const {data,error} = await supabase.from("students").insert([{...payload,id:newId}]).select();
      if(error) { showToast("Error: "+error.message,false); setSaving(false); return; }
      const mapped = {id:newId,name:form.full_name,father:form.father_name,
        class:form.class,stream:form.stream,dob:form.dob,contact:form.contact,
        address:form.address,cnic:form.cnic,occupation:form.occupation,
        enrolled:form.enrolled_date,fees:form.fees_status,
        is_boarding:!!form.is_boarding,monthly_fee:Number(form.monthly_fee)||0};
      onAdd && onAdd(mapped);
      showToast("Student added!"); setAdding(false); setForm(blankForm);
    }
    setSaving(false);
  };

  // ── Delete ─────────────────────────────────────────────────────
  const deleteStudent = async (s) => {
    setSaving(true);
    const {error} = await supabase.from("students").delete().eq("id",s.id);
    if(error) { showToast("Error: "+error.message,false); setSaving(false); return; }
    onDelete && onDelete(s.id);
    showToast("Student removed"); setConfirmDel(null); setViewing(null);
    setSaving(false);
  };

  // ── Open edit form ─────────────────────────────────────────────
  const openEdit = (s) => {
    setForm({
      id:s.id, full_name:s.name||s.full_name||"",
      father_name:s.father||s.father_name||"",
      class:s.class||s["class"]||"", stream:s.stream||"School Only",
      dob:s.dob||"", contact:s.contact||"", address:s.address||"",
      cnic:s.cnic||"", occupation:s.occupation||"",
      enrolled_date:s.enrolled||s.enrolled_date||"",
      fees_status:s.fees||s.fees_status||"Unpaid",
      gender:s.gender||"Male",
      monthly_fee:String(s.monthly_fee||1500),
      is_boarding:!!s.is_boarding, village:s.village||"",
      phone:s.phone||s.contact||"",
      scholarship_status:s.scholarship_status||"None"
    });
    setEditing(s); setViewing(null);
  };

  // ── Form modal (shared for Add & Edit) ────────────────────────
  const StudentFormModal = () => (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}
      onClick={()=>{setEditing(null);setAdding(false);}}>
      <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:680,maxHeight:"92vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}
        onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div style={{color:"white",fontWeight:800,fontSize:15}}>{editing?"✏️ Edit Student":"➕ Add New Student"}</div>
          <button onClick={()=>{setEditing(null);setAdding(false);}} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:32,height:32,fontSize:18,cursor:"pointer"}}>×</button>
        </div>
        {/* Body */}
        <div style={{overflowY:"auto",flex:1,padding:"20px 24px"}}>
          {toast && <div style={{marginBottom:12,padding:"10px 14px",borderRadius:10,background:toast.ok?"#f0fdf7":"#fff0f3",color:toast.ok?C.primary:"#e11d48",fontWeight:700,fontSize:13}}>{toast.msg}</div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {[
              {l:"Student ID",k:"id",type:"text",ph:"Auto-generated",dis:!!editing},
              {l:"Full Name *",k:"full_name",type:"text",ph:"Muhammad Ali"},
              {l:"Father's Name",k:"father_name",type:"text",ph:"Ali Khan"},
              {l:"Gender",k:"gender",type:"select",opts:["Male","Female"]},
              {l:"Class *",k:"class",type:"select",opts:["--Select--",...ALL_CLASSES]},
              {l:"Stream *",k:"stream",type:"select",opts:["School Only","Both (Hifz + School)","Hifz Only"]},
              {l:"Date of Birth",k:"dob",type:"date"},
              {l:"Enrolled Date",k:"enrolled_date",type:"date"},
              {l:"Contact",k:"contact",type:"text",ph:"03001234567"},
              {l:"Phone (alt)",k:"phone",type:"text",ph:"03001234568"},
              {l:"Monthly Fee (PKR)",k:"monthly_fee",type:"number",ph:"1500"},
              {l:"Fees Status",k:"fees_status",type:"select",opts:["Paid","Unpaid"]},
              {l:"Scholarship",k:"scholarship_status",type:"select",opts:["None","Full","Partial"]},
              {l:"Occupation (parent)",k:"occupation",type:"text",ph:"Farmer"},
              {l:"CNIC (parent)",k:"cnic",type:"text",ph:"35202-1234567-1"},
              {l:"Village",k:"village",type:"text",ph:"Jatli"},
            ].map(f=>(
              <div key={f.k} style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{f.l}</label>
                {f.type==="select"?(
                  <select value={form[f.k]||""} onChange={e=>upd(f.k,e.target.value)}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13}}>
                    {f.opts.map(o=><option key={o}>{o}</option>)}
                  </select>
                ):(
                  <input type={f.type} value={form[f.k]||""} placeholder={f.ph||""} disabled={f.dis}
                    onChange={e=>upd(f.k,e.target.value)}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,background:f.dis?"#f3f4f6":"white"}}/>
                )}
              </div>
            ))}
          </div>
          <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:5}}>
            <label style={{fontSize:11,fontWeight:700,color:C.primary}}>Address</label>
            <textarea value={form.address||""} onChange={e=>upd("address",e.target.value)} rows={2}
              placeholder="Full address..." style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,resize:"vertical"}}/>
          </div>
          <div style={{marginTop:14,display:"flex",alignItems:"center",gap:10}}>
            <input type="checkbox" id="boarding_chk" checked={!!form.is_boarding} onChange={e=>upd("is_boarding",e.target.checked)}/>
            <label htmlFor="boarding_chk" style={{fontSize:13,fontWeight:600,color:C.text,cursor:"pointer"}}>🏠 Boarding Student</label>
          </div>
        </div>
        {/* Footer */}
        <div style={{padding:"12px 24px",borderTop:`1px solid ${C.border}`,display:"flex",gap:10,background:"white",flexShrink:0}}>
          <button onClick={saveStudent} disabled={saving}
            style={{flex:1,background:saving?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
            {saving?"Saving...":editing?"💾 Save Changes":"➕ Add Student"}
          </button>
          <button onClick={()=>{setEditing(null);setAdding(false);}}
            style={{padding:"11px 20px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
        </div>
      </div>
    </div>
  );

  const exportCSV = () => {
    const headers = ["ID","Name","Father","Class","Stream","DOB","Contact","Address","Fees","Boarding"];
    const rows = filtered.map(s=>[s.id,s.name||s.full_name||"",s.father||s.father_name||"",
      s.class||s["class"]||"",s.stream||"",s.dob||"",s.contact||"",
      s.address||"",s.fees||s.fees_status||"",s.is_boarding?"Yes":"No"]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,"'")}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="QKA_Students.csv"; a.click();
  };

  const ViewModal = ({s,onClose}) => {
    const isHifz = (s.stream||"").includes("Hifz") || (s.stream||"").includes("Both");
    const [hifz, setHifz] = React.useState(null);
    const [hifzForm, setHifzForm] = React.useState(null);
    const [savingHifz, setSavingHifz] = React.useState(false);
    const [showIdCard, setShowIdCard] = React.useState(false);
    const [showChallan, setShowChallan] = React.useState(false);

    React.useEffect(()=>{
      if(!isHifz) return;
      supabase.from("hifz_progress").select("*").eq("student_id",s.id).maybeSingle()
        .then(({data})=>{ setHifz(data); if(data) setHifzForm({...data}); else setHifzForm({student_id:s.id,student_name:s.name||s.full_name,current_juz:1,current_surah:"",sabaq:"",manzil_pages:"",teacher_note:""}); });
    },[s.id]);

    const saveHifz = async () => {
      setSavingHifz(true);
      const payload = {...hifzForm, student_id:s.id, student_name:s.name||s.full_name, updated_at:new Date().toISOString()};
      if(hifz?.id) await supabase.from("hifz_progress").update(payload).eq("id",hifz.id);
      else { const {data} = await supabase.from("hifz_progress").insert(payload).select().single(); setHifz(data); }
      setSavingHifz(false);
    };

    return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)",overflowY:"auto"}} onClick={onClose}>
      <div style={{background:C.white,borderRadius:20,width:540,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,0.25)"}} onClick={e=>e.stopPropagation()}>
        <div style={{background:`linear-gradient(135deg,${C.primary},${C.teal})`,padding:"22px 24px",borderRadius:"20px 20px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:14}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",color:C.white,fontSize:20,fontWeight:900}}>{(s.name||s.full_name||"?")[0]}</div>
            <div>
              <div style={{color:C.white,fontWeight:900,fontSize:16}}>{s.name||s.full_name}</div>
              <div style={{color:"rgba(255,255,255,.8)",fontSize:12}}>{s.id} · {s.class||s["class"]}</div>
            </div>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.2)",color:C.white,border:"none",borderRadius:10,width:34,height:34,fontSize:18,cursor:"pointer",fontWeight:700}}>✕</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          {[["👤 Name",s.name||s.full_name],["👨 Father",s.father||s.father_name],["📚 Class",s.class||s["class"]],["🎓 Stream",s.stream],["🎂 DOB",s.dob],["📞 Contact",s.contact],["🏠 Address",s.address||"—"],["🪪 CNIC",s.cnic||"—"],["💼 Occupation",s.occupation||"—"],["📅 Enrolled",s.enrolled||s.enrolled_date],["💰 Fees",s.fees||s.fees_status],["🏨 Boarding",(s.is_boarding?"Yes":"No")],["💵 Monthly Fee",`PKR ${s.monthly_fee||"—"}`]].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{color:C.muted,fontWeight:600}}>{l}</span>
              <span style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontWeight:700,color:l.includes("Fees")?(v==="Paid"?C.primary:C.rose):C.text}}>{v||"—"}</span>
                {l==="📞 Contact"&&v&&waLink(v,`Assalamu Alaikum! Regarding ${s.name||s.full_name} — `)&&(
                  <a href={waLink(v,`Assalamu Alaikum! Regarding ${s.name||s.full_name} — `)} target="_blank" rel="noopener noreferrer"
                    style={{background:"#16a34a",color:"white",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700,textDecoration:"none",flexShrink:0}}>
                    💬
                  </a>
                )}
              </span>
            </div>
          ))}

          {/* Hifz Progress Section */}
          {isHifz && hifzForm && (
            <div style={{marginTop:20,background:"#f5f3ff",borderRadius:14,padding:"16px 18px",border:"1px solid #c4b5fd"}}>
              <div style={{fontWeight:800,fontSize:14,color:"#7c3aed",marginBottom:14}}>📖 Hifz Progress</div>
              
              {/* Progress Bar */}
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#6b7280",marginBottom:6}}>
                  <span>Juz Completed</span>
                  <span style={{fontWeight:700,color:"#7c3aed"}}>{hifzForm.current_juz-1}/30</span>
                </div>
                <div style={{height:8,background:"#e9d5ff",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${((hifzForm.current_juz-1)/30)*100}%`,background:"linear-gradient(90deg,#7c3aed,#a855f7)",borderRadius:4,transition:"width .3s"}}/>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Current Juz</label>
                  <select value={hifzForm.current_juz} onChange={e=>setHifzForm(p=>({...p,current_juz:Number(e.target.value)}))}
                    style={{width:"100%",padding:"7px 10px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13}}>
                    {Array.from({length:30},(_,i)=>i+1).map(j=><option key={j} value={j}>{`پارہ ${j} — ${QURAN_JUZ.find(q=>q.n===j)?.ar||""}`}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Current Surah</label>
                  <input value={hifzForm.current_surah||""} onChange={e=>setHifzForm(p=>({...p,current_surah:e.target.value}))}
                    placeholder="e.g. Al-Baqarah" style={{width:"100%",padding:"7px 10px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Today's Sabaq</label>
                  <input value={hifzForm.sabaq||""} onChange={e=>setHifzForm(p=>({...p,sabaq:e.target.value}))}
                    placeholder="e.g. Ayat 1-10" style={{width:"100%",padding:"7px 10px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Manzil Pages Revised</label>
                  <input value={hifzForm.manzil_pages||""} onChange={e=>setHifzForm(p=>({...p,manzil_pages:e.target.value}))}
                    placeholder="e.g. 5 pages" style={{width:"100%",padding:"7px 10px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                </div>
              </div>
              <div style={{marginBottom:10}}>
                <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>Teacher's Note</label>
                <textarea value={hifzForm.teacher_note||""} onChange={e=>setHifzForm(p=>({...p,teacher_note:e.target.value}))}
                  placeholder="Progress notes, areas to improve..." rows={2}
                  style={{width:"100%",padding:"7px 10px",border:"1px solid #c4b5fd",borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
              </div>
              {hifz?.updated_at && <div style={{fontSize:11,color:"#9ca3af",marginBottom:8}}>Last updated: {new Date(hifz.updated_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</div>}
              <button onClick={saveHifz} disabled={savingHifz}
                style={{width:"100%",background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"white",border:"none",borderRadius:9,padding:"10px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                {savingHifz?"Saving...":"Save Hifz Progress"}
              </button>
            </div>
          )}

          <div style={{display:"flex",gap:8,marginTop:18,flexWrap:"wrap"}}>
            <button onClick={()=>{
              supabase.from("fee_payments").select("*").eq("student_id",s.id).order("created_at",{ascending:false}).limit(1).then(({data})=>setReceiptFeeData(data?.[0]||null));
              setReceipting(s);onClose();
            }} style={{flex:1,background:C.accentPale,color:C.accent,border:`1.5px solid ${C.accentLight}`,borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🧾 Receipt</button>
            <button onClick={()=>openEdit(s)} style={{flex:1,background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>✏️ Edit</button>
            <button onClick={()=>{setConfirmDel(s);onClose();}} style={{flex:1,background:"#fff0f3",color:"#e11d48",border:"1px solid #fda4af",borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🗑️ Delete</button>
          </div>
          <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
            <button onClick={()=>setShowIdCard(true)} style={{flex:1,background:"#f5f3ff",color:"#7c3aed",border:"1px solid #ddd6fe",borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🪪 ID Card</button>
            <button onClick={()=>setShowChallan(true)} style={{flex:1,background:"#fffbeb",color:"#d97706",border:"1px solid #fde68a",borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>📄 Challan</button>
            <button onClick={()=>{onClose();window.dispatchEvent(new CustomEvent("openConduct",{detail:{studentId:s.id,studentName:s.name||s.full_name}}));}} style={{flex:1,background:"#fff0f3",color:"#dc2626",border:"1px solid #fecaca",borderRadius:10,padding:"9px",fontWeight:700,fontSize:12,cursor:"pointer"}}>📋 Conduct</button>
          </div>
          {showIdCard&&<StudentIDCard student={s} onClose={()=>setShowIdCard(false)}/>}
          {showChallan&&<FeeChallan student={s} onClose={()=>setShowChallan(false)}/>}
        </div>
      </div>
    </div>
    );
  };

  const ReceiptModal = ({s,onClose}) => {
    // Receipt number based on student ID + current month/year
    const _now = new Date();
    const _mon = _now.toLocaleString("default",{month:"short"}).toUpperCase();
    const _yr  = String(_now.getFullYear()).slice(-2);
    const receiptNo = `RCP-${String(s.id||"0000").padStart(4,"0").slice(-4)}-${_mon}${_yr}`;
    const todayFull = new Date().toLocaleDateString("en-PK",{year:"numeric",month:"long",day:"numeric"});
    const isPaid = (s.fees||s.fees_status)==="Paid";
    const amount = s.monthly_fee ? Number(s.monthly_fee).toLocaleString() : "—";
    const thisMonth = new Date().toLocaleDateString("en-PK",{month:"long",year:"numeric"});
    const G = "#1a6b3c", GD = "#0a4a24", GOLD = "#d4af37";

    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(6px)",overflowY:"auto",padding:16}} onClick={onClose}>
        <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:520,boxShadow:"0 32px 80px rgba(0,0,0,0.35)"}} onClick={e=>e.stopPropagation()}>

          {/* ── Printable receipt body ── */}
          <div id="receipt-print">

            {/* Header — full letterhead */}
            <AcademyHeader subtitle="Fee Payment Receipt — رسید فیس"/>

            {/* Gold accent bar */}
            <div style={{height:3,background:`linear-gradient(90deg,${GOLD},#f5d76e,${GOLD})`}}/>

            {/* Receipt meta row */}
            <div style={{background:"#f9fafb",padding:"12px 28px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #e5e7eb"}}>
              <div>
                <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,letterSpacing:.8,textTransform:"uppercase"}}>Receipt No.</div>
                <div style={{fontSize:15,fontWeight:900,color:G,letterSpacing:.5}}>{receiptNo}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,letterSpacing:.8,textTransform:"uppercase"}}>Date Issued</div>
                <div style={{fontSize:13,fontWeight:700,color:"#374151"}}>{todayFull}</div>
              </div>
            </div>

            <div style={{padding:"20px 28px"}}>

              {/* Student info card */}
              <div style={{background:`linear-gradient(135deg,${G}10,${G}05)`,border:`1.5px solid ${G}30`,borderRadius:12,padding:"14px 18px",marginBottom:18,display:"flex",gap:16,alignItems:"center"}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:`linear-gradient(135deg,${G},#0d9488)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                  {s.gender==="Female"?"👧":"👦"}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:900,fontSize:16,color:"#111827",marginBottom:2}}>{s.name||s.full_name}</div>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                    {[["Father",s.father||s.father_name||"—"],["Class",s.class||s["class"]||"—"],["ID",s.id]].map(([l,v])=>(
                      <span key={l} style={{fontSize:11,color:"#6b7280"}}><strong style={{color:G}}>{l}:</strong> {v}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Payment details table */}
              <div style={{border:`1px solid #e5e7eb`,borderRadius:12,overflow:"hidden",marginBottom:18}}>
                <div style={{background:`linear-gradient(135deg,${G},${GD})`,padding:"9px 16px"}}>
                  <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.9)",letterSpacing:.8,textTransform:"uppercase"}}>Payment Details</div>
                </div>
                {[
                  ["Fee Type","Monthly Tuition Fee"],
                  ["Period",thisMonth],
                  ["Payment Method","Cash"],
                ].map(([l,v],i)=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"9px 16px",borderBottom:"1px solid #f3f4f6",background:i%2===0?"white":"#f9fafb"}}>
                    <span style={{fontSize:12,color:"#6b7280",fontWeight:600}}>{l}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"#374151"}}>{v}</span>
                  </div>
                ))}
                {/* Amount row — styled */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:`linear-gradient(135deg,${G}08,${G}03)`,borderTop:`2px solid ${G}30`}}>
                  <span style={{fontSize:13,fontWeight:800,color:G}}>Total Amount</span>
                  <span style={{fontSize:20,fontWeight:900,color:G}}>PKR {amount}</span>
                </div>
              </div>

              {/* Status stamp */}
              <div style={{display:"flex",justifyContent:"center",marginBottom:18}}>
                <div style={{
                  border:`3px solid ${isPaid?"#16a34a":"#dc2626"}`,
                  borderRadius:12,padding:"8px 28px",
                  display:"inline-flex",alignItems:"center",gap:8,
                  transform:"rotate(-2deg)",
                  background:isPaid?"#f0fdf4":"#fef2f2"
                }}>
                  <span style={{fontSize:18}}>{isPaid?"✅":"❌"}</span>
                  <span style={{fontWeight:900,fontSize:20,color:isPaid?"#16a34a":"#dc2626",letterSpacing:2}}>
                    {isPaid?"PAID":"UNPAID"}
                  </span>
                </div>
              </div>

              {/* Signature lines */}
              <div style={{display:"flex",gap:20,marginBottom:14}}>
                {["Received By (School Seal)","Parent / Guardian Signature"].map(l=>(
                  <div key={l} style={{flex:1,textAlign:"center"}}>
                    <div style={{height:40,borderBottom:`1.5px dashed #9ca3af`,marginBottom:6}}/>
                    <div style={{fontSize:10,color:"#9ca3af",fontWeight:600}}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div style={{textAlign:"center",paddingTop:12,borderTop:"1px solid #f3f4f6"}}>
                <div style={{fontSize:10,color:"#9ca3af"}}>
                  This is a computer-generated receipt and is valid without a physical signature.
                </div>
                <div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>
                  {S.school_name_en||DEFAULT_SETTINGS.school_name_en} &nbsp;·&nbsp; Reg. No: {S.reg_no||DEFAULT_SETTINGS.reg_no}
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons — outside print area */}
          <div style={{display:"flex",gap:10,padding:"0 28px 20px"}}>
            <button onClick={()=>window.print()}
              style={{flex:1,background:`linear-gradient(135deg,${G},${GD})`,color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:13,cursor:"pointer",boxShadow:`0 4px 14px ${G}40`}}>
              🖨️ Print Receipt
            </button>
            <button onClick={onClose}
              style={{flex:1,background:"#f3f4f6",color:"#374151",border:"1.5px solid #e5e7eb",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
      {(editing||adding) && <StudentFormModal/>}
      {viewing && <ViewModal s={viewing} onClose={()=>setViewing(null)}/>}
      {receipting && <FeeReceipt student={receipting} feeRecord={receiptFeeData} copyType="school" onClose={()=>{setReceipting(null);setReceiptFeeData(null);}}/>}

      {/* Confirm Delete */}
      {confirmDel && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setConfirmDel(null)}>
          <div style={{background:"white",borderRadius:16,padding:"28px 32px",maxWidth:380,textAlign:"center",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:10}}>🗑️</div>
            <div style={{fontWeight:800,fontSize:16,color:"#1a1a1a",marginBottom:8}}>Delete Student?</div>
            <div style={{color:"#6b7280",fontSize:13,marginBottom:20}}>This will permanently remove <strong>{confirmDel.name||confirmDel.full_name}</strong> and all their records from the database. This cannot be undone.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>deleteStudent(confirmDel)} disabled={saving}
                style={{flex:1,background:"#e11d48",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Deleting...":"Yes, Delete"}
              </button>
              <button onClick={()=>setConfirmDel(null)} style={{flex:1,background:"#f3f4f6",border:"none",borderRadius:10,padding:"11px",fontWeight:700,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"18px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",boxShadow:`0 4px 20px ${C.primary}40`}}>
        <div>
          <div style={{fontWeight:900,fontSize:18,color:"white"}}>👥 Student Records</div>
          <div style={{fontSize:13,opacity:.85,color:"white",marginTop:2}}>{filtered.length} of {students.length} students · Full CRUD + Roster Print</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setAdding(true)} style={{background:"white",color:C.primary,border:"none",borderRadius:10,padding:"9px 16px",fontWeight:800,fontSize:13,cursor:"pointer"}}>➕ Add Student</button>
          <button onClick={printRoster} style={{background:"#f0a500",color:"white",border:"none",borderRadius:10,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:"pointer"}}>🖨️ Print Roster</button>
          <button onClick={exportCSV} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.4)",borderRadius:10,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:"pointer"}}>📥 CSV</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Filter:</span>
        <select value={filterClass} onChange={e=>setFilterClass(e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 12px",fontSize:12,color:C.text}}>
          {classes.map(c=><option key={c}>{c}</option>)}
        </select>
        <select value={filterStream} onChange={e=>setFilterStream(e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 12px",fontSize:12,color:C.text}}>
          {STREAMS.map(s=><option key={s}>{s}</option>)}
        </select>
        <select value={filterBoarding} onChange={e=>setFilterBoarding(e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 12px",fontSize:12,color:C.text}}>
          {BOARDING_OPTS.map(o=><option key={o}>{o}</option>)}
        </select>
        <select value={filterFees} onChange={e=>setFilterFees(e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 12px",fontSize:12,color:C.text}}>
          {FEES_OPTS.map(o=><option key={o}>{o}</option>)}
        </select>
        <div style={{position:"relative",flex:1,minWidth:180}}>
          <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:C.muted}}>🔍</span>
          <input placeholder="Search by name or class…" value={search} onChange={e=>setSearch(e.target.value)}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 12px 7px 32px",fontSize:12,width:"100%"}}/>
        </div>
        {(search||filterClass!=="All"||filterStream!=="All Streams"||filterBoarding!=="All"||filterFees!=="All") && (
          <button onClick={()=>{setSearch("");setFilterClass("All");setFilterStream("All Streams");setFilterBoarding("All");setFilterFees("All");}}
            style={{background:"#fff0f3",color:"#e11d48",border:"1px solid #fda4af",borderRadius:8,padding:"7px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>✕ Clear</button>
        )}
      </div>

      {/* Table */}
      {filtered.length===0 ? (
        <div style={{background:"white",borderRadius:16,padding:60,textAlign:"center",boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontSize:40,marginBottom:12}}>🔍</div>
          <div style={{fontSize:15,fontWeight:700,color:C.primary}}>No students match your filters</div>
        </div>
      ) : (
        <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:`0 2px 12px ${C.shadow}`}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`}}>
                {["#","Name","Father","Class","Stream","Contact","Fees","Boarding","Actions"].map(h=>(
                  <th key={h} style={{padding:"11px 12px",textAlign:"left",color:"white",fontWeight:700,fontSize:11}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s,i)=>{
                const name = s.name||s.full_name||"";
                const fees = s.fees||s.fees_status||"Unpaid";
                return (
                  <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"white":"#f9fffe"}}>
                    <td style={{padding:"10px 12px",color:C.muted,fontSize:11}}>{i+1}</td>
                    <td style={{padding:"10px 12px",fontWeight:700}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{width:28,height:28,borderRadius:"50%",background:`linear-gradient(135deg,${C.primary},#0d9488)`,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:12,fontWeight:800,flexShrink:0}}>{name[0]||"?"}</div>
                        <div>
                          <div style={{fontWeight:700,color:C.text}}>{name}</div>
                          <div style={{fontSize:10,color:C.muted}}>{s.id}</div>
                          {(()=>{const cs=conductSummary[String(s.id)];return cs?(
                            <div style={{display:"flex",gap:3,marginTop:2,flexWrap:"wrap"}}>
                              {cs.comm>0&&<span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:4,padding:"1px 5px",fontSize:9,fontWeight:700}}>🌟{cs.comm}</span>}
                              {cs.warn>0&&<span style={{background:"#fef2f2",color:"#dc2626",borderRadius:4,padding:"1px 5px",fontSize:9,fontWeight:700}}>⚠️{cs.warn}</span>}
                            </div>
                          ):null;})()}
                        </div>
                      </div>
                    </td>
                    <td style={{padding:"10px 12px",color:C.muted,fontSize:12}}>{s.father||s.father_name||"—"}</td>
                    <td style={{padding:"10px 12px",fontWeight:600}}>{s.class||s["class"]||"—"}</td>
                    <td style={{padding:"10px 12px"}}>
                      <span style={{background:(s.stream||"").includes("Both")?"#f5f3ff":(s.stream||"").includes("Hifz")?"#fef3c7":C.primaryPale,color:(s.stream||"").includes("Both")?"#7c3aed":(s.stream||"").includes("Hifz")?"#d97706":C.primary,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700}}>
                        {(s.stream||"").includes("Both")?"Hifz+School":(s.stream||"").includes("Hifz")?"Hifz Only":"School"}
                      </span>
                    </td>
                    <td style={{padding:"10px 12px",color:C.muted,fontSize:12}}>{s.contact||s.phone||"—"}</td>
                    <td style={{padding:"10px 12px"}}>
                      <span style={{background:fees==="Paid"?C.primaryPale:C.rosePale,color:fees==="Paid"?C.primary:C.rose,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700}}>{fees}</span>
                    </td>
                    <td style={{padding:"10px 12px",textAlign:"center",fontSize:13}}>{s.is_boarding?"🏠":"—"}</td>
                    <td style={{padding:"10px 12px"}}>
                      <div style={{display:"flex",gap:5}}>
                        <button onClick={()=>setViewing(s)} style={{background:C.primaryPale,color:C.primary,border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>👁 View</button>
                        {(s.contact||s.phone)&&(
                          <a href={waLink(s.contact||s.phone,`Assalamu Alaikum! Regarding ${s.name||s.full_name} — `)} target="_blank" rel="noopener noreferrer"
                            style={{background:"#dcfce7",color:"#16a34a",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer",textDecoration:"none",display:"inline-flex",alignItems:"center"}}>💬</a>
                        )}
                        <button onClick={()=>openEdit(s)} style={{background:"#eff6ff",color:"#2563eb",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>✏️</button>
                        <button onClick={()=>setConfirmDel(s)} style={{background:"#fff0f3",color:"#e11d48",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function ReportsTab({ students, attendance, results=[] }) {
  const [search, setSearch] = useState("");
  const [filterClass, setFilterClass] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [selected, setSelected] = useState(null);

  // attendance arrives as [{date,student_id,status}] array from attArray — normalise to {date:{sid:status}}
  const attObj = React.useMemo(() => {
    if (Array.isArray(attendance)) {
      const obj = {};
      attendance.forEach(r => { if (r?.date) { if (!obj[r.date]) obj[r.date] = {}; obj[r.date][r.student_id] = r.status; } });
      return obj;
    }
    return attendance || {};
  }, [attendance]);
  const allDates = Object.keys(attObj);
  const classes = ["All",...[...new Set(students.map(s=>s.class||"Unknown"))].sort()];

  const withStats = students.map(s=>{
    const present = allDates.filter(d=>attObj[d]?.[s.id]==="P").length;
    const absent = allDates.filter(d=>attObj[d]?.[s.id]==="A").length;
    const pct = allDates.length ? Math.round((present/allDates.length)*100) : 0;
    return {...s, present, absent, totalDays:allDates.length, attPct:pct};
  });

  const filtered = withStats
    .filter(s=>filterClass==="All"||s.class===filterClass)
    .filter(s=>(s.name||"").toLowerCase().includes(search.toLowerCase())||(s.father||"").toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>sortBy==="name"?a.name?.localeCompare(b.name):sortBy==="att"?b.attPct-a.attPct:sortBy==="fees"?(a.fees==="Paid"?-1:1):0);

  if(selected){
    const s = selected;
    const monthlyAtt = {};
    allDates.forEach(d=>{
      try{
        const dt=new Date(d);
        const key=dt.toLocaleString('default',{month:'long',year:'numeric'});
        if(!monthlyAtt[key]) monthlyAtt[key]={present:0,absent:0,total:0};
        monthlyAtt[key].total++;
        if(attObj[d]?.[s.id]==="P") monthlyAtt[key].present++;
        else if(attObj[d]?.[s.id]==="A") monthlyAtt[key].absent++;
      }catch(e){}
    });
    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelected(null)} style={{background:C.primaryPale,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"8px 16px",fontWeight:700,color:C.primary,cursor:"pointer"}}>← Back</button>
          <div style={{fontSize:18,fontWeight:900,color:C.primary}}>📋 Student Report: {s.name}</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}}>
          <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.primary,marginBottom:14,fontSize:14}}>👤 Student Info</div>
            {[["Full Name",s.name],["Father Name",s.father],["Class",s.class],["Stream",s.stream],["DOB",s.dob],["Contact",s.contact],["CNIC",s.cnic],["Enrolled",s.enrolled],["Fees Status",s.fees]].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                <span style={{fontSize:12,color:C.muted,fontWeight:600}}>{l}</span>
                <span style={{fontSize:13,fontWeight:700,color:l==="Fees Status"?(s.fees==="Paid"?C.primary:C.rose):C.text}}>{v||"—"}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,marginBottom:14,fontSize:14}}>📅 Attendance Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
                {[{label:"Present",value:s.present,color:C.primary,bg:C.primaryPale},{label:"Absent",value:s.absent,color:C.rose,bg:C.rosePale},{label:"Att %",value:`${s.attPct}%`,color:s.attPct>=85?C.primary:C.rose,bg:s.attPct>=85?C.primaryPale:C.rosePale}].map(k=>(
                  <div key={k.label} style={{background:k.bg,borderRadius:10,padding:12,textAlign:"center"}}>
                    <div style={{fontSize:22,fontWeight:900,color:k.color}}>{k.value}</div>
                    <div style={{fontSize:11,color:C.muted,fontWeight:600}}>{k.label}</div>
                  </div>
                ))}
              </div>
              {Object.entries(monthlyAtt).map(([month,data])=>(
                <div key={month} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                  <span style={{color:C.muted,fontWeight:600}}>{month}</span>
                  <span style={{fontWeight:700,color:C.text}}>{data.present}P / {data.absent}A <span style={{color:data.total?Math.round(data.present/data.total*100)>=85?C.primary:C.rose:C.muted}}>({data.total?Math.round(data.present/data.total*100):0}%)</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Marks / Results ── */}
        {(()=>{
          const sResults = results.filter(r=>String(r.student_id)===String(s.id));
          if(!sResults.length) return null;
          const byExam = {};
          sResults.forEach(r=>{ const k=r.exam_name||r.exam_id||"Unknown"; if(!byExam[k]) byExam[k]={name:k,rows:[]}; byExam[k].rows.push(r); });
          const GCOL = g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
          return (
            <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,marginBottom:14,fontSize:14}}>📊 Exam Results</div>
              {Object.values(byExam).map(ex=>(
                <div key={ex.name} style={{marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:12,color:C.muted,textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>{ex.name}</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {ex.rows.map((r,i)=>{
                      const pct = r.total_marks>0 ? Math.round(r.marks_obtained/r.total_marks*100) : 0;
                      return (
                        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:C.bg,borderRadius:8}}>
                          <div style={{flex:1,fontSize:13,fontWeight:600,color:C.text}}>{r.subject||"—"}</div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{r.marks_obtained}/{r.total_marks}</div>
                          <div style={{width:60,height:6,borderRadius:4,background:C.border}}>
                            <div style={{height:6,borderRadius:4,background:GCOL(r.grade),width:`${Math.min(pct,100)}%`}}/>
                          </div>
                          <span style={{fontSize:12,fontWeight:800,color:GCOL(r.grade),minWidth:28,textAlign:"right"}}>{r.grade||pct+"%"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div style={{fontSize:20,fontWeight:900,color:C.primary}}>📊 Student Reports</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name / father..."
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,width:200,outline:"none"}}/>
          <select value={filterClass} onChange={e=>setFilterClass(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,outline:"none"}}>
            {classes.map(c=><option key={c}>{c}</option>)}
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,outline:"none"}}>
            <option value="name">Sort: Name</option>
            <option value="att">Sort: Attendance</option>
            <option value="fees">Sort: Fees Status</option>
          </select>
        </div>
      </div>

      <div style={{background:C.white,borderRadius:16,overflow:"hidden",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:C.primaryPale}}>
              {["Student","Father","Class","Stream","Enrolled","Att %","Fees","Action"].map(h=>(
                <th key={h} style={{padding:"12px 14px",textAlign:"left",fontSize:11,fontWeight:800,color:C.primary,textTransform:"uppercase",letterSpacing:.5}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={8} style={{padding:40,textAlign:"center",color:C.muted,fontSize:14}}>No students found</td></tr>
            )}
            {filtered.map((s,i)=>(
              <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                <td style={{padding:"11px 14px",fontWeight:700,fontSize:13,color:C.text}}>{s.name}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.muted}}>{s.father||"—"}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.text}}>{s.class||"—"}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.text}}>{s.stream||"—"}</td>
                <td style={{padding:"11px 14px",fontSize:12,color:C.muted}}>{s.enrolled||"—"}</td>
                <td style={{padding:"11px 14px"}}>
                  <span style={{fontWeight:800,fontSize:13,color:s.attPct>=85?C.primary:C.rose}}>{s.attPct}%</span>
                  <div style={{marginTop:3,height:4,borderRadius:4,background:C.border,width:60}}>
                    <div style={{height:4,borderRadius:4,background:s.attPct>=85?C.primary:C.rose,width:`${Math.min(s.attPct,100)}%`}}/>
                  </div>
                </td>
                <td style={{padding:"11px 14px"}}>
                  <span style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:20,background:s.fees==="Paid"?C.primaryPale:C.rosePale,color:s.fees==="Paid"?C.primary:C.rose}}>{s.fees||"Unpaid"}</span>
                </td>
                <td style={{padding:"11px 14px"}}>
                  <button onClick={()=>setSelected(s)} style={{background:C.primary,color:"#fff",border:"none",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{fontSize:12,color:C.muted,textAlign:"right"}}>{filtered.length} student{filtered.length!==1?"s":""} shown</div>
    </div>
  );
}
// ── AI REPORT ──────────────────────────────────────────────────────
// -- SCHOLARSHIP MANAGEMENT ------------------------------------------
function ScholarshipManagement({ sheetAPI }) {
  const S = useSettings();
  const [view, setView] = React.useState("log"); // log | detail | new
  const [apps, setApps] = React.useState([]);
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [rejectNote, setRejectNote] = React.useState("");
  const [showRejectBox, setShowRejectBox] = React.useState(false);
  // Conduct history for the currently selected scholarship application
  const [sConduct, setSConduct] = React.useState(null);
  // New application quick-form
  const [newForm, setNewForm] = React.useState({ student_id:"", scholarship_type:"Full", approved_fee:"0", months:6, notes:"", reason:"" });

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3200); };

  const load = async () => {
    setLoading(true);
    try {
      const [schRes, stuRes] = await Promise.all([
        supabase.from("scholarships").select("*").order("submitted_at",{ascending:false}),
        supabase.from("students").select("id,full_name,father_name,class,stream,monthly_fee,scholarship_status,contact,phone").order("full_name")
      ]);
      if(schRes.error) console.warn("Scholarships load error:", schRes.error.message);
      if(stuRes.error) console.warn("Students load error:", stuRes.error.message);
      // Auto-flag expired scholarships
      const schData = (schRes.data||[]).map(a => {
        if(a.status==="approved" && a.expiry_date && new Date(a.expiry_date) < new Date()) {
          return {...a, status:"expired"};
        }
        return a;
      });
      setApps(schData);
      setStudents(stuRes.data||[]);
    } catch(e) {
      console.error("Scholarship load failed:", e);
    }
    setLoading(false);
  };
  React.useEffect(()=>{ load(); },[]);
  React.useEffect(()=>{
    if(!selected?.student_id) { setSConduct(null); return; }
    setSConduct(null);
    supabase.from("student_conduct").select("*")
      .eq("student_id", String(selected.student_id))
      .order("date", {ascending: false})
      .then(({data}) => setSConduct(data||[]));
  }, [selected?.student_id]);

  const STATUS_COLOR = {pending:"#d97706", approved:C.primary, rejected:"#e11d48", expired:"#6b7280"};
  const STATUS_BG    = {pending:"#fef9c3", approved:C.primaryPale, rejected:"#fef2f2", expired:"#f3f4f6"};
  const SCHTYPE_LABEL = {Full:"Full (100% waiver)", "50%":"50% discount", Partial:"Partial (custom amount)"};

  const pending  = apps.filter(a=>(a.status||"pending")==="pending");
  const approved = apps.filter(a=>a.status==="approved");
  const rejected = apps.filter(a=>a.status==="rejected");
  const filtered = filterStatus==="all" ? apps : apps.filter(a=>(a.status||"pending")===filterStatus);

  const clearScholarships = async () => {
    const done = apps.filter(a => a.status==="rejected" || a.status==="expired");
    if (!done.length) { showToast("No rejected/expired applications to clear.", true); return; }
    if (!window.confirm(`Clear ${done.length} rejected/expired scholarship application(s)? This cannot be undone.`)) return;
    setSaving(true);
    const ids = done.map(a => a.id);
    const { error } = await supabase.from("scholarships").delete().in("id", ids);
    if (!error) {
      setApps(prev => prev.filter(a => !ids.includes(a.id)));
      if (selected && ids.includes(selected?.id)) setSelected(null);
      showToast("🗑 " + done.length + " application(s) cleared.");
    } else { showToast("Error: " + error.message, false); }
    setSaving(false);
  };

  const getStu = (id) => students.find(s=>s.id===id)||{};

  const pickStudent = (id) => {
    const s = students.find(x=>x.id===id);
    if(!s) return;
    const orig = s.monthly_fee||1500;
    const fee = newForm.scholarship_type==="Full"?0: newForm.scholarship_type==="50%"?Math.round(orig/2):orig;
    setNewForm(p=>({...p, student_id:id, approved_fee:String(fee)}));
  };

  // Approve: updates student record + creates/marks fee records for N months
  const approve = async (app) => {
    if(!app) return;
    setSaving(true);
    const s = getStu(app.student_id);
    const newFee = Number(app.approved_fee)||0;
    const months = app.months||6;
    const statusLabel = app.scholarship_type==="Full"?"Full Scholarship":
                        app.scholarship_type==="50%"?"50% Scholarship":"Partial Scholarship";
    const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const now = new Date();

    // 1. Update student — try all columns, ignore missing ones gracefully
    const stuUpdate = {scholarship_status: statusLabel};
    if(s?.monthly_fee!==undefined||newFee>=0) stuUpdate.monthly_fee = newFee;
    const stuRes = await supabase.from("students").update(stuUpdate).eq("id", app.student_id);
    if(stuRes.error) console.warn("Student update partial error:", stuRes.error.message);

    // 2. Create fee payment records for approved months (amount = newFee, marked as scholarship)
    const feeRows = [];
    for(let i=0; i<months; i++){
      const d = new Date(now.getFullYear(), now.getMonth()+i, 1);
      feeRows.push({
        student_id: app.student_id,
        student_name: app.student_name,
        class: app.class,
        amount: newFee,
        month: MONTH_NAMES[d.getMonth()],
        year: String(d.getFullYear()),
        method: "Scholarship",
        notes: `${statusLabel} — ${months} months approved`,
        paid_date: new Date().toISOString().slice(0,10),
        receipt_no: "SCH-"+app.id?.slice(0,8)
      });
    }
    if(feeRows.length) await supabase.from("fee_payments").insert(feeRows);

    // 3. Mark scholarship approved + store expiry
    const expiry = new Date(now.getFullYear(), now.getMonth()+months, 1).toISOString().slice(0,10);
    await supabase.from("scholarships").update({status:"approved", approved_at:new Date().toISOString(), expiry_date:expiry}).eq("id",app.id);

    setApps(p=>p.map(a=>a.id===app.id?{...a,status:"approved",expiry_date:expiry}:a));
    setStudents(p=>p.map(st=>st.id===app.student_id?{...st,monthly_fee:newFee,scholarship_status:statusLabel}:st));
    setSelected(null);
    showToast(`Scholarship approved — ${months} months applied to ${app.student_name}'s fee record.`);
    setSaving(false);
    // WhatsApp parent notification
    const appStu = students.find(st=>st.id===app.student_id);
    const phone = appStu?.contact||appStu?.phone||app.contact||app.phone;
    if(phone){
      const feeNote = newFee===0?"completely waived":`reduced to PKR ${newFee}/month`;
      const msg = `Assalamu Alaikum! We are pleased to inform you that the scholarship application for ${app.student_name} has been APPROVED. The monthly fee has been ${feeNote} for ${months} months. JazakAllah Khair for your trust in QK Academy.`;
      const link = waLink(phone, msg);
      if(link) window.open(link,"_blank");
    }
  };

  const reject = async (app, note) => {
    setSaving(true);
    await supabase.from("scholarships").update({status:"rejected", rejection_note:note||"", rejected_at:new Date().toISOString()}).eq("id",app.id);
    setApps(p=>p.map(a=>a.id===app.id?{...a,status:"rejected",rejection_note:note}:a));
    setSelected(null); setShowRejectBox(false); setRejectNote("");
    showToast("Application rejected and noted.");
    setSaving(false);
    // WhatsApp parent notification
    const rejStu = students.find(st=>st.id===app.student_id);
    const rejPhone = rejStu?.contact||rejStu?.phone||app.contact||app.phone;
    if(rejPhone){
      const rejMsg = `Assalamu Alaikum! Regarding the scholarship application for ${app.student_name}: after careful review, we regret to inform you that the application could not be approved at this time${note?` (${note})`:""}. Please contact the academy for further information. JazakAllah Khair — QK Academy`;
      const rejLink = waLink(rejPhone, rejMsg);
      if(rejLink) window.open(rejLink,"_blank");
    }
  };

  const submitNew = async () => {
    if(!newForm.student_id) return showToast("Select a student",false);
    const s = students.find(x=>x.id===newForm.student_id);
    if(!s) return;
    // Server-side duplicate check — pending application guard
    const { data: existCheck } = await supabase.from("scholarships").select("id,status").eq("student_id",s.id).eq("status","pending");
    if(existCheck && existCheck.length>0) return showToast("A pending application already exists for this student. Action it before submitting a new one.",false);
    setSaving(true);
    const {data: insertedRows, error} = await supabase.from("scholarships").insert([{
      student_id:s.id, student_name:s.full_name, father_name:s.father_name,
      contact:s.contact||s.phone||"",
      class:s.class,
      scholarship_type:newForm.scholarship_type,
      approved_fee:Number(newForm.approved_fee)||0,
      months:Number(newForm.months)||6,
      notes:newForm.notes||"",
      reason:newForm.reason||"",
      status:"pending",
      submitted_at:new Date().toISOString()
    }]).select();
    if(error) { showToast("Error: "+error.message,false); setSaving(false); return; }
    await supabase.from("students").update({scholarship_status:"Pending Approval"}).eq("id",s.id);
    // Optimistically add the new record to state immediately so it shows in the log
    if(insertedRows && insertedRows.length > 0) {
      setApps(prev => [insertedRows[0], ...prev]);
    }
    await load();
    setView("log"); setNewForm({student_id:"",scholarship_type:"Full",approved_fee:"0",months:6,notes:"",reason:""});
    showToast("Application submitted — pending management approval.");
    setSaving(false);
  };

  // ── DETAIL VIEW ──
  if(view==="detail" && selected) {
    const app = selected;
    const s = getStu(app.student_id);
    const statusC = STATUS_COLOR[app.status||"pending"];
    const statusB = STATUS_BG[app.status||"pending"];
    return (
      <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>
        {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
        <button onClick={()=>{setView("log");setSelected(null);setShowRejectBox(false);}}
          style={{background:"none",border:"none",color:C.primary,fontWeight:700,fontSize:13,cursor:"pointer",alignSelf:"flex-start",display:"flex",alignItems:"center",gap:6}}>← Back to Log</button>

        {/* Header card */}
        <div style={{background:`linear-gradient(135deg,${C.primary},#0d5e35)`,borderRadius:16,padding:"20px 24px",color:"white",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
          <div style={{width:56,height:56,borderRadius:16,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>🎓</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:900,fontSize:18}}>{app.student_name}</div>
            <div style={{fontSize:13,opacity:.85,marginTop:2}}>{app.class} · Father: {app.father_name||s.father_name||"—"} · {app.contact||s.contact||"—"}</div>
            <div style={{marginTop:6}}>
              <span style={{background:statusB,color:statusC,borderRadius:20,padding:"4px 14px",fontSize:12,fontWeight:800}}>{(app.status||"pending").toUpperCase()}</span>
              {app.expiry_date&&<span style={{marginLeft:8,fontSize:11,opacity:.8}}>Expires: {app.expiry_date}</span>}
            </div>
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          {/* Scholarship details */}
          <div style={{background:"white",borderRadius:16,padding:"20px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:14}}>📋 Scholarship Details</div>
            {[
              ["Type", SCHTYPE_LABEL[app.scholarship_type]||app.scholarship_type],
              ["Monthly Fee After", `PKR ${app.approved_fee||0}`],
              ["Duration", `${app.months||6} months`],
              ["Applied On", (app.submitted_at||app.created_at)?new Date(app.submitted_at||app.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):"—"],
              ["Expiry", app.expiry_date||"Pending approval"],
              ["Original Fee", `PKR ${s.monthly_fee||1500}`],
              ["Savings/Month", `PKR ${(s.monthly_fee||1500)-(app.approved_fee||0)}`],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.muted,fontWeight:600}}>{k}</span>
                <span style={{fontWeight:800,color:C.text}}>{v}</span>
              </div>
            ))}
          </div>

          {/* Student info */}
          <div style={{background:"white",borderRadius:16,padding:"20px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:14}}>👤 Student Info</div>
            {[
              ["Class", s.class||app.class],
              ["Stream", s.stream||"—"],
              ["DOB", s.dob||app.dob||"—"],
              ["Current Fee", `PKR ${s.monthly_fee||1500}/mo`],
              ["Scholarship Status", s.scholarship_status||"None"],
              ["Fee Status", s.fees_status||"—"],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
                <span style={{color:C.muted,fontWeight:600}}>{k}</span>
                <span style={{fontWeight:800,color:C.text}}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reason/Notes */}
        {(app.reason||app.notes)&&(
          <div style={{background:"#fffbeb",borderRadius:12,padding:"14px 18px",border:"1px solid #fde68a"}}>
            <div style={{fontWeight:700,color:"#92400e",fontSize:13,marginBottom:6}}>📝 Reason / Notes</div>
            <div style={{fontSize:13,color:"#78350f"}}>{app.reason||""} {app.notes&&<span style={{color:C.muted}}>— {app.notes}</span>}</div>
          </div>
        )}

        {/* ── Conduct History for scholarship decision ── */}
        {sConduct!==null && (()=>{
          const warns = sConduct.filter(r=>r.type!=="commendation").length;
          const comms = sConduct.filter(r=>r.type==="commendation").length;
          return (
            <div style={{background:"white",borderRadius:16,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.text,fontSize:14,marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>📋 Student Conduct History</span>
                <div style={{display:"flex",gap:8}}>
                  <span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:20,padding:"3px 12px",fontSize:12,fontWeight:700}}>🌟 {comms} Commendations</span>
                  <span style={{background:warns>0?"#fef2f2":"#f9fafb",color:warns>0?"#dc2626":"#6b7280",borderRadius:20,padding:"3px 12px",fontSize:12,fontWeight:700}}>⚠️ {warns} Issues</span>
                </div>
              </div>
              {sConduct.length===0?(
                <div style={{textAlign:"center",padding:"16px 0",color:C.muted,fontSize:13}}>✅ No conduct issues — clean record</div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:180,overflowY:"auto"}}>
                  {sConduct.map((r,i)=>{
                    const t=CONDUCT_TYPES.find(x=>x.id===r.type)||CONDUCT_TYPES[1];
                    return (
                      <div key={i} style={{display:"flex",gap:10,padding:"8px 12px",background:t.bg,borderRadius:8,border:`1px solid ${t.border}`}}>
                        <span style={{fontSize:16,flexShrink:0}}>{t.icon}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",justifyContent:"space-between",gap:8}}>
                            <span style={{fontWeight:700,fontSize:12,color:t.color}}>{t.label}</span>
                            <span style={{fontSize:10,color:C.muted,flexShrink:0}}>{r.date}</span>
                          </div>
                          <div style={{fontSize:11,color:C.text,marginTop:1}}>{r.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Action buttons — only for pending */}
        {(app.status==="pending"||!app.status) && (
          <div style={{background:"white",borderRadius:16,padding:"20px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.text,fontSize:14,marginBottom:14}}>⚖️ Management Decision — All fields editable before approving</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.primary}}>Scholarship Type</label>
                <select value={app.scholarship_type} onChange={e=>setSelected(p=>({...p,scholarship_type:e.target.value,approved_fee:e.target.value==="Full"?0:e.target.value==="50%"?Math.round((s.monthly_fee||1500)/2):p.approved_fee}))}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}>
                  <option value="Full">Full (100% waiver)</option>
                  <option value="50%">50% discount</option>
                  <option value="Partial">Partial (custom amount)</option>
                  <option value="Custom">Custom %</option>
                </select>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.primary}}>Fee After Scholarship (PKR/mo) <span style={{color:C.muted,fontWeight:400}}>— original: PKR {s.monthly_fee||"N/A"}</span></label>
                <input type="number" min="0" value={app.approved_fee||0} onChange={e=>setSelected(p=>({...p,approved_fee:e.target.value}))}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11,fontWeight:700,color:C.primary}}>Duration (months) <span style={{color:C.muted,fontWeight:400}}>1–24</span></label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[1,3,6,9,12,18,24].map(m=>(
                    <button key={m} onClick={()=>setSelected(p=>({...p,months:m}))}
                      style={{padding:"5px 10px",borderRadius:7,border:`1.5px solid ${(app.months||6)===m?C.primary:C.border}`,background:(app.months||6)===m?C.primaryPale:"white",color:(app.months||6)===m?C.primary:C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                      {m}mo
                    </button>
                  ))}
                  <input type="number" min="1" max="24" value={app.months||6} onChange={e=>setSelected(p=>({...p,months:Number(e.target.value)}))}
                    style={{width:60,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"5px 8px",fontSize:13,textAlign:"center"}}/>
                </div>
              </div>
              <div style={{background:C.primaryPale,borderRadius:10,padding:"12px",display:"flex",flexDirection:"column",gap:2,justifyContent:"center"}}>
                <div style={{fontSize:11,color:C.muted,fontWeight:600}}>Fee records created</div>
                <div style={{fontSize:18,fontWeight:900,color:C.primary}}>{app.months||6} months</div>
                <div style={{fontSize:11,color:C.muted}}>PKR {app.approved_fee||0}/mo → Total: PKR {(app.months||6)*(app.approved_fee||0)}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>approve(app)} disabled={saving}
                style={{flex:1,background:saving?C.muted:C.primary,color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Processing...":"✅ Approve & Apply to Fees"}
              </button>
              <button onClick={()=>setShowRejectBox(v=>!v)}
                style={{padding:"11px 20px",background:"#fef2f2",color:"#dc2626",border:"1.5px solid #fecaca",borderRadius:10,fontWeight:800,fontSize:13,cursor:"pointer"}}>
                ❌ Reject
              </button>
            </div>
            {showRejectBox&&(
              <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:8}}>
                <textarea value={rejectNote} onChange={e=>setRejectNote(e.target.value)} rows={2} placeholder="Reason for rejection (will be noted)..."
                  style={{border:`1.5px solid #fca5a5`,borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical"}}/>
                <button onClick={()=>reject(app,rejectNote)} disabled={saving}
                  style={{background:"#dc2626",color:"white",border:"none",borderRadius:8,padding:"9px",fontWeight:700,cursor:saving?"not-allowed":"pointer"}}>
                  Confirm Rejection
                </button>
              </div>
            )}
          </div>
        )}
        {app.rejection_note&&<div style={{background:"#fef2f2",borderRadius:12,padding:"12px 16px",fontSize:13,color:"#7f1d1d",border:"1px solid #fecaca"}}>
          <strong>Rejection reason:</strong> {app.rejection_note}
        </div>}
        {/* Reset fee for expired scholarships */}
        {(app.status==="expired"||app.status==="approved")&&(
          <div style={{background:C.bg,borderRadius:12,padding:"14px 18px",border:`1px solid ${C.border}`}}>
            <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:10}}>🔄 Scholarship Actions</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button onClick={async()=>{
                if(!window.confirm("Reset student fee to original? This will remove scholarship discount.")) return;
                setSaving(true);
                const origFee = s.monthly_fee||1500;
                await supabase.from("students").update({monthly_fee:origFee,scholarship_status:"None"}).eq("id",app.student_id);
                await supabase.from("scholarships").update({status:"expired"}).eq("id",app.id);
                setApps(p=>p.map(a=>a.id===app.id?{...a,status:"expired"}:a));
                showToast("Fee reset — scholarship expired");
                setSaving(false); setSelected(null);
              }} style={{background:"#fff0f3",color:C.rose,border:"1px solid #fda4af",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                ❌ Reset Fee (expire scholarship)
              </button>
              <button onClick={async()=>{
                const extMonths = Number(prompt("Extend by how many months?",6));
                if(!extMonths||isNaN(extMonths)) return;
                const newExpiry = new Date(new Date(app.expiry_date||new Date()).getTime());
                newExpiry.setMonth(newExpiry.getMonth()+extMonths);
                await supabase.from("scholarships").update({status:"approved",expiry_date:newExpiry.toISOString().slice(0,10)}).eq("id",app.id);
                setApps(p=>p.map(a=>a.id===app.id?{...a,status:"approved",expiry_date:newExpiry.toISOString().slice(0,10)}:a));
                showToast(`Scholarship extended by ${extMonths} months`);
              }} style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                ➕ Extend Scholarship
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── NEW APPLICATION FORM ──
  if(view==="new") {
    const selStu = students.find(s=>s.id===newForm.student_id);
    const origFee = selStu?.monthly_fee||1500;

    // Scholarship status checks for selected student
    const existingActive  = newForm.student_id ? apps.find(a=>a.student_id===newForm.student_id && a.status==="approved") : null;
    const existingPending = newForm.student_id ? apps.find(a=>a.student_id===newForm.student_id && a.status==="pending")  : null;
    const hasActiveScholarship = !!existingActive;
    const hasPendingApplication = !!existingPending;
    const canSubmit = !saving && newForm.student_id && !hasPendingApplication;

    // Group students: eligible / has pending / has active
    const eligibleStudents  = students.filter(s=>!apps.find(a=>a.student_id===s.id&&(a.status==="approved"||a.status==="pending")));
    const pendingStudents   = students.filter(s=> apps.find(a=>a.student_id===s.id&&a.status==="pending"));
    const activeStudents    = students.filter(s=> apps.find(a=>a.student_id===s.id&&a.status==="approved"));

    return (
      <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>
        {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
        <button onClick={()=>setView("log")} style={{background:"none",border:"none",color:C.primary,fontWeight:700,fontSize:13,cursor:"pointer",alignSelf:"flex-start"}}>← Back</button>
        <div style={{background:"white",borderRadius:16,padding:"24px",border:`1px solid ${C.border}`,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:16,marginBottom:20}}>🎓 New Scholarship Application</div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Student <span style={{color:C.rose}}>*</span></label>
              <select value={newForm.student_id} onChange={e=>{pickStudent(e.target.value);}}
                style={{border:`1.5px solid ${hasPendingApplication?"#f59e0b":hasActiveScholarship?"#7c3aed":C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13}}>
                <option value="">-- Select Student --</option>
                {eligibleStudents.length>0&&<optgroup label="✅ Eligible (no active scholarship)">{eligibleStudents.map(s=><option key={s.id} value={s.id}>{s.full_name} ({s.class})</option>)}</optgroup>}
                {pendingStudents.length>0&&<optgroup label="⏳ Pending Application">{pendingStudents.map(s=><option key={s.id} value={s.id}>{s.full_name} ({s.class})</option>)}</optgroup>}
                {activeStudents.length>0&&<optgroup label="🎓 Active Scholarship">{activeStudents.map(s=><option key={s.id} value={s.id}>{s.full_name} ({s.class})</option>)}</optgroup>}
              </select>
            </div>

            {/* ── Active scholarship warning ── */}
            {hasActiveScholarship&&(
              <div style={{background:"#f5f3ff",border:"2px solid #c4b5fd",borderRadius:12,padding:"16px 18px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:20}}>🎓</span>
                  <div style={{fontWeight:800,fontSize:14,color:"#5b21b6"}}>Active Scholarship Already Applied</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                  {[
                    ["Type",        existingActive.scholarship_type],
                    ["Fee/Month",   `PKR ${existingActive.approved_fee||0}`],
                    ["Duration",    `${existingActive.months||6} months`],
                    ["Expires",     existingActive.expiry_date ? new Date(existingActive.expiry_date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—"],
                    ["Approved",    existingActive.approved_at ? new Date(existingActive.approved_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—"],
                    ["Status",      "✅ Approved & Active"],
                  ].map(([l,v])=>(
                    <div key={l} style={{background:"white",borderRadius:8,padding:"8px 12px",border:"1px solid #ddd6fe"}}>
                      <div style={{fontSize:10,color:"#7c3aed",fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:700,color:"#3730a3",marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:"#ede9fe",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#5b21b6",fontWeight:600,lineHeight:1.5}}>
                  ⚠️ This student already has an active scholarship. You can still submit a new application (e.g. for renewal or type change), but it will require separate management approval. To extend the current one, go to the existing application and use <strong>Extend Scholarship</strong>.
                </div>
              </div>
            )}

            {/* ── Pending application warning — blocks submit ── */}
            {hasPendingApplication&&(
              <div style={{background:"#fffbeb",border:"2px solid #f59e0b",borderRadius:12,padding:"16px 18px"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:20}}>⏳</span>
                  <div style={{fontWeight:800,fontSize:14,color:"#92400e"}}>Pending Application Already Exists</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                  {[
                    ["Type",        existingPending.scholarship_type],
                    ["Requested Fee", `PKR ${existingPending.approved_fee||0}/mo`],
                    ["Duration",    `${existingPending.months||6} months`],
                    ["Submitted",   existingPending.submitted_at ? new Date(existingPending.submitted_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—"],
                    ["Reason",      existingPending.reason||"—"],
                    ["Status",      "⏳ Awaiting Approval"],
                  ].map(([l,v])=>(
                    <div key={l} style={{background:"white",borderRadius:8,padding:"8px 12px",border:"1px solid #fde68a"}}>
                      <div style={{fontSize:10,color:"#d97706",fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:700,color:"#92400e",marginTop:2}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:"#fef3c7",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#92400e",fontWeight:600,lineHeight:1.5}}>
                  🚫 A pending application already exists for this student. Submitting a duplicate is not allowed. Please review and action the existing application first, or select a different student.
                </div>
                <button onClick={()=>{setSelected({...existingPending});setView("detail");}}
                  style={{marginTop:10,background:"#d97706",color:"white",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  → Go to Existing Application
                </button>
              </div>
            )}

            {selStu&&!hasPendingApplication&&!hasActiveScholarship&&(
              <div style={{background:C.primaryPale,borderRadius:10,padding:"10px 14px",fontSize:12,color:C.primary,fontWeight:600}}>
                ✅ Eligible — Current fee: PKR {origFee}/mo · Scholarship status: {selStu.scholarship_status||"None"}
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Scholarship Type</label>
                <select value={newForm.scholarship_type} onChange={e=>{
                  const t=e.target.value;
                  const fee=t==="Full"?0:t==="50%"?Math.round(origFee/2):origFee;
                  setNewForm(p=>({...p,scholarship_type:t,approved_fee:String(fee)}));
                }} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13}}>
                  <option value="Full">Full (100% waiver)</option>
                  <option value="50%">50% discount</option>
                  <option value="Partial">Partial (custom)</option>
                </select>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Fee After Scholarship (PKR)</label>
                <input type="number" value={newForm.approved_fee} onChange={e=>setNewForm(p=>({...p,approved_fee:e.target.value}))}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Duration (months)</label>
                <select value={newForm.months} onChange={e=>setNewForm(p=>({...p,months:Number(e.target.value)}))}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13}}>
                  {[1,2,3,6,9,12,18,24].map(m=><option key={m} value={m}>{m} month{m>1?"s":""}</option>)}
                </select>
              </div>
              <div style={{background:C.bg,borderRadius:10,padding:"10px 12px",display:"flex",flexDirection:"column",gap:2}}>
                <div style={{fontSize:11,color:C.muted}}>Savings per month</div>
                <div style={{fontSize:20,fontWeight:900,color:C.primary}}>PKR {origFee-(Number(newForm.approved_fee)||0)}</div>
                <div style={{fontSize:11,color:C.muted}}>over {newForm.months} mo = PKR {newForm.months*(origFee-(Number(newForm.approved_fee)||0))}</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Reason / Justification</label>
              <textarea value={newForm.reason} onChange={e=>setNewForm(p=>({...p,reason:e.target.value}))} rows={2}
                placeholder="e.g. Deserving family, orphan, academic excellence..."
                style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13,resize:"vertical"}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Staff Notes (optional)</label>
              <textarea value={newForm.notes} onChange={e=>setNewForm(p=>({...p,notes:e.target.value}))} rows={2}
                placeholder="Additional context for management..."
                style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 12px",fontSize:13,resize:"vertical"}}/>
            </div>
            <button onClick={submitNew} disabled={!canSubmit}
              style={{background:!canSubmit?C.muted:C.primary,color:"white",border:"none",borderRadius:10,padding:"12px",fontWeight:800,fontSize:14,cursor:!canSubmit?"not-allowed":"pointer",opacity:hasPendingApplication?0.5:1}}>
              {saving?"Submitting...":hasPendingApplication?"🚫 Cannot Submit — Pending Application Exists":"📤 Submit for Management Approval"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LOG VIEW (default) ──
  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#7c3aed)`,borderRadius:16,padding:"18px 24px",color:"white",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:18}}>🎓 Scholarship Management</div>
          <div style={{fontSize:12,opacity:.85,marginTop:2}}>Review applications · Approve · Integrated with Fee Database</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={clearScholarships} disabled={saving}
            style={{background:"rgba(220,38,38,0.25)",border:"2px solid rgba(255,255,255,0.4)",color:"white",borderRadius:10,padding:"9px 18px",fontWeight:800,fontSize:13,cursor:"pointer"}}>
            🗑 Clear Rejected
          </button>
          <button onClick={()=>setView("new")}
            style={{background:"rgba(255,255,255,0.2)",border:"2px solid rgba(255,255,255,0.5)",color:"white",borderRadius:10,padding:"9px 18px",fontWeight:800,fontSize:13,cursor:"pointer"}}>
            ➕ New Application
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:12}}>
        {[
          {label:"Total",value:apps.length,color:C.primary,bg:C.primaryPale},
          {label:"Pending",value:pending.length,color:"#d97706",bg:"#fef9c3"},
          {label:"Approved",value:approved.length,color:C.primary,bg:C.primaryPale},
          {label:"Rejected",value:rejected.length,color:"#e11d48",bg:"#fef2f2"},
          {label:"Monthly Savings",value:`PKR ${approved.reduce((s,a)=>s+((students.find(st=>st.id===a.student_id)?.monthly_fee||1500)-(a.approved_fee||0)),0).toLocaleString()}`,color:"#0d9488",bg:"#f0fdfa"},
        ].map(k=>(
          <div key={k.label} style={{background:k.bg,borderRadius:14,padding:"14px 16px",border:`1px solid ${k.color}20`}}>
            <div style={{fontSize:20,fontWeight:900,color:k.color}}>{k.value}</div>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginTop:2}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {["all","pending","approved","rejected","expired"].map(s=>(
          <button key={s} onClick={()=>setFilterStatus(s)}
            style={{padding:"7px 16px",borderRadius:8,border:`1.5px solid ${filterStatus===s?(STATUS_COLOR[s]||C.primary):C.border}`,background:filterStatus===s?(STATUS_BG[s]||C.primaryPale):"white",color:filterStatus===s?(STATUS_COLOR[s]||C.primary):C.muted,fontWeight:700,fontSize:12,cursor:"pointer",textTransform:"capitalize"}}>
            {s==="all"?"All":s.charAt(0).toUpperCase()+s.slice(1)}
            {s!=="all"&&<span style={{marginLeft:6,background:"rgba(0,0,0,0.1)",borderRadius:10,padding:"1px 7px",fontSize:10}}>
              {s==="pending"?pending.length:s==="approved"?approved.length:rejected.length}
            </span>}
          </button>
        ))}
      </div>

      {/* Application log */}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {loading&&<div style={{textAlign:"center",padding:32,color:C.muted}}>Loading...</div>}
        {!loading&&filtered.length===0&&<div style={{textAlign:"center",padding:40,color:C.muted,background:"white",borderRadius:16,border:`1px solid ${C.border}`}}>No applications found.</div>}
        {filtered.map(app=>{
          const s = getStu(app.student_id);
          const statusC = STATUS_COLOR[app.status||"pending"];
          const statusB = STATUS_BG[app.status||"pending"];
          const savings = (s.monthly_fee||1500)-(app.approved_fee||0);
          const isPending = !app.status||app.status==="pending";
          return (
            <div key={app.id} style={{background:"white",borderRadius:14,padding:"14px 18px",border:`1px solid ${isPending?"#fde68a":C.border}`,boxShadow:`0 2px 8px ${C.shadow}`,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",transition:"all .15s",borderLeft:`4px solid ${statusC}`}}>
              <div style={{width:40,height:40,borderRadius:12,background:statusB,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🎓</div>
              <div style={{flex:1,minWidth:180}}>
                <div style={{fontWeight:800,color:C.text,fontSize:14}}>{app.student_name}</div>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                  {app.class} · {app.scholarship_type} · PKR {app.approved_fee||0}/mo · {app.months||6} months
                </div>
                {app.reason&&<div style={{fontSize:11,color:"#92400e",marginTop:2,fontStyle:"italic"}}>"{app.reason}"</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                <span style={{background:statusB,color:statusC,borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:800}}>{(app.status||"pending").toUpperCase()}</span>
                <div style={{fontSize:10,color:C.muted}}>{(app.submitted_at||app.created_at)&&new Date(app.submitted_at||app.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</div>
                {app.status==="approved"&&<div style={{fontSize:10,color:C.primary,fontWeight:700}}>Saves PKR {savings}/mo</div>}
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,alignItems:"center"}}>
                {(s.contact||s.phone||app.contact||app.phone)&&(()=>{
                  const phone = s.contact||s.phone||app.contact||app.phone;
                  const msg = `Assalamu Alaikum! Regarding the scholarship application for ${app.student_name} — `;
                  const link = waLink(phone, msg);
                  return link ? (
                    <a href={link} target="_blank" rel="noopener noreferrer"
                      style={{background:"#dcfce7",color:"#16a34a",border:"1.5px solid #bbf7d0",borderRadius:10,padding:"8px 12px",fontWeight:700,fontSize:12,cursor:"pointer",textDecoration:"none",display:"inline-flex",alignItems:"center"}}>💬</a>
                  ) : null;
                })()}
                <button onClick={()=>{setSelected({...app});setView("detail");}}
                  style={{background:isPending?C.primary:C.bg,color:isPending?"white":C.muted,border:`1.5px solid ${isPending?C.primary:C.border}`,borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  {isPending?"Review →":"View"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// Shared Groq AI helper
const groqGenerate = async (prompt, maxTokens=350) => {
  const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method:"POST",
    headers:{"Content-Type":"application/json","Authorization":"Bearer "+GROQ_KEY_PLACEHOLDER},
    body:JSON.stringify({
      model:"llama-3.3-70b-versatile",
      max_tokens:maxTokens,
      messages:[{role:"user",content:prompt}]
    })
  });
  const data = await res.json();
  if(data.error) throw new Error(data.error.message);
  return data.choices?.[0]?.message?.content||"";
};

function StaffManagement() {
  const S = useSettings();
  const ROLES = ["Teacher","Admin","Accountant","Librarian","Peon","Guard","Cook","Driver"];
  const SUBJECTS = ["Quran Nazra","Quran Hifz","Urdu","English","Mathematics","Science","Social Studies","Islamiat","Computer","General Knowledge","N/A"];

  const emptyForm = {name:"",role:"Teacher",subject:"",phone:"",cnic:"",salary:"",joining_date:"",gender:"",email:""};
  const [staff, setStaff] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState("list");
  const [filterRole, setFilterRole] = React.useState("All");
  const [search, setSearch] = React.useState("");
  const [salaryForm, setSalaryForm] = React.useState({staff_id:"",month:"",year:"",amount:"",method:"Cash",notes:""});
  const [editStaff, setEditStaff] = React.useState(null);
  const [confirmDelStaff, setConfirmDelStaff] = React.useState(null);
  const [salaryHistory, setSalaryHistory] = React.useState([]);
  const [profileStaff, setProfileStaff] = React.useState(null);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const THISYEAR = String(new Date().getFullYear());
  const THISMONTH = new Date().toLocaleString("default",{month:"long"});

  // Payroll tab state (hoisted — hooks cannot live inside IIFE conditionals)
  const [payMonth, setPayMonth] = React.useState(THISMONTH);
  const [payYear, setPayYear] = React.useState(THISYEAR);
  const [payMethod, setPayMethod] = React.useState("Cash");
  const [payRows, setPayRows] = React.useState([]);
  const [confirming, setConfirming] = React.useState(false);
  const [runDone, setRunDone] = React.useState(false);

  // One-off payment tab state (hoisted)
  const [form2, setForm2] = React.useState({staff_id:"",type:"bonus",amount:"",month:THISMONTH,year:THISYEAR,method:"Cash",notes:""});

  // Salary history tab state (hoisted)
  const [hMonth, setHMonth] = React.useState("All");
  const [hYear, setHYear] = React.useState(THISYEAR);
  const [hStaff, setHStaff] = React.useState("All");
  const [expandId, setExpandId] = React.useState(null);

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  React.useEffect(()=>{
    supabase.from("staff").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ if(data) setStaff(data); });
    supabase.from("salary_payments").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ if(data) setSalaryHistory(data); });
  },[]);

  // Payroll tab: rebuild rows whenever month/year/staff/history changes
  React.useEffect(()=>{
    const active = staff.filter(s=>s.status==="Active");
    const alreadyPaid = new Set(
      salaryHistory.filter(h=>h.month===payMonth&&h.year===payYear).map(h=>h.staff_id)
    );
    setPayRows(active.map(s=>({
      ...s,
      baseSalary: Number(s.salary||0),
      bonus: 0,
      deduction: 0,
      notes: "",
      include: !alreadyPaid.has(s.id),
      alreadyPaid: alreadyPaid.has(s.id),
    })));
    setRunDone(false);
  },[payMonth, payYear, staff.length, salaryHistory.length]);

  const saveStaff = async () => {
    if(!form.name||!form.role) return showToast("Name and role are required","error");
    setSaving(true);
    const {data,error} = await supabase.from("staff").insert([{
      name:form.name, role:form.role, subject:form.subject, phone:form.phone,
      cnic:form.cnic, salary:Number(form.salary)||0,
      joining_date:form.joining_date||null, gender:form.gender, status:"Active",
      ...(form.email?{email:form.email}:{}), // only include email if DB column exists
    }]).select();
    if(error){
      if(error.message?.includes("email")){
        // email column doesn't exist yet — insert without it
        const {data:data2,error:err2}=await supabase.from("staff").insert([{
          name:form.name,role:form.role,subject:form.subject,phone:form.phone,
          cnic:form.cnic,salary:Number(form.salary)||0,
          joining_date:form.joining_date||null,gender:form.gender,status:"Active"
        }]).select();
        if(err2) showToast("Error: "+err2.message,false);
        else { setStaff(p=>[data2[0],...p]); showToast("Staff added! (Run SQL to enable email field)"); setForm(emptyForm); fireConfetti(); }
      } else showToast("Error: "+error.message,false);
    } else { setStaff(p=>[data[0],...p]); showToast("Staff member added!"); setForm(emptyForm); fireConfetti(); }
    setSaving(false);
  };

  const toggleStatus = async (id, current) => {
    const newStatus = current==="Active"?"Inactive":"Active";
    const {error} = await supabase.from("staff").update({status:newStatus}).eq("id",id);
    if(!error) setStaff(p=>p.map(s=>s.id===id?{...s,status:newStatus}:s));
  };

  const updateStaff = async () => {
    if(!editStaff||!editStaff.name||!editStaff.role) return showToast("Name and role required",false);
    setSaving(true);
    const {error} = await supabase.from("staff").update({
      name:editStaff.name, role:editStaff.role, subject:editStaff.subject||"",
      phone:editStaff.phone||"", cnic:editStaff.cnic||"",
      salary:Number(editStaff.salary)||0, joining_date:editStaff.joining_date||null,
      gender:editStaff.gender||"", status:editStaff.status||"Active"
    }).eq("id",editStaff.id);
    if(error) showToast("Error: "+error.message,false);
    else { setStaff(p=>p.map(s=>s.id===editStaff.id?{...s,...editStaff}:s)); setEditStaff(null); showToast("Staff updated!"); }
    setSaving(false);
  };

  const deleteStaff = async (s) => {
    setSaving(true);
    const {error} = await supabase.from("staff").delete().eq("id",s.id);
    if(error) { showToast("Error: "+error.message,false); setSaving(false); return; }
    setStaff(p=>p.filter(x=>x.id!==s.id)); setConfirmDelStaff(null); showToast("Staff member removed");
    setSaving(false);
  };

  const printStaffList = () => {
    const roleLabel = filterRole!=="All"?filterRole:"All Roles";
    const rows = filtered.map((s,i)=>`
      <tr style="background:${i%2===0?"#fff":"#f0fdf7"}">
        <td>${i+1}</td>
        <td><strong>${s.name}</strong></td>
        <td>${s.role||"—"}</td>
        <td>${s.subject||"—"}</td>
        <td>${s.gender||"—"}</td>
        <td>${s.phone||"—"}</td>
        <td>${s.joining_date||"—"}</td>
        <td>PKR ${Number(s.salary||0).toLocaleString()}</td>
        <td style="color:${s.status==="Active"?"#1a6b3c":"#e11d48"};font-weight:700">${s.status||"Active"}</td>
      </tr>`).join("");
    const w = window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Staff List</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:"Segoe UI",sans-serif;padding:16px;font-size:11px;color:#111;}
      h1{font-size:16px;color:#1a6b3c;margin-bottom:2px;}
      table{width:100%;border-collapse:collapse;margin-top:10px;}
      th{background:#1a6b3c;color:white;padding:7px 10px;text-align:left;font-size:10px;}
      td{border:1px solid #e5e7eb;padding:6px 10px;}
      .meta{display:flex;justify-content:space-between;margin:8px 0;font-size:11px;color:#6b7280;}
      @media print{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    </style></head><body>
    <h1>🏫 ${S.school_name_en||'QK Academy'} — Staff Register (${roleLabel})</h1>
    <div class="meta">
      <span>Total Staff: <strong>${filtered.length}</strong> &nbsp;|&nbsp; Active: <strong>${filtered.filter(s=>s.status==="Active").length}</strong></span>
      <span>Printed: ${new Date().toLocaleDateString("en-PK",{day:"2-digit",month:"long",year:"numeric"})}</span>
    </div>
    <table>
      <thead><tr><th>#</th><th>Name</th><th>Role</th><th>Subject</th><th>Gender</th><th>Phone</th><th>Joining Date</th><th>Salary</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    </body></html>`);
    w.document.close();
    setTimeout(()=>{w.focus();w.print();},500);
  };

  const saveSalary = async () => {
    if(!salaryForm.staff_id||!salaryForm.month||!salaryForm.amount) return showToast("Fill all required fields","error");
    setSaving(true);
    const member = staff.find(s=>s.id===salaryForm.staff_id);
    const {data,error} = await supabase.from("salary_payments").insert([{
      staff_id:salaryForm.staff_id, staff_name:member?.name,
      month:salaryForm.month, year:salaryForm.year||THISYEAR,
      amount:Number(salaryForm.amount), method:salaryForm.method,
      notes:salaryForm.notes, payment_date:new Date().toISOString().split("T")[0]
    }]).select();
    if(error) showToast("Error: "+error.message,false);
    else { setSalaryHistory(p=>[data[0],...p]); showToast("Salary recorded!"); setSalaryForm({staff_id:"",month:"",year:"",amount:"",method:"Cash",notes:""}); }
    setSaving(false);
  };

  const filtered = staff.filter(s=>{
    if(filterRole!=="All"&&s.role!==filterRole) return false;
    if(search&&!s.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPayroll = staff.filter(s=>s.status==="Active").reduce((a,s)=>a+Number(s.salary||0),0);
  const totalPaidThisMonth = salaryHistory.filter(s=>s.month===THISMONTH&&s.year===THISYEAR).reduce((a,s)=>a+Number(s.amount||0),0);

  const tabBtn = (id,label,icon) => (
    <button onClick={()=>setActiveTab(id)} style={{
      padding:"8px 18px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
      background:activeTab===id?C.primary:C.primaryPale, color:activeTab===id?C.white:C.primary, transition:"all .2s"
    }}>{icon} {label}</button>
  );

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
      {/* Header */}
      <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:20,color:C.primary,marginBottom:4}}>👨‍🏫 Staff Management</div>
        <div style={{fontSize:13,color:C.muted}}>Manage teachers, staff records and salary payments</div>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14}}>
        {[
          {icon:"👥",label:"Total Staff",value:staff.length,color:C.primary,bg:C.primaryPale},
          {icon:"✅",label:"Active Staff",value:staff.filter(s=>s.status==="Active").length,color:C.teal,bg:C.tealPale},
          {icon:"💰",label:"Monthly Payroll",value:`PKR ${totalPayroll.toLocaleString()}`,color:"#7c3aed",bg:C.purplePale},
          {icon:"📤",label:"Paid This Month",value:`PKR ${totalPaidThisMonth.toLocaleString()}`,color:C.accent,bg:C.accentPale},
        ].map((c,i)=>(
          <div key={i} style={{background:C.white,borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 12px ${C.shadow}`,borderTop:`3px solid ${c.color}`}}>
            <div style={{fontSize:24,marginBottom:6}}>{c.icon}</div>
            <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,fontWeight:600}}>{c.label}</div>
            <div style={{fontSize:c.value.toString().length>6?18:26,fontWeight:900,color:c.color,lineHeight:1.1,marginTop:2}}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {tabBtn("list","Staff List","👥")}
        {tabBtn("add","Add Staff","➕")}
        {tabBtn("payroll","Run Payroll","💸")}
        {tabBtn("oneoff","One-off Payment","🎁")}
        {tabBtn("history","Salary History","📋")}
        {tabBtn("staffleave","Leave Requests","📝")}
        {tabBtn("staffatt","Attendance","🗓️")}
        {tabBtn("coverduty","Cover Duty","🔄")}
      </div>

      {/* Staff List */}
      {activeTab==="list" && (
        <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15}}>Staff Directory ({filtered.length})</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <select value={filterRole} onChange={e=>setFilterRole(e.target.value)}
                style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.white}}>
                <option>All</option>
                {ROLES.map(r=><option key={r}>{r}</option>)}
              </select>
              <input placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 12px",fontSize:13,width:150}}/>
              <button onClick={printStaffList} style={{background:"#f0a500",color:"white",border:"none",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🖨️ Print Roster</button>
            </div>
          </div>
          {filtered.length===0 ? (
            <div style={{textAlign:"center",padding:50,color:C.muted}}>
              <div style={{fontSize:40,marginBottom:12}}>👨‍🏫</div>
              <div style={{fontWeight:700,color:C.primary}}>No staff members yet</div>
              <div style={{fontSize:13,marginTop:4}}>Use "Add Staff" tab to register staff</div>
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:C.primaryPale}}>
                    {["Name","Role","Subject","Phone","Salary/Month","Joined","Status","Action"].map(h=>(
                      <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:12,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s,i)=>(
                    <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${C.primary},${C.teal})`,display:"flex",alignItems:"center",justifyContent:"center",color:C.white,fontWeight:800,fontSize:13,flexShrink:0}}>
                            {s.name?.[0]}
                          </div>
                          <div>
                            <div style={{fontWeight:700,fontSize:13}}>{s.name}</div>
                            <div style={{fontSize:11,color:C.muted}}>{s.gender||""}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        <span style={{background:C.primaryPale,color:C.primary,borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700}}>{s.role}</span>
                      </td>
                      <td style={{padding:"10px 12px",color:C.muted}}>{s.subject||"—"}</td>
                      <td style={{padding:"10px 12px",color:C.muted}}>{s.phone||"—"}</td>
                      <td style={{padding:"10px 12px",fontWeight:800,color:C.primary}}>PKR {Number(s.salary||0).toLocaleString()}</td>
                      <td style={{padding:"10px 12px",color:C.muted,whiteSpace:"nowrap"}}>{s.joining_date||"—"}</td>
                      <td style={{padding:"10px 12px"}}>
                        <span style={{
                          background:s.status==="Active"?C.primaryPale:C.rosePale,
                          color:s.status==="Active"?C.primary:C.rose,
                          borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700
                        }}>{s.status}</span>
                      </td>
                      <td style={{padding:"10px 12px"}}>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                          <button onClick={()=>setProfileStaff(s)} style={{background:"#e8f5ee",color:"#1a6b3c",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>👤 Profile</button>
                          <button onClick={()=>setEditStaff({...s})} style={{background:"#eff6ff",color:"#2563eb",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>✏️ Edit</button>
                          <button onClick={()=>toggleStatus(s.id,s.status)} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 9px",fontSize:10,cursor:"pointer",color:C.muted,fontWeight:600}}>{s.status==="Active"?"Deactivate":"Activate"}</button>
                          <button onClick={()=>setConfirmDelStaff(s)} style={{background:"#fff0f3",color:"#e11d48",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit Staff Modal */}
      {editStaff && (
        <Portal>
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}} onClick={()=>setEditStaff(null)}>
          <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:600,maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
            <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,padding:"14px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <div style={{color:"white",fontWeight:800,fontSize:15}}>✏️ Edit Staff Member</div>
              <button onClick={()=>setEditStaff(null)} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:32,height:32,fontSize:18,cursor:"pointer"}}>×</button>
            </div>
            <div style={{overflowY:"auto",flex:1,padding:"20px 24px"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                {[
                  {l:"Full Name *",k:"name",type:"text"},
                  {l:"Role *",k:"role",type:"select",opts:ROLES},
                  {l:"Subject",k:"subject",type:"select",opts:["N/A",...SUBJECTS]},
                  {l:"Gender",k:"gender",type:"select",opts:["Male","Female"]},
                  {l:"Phone",k:"phone",type:"text"},
                  {l:"CNIC",k:"cnic",type:"text"},
                  {l:"Salary (PKR)",k:"salary",type:"number"},
                  {l:"Joining Date",k:"joining_date",type:"date"},
                  {l:"Status",k:"status",type:"select",opts:["Active","Inactive"]},
                ].map(f=>(
                  <div key={f.k} style={{display:"flex",flexDirection:"column",gap:5}}>
                    <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{f.l}</label>
                    {f.type==="select"?(
                      <select value={editStaff[f.k]||""} onChange={e=>setEditStaff(p=>({...p,[f.k]:e.target.value}))}
                        style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13}}>
                        {f.opts.map(o=><option key={o}>{o}</option>)}
                      </select>
                    ):(
                      <input type={f.type} value={editStaff[f.k]||""} onChange={e=>setEditStaff(p=>({...p,[f.k]:e.target.value}))}
                        style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div style={{padding:"12px 24px",borderTop:`1px solid ${C.border}`,display:"flex",gap:10,background:"white",flexShrink:0}}>
              <button onClick={updateStaff} disabled={saving}
                style={{flex:1,background:saving?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Saving...":"💾 Save Changes"}
              </button>
              <button onClick={()=>setEditStaff(null)} style={{padding:"11px 20px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Confirm Delete Staff */}
      {confirmDelStaff && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setConfirmDelStaff(null)}>
          <div style={{background:"white",borderRadius:16,padding:"28px 32px",maxWidth:380,textAlign:"center",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:10}}>🗑️</div>
            <div style={{fontWeight:800,fontSize:16,marginBottom:8}}>Remove Staff Member?</div>
            <div style={{color:"#6b7280",fontSize:13,marginBottom:20}}>This will permanently remove <strong>{confirmDelStaff.name}</strong> from the staff register.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>deleteStaff(confirmDelStaff)} disabled={saving}
                style={{flex:1,background:"#e11d48",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Removing...":"Yes, Remove"}
              </button>
              <button onClick={()=>setConfirmDelStaff(null)} style={{flex:1,background:"#f3f4f6",border:"none",borderRadius:10,padding:"11px",fontWeight:700,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff */}
      {activeTab==="add" && (
        <div style={{background:C.white,borderRadius:16,padding:28,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:20}}>➕ Add New Staff Member</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            {[
              {label:"Full Name *",key:"name",placeholder:"e.g. Muhammad Ali"},
              {label:"Role *",key:"role",type:"select",options:ROLES},
              {label:"Subject (if Teacher)",key:"subject",type:"select",options:["N/A",...SUBJECTS]},
              {label:"Gender",key:"gender",type:"select",options:["","Male","Female"]},
              {label:"Phone Number",key:"phone",placeholder:"03XX-XXXXXXX"},
              {label:"CNIC",key:"cnic",placeholder:"XXXXX-XXXXXXX-X"},
              {label:"Monthly Salary (PKR)",key:"salary",placeholder:"e.g. 15000"},
              {label:"Joining Date",key:"joining_date",type:"date"},
            ].map(f=>(
              <div key={f.key} style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>{f.label}</label>
                {f.type==="select" ? (
                  <select value={form[f.key]} onChange={e=>{
                    const val=e.target.value;
                    setForm(p=>({...p,[f.key]:val}));
                  }}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                    {f.options.map(o=><option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type||"text"} value={form[f.key]} placeholder={f.placeholder}
                    onChange={e=>{
                      const val=e.target.value;
                      if(f.key==="name") setForm(p=>({...p,name:val,email:genStaffEmail(val)}));
                      else setForm(p=>({...p,[f.key]:val}));
                    }}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,color:C.text}}/>
                )}
              </div>
            ))}
            {/* Email — auto-generated, editable */}
            <div style={{display:"flex",flexDirection:"column",gap:5,gridColumn:"1/-1"}}>
              <label style={{fontSize:12,fontWeight:700,color:C.primary}}>
                Portal Email <span style={{fontWeight:400,color:C.muted}}>(auto-generated from name, editable)</span>
              </label>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="email" value={form.email||""} placeholder="will auto-fill when name is typed"
                  onChange={e=>setForm(p=>({...p,email:e.target.value}))}
                  style={{flex:1,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,color:C.text,background:form.email?"#f0fdf4":"white"}}/>
                {form.name&&<button onClick={()=>setForm(p=>({...p,email:genStaffEmail(form.name)}))}
                  style={{background:"#f0fdf4",color:C.primary,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 12px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                  ↺ Reset
                </button>}
              </div>
              {form.email&&<div style={{fontSize:11,color:"#6b7280"}}>
                Format: <strong>initials.lastname@qkacademy.org</strong> — used for portal login
              </div>}
            </div>
          </div>
          <div style={{display:"flex",gap:10,marginTop:20}}>
            <button onClick={()=>setForm(emptyForm)} style={{background:"transparent",border:`1.5px solid ${C.border}`,color:C.muted,borderRadius:10,padding:"10px 24px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Clear</button>
            <button onClick={saveStaff} disabled={saving} style={{
              background:saving?C.muted:C.primary,color:C.white,border:"none",
              borderRadius:10,padding:"10px 28px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer"
            }}>{saving?"Saving...":"Add Staff Member"}</button>
          </div>
        </div>
      )}

      {/* Run Monthly Payroll */}
      {activeTab==="payroll" && (()=>{
        const updateRow = (id,k,v) => setPayRows(p=>p.map(r=>r.id===id?{...r,[k]:v}:r));
        const total = payRows.filter(r=>r.include).reduce((a,r)=>a+(r.baseSalary+Number(r.bonus||0)-Number(r.deduction||0)),0);

        const runPayroll = async () => {
          setConfirming(true);
          const toProcess = payRows.filter(r=>r.include);
          const rows = toProcess.map(r=>({
            staff_id: r.id, staff_name: r.name,
            month: payMonth, year: payYear,
            amount: r.baseSalary + Number(r.bonus||0) - Number(r.deduction||0),
            base_salary: r.baseSalary,
            bonus: Number(r.bonus||0),
            deduction: Number(r.deduction||0),
            method: payMethod,
            payment_type: "monthly",
            notes: r.notes||"",
            payment_date: new Date().toISOString().split("T")[0],
          }));
          const {data,error} = await supabase.from("salary_payments").insert(rows).select();
          if(error) showToast("Error: "+error.message,false);
          else {
            setSalaryHistory(p=>[...(data||[]),...p]);
            setRunDone(true);
            showToast(`✅ Payroll run for ${toProcess.length} staff — PKR ${total.toLocaleString()}`);
          }
          setConfirming(false);
        };

        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Header controls */}
            <div style={{background:C.white,borderRadius:16,padding:"18px 22px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:14}}>💸 Run Monthly Payroll</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.primary}}>MONTH</label>
                  <select value={payMonth} onChange={e=>setPayMonth(e.target.value)}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,background:C.white}}>
                    {MONTHS.map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.primary}}>YEAR</label>
                  <input value={payYear} onChange={e=>setPayYear(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,width:80}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.primary}}>METHOD</label>
                  <select value={payMethod} onChange={e=>setPayMethod(e.target.value)}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,background:C.white}}>
                    {["Cash","Bank Transfer","Cheque"].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{marginLeft:"auto",textAlign:"right"}}>
                  <div style={{fontSize:11,color:C.muted,fontWeight:600}}>TOTAL PAYOUT</div>
                  <div style={{fontWeight:900,fontSize:22,color:C.primary}}>PKR {total.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Staff table */}
            <div style={{background:C.white,borderRadius:16,boxShadow:`0 2px 12px ${C.shadow}`,overflow:"hidden"}}>
              <div style={{background:`linear-gradient(135deg,${C.primary},#0a4a24)`,padding:"10px 18px",display:"grid",gridTemplateColumns:"36px 1fr 100px 90px 90px 90px 1fr",gap:10,alignItems:"center"}}>
                {["✓","Staff","Base","Bonus","Deduct","Net","Notes"].map(h=>(
                  <div key={h} style={{color:"white",fontWeight:700,fontSize:11,textTransform:"uppercase"}}>{h}</div>
                ))}
              </div>
              {payRows.map((r,i)=>{
                const net = r.baseSalary + Number(r.bonus||0) - Number(r.deduction||0);
                return (
                  <div key={r.id} style={{display:"grid",gridTemplateColumns:"36px 1fr 100px 90px 90px 90px 1fr",gap:10,alignItems:"center",padding:"10px 18px",background:i%2===0?C.white:C.bg,borderTop:`1px solid ${C.border}`,opacity:r.include?1:0.5}}>
                    <input type="checkbox" checked={!!r.include} onChange={e=>updateRow(r.id,"include",e.target.checked)}
                      style={{width:18,height:18,cursor:"pointer"}}/>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{r.role}{r.alreadyPaid?" · ⚠️ Already paid":""}</div>
                    </div>
                    <div style={{fontWeight:700,fontSize:13,color:C.muted}}>PKR {r.baseSalary.toLocaleString()}</div>
                    <input type="number" min="0" value={r.bonus||""} onChange={e=>updateRow(r.id,"bonus",e.target.value)}
                      placeholder="0" style={{border:`1.5px solid ${C.border}`,borderRadius:7,padding:"6px 8px",fontSize:12,width:"100%",background:"#f0fdf4",color:"#15803d"}}/>
                    <input type="number" min="0" value={r.deduction||""} onChange={e=>updateRow(r.id,"deduction",e.target.value)}
                      placeholder="0" style={{border:`1.5px solid ${C.border}`,borderRadius:7,padding:"6px 8px",fontSize:12,width:"100%",background:"#fef2f2",color:"#dc2626"}}/>
                    <div style={{fontWeight:800,fontSize:13,color:net>=r.baseSalary?C.primary:net<r.baseSalary?"#dc2626":C.text}}>
                      PKR {net.toLocaleString()}
                    </div>
                    <input type="text" value={r.notes||""} onChange={e=>updateRow(r.id,"notes",e.target.value)}
                      placeholder="Note…" style={{border:`1.5px solid ${C.border}`,borderRadius:7,padding:"6px 8px",fontSize:12,width:"100%"}}/>
                  </div>
                );
              })}
            </div>

            {/* Run button */}
            {!runDone ? (
              <button onClick={runPayroll} disabled={confirming||payRows.filter(r=>r.include).length===0}
                style={{background:confirming?C.muted:`linear-gradient(135deg,${C.primary},#0a4a24)`,color:"white",border:"none",borderRadius:12,padding:"14px 32px",fontWeight:800,fontSize:15,cursor:"pointer",boxShadow:`0 4px 16px ${C.primary}40`,alignSelf:"flex-start"}}>
                {confirming?"Processing…":`💸 Run Payroll — ${payRows.filter(r=>r.include).length} Staff · PKR ${total.toLocaleString()}`}
              </button>
            ) : (
              <div style={{background:"#f0fdf4",borderRadius:12,padding:"16px 20px",border:"2px solid #bbf7d0",display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:28}}>✅</span>
                <div>
                  <div style={{fontWeight:800,color:"#15803d",fontSize:15}}>Payroll Run Complete</div>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{payMonth} {payYear} · PKR {total.toLocaleString()} paid to {payRows.filter(r=>r.include).length} staff</div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* One-off Payment */}
      {activeTab==="oneoff" && (()=>{
        const TYPES = [{v:"bonus",l:"Bonus",icon:"⭐"},{v:"eidi",l:"Eidi",icon:"🎁"},{v:"incentive",l:"Incentive",icon:"🏆"},{v:"advance",l:"Salary Advance",icon:"⬆️"},{v:"deduction",l:"Deduction",icon:"⬇️"},{v:"other",l:"Other",icon:"💬"}];
        const f2 = (k,v) => setForm2(p=>({...p,[k]:v}));

        const saveOneOff = async () => {
          if(!form2.staff_id||!form2.amount) return showToast("Select staff and enter amount",false);
          setSaving(true);
          const member = staff.find(s=>s.id===form2.staff_id);
          const amt = Number(form2.amount)||0;
          const isDeduction = form2.type==="deduction";
          const {data,error} = await supabase.from("salary_payments").insert([{
            staff_id:form2.staff_id, staff_name:member?.name,
            month:form2.month, year:form2.year,
            amount: isDeduction ? -Math.abs(amt) : amt,
            method:form2.method, payment_type:form2.type,
            notes:form2.notes||form2.type,
            payment_date:new Date().toISOString().split("T")[0],
          }]).select();
          if(error) showToast("Error: "+error.message,false);
          else {
            setSalaryHistory(p=>[data[0],...p]);
            showToast(`✅ ${form2.type.charAt(0).toUpperCase()+form2.type.slice(1)} recorded!`);
            setForm2({staff_id:"",type:"bonus",amount:"",month:THISMONTH,year:THISYEAR,method:"Cash",notes:""});
          }
          setSaving(false);
        };

        return (
          <div style={{background:C.white,borderRadius:16,padding:28,boxShadow:`0 2px 12px ${C.shadow}`,maxWidth:600}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:20}}>🎁 One-off Payment / Incentive</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
              {TYPES.map(t=>(
                <button key={t.v} onClick={()=>f2("type",t.v)}
                  style={{padding:"8px 16px",borderRadius:10,border:`2px solid ${form2.type===t.v?C.primary:C.border}`,background:form2.type===t.v?C.primaryPale:C.white,color:form2.type===t.v?C.primary:C.text,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  {t.icon} {t.l}
                </button>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{gridColumn:"1/-1",display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Staff Member *</label>
                <select value={form2.staff_id} onChange={e=>f2("staff_id",e.target.value)}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                  <option value="">-- Select --</option>
                  {staff.filter(s=>s.status==="Active").map(s=><option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
              {[{l:"Amount (PKR) *",k:"amount",ph:"e.g. 2000"},{l:"Month",k:"month",type:"select",opts:MONTHS},{l:"Year",k:"year",ph:THISYEAR},{l:"Method",k:"method",type:"select",opts:["Cash","Bank Transfer","Cheque"]}].map(fd=>(
                <div key={fd.k} style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>{fd.l}</label>
                  {fd.type==="select"
                    ? <select value={form2[fd.k]} onChange={e=>f2(fd.k,e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 10px",fontSize:13,background:C.white}}>{(fd.opts||[]).map(o=><option key={o}>{o}</option>)}</select>
                    : <input value={form2[fd.k]} onChange={e=>f2(fd.k,e.target.value)} placeholder={fd.ph||""} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 10px",fontSize:13}}/>
                  }
                </div>
              ))}
              <div style={{gridColumn:"1/-1",display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Notes</label>
                <input value={form2.notes} onChange={e=>f2("notes",e.target.value)} placeholder="Reason / description"
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 10px",fontSize:13}}/>
              </div>
            </div>
            <button onClick={saveOneOff} disabled={saving} style={{marginTop:20,padding:"11px 28px",background:saving?C.muted:C.primary,color:C.white,border:"none",borderRadius:10,fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
              {saving?"Saving…":"💾 Record Payment"}
            </button>
          </div>
        );
      })()}

      {/* Salary History */}
      {activeTab==="history" && (()=>{
        const histFiltered = salaryHistory.filter(h=>{
          if(hMonth!=="All"&&h.month!==hMonth) return false;
          if(h.year!==hYear) return false;
          if(hStaff!=="All"&&h.staff_id!==hStaff) return false;
          return true;
        });

        function dlCSV(rows,name){
          if(!rows.length){alert("No data to export");return;}
          const headers=Object.keys(rows[0]);
          const csv=[headers.join(","),...rows.map(r=>headers.map(h=>{const v=r[h]??"";return typeof v==="string"&&v.includes(",")?`"${v}"`:v;}).join(","))].join("\n");
          const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));a.download=name;a.click();
        }
        const dlMonthly=()=>dlCSV(histFiltered.map(h=>({"Staff Name":h.staff_name||"","Payment Type":h.payment_type||"monthly","Month":h.month,"Year":h.year,"Base Salary (PKR)":h.base_salary||h.amount||0,"Bonus (PKR)":h.bonus||0,"Deduction (PKR)":h.deduction||0,"Net Amount (PKR)":h.amount||0,"Method":h.method||"Cash","Payment Date":h.payment_date||"","Notes":h.notes||""})),`payroll-${hMonth==="All"?"filtered":hMonth}-${hYear}.csv`);
        const dlAnnual=()=>{
          const uniqueStaff=[...new Map(salaryHistory.filter(h=>h.year===hYear).map(h=>[h.staff_id,{id:h.staff_id,name:h.staff_name}])).values()];
          const rows=uniqueStaff.map(s=>{
            const row={"Staff Name":s.name,"Role":staff.find(x=>x.id===s.id)?.role||""};
            let total=0;
            MONTHS.forEach(m=>{const paid=salaryHistory.filter(h=>h.staff_id===s.id&&h.month===m&&h.year===hYear&&h.payment_type==="monthly").reduce((a,h)=>a+Number(h.amount||0),0);row[m.slice(0,3)]=paid||"";total+=paid;});
            row["Year Total (PKR)"]=total;
            return row;
          });
          dlCSV(rows,`salary-ledger-${hYear}.csv`);
        };
        const dlFull=()=>dlCSV(salaryHistory.map(h=>({"Staff Name":h.staff_name||"","Payment Type":h.payment_type||"monthly","Month":h.month,"Year":h.year,"Amount (PKR)":h.amount||0,"Base Salary":h.base_salary||"","Bonus":h.bonus||"","Deduction":h.deduction||"","Method":h.method||"Cash","Payment Date":h.payment_date||"","Notes":h.notes||""})),`payroll-full-history-${Date.now()}.csv`);

        const TYPE_BADGE = {monthly:{label:"Salary",bg:"#f0fdf4",col:"#15803d"},bonus:{label:"Bonus",bg:"#fefce8",col:"#a16207"},eidi:{label:"Eidi",bg:"#fff7ed",col:"#c2410c"},incentive:{label:"Incentive",bg:"#f5f3ff",col:"#6d28d9"},advance:{label:"Advance",bg:"#eff6ff",col:"#1d4ed8"},deduction:{label:"Deduction",bg:"#fef2f2",col:"#dc2626"},other:{label:"Other",bg:"#f3f4f6",col:"#374151"}};
        const badge = (type) => {
          const b = TYPE_BADGE[type]||TYPE_BADGE.other;
          return <span style={{background:b.bg,color:b.col,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{b.label}</span>;
        };

        // Group by month for the monthly summary
        const byMonth = {};
        salaryHistory.filter(h=>h.year===hYear).forEach(h=>{
          if(!byMonth[h.month]) byMonth[h.month]={month:h.month,total:0,count:0};
          byMonth[h.month].total += Number(h.amount||0);
          byMonth[h.month].count++;
        });
        const monthSummary = Object.values(byMonth).sort((a,b)=>MONTHS.indexOf(a.month)-MONTHS.indexOf(b.month));

        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Payroll CSV Exports */}
            <div style={{background:C.white,borderRadius:16,padding:"16px 22px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:12}}>📥 Export Payroll</div>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                {[
                  {icon:"📄",label:"Monthly Sheet",desc:`${hMonth==="All"?"Filtered view":hMonth+" "+hYear} — one row per payment`,fn:dlMonthly},
                  {icon:"📊",label:"Annual Salary Ledger",desc:`${hYear} — staff vs Jan–Dec pivot with year total`,fn:dlAnnual},
                  {icon:"📋",label:"Full Payment History",desc:"Every payment ever recorded — complete audit trail",fn:dlFull},
                ].map((e,i)=>(
                  <div key={i} style={{flex:"1 1 220px",background:C.bg,borderRadius:12,padding:"12px 14px",display:"flex",alignItems:"center",gap:12,borderLeft:`4px solid ${C.primary}`}}>
                    <span style={{fontSize:22,flexShrink:0}}>{e.icon}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:800,fontSize:13,color:C.text}}>{e.label}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:2}}>{e.desc}</div>
                    </div>
                    <button onClick={e.fn}
                      style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}
                      onMouseEnter={ev=>{ev.currentTarget.style.background=C.primary;ev.currentTarget.style.color=C.white;}}
                      onMouseLeave={ev=>{ev.currentTarget.style.background=C.primaryPale;ev.currentTarget.style.color=C.primary;}}>
                      ⬇ CSV
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly bar summary */}
            <div style={{background:C.white,borderRadius:16,padding:"18px 22px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:14}}>📊 Monthly Payroll Summary — {hYear}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {monthSummary.map(m=>(
                  <div key={m.month} onClick={()=>setHMonth(hMonth===m.month?"All":m.month)}
                    style={{background:hMonth===m.month?C.primaryPale:C.bg,border:`2px solid ${hMonth===m.month?C.primary:C.border}`,borderRadius:10,padding:"10px 14px",cursor:"pointer",minWidth:100,textAlign:"center",transition:"all .15s"}}>
                    <div style={{fontWeight:700,fontSize:11,color:C.muted}}>{m.month.slice(0,3).toUpperCase()}</div>
                    <div style={{fontWeight:900,fontSize:14,color:C.primary,marginTop:2}}>PKR {(m.total/1000).toFixed(0)}k</div>
                    <div style={{fontSize:10,color:C.muted}}>{m.count} payment{m.count!==1?"s":""}</div>
                  </div>
                ))}
                {monthSummary.length===0&&<div style={{color:C.muted,fontSize:13,padding:8}}>No payments for {hYear} yet.</div>}
              </div>
            </div>

            {/* Filters + table */}
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16,alignItems:"center"}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:14,flex:1}}>📋 Payment Records</div>
                <select value={hMonth} onChange={e=>setHMonth(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 10px",fontSize:12,background:C.white}}>
                  <option value="All">All Months</option>
                  {MONTHS.map(m=><option key={m}>{m}</option>)}
                </select>
                <input value={hYear} onChange={e=>setHYear(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 10px",fontSize:12,width:70}}/>
                <select value={hStaff} onChange={e=>setHStaff(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 10px",fontSize:12,background:C.white}}>
                  <option value="All">All Staff</option>
                  {staff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <div style={{fontWeight:800,color:C.primary,fontSize:13}}>PKR {histFiltered.reduce((a,h)=>a+Number(h.amount||0),0).toLocaleString()}</div>
              </div>

              {histFiltered.length===0 ? (
                <div style={{textAlign:"center",padding:40,color:C.muted,fontSize:13}}>No records match the filters.</div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:C.primaryPale}}>
                        {["Staff","Type","Month / Year","Amount","Method","Date","Notes"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {histFiltered.map((h,i)=>(
                        <tr key={h.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                          <td style={{padding:"9px 12px",fontWeight:700}}>{h.staff_name}</td>
                          <td style={{padding:"9px 12px"}}>{badge(h.payment_type||"monthly")}</td>
                          <td style={{padding:"9px 12px",color:C.muted,whiteSpace:"nowrap"}}>{h.month} {h.year}</td>
                          <td style={{padding:"9px 12px",fontWeight:800,color:Number(h.amount||0)<0?"#dc2626":C.primary}}>PKR {Number(h.amount||0).toLocaleString()}</td>
                          <td style={{padding:"9px 12px"}}><span style={{background:C.primaryPale,color:C.primary,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{h.method}</span></td>
                          <td style={{padding:"9px 12px",color:C.muted,whiteSpace:"nowrap"}}>{h.payment_date}</td>
                          <td style={{padding:"9px 12px",color:C.muted,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.notes||"—"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{background:C.primaryPale,borderTop:`2px solid ${C.primaryMid}`}}>
                        <td colSpan={3} style={{padding:"9px 12px",fontWeight:800,color:C.primary}}>Total ({histFiltered.length} records)</td>
                        <td style={{padding:"9px 12px",fontWeight:900,color:C.primary}}>PKR {histFiltered.reduce((a,h)=>a+Number(h.amount||0),0).toLocaleString()}</td>
                        <td colSpan={3}/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Staff Leave Requests tab */}
      {activeTab==="staffleave" && <StaffLeaveAdmin staff={staff}/>}

      {/* Staff Attendance tab */}
      {activeTab==="staffatt" && <StaffAttendanceAdmin staff={staff}/>}

      {/* Cover Duty tab */}
      {activeTab==="coverduty" && <CoverDutyAdmin staff={staff}/>}

      {/* Staff Profile Modal */}
      {profileStaff && (
        <StaffProfile staff={profileStaff} onClose={()=>setProfileStaff(null)}/>
      )}
    </div>
  );
}


// ── SETTINGS MANAGEMENT ──────────────────────────────────────────


// ── EXPORT DATA ────────────────────────────────────────────────────
function ExportDataManagement({ students = [], attendance = [], fees = [], results = [] }) {
  const MONTHS_ORD = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [expMonth,        setExpMonth]        = useState(-1); // -1 = All Months
  const [expYear,         setExpYear]         = useState(new Date().getFullYear());
  const [selExam,         setSelExam]         = useState("All");
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Extra data — self-loaded on mount so Export tab is fully self-sufficient
  const [staffData,       setStaffData]       = useState([]);
  const [salaryData,      setSalaryData]      = useState([]);
  const [staffAttData,    setStaffAttData]    = useState([]);
  const [staffLeaveData,  setStaffLeaveData]  = useState([]);
  const [staffConduct,    setStaffConduct]    = useState([]);
  const [studentConduct,  setStudentConduct]  = useState([]);
  const [scholarshipData, setScholarshipData] = useState([]);
  const [admissions,      setAdmissions]      = useState([]);
  const [calEvents,       setCalEvents]       = useState([]);
  const [studentLeave,    setStudentLeave]    = useState([]);
  const [coverDutyData,   setCoverDutyData]   = useState([]);
  const [extraLoading,    setExtraLoading]    = useState(true);

  React.useEffect(() => {
    (async () => {
      setExtraLoading(true);
      const [st, sl, sa, slr, sc, sct, sch, adm, cal, slv, cd] = await Promise.all([
        supabase.from("staff").select("*").order("name"),
        supabase.from("salary_payments").select("*").order("created_at",{ascending:false}),
        supabase.from("staff_attendance").select("*").order("date",{ascending:false}),
        supabase.from("staff_leave_requests").select("*").order("created_at",{ascending:false}),
        supabase.from("staff_conduct").select("*").order("date",{ascending:false}),
        supabase.from("student_conduct").select("*").order("date",{ascending:false}),
        supabase.from("scholarships").select("*").order("created_at",{ascending:false}),
        supabase.from("admission_applications").select("*").order("created_at",{ascending:false}),
        supabase.from("school_events").select("*").order("date",{ascending:false}),
        supabase.from("leave_requests").select("*").order("created_at",{ascending:false}),
        supabase.from("cover_duties").select("*").order("date",{ascending:false}),
      ]);
      setStaffData(st.data||[]);
      setSalaryData(sl.data||[]);
      setStaffAttData(sa.data||[]);
      setStaffLeaveData(slr.data||[]);
      setStaffConduct(sc.data||[]);
      setStudentConduct(sct.data||[]);
      setScholarshipData(sch.data||[]);
      setAdmissions(adm.data||[]);
      setCalEvents(cal.data||[]);
      setStudentLeave(slv.data||[]);
      setCoverDutyData(cd.data||[]);
      setExtraLoading(false);
    })();
  }, []);

  const allExams  = ["All", ...[...new Set(results.map(r=>r.exam_name).filter(Boolean))].sort()];
  const getGrade  = (obt, tot) => { const p=tot>0?(obt/tot)*100:0; if(p>=90)return"A+"; if(p>=80)return"A"; if(p>=70)return"B+"; if(p>=60)return"B"; if(p>=50)return"C"; return"F"; };
  const attArr    = Array.isArray(attendance) ? attendance : Object.entries(attendance||{}).flatMap(([date,ids])=>Object.entries(ids||{}).map(([sid,status])=>({date,student_id:sid,status})));
  const daysBetween = (a,b) => (a&&b) ? Math.max(1,Math.round((new Date(b)-new Date(a))/86400000)+1) : 0;

  function dlCSV(rows, name){
    if(!rows||!rows.length){ alert("No data to export."); return; }
    const headers = Object.keys(rows[0]);
    const esc = v => { const s=String(v??""); return (s.includes(",")||s.includes('"')||s.includes("\n")) ? `"${s.replace(/"/g,'""')}"` : s; };
    const csv = [headers.join(","), ...rows.map(r=>headers.map(h=>esc(r[h])).join(","))].join("\n");
    const a   = document.createElement("a"); a.href=URL.createObjectURL(new Blob(["﻿"+csv,{type:"text/csv;charset=utf-8"}])); a.download=name; a.click();
  }

  // ── Bulk Excel download ──────────────────────────────────────────
  const buildSheet = (rows) => {
    if(!rows||!rows.length) return XLSX.utils.aoa_to_sheet([["No data"]]);
    return XLSX.utils.json_to_sheet(rows);
  };

  const downloadAllAsExcel = async () => {
    setBulkDownloading(true);
    const getGradeL = getGrade;
    const wb = XLSX.utils.book_new();
    const label = (s) => XLSX.utils.book_append_sheet(wb, buildSheet(s.rows), s.name);

    // ── Students & Attendance ──
    label({ name:"Students", rows: students.map(s=>({Name:s.name||s.full_name||"",Class:s.class||"","Father Name":s.father||s.father_name||"",Contact:s.contact||s.phone||"",Gender:s.gender||"",Stream:s.stream||"",Enrolled:s.enrolled||s.enrolled_date||"","Fee Status":s.fees||"",Status:s.status||""})) });
    label({ name:"Student Attendance", rows: attArr.map(a=>{const s=students.find(x=>x.id===a.student_id)||{};return{Name:s.name||s.full_name||a.student_id,Class:s.class||a.class||"",Date:a.date,Status:a.status,Note:a.note||""};}) });
    label({ name:"Student Leave", rows: studentLeave.map(r=>({Student:r.student_name||"","From":r.from_date,"To":r.to_date,Days:daysBetween(r.from_date,r.to_date),Reason:r.reason||"",Status:r.status,"Admin Note":r.admin_note||"","Applied On":r.created_at?.split("T")[0]||""})) });
    label({ name:"Student Conduct", rows: studentConduct.map(r=>{const s=students.find(x=>x.id===r.student_id)||{};return{Student:s.name||s.full_name||r.student_name||"",Class:s.class||r.class||"",Date:r.date||"",Type:r.type||"",Title:r.title||"",Details:r.notes||r.description||""};}) });

    // ── Fees ──
    label({ name:"Fee History", rows: fees.map(f=>({Receipt:f.receipt_no||"",Student:f.student_name||"",Class:f.class||"",Month:f.month||"",Year:f.year||"","Amount (PKR)":Number(f.amount||0),Method:f.method||"Cash",Date:f.paid_date||""})) });
    label({ name:"Scholarships", rows: scholarshipData.map(s=>({Student:s.student_name||"",Class:s.class||"",Type:s.type||"","Discount (PKR)":s.discount||0,Reason:s.reason||"",Status:s.status||"",From:s.from_date||"",To:s.to_date||""})) });

    // ── Academic ──
    label({ name:"Exam Results", rows: results.map(r=>{const s=students.find(x=>x.id===r.student_id)||{};const pct=r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0;return{Student:s.name||s.full_name||"",Class:r.class||s.class||"",Exam:r.exam_name||"",Subject:r.subject||"",Marks:r.marks_obtained||0,"Out Of":r.total_marks||0,"%":pct,Grade:r.grade||getGradeL(r.marks_obtained,r.total_marks)};}) });

    // ── Admissions ──
    label({ name:"Admissions", rows: admissions.map(a=>({Name:a.student_name||a.name||"","Father Name":a.father_name||"",Class:a.class_applying||a.class||"",Phone:a.phone||"",Email:a.email||"",Status:a.status||"","Applied On":a.created_at?.split("T")[0]||""})) });

    // ── Staff ──
    label({ name:"Staff Directory", rows: staffData.map(s=>({Name:s.name||"",Role:s.role||"",Subject:s.subject||"",Gender:s.gender||"",Phone:s.phone||"",CNIC:s.cnic||"","Salary (PKR)":s.salary||0,"Joining Date":s.joining_date||"",Status:s.status||""})) });
    label({ name:"Payroll History", rows: salaryData.map(h=>({Staff:h.staff_name||"",Type:h.payment_type||"monthly",Month:h.month||"",Year:h.year||"","Amount (PKR)":h.amount||0,"Bonus":h.bonus||0,"Deduction":h.deduction||0,Method:h.method||"Cash","Payment Date":h.payment_date||""})) });
    label({ name:"Staff Attendance", rows: staffAttData.map(r=>({Staff:r.staff_name||"",Date:r.date||"",Status:r.status||"","Marked By":r.marked_by||"","Check In":r.check_in||"","Check Out":r.check_out||"",Notes:r.notes||""})) });
    label({ name:"Staff Leave", rows: staffLeaveData.map(r=>({Staff:r.staff_name||"","Leave Type":r.leave_type||"","From":r.from_date,"To":r.to_date,Days:daysBetween(r.from_date,r.to_date),Reason:r.reason||"",Status:r.status,"Admin Note":r.admin_note||""})) });
    label({ name:"Staff Conduct", rows: staffConduct.map(r=>({Staff:r.staff_name||"",Date:r.date||"",Type:r.type||"",Title:r.title||"",Details:r.body||""})) });

    // ── Calendar ──
    label({ name:"School Events", rows: calEvents.map(e=>({Title:e.title||"",Type:e.event_type||"",Date:e.date||"",Class:e.class||"All",Description:e.description||""})) });
    label({ name:"Cover Duties", rows: coverDutyData.map(r=>({Date:r.date||"","Absent Staff":r.absent_staff_name||"","Cover Staff":r.cover_staff_name||"Unassigned",Class:r.class_name||"",Subject:r.subject||"","Period":r.period_no!=null&&r.period_no<200?r.period_no:"manual",Time:r.period_time||"",Status:r.status||""})) });

    const date = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `QK-Academy-Full-Export-${date}.xlsx`);
    setBulkDownloading(false);
  };

  // ── Section builder helpers ──────────────────────────────────────
  const ExportCard = ({e}) => (
    <div style={{background:C.white,borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",alignItems:"center",gap:14,borderLeft:`4px solid ${C.primary}`}}>
      <span style={{fontSize:24,flexShrink:0}}>{e.icon}</span>
      <div style={{flex:1}}>
        <div style={{fontWeight:800,fontSize:13,color:C.text,marginBottom:2}}>{e.label}</div>
        <div style={{fontSize:11,color:C.muted}}>{e.desc}</div>
      </div>
      <button onClick={e.fn}
        style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:10,padding:"7px 15px",fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}
        onMouseEnter={ev=>{ev.currentTarget.style.background=C.primary;ev.currentTarget.style.color=C.white;}}
        onMouseLeave={ev=>{ev.currentTarget.style.background=C.primaryPale;ev.currentTarget.style.color=C.primary;}}>
        ⬇ CSV
      </button>
    </div>
  );
  const SectionHead = ({title,sub,count}) => (
    <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8,marginBottom:0}}>
      <div style={{fontWeight:900,fontSize:14,color:C.primary}}>{title}</div>
      {count!=null&&<span style={{background:C.primaryPale,color:C.primary,borderRadius:20,padding:"1px 8px",fontSize:11,fontWeight:700}}>{count} records</span>}
      {sub&&<div style={{fontSize:11,color:C.muted}}>{sub}</div>}
    </div>
  );
  const Grid = ({items}) => (
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:12}}>
      {items.map((e,i)=><ExportCard key={i} e={e}/>)}
    </div>
  );

  // ── 1. Students & Attendance ─────────────────────────────────────
  const studentExports = [
    { icon:"👥", label:"Student Roster",    desc:"Full list — name, class, father, contact, enrolled date, gender",
      fn:()=>dlCSV(students.map(s=>({Name:s.name||s.full_name||"",Class:s.class||"","Father Name":s.father||s.father_name||"",Contact:s.contact||s.phone||"",Gender:s.gender||"",Stream:s.stream||"",Enrolled:s.enrolled||s.enrolled_date||"","Fee Status":s.fees||"",Status:s.status||""})),`students-roster-${Date.now()}.csv`) },
    { icon:"📅", label:"Student Attendance (All)", desc:"Every attendance record — date, student, class, status",
      fn:()=>dlCSV(attArr.map(a=>{const s=students.find(x=>x.id===a.student_id)||{};return{Name:s.name||s.full_name||a.student_id,Class:s.class||a.class||"",Date:a.date,Status:a.status,Note:a.note||""};}),`student-attendance-all-${Date.now()}.csv`) },
    { icon:"📊", label:"Attendance Summary (per student)", desc:"Total present/absent/late count per student",
      fn:()=>{
        const map={};
        attArr.forEach(a=>{if(!map[a.student_id])map[a.student_id]={P:0,A:0,L:0,LV:0};const k=a.status==="Present"||a.status==="P"?"P":a.status==="Absent"||a.status==="A"?"A":a.status==="Late"||a.status==="L"?"L":"LV";map[a.student_id][k]++;});
        dlCSV(students.map(s=>{const r=map[s.id]||{P:0,A:0,L:0,LV:0};const tot=r.P+r.A+r.L+r.LV;return{Name:s.name||s.full_name||"",Class:s.class||"",Present:r.P,Absent:r.A,Late:r.L,Leave:r.LV,Total:tot,"Att %":tot>0?Math.round(((r.P+r.L)/tot)*100)+"%" :"—"};}),`attendance-summary-${Date.now()}.csv`);
      }},
    { icon:"📝", label:"Student Leave Requests", desc:"All student leave request history — status, dates, reason",
      fn:()=>dlCSV(studentLeave.map(r=>({Student:r.student_name||"","From":r.from_date,"To":r.to_date,Days:daysBetween(r.from_date,r.to_date),Reason:r.reason||"",Status:r.status,"Admin Note":r.admin_note||"","Decided By":r.decided_by||"","Applied On":r.created_at?.split("T")[0]||""})),`student-leave-requests-${Date.now()}.csv`) },
  ];

  // ── 2. Fees ──────────────────────────────────────────────────────
  const feeExports = [
    { icon:"💰", label:"Fee History (All Time)", desc:"Every fee payment — receipt, student, month, amount, method",
      fn:()=>dlCSV(fees.map(f=>({Receipt:f.receipt_no||"",Student:f.student_name||"",Class:f.class||"",Month:f.month||"",Year:f.year||"","Amount (PKR)":f.amount||0,Method:f.method||"Cash",Date:f.paid_date||"",Notes:f.notes||""})),`fee-history-all-${Date.now()}.csv`) },
    { icon:"📋", label: expMonth===-1 ? `Yearly Collection — ${expYear} (All Months)` : `Monthly Collection — ${MONTHS_ORD[expMonth]} ${expYear}`,
      desc: expMonth===-1 ? "One row per student — total collected across all months for selected year" : "Net collected per student for selected month",
      fn:()=>{
        if(expMonth===-1){
          // All months: one row per student with a column per month + total
          const rows = students.map(s=>{
            const row={Name:s.name||s.full_name||"",Class:s.class||"",Year:expYear};
            let total=0;
            MONTHS_ORD.forEach(m=>{const amt=fees.filter(f=>f.student_id===s.id&&f.month===m&&String(f.year)===String(expYear)).reduce((a,f)=>a+Number(f.amount||0),0);row[m]=amt;total+=amt;});
            row["TOTAL (PKR)"]=total;
            return row;
          });
          dlCSV(rows, `fee-yearly-${expYear}.csv`);
        } else {
          dlCSV(students.map(s=>({Name:s.name||s.full_name||"",Class:s.class||"",Month:MONTHS_ORD[expMonth],Year:expYear,"Collected (PKR)":fees.filter(f=>f.student_id===s.id&&f.month===MONTHS_ORD[expMonth]&&String(f.year)===String(expYear)).reduce((a,f)=>a+Number(f.amount||0),0)})),`fee-${MONTHS_ORD[expMonth]}-${expYear}.csv`);
        }
      }},
    { icon:"🎓", label:"Scholarship Records", desc:"All scholarships — student, type, discount, status",
      fn:()=>dlCSV(scholarshipData.map(s=>({Student:s.student_name||"",Class:s.class||"",Type:s.type||"","Discount (PKR)":s.discount||0,Reason:s.reason||"",Status:s.status||"",From:s.from_date||"",To:s.to_date||"","Added On":s.created_at?.split("T")[0]||""})),`scholarships-${Date.now()}.csv`) },
  ];

  // ── 3. Academic Performance ──────────────────────────────────────
  const examResults = selExam==="All" ? results : results.filter(r=>r.exam_name===selExam);
  const perfExports = [
    { icon:"📝", label:"Exam Result Sheet", desc:`Marks per student per subject${selExam!=="All"?" — "+selExam:""}`,
      fn:()=>dlCSV(examResults.map(r=>{const s=students.find(x=>x.id===r.student_id)||{};const pct=r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0;return{Student:s.name||s.full_name||r.student_id||"",Class:r.class||s.class||"",Exam:r.exam_name||"",Subject:r.subject||"",Marks:r.marks_obtained||0,"Out Of":r.total_marks||0,"%":pct,Grade:r.grade||getGrade(r.marks_obtained,r.total_marks)};}),`exam-results-${selExam.replace(/\s/g,"-")}-${Date.now()}.csv`) },
    { icon:"🏆", label:"Class Ranking", desc:"Students ranked by overall % for selected exam",
      fn:()=>{const by={};examResults.forEach(r=>{if(!by[r.student_id])by[r.student_id]={id:r.student_id,o:0,t:0};by[r.student_id].o+=Number(r.marks_obtained||0);by[r.student_id].t+=Number(r.total_marks||0);});const ranked=Object.values(by).sort((a,b)=>(b.t>0?b.o/b.t:0)-(a.t>0?a.o/a.t:0));dlCSV(ranked.map((r,i)=>{const s=students.find(x=>x.id===r.id)||{};const pct=r.t>0?Math.round((r.o/r.t)*100):0;return{Rank:i+1,Student:s.name||s.full_name||"",Class:s.class||"","Total Marks":r.o,"Out Of":r.t,"%":pct,Grade:getGrade(r.o,r.t),"Pass/Fail":pct>=50?"Pass":"Fail"};}),`class-ranking-${Date.now()}.csv`);} },
    { icon:"📋", label:"Full Student Transcript", desc:"Complete academic history — every exam, every subject",
      fn:()=>dlCSV(results.map(r=>{const s=students.find(x=>x.id===r.student_id)||{};const pct=r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0;return{Student:s.name||s.full_name||r.student_id||"",Class:r.class||s.class||"",Exam:r.exam_name||"",Subject:r.subject||"",Marks:r.marks_obtained||0,"Out Of":r.total_marks||0,"%":pct,Grade:r.grade||getGrade(r.marks_obtained,r.total_marks)};}),`full-transcript-${Date.now()}.csv`) },
    { icon:"⚠️", label:"At-Risk Students", desc:"Students below 50% overall — for intervention planning",
      fn:()=>{const by={};results.forEach(r=>{if(!by[r.student_id])by[r.student_id]={id:r.student_id,o:0,t:0};by[r.student_id].o+=Number(r.marks_obtained||0);by[r.student_id].t+=Number(r.total_marks||0);});dlCSV(Object.values(by).filter(r=>r.t>0&&(r.o/r.t)<0.5).map(r=>{const s=students.find(x=>x.id===r.id)||{};const pct=r.t>0?Math.round((r.o/r.t)*100):0;return{Student:s.name||s.full_name||"",Class:s.class||"","Overall %":pct,"Marks":r.o,"Out Of":r.t,Grade:getGrade(r.o,r.t)};}),`at-risk-students-${Date.now()}.csv`);} },
  ];

  // ── 4. Student Conduct ───────────────────────────────────────────
  const conductExports = [
    { icon:"📌", label:"Student Conduct Log", desc:"All warnings, commendations and disciplinary entries",
      fn:()=>dlCSV(studentConduct.map(r=>{const s=students.find(x=>x.id===r.student_id)||{};return{Student:s.name||s.full_name||r.student_name||"",Class:s.class||r.class||"",Date:r.date||"",Type:r.type||"",Title:r.title||"",Details:r.notes||r.description||"","Added By":r.added_by||r.created_by||""}}),`student-conduct-${Date.now()}.csv`) },
  ];

  // ── 5. Admissions ────────────────────────────────────────────────
  const admExports = [
    { icon:"📩", label:"Admission Applications", desc:"Online applications — applicant, class, contact, status",
      fn:()=>dlCSV(admissions.map(a=>({Name:a.student_name||a.name||"","Father Name":a.father_name||"",Class:a.class_applying||a.class||"",Phone:a.phone||a.contact||"",Email:a.email||"",Status:a.status||"","Applied On":a.created_at?.split("T")[0]||"",Notes:a.notes||""})),`admissions-${Date.now()}.csv`) },
  ];

  // ── 6. Staff Directory & Payroll ─────────────────────────────────
  const staffExports = [
    { icon:"👨‍🏫", label:"Staff Directory", desc:"All staff — name, role, subject, phone, CNIC, salary, joining date",
      fn:()=>dlCSV(staffData.map(s=>({Name:s.name||"",Role:s.role||"",Subject:s.subject||"",Gender:s.gender||"",Phone:s.phone||"",CNIC:s.cnic||"",Email:s.email||"","Salary (PKR)":s.salary||0,"Joining Date":s.joining_date||"",Status:s.status||""})),`staff-directory-${Date.now()}.csv`) },
    { icon:"💸", label:"Payroll History (All Time)", desc:"Every salary payment — staff, month, base, bonus, deduction, net",
      fn:()=>dlCSV(salaryData.map(h=>({Staff:h.staff_name||"",Type:h.payment_type||"monthly",Month:h.month||"",Year:h.year||""," Base (PKR)":h.base_salary||h.amount||0,"Bonus (PKR)":h.bonus||0,"Deduction (PKR)":h.deduction||0,"Net (PKR)":h.amount||0,Method:h.method||"Cash","Payment Date":h.payment_date||"",Notes:h.notes||""})),`payroll-all-${Date.now()}.csv`) },
    { icon:"📊", label:"Payroll Summary (per staff)", desc:"Total paid to each staff member across all time",
      fn:()=>{const map={};salaryData.forEach(h=>{if(!map[h.staff_id])map[h.staff_id]={name:h.staff_name||"",total:0,count:0};map[h.staff_id].total+=Number(h.amount||0);map[h.staff_id].count++;});dlCSV(Object.values(map).sort((a,b)=>b.total-a.total).map(r=>({Staff:r.name,"Total Paid (PKR)":r.total,"Payments":r.count,"Avg Per Payment (PKR)":r.count>0?Math.round(r.total/r.count):0})),`payroll-summary-${Date.now()}.csv`);} },
  ];

  // ── 7. Staff Attendance ──────────────────────────────────────────
  const staffAttExports = [
    { icon:"📋", label:"Staff Attendance (All Time)", desc:"Every staff attendance record — date, status (P/A/L/H/LV)",
      fn:()=>dlCSV(staffAttData.map(r=>({Staff:r.staff_name||"",Date:r.date||"",Status:r.status||"","Marked By":r.marked_by||"","Check In":r.check_in||"","Check Out":r.check_out||"",Notes:r.notes||""})),`staff-attendance-all-${Date.now()}.csv`) },
    { icon:"📊", label:"Staff Attendance Summary", desc:"Total present/absent/late per staff member",
      fn:()=>{const map={};staffAttData.forEach(r=>{if(!map[r.staff_id])map[r.staff_id]={name:r.staff_name||"",P:0,A:0,L:0,H:0,LV:0};const k=r.status;if(map[r.staff_id][k]!==undefined)map[r.staff_id][k]++;});dlCSV(Object.values(map).map(s=>{const tot=s.P+s.A+s.L+s.H+s.LV;return{Staff:s.name,Present:s.P,Late:s.L,"Half Day":s.H,Absent:s.A,"On Leave":s.LV,Total:tot,"Att %":tot>0?Math.round(((s.P+s.L)/tot)*100)+"%":"—"};}),`staff-att-summary-${Date.now()}.csv`);} },
  ];

  // ── 8. Staff Leave, Conduct & Cover Duty ─────────────────────────
  const staffHRExports = [
    { icon:"📝", label:"Staff Leave Requests", desc:"All leave requests — type, dates, days, status, approval notes",
      fn:()=>dlCSV(staffLeaveData.map(r=>({Staff:r.staff_name||"","Leave Type":r.leave_type||"Casual","From":r.from_date,"To":r.to_date,Days:daysBetween(r.from_date,r.to_date),Reason:r.reason||"",Status:r.status,"Admin Note":r.admin_note||"","Decided By":r.decided_by||"","Applied On":r.created_at?.split("T")[0]||""})),`staff-leave-requests-${Date.now()}.csv`) },
    { icon:"📋", label:"Staff Conduct Log", desc:"Commendations, warnings, disciplinary notes for all staff",
      fn:()=>dlCSV(staffConduct.map(r=>({Staff:r.staff_name||"",Date:r.date||"",Type:r.type||"",Title:r.title||"",Details:r.body||"","Added By":r.added_by||""})),`staff-conduct-${Date.now()}.csv`) },
    { icon:"🔄", label:"Cover Duty Log", desc:"Who covered which class for whom — status, dates, all assignments",
      fn:()=>dlCSV(coverDutyData.map(r=>({Date:r.date||"","Absent Staff":r.absent_staff_name||"","Cover Staff":r.cover_staff_name||"Unassigned",Class:r.class_name||"",Subject:r.subject||"","Period No":r.period_no!=null&&r.period_no<200?r.period_no:"manual","Time":r.period_time||"",Status:r.status||"","Acknowledged":r.acknowledged_at?.split("T")[0]||"","Done":r.done_at?.split("T")[0]||"","Manual":r.is_manual?"Yes":"No"})),`cover-duties-${Date.now()}.csv`) },
  ];

  // ── 9. Calendar & Events ─────────────────────────────────────────
  const calExports = [
    { icon:"🗓️", label:"School Events", desc:"All calendar events — title, type, date, class, description",
      fn:()=>dlCSV(calEvents.map(e=>({Title:e.title||"",Type:e.event_type||"",Date:e.date||"",Class:e.class||"All",Description:e.description||"","Visible To":e.visible_to||"all","Created By":e.created_by||"","Created On":e.created_at?.split("T")[0]||""})),`school-events-${Date.now()}.csv`) },
  ];

  const loadingNote = extraLoading ? (
    <div style={{background:"#f0f9ff",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#0369a1",border:"1px solid #bae6fd",display:"flex",alignItems:"center",gap:8}}>
      <span style={{display:"inline-block",width:14,height:14,border:"2px solid #bae6fd",borderTopColor:"#0369a1",borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
      Loading additional records from database…
    </div>
  ) : null;

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>
      <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontWeight:900,fontSize:20,color:C.primary,marginBottom:4}}>📥 Export Data</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:10}}>Every table in one place — download individually as CSV or grab everything at once</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[["👥 Students",students.length],["💰 Fees",fees.length],["📊 Results",results.length],["👨‍🏫 Staff",staffData.length],["📋 Staff Att.",staffAttData.length],["📝 Staff Leave",staffLeaveData.length],["📩 Admissions",admissions.length]].map(([lbl,cnt])=>(
                <span key={lbl} style={{background:C.primaryPale,color:C.primary,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700}}>{lbl}: {cnt}</span>
              ))}
            </div>
          </div>
          <button
            onClick={downloadAllAsExcel}
            disabled={bulkDownloading||extraLoading}
            style={{background:bulkDownloading||extraLoading?"#9ca3af":C.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px 24px",fontWeight:800,fontSize:14,cursor:bulkDownloading||extraLoading?"not-allowed":"pointer",boxShadow:`0 4px 18px ${C.primary}50`,display:"flex",alignItems:"center",gap:10,flexShrink:0,whiteSpace:"nowrap"}}>
            {bulkDownloading
              ? <><span style={{display:"inline-block",width:16,height:16,border:"2px solid #ffffff60",borderTopColor:"#fff",borderRadius:"50%",animation:"spin .8s linear infinite"}}/>  Building…</>
              : <>📊 Download Everything<br/><span style={{fontSize:11,fontWeight:600,opacity:.85}}>One Excel file · {15} sheets</span></>
            }
          </button>
        </div>
      </div>

      {loadingNote}

      {/* ── 1. Students & Attendance ── */}
      <SectionHead title="👥 Students & Attendance" count={students.length}/>
      <Grid items={studentExports}/>

      {/* ── 2. Fees & Scholarships ── */}
      <SectionHead title="💰 Fees & Scholarships"/>
      <div style={{background:C.white,borderRadius:12,padding:"12px 18px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Filter:</span>
        <select value={expMonth} onChange={e=>setExpMonth(+e.target.value)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
          <option value={-1}>All Months</option>
          {MONTHS_ORD.map((m,i)=><option key={i} value={i}>{m}</option>)}
        </select>
        <select value={expYear} onChange={e=>setExpYear(+e.target.value)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
          {[new Date().getFullYear()-1, new Date().getFullYear(), new Date().getFullYear()+1].map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        {expMonth===-1&&<span style={{fontSize:11,color:C.muted,background:C.primaryPale,borderRadius:6,padding:"3px 8px"}}>Exports a yearly breakdown with one column per month</span>}
      </div>
      <Grid items={feeExports}/>

      {/* ── 3. Academic Performance ── */}
      <SectionHead title="📊 Academic Performance" count={results.length}/>
      {results.length===0
        ? <div style={{background:"#fffbeb",borderRadius:12,padding:"14px 18px",fontSize:13,color:"#92400e",border:"1px solid #fde68a"}}>⚠️ No exam results yet. Add results in the Results & Reports tab first.</div>
        : <>
          <div style={{background:C.white,borderRadius:12,padding:"12px 18px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Filter by exam:</span>
            <select value={selExam} onChange={e=>setSelExam(e.target.value)} style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:12,background:C.white}}>
              {allExams.map(ex=><option key={ex}>{ex}</option>)}
            </select>
            <span style={{fontSize:12,color:C.muted}}>{examResults.length} records match</span>
          </div>
          <Grid items={perfExports}/>
        </>
      }

      {/* ── 4. Student Conduct ── */}
      <SectionHead title="📌 Student Conduct" count={studentConduct.length}/>
      <Grid items={conductExports}/>

      {/* ── 5. Admissions ── */}
      <SectionHead title="📩 Admissions Pipeline" count={admissions.length}/>
      <Grid items={admExports}/>

      {/* ── 6. Staff Directory & Payroll ── */}
      <SectionHead title="👨‍🏫 Staff Directory & Payroll" count={staffData.length}/>
      <Grid items={staffExports}/>

      {/* ── 7. Staff Attendance ── */}
      <SectionHead title="📋 Staff Attendance" count={staffAttData.length}/>
      {staffAttData.length===0&&!extraLoading
        ? <div style={{background:"#f0f9ff",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#0369a1",border:"1px solid #bae6fd"}}>No staff attendance records yet — mark attendance in Staff Management → Attendance first.</div>
        : <Grid items={staffAttExports}/>}

      {/* ── 8. Staff Leave & Conduct ── */}
      <SectionHead title="📝 Staff Leave & Conduct" count={staffLeaveData.length+staffConduct.length}/>
      <Grid items={staffHRExports}/>

      {/* ── 9. Calendar Events ── */}
      <SectionHead title="🗓️ School Calendar" count={calEvents.length}/>
      <Grid items={calExports}/>
    </div>
  );
}


/* =========================
   ACADEMIC YEAR HELPERS
========================= */
function normalizeAcademicYearLabel(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  const m = raw.match(/^(\d{4})\D*(\d{4})$/);
  if (!m) return raw;
  return `${m[1]}-${m[2]}`;
}

function academicYearValueFromDates(startDate, endDate) {
  const sy = new Date(startDate).getFullYear();
  const ey = new Date(endDate).getFullYear();
  return `${sy}${ey}`;
}

function academicYearLabelFromDates(startDate, endDate) {
  const sy = new Date(startDate).getFullYear();
  const ey = new Date(endDate).getFullYear();
  return `${sy}-${ey}`;
}

function parseAcademicYearValue(value) {
  const raw = String(value || "").trim();
  const m = raw.match(/^(\d{4})\D*(\d{4})$/);
  if (!m) return null;
  const startYear = Number(m[1]);
  const endYear = Number(m[2]);
  if (!startYear || !endYear || endYear < startYear) return null;
  return { startYear, endYear };
}

function getAcademicYearForDate(dateLike, academicYears = [], fallbackValue = "") {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return fallbackValue || "";

  const hit = academicYears.find((y) => {
    const s = new Date(y.start_date);
    const e = new Date(y.end_date);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
    return d >= s && d <= e;
  });

  if (hit) return hit.year_label || normalizeAcademicYearLabel(hit.year_name || hit.year || "");

  return normalizeAcademicYearLabel(fallbackValue);
}

function getCurrentAcademicYear(academicYears = [], fallbackValue = "") {
  const today = new Date();

  const active = academicYears.find((y) => {
    if (!y.is_current) return false;
    const s = new Date(y.start_date);
    const e = new Date(y.end_date);
    return !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime());
  });

  if (active) return active;

  const byDate = academicYears.find((y) => {
    const s = new Date(y.start_date);
    const e = new Date(y.end_date);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return false;
    return today >= s && today <= e;
  });

  if (byDate) return byDate;

  const parsed = parseAcademicYearValue(fallbackValue);
  if (parsed) {
    return {
      id: `fallback-${fallbackValue}`,
      year_name: `${parsed.startYear}${parsed.endYear}`,
      year_label: `${parsed.startYear}-${parsed.endYear}`,
      start_date: `${parsed.startYear}-04-01`,
      end_date: `${parsed.endYear}-03-31`,
      is_current: true,
      is_closed: false,
      fallback: true,
    };
  }

  return null;
}

function SettingsManagement({
  settings,
  setSettings,
  academicYears = [],
  setAcademicYears,
  activeAcademicYear,
  reloadAcademicYears,
  students = [],
  fees = [],
  attendance = [],
  results = [],
}) {
  const [form,    setForm]    = React.useState({...DEFAULT_SETTINGS,...settings});
  const [saving,  setSaving]  = React.useState(false);
  const [toast,   setToast]   = React.useState(null);
  const [section, setSection] = React.useState("identity");

  const emptyAcademicYearForm = {
    id: null,
    year_name: "",
    year_label: "",
    start_date: "",
    end_date: "",
    is_current: false,
    is_closed: false,
  };

  const [ayForm, setAyForm] = React.useState(emptyAcademicYearForm);
  const [aySaving, setAySaving] = React.useState(false);
  const [ayDeleting, setAyDeleting] = React.useState(false);
  const [editingAyId, setEditingAyId] = React.useState(null);

  // Classes tab state (hoisted from conditional IIFE to keep hook count stable)
  const [classStudents, setClassStudents] = React.useState([]);
  const [teacherList, setTeacherList] = React.useState([]);
  const [classTeachers, setClassTeachers] = React.useState({});
  const [addingClass, setAddingClass] = React.useState("");

  // Session tab state (hoisted from conditional IIFE to keep hook count stable)
  const [terms, setTerms] = React.useState([]);
  const [archiving, setArchiving] = React.useState(false);
  const [termMsg, setTermMsg] = React.useState(null);
  const [scheduleSubTab, setScheduleSubTab] = React.useState("hifz_winter");

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3500); };
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));
  const setAy = (k,v) => setAyForm(p=>({...p,[k]:v}));
  React.useEffect(()=>{ setForm({...DEFAULT_SETTINGS,...settings}); },[settings]);
  React.useEffect(()=>{
    supabase.from("staff").select("id,name,role").eq("status","Active").order("name")
      .then(({data})=>setTeacherList(data||[]));
    supabase.from("students").select("id,class").then(({data})=>setClassStudents(data||[]));
    supabase.from("class_teachers").select("*").then(({data})=>{
      if(data){ const m={}; data.forEach(r=>{m[r.class_name]=r.staff_id;}); setClassTeachers(m); }
    });
  },[]);
  React.useEffect(()=>{
    supabase.from("terms").select("*").order("created_at",{ascending:false})
      .then(({data})=>setTerms(data||[]));
  },[]);

  const resetAyForm = () => {
    setEditingAyId(null);
    setAyForm(emptyAcademicYearForm);
  };

  const editAcademicYear = (row) => {
    setEditingAyId(row.id);
    setAyForm({
      id: row.id,
      year_name: row.year_name || "",
      year_label: row.year_label || normalizeAcademicYearLabel(row.year_name || row.year || ""),
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      is_current: !!row.is_current,
      is_closed: !!row.is_closed,
    });
  };

  const saveAcademicYear = async () => {
    if (!ayForm.start_date || !ayForm.end_date) return showToast("Start date and end date are required.", false);
    const s = new Date(ayForm.start_date);
    const e = new Date(ayForm.end_date);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return showToast("Academic year dates are invalid.", false);

    const value = ayForm.year_name || academicYearValueFromDates(ayForm.start_date, ayForm.end_date);
    const label = ayForm.year_label || academicYearLabelFromDates(ayForm.start_date, ayForm.end_date);

    setAySaving(true);
    try {
      if (ayForm.is_current) {
        await supabase.from("academic_years").update({ is_current: false }).neq("id", ayForm.id || "");
      }

      const payload = {
        year_name: value,
        year_label: normalizeAcademicYearLabel(label),
        start_date: ayForm.start_date,
        end_date: ayForm.end_date,
        is_current: !!ayForm.is_current,
        is_closed: !!ayForm.is_closed,
        updated_at: new Date().toISOString(),
      };

      if (ayForm.id) payload.id = ayForm.id;

      const { error } = await supabase.from("academic_years").upsert(payload);
      if (error) throw error;

      if (ayForm.is_current) {
        const yearString = String(value).replace(/\D/g, "");
        const { error: settingsErr } = await supabase.from("school_settings").upsert([{ key: "academic_year", value: yearString }], { onConflict: "key" });
        if (settingsErr) throw settingsErr;
        setSettings((p)=>({ ...p, academic_year: normalizeAcademicYearLabel(label) }));
        setForm((p)=>({ ...p, academic_year: normalizeAcademicYearLabel(label) }));
      }

      await reloadAcademicYears?.();
      resetAyForm();
      showToast("Academic year saved successfully.");
    } catch (e) {
      showToast(`Academic year save failed: ${e?.message || e}`, false);
    } finally {
      setAySaving(false);
    }
  };

  const makeAcademicYearCurrent = async (row) => {
    setAySaving(true);
    try {
      await supabase.from("academic_years").update({ is_current: false }).neq("id", row.id);
      const { error } = await supabase.from("academic_years").update({ is_current: true, updated_at: new Date().toISOString() }).eq("id", row.id);
      if (error) throw error;

      const label = row.year_label || normalizeAcademicYearLabel(row.year_name || row.year || "");
      const yearString = String(row.year_name || label).replace(/\D/g, "");
      const { error: settingsErr } = await supabase.from("school_settings").upsert([{ key: "academic_year", value: yearString }], { onConflict: "key" });
      if (settingsErr) throw settingsErr;

      setSettings((p)=>({ ...p, academic_year: label }));
      setForm((p)=>({ ...p, academic_year: label }));

      await reloadAcademicYears?.();
      showToast("Current academic year updated.");
    } catch (e) {
      showToast(`Failed to update current year: ${e?.message || e}`, false);
    } finally {
      setAySaving(false);
    }
  };

  const deleteAcademicYear = async (row) => {
    if (!row?.id) return;
    if (row.is_current) return showToast("You cannot delete the current academic year.", false);

    setAyDeleting(true);
    try {
      const { error } = await supabase.from("academic_years").delete().eq("id", row.id);
      if (error) throw error;
      await reloadAcademicYears?.();
      if (editingAyId === row.id) resetAyForm();
      showToast("Academic year deleted.");
    } catch (e) {
      showToast(`Delete failed: ${e?.message || e}`, false);
    } finally {
      setAyDeleting(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    const rows = Object.entries(form).map(([key,value])=>({key,value:String(value)}));
    const {error} = await supabase.from("school_settings").upsert(rows,{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings({...DEFAULT_SETTINGS,...form}); showToast("Settings saved! All changes applied immediately."); }
    setSaving(false);
  };

  const sBtn = (id,label,icon) => (
    <button onClick={()=>setSection(id)} style={{
      background:section===id?C.primary:"white",color:section===id?"white":C.text,
      border:`1px solid ${section===id?C.primary:C.border}`,
      borderRadius:10,padding:"8px 16px",fontWeight:700,fontSize:12,
      cursor:"pointer",display:"flex",alignItems:"center",gap:6
    }}>{icon} {label}</button>
  );

  const Fld = ({label,k,type="text",ph,help,rows,opts}) => (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{label}</label>
      {opts ? (
        <select value={form[k]||""} onChange={e=>upd(k,e.target.value)}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}>
          {opts.map(o=><option key={o.v||o} value={o.v||o}>{o.l||o}</option>)}
        </select>
      ) : rows ? (
        <textarea value={form[k]||""} onChange={e=>upd(k,e.target.value)} rows={rows}
          placeholder={ph} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,resize:"vertical",fontFamily:"inherit"}}/>
      ) : (
        <input type={type} value={form[k]||""} onChange={e=>upd(k,e.target.value)} placeholder={ph||""}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}/>
      )}
      {help && <div style={{fontSize:10,color:C.muted}}>{help}</div>}
    </div>
  );

  const UrdFld = ({label,k,ph}) => (
    <div style={{display:"flex",flexDirection:"column",gap:5}}>
      <label style={{fontSize:11,fontWeight:700,color:"#7c3aed"}}>{label}</label>
      <input value={form[k]||""} onChange={e=>upd(k,e.target.value)} placeholder={ph}
        style={{border:"1.5px solid #c4b5fd",borderRadius:8,padding:"9px 12px",fontSize:15,direction:"rtl",fontFamily:"'Noto Nastaliq Urdu',serif"}}/>
    </div>
  );

  const SliderFld = ({label,k,min,max,unit}) => {
    const val = Number(form[k])||min;
    return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{label}</label>
        <span style={{fontSize:13,fontWeight:900,color:C.teal,background:C.primaryPale,borderRadius:6,padding:"2px 10px"}}>{val}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={1} value={val}
        onChange={e=>upd(k, String(e.target.value))}
        style={{width:"100%",accentColor:C.primary,height:4,cursor:"pointer"}}/>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.muted}}>
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </div>
  );};

  const ALIGN_OPTS = [{v:"flex-start",l:"Top"},{v:"center",l:"Middle"},{v:"flex-end",l:"Bottom"}];
  const TOGGLE_OPTS = [{v:"true",l:"Visible"},{v:"false",l:"Hidden"}];

  const PreviewHeader = () => {
    const logoSize = Number(form.header_logo_size)||92;
    const enFont   = Number(form.header_en_font)||16;
    const urFont   = Number(form.header_ur_font)||24;
    const enVAlign = form.header_en_valign||"center";
    const urVAlign = form.header_ur_valign||"center";
    const parts    = (form.header_gradient||"#1a6b3c,#0a4a24").split(",");
    const g1       = parts[0]||"#1a6b3c";
    const g2       = parts[1]||"#0a4a24";
    const grad     = `linear-gradient(135deg,${g1} 0%,${g2} 100%)`;
    const accent   = form.header_accent||"#f0a500";
    const enName   = form.school_name_en||DEFAULT_SETTINGS.school_name_en;
    const enSub    = form.school_name_en_sub||DEFAULT_SETTINGS.school_name_en_sub;
    const addr     = form.address||DEFAULT_SETTINGS.address;
    const regNo    = form.reg_no||DEFAULT_SETTINGS.reg_no;
    const urName   = form.school_name_ur||DEFAULT_SETTINGS.school_name_ur;
    const urSub    = form.school_name_ur_sub||DEFAULT_SETTINGS.school_name_ur_sub;
    const urReg    = form.school_name_ur_reg||DEFAULT_SETTINGS.school_name_ur_reg;
    const urAddr   = form.school_name_ur_addr||DEFAULT_SETTINGS.school_name_ur_addr;
    return (
      <div style={{borderRadius:12,overflow:"hidden",boxShadow:`0 2px 16px ${C.shadow}`,marginBottom:20}}>
        <div style={{background:"#eff6ff",padding:"7px 16px",fontSize:11,color:"#1d4ed8",fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
          👁 Live preview — changes apply instantly as you adjust
        </div>
        <div style={{background:grad,borderBottom:`4px solid ${accent}`}}>
          <div style={{display:"flex",alignItems:"center",padding:"14px 20px",gap:14}}>
            <img src={SCHOOL_LOGO} style={{width:logoSize,height:logoSize,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 2px 6px rgba(0,0,0,0.3))"}}/>
            <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:enVAlign,gap:2}}>
              <div style={{fontSize:enFont,fontWeight:900,color:"white",letterSpacing:.5}}>{enName}</div>
              <div style={{fontSize:Math.max(enFont-4,9),fontWeight:600,opacity:.9,color:"white"}}>{enSub}</div>
              <div style={{fontSize:Math.max(enFont-5,8),opacity:.75,color:"white"}}>{(form.affiliation||DEFAULT_SETTINGS.affiliation)}</div>
              <div style={{fontSize:Math.max(enFont-5,8),opacity:.75,color:"white"}}>{addr} · Reg. No: {regNo}</div>
            </div>
            <div style={{textAlign:"right",fontFamily:"'Noto Nastaliq Urdu',serif",display:"flex",flexDirection:"column",justifyContent:urVAlign,gap:2}}>
              <div style={{fontSize:urFont,fontWeight:900,color:"white",lineHeight:1.3}}>{urName}</div>
              <div style={{fontSize:Math.max(urFont-9,10),fontWeight:700,color:"#ffd166"}}>{urSub}</div>
              <div style={{fontSize:Math.max(urFont-11,9),opacity:.8,color:"white"}}>{urReg}</div>
              <div style={{fontSize:Math.max(urFont-11,9),opacity:.8,color:"white"}}>{urAddr}</div>
            </div>
          </div>
          <div style={{height:3,background:"linear-gradient(90deg,#2563eb,#0d9488,#2563eb)",opacity:.8}}/>
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"18px 24px",color:"white",boxShadow:`0 4px 20px ${C.primary}40`}}>
        <div style={{fontWeight:900,fontSize:18}}>⚙️ School Settings</div>
        <div style={{fontSize:13,opacity:.85,marginTop:3}}>Manage every aspect of your school portal. Settings tab is always at the end of navigation.</div>
      </div>

      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {sBtn("identity","Identity","🏫")}
        {sBtn("header","Letterhead","🎨")}
        {sBtn("fees","Fees","💰")}
        
        {sBtn("portal","Portal","🖥️")}
        {sBtn("session","Session","📅")}
      </div>

      <div style={{background:"white",borderRadius:16,padding:"24px",boxShadow:`0 2px 12px ${C.shadow}`}}>

        {section==="identity" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15}}>🏫 School Identity</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Fld label="School Name (English) *" k="school_name_en" ph="QUR'AN-E-KARIM ACADEMY"/>
              <Fld label="Tagline (English)" k="school_name_en_sub" ph="Modern Islamic School System"/>
              <Fld label="Registration No." k="reg_no" ph="CMR-199/2015"/>
              <Fld label="Affiliation" k="affiliation" ph="Registered with Wifaq-ul Madaris..."/>
            </div>
            <Fld label="Full Address" k="address" ph="Gorsian, Jatli, Dist. Rawalpindi"/>
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16}}>
              <div style={{fontWeight:700,color:"#7c3aed",fontSize:13,marginBottom:12}}>📖 Urdu Text (right side of letterhead)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <UrdFld label="School Name (Urdu) *" k="school_name_ur" ph="قرآن کریم اکیڈمی"/>
                <UrdFld label="Tagline (Urdu)" k="school_name_ur_sub" ph="ماڈرن اسلامک سکول سسٹم"/>
                <UrdFld label="Affiliation (Urdu)" k="school_name_ur_reg" ph="رجسٹرڈ وفاق المدارس"/>
                <UrdFld label="Address (Urdu)" k="school_name_ur_addr" ph="گورسیاں - جاتلی"/>
              </div>
            </div>
          </div>
        )}

        {section==="header" && (()=>{
          const g1 = (form.header_gradient||"#1a6b3c,#0a4a24").split(",")[0]||"#1a6b3c";
          const g2 = (form.header_gradient||"#1a6b3c,#0a4a24").split(",")[1]||"#0a4a24";
          const setG1 = v => upd("header_gradient", v+","+g2);
          const setG2 = v => upd("header_gradient", g1+","+v);
          const ColorRow = ({label,val,onChange}) => (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{label}</label>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="color" value={val} onChange={e=>onChange(e.target.value)}
                  style={{width:44,height:44,border:`1.5px solid ${C.border}`,borderRadius:8,cursor:"pointer",padding:3,flexShrink:0}}/>
                <input value={val} onChange={e=>onChange(e.target.value)}
                  placeholder="#1a6b3c"
                  style={{flex:1,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 10px",fontSize:13,fontFamily:"monospace"}}/>
                <div style={{width:32,height:32,borderRadius:6,background:val,border:`1px solid ${C.border}`,flexShrink:0}}/>
              </div>
            </div>
          );
          return (
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15}}>🎨 Letterhead Appearance</div>
            <PreviewHeader/>
            <div style={{background:C.bg,borderRadius:12,padding:"16px 18px",display:"flex",flexDirection:"column",gap:16}}>
              <div style={{fontWeight:700,color:C.primary,fontSize:12}}>📐 Logo & Text Sizes</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:20}}>
                <SliderFld label="Logo Size" k="header_logo_size" min={50} max={140} unit="px"/>
                <SliderFld label="English Text" k="header_en_font" min={10} max={26} unit="px"/>
                <SliderFld label="Urdu Text" k="header_ur_font" min={14} max={38} unit="px"/>
              </div>
            </div>
            <div style={{background:C.bg,borderRadius:12,padding:"16px 18px",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{fontWeight:700,color:C.primary,fontSize:12}}>↕️ Vertical Alignment</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <Fld label="English side" k="header_en_valign" opts={ALIGN_OPTS} help="Top / Middle / Bottom"/>
                <Fld label="Urdu side" k="header_ur_valign" opts={ALIGN_OPTS} help="Top / Middle / Bottom"/>
              </div>
            </div>
            <div style={{background:C.bg,borderRadius:12,padding:"16px 18px",display:"flex",flexDirection:"column",gap:14}}>
              <div style={{fontWeight:700,color:C.primary,fontSize:12}}>🎨 Colors</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                <ColorRow label="Background start" val={g1} onChange={setG1}/>
                <ColorRow label="Background end" val={g2} onChange={setG2}/>
                <ColorRow label="Accent line" val={form.header_accent||"#f0a500"} onChange={v=>upd("header_accent",v)}/>
              </div>
              <div style={{height:8,borderRadius:4,background:`linear-gradient(90deg,${g1},${g2})`,marginTop:4}}/>
            </div>
            <div style={{background:"#eff6ff",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#1d4ed8"}}>
              💡 The logo image is embedded in code and cannot be changed here. All sizes, alignment and colours update live in the preview above.
            </div>
          </div>
          );
        })()}

        {section==="fees" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15}}>💰 Fees & Thresholds</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Fld label="Default Monthly Fee (PKR)" k="default_fee" type="number" ph="1500" help="Default when adding new student"/>
              <Fld label="School Only Fee (PKR)" k="fee_school_only" type="number" ph="1500"/>
              <Fld label="Hifz + School Fee (PKR)" k="fee_hifz_school" type="number" ph="1800"/>
              <Fld label="Hifz Only Fee (PKR)" k="fee_hifz_only" type="number" ph="1200"/>
              <Fld label="Attendance At-Risk Threshold (%)" k="att_threshold" type="number" ph="85" help="Students below this % flagged in dashboard"/>
            </div>
          </div>
        )}

        {section==="classes" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,color:"#1a6b3c",fontSize:15}}>📚 Classes & Class Teachers</div>
            <div style={{background:"#f0fdf4",borderRadius:14,padding:"24px",border:"2px solid #bbf7d0",textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:12}}>🗓️</div>
              <div style={{fontWeight:800,fontSize:16,color:"#1a6b3c",marginBottom:8}}>Classes are now in Timetable & Schedule</div>
              <div style={{fontSize:13,color:"#374151",marginBottom:16,lineHeight:1.7}}>
                Class management — including adding/removing classes and assigning class teachers — has been moved to the <strong>Timetable & Schedule</strong> tab for better organisation.
              </div>
              <div style={{background:"white",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#6b7280",border:"1px solid #d1fae5"}}>
                📍 Go to <strong>Timetable & Schedule</strong> (sidebar) → <strong>Classes</strong> tab
              </div>
            </div>
          </div>
        )}
        {section==="portal" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15}}>🖥️ Portal Options</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <Fld label="Dashboard subtitle text" k="dashboard_title" ph="School Management Portal" help="Shown below the letterhead on dashboard"/>
              <div/>
              <Fld label="Show AI Reports tab" k="show_ai_tab" opts={TOGGLE_OPTS}/>
              <Fld label="Show Timetable tab" k="show_timetable" opts={TOGGLE_OPTS}/>
              <Fld label="Show Scholarship tab" k="show_scholarship" opts={TOGGLE_OPTS}/>
            </div>
            <div style={{background:"#eff6ff",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#1d4ed8"}}>
              💡 Hiding a tab only removes it from the navigation bar. All data is preserved in Supabase and can be restored any time.
            </div>
          </div>
        )}

        {section==="session" && (()=>{

          const startNewTerm = async () => {
            const termName = form.term_name||"Term 1";
            if(!window.confirm(`Archive current data as "${termName}" and start fresh? All data is preserved.`)) return;
            setArchiving(true);
            const {error} = await supabase.from("terms").insert({
              term_name:termName, academic_year:form.academic_year||"2025\u20132026",
              start_date:form.term_start||null, end_date:form.term_end||null,
              archived_at:new Date().toISOString()
            });
            if(!error){
              await supabase.from("attendance").update({term_label:termName}).is("term_label",null);
              await supabase.from("results").update({term_label:termName}).is("term_label",null);
              setTerms(p=>[{term_name:termName,archived_at:new Date().toISOString()},...p]);
              setTermMsg({ok:true,t:`\u2705 "${termName}" archived. Dashboards reset to new term.`});
            } else setTermMsg({ok:false,t:"Error: "+error.message});
            setArchiving(false);
          };

          const hifzW = (()=>{ try{return JSON.parse(form.hifz_schedule_winter||"[]");}catch(e){return [];} })();
          const hifzS = (()=>{ try{return JSON.parse(form.hifz_schedule_summer||"[]");}catch(e){return [];} })();
          const saveW = s=>upd("hifz_schedule_winter",JSON.stringify(s));
          const saveS = s=>upd("hifz_schedule_summer",JSON.stringify(s));
          const subBtn = (id,lbl) => (
            <button key={id} onClick={()=>setScheduleSubTab(id)} style={{padding:"7px 14px",borderRadius:8,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,background:scheduleSubTab===id?C.primary:C.bg,color:scheduleSubTab===id?"white":C.muted,transition:"all .15s"}}>{lbl}</button>
          );
          const inp = {border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12};

          return (
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              {termMsg&&<div style={{background:termMsg.ok?"#f0fdf4":"#fef2f2",borderRadius:10,padding:"10px 14px",fontSize:12,color:termMsg.ok?"#16a34a":"#dc2626",fontWeight:600,border:`1px solid ${termMsg.ok?"#bbf7d0":"#fecaca"}`}}>{termMsg.t}</div>}

              {/* Academic Year & Term */}
              <div>
                <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:12}}>📅 Academic Session</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <Fld label="Academic Year" k="academic_year" ph="2025\u20132026" help="Appears on report cards"/>
                  <Fld label="Current Term Name" k="term_name" ph="Term 1"/>
                  <Fld label="Term Start Date" k="term_start" type="date"/>
                  <Fld label="Term End Date" k="term_end" type="date"/>
                </div>
                <div style={{background:C.bg,borderRadius:10,padding:"10px 14px",fontSize:12,color:C.muted,marginTop:10}}>
                  Report card footer: <strong style={{color:C.text}}>SESSION {form.academic_year||"2025\u20132026"}</strong>
                </div>
              </div>

              {/* Term Management */}
              <div style={{background:"white",borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                <div style={{background:`linear-gradient(135deg,${C.primary},#0a4a24)`,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{color:"white",fontWeight:800,fontSize:14}}>📦 Term Management</div>
                    <div style={{color:"rgba(255,255,255,0.7)",fontSize:11,marginTop:2}}>Archive the current term — all data is preserved and filterable by term</div>
                  </div>
                  <button onClick={startNewTerm} disabled={archiving} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.35)",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:archiving?"not-allowed":"pointer"}}>
                    {archiving?"Archiving\u2026":"\uD83D\uDDC4\uFE0F Start New Term"}
                  </button>
                </div>
                <div style={{padding:"12px 16px"}}>
                  {terms.length===0
                    ? <div style={{color:C.muted,fontSize:12,textAlign:"center",padding:12}}>No archived terms yet.</div>
                    : terms.map((t,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<terms.length-1?`1px solid ${C.border}`:"none"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:C.text}}>{t.term_name}</div>
                          <div style={{fontSize:11,color:C.muted}}>{t.academic_year}{t.start_date?` \u00b7 ${t.start_date} \u2192 ${t.end_date||"ongoing"}`:""}</div>
                        </div>
                        <span style={{background:C.primaryPale,color:C.primary,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>
                          {t.archived_at?new Date(t.archived_at).toLocaleDateString("en-PK",{day:"numeric",month:"short",year:"numeric"}):"Active"}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Moved notices */}
              <div style={{background:"#f0fdf4",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#15803d",fontWeight:600,border:"1px solid #bbf7d0"}}>
                Hifz Schedule and School Days have moved to <strong>Timetable</strong> tab for better integration with the timetable and auto-scheduler.
              </div>
            </div>
          );
        })()}
      </div>


      {/* Save — hidden on export section since nothing to save */}
      {section!=="export" && <div style={{display:"flex",gap:12,alignItems:"center"}}>
        <button onClick={saveAll} disabled={saving}
          style={{background:saving?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:12,padding:"13px 32px",fontWeight:800,fontSize:15,cursor:saving?"not-allowed":"pointer",boxShadow:`0 4px 16px ${C.primary}40`}}>
          {saving?"💾 Saving…":"💾 Save All Settings"}
        </button>
        <button onClick={()=>setForm({...DEFAULT_SETTINGS,...settings})}
          style={{background:"white",color:C.muted,border:`1px solid ${C.border}`,borderRadius:12,padding:"13px 24px",fontWeight:700,fontSize:14,cursor:"pointer"}}>
          ↩ Reset
        </button>
        <span style={{fontSize:12,color:C.muted}}>Changes take effect immediately after saving.</span>
      </div>}
    </div>
  );
}



// ── PROMOTION WIZARD ────────────────────────────────────────────
function PromotionWizard({ students=[], results=[], onStudentsUpdated, showToast }) {
  const CLASS_ORDER = ["Nursery","KG","Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8","Class 9","Hifz Only","Graduated"];
  const PASS_THRESHOLD = 50; // % to auto-suggest pass

  const [step,       setStep]       = React.useState(1); // 1=select class, 2=review, 3=confirm, 4=done
  const [selClass,   setSelClass]   = React.useState("");
  const [decisions,  setDecisions]  = React.useState({}); // {studentId: {status, newClass}}
  const [saving,     setSaving]     = React.useState(false);
  const [log,        setLog]        = React.useState([]); // completed promotions history

  // Load promotion log from supabase on mount
  React.useEffect(()=>{
    supabase.from("promotion_log").select("*").order("promoted_at",{ascending:false}).limit(50)
      .then(({data})=>{ if(data) setLog(data); });
  },[]);

  const srcStudents = students.filter(s=>(s.class||s["class"])===selClass);

  const getAvgPct = (studentId) => {
    const r = results.filter(x=>String(x.student_id)===String(studentId));
    if(!r.length) return null;
    const total = r.reduce((a,x)=>a+(Number(x.marks_obtained)||0),0);
    const max   = r.reduce((a,x)=>a+(Number(x.total_marks)||0),0);
    return max>0 ? Math.round(total/max*100) : null;
  };

  const nextClass = (cls) => {
    const idx = CLASS_ORDER.indexOf(cls);
    if(idx===-1||idx>=CLASS_ORDER.length-2) return "Graduated";
    return CLASS_ORDER[idx+1];
  };

  // Auto-populate decisions when class is selected
  const initDecisions = (cls) => {
    const studs = students.filter(s=>(s.class||s["class"])===cls);
    const d = {};
    studs.forEach(s=>{
      const pct = getAvgPct(s.id);
      const suggested = pct===null ? "manual" : pct>=PASS_THRESHOLD ? "promote" : "holdback";
      d[s.id] = { status: suggested, newClass: suggested==="promote" ? nextClass(cls) : cls };
    });
    setDecisions(d);
  };

  const setDecision = (id, status, cls) => {
    setDecisions(p=>({...p,[id]:{status,newClass:cls}}));
  };

  const confirmPromotions = async () => {
    setSaving(true);
    const toUpdate = Object.entries(decisions).filter(([,d])=>d.status!=="holdback");
    const errors = [];
    const updated = [];
    const logRows = [];
    const now = new Date().toISOString();

    for(const [id, d] of toUpdate) {
      const s = students.find(x=>String(x.id)===String(id));
      if(!s) continue;
      const oldClass = s.class||s["class"];
      const {error} = await supabase.from("students").update({"class": d.newClass}).eq("id", id);
      if(error) { errors.push(s.full_name||s.name); continue; }
      updated.push({id, newClass: d.newClass});
      logRows.push({
        student_id: String(id),
        student_name: s.full_name||s.name,
        from_class: oldClass,
        to_class: d.newClass,
        status: d.status,
        promoted_at: now,
        academic_year: new Date().getFullYear()+"-"+(new Date().getFullYear()+1),
      });
    }

    // Save promotion log
    if(logRows.length) {
      await supabase.from("promotion_log").insert(logRows);
      setLog(p=>[...logRows,...p]);
    }

    setSaving(false);
    if(errors.length) showToast("⚠️ Some updates failed: "+errors.join(", "), false);
    else {
      onStudentsUpdated(updated);
      showToast(`✅ ${updated.length} student${updated.length>1?"s":""} updated successfully!`);
      setStep(4);
    }
  };

  const C = {primary:"#1a6b3c",bg:"#f8fafc",border:"#e5e7eb",muted:"#6b7280",text:"#111827",
    shadow:"rgba(0,0,0,0.06)",green:"#16a34a",red:"#dc2626",yellow:"#d97706",blue:"#2563eb"};

  const STATUSES = [
    {id:"promote",     label:"Promote",          color:C.green,  bg:"#f0fdf4", desc:"Move to next class"},
    {id:"provisional", label:"Provisional Pass",  color:C.blue,   bg:"#eff6ff", desc:"Move up, flagged"},
    {id:"holdback",    label:"Hold Back",         color:C.red,    bg:"#fef2f2", desc:"Stay in same class"},
    {id:"graduated",   label:"Graduated",         color:"#7c3aed",bg:"#f5f3ff", desc:"Left the school"},
  ];

  const srcClasses = [...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort((a,b)=>{
    const ai=CLASS_ORDER.indexOf(a), bi=CLASS_ORDER.indexOf(b);
    return (ai===-1?99:ai)-(bi===-1?99:bi);
  });

  // Step indicators
  const STEPS = ["Select Class","Review Students","Confirm","Done"];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20,maxWidth:900,margin:"0 auto"}}>

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"20px 24px",color:"white"}}>
        <div style={{fontWeight:900,fontSize:18,marginBottom:4}}>🎓 Promotion Wizard</div>
        <div style={{fontSize:13,opacity:.85}}>Move students to their next class at the end of term. All history is preserved.</div>
      </div>

      {/* Step bar */}
      <div style={{display:"flex",gap:0,background:"white",borderRadius:12,padding:"4px",boxShadow:`0 2px 8px ${C.shadow}`,border:`1px solid ${C.border}`}}>
        {STEPS.map((s,i)=>(
          <div key={s} style={{flex:1,textAlign:"center",padding:"10px 8px",borderRadius:8,
            background:step===i+1?"#1a6b3c":step>i+1?"#f0fdf4":"transparent",
            color:step===i+1?"white":step>i+1?C.green:C.muted,
            fontWeight:step===i+1?800:600,fontSize:12,transition:"all .2s"}}>
            {step>i+1?"✓ ":""}{s}
          </div>
        ))}
      </div>

      {/* ── STEP 1: Select Class ── */}
      {step===1 && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"white",borderRadius:14,padding:"20px 24px",boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:800,fontSize:14,color:C.primary,marginBottom:16}}>Select a class to promote</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
              {srcClasses.map(cls=>{
                const count = students.filter(s=>(s.class||s["class"])===cls).length;
                return (
                  <button key={cls} onClick={()=>setSelClass(cls)}
                    style={{padding:"14px 12px",borderRadius:12,border:`2px solid ${selClass===cls?C.primary:C.border}`,
                      background:selClass===cls?"#f0fdf4":"white",cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
                    <div style={{fontWeight:800,fontSize:14,color:selClass===cls?C.primary:C.text}}>{cls}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:3}}>{count} student{count!==1?"s":""}</div>
                  </button>
                );
              })}
            </div>
          </div>
          {selClass && (
            <button onClick={()=>{ initDecisions(selClass); setStep(2); }}
              style={{alignSelf:"flex-end",background:C.primary,color:"white",border:"none",borderRadius:10,
                padding:"12px 28px",fontWeight:800,fontSize:14,cursor:"pointer"}}>
              Continue →
            </button>
          )}
        </div>
      )}

      {/* ── STEP 2: Review ── */}
      {step===2 && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:800,fontSize:14,color:C.primary,marginBottom:4}}>{selClass} → Review {srcStudents.length} students</div>
            <div style={{fontSize:12,color:C.muted}}>Auto-suggested based on scores. Override any decision below.</div>
          </div>

          {srcStudents.map((s,i)=>{
            const pct    = getAvgPct(s.id);
            const d      = decisions[s.id]||{status:"promote",newClass:nextClass(selClass)};
            const status = STATUSES.find(x=>x.id===d.status)||STATUSES[0];
            return (
              <div key={s.id} style={{background:"white",borderRadius:14,padding:"16px 20px",
                boxShadow:`0 2px 8px ${C.shadow}`,border:`2px solid ${status.bg}`,display:"flex",gap:14,alignItems:"center",flexWrap:"wrap"}}>
                {/* Student info */}
                <div style={{minWidth:180,flex:1}}>
                  <div style={{fontWeight:800,fontSize:14,color:C.text}}>{s.full_name||s.name}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>ID: {s.id}</div>
                  {pct!==null
                    ? <div style={{fontSize:12,fontWeight:700,marginTop:4,color:pct>=PASS_THRESHOLD?C.green:C.red}}>Avg Score: {pct}%</div>
                    : <div style={{fontSize:12,color:C.muted,marginTop:4}}>No results recorded</div>
                  }
                </div>

                {/* Status buttons */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {STATUSES.map(st=>(
                    <button key={st.id}
                      onClick={()=>setDecision(s.id, st.id, st.id==="holdback"?selClass:st.id==="graduated"?"Graduated":nextClass(selClass))}
                      style={{padding:"6px 12px",borderRadius:8,border:`2px solid ${d.status===st.id?st.color:C.border}`,
                        background:d.status===st.id?st.bg:"white",color:d.status===st.id?st.color:C.muted,
                        fontWeight:d.status===st.id?800:600,fontSize:12,cursor:"pointer",transition:"all .1s"}}>
                      {st.label}
                    </button>
                  ))}
                </div>

                {/* Target class (editable for promote/provisional) */}
                {d.status!=="holdback"&&d.status!=="graduated"&&(
                  <div style={{minWidth:130}}>
                    <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:4}}>MOVING TO</label>
                    <select value={d.newClass}
                      onChange={e=>setDecision(s.id, d.status, e.target.value)}
                      style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:13,width:"100%"}}>
                      {CLASS_ORDER.filter(c=>c!==selClass).map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}

          {/* Summary + nav */}
          <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:`0 2px 8px ${C.shadow}`,
            display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
            {STATUSES.map(st=>{
              const n=Object.values(decisions).filter(d=>d.status===st.id).length;
              return n>0?(
                <div key={st.id} style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{width:10,height:10,borderRadius:"50%",background:st.color,display:"inline-block"}}/>
                  <span style={{fontSize:13,fontWeight:700,color:st.color}}>{n} {st.label}</span>
                </div>
              ):null;
            })}
            <div style={{marginLeft:"auto",display:"flex",gap:10}}>
              <button onClick={()=>setStep(1)}
                style={{padding:"10px 20px",borderRadius:8,border:`1px solid ${C.border}`,background:"white",cursor:"pointer",fontSize:13}}>
                ← Back
              </button>
              <button onClick={()=>setStep(3)}
                style={{padding:"10px 24px",borderRadius:8,border:"none",background:C.primary,color:"white",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                Review & Confirm →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 3: Confirm ── */}
      {step===3 && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:14,padding:"16px 20px"}}>
            <div style={{fontWeight:800,fontSize:14,color:"#92400e",marginBottom:4}}>⚠️ Please review before confirming</div>
            <div style={{fontSize:13,color:"#92400e"}}>This will update student records in the database. Hold Back students are not moved.</div>
          </div>

          <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C.shadow}`,overflow:"hidden"}}>
            <div style={{background:C.primary,padding:"10px 18px",display:"flex",gap:8}}>
              {["STUDENT","FROM","TO","STATUS"].map(h=>(
                <div key={h} style={{flex:1,color:"white",fontWeight:700,fontSize:11}}>{h}</div>
              ))}
            </div>
            {srcStudents.map((s,i)=>{
              const d = decisions[s.id]||{status:"promote",newClass:nextClass(selClass)};
              const st= STATUSES.find(x=>x.id===d.status)||STATUSES[0];
              return (
                <div key={s.id} style={{display:"flex",gap:8,padding:"12px 18px",
                  background:i%2===0?"white":C.bg,borderBottom:`1px solid ${C.border}`,alignItems:"center"}}>
                  <div style={{flex:1,fontWeight:600,fontSize:13}}>{s.full_name||s.name}</div>
                  <div style={{flex:1,fontSize:13,color:C.muted}}>{selClass}</div>
                  <div style={{flex:1,fontSize:13,fontWeight:700,color:st.color}}>{d.newClass}</div>
                  <div style={{flex:1}}>
                    <span style={{background:st.bg,color:st.color,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:800}}>
                      {st.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setStep(2)}
              style={{padding:"12px 24px",borderRadius:8,border:`1px solid ${C.border}`,background:"white",cursor:"pointer",fontSize:13}}>
              ← Back
            </button>
            <button onClick={confirmPromotions} disabled={saving}
              style={{padding:"12px 32px",borderRadius:8,border:"none",background:saving?"#9ca3af":C.primary,
                color:"white",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
              {saving?"Updating...":"✅ Confirm Promotions"}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: Done ── */}
      {step===4 && (
        <div style={{display:"flex",flexDirection:"column",gap:16,alignItems:"center",padding:"40px 20px"}}>
          <div style={{fontSize:48}}>🎉</div>
          <div style={{fontWeight:900,fontSize:20,color:C.primary}}>Promotions Complete!</div>
          <div style={{fontSize:14,color:C.muted}}>Student records have been updated in the database.</div>
          <button onClick={()=>{ setStep(1); setSelClass(""); setDecisions({}); }}
            style={{padding:"12px 32px",borderRadius:10,border:"none",background:C.primary,color:"white",fontWeight:800,fontSize:14,cursor:"pointer",marginTop:8}}>
            Promote Another Class
          </button>
        </div>
      )}

      {/* ── Promotion Log ── */}
      {log.length>0 && step===1 && (
        <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C.shadow}`,overflow:"hidden"}}>
          <div style={{padding:"14px 20px",fontWeight:800,fontSize:14,color:C.primary,borderBottom:`1px solid ${C.border}`}}>
            📋 Recent Promotion History
          </div>
          <div style={{overflowX:"auto"}}>
            <div style={{display:"flex",gap:8,padding:"8px 18px",background:C.bg,borderBottom:`1px solid ${C.border}`}}>
              {["STUDENT","FROM","TO","STATUS","DATE"].map(h=>(
                <div key={h} style={{flex:1,fontSize:10,fontWeight:700,color:C.muted,minWidth:80}}>{h}</div>
              ))}
            </div>
            {log.slice(0,10).map((r,i)=>{
              const st=STATUSES.find(x=>x.id===r.status)||STATUSES[0];
              return (
                <div key={i} style={{display:"flex",gap:8,padding:"10px 18px",background:i%2===0?"white":C.bg,
                  borderBottom:`1px solid ${C.border}`,alignItems:"center"}}>
                  <div style={{flex:1,fontSize:13,fontWeight:600,minWidth:80}}>{r.student_name}</div>
                  <div style={{flex:1,fontSize:12,color:C.muted,minWidth:80}}>{r.from_class}</div>
                  <div style={{flex:1,fontSize:12,fontWeight:700,color:st.color,minWidth:80}}>{r.to_class}</div>
                  <div style={{flex:1,minWidth:80}}>
                    <span style={{background:st.bg,color:st.color,borderRadius:20,padding:"2px 8px",fontSize:10,fontWeight:800}}>{st.label}</span>
                  </div>
                  <div style={{flex:1,fontSize:11,color:C.muted,minWidth:80}}>{new Date(r.promoted_at).toLocaleDateString("en-GB")}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}

// ── TIMETABLE & SCHEDULE (combined) ────────────────────────────
function TimetableManagement({ setSettings = ()=>{}, students = [], settings = {} }) {
  const S = useSettings();
  const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const [classTeachers, setClassTeachers] = React.useState({});
  const [addingClass,   setAddingClass]   = React.useState("");
  const [staffList,     setStaffList]     = React.useState([]);
  React.useEffect(()=>{
    supabase.from("class_teachers").select("class_name,staff_id").then(({data})=>{
      if(data) setClassTeachers(Object.fromEntries(data.map(r=>[r.class_name,r.staff_id])));
    });
    supabase.from("staff").select("id,name,role").eq("status","Active").order("name").then(({data})=>setStaffList(data||[]));
  },[]);
  const updClasses = (val) => setSettings(p=>({...p,classes:val}));
  const DAY_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat"];
  const FALLBACK_SUBS = ["Quran Nazra","Quran Hifz","Urdu","English","Mathematics","Science","Social Studies","Islamiat","Computer","General Knowledge","Assembly","Break"];
  const TYPE_OPTS  = ["period","break","assembly","lunch","prayer","hifz","transition","other"];
  const TYPE_COLOR = {period:"#1a6b3c",break:"#d97706",assembly:"#7c3aed",lunch:"#0891b2",prayer:"#16a34a",hifz:"#6d28d9",transition:"#0891b2",other:"#374151"};

  const [mainTab, setMainTab] = React.useState("timetable");
  const [timetable, setTimetable] = React.useState([]);
  const [staff,     setStaff]     = React.useState([]);
  const [subjects,  setSubjects]  = React.useState([]);
  const [ttTab,     setTtTab]     = React.useState("view");
  const [viewClass, setViewClass] = React.useState("Class 1");
  const [viewStream, setViewStream] = React.useState("all"); // "all"|"school"|"hifz_school"
  const emptyForm = {cls:"",days:["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],period_no:"1",bell_label:"",subject:"",teacher_name:"",start_time:"",end_time:""};
  const [form,      setForm]      = React.useState(emptyForm);
  const [saving,    setSaving]    = React.useState(false);
  const [bellPeriods, setBellPeriods] = React.useState([]);
  const [bellSaving,  setBellSaving]  = React.useState(false);

  // Auto-Schedule state
  const [asAssignments, setAsAssignments] = React.useState({});
  // asAssignments: { "Class 1": { "Mathematics": "Sir Ahmed", "English": "Miss Sara" }, ... }
  const [asClass,       setAsClass]       = React.useState("Class 1");
  const [asConstraints, setAsConstraints] = React.useState({quranFirst:true,coreFirst:true,maxPerTeacher:6});
  const [asAssignSaving,setAsAssignSaving]= React.useState(false);
  const [asDraft,       setAsDraft]       = React.useState(null);
  const [asGenerating,  setAsGenerating]  = React.useState(false);
  const [asApplying,    setAsApplying]    = React.useState(false);
  const [asConflicts,   setAsConflicts]   = React.useState([]);
  const [asPeriodsWeek, setAsPeriodsWeek] = React.useState({});
  const [teacherCons,   setTeacherCons]   = React.useState({});
  const [subjectRules,  setSubjectRules]  = React.useState({});
  const [classTchrs,    setClassTchrs]    = React.useState({});
  const [asPreviewDay,  setAsPreviewDay]  = React.useState("");
  const [asAdvanced,    setAsAdvanced]    = React.useState(false);
  const [pwSaving,      setPwSaving]      = React.useState(false);
  const [tcSaving,      setTcSaving]      = React.useState(false);
  const [srSaving,      setSrSaving]      = React.useState(false);

  // Hifz schedule (synced from settings)
  const [hifzW,     setHifzW]     = React.useState([]);
  const [hifzS,     setHifzS]     = React.useState([]);
  const [hifzSubTab,setHifzSubTab]= React.useState("winter");
  const [hifzSaving,setHifzSaving]= React.useState(false);
  // School days (synced from settings)
  const [offDays,   setOffDays]   = React.useState(["Saturday","Sunday"]);
  const [daysSaving,setDaysSaving]= React.useState(false);
  // Hifz+School enrolled subjects
  const [hifzSchoolSubs, setHifzSchoolSubs] = React.useState([]);
  const [hsSaving,       setHsSaving]       = React.useState(false);

  const [toast, setToast] = React.useState(null);
  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  React.useEffect(()=>{
    Promise.all([
      supabase.from("timetable").select("*").order("period_no"),
      supabase.from("staff").select("id,name,role,subject").eq("status","Active").order("name"),
      supabase.from("subjects").select("*").order("type").order("sort_order").order("name"),
      supabase.from("class_teachers").select("class_name,staff_id"),
    ]).then(([tt,st,sub,ct])=>{
      setTimetable(tt.data||[]);
      setStaff(st.data||[]);
      setSubjects(sub.data||[]);
      const staffData=st.data||[];
      const ctMap={};
      (ct.data||[]).forEach(r=>{const s=staffData.find(x=>x.id===r.staff_id);if(s)ctMap[r.class_name]=s.name;});
      setClassTchrs(ctMap);
    });
  },[]);

  React.useEffect(()=>{
    try { setBellPeriods(JSON.parse(S.bell_schedule||"[]")); } catch(e){ setBellPeriods([]); }
  },[S.bell_schedule]);

  React.useEffect(()=>{
    try { setHifzW(JSON.parse(S.hifz_schedule_winter||"[]")); } catch(e){ setHifzW([]); }
  },[S.hifz_schedule_winter]);

  React.useEffect(()=>{
    try { setHifzS(JSON.parse(S.hifz_schedule_summer||"[]")); } catch(e){ setHifzS([]); }
  },[S.hifz_schedule_summer]);

  React.useEffect(()=>{
    try { setHifzSchoolSubs(JSON.parse(S.hifz_school_subjects||"[]")); } catch(e){ setHifzSchoolSubs([]); }
  },[S.hifz_school_subjects]);

  React.useEffect(()=>{
    try { setAsAssignments(JSON.parse(S.timetable_assignments||"{}")); } catch(e){ setAsAssignments({}); }
  },[S.timetable_assignments]);

  React.useEffect(()=>{
    const raw = S.off_days||"Saturday,Sunday";
    setOffDays(raw.split(",").map(d=>d.trim()).filter(Boolean));
  },[S.off_days]);

  React.useEffect(()=>{ try{setAsPeriodsWeek(JSON.parse(S.timetable_periods_week||"{}"));}catch(e){setAsPeriodsWeek({});} },[S.timetable_periods_week]);
  React.useEffect(()=>{ try{setTeacherCons(JSON.parse(S.teacher_constraints||"{}"));}catch(e){setTeacherCons({});} },[S.teacher_constraints]);
  React.useEffect(()=>{ try{setSubjectRules(JSON.parse(S.subject_rules||"{}"));}catch(e){setSubjectRules({});} },[S.subject_rules]);

  const prevTabRef = React.useRef(mainTab);
  React.useEffect(()=>{
    if(prevTabRef.current==="subjects"&&mainTab!=="subjects"){
      supabase.from("subjects").select("*").order("type").order("sort_order").order("name")
        .then(({data})=>{ if(data) setSubjects(data); });
    }
    prevTabRef.current=mainTab;
  },[mainTab]);

  const activeSubjects = subjects.filter(s=>s.is_active!==false&&s.type!=="HifzComponent").map(s=>s.name);
  const SUBJECT_OPTS = activeSubjects.length > 0 ? activeSubjects : FALLBACK_SUBS;
  const hasBell = bellPeriods.length > 0;
  const selBell = bellPeriods.find(b=>b.label===form.bell_label)||null;
  const ALL_WEEK = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
  const schoolDays = DAYS.filter(d=>!offDays.includes(d));

  const saveHifz = async (season, slots) => {
    const key = season==="winter"?"hifz_schedule_winter":"hifz_schedule_summer";
    const val = JSON.stringify(slots);
    setHifzSaving(true);
    const {error} = await supabase.from("school_settings").upsert([{key,value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings(p=>({...p,[key]:val})); showToast("Hifz schedule saved!"); }
    setHifzSaving(false);
  };

  const saveAssignments = async () => {
    const val = JSON.stringify(asAssignments);
    setAsAssignSaving(true);
    const {error} = await supabase.from("school_settings").upsert([{key:"timetable_assignments",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings(p=>({...p,timetable_assignments:val})); showToast("Assignments saved!"); }
    setAsAssignSaving(false);
  };

  const savePeriodsWeek = async () => {
    const val=JSON.stringify(asPeriodsWeek); setPwSaving(true);
    const {error}=await supabase.from("school_settings").upsert([{key:"timetable_periods_week",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false); else { setSettings(p=>({...p,timetable_periods_week:val})); showToast("Schedule saved!"); }
    setPwSaving(false);
  };
  const saveTeacherCons = async () => {
    const val=JSON.stringify(teacherCons); setTcSaving(true);
    const {error}=await supabase.from("school_settings").upsert([{key:"teacher_constraints",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false); else { setSettings(p=>({...p,teacher_constraints:val})); showToast("Teacher constraints saved!"); }
    setTcSaving(false);
  };
  const saveSubjectRules = async () => {
    const val=JSON.stringify(subjectRules); setSrSaving(true);
    const {error}=await supabase.from("school_settings").upsert([{key:"subject_rules",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false); else { setSettings(p=>({...p,subject_rules:val})); showToast("Subject rules saved!"); }
    setSrSaving(false);
  };

  const saveHifzSchoolSubs = async (subs) => {
    const val = JSON.stringify(subs);
    setHsSaving(true);
    const {error} = await supabase.from("school_settings").upsert([{key:"hifz_school_subjects",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings(p=>({...p,hifz_school_subjects:val})); showToast("Hifz+School subjects saved!"); }
    setHsSaving(false);
  };

  const saveOffDays = async (newOffDays) => {
    const val = newOffDays.join(",");
    setDaysSaving(true);
    const {error} = await supabase.from("school_settings").upsert([{key:"off_days",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings(p=>({...p,off_days:val})); showToast("School days saved!"); }
    setDaysSaving(false);
  };

  const toggleOffDay = day => {
    const next = offDays.includes(day)?offDays.filter(d=>d!==day):[...offDays,day];
    setOffDays(next);
    saveOffDays(next);
  };
  const classSchedule = timetable.filter(t=>t.class===viewClass);

  const subjectColor = s => {
    const m = {"Quran Nazra":C.primary,"Quran Hifz":C.teal,"Urdu":"#7c3aed","English":C.blue,
      "Mathematics":C.rose,"Science":"#d97706","Social Studies":"#0891b2",
      "Islamiat":C.primary,"Computer":"#6366f1","Break":"#6b7280","Assembly":C.accent};
    return m[s]||C.muted;
  };

  // Multi-day insert
  const saveSlot = async () => {
    if(!form.cls||form.cls==="--Select--"||!form.subject||form.subject==="--Select--")
      return showToast("Class and subject are required",false);
    if(!form.days||form.days.length===0)
      return showToast("Select at least one day",false);
    setSaving(true);
    const rows = form.days.map(day=>({
      class:form.cls, day, period_no:Number(form.period_no)||1,
      subject:form.subject, teacher_name:form.teacher_name,
      start_time:form.start_time, end_time:form.end_time,
    }));
    const {data,error} = await supabase.from("timetable").insert(rows).select();
    if(error) showToast("Error: "+error.message,false);
    else { setTimetable(p=>[...p,...(data||[])]); showToast(rows.length+" period(s) added!"); setForm(emptyForm); }
    setSaving(false);
  };

  const deleteSlot = async (id) => {
    await supabase.from("timetable").delete().eq("id",id);
    setTimetable(p=>p.filter(t=>t.id!==id));
    showToast("Period removed");
  };

  const saveBellToDB = async () => {
    setBellSaving(true);
    const val = JSON.stringify(bellPeriods);
    const {error} = await supabase.from("school_settings").upsert([{key:"bell_schedule",value:val}],{onConflict:"key"});
    if(error) showToast("Error: "+error.message,false);
    else { setSettings(p=>({...p,bell_schedule:val})); showToast("Bell schedule saved!"); }
    setBellSaving(false);
  };

  const updBell = (i,field,val) => setBellPeriods(p=>{ const a=[...p]; a[i]={...a[i],[field]:val}; return a; });

  // Auto-Schedule helpers
  const getAssignment   = (cls,sub) => (asAssignments[cls]||{})[sub]||"";
  const setAssignment   = (cls,sub,teacher) => setAsAssignments(p=>({...p,[cls]:{...(p[cls]||{}),[sub]:teacher}}));
  const classSubjectCount = cls => Object.values(asAssignments[cls]||{}).filter(t=>t&&t!=="").length;
  const getPW           = (cls,sub) => { const v=(asPeriodsWeek[cls]||{})[sub]; return (v&&v>0)?v:schoolDays.length; };
  const setPW           = (cls,sub,n) => setAsPeriodsWeek(p=>({...p,[cls]:{...(p[cls]||{}),[sub]:Number(n)||1}}));
  const getMaxPerDay    = (t) => teacherCons[t]?.maxPerDay || asConstraints.maxPerTeacher;
  const getUnavail      = (t) => teacherCons[t]?.unavailable || [];

  const runGenerate = () => {
    setAsGenerating(true);
    setAsConflicts([]);

    if(!hasBell){
      setAsConflicts([{msg:"Bell Schedule required — define your period structure first."}]);
      setAsDraft(null); setAsGenerating(false); return;
    }

    const conflicts = [];
    const results   = [];
    const teachingSubsList = SUBJECT_OPTS.filter(s=>!/break|lunch|prayer|assembly|transition/i.test(s));

    // ── Bell schedule breakdown ─────────────────────────────────
    const fixedPeriodInfo = {};   // pNo -> bell
    const teachingPeriodNos = []; // pNos that are teaching slots
    bellPeriods.forEach((bell,idx)=>{
      const pNo = idx+1;
      if(/break|lunch|prayer|assembly|transition/i.test(bell.type||"period")) fixedPeriodInfo[pNo]=bell;
      else teachingPeriodNos.push(pNo);
    });

    // ── Configured classes ──────────────────────────────────────
    const configuredClasses = ALLCLASSES.filter(cls=>
      teachingSubsList.some(sub=>(asAssignments[cls]||{})[sub])
    );
    if(!configuredClasses.length){
      setAsConflicts([{msg:"No classes configured — assign at least one subject-teacher pair."}]);
      setAsDraft(null); setAsGenerating(false); return;
    }

    // ── Weekly schedule grid ────────────────────────────────────
    // schedule[day][cls][pNo] = {subject, teacher, isFixed, start, end}
    const schedule = {};
    schoolDays.forEach(day=>{
      schedule[day]={};
      configuredClasses.forEach(cls=>{
        schedule[day][cls]={};
        Object.entries(fixedPeriodInfo).forEach(([pNo,bell])=>{
          schedule[day][cls][Number(pNo)]={
            subject:bell.label||(bell.type.charAt(0).toUpperCase()+bell.type.slice(1)),
            teacher:"",isFixed:true,start:bell.start||"",end:bell.end||""
          };
        });
      });
    });

    // ── Per-day tracking ────────────────────────────────────────
    const teacherUsed = {}; // [day][teacher] -> Set<pNo>
    const roomUsed    = {}; // [day][room]    -> Set<pNo>
    schoolDays.forEach(day=>{ teacherUsed[day]={}; roomUsed[day]={}; });

    // ── Tokens: one per (class, subject) ────────────────────────
    let tokens = [];
    configuredClasses.forEach(cls=>{
      const clsAssign = asAssignments[cls]||{};
      teachingSubsList.forEach(sub=>{
        const teacher = clsAssign[sub];
        if(!teacher) return;
        tokens.push({
          cls, subject:sub, teacher,
          periodsPerWeek: getPW(cls,sub),
          isDouble: subjectRules[sub]?.doublePeriod||false,
          specialRoom: subjectRules[sub]?.specialRoom||null,
        });
      });
    });

    // Sort most-constrained first
    tokens.sort((a,b)=>{
      const score = t=>(t.isDouble?4:0)+(t.specialRoom?2:0)+(getUnavail(t.teacher).length>0?1:0);
      return score(b)-score(a);
    });

    // ── Soft score (lower = better placement) ───────────────────
    const softScore = (day,pNo,teacher) => {
      let s = 0;
      const used = teacherUsed[day][teacher]||new Set();
      if(used.size>0){
        const arr=[...used].sort((a,b)=>a-b);
        const mn=arr[0], mx=arr[arr.length-1];
        if(pNo>mx+1) s+=(pNo-mx-1)*4;
        if(pNo<mn-1) s+=(mn-pNo-1)*4;
        if(pNo>mn&&pNo<mx&&!used.has(pNo)) s-=8; // fills gap
      }
      if(pNo===teachingPeriodNos[teachingPeriodNos.length-1]) s+=2; // avoid last
      return s;
    };

    // ── Spread helper: choose N days spread across the week ─────
    const spreadDays = (n,days) => {
      if(n>=days.length) return [...days];
      const step=days.length/n;
      const chosen=[];
      for(let i=0;i<n;i++) chosen.push(days[Math.round(i*step)%days.length]);
      return [...new Set(chosen)];
    };

    // ── Place a single (non-double) token on a specific day ─────
    const placeOnDay = (token,day) => {
      const {cls,subject,teacher,specialRoom} = token;
      const maxP = getMaxPerDay(teacher);
      const unavail = getUnavail(teacher);
      const used = teacherUsed[day][teacher]||new Set();
      if(used.size>=maxP) return false;
      const alreadyThisDay = Object.values(schedule[day][cls]).some(s=>!s.isFixed&&s.subject===subject);
      if(alreadyThisDay) return false;
      const available = teachingPeriodNos.filter(p=>{
        if(schedule[day][cls][p]) return false;
        if(used.has(p)) return false;
        if(unavail.includes(p)) return false;
        if(specialRoom&&(roomUsed[day][specialRoom]||new Set()).has(p)) return false;
        return true;
      });
      if(!available.length) return false;
      const best = available.reduce((a,b)=>softScore(day,a,teacher)<=softScore(day,b,teacher)?a:b);
      const bell = bellPeriods[best-1]||{};
      schedule[day][cls][best]={subject,teacher,isFixed:false,start:bell.start||"",end:bell.end||""};
      if(!teacherUsed[day][teacher]) teacherUsed[day][teacher]=new Set();
      teacherUsed[day][teacher].add(best);
      if(specialRoom){
        if(!roomUsed[day][specialRoom]) roomUsed[day][specialRoom]=new Set();
        roomUsed[day][specialRoom].add(best);
      }
      return true;
    };

    // ── Phase 2: Homeroom teacher → Period 1 for their class ────
    const p1 = teachingPeriodNos[0];
    const homeroomTokens=[], regularTokens=[];
    tokens.forEach(t=>{
      if(classTchrs[t.cls]&&t.teacher===classTchrs[t.cls]) homeroomTokens.push(t);
      else regularTokens.push(t);
    });

    if(p1){
      homeroomTokens.forEach(token=>{
        const {cls,subject,teacher,periodsPerWeek,specialRoom} = token;
        const bell = bellPeriods[p1-1]||{};
        let placed=0;
        for(const day of schoolDays){
          if(placed>=periodsPerWeek) break;
          if(schedule[day][cls][p1]) continue;
          const used=teacherUsed[day][teacher]||new Set();
          if(used.has(p1)||getUnavail(teacher).includes(p1)) continue;
          if(specialRoom&&(roomUsed[day][specialRoom]||new Set()).has(p1)) continue;
          schedule[day][cls][p1]={subject,teacher,isFixed:false,start:bell.start||"",end:bell.end||""};
          if(!teacherUsed[day][teacher]) teacherUsed[day][teacher]=new Set();
          teacherUsed[day][teacher].add(p1);
          if(specialRoom){
            if(!roomUsed[day][specialRoom]) roomUsed[day][specialRoom]=new Set();
            roomUsed[day][specialRoom].add(p1);
          }
          placed++;
        }
        if(placed<periodsPerWeek)
          regularTokens.unshift({...token,periodsPerWeek:periodsPerWeek-placed});
      });
    } else {
      regularTokens.unshift(...homeroomTokens);
    }

    // ── Phase 3: Assign regular tokens ─────────────────────────
    for(const token of regularTokens){
      const {cls,subject,teacher,periodsPerWeek,isDouble,specialRoom} = token;
      const maxP = getMaxPerDay(teacher);
      const unavail = getUnavail(teacher);
      const preferred = spreadDays(periodsPerWeek,schoolDays);
      const daysToTry = [...preferred,...schoolDays.filter(d=>!preferred.includes(d))];
      let placed=0;

      for(const day of daysToTry){
        if(placed>=periodsPerWeek) break;
        const alreadyThisDay=Object.values(schedule[day][cls]).some(s=>!s.isFixed&&s.subject===subject);
        if(alreadyThisDay) continue;
        const used=teacherUsed[day][teacher]||new Set();
        if(used.size>=maxP) continue;

        if(isDouble){
          let foundPair=null;
          for(let i=0;i<teachingPeriodNos.length-1;i++){
            const pa=teachingPeriodNos[i], pb=teachingPeriodNos[i+1];
            if(pb-pa!==1) continue;
            if(schedule[day][cls][pa]||schedule[day][cls][pb]) continue;
            if(used.has(pa)||used.has(pb)) continue;
            if(unavail.includes(pa)||unavail.includes(pb)) continue;
            const rm=specialRoom?(roomUsed[day][specialRoom]||new Set()):new Set();
            if(rm.has(pa)||rm.has(pb)) continue;
            if((used.size+2)>maxP) continue;
            foundPair=[pa,pb]; break;
          }
          if(foundPair){
            const [fp1,fp2]=foundPair;
            const b1=bellPeriods[fp1-1]||{},b2=bellPeriods[fp2-1]||{};
            schedule[day][cls][fp1]={subject,teacher,isFixed:false,start:b1.start||"",end:b1.end||""};
            schedule[day][cls][fp2]={subject,teacher,isFixed:false,start:b2.start||"",end:b2.end||""};
            if(!teacherUsed[day][teacher]) teacherUsed[day][teacher]=new Set();
            teacherUsed[day][teacher].add(fp1); teacherUsed[day][teacher].add(fp2);
            if(specialRoom){
              if(!roomUsed[day][specialRoom]) roomUsed[day][specialRoom]=new Set();
              roomUsed[day][specialRoom].add(fp1); roomUsed[day][specialRoom].add(fp2);
            }
            placed++;
          } else {
            // fallback: place as single
            if(placeOnDay(token,day)){
              conflicts.push({msg:`Double period for "${subject}" in ${cls} on ${day}: no consecutive slots, placed as single.`});
              placed++;
            }
          }
        } else {
          if(placeOnDay(token,day)) placed++;
        }
      }

      if(placed<periodsPerWeek)
        conflicts.push({msg:`"${subject}" in ${cls}: placed ${placed}/${periodsPerWeek}×/week. Teacher "${teacher}" may be over-booked or unavailable on remaining days.`});
    }

    // ── Phase 4: Local search — reduce teacher gaps ─────────────
    const dayPenalty = (day) => {
      let pen=0;
      Object.values(teacherUsed[day]).forEach(periods=>{
        const sorted=[...periods].sort((a,b)=>a-b);
        if(sorted.length<2) return;
        const mn=sorted[0],mx=sorted[sorted.length-1];
        for(let p=mn+1;p<mx;p++){
          if(teachingPeriodNos.includes(p)&&!periods.has(p)) pen+=3;
        }
      });
      return pen;
    };

    for(let iter=0;iter<25;iter++){
      for(const day of schoolDays){
        for(const cls of configuredClasses){
          const slots=Object.entries(schedule[day][cls])
            .filter(([,v])=>!v.isFixed&&v.teacher)
            .map(([p,v])=>({p:Number(p),v}));
          if(slots.length<2) continue;
          for(let i=0;i<slots.length;i++){
            for(let j=i+1;j<slots.length;j++){
              const {p:p1s,v:v1}=slots[i], {p:p2s,v:v2}=slots[j];
              if(v1.teacher===v2.teacher) continue;
              const t1=teacherUsed[day][v1.teacher]||new Set();
              const t2=teacherUsed[day][v2.teacher]||new Set();
              if(t2.has(p2s)||getUnavail(v1.teacher).includes(p2s)) continue;
              if(t1.has(p1s)||getUnavail(v2.teacher).includes(p1s)) continue;
              const before=dayPenalty(day);
              const b1=bellPeriods[p1s-1]||{},b2=bellPeriods[p2s-1]||{};
              schedule[day][cls][p1s]={...v2,start:b1.start||"",end:b1.end||""};
              schedule[day][cls][p2s]={...v1,start:b2.start||"",end:b2.end||""};
              t1.delete(p1s); t1.add(p2s);
              t2.delete(p2s); t2.add(p1s);
              if(dayPenalty(day)>=before){
                schedule[day][cls][p1s]={...v1,start:b1.start||"",end:b1.end||""};
                schedule[day][cls][p2s]={...v2,start:b2.start||"",end:b2.end||""};
                t1.delete(p2s); t1.add(p1s);
                t2.delete(p1s); t2.add(p2s);
              }
            }
          }
        }
      }
    }

    // ── Phase 5: Hard constraint check — teacher double-booking ─
    schoolDays.forEach(day=>{
      const tPeriodMap={};
      configuredClasses.forEach(cls=>{
        Object.entries(schedule[day][cls]).forEach(([pNo,slot])=>{
          if(!slot.teacher||slot.isFixed) return;
          const key=`${slot.teacher}_${pNo}`;
          if(tPeriodMap[key]) conflicts.push({msg:`"${slot.teacher}" double-booked on ${day} P${pNo}: ${tPeriodMap[key]} & ${cls}`});
          else tPeriodMap[key]=cls;
        });
      });
    });

    // ── Phase 6: Flatten to results array ───────────────────────
    configuredClasses.forEach(cls=>{
      schoolDays.forEach(day=>{
        bellPeriods.forEach((bell,idx)=>{
          const pNo=idx+1;
          const slot=schedule[day][cls][pNo];
          if(!slot) return;
          results.push({class:cls,day,period_no:pNo,subject:slot.subject,teacher_name:slot.teacher||"",start_time:bell.start||"",end_time:bell.end||""});
        });
      });
    });

    setAsConflicts(conflicts);
    setAsDraft(results.length>0?results:null);
    if(!results.length) setAsConflicts(p=>[...p,{msg:"No schedule generated. Check assignments and bell schedule."}]);
    setAsGenerating(false);
  };

  const applyDraft = async () => {
    if(!asDraft||!asDraft.length) return;
    const cls_list = [...new Set(asDraft.map(r=>r.class))];
    if(!window.confirm("This will REPLACE all timetable entries for: "+cls_list.join(", ")+".\n\n"+asDraft.length+" new entries will be inserted.\n\nContinue?")) return;
    setAsApplying(true);
    for(const cls of cls_list) await supabase.from("timetable").delete().eq("class",cls);
    const {data,error} = await supabase.from("timetable").insert(asDraft).select();
    if(error) showToast("Error: "+error.message,false);
    else {
      setTimetable(p=>[...p.filter(t=>!cls_list.includes(t.class)),...(data||[])]);
      showToast("Applied "+(data||[]).length+" entries!");
      setAsDraft(null);
    }
    setAsApplying(false);
  };

  const mBtn = (id,lbl,icon) => (
    <button onClick={()=>setMainTab(id)} style={{padding:"10px 22px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
      background:mainTab===id?C.primary:C.primaryPale,color:mainTab===id?C.white:C.primary,transition:"all .2s"}}>{icon} {lbl}</button>
  );
  const ttBtn = (id,lbl,icon) => (
    <button onClick={()=>setTtTab(id)} style={{padding:"8px 18px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
      background:ttTab===id?C.primary:C.primaryPale,color:ttTab===id?C.white:C.primary,transition:"all .2s"}}>{icon} {lbl}</button>
  );
  const inp = {border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px",fontSize:12};

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Header */}
      <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:20,color:C.primary,marginBottom:4}}>Timetable & Schedule</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:activeSubjects.length>0?8:0}}>Class timetables, bell schedule and subjects in one place</div>
        {activeSubjects.length>0&&(
          <div style={{background:C.primaryPale,borderRadius:8,padding:"5px 12px",fontSize:11,color:C.primary,fontWeight:600,display:"inline-block"}}>
            Using {activeSubjects.length} live subjects from the Subjects tab
          </div>
        )}
      </div>

      {/* Main tabs */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        {mBtn("timetable","Class Timetable","📅")}
        {mBtn("bell","Bell Schedule","🔔")}
        {mBtn("subjects","Subjects","📚")}
        {mBtn("autoschedule","Auto-Schedule","⚡")}
        {mBtn("hifz","Hifz Schedule","📿")}
        {mBtn("classes","Classes","📚")}
      </div>

      {/* TIMETABLE TAB */}
      {mainTab==="timetable" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {ttBtn("view","View Timetable","📅")}
            {ttBtn("add","Add Period","➕")}
            {ttBtn("all","All Entries","📋")}
          </div>

          {ttTab==="view" && (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div style={{background:C.white,borderRadius:14,padding:"14px 20px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",flexDirection:"column",gap:12}}>
                <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <span style={{fontSize:13,fontWeight:700,color:C.muted}}>Class:</span>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {ALLCLASSES.map(c=>(
                      <button key={c} onClick={()=>setViewClass(c)} style={{
                        padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,
                        background:viewClass===c?C.primary:C.primaryPale,color:viewClass===c?C.white:C.primary,transition:"all .15s"
                      }}>{c}</button>
                    ))}
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Stream:</span>
                  {[["all","All Students","#374151"],["school","School Only",C.blue],["hifz_school","Hifz+School Composite","#7c3aed"]].map(([id,lbl,col])=>(
                    <button key={id} onClick={()=>setViewStream(id)} style={{
                      padding:"5px 14px",borderRadius:20,fontSize:12,fontWeight:700,cursor:"pointer",transition:"all .15s",
                      border:"2px solid "+(viewStream===id?col:"transparent"),
                      background:viewStream===id?col+"18":C.bg,
                      color:viewStream===id?col:C.muted
                    }}>{lbl}</button>
                  ))}
                </div>
              </div>
              {/* Hifz+School Composite View */}
              {viewStream==="hifz_school" && (()=>{
                const isWinterNow = new Date().getMonth()>=9||new Date().getMonth()<=2;
                const hifzSlots = (isWinterNow?hifzW:hifzS).map(s=>({...s,source:"hifz"}));
                const enrolledSchedule = classSchedule.filter(t=>t.day==="Monday"&&hifzSchoolSubs.includes(t.subject)).map(t=>({label:t.subject,start:t.start_time,end:t.end_time,teacher:t.teacher_name,source:"school"}));
                const merged = [...hifzSlots,...enrolledSchedule].sort((a,b)=>{
                  if(!a.start&&!b.start) return 0;
                  if(!a.start) return 1; if(!b.start) return -1;
                  return a.start.localeCompare(b.start);
                });
                const hasMissing = hifzSchoolSubs.length===0;
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <div style={{background:"#f5f3ff",borderRadius:12,padding:"12px 16px",fontSize:12,color:"#6d28d9",fontWeight:600,border:"1px solid #c4b5fd"}}>
                      Composite schedule for Hifz+School students — {isWinterNow?"Winter":"Summer"} timings · School subjects: {hifzSchoolSubs.length>0?hifzSchoolSubs.join(", "):"none configured (set in Hifz Schedule tab)"}
                    </div>
                    {hasMissing&&(
                      <div style={{background:"#fffbeb",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e",border:"1px solid #fde68a"}}>
                        No subjects configured for Hifz+School stream yet. Go to the Hifz Schedule tab to select which school subjects Hifz+School students attend.
                      </div>
                    )}
                    <div style={{background:C.white,borderRadius:16,padding:20,boxShadow:`0 2px 12px ${C.shadow}`}}>
                      <div style={{fontWeight:800,color:"#6d28d9",fontSize:14,marginBottom:14}}>{viewClass} — Full Day (Hifz+School)</div>
                      {merged.length===0?(
                        <div style={{textAlign:"center",padding:40,color:C.muted}}>No schedule data. Add Hifz slots in the Hifz Schedule tab and add class periods in Add Period.</div>
                      ):(
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {merged.map((slot,i)=>{
                            const isHifz = slot.source==="hifz";
                            const isSchool = slot.source==="school";
                            const isTransition = slot.label&&/transition/i.test(slot.label);
                            const bg = isTransition?"#e0f2fe":isHifz?"#f5f3ff":C.primaryPale;
                            const border = isTransition?"#38bdf8":isHifz?"#c4b5fd":C.primary+"40";
                            const labelColor = isTransition?"#0369a1":isHifz?"#6d28d9":C.primary;
                            const badge = isTransition?"Transition":isHifz?"Hifz":"School";
                            const badgeBg = isTransition?"#0ea5e9":isHifz?"#7c3aed":C.primary;
                            return (
                              <div key={i} style={{display:"flex",gap:12,alignItems:"center",background:bg,border:"1px solid "+border,borderRadius:10,padding:"10px 14px"}}>
                                <div style={{minWidth:80,fontSize:11,color:C.muted,fontWeight:600}}>{slot.start&&slot.end?slot.start+" - "+slot.end:slot.start||"—"}</div>
                                <div style={{flex:1}}>
                                  <div style={{fontWeight:700,fontSize:13,color:labelColor}}>{slot.label}</div>
                                  {slot.description&&<div style={{fontSize:11,color:C.muted,marginTop:1}}>{slot.description}</div>}
                                  {slot.teacher&&<div style={{fontSize:11,color:C.muted,marginTop:1}}>{slot.teacher}</div>}
                                </div>
                                <span style={{background:badgeBg,color:"white",borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:700}}>{badge}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Standard grid view (All or School Only) */}
              {viewStream!=="hifz_school" && (classSchedule.length===0 ? (
                <div style={{background:C.white,borderRadius:16,padding:60,textAlign:"center",boxShadow:`0 2px 12px ${C.shadow}`}}>
                  <div style={{fontSize:48,marginBottom:12}}>📅</div>
                  <div style={{fontWeight:700,color:C.primary,fontSize:16}}>No timetable for {viewClass}</div>
                  <div style={{color:C.muted,fontSize:13,marginTop:6}}>Add periods using "Add Period" or use Auto-Schedule to generate one.</div>
                </div>
              ) : (()=>{
                const filteredSchedule = viewStream==="school" ? classSchedule.filter(t=>!hifzSchoolSubs.includes(t.subject)||hifzSchoolSubs.length===0) : classSchedule;
                const daysPresent = [...new Set(filteredSchedule.map(t=>t.day))].sort((a,b)=>DAYS.indexOf(a)-DAYS.indexOf(b));
                const maxPeriods = filteredSchedule.length>0?Math.max(...filteredSchedule.map(t=>t.period_no)):0;
                if(daysPresent.length===0) return <div style={{textAlign:"center",padding:40,color:C.muted}}>No entries for this view.</div>;
                return (
                  <div style={{background:C.white,borderRadius:16,padding:20,boxShadow:`0 2px 12px ${C.shadow}`,overflowX:"auto"}}>
                    <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:14}}>
                      {viewClass} — {viewStream==="school"?"School-Only Periods":"Full Weekly Timetable"}
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:C.primaryPale}}>
                          <th style={{padding:"8px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11,minWidth:80}}>Period</th>
                          {daysPresent.map(d=><th key={d} style={{padding:"8px 12px",textAlign:"center",fontWeight:700,color:C.primary,fontSize:11,minWidth:110}}>{d}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({length:maxPeriods},(_,i)=>i+1).map(p=>(
                          <tr key={p} style={{borderBottom:`1px solid ${C.border}`}}>
                            <td style={{padding:"8px 12px",fontWeight:700,color:C.muted,fontSize:11,whiteSpace:"nowrap"}}>
                              P{p}{bellPeriods[p-1]&&bellPeriods[p-1].start?(" "+bellPeriods[p-1].start):""}
                            </td>
                            {daysPresent.map(d=>{
                              const slot = filteredSchedule.find(t=>t.day===d&&t.period_no===p);
                              return (
                                <td key={d} style={{padding:"6px 10px",textAlign:"center",verticalAlign:"middle"}}>
                                  {slot ? (
                                    <div style={{background:subjectColor(slot.subject)+"15",border:"1px solid "+subjectColor(slot.subject)+"40",borderRadius:8,padding:"5px 8px"}}>
                                      <div style={{fontWeight:700,color:subjectColor(slot.subject),fontSize:11}}>{slot.subject}</div>
                                      {slot.teacher_name&&<div style={{fontSize:10,color:C.muted,marginTop:2}}>{slot.teacher_name}</div>}
                                    </div>
                                  ) : <div style={{color:C.border,fontSize:10}}>—</div>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })())}
            </div>
          )}

          {ttTab==="add" && (
            <div style={{background:C.white,borderRadius:16,padding:28,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>Add Period to Timetable</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:20}}>All days are checked by default since most subjects run daily. Uncheck days for once-a-week subjects.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Class *</label>
                  <select value={form.cls} onChange={e=>setForm(p=>({...p,cls:e.target.value}))}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                    {["--Select--",...ALLCLASSES].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Subject *</label>
                  <select value={form.subject} onChange={e=>setForm(p=>({...p,subject:e.target.value}))}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                    {["--Select--",...SUBJECT_OPTS].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Teacher</label>
                  <select value={form.teacher_name} onChange={e=>setForm(p=>({...p,teacher_name:e.target.value}))}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                    <option value="">-- Optional --</option>
                    {staff.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Period</label>
                  {hasBell ? (
                    <select value={form.bell_label||""} onChange={e=>{
                      const bp = bellPeriods.find(b=>b.label===e.target.value);
                      setForm(p=>({...p,bell_label:e.target.value,
                        period_no:String(bellPeriods.findIndex(b=>b.label===e.target.value)+1),
                        start_time:bp&&bp.start||"",end_time:bp&&bp.end||""}));
                    }} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:C.white}}>
                      <option value="">-- Select Period --</option>
                      {bellPeriods.map((b,i)=>(
                        <option key={i} value={b.label}>{b.label}{b.start?" ("+b.start+"-"+b.end+")":""}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={form.period_no} placeholder="e.g. 1"
                      onChange={e=>setForm(p=>({...p,period_no:e.target.value}))}
                      style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}/>
                  )}
                </div>
                {hasBell ? (
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Time (from Bell Schedule)</label>
                    <div style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13,background:"#f9fafb",color:C.muted}}>
                      {selBell&&selBell.start&&selBell.end?(selBell.start+" - "+selBell.end):"-- select a period above --"}
                    </div>
                  </div>
                ) : (
                  [["Start Time","start_time"],["End Time","end_time"]].map(([lbl,key])=>(
                    <div key={key} style={{display:"flex",flexDirection:"column",gap:5}}>
                      <label style={{fontSize:12,fontWeight:700,color:C.primary}}>{lbl}</label>
                      <input type="time" value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))}
                        style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}/>
                    </div>
                  ))
                )}
                {/* Days multi-select */}
                <div style={{gridColumn:"1/-1",display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Apply to Days</label>
                    <button onClick={()=>setForm(p=>({...p,days:DAYS}))}
                      style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid "+C.primary,background:C.primaryPale,color:C.primary,cursor:"pointer",fontWeight:700}}>All Days</button>
                    <button onClick={()=>setForm(p=>({...p,days:["Monday","Tuesday","Wednesday","Thursday","Friday"]}))}
                      style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid "+C.border,background:C.bg,color:C.muted,cursor:"pointer",fontWeight:700}}>Mon-Fri</button>
                    <button onClick={()=>setForm(p=>({...p,days:[]}))}
                      style={{fontSize:11,padding:"3px 10px",borderRadius:6,border:"1px solid "+C.border,background:C.bg,color:C.muted,cursor:"pointer",fontWeight:700}}>Clear</button>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {DAYS.map((day,i)=>{
                      const checked = (form.days||[]).includes(day);
                      return (
                        <button key={day} onClick={()=>setForm(p=>({...p,days:checked?(p.days||[]).filter(d=>d!==day):[...(p.days||[]),day]}))}
                          style={{padding:"7px 16px",borderRadius:20,border:"2px solid "+(checked?C.primary:C.border),
                          background:checked?C.primary:C.white,color:checked?C.white:C.muted,
                          fontWeight:700,fontSize:12,cursor:"pointer",transition:"all .15s"}}>
                          {DAY_SHORT[i]}
                        </button>
                      );
                    })}
                  </div>
                  {(form.days||[]).length>0 && (
                    <div style={{fontSize:11,color:C.teal,fontWeight:600}}>
                      Will insert {(form.days||[]).length} entr{(form.days||[]).length===1?"y":"ies"} — one per selected day
                    </div>
                  )}
                </div>
              </div>
              <button onClick={saveSlot} disabled={saving} style={{
                marginTop:24,background:saving?C.muted:C.primary,color:C.white,border:"none",borderRadius:10,
                padding:"12px 32px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Saving...":"Add Period"}
              </button>
            </div>
          )}

          {ttTab==="all" && (
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:16}}>All Timetable Entries ({timetable.length})</div>
              {timetable.length===0 ? (
                <div style={{textAlign:"center",padding:40,color:C.muted}}>No periods added yet</div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:C.primaryPale}}>
                        {["Class","Day","Period","Subject","Teacher","Time","Action"].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:12}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...timetable].sort((a,b)=>a.class.localeCompare(b.class)||DAYS.indexOf(a.day)-DAYS.indexOf(b.day)||a.period_no-b.period_no).map((t,i)=>(
                        <tr key={t.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                          <td style={{padding:"9px 12px",fontWeight:700}}>{t.class}</td>
                          <td style={{padding:"9px 12px",color:C.muted}}>{t.day}</td>
                          <td style={{padding:"9px 12px",color:C.muted}}>P{t.period_no}</td>
                          <td style={{padding:"9px 12px"}}>
                            <span style={{background:subjectColor(t.subject)+"18",color:subjectColor(t.subject),borderRadius:8,padding:"3px 10px",fontSize:11,fontWeight:700}}>{t.subject}</span>
                          </td>
                          <td style={{padding:"9px 12px",color:C.muted}}>{t.teacher_name||"—"}</td>
                          <td style={{padding:"9px 12px",color:C.muted,whiteSpace:"nowrap"}}>{t.start_time&&t.end_time?t.start_time+"-"+t.end_time:"—"}</td>
                          <td style={{padding:"9px 12px"}}>
                            <button onClick={()=>deleteSlot(t.id)} style={{background:C.rosePale,border:"none",color:C.rose,borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:700}}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* BELL SCHEDULE TAB */}
      {mainTab==="bell" && (
        <div style={{background:C.white,borderRadius:16,padding:"24px 28px",boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>Bell Schedule</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:16}}>
            Define your daily period times here. These automatically populate the period picker in the Timetable tab.
          </div>
          {bellPeriods.length===0 && (
            <div style={{background:"#fffbeb",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#92400e",fontWeight:600,marginBottom:12,border:"1px solid #fde68a"}}>
              No periods defined yet. Click "+ Add Row" to start building your bell schedule.
            </div>
          )}
          {bellPeriods.length>0 && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 140px 36px",gap:8,padding:"0 10px",marginBottom:4}}>
              {["Label","Start","End","Type",""].map(h=><div key={h} style={{fontSize:11,fontWeight:700,color:C.muted}}>{h}</div>)}
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {bellPeriods.map((p,i)=>(
              <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 140px 36px",gap:8,alignItems:"center",background:C.bg,borderRadius:8,padding:"8px 10px"}}>
                <input value={p.label} onChange={e=>updBell(i,"label",e.target.value)} placeholder="e.g. Period 1" style={inp}/>
                <input type="time" value={p.start||""} onChange={e=>updBell(i,"start",e.target.value)} style={inp}/>
                <input type="time" value={p.end||""} onChange={e=>updBell(i,"end",e.target.value)} style={inp}/>
                <select value={p.type||"period"} onChange={e=>updBell(i,"type",e.target.value)} style={{...inp,background:"white"}}>
                  {TYPE_OPTS.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
                <button onClick={()=>setBellPeriods(p=>p.filter((_,j)=>j!==i))} style={{width:36,height:34,borderRadius:6,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:16,cursor:"pointer"}}>x</button>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,marginTop:12,alignItems:"center",flexWrap:"wrap"}}>
            <button onClick={()=>setBellPeriods(p=>[...p,{label:"Period "+(p.length+1),type:"period",start:"",end:""}])}
              style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
              + Add Row
            </button>
            <button onClick={saveBellToDB} disabled={bellSaving}
              style={{background:bellSaving?C.muted:C.primary,color:"white",border:"none",borderRadius:8,padding:"8px 22px",fontWeight:800,fontSize:12,cursor:bellSaving?"not-allowed":"pointer"}}>
              {bellSaving?"Saving...":"Save Bell Schedule"}
            </button>
          </div>
          {bellPeriods.length>0 && (
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:14,padding:"10px 14px",background:C.primaryPale,borderRadius:10}}>
              <span style={{fontSize:11,fontWeight:700,color:C.primary,marginRight:4,alignSelf:"center"}}>Preview:</span>
              {bellPeriods.map((p,i)=>(
                <span key={i} style={{background:(TYPE_COLOR[p.type]||"#374151")+"18",color:TYPE_COLOR[p.type]||"#374151",borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>
                  {p.label}{p.start?" "+p.start+"-"+p.end:""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SCHOOL DAYS — inside Bell tab, rendered separately */}
      {mainTab==="bell" && (
        <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>School Days</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
            Toggle which days the school operates. Off days are excluded from attendance records and Auto-Schedule generation.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:8,marginBottom:12}}>
            {ALL_WEEK.map(day=>{
              const isOff = offDays.includes(day);
              return (
                <button key={day} onClick={()=>toggleOffDay(day)} style={{
                  padding:"12px 4px",borderRadius:10,cursor:"pointer",textAlign:"center",transition:"all .15s",
                  border:"2px solid "+(isOff?"#fca5a5":C.primary),
                  background:isOff?"#fef2f2":C.primaryPale,
                  color:isOff?"#dc2626":C.primary,fontWeight:700,fontSize:11
                }}>
                  {day.slice(0,3)}<br/>
                  <span style={{fontSize:9,fontWeight:600,marginTop:2,display:"block"}}>{isOff?"Off":"School"}</span>
                </button>
              );
            })}
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{fontSize:12,color:C.muted}}>
              Active school days: <strong style={{color:C.primary}}>{schoolDays.join(", ")||"None"}</strong>
            </div>
            {daysSaving&&<span style={{fontSize:12,color:C.teal,fontWeight:600}}>Saving...</span>}
          </div>
          {schoolDays.length>0&&(
            <div style={{marginTop:10,background:C.primaryPale,borderRadius:8,padding:"8px 14px",fontSize:11,color:C.primary,fontWeight:600}}>
              Auto-Schedule will generate entries for: {schoolDays.join(", ")}
            </div>
          )}
        </div>
      )}

      {/* SUBJECTS TAB */}
      {mainTab==="subjects" && <SubjectManagement/>}

      {/* HIFZ SCHEDULE TAB */}
      {mainTab==="hifz" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:"linear-gradient(135deg,#6d28d9,#7c3aed)",borderRadius:16,padding:"20px 24px",color:"white"}}>
            <div style={{fontWeight:900,fontSize:17,marginBottom:4}}>Hifz Daily Schedule</div>
            <div style={{fontSize:12,opacity:.85}}>Daily routine for Hifz students — two versions for winter and summer timings.</div>
          </div>
          <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
            <div style={{display:"flex",gap:10,marginBottom:20}}>
              {[["winter","Winter (Oct-Mar)"],["summer","Summer (Apr-Sep)"],["subjects","Hifz+School Subjects"]].map(([id,lbl])=>(
                <button key={id} onClick={()=>setHifzSubTab(id)} style={{padding:"9px 22px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
                  background:hifzSubTab===id?"#6d28d9":C.bg,color:hifzSubTab===id?"white":C.muted,transition:"all .2s"}}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* Hifz+School Subject Config */}
            {hifzSubTab==="subjects" && (
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div style={{fontSize:12,color:C.muted}}>
                  Select which school subjects Hifz+School students attend. These appear in the "Hifz+School Composite" view in Class Timetable.
                </div>
                {SUBJECT_OPTS.length===0&&<div style={{color:C.muted,fontSize:12}}>No subjects loaded yet. Add subjects in the Subjects tab first.</div>}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {SUBJECT_OPTS.filter(s=>!/break|prayer|assembly/i.test(s)).map(sub=>{
                    const enrolled = hifzSchoolSubs.includes(sub);
                    return (
                      <button key={sub} onClick={()=>{
                        const next = enrolled?hifzSchoolSubs.filter(s=>s!==sub):[...hifzSchoolSubs,sub];
                        setHifzSchoolSubs(next);
                      }} style={{
                        display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderRadius:10,cursor:"pointer",textAlign:"left",
                        border:"2px solid "+(enrolled?C.primary:C.border),
                        background:enrolled?C.primaryPale:C.white,transition:"all .15s"
                      }}>
                        <span style={{width:18,height:18,borderRadius:4,border:"2px solid "+(enrolled?C.primary:C.border),background:enrolled?C.primary:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {enrolled&&<span style={{color:"white",fontSize:11,fontWeight:700}}>✓</span>}
                        </span>
                        <span style={{fontSize:12,fontWeight:enrolled?700:400,color:enrolled?C.primary:C.muted}}>{sub}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center",marginTop:4}}>
                  <button onClick={()=>saveHifzSchoolSubs(hifzSchoolSubs)} disabled={hsSaving}
                    style={{background:hsSaving?"#9ca3af":"#6d28d9",color:"white",border:"none",borderRadius:8,padding:"9px 22px",fontWeight:800,fontSize:12,cursor:hsSaving?"not-allowed":"pointer"}}>
                    {hsSaving?"Saving...":"Save Subject List"}
                  </button>
                  <span style={{fontSize:11,color:C.muted}}>{hifzSchoolSubs.length} subject{hifzSchoolSubs.length!==1?"s":""} selected</span>
                </div>
              </div>
            )}

            {(hifzSubTab==="winter"||hifzSubTab==="summer") && (()=>{
              const isW = hifzSubTab==="winter";
              const slots = isW?hifzW:hifzS;
              const setSlots = isW?setHifzW:setHifzS;
              const updSlot = (i,field,val) => { const a=[...slots]; a[i]={...a[i],[field]:val}; setSlots(a); };
              return (
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div style={{fontSize:12,color:C.muted}}>{isW?"Winter":"Summer"} daily routine for Hifz students</div>
                  {slots.length===0&&(
                    <div style={{background:"#f5f3ff",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#6d28d9",fontWeight:600,border:"1px solid #c4b5fd"}}>
                      No slots yet. Click "+ Add Slot" to build the schedule.
                    </div>
                  )}
                  {slots.length>0&&(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 1fr 36px",gap:8,padding:"0 10px",marginBottom:2}}>
                      {["Activity","Start","End","Notes",""].map(h=><div key={h} style={{fontSize:11,fontWeight:700,color:C.muted}}>{h}</div>)}
                    </div>
                  )}
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {slots.map((s,i)=>(
                      <div key={i} style={{display:"grid",gridTemplateColumns:"1fr 90px 90px 1fr 36px",gap:8,alignItems:"center",background:C.bg,borderRadius:8,padding:"8px 10px"}}>
                        <input value={s.label||""} onChange={e=>updSlot(i,"label",e.target.value)} placeholder="e.g. Fajr Hifz" style={inp}/>
                        <input type="time" value={s.start||""} onChange={e=>updSlot(i,"start",e.target.value)} style={inp}/>
                        <input type="time" value={s.end||""} onChange={e=>updSlot(i,"end",e.target.value)} style={inp}/>
                        <input value={s.description||""} onChange={e=>updSlot(i,"description",e.target.value)} placeholder="Notes" style={inp}/>
                        <button onClick={()=>setSlots(slots.filter((_,j)=>j!==i))} style={{width:36,height:34,borderRadius:6,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:16,cursor:"pointer"}}>x</button>
                      </div>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:10,marginTop:4,alignItems:"center"}}>
                    <button onClick={()=>setSlots([...slots,{label:"",start:"",end:"",description:""}])}
                      style={{background:"#f5f3ff",color:"#7c3aed",border:"1px solid #c4b5fd",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                      + Add Slot
                    </button>
                    <button onClick={()=>saveHifz(hifzSubTab,slots)} disabled={hifzSaving}
                      style={{background:hifzSaving?"#9ca3af":"#6d28d9",color:"white",border:"none",borderRadius:8,padding:"8px 22px",fontWeight:800,fontSize:12,cursor:hifzSaving?"not-allowed":"pointer"}}>
                      {hifzSaving?"Saving...":"Save Schedule"}
                    </button>
                  </div>
                  {slots.length>0&&(
                    <div style={{background:"#f5f3ff",borderRadius:12,padding:"12px 16px",marginTop:4}}>
                      <div style={{fontSize:11,fontWeight:700,color:"#6d28d9",marginBottom:8}}>Preview</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {slots.filter(s=>s.label).map((s,i)=>(
                          <div key={i} style={{background:"white",border:"1px solid #c4b5fd",borderRadius:8,padding:"6px 12px",minWidth:100}}>
                            <div style={{fontSize:10,color:"#7c3aed",fontWeight:700}}>{s.start&&s.end?s.start+" - "+s.end:""}</div>
                            <div style={{fontSize:12,fontWeight:700,color:"#4c1d95",marginTop:1}}>{s.label}</div>
                            {s.description&&<div style={{fontSize:10,color:C.muted,marginTop:1}}>{s.description}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* AUTO-SCHEDULE TAB */}
      {mainTab==="autoschedule" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Header */}
          <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d9488)",borderRadius:16,padding:"20px 24px",color:"white"}}>
            <div style={{fontWeight:900,fontSize:17,marginBottom:6}}>Auto-Schedule Generator</div>
            <div style={{fontSize:12,opacity:.85,lineHeight:1.6}}>
              Assign a teacher to each subject for each class and set how many times per week it runs. The optimizer respects teacher availability, homeroom rules, double-period requirements, and special rooms — then generates a full weekly timetable.
            </div>
          </div>

          {!hasBell&&(
            <div style={{background:"#fffbeb",borderRadius:10,padding:"12px 16px",fontSize:12,color:"#92400e",fontWeight:600,border:"1px solid #fde68a"}}>
              No Bell Schedule defined. Set up Bell Schedule first so the generator knows your period structure.
            </div>
          )}

          {/* ── ASSIGNMENT TABLE ───────────────────────────────── */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:16,alignItems:"start"}}>
            <div style={{background:C.white,borderRadius:16,padding:20,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:3}}>Class — Subject — Teacher — ×/Week</div>
              <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Set a teacher and weekly frequency for each subject. Leave teacher blank to skip. "×/wk" defaults to every school day.</div>

              {/* Class selector pills */}
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:16}}>
                {ALLCLASSES.map(c=>(
                  <button key={c} onClick={()=>setAsClass(c)} style={{
                    padding:"5px 11px",borderRadius:20,fontSize:10,fontWeight:700,cursor:"pointer",transition:"all .15s",
                    border:"2px solid "+(asClass===c?C.primary:(classSubjectCount(c)>0?C.teal:C.border)),
                    background:asClass===c?C.primary:C.white,
                    color:asClass===c?C.white:(classSubjectCount(c)>0?C.teal:C.muted),
                  }}>{c}{classSubjectCount(c)>0?" ("+classSubjectCount(c)+")":""}</button>
                ))}
              </div>

              {/* Subject rows */}
              <div style={{fontWeight:700,color:C.primary,fontSize:12,marginBottom:8}}>
                {asClass}
                {classTchrs[asClass]&&<span style={{fontSize:10,color:C.teal,fontWeight:600,marginLeft:8}}>Class Teacher: {classTchrs[asClass]}</span>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:420,overflowY:"auto"}}>
                {SUBJECT_OPTS.filter(s=>!/break|lunch|prayer|assembly|transition/i.test(s)).map(sub=>{
                  const assigned=getAssignment(asClass,sub);
                  const pw=getPW(asClass,sub);
                  const isDouble=subjectRules[sub]?.doublePeriod||false;
                  const specialRoom=subjectRules[sub]?.specialRoom||"";
                  return (
                    <div key={sub} style={{display:"flex",alignItems:"center",gap:8,borderRadius:8,padding:"6px 10px",
                      background:assigned?C.primaryPale:C.bg,border:"1px solid "+(assigned?C.primary+"40":C.border),transition:"all .2s"}}>
                      <div style={{flex:1,fontSize:12,fontWeight:assigned?700:400,color:assigned?C.primary:C.muted}}>
                        {sub}
                        {isDouble&&<span style={{fontSize:9,background:"#dbeafe",color:"#1e40af",borderRadius:4,padding:"1px 5px",marginLeft:5}}>2×</span>}
                        {specialRoom&&<span style={{fontSize:9,background:"#fef3c7",color:"#92400e",borderRadius:4,padding:"1px 5px",marginLeft:3}}>{specialRoom}</span>}
                      </div>
                      <select value={assigned} onChange={e=>setAssignment(asClass,sub,e.target.value)}
                        style={{border:"1.5px solid "+(assigned?C.primary:C.border),borderRadius:6,padding:"4px 7px",fontSize:11,
                        background:C.white,color:assigned?C.primary:C.muted,minWidth:140,cursor:"pointer"}}>
                        <option value="">-- skip --</option>
                        {staff.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                      </select>
                      {assigned&&(
                        <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                          <input type="number" min={1} max={schoolDays.length*2} value={pw}
                            onChange={e=>setPW(asClass,sub,e.target.value)}
                            style={{width:38,border:"1.5px solid "+C.border,borderRadius:6,padding:"4px 5px",fontSize:11,textAlign:"center"}}/>
                          <span style={{fontSize:10,color:C.muted,whiteSpace:"nowrap"}}>×/wk</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{marginTop:14,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <button onClick={()=>{saveAssignments();savePeriodsWeek();}} disabled={asAssignSaving||pwSaving}
                  style={{background:(asAssignSaving||pwSaving)?"#9ca3af":C.teal,color:"white",border:"none",borderRadius:8,padding:"8px 20px",fontWeight:800,fontSize:12,cursor:(asAssignSaving||pwSaving)?"not-allowed":"pointer"}}>
                  {(asAssignSaving||pwSaving)?"Saving...":"Save Assignments"}
                </button>
                <span style={{fontSize:11,color:C.muted}}>{ALLCLASSES.filter(c=>classSubjectCount(c)>0).length} class(es) configured</span>
              </div>
            </div>

            {/* RIGHT sidebar */}
            <div style={{display:"flex",flexDirection:"column",gap:12}}>

              {/* Global constraints */}
              <div style={{background:C.white,borderRadius:16,padding:18,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:13,marginBottom:10}}>Global Rules</div>
                <div style={{display:"flex",alignItems:"center",gap:8,fontSize:11,color:C.muted,paddingTop:4}}>
                  <span style={{flex:1}}>Default max periods/teacher/day</span>
                  <input type="number" value={asConstraints.maxPerTeacher} min={1} max={10}
                    onChange={e=>setAsConstraints(p=>({...p,maxPerTeacher:Number(e.target.value)||6}))}
                    style={{width:44,border:"1.5px solid "+C.border,borderRadius:6,padding:"3px 6px",fontSize:12,textAlign:"center"}}/>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:8,lineHeight:1.5}}>
                  Override per-teacher in Advanced Rules below. Homeroom teachers always teach Period 1 for their class.
                </div>
              </div>

              {/* Configured summary */}
              {ALLCLASSES.some(c=>classSubjectCount(c)>0)&&(
                <div style={{background:C.primaryPale,borderRadius:12,padding:"12px 14px"}}>
                  <div style={{fontWeight:700,color:C.primary,fontSize:12,marginBottom:6}}>Configured</div>
                  {ALLCLASSES.filter(c=>classSubjectCount(c)>0).map(c=>(
                    <div key={c} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"2px 0"}}>
                      <span style={{fontWeight:700,color:C.primary}}>{c}</span>
                      <span style={{color:C.muted}}>{classSubjectCount(c)} subjects</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Teacher workload */}
              {(()=>{
                const wl={};
                ALLCLASSES.forEach(cls=>SUBJECT_OPTS.forEach(sub=>{const t=getAssignment(cls,sub);if(t)wl[t]=(wl[t]||0)+1;}));
                const entries=Object.entries(wl).sort((a,b)=>b[1]-a[1]);
                if(!entries.length) return null;
                const maxW=Math.max(...entries.map(e=>e[1]));
                return (
                  <div style={{background:C.white,borderRadius:12,padding:"14px 16px",boxShadow:`0 2px 12px ${C.shadow}`}}>
                    <div style={{fontWeight:700,color:C.primary,fontSize:12,marginBottom:8}}>Teacher Workload</div>
                    {entries.map(([t,n])=>(
                      <div key={t} style={{display:"flex",gap:8,alignItems:"center",padding:"4px 0",fontSize:11}}>
                        <span style={{flex:1,color:n>asConstraints.maxPerTeacher?C.rose:C.primary,fontWeight:700,fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t}</span>
                        <div style={{width:44,height:6,borderRadius:3,background:C.bg,overflow:"hidden",flexShrink:0}}>
                          <div style={{width:(n/maxW)*100+"%",height:"100%",background:n>asConstraints.maxPerTeacher?C.rose:C.teal,borderRadius:3,transition:"width .3s"}}/>
                        </div>
                        <span style={{color:n>asConstraints.maxPerTeacher?C.rose:C.muted,fontWeight:n>asConstraints.maxPerTeacher?700:400,fontSize:10,flexShrink:0}}>{n}</span>
                      </div>
                    ))}
                    {entries.some(([,n])=>n>asConstraints.maxPerTeacher)&&(
                      <div style={{fontSize:10,color:C.rose,marginTop:6,fontWeight:600}}>Red = over default max/day limit</div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── ADVANCED RULES (collapsible) ─────────────────────── */}
          <div style={{background:C.white,borderRadius:16,boxShadow:`0 2px 12px ${C.shadow}`,overflow:"hidden"}}>
            <button onClick={()=>setAsAdvanced(p=>!p)}
              style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 20px",
              background:"none",border:"none",cursor:"pointer",fontWeight:800,color:C.primary,fontSize:13}}>
              <span>Advanced Rules</span>
              <span style={{fontSize:11,color:C.muted,fontWeight:600}}>
                {asAdvanced?"▲ collapse":"▼ Teacher constraints · Subject rules"}
              </span>
            </button>
            {asAdvanced&&(
              <div style={{padding:"0 20px 20px",borderTop:"1px solid "+C.border,display:"flex",flexDirection:"column",gap:20}}>

                {/* Teacher Constraints */}
                <div>
                  <div style={{fontWeight:700,color:C.primary,fontSize:13,marginTop:16,marginBottom:10}}>Teacher Constraints</div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:12}}>
                    Set per-teacher max periods and mark any periods they cannot teach. Unavailable periods are blocked during generation.
                  </div>
                  {staff.length===0&&<div style={{fontSize:12,color:C.muted}}>No active staff loaded.</div>}
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {staff.map(s=>{
                      const tc=teacherCons[s.name]||{};
                      const unavail=tc.unavailable||[];
                      return (
                        <div key={s.id} style={{borderRadius:10,border:"1px solid "+C.border,padding:"12px 14px",background:C.bg}}>
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                            <div style={{fontWeight:700,color:C.primary,fontSize:12,flex:1}}>{s.name}</div>
                            <span style={{fontSize:10,color:C.muted}}>{s.subject||s.role}</span>
                            <div style={{display:"flex",alignItems:"center",gap:6,fontSize:11,color:C.muted}}>
                              <span>Max/day:</span>
                              <input type="number" min={1} max={bellPeriods.length} value={tc.maxPerDay||asConstraints.maxPerTeacher}
                                onChange={e=>setTeacherCons(p=>({...p,[s.name]:{...(p[s.name]||{}),maxPerDay:Number(e.target.value)||6}}))}
                                style={{width:40,border:"1.5px solid "+C.border,borderRadius:6,padding:"2px 5px",fontSize:11,textAlign:"center"}}/>
                            </div>
                          </div>
                          {hasBell&&teachingPeriodNos.length>0&&(
                            <div>
                              <div style={{fontSize:10,color:C.muted,marginBottom:5}}>Unavailable periods (check to block):</div>
                              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                                {bellPeriods.map((bell,idx)=>{
                                  const pNo=idx+1;
                                  const isFixed=/break|lunch|prayer|assembly|transition/i.test(bell.type||"period");
                                  if(isFixed) return null;
                                  const blocked=unavail.includes(pNo);
                                  return (
                                    <label key={pNo} style={{display:"flex",alignItems:"center",gap:4,cursor:"pointer",
                                      background:blocked?"#fee2e2":C.white,borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,
                                      border:"1px solid "+(blocked?C.rose+"60":C.border),color:blocked?C.rose:C.muted,transition:"all .15s"}}>
                                      <input type="checkbox" checked={blocked} style={{accentColor:C.rose}}
                                        onChange={e=>{
                                          const next=e.target.checked?[...unavail,pNo]:unavail.filter(p=>p!==pNo);
                                          setTeacherCons(p=>({...p,[s.name]:{...(p[s.name]||{}),unavailable:next}}));
                                        }}/>
                                      P{pNo}{bell.start?" "+bell.start:""}
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={saveTeacherCons} disabled={tcSaving}
                    style={{marginTop:12,background:tcSaving?"#9ca3af":C.teal,color:"white",border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:11,cursor:tcSaving?"not-allowed":"pointer"}}>
                    {tcSaving?"Saving...":"Save Teacher Constraints"}
                  </button>
                </div>

                {/* Subject Rules */}
                <div style={{borderTop:"1px solid "+C.border,paddingTop:20}}>
                  <div style={{fontWeight:700,color:C.primary,fontSize:13,marginBottom:10}}>Subject Rules</div>
                  <div style={{fontSize:11,color:C.muted,marginBottom:12}}>
                    Mark subjects that need consecutive double periods or a dedicated room (e.g. Computer Lab). Double periods are placed in adjacent slots.
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {SUBJECT_OPTS.filter(s=>!/break|lunch|prayer|assembly|transition/i.test(s)).map(sub=>{
                      const sr=subjectRules[sub]||{};
                      return (
                        <div key={sub} style={{borderRadius:8,border:"1px solid "+C.border,padding:"10px 12px",background:C.bg}}>
                          <div style={{fontWeight:700,fontSize:12,color:C.primary,marginBottom:7}}>{sub}</div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:7}}>
                            <button onClick={()=>setSubjectRules(p=>({...p,[sub]:{...(p[sub]||{}),doublePeriod:!sr.doublePeriod}}))}
                              style={{width:32,height:18,borderRadius:9,border:"none",cursor:"pointer",position:"relative",flexShrink:0,
                              background:sr.doublePeriod?C.primary:"#d1d5db",transition:"all .2s"}}>
                              <span style={{position:"absolute",top:1,left:sr.doublePeriod?14:1,width:16,height:16,borderRadius:8,background:"white",transition:"all .2s",display:"block"}}/>
                            </button>
                            <span style={{fontSize:10,color:C.muted}}>Double period</span>
                          </div>
                          <input placeholder="Special room (e.g. Computer Lab)" value={sr.specialRoom||""}
                            onChange={e=>setSubjectRules(p=>({...p,[sub]:{...(p[sub]||{}),specialRoom:e.target.value}}))}
                            style={{width:"100%",border:"1.5px solid "+C.border,borderRadius:6,padding:"4px 8px",fontSize:10,color:C.primary,background:C.white,boxSizing:"border-box"}}/>
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={saveSubjectRules} disabled={srSaving}
                    style={{marginTop:12,background:srSaving?"#9ca3af":C.teal,color:"white",border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:11,cursor:srSaving?"not-allowed":"pointer"}}>
                    {srSaving?"Saving...":"Save Subject Rules"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── GENERATE / APPLY / DISCARD ───────────────────────── */}
          <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
            <button onClick={runGenerate} disabled={asGenerating}
              style={{background:asGenerating?"#6b7280":"linear-gradient(135deg,#1a6b3c,#0d9488)",color:"white",border:"none",borderRadius:12,
              padding:"13px 36px",fontWeight:900,fontSize:15,cursor:asGenerating?"not-allowed":"pointer",boxShadow:"0 4px 15px #1a6b3c40"}}>
              {asGenerating?"Generating...":"Generate Schedule"}
            </button>
            {asDraft&&(
              <button onClick={applyDraft} disabled={asApplying}
                style={{background:asApplying?"#6b7280":C.teal,color:"white",border:"none",borderRadius:12,padding:"13px 32px",fontWeight:900,fontSize:15,cursor:asApplying?"not-allowed":"pointer"}}>
                {asApplying?"Applying...":"Apply to Timetable"}
              </button>
            )}
            {asDraft&&(
              <button onClick={()=>{setAsDraft(null);setAsConflicts([]);}}
                style={{background:C.rosePale,color:C.rose,border:"1px solid "+C.rose+"40",borderRadius:12,padding:"13px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                Discard
              </button>
            )}
          </div>

          {/* Conflicts */}
          {asConflicts.length>0&&(
            <div style={{background:"#fffbeb",borderRadius:12,padding:"14px 18px",border:"1px solid #fde68a"}}>
              <div style={{fontWeight:700,color:"#92400e",fontSize:13,marginBottom:8}}>Notices ({asConflicts.length})</div>
              {asConflicts.map((c,i)=>(
                <div key={i} style={{fontSize:12,color:"#92400e",padding:"4px 0",borderBottom:i<asConflicts.length-1?"1px solid #fde68a":"none"}}>{c.msg}</div>
              ))}
              <div style={{fontSize:11,color:"#a16207",marginTop:6}}>Adjust assignments or constraints above, then regenerate.</div>
            </div>
          )}

          {/* Draft Preview */}
          {asDraft&&(
            <div style={{background:C.white,borderRadius:16,padding:20,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:10}}>Draft Preview</div>

              {/* Day switcher */}
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
                {[...new Set(asDraft.map(r=>r.day))].map(day=>(
                  <button key={day} onClick={()=>setAsPreviewDay(day)}
                    style={{padding:"5px 14px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",border:"2px solid "+(asPreviewDay===day||(!asPreviewDay&&day===schoolDays[0])?C.primary:C.border),
                    background:(asPreviewDay===day||(!asPreviewDay&&day===schoolDays[0]))?C.primary:C.white,
                    color:(asPreviewDay===day||(!asPreviewDay&&day===schoolDays[0]))?C.white:C.muted}}>
                    {day}
                  </button>
                ))}
              </div>

              {(()=>{
                const previewDay=asPreviewDay||schoolDays[0]||"Monday";
                const dayDraft=asDraft.filter(r=>r.day===previewDay);
                const classes=[...new Set(dayDraft.map(r=>r.class))];
                return classes.map(cls=>(
                  <div key={cls} style={{marginBottom:18}}>
                    <div style={{fontWeight:700,color:C.primary,fontSize:13,marginBottom:8,paddingBottom:4,borderBottom:"2px solid "+C.primaryPale}}>{cls}</div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {dayDraft.filter(r=>r.class===cls).sort((a,b)=>a.period_no-b.period_no).map((r,i)=>{
                        const isFixed=/break|lunch|prayer|assembly|transition/i.test(r.subject);
                        return (
                          <div key={i} style={{background:isFixed?"#f1f5f9":subjectColor(r.subject)+"15",
                            border:"1px solid "+(isFixed?"#cbd5e1":subjectColor(r.subject)+"40"),borderRadius:10,padding:"8px 12px",minWidth:88}}>
                            <div style={{fontSize:10,color:C.muted,fontWeight:600}}>P{r.period_no}{r.start_time?" "+r.start_time:""}</div>
                            <div style={{fontWeight:800,color:isFixed?C.muted:subjectColor(r.subject),fontSize:11,marginTop:2}}>{r.subject}</div>
                            {r.teacher_name?<div style={{fontSize:10,color:C.muted,marginTop:1}}>{r.teacher_name}</div>:
                              !isFixed&&<div style={{fontSize:10,color:C.rose,marginTop:1,fontWeight:600}}>No teacher</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* ── CLASSES TAB ── */}
      {mainTab==="classes"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16,padding:"4px 0"}}>
          <div style={{fontWeight:800,color:G,fontSize:15}}>📚 Classes & Class Teachers</div>
          <div style={{background:"#fffbeb",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e",border:"1px solid #fde68a"}}>
            ⚠️ The class list controls all dropdowns throughout the portal. Add, remove, or reorder classes here and assign a class teacher to each.
          </div>

          {/* Add new class */}
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <input value={addingClass} onChange={e=>setAddingClass(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"){
                const t=addingClass.trim();
                if(!t) return;
                const cur=(settings.classes||S.classes||"").split(",").map(c=>c.trim()).filter(Boolean);
                if(!cur.includes(t)) updClasses([...cur,t].join(","));
                setAddingClass("");
              }}}
              placeholder="Add new class, e.g. Class 10"
              style={{flex:1,border:"1.5px solid #d1d5db",borderRadius:8,padding:"9px 12px",fontSize:13}}/>
            <button onClick={()=>{
              const t=addingClass.trim();
              if(!t) return;
              const cur=(settings.classes||S.classes||"").split(",").map(c=>c.trim()).filter(Boolean);
              if(!cur.includes(t)) updClasses([...cur,t].join(","));
              setAddingClass("");
            }} style={{background:G,color:"white",border:"none",borderRadius:8,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Add</button>
          </div>

          {/* Class table */}
          {(()=>{
            const classList=(settings.classes||S.classes||"").split(",").map(c=>c.trim()).filter(Boolean);
            const countFor=cls=>students.filter(s=>(s.class||s["class"])===cls).length;
            const saveTeacher=async(cls,sid)=>{
              setClassTeachers(p=>({...p,[cls]:sid}));
              if(sid) await supabase.from("class_teachers").upsert({class_name:cls,staff_id:sid},{onConflict:"class_name"});
              else await supabase.from("class_teachers").delete().eq("class_name",cls);
            };
            return classList.length===0?(
              <div style={{textAlign:"center",padding:32,color:"#9ca3af",background:"white",borderRadius:12,border:"1px dashed #d1d5db",fontSize:13}}>No classes defined yet. Add one above.</div>
            ):(
              <div style={{background:"white",borderRadius:12,overflow:"hidden",border:"1px solid #e5e7eb"}}>
                <div style={{background:`linear-gradient(135deg,${G},#0a4a24)`,padding:"10px 16px",display:"grid",gridTemplateColumns:"1fr 90px 1fr 44px",gap:12,alignItems:"center"}}>
                  {["Class","Students","Class Teacher",""].map((h,i)=>(
                    <div key={i} style={{color:"white",fontWeight:700,fontSize:11,textTransform:"uppercase"}}>{h}</div>
                  ))}
                </div>
                {classList.map((cls,i)=>(
                  <div key={cls} style={{display:"grid",gridTemplateColumns:"1fr 90px 1fr 44px",gap:12,alignItems:"center",padding:"10px 16px",background:i%2===0?"white":"#fafafa",borderTop:"1px solid #f3f4f6"}}>
                    <div style={{fontWeight:700,fontSize:14,color:"#111827"}}>{cls}</div>
                    <span style={{background:countFor(cls)>0?"#dcfce7":"#f3f4f6",color:countFor(cls)>0?G:"#9ca3af",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:700}}>
                      👥 {countFor(cls)}
                    </span>
                    <select value={classTeachers[cls]||""} onChange={e=>saveTeacher(cls,e.target.value)}
                      style={{border:"1px solid #d1d5db",borderRadius:8,padding:"7px 10px",fontSize:12,background:"white",width:"100%"}}>
                      <option value="">— No class teacher —</option>
                      {staffList.map(t=><option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
                    </select>
                    <button onClick={()=>{
                      const cur=(settings.classes||S.classes||"").split(",").map(c=>c.trim()).filter(Boolean);
                      updClasses(cur.filter(c=>c!==cls).join(","));
                    }} style={{width:36,height:36,borderRadius:8,border:"1px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontSize:18,cursor:"pointer"}}>×</button>
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{background:"#f9fafb",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#6b7280"}}>
            💡 Tip: To reorder classes, use the Raw Order field in Settings → Classes.
          </div>
        </div>
      )}

    </div>
  );
}

function PortalLoginPage({ onLogin, settings: S, onGoPublic }) {
  const [email, setEmail] = React.useState("");
  const [pass,  setPass]  = React.useState("");
  const [show,  setShow]  = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err,   setErr]   = React.useState("");
  const [quoteIdx, setQuoteIdx] = React.useState(0);

  const g1 = (S?.header_gradient||"#1a6b3c,#0a4a24").split(",")[0];
  const g2 = (S?.header_gradient||"#1a6b3c,#0a4a24").split(",")[1]||"#0a4a24";
  const grad = `linear-gradient(135deg,${g1},${g2})`;

  const hr = new Date().getHours();
  const greeting   = hr < 12 ? "Good Morning"   : hr < 17 ? "Good Afternoon" : "Good Evening";
  const greetEmoji = hr < 12 ? "🌅"             : hr < 17 ? "☀️"             : "🌙";

  const hadiths = [
    { ar:"طَلَبُ الْعِلْمِ فَرِيضَةٌ عَلَى كُلِّ مُسْلِمٍ",                        en:"Seeking knowledge is an obligation upon every Muslim"         },
    { ar:"خَيْرُكُمْ مَنْ تَعَلَّمَ الْقُرْآنَ وَعَلَّمَهُ",                        en:"The best of you are those who learn the Quran and teach it"   },
    { ar:"الْعُلَمَاءُ وَرَثَةُ الْأَنْبِيَاءِ",                                     en:"The scholars are the inheritors of the Prophets"              },
    { ar:"مَنْ خَرَجَ فِي طَلَبِ الْعِلْمِ فَهُوَ فِي سَبِيلِ اللَّهِ",            en:"Whoever goes out seeking knowledge is on the path of Allah"   },
  ];

  const geoSvg = encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><g fill='none' stroke='rgba(255,255,255,0.07)' stroke-width='0.8'><path d='M40 4 L76 40 L40 76 L4 40 Z'/><path d='M40 20 L60 40 L40 60 L20 40 Z'/><line x1='4' y1='4' x2='20' y2='20'/><line x1='76' y1='4' x2='60' y2='20'/><line x1='76' y1='76' x2='60' y2='60'/><line x1='4' y1='76' x2='20' y2='60'/></g></svg>`);

  React.useEffect(() => {
    const t = setInterval(() => setQuoteIdx(i => (i + 1) % 4), 5000);
    return () => clearInterval(t);
  }, []);

  const submit = async e => {
    e && e.preventDefault();
    if(!email||!pass) return setErr("Please enter email and password.");
    setLoading(true); setErr("");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: pass });
    if(error) { setErr(error.message); setLoading(false); }
    else onLogin(data.session);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
      <style>{`
        @media(min-width:640px){.qka-login-wrap{flex-direction:row!important}.qka-login-brand{display:flex!important}.qka-login-form{width:440px!important;padding:48px 44px!important}}
        @media(max-width:639px){
          .qka-login-brand{padding:28px 20px 24px!important;min-height:auto!important}
          .qka-login-form{width:100%!important;padding:28px 20px!important;box-shadow:none!important}
          .qka-hero-logo-img{width:88px!important;height:88px!important}
          .qka-stats{padding:8px 0!important}
          .qka-feat-row{padding:8px 12px!important}
          .qka-feat-icon{font-size:15px!important}
          .qka-feat-label{font-size:12px!important}
          .qka-feats-title{font-size:9px!important}
          .qka-hadith-ar{font-size:14.5px!important}
          .qka-hadith-en{font-size:10.5px!important}
          .qka-login-brand{animation:none!important}
          .qka-login-form{animation:qka-fade-up 0.5s cubic-bezier(0.16,1,0.3,1) both!important}
        }
        @keyframes qka-drift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(18px,-14px) scale(1.06)}}
        @keyframes qka-drift2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-14px,18px) scale(1.08)}}
        @keyframes qka-shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(400%)}}
        @keyframes qka-slide-in{from{transform:translateX(-30px);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes qka-fade-up{from{transform:translateY(18px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes qka-quote-fade{0%{opacity:0;transform:translateY(5px)}15%{opacity:1;transform:translateY(0)}80%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(-3px)}}
        .qka-orb1{animation:qka-drift1 9s ease-in-out infinite}
        .qka-orb2{animation:qka-drift2 12s ease-in-out infinite}
        .qka-shimmer-bar{animation:qka-shimmer 2.8s ease-in-out infinite}
        .qka-login-brand{animation:qka-slide-in 0.65s cubic-bezier(0.16,1,0.3,1) both}
        .qka-login-form{animation:qka-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 0.18s both}
        .qka-feat{transition:background .25s,transform .25s,border-color .25s}
        .qka-feat:hover{background:rgba(255,255,255,0.16)!important;transform:translateX(5px);border-color:rgba(240,165,0,0.65)!important}
        .qka-input{transition:border-color .2s,box-shadow .2s}
        .qka-input:focus{outline:none;border-color:${g1}!important;box-shadow:0 0 0 3px ${g1}22!important}
        .qka-submit{transition:opacity .2s,box-shadow .2s,transform .15s}
        .qka-submit:not(:disabled):hover{opacity:.92;box-shadow:0 6px 22px ${g1}55;transform:translateY(-1px)}
        .qka-submit:not(:disabled):active{transform:translateY(0)}
        .qka-back{transition:background .2s,color .2s}
        .qka-back:hover{background:rgba(255,255,255,0.2)!important;color:white!important}
        .qka-quote{animation:qka-quote-fade 5s ease-in-out forwards}
        .qka-dot{transition:width .3s,background .3s;cursor:pointer}
      `}</style>
      {/* Gold shimmer line */}
      <div style={{position:"fixed",top:0,left:0,right:0,height:"2px",overflow:"hidden",zIndex:9999,pointerEvents:"none"}}>
        <div className="qka-shimmer-bar" style={{height:"100%",width:"25%",background:"linear-gradient(90deg,transparent,#f0a500,#ffe066,#f0a500,transparent)"}}/>
      </div>

      <div className="qka-login-wrap" style={{flex:1,display:"flex",flexDirection:"column"}}>

        {/* ── Branding panel ─────────────────────────────────── */}
        <div className="qka-login-brand" style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"space-between",padding:"44px 36px 32px",position:"relative",overflow:"hidden",minHeight:320}}>
          {/* Campus photo */}
          <img src={CAMPUS_AERIAL} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:"center 55%",zIndex:0}}/>
          {/* Vignette — lighter so photo breathes */}
          <div style={{position:"absolute",inset:0,background:`linear-gradient(160deg,${g1}c4 0%,${g2}a8 45%,rgba(0,0,0,0.60) 100%)`,zIndex:1}}/>
          {/* Islamic geometric pattern tile */}
          <div style={{position:"absolute",inset:0,backgroundImage:`url("data:image/svg+xml,${geoSvg}")`,backgroundSize:"80px 80px",zIndex:2}}/>
          {/* Gold-tinted drifting orb */}
          <div className="qka-orb2" style={{position:"absolute",bottom:-80,right:-50,width:360,height:360,borderRadius:"50%",background:"rgba(240,165,0,0.05)",zIndex:3}}/>

          {/* Logo + school name */}
          <div style={{position:"relative",zIndex:4,textAlign:"center"}}>
            <img src={SCHOOL_LOGO} className="qka-hero-logo-img" style={{width:118,height:118,objectFit:"contain",display:"block",margin:"0 auto 10px",filter:"drop-shadow(0 4px 18px rgba(0,0,0,0.55))"}}/>
            <div style={{fontSize:20,fontWeight:900,color:"white",marginBottom:3,textShadow:"0 2px 10px rgba(0,0,0,0.5)",letterSpacing:.3}}>{S?.school_name_en||"QK ACADEMY"}</div>
            <div style={{fontFamily:"'Noto Nastaliq Urdu',serif",fontSize:16,color:"rgba(255,255,255,0.88)",marginBottom:10,lineHeight:1.7,textShadow:"0 1px 6px rgba(0,0,0,0.4)"}}>{S?.school_name_ur||"قرآن کریم اکیڈمی"}</div>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(240,165,0,0.18)",border:"1px solid rgba(240,165,0,0.45)",borderRadius:20,padding:"4px 14px"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:"#f0a500"}}/>
              <span style={{fontSize:10,color:"#f0a500",fontWeight:800,letterSpacing:1.4,textTransform:"uppercase"}}>School Portal</span>
            </div>
          </div>

          {/* Stats strip */}
          <div className="qka-stats" style={{position:"relative",zIndex:4,display:"flex",justifyContent:"center",alignItems:"center",background:"rgba(0,0,0,0.22)",borderRadius:12,backdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.08)",padding:"10px 0"}}>
            {[{num:"300+",label:"Students",color:"#f0a500"},{num:"Serving the Unserved",label:"Our Mission",color:"#86efac",single:true},{num:"98%",label:"Hifz Rate",color:"#f0a500"}].map(({num,label,color,single},i)=>(
              <React.Fragment key={label}>
                {i>0&&<div style={{width:"1px",height:36,background:"rgba(240,165,0,0.25)"}}/>}
                <div style={{textAlign:"center",flex:single?1.8:1,padding:"0 6px"}}>
                  {single ? (
                    <>
                      <div style={{fontSize:10.5,color:"rgba(255,255,255,0.5)",fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",marginBottom:4}}>{label}</div>
                      <div style={{fontSize:15,fontWeight:900,color,lineHeight:1.2,letterSpacing:0.2}}>{num}</div>
                    </>
                  ) : (
                    <>
                      <div style={{fontSize:19,fontWeight:900,color,lineHeight:1.15,whiteSpace:"nowrap"}}>{num}</div>
                      <div style={{fontSize:9,color:"rgba(255,255,255,0.45)",fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",marginTop:3}}>{label}</div>
                    </>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Feature highlights */}
          <div className="qka-feats" style={{position:"relative",zIndex:4}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
              <div style={{flex:1,height:"1px",background:"rgba(240,165,0,0.25)"}}/>
              <span className="qka-feats-title" style={{fontSize:10.5,fontWeight:800,color:"#f0a500",letterSpacing:2.2,textTransform:"uppercase"}}>Portal Highlights</span>
              <div style={{flex:1,height:"1px",background:"rgba(240,165,0,0.25)"}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:0,background:"rgba(0,0,0,0.22)",borderRadius:12,border:"1px solid rgba(255,255,255,0.07)",overflow:"hidden"}}>
              {[
                {icon:"📖",label:"Track Your Child's Hifz & Academic Progress", color:"#86efac"},
                {icon:"🤖",label:"AI Insights Into Every Student's Growth",      color:"#93c5fd"},
                {icon:"🕌",label:"Wifaq-Certified Hifz Tracking",                color:"#c4b5fd"},
                {icon:"📊",label:"Real-Time Reports, Always Up to Date",         color:"#fde68a"},
                {icon:"🔔",label:"Fees, Attendance & Updates in One Place",      color:"#f0a500"},
              ].map(({icon,label,color},i,arr)=>(
                <div key={label} className="qka-feat-row" style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderBottom:i<arr.length-1?"1px solid rgba(255,255,255,0.06)":"none"}}>
                  <span className="qka-feat-icon" style={{fontSize:19,flexShrink:0,lineHeight:1}}>{icon}</span>
                  <div style={{width:5,height:5,borderRadius:"50%",background:color,flexShrink:0}}/>
                  <span className="qka-feat-label" style={{fontSize:14,fontWeight:700,color,letterSpacing:.2,lineHeight:1.35}}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Rotating hadith carousel */}
          <div style={{position:"relative",zIndex:4,textAlign:"center"}}>
            <div style={{minHeight:68,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",marginBottom:8}}>
              <div key={quoteIdx} className="qka-quote">
                <div className="qka-hadith-ar" style={{fontFamily:"'Noto Nastaliq Urdu',serif",fontSize:17,color:"rgba(255,255,255,0.65)",lineHeight:1.8,marginBottom:4}}>{hadiths[quoteIdx].ar}</div>
                <div className="qka-hadith-en" style={{fontSize:12,color:"rgba(255,255,255,0.42)",fontStyle:"italic",letterSpacing:.2}}>{hadiths[quoteIdx].en}</div>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"center",gap:5,marginBottom:12}}>
              {hadiths.map((_,i)=>(
                <div key={i} className="qka-dot" onClick={()=>setQuoteIdx(i)}
                  style={{height:5,borderRadius:3,background:i===quoteIdx?"#f0a500":"rgba(255,255,255,0.22)",width:i===quoteIdx?18:5}}/>
              ))}
            </div>
            <button className="qka-back" onClick={onGoPublic} style={{background:"rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.72)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,padding:"6px 18px",fontSize:11.5,cursor:"pointer",backdropFilter:"blur(4px)"}}>
              ← Back to Website
            </button>
          </div>
        </div>

        {/* ── Login form panel ───────────────────────────────── */}
        <div className="qka-login-form" style={{width:440,display:"flex",flexDirection:"column",justifyContent:"center",background:"#fdfaf5",padding:"48px 44px",boxShadow:"-10px 0 50px rgba(0,0,0,0.14)",position:"relative",overflow:"hidden"}}>
          {/* Watermark logo */}
          <img src={SCHOOL_LOGO} alt="" aria-hidden="true" style={{position:"absolute",bottom:-20,left:"50%",transform:"translateX(-50%)",width:220,height:220,objectFit:"contain",opacity:.05,pointerEvents:"none",userSelect:"none"}}/>

          <div style={{position:"relative",zIndex:1}}>
            <div style={{marginBottom:32}}>
              <div style={{fontSize:27,fontWeight:900,color:"#111827",marginBottom:6,lineHeight:1.2}}>{greeting} {greetEmoji}</div>
              <div style={{fontSize:13.5,color:"#6b7280",lineHeight:1.5}}>Sign in to access the school portal</div>
            </div>

            <form onSubmit={submit} style={{display:"flex",flexDirection:"column",gap:18}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11.5,fontWeight:700,color:"#374151",letterSpacing:.3}}>Email Address</label>
                <input className="qka-input" type="email" value={email} onChange={e=>setEmail(e.target.value)}
                  placeholder="staff@qkacademy.org" autoComplete="email"
                  style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:"12px 14px",fontSize:14,background:"white",boxSizing:"border-box",width:"100%"}}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:11.5,fontWeight:700,color:"#374151",letterSpacing:.3}}>Password</label>
                <div style={{position:"relative"}}>
                  <input className="qka-input" type={show?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)}
                    placeholder="••••••••" autoComplete="current-password"
                    style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:"12px 44px 12px 14px",fontSize:14,background:"white",boxSizing:"border-box",width:"100%"}}/>
                  <button type="button" onClick={()=>setShow(p=>!p)}
                    style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,opacity:.45}}>{show?"🙈":"👁"}</button>
                </div>
              </div>
              {err && <div style={{background:"#fff0f3",border:"1px solid #fda4af",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#e11d48",fontWeight:600}}>⚠️ {err}</div>}
              <button type="submit" disabled={loading} className="qka-submit"
                style={{background:loading?"#9ca3af":`linear-gradient(135deg,${g1},${g2})`,color:"white",border:"none",borderRadius:10,padding:"13px",fontWeight:800,fontSize:15,cursor:loading?"not-allowed":"pointer",marginTop:2,letterSpacing:.2}}>
                {loading ? "Signing in…" : "Sign In →"}
              </button>
            </form>

            <div style={{marginTop:28,paddingTop:22,borderTop:"1px solid #ede9de",textAlign:"center"}}>
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:20,padding:"5px 14px"}}>
                <span style={{fontSize:12}}>🔒</span>
                <span style={{fontSize:11.5,color:"#166534",fontWeight:700}}>Authorised staff only</span>
              </div>
              <div style={{fontSize:11,color:"#c4b99a",marginTop:8}}>Contact your administrator to reset your password</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

const SafePublicSite = React.memo(({ onLogin, settings, onPortalClick }) => {
  try {
    return <PublicSiteComponent onLogin={onLogin} settings={settings} onPortalClick={onPortalClick} />;
  } catch (err) {
    console.error("PublicSite render failed", err);
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:"#f0f7f2",fontFamily:"Plus Jakarta Sans, sans-serif"}}>
        <div style={{maxWidth:720,background:"white",border:"1px solid #d1e8d8",borderRadius:16,padding:24,boxShadow:"0 10px 30px rgba(26,107,60,0.08)"}}>
          <div style={{fontSize:24,fontWeight:900,color:"#1a6b3c",marginBottom:10}}>Public site failed to render</div>
          <div style={{fontSize:14,color:"#374151",lineHeight:1.7,marginBottom:14}}>A runtime error occurred inside the public site component. Open browser console to see the exact component error.</div>
          <button onClick={onPortalClick} style={{background:"#1a6b3c",color:"white",border:"none",borderRadius:10,padding:"12px 18px",fontWeight:800,cursor:"pointer"}}>Open Portal</button>
        </div>
      </div>
    );
  }
});

// ── ANNOUNCEMENTS MANAGEMENT ──────────────────────────────────────
function PopupImageUploader({ onUpload, uploading }) {
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef();

  const handleFile = file => {
    if (!file || !file.type.startsWith("image/")) return;
    onUpload(file);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
      onClick={() => !uploading && inputRef.current.click()}
      style={{
        border:          `2px dashed ${dragging ? "#1a6b3c" : "#86efac"}`,
        borderRadius:    10,
        background:      dragging ? "#f0fdf4" : "#f9fefb",
        padding:         "32px 20px",
        display:         "flex",
        flexDirection:   "column",
        alignItems:      "center",
        gap:             8,
        cursor:          uploading ? "not-allowed" : "pointer",
        transition:      "all .2s",
        textAlign:       "center",
      }}
    >
      {uploading ? (
        <>
          <div style={{fontSize:28}}>⏳</div>
          <div style={{fontSize:13,fontWeight:700,color:"#1a6b3c"}}>Uploading image…</div>
        </>
      ) : (
        <>
          <div style={{fontSize:32}}>🖼️</div>
          <div style={{fontSize:13,fontWeight:700,color:"#1a6b3c"}}>{dragging ? "Drop it!" : "Drag & drop your photo here"}</div>
          <div style={{fontSize:11,color:"#6b7280"}}>or click to browse — JPG, PNG, WEBP</div>
        </>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
    </div>
  );
}

function AnnouncementsManagement() {
  const C = React.useContext(SettingsContext);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [error, setError] = React.useState("");
  const POPUP_IMAGES = [
    { label:"Children in Classroom", url:"https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=900&q=80" },
    { label:"Students Reading Quran", url:"https://images.unsplash.com/photo-1609599006353-e629aaabfeae?w=900&q=80" },
    { label:"School Building",        url:"https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=900&q=80" },
    { label:"Students in Uniform",    url:"https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=900&q=80" },
    { label:"Graduation / Ceremony",  url:"https://images.unsplash.com/photo-1627556704302-624286467c65?w=900&q=80" },
    { label:"Custom URL",             url:"custom" },
  ];
  const blank = { title:"", body:"", category:"News", is_published:true, target_class:"All",
    show_as_popup:false, popup_image_url:"", popup_image_preset:"", popup_button_text:"Learn More", popup_button_url:"", popup_expires_at:"",
    popup_overlay_opacity:60, popup_width:"standard", popup_card_style:"solid" };
  const [form, setForm] = React.useState(blank);
  const [editing, setEditing] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState("");
  const [file, setFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [waAnn, setWaAnn] = React.useState(null); // {title, body} for WA broadcast
  const [filterCat, setFilterCat] = React.useState("All");
  const [filterStatus, setFilterStatus] = React.useState("All"); // All/Published/Draft
  const [search, setSearch] = React.useState("");

  const CATS = ["News","Event","Achievement","Notice","Holiday"];
  const CAT_COLOR = { News:"#0891b2", Event:"#7c3aed", Achievement:"#16a34a", Notice:"#d97706", Holiday:"#e11d48" };

  React.useEffect(()=>{ load(); },[]);

  function showToast(msg) { setToast(msg); setTimeout(()=>setToast(""),3000); }

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.from("announcements").select("*").order("created_at",{ascending:false});
    if(error) console.error("Load error:",error);
    setItems(data||[]);
    setLoading(false);
  }

  async function uploadFile(announcementId) {
    if(!file) return null;
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${announcementId}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("announcements").upload(path, file, { upsert:true });
    setUploading(false);
    if(error){ setError("File upload failed: "+error.message); return null; }
    const { data } = supabase.storage.from("announcements").getPublicUrl(path);
    return data.publicUrl;
  }

  async function save() {
    setError("");
    if(!form.title.trim()||!form.body.trim()) { setError("Title and content are required."); return; }
    setSaving(true);
    let err, savedId = editing;

    if(editing) {
      let file_url = undefined;
      if(file) {
        file_url = await uploadFile(editing);
        if(file_url === null) { setSaving(false); return; }
      }
      const updateData = { title:form.title, body:form.body, category:form.category, is_published:form.is_published, target_class:form.target_class||"All",
        show_as_popup:!!form.show_as_popup, popup_image_url:form.popup_image_url||"",
        popup_button_text:form.popup_button_text||"Learn More", popup_button_url:form.popup_button_url||"", popup_expires_at:form.popup_expires_at||null,
        popup_overlay_opacity:form.popup_overlay_opacity??60, popup_width:form.popup_width||"standard", popup_card_style:form.popup_card_style||"solid",
        updated_at:new Date().toISOString() };
      if(file_url) updateData.file_url = file_url;
      if(file) updateData.file_name = file.name;
      const res = await supabase.from("announcements").update(updateData).eq("id",editing);
      err = res.error;
    } else {
      const res = await supabase.from("announcements").insert({ title:form.title, body:form.body, category:form.category, is_published:form.is_published, target_class:form.target_class||"All",
        show_as_popup:!!form.show_as_popup, popup_image_url:form.popup_image_preset==="custom"?form.popup_image_url:(form.popup_image_preset||""),
        popup_button_text:form.popup_button_text||"Learn More", popup_button_url:form.popup_button_url||"", popup_expires_at:form.popup_expires_at||null,
        popup_overlay_opacity:form.popup_overlay_opacity??60, popup_width:form.popup_width||"standard", popup_card_style:form.popup_card_style||"solid" }).select().single();
      err = res.error;
      savedId = res.data?.id;
      if(!err && file && savedId) {
        const file_url = await uploadFile(savedId);
        if(file_url === null) { setSaving(false); return; }
        await supabase.from("announcements").update({ file_url, file_name:file.name }).eq("id",savedId);
      }
      // Create ONE linked notification (delete any stale one first to avoid duplicates)
      if(!err && savedId && form.is_published) {
        await supabase.from("notifications").delete().eq("source_id", savedId).eq("source_type","announcements");
        const safeBody = (form.body||"").replace(/\s+/g," ").trim().slice(0,120);
        await supabase.from("notifications").insert({
          title: form.title,
          body: safeBody,
          type: "announcement",
          target_role: "all",
          target_class: (!form.target_class||form.target_class==="All") ? null : form.target_class,
          source_id: savedId,
          source_type: "announcements",
          created_by: "Admin"
        });
      }
    }
    setSaving(false);
    if(err) { setError("Error: "+err.message+" — Check Supabase RLS policies."); return; }
    showToast(editing?"Updated!":"Announcement saved!");
    setShowForm(false); setEditing(null); setForm(blank); setFile(null);
    load();
  }

  async function removeFile(item) {
    await supabase.from("announcements").update({ file_url:null, file_name:null }).eq("id",item.id);
    showToast("Attachment removed!");
    load();
  }

  async function del(id) {
    if(!window.confirm("Delete this announcement?")) return;
    const { error } = await supabase.from("announcements").delete().eq("id",id);
    if(error) return alert("Delete error: "+error.message);
    await supabase.from("notifications").delete().eq("source_id",id).eq("source_type","announcements");
    showToast("Deleted!"); load();
  }

  async function toggle(item) {
    const publishing = !item.is_published;
    const { error } = await supabase.from("announcements").update({ is_published: publishing }).eq("id",item.id);
    if(error) return alert("Error: "+error.message);
    // Sync notification
    await supabase.from("notifications").delete().eq("source_id", item.id).eq("source_type","announcements");
    if(publishing) {
      const safeBody = (item.body||"").replace(/\s+/g," ").trim().slice(0,120);
      await supabase.from("notifications").insert({
        title: item.title, body: safeBody, type: "announcement", target_role: "all",
        target_class: (!item.target_class||item.target_class==="All") ? null : item.target_class,
        source_id: item.id, source_type: "announcements", created_by: "Admin"
      });
      // Show WhatsApp broadcast prompt
      setWaAnn({ title: item.title, body: item.body||"" });
    }
    showToast(publishing?"Published!":"Unpublished"); load();
  }

  const [viewItem, setViewItem] = React.useState(null); // full view modal

  function edit(item) {
    const isCustom = item.popup_image_url && !POPUP_IMAGES.find(p=>p.url===item.popup_image_url&&p.url!=="custom");
    setForm({ title:item.title, body:item.body, category:item.category, is_published:item.is_published, target_class:item.target_class||"All",
      show_as_popup:!!item.show_as_popup, popup_image_preset:isCustom?"custom":(item.popup_image_url||""),
      popup_image_url:isCustom?item.popup_image_url:"", popup_button_text:item.popup_button_text||"Learn More",
      popup_button_url:item.popup_button_url||"", popup_expires_at:item.popup_expires_at||"",
      popup_overlay_opacity:item.popup_overlay_opacity??60, popup_width:item.popup_width||"standard", popup_card_style:item.popup_card_style||"solid" });
    setEditing(item.id); setError(""); setFile(null); setShowForm(true);
  }

  const G = "#1a6b3c";
  const inp = { width:"100%", padding:"10px 12px", border:"1px solid #d1d5db", borderRadius:8, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", outline:"none" };
  const btnPrimary = { background:G, color:"white", border:"none", borderRadius:9, padding:"11px 24px", fontWeight:700, fontSize:13, cursor:"pointer" };
  const btnGray = { background:"#f3f4f6", color:"#374151", border:"1px solid #d1d5db", borderRadius:9, padding:"11px 24px", fontWeight:700, fontSize:13, cursor:"pointer" };

  function fileIcon(name) {
    if(!name) return "📎";
    const ext = name.split('.').pop().toLowerCase();
    if(["pdf"].includes(ext)) return "📄";
    if(["doc","docx"].includes(ext)) return "📝";
    if(["jpg","jpeg","png","gif","webp"].includes(ext)) return "🖼️";
    if(["xls","xlsx"].includes(ext)) return "📊";
    if(["ppt","pptx"].includes(ext)) return "📑";
    return "📎";
  }
  const isImg = name => name && ["jpg","jpeg","png","gif","webp"].includes(name.split('.').pop().toLowerCase());

  return (
    <div style={{ padding:28 }}>
      {toast && <div style={{ position:"fixed", top:20, right:20, background:G, color:"white", borderRadius:10, padding:"12px 20px", fontWeight:700, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.2)" }}>{toast}</div>}

      {/* ── WhatsApp Broadcast Banner (appears after publishing) ── */}
      {waAnn && (()=>{
        const preview = waAnn.body.replace(/\s+/g," ").trim().slice(0,200)+(waAnn.body.length>200?"…":"");
        const msg = `📢 *${waAnn.title}*\n\n${preview}\n\n— QK Academy`;
        // For broadcast we open a generic wa.me link (no number) so admin can copy-paste to groups
        const broadcastLink = `https://wa.me/?text=${encodeURIComponent(msg)}`;
        return (
          <div style={{background:"#f0fdf4",border:"1.5px solid #86efac",borderRadius:14,padding:"14px 18px",marginBottom:16,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",boxShadow:"0 2px 8px rgba(34,197,94,.1)"}}>
            <div style={{fontSize:24}}>📢</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:13,color:"#166534"}}>Announcement Published — Share on WhatsApp?</div>
              <div style={{fontSize:12,color:"#6b7280",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:400}}>{waAnn.title}</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <a href={broadcastLink} target="_blank" rel="noopener noreferrer"
                style={{background:"#22c55e",color:"white",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:13,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
                💬 Share to Parent Groups
              </a>
              <button onClick={()=>setWaAnn(null)}
                style={{background:"white",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:9,padding:"8px 12px",fontSize:12,cursor:"pointer"}}>
                Dismiss
              </button>
            </div>
          </div>
        );
      })()}

      {/* Full view modal */}
      {viewItem&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setViewItem(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:600,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${CAT_COLOR[viewItem.category]||G},${CAT_COLOR[viewItem.category]||G}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 24px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
                      <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700}}>{viewItem.category}</span>
                      <span style={{background:viewItem.is_published?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.2)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700}}>{viewItem.is_published?"● Published":"○ Draft"}</span>
                      {viewItem.target_class&&viewItem.target_class!=="All"&&<span style={{background:"rgba(255,255,255,0.2)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11}}>{viewItem.target_class}</span>}
                    </div>
                    <div style={{fontWeight:900,fontSize:19,color:"white",lineHeight:1.3}}>{viewItem.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6}}>{new Date(viewItem.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
                  </div>
                  <button onClick={()=>setViewItem(null)} style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,width:34,height:34,color:"white",fontWeight:900,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              <div style={{padding:"24px 26px"}}>
                <div style={{fontSize:15,color:"#374151",lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>{viewItem.body||"No content."}</div>
                {viewItem.file_url&&(
                  isImg(viewItem.file_name)?(
                    <a href={viewItem.file_url} target="_blank" rel="noopener noreferrer" style={{display:"block",marginTop:14,borderRadius:10,overflow:"hidden",border:"1px solid #e5e7eb"}}>
                      <img src={viewItem.file_url} alt={viewItem.file_name||"Attachment"} style={{width:"100%",maxHeight:340,objectFit:"contain",background:"#f9fafb",display:"block"}}/>
                    </a>
                  ):(
                    <a href={viewItem.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:16,fontSize:13,color:"#7c3aed",fontWeight:700,textDecoration:"none",background:"#f5f3ff",borderRadius:10,padding:"10px 16px",border:"1px solid #ddd6fe"}}>📎 {viewItem.file_name||"View Attachment"}</a>
                  )
                )}
                <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid #f3f4f6",display:"flex",gap:10}}>
                  <button onClick={()=>{setViewItem(null);edit(viewItem);}} style={{flex:1,background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✏️ Edit</button>
                  <button onClick={()=>setViewItem(null)} style={{flex:1,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:900, color:G }}>📢 Announcements</div>
          <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>Manage news, events and notices shown on the public website</div>
        </div>
        <button onClick={()=>{ setForm(blank); setEditing(null); setError(""); setFile(null); setShowForm(true); }} style={btnPrimary}>
          + New Announcement
        </button>
      </div>

      {showForm && (
        <div style={{ background:"white", borderRadius:16, border:"1.5px solid #d1d5db", padding:28, marginBottom:28, boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize:17, fontWeight:800, color:G, marginBottom:20 }}>{editing?"Edit":"New"} Announcement</div>
          {error && <div style={{ background:"#fef2f2", color:"#dc2626", borderRadius:8, padding:"10px 14px", fontSize:13, marginBottom:16, fontWeight:600 }}>{error}</div>}
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>TITLE *</label>
              <input style={inp} value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Announcement title"/>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>CATEGORY</label>
              <select style={inp} value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                {CATS.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>TARGET AUDIENCE</label>
              <select style={inp} value={form.target_class||"All"} onChange={e=>setForm(p=>({...p,target_class:e.target.value}))}>
                <option value="All">All Classes (Everyone)</option>
                {ALL_CLASSES.map(c=><option key={c} value={c}>{c} only</option>)}
              </select>
              <div style={{fontSize:11,color:"#6b7280",marginTop:3}}>Class-specific announcements only show to parents of that class</div>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>CONTENT *</label>
              <textarea style={{...inp, minHeight:110, resize:"vertical"}} value={form.body} onChange={e=>setForm(p=>({...p,body:e.target.value}))} placeholder="Write the announcement content here..."/>
            </div>
            <div>
              <label style={{ fontSize:12, fontWeight:700, color:"#374151", display:"block", marginBottom:5 }}>ATTACHMENT (optional — PDF, Word, image etc.)</label>
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files[0]||null)}
                style={{ fontSize:13, color:"#374151", cursor:"pointer" }}/>
              {file && <div style={{ marginTop:6, fontSize:12, color:G, fontWeight:600 }}>Selected: {file.name} ({(file.size/1024).toFixed(0)} KB)</div>}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <input type="checkbox" id="pub" checked={form.is_published} onChange={e=>setForm(p=>({...p,is_published:e.target.checked}))} style={{ width:16, height:16, cursor:"pointer" }}/>
              <label htmlFor="pub" style={{ fontSize:14, fontWeight:600, color:"#374151", cursor:"pointer" }}>Publish immediately (visible on public website)</label>
            </div>

            {/* ── Popup Settings ── */}
            <div style={{borderTop:"1.5px solid #e5e7eb",paddingTop:16,marginTop:4}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:form.show_as_popup?14:0}}>
                <input type="checkbox" id="popup_chk" checked={!!form.show_as_popup} onChange={e=>setForm(p=>({...p,show_as_popup:e.target.checked}))} style={{width:16,height:16,cursor:"pointer",accentColor:G}}/>
                <label htmlFor="popup_chk" style={{fontSize:14,fontWeight:700,color:G,cursor:"pointer"}}>🎯 Show as Website Popup</label>
                <span style={{fontSize:11,color:"#6b7280",fontStyle:"italic"}}>Appears as a popup on the public website when visitors land</span>
              </div>

              {form.show_as_popup&&(
                <div style={{background:"#f0fdf4",borderRadius:12,padding:"16px 18px",border:"1px solid #bbf7d0",display:"flex",flexDirection:"column",gap:14}}>
                  {/* Background image — drag & drop upload */}
                  <div>
                    <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:8}}>POPUP BACKGROUND IMAGE</label>
                    {form.popup_image_url ? (
                      <div style={{position:"relative",borderRadius:10,overflow:"hidden",height:160,border:"2px solid #bbf7d0"}}>
                        <img src={form.popup_image_url} alt="popup bg" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,rgba(0,0,0,0.1),rgba(0,0,0,0.5))"}}/>
                        <div style={{position:"absolute",bottom:10,left:12,color:"white",fontSize:11,fontWeight:700,textShadow:"0 1px 3px rgba(0,0,0,0.5)"}}>✅ Image set — will show in popup</div>
                        <button onClick={()=>setForm(p=>({...p,popup_image_url:"",popup_image_preset:""}))}
                          style={{position:"absolute",top:8,right:8,background:"rgba(225,29,72,0.9)",color:"white",border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                          ✕ Remove
                        </button>
                      </div>
                    ) : (
                      <PopupImageUploader
                        uploading={uploading}
                        onUpload={async (file) => {
                          setUploading(true);
                          const ext = file.name.split('.').pop();
                          const path = `popup-images/${Date.now()}.${ext}`;
                          const { error: upErr } = await supabase.storage.from("announcements").upload(path, file, { upsert:true });
                          setUploading(false);
                          if(upErr){ setError("Image upload failed: "+upErr.message); return; }
                          const { data } = supabase.storage.from("announcements").getPublicUrl(path);
                          setForm(p=>({...p, popup_image_url: data.publicUrl, popup_image_preset:"custom"}));
                        }}
                      />
                    )}
                  </div>

                  {/* Button + expiry */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:5}}>BUTTON TEXT</label>
                      <input style={inp} value={form.popup_button_text} onChange={e=>setForm(p=>({...p,popup_button_text:e.target.value}))} placeholder="e.g. Apply Now"/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:5}}>BUTTON LINK (optional)</label>
                      <input style={inp} value={form.popup_button_url} onChange={e=>setForm(p=>({...p,popup_button_url:e.target.value}))} placeholder="https://..."/>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:5}}>EXPIRES ON (optional)</label>
                      <input type="date" style={inp} value={form.popup_expires_at} onChange={e=>setForm(p=>({...p,popup_expires_at:e.target.value}))}/>
                    </div>
                  </div>

                  {/* Appearance controls */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                    {/* Backdrop darkness */}
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"flex",justifyContent:"space-between",marginBottom:6}}>
                        <span>BACKDROP DARKNESS</span>
                        <span style={{color:G,fontWeight:800}}>{form.popup_overlay_opacity}%</span>
                      </label>
                      <input type="range" min={0} max={90} step={10}
                        value={form.popup_overlay_opacity??60}
                        onChange={e=>setForm(p=>({...p,popup_overlay_opacity:+e.target.value}))}
                        style={{width:"100%",accentColor:G}}/>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#9ca3af",marginTop:2}}>
                        <span>Light</span><span>Dark</span>
                      </div>
                    </div>
                    {/* Popup size */}
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>POPUP SIZE</label>
                      <div style={{display:"flex",gap:6}}>
                        {[["compact","Compact"],["standard","Standard"],["wide","Wide"]].map(([v,l])=>(
                          <button key={v} type="button" onClick={()=>setForm(p=>({...p,popup_width:v}))}
                            style={{flex:1,padding:"7px 4px",borderRadius:8,border:`2px solid ${form.popup_width===v?G:"#d1d5db"}`,
                              background:form.popup_width===v?"#f0fdf4":"white",color:form.popup_width===v?G:"#374151",
                              fontWeight:700,fontSize:11,cursor:"pointer"}}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Card background style */}
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>CARD BACKGROUND</label>
                    <div style={{display:"flex",gap:8}}>
                      {[
                        ["solid",   "Solid White",     "#ffffff",                    "none",        "Clean & readable"],
                        ["frosted", "Frosted Glass",   "rgba(255,255,255,0.82)",     "blur(18px)",  "Modern glass effect"],
                        ["clear",   "Almost Clear",    "rgba(255,255,255,0.52)",     "blur(16px)",  "Dramatic & bold"],
                      ].map(([v,l,bg,blur,hint])=>(
                        <button key={v} type="button" onClick={()=>setForm(p=>({...p,popup_card_style:v}))}
                          style={{flex:1,padding:"10px 8px",borderRadius:10,border:`2px solid ${form.popup_card_style===v?G:"#d1d5db"}`,
                            background:form.popup_card_style===v?"#f0fdf4":"white",cursor:"pointer",textAlign:"center"}}>
                          <div style={{height:28,borderRadius:6,background:bg,backdropFilter:blur,WebkitBackdropFilter:blur,
                            border:"1px solid #e5e7eb",marginBottom:5}}/>
                          <div style={{fontSize:11,fontWeight:700,color:form.popup_card_style===v?G:"#374151"}}>{l}</div>
                          <div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>{hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Live preview */}
                  {(form.popup_image_preset&&form.popup_image_preset!=="custom"||form.popup_image_url)&&(()=>{
                    const previewSrc = form.popup_image_preset==="custom"?form.popup_image_url:form.popup_image_preset;
                    const overlayAlpha = ((form.popup_overlay_opacity??60)/100).toFixed(2);
                    const cardBg = form.popup_card_style==="frosted"?"rgba(255,255,255,0.82)":form.popup_card_style==="clear"?"rgba(255,255,255,0.52)":"#fff";
                    const cardBlur = form.popup_card_style!=="solid"?"blur(18px)":"none";
                    const widthPx = form.popup_width==="compact"?220:form.popup_width==="wide"?360:280;
                    return (
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>LIVE PREVIEW</label>
                        <div style={{position:"relative",width:widthPx,borderRadius:14,overflow:"hidden",boxShadow:"0 8px 30px rgba(0,0,0,0.2)",background:cardBg,backdropFilter:cardBlur,WebkitBackdropFilter:cardBlur}}>
                          <div style={{position:"relative",height:110,overflow:"hidden"}}>
                            <img src={previewSrc} alt="preview" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                            <div style={{position:"absolute",inset:0,background:`rgba(0,0,0,${overlayAlpha})`}}/>
                            <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,transparent 40%,rgba(0,0,0,0.55) 100%)",display:"flex",flexDirection:"column",justifyContent:"flex-end",padding:10}}>
                              <div style={{color:"white",fontWeight:900,fontSize:11,lineHeight:1.3}}>{form.title||"Announcement title"}</div>
                            </div>
                            <div style={{position:"absolute",top:6,right:6,background:"rgba(255,255,255,0.9)",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:900,color:"#374151"}}>✕</div>
                          </div>
                          <div style={{padding:"8px 10px 10px",background:cardBg,backdropFilter:cardBlur,WebkitBackdropFilter:cardBlur}}>
                            <div style={{fontSize:10,color:"#6b7280",lineHeight:1.5,marginBottom:6}}>{(form.body||"Your announcement content…").slice(0,60)}{form.body?.length>60?"…":""}</div>
                            {form.popup_button_text&&<div style={{background:G,color:"white",borderRadius:5,padding:"4px 10px",fontSize:9,fontWeight:700,display:"inline-block"}}>{form.popup_button_text}</div>}
                          </div>
                        </div>
                        <div style={{fontSize:10,color:"#9ca3af",marginTop:6}}>Preview reflects size, darkness & card style</div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
          <div style={{ display:"flex", gap:12, marginTop:20 }}>
            <button onClick={save} disabled={saving||uploading} style={{...btnPrimary, opacity:(saving||uploading)?0.7:1}}>
              {saving?"Saving...":(uploading?"Uploading file...":"Save Announcement")}
            </button>
            <button onClick={()=>{ setShowForm(false); setEditing(null); setError(""); setFile(null); }} style={btnGray}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Filter bar ── */}
      {!loading && items.length > 0 && (
        <div style={{background:"white",borderRadius:14,border:"1px solid #e5e7eb",padding:"14px 18px",display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{position:"relative",flex:1,minWidth:180}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search announcements…"
                style={{...inp,paddingLeft:32,background:"#f9fafb"}}/>
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["All","Published","Draft"].map(s=>(
                <button key={s} onClick={()=>setFilterStatus(s)}
                  style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${filterStatus===s?G:"#d1d5db"}`,
                    background:filterStatus===s?G:"white",color:filterStatus===s?"white":"#374151",
                    fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {["All",...CATS].map(c=>(
              <button key={c} onClick={()=>setFilterCat(c)}
                style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${filterCat===c?(CAT_COLOR[c]||G):"#e5e7eb"}`,
                  background:filterCat===c?(CAT_COLOR[c]||G):"white",color:filterCat===c?"white":"#374151",
                  fontWeight:600,fontSize:11,cursor:"pointer"}}>
                {c}
              </button>
            ))}
            {(search||filterCat!=="All"||filterStatus!=="All")&&(
              <button onClick={()=>{setSearch("");setFilterCat("All");setFilterStatus("All");}}
                style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid #fecaca",background:"#fef2f2",color:"#dc2626",fontWeight:600,fontSize:11,cursor:"pointer"}}>
                ✕ Clear
              </button>
            )}
            <span style={{marginLeft:"auto",fontSize:11,color:"#9ca3af",fontWeight:600}}>
              {(()=>{const f=items.filter(i=>{
                if(filterCat!=="All"&&i.category!==filterCat) return false;
                if(filterStatus==="Published"&&!i.is_published) return false;
                if(filterStatus==="Draft"&&i.is_published) return false;
                if(search&&!i.title.toLowerCase().includes(search.toLowerCase())&&!(i.body||"").toLowerCase().includes(search.toLowerCase())) return false;
                return true;
              });return `${f.length} of ${items.length}`;})()}
            </span>
          </div>
        </div>
      )}

      {loading ? <div style={{ color:"#6b7280", padding:40, textAlign:"center" }}>Loading...</div> : (
        items.length === 0 ? (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af", background:"white", borderRadius:14, border:"1px solid #e5e7eb" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📢</div>
            <div style={{ fontSize:16, fontWeight:700 }}>No announcements yet</div>
            <div style={{ fontSize:13, marginTop:6 }}>Click "+ New Announcement" to add your first one</div>
          </div>
        ) : (()=>{
          const filtered = items.filter(i=>{
            if(filterCat!=="All"&&i.category!==filterCat) return false;
            if(filterStatus==="Published"&&!i.is_published) return false;
            if(filterStatus==="Draft"&&i.is_published) return false;
            if(search&&!i.title.toLowerCase().includes(search.toLowerCase())&&!(i.body||"").toLowerCase().includes(search.toLowerCase())) return false;
            return true;
          });
          return filtered.length===0?(
            <div style={{textAlign:"center",padding:40,color:"#9ca3af",background:"white",borderRadius:14,border:"1px solid #e5e7eb",fontSize:13}}>
              No announcements match your filters.
            </div>
          ):(
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {filtered.map(item=>(
              <div key={item.id} style={{ background:"white", borderRadius:14, border:"1px solid #e5e7eb", overflow:"hidden", display:"flex", gap:0, alignItems:"stretch" }}>
                {/* Left accent bar */}
                <div style={{width:4,background:CAT_COLOR[item.category]||G,flexShrink:0,borderRadius:"0"}}/>
                <div style={{flex:1,padding:"16px 20px",display:"flex",gap:16,alignItems:"flex-start"}}>
                  <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                    <span style={{ background:CAT_COLOR[item.category]||G, color:"white", borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700 }}>{item.category}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:item.is_published?"#15803d":"#6b7280", background:item.is_published?"#dcfce7":"#f3f4f6", borderRadius:20, padding:"3px 10px", border:`1px solid ${item.is_published?"#bbf7d0":"#e5e7eb"}` }}>
                      {item.is_published?"● Published":"○ Draft"}
                    </span>
                    {item.file_url && <span style={{ fontSize:11, color:"#7c3aed", background:"#f3e8ff", borderRadius:20, padding:"3px 10px", border:"1px solid #c4b5fd" }}>{fileIcon(item.file_name)} {isImg(item.file_name)?"Image":"File"}</span>}
                    {item.show_as_popup && <span style={{ fontSize:11, color:"#0369a1", background:"#e0f2fe", borderRadius:20, padding:"3px 10px", border:"1px solid #bae6fd" }}>🎯 Popup</span>}
                    <span style={{ fontSize:11, color:"#9ca3af", marginLeft:"auto" }}>{new Date(item.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                  </div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#111827", marginBottom:5, cursor:"pointer" }} onClick={()=>setViewItem(item)}>{item.title}</div>
                  <div style={{ fontSize:13, color:"#6b7280", lineHeight:1.7 }}>{item.body.length>180?item.body.slice(0,180)+"...":item.body}</div>
                  {item.file_url && (
                    <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:10 }}>
                      {isImg(item.file_name)?(
                        <a href={item.file_url} target="_blank" rel="noopener noreferrer" style={{display:"block",borderRadius:8,overflow:"hidden",border:"1px solid #e5e7eb",flexShrink:0}}>
                          <img src={item.file_url} alt={item.file_name} style={{height:60,width:90,objectFit:"cover",display:"block"}}/>
                        </a>
                      ):(
                        <a href={item.file_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize:12, color:"#7c3aed", fontWeight:700, textDecoration:"none", background:"#f3e8ff", borderRadius:8, padding:"5px 12px", border:"1px solid #c4b5fd" }}>
                          {fileIcon(item.file_name)} {item.file_name||"View Attachment"}
                        </a>
                      )}
                      <button onClick={()=>removeFile(item)} style={{ fontSize:11, color:"#dc2626", background:"none", border:"none", cursor:"pointer", fontWeight:600 }}>✕ Remove</button>
                    </div>
                  )}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, flexShrink:0, alignItems:"flex-end" }}>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setViewItem(item)} style={{ background:"#f0fdf4", color:"#15803d", border:"1px solid #bbf7d0", borderRadius:8, padding:"7px 12px", fontWeight:700, fontSize:12, cursor:"pointer" }}>👁 View</button>
                    <button onClick={()=>edit(item)} style={{ background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:8, padding:"7px 12px", fontWeight:700, fontSize:12, cursor:"pointer" }}>✏️ Edit</button>
                    <button onClick={()=>del(item.id)} style={{ background:"#fef2f2", color:"#991b1b", border:"1px solid #fecaca", borderRadius:8, padding:"7px 12px", fontWeight:700, fontSize:12, cursor:"pointer" }}>🗑</button>
                  </div>
                  <button onClick={()=>toggle(item)}
                    style={{ background:item.is_published?"#fef3c7":"#dcfce7", color:item.is_published?"#92400e":"#166534", border:`1px solid ${item.is_published?"#fcd34d":"#86efac"}`, borderRadius:8, padding:"6px 14px", fontWeight:700, fontSize:11, cursor:"pointer", width:"100%" }}>
                    {item.is_published?"Unpublish":"Publish"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        );
      })()
    )}



    </div>
  );
}
// ── REPORT CARDS TAB (extracted to avoid hooks-in-IIFE error) ────
function ReportCardsTab({ reportCards }) {
  const S = useSettings();
  const GRADES=[{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}];
  const getGrade=(m,t)=>{if(!t)return"—";const p=(m/t)*100;return GRADES.find(g=>p>=g.min)?.label||"F";};
  const GRADE_C=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
  const EXAM_COLOR={monthly:"#2563eb",midterm:"#d97706",final:"#1a6b3c",quiz:"#7c3aed",general:"#374151",comprehensive:"#1a6b3c",custom:"#374151"};
  const EXAM_ICON={monthly:"📅",midterm:"📋",final:"🎓",quiz:"⚡",general:"📝",comprehensive:"📊",custom:"📝"};
  const [openCard, setOpenCard] = React.useState(null);
  const sorted = [...(reportCards||[])].sort((a,b)=>new Date(b.published_at)-new Date(a.published_at));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Full report card modal — same as admin */}
      {openCard&&(()=>{
        const card = openCard;
        const sRes = card.school_results||[];
        const hRes = card.hifz_results||[];
        const sTotal = card.school_total||0, sMax = card.school_max||0;
        const hTotal = card.hifz_total||0,  hMax = card.hifz_max||0;
        const sPct = sMax>0?Math.round((sTotal/sMax)*100):0;
        const sGrade = sMax>0?getGrade(sTotal,sMax):"—";
        const color = EXAM_COLOR[card.exam_type]||"#374151";
        const icon  = EXAM_ICON[card.exam_type]||"📝";
        return (
          <Portal>
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"12px",overflowY:"auto"}} onClick={()=>setOpenCard(null)}>
              <div style={{background:"white",borderRadius:16,width:"100%",maxWidth:640,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.35)"}} onClick={e=>e.stopPropagation()}>

                {/* ── Letterhead — white background, colour top border only ── */}
                <div style={{borderRadius:"16px 16px 0 0",overflow:"hidden"}}>
                  {/* Top accent strip */}
                  <div style={{height:6,background:`linear-gradient(90deg,${color},${color}99)`}}/>
                  {/* White letterhead */}
                  <div style={{padding:"20px 24px 16px",background:"white",borderBottom:`1px solid #f3f4f6`}}>
                    <AcademyHeader/>
                  </div>
                  {/* Exam info bar */}
                  <div style={{background:"#f8fafc",padding:"14px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,borderBottom:`2px solid ${color}20`}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{background:color,color:"white",borderRadius:7,padding:"4px 12px",fontWeight:800,fontSize:13}}>{icon} {card.exam_type.charAt(0).toUpperCase()+card.exam_type.slice(1)}{card.exam_month?" — "+card.exam_month:""}</span>
                        <span style={{fontWeight:800,fontSize:15,color:"#111827"}}>{card.student_name}</span>
                        <span style={{fontSize:13,color:"#6b7280"}}>Class {card.class}</span>
                      </div>
                      <div style={{fontSize:11,color:"#9ca3af",marginTop:5}}>Published {new Date(card.published_at).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</div>
                    </div>
                    {sMax>0&&(
                      <div style={{background:`${color}10`,borderRadius:12,padding:"10px 18px",textAlign:"center",border:`2px solid ${color}30`}}>
                        <div style={{fontWeight:900,fontSize:36,lineHeight:1,color:color}}>{sGrade}</div>
                        <div style={{fontSize:14,fontWeight:700,color:color}}>{sPct}%</div>
                        <div style={{fontSize:10,color:"#6b7280"}}>{sTotal}/{sMax} marks</div>
                      </div>
                    )}
                  </div>
                  {/* Overall progress bar */}
                  {sMax>0&&(
                    <div style={{height:5,background:"#f3f4f6"}}>
                      <div style={{height:"100%",width:`${sPct}%`,background:color,transition:"width .5s"}}/>
                    </div>
                  )}
                </div>

                {/* ── Body ── */}
                <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>
                  {/* Academic subjects */}
                  {sRes.length>0&&(
                    <div>
                      <div style={{fontSize:11,fontWeight:800,color:"#374151",textTransform:"uppercase",letterSpacing:.5,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                        <span style={{width:3,height:14,background:color,borderRadius:2,display:"inline-block"}}/>
                        Academic Subjects
                      </div>
                      {sRes.map((r,i)=>{
                        const g=getGrade(r.marks_obtained,r.total_marks);
                        const pct=r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0;
                        return (
                          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #f3f4f6"}}>
                            <span style={{flex:1,fontSize:13,color:"#374151",fontWeight:600}}>{r.subject}</span>
                            <div style={{width:80,height:5,background:"#f3f4f6",borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct}%`,background:GRADE_C(g),borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:13,fontWeight:800,color:GRADE_C(g),minWidth:60,textAlign:"right"}}>{r.marks_obtained}/{r.total_marks}</span>
                            <span style={{fontSize:11,fontWeight:800,color:"white",background:GRADE_C(g),borderRadius:6,padding:"2px 8px",minWidth:28,textAlign:"center"}}>{g}</span>
                          </div>
                        );
                      })}
                      <div style={{display:"flex",justifyContent:"space-between",fontWeight:900,paddingTop:10,color:color,fontSize:14,borderTop:`2px solid ${color}20`,marginTop:4}}>
                        <span>Total Academic</span>
                        <span>{sTotal}/{sMax} ({sPct}%) — {sGrade}</span>
                      </div>
                    </div>
                  )}

                  {/* Hifz subjects */}
                  {hRes.length>0&&(
                    <div style={{background:"#f5f3ff",borderRadius:12,padding:"14px 16px"}}>
                      <div style={{fontSize:11,fontWeight:800,color:"#5b21b6",textTransform:"uppercase",letterSpacing:.5,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                        <span style={{width:3,height:14,background:"#7c3aed",borderRadius:2,display:"inline-block"}}/>
                        Hifz Assessment
                      </div>
                      {hRes.map((r,i)=>{
                        const g=getGrade(r.marks_obtained,r.total_marks);
                        const pct=r.total_marks>0?Math.round((r.marks_obtained/r.total_marks)*100):0;
                        return (
                          <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #ddd6fe"}}>
                            <span style={{flex:1,fontSize:13,color:"#4c1d95",fontWeight:600}}>{r.subject}</span>
                            <div style={{width:60,height:5,background:"#ede9fe",borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${pct}%`,background:GRADE_C(g),borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:13,fontWeight:800,color:GRADE_C(g),minWidth:60,textAlign:"right"}}>{r.marks_obtained}/{r.total_marks}</span>
                            <span style={{fontSize:11,fontWeight:800,color:"white",background:GRADE_C(g),borderRadius:6,padding:"2px 8px",minWidth:28,textAlign:"center"}}>{g}</span>
                          </div>
                        );
                      })}
                      {hMax>0&&<div style={{display:"flex",justifyContent:"space-between",fontWeight:900,paddingTop:8,color:"#5b21b6",fontSize:13,borderTop:"1px solid #ddd6fe",marginTop:4}}>
                        <span>Total Hifz</span><span>{hTotal}/{hMax}</span>
                      </div>}
                    </div>
                  )}

                  {/* Hifz monthly summary */}
                  {card.hifz_summary&&(card.hifz_summary.current_juz>0||card.hifz_summary.month_sabaq_pages>0)&&(
                    <div style={{background:"linear-gradient(135deg,#f5f3ff,#ede9fe)",borderRadius:12,padding:"14px 16px",border:"1px solid #ddd6fe"}}>
                      <div style={{fontWeight:800,color:"#4c1d95",fontSize:13,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
                        <span style={{width:3,height:14,background:"#7c3aed",borderRadius:2,display:"inline-block"}}/>
                        📖 Hifz Progress — {card.exam_month||"This Period"}
                      </div>
                      {card.hifz_summary.current_juz>0&&(
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
                          <span style={{background:"#4c1d95",color:"white",borderRadius:8,padding:"4px 12px",fontSize:12,fontWeight:700}}>پارہ {card.hifz_summary.current_juz} — {card.hifz_summary.juz_ar}</span>
                          {card.hifz_summary.current_surah&&<span style={{background:"white",color:"#7c3aed",borderRadius:8,padding:"4px 12px",fontSize:12,fontWeight:700,border:"1px solid #ddd6fe"}}>{card.hifz_summary.current_surah}</span>}
                        </div>
                      )}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                        {[["📗 سبق (Sabaq)",card.hifz_summary.month_sabaq_pages,"#1a6b3c","#f0fdf4"],["📘 سبقی (Sabqi)",card.hifz_summary.month_sabqi_pages,"#2563eb","#eff6ff"],["📕 منزل (Manzil)",card.hifz_summary.month_manzil_pages,"#5b21b6","#f5f3ff"]].map(([lbl,pages,col,bg])=>(
                          <div key={lbl} style={{background:bg,borderRadius:10,padding:"10px 8px",textAlign:"center",border:`1px solid ${col}20`}}>
                            <div style={{fontSize:10,color:col,fontWeight:700,marginBottom:3}}>{lbl}</div>
                            <div style={{fontWeight:900,color:col,fontSize:20}}>{pages||0}</div>
                            <div style={{fontSize:9,color:"#9ca3af"}}>pages</div>
                          </div>
                        ))}
                      </div>
                      {(card.hifz_summary.month_sessions>0||card.hifz_summary.month_absent>0)&&(
                        <div style={{fontSize:11,color:"#6b7280",marginTop:8,fontWeight:600,textAlign:"center"}}>✅ {card.hifz_summary.month_sessions||0} sessions present · ❌ {card.hifz_summary.month_absent||0} absent</div>
                      )}
                    </div>
                  )}

                  {/* Teacher comment */}
                  {card.teacher_comment&&(
                    <div style={{background:"#fffbeb",borderRadius:10,padding:"12px 16px",border:"1px solid #fde68a"}}>
                      <div style={{fontSize:10,fontWeight:800,color:"#92400e",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>💬 Class Teacher's Comment</div>
                      <div style={{fontSize:13,color:"#78350f",fontStyle:"italic",lineHeight:1.6}}>"{card.teacher_comment}"</div>
                    </div>
                  )}
                  {card.remarks&&(
                    <div style={{fontSize:12,color:"#6b7280",background:"#f9fafb",borderRadius:8,padding:"8px 12px"}}>
                      📝 <strong style={{color:"#374151"}}>Remarks:</strong> {card.remarks}
                    </div>
                  )}

                  <button onClick={()=>setOpenCard(null)}
                    style={{background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"12px",fontWeight:700,cursor:"pointer",fontSize:13,width:"100%"}}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </Portal>
        );
      })()}

      {/* Card list */}
      {sorted.length===0?(
        <div style={{background:"white",borderRadius:16,padding:40,textAlign:"center",color:"#9ca3af",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
          <div style={{fontSize:36,marginBottom:12}}>📄</div>
          <div style={{fontWeight:700,color:"#374151"}}>No report cards yet</div>
          <div style={{fontSize:13,marginTop:6}}>Your child's teacher will publish report cards after each exam.</div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {sorted.map(card=>{
            const sPct=card.school_max>0?Math.round((card.school_total/card.school_max)*100):0;
            const sGrade=card.school_max>0?getGrade(card.school_total,card.school_max):"—";
            const color=EXAM_COLOR[card.exam_type]||"#374151";
            const icon=EXAM_ICON[card.exam_type]||"📝";
            return (
              <button key={card.id} onClick={()=>setOpenCard(card)}
                style={{background:"white",borderRadius:14,padding:"16px 18px",border:`1.5px solid ${color}30`,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",display:"flex",alignItems:"center",gap:14,cursor:"pointer",textAlign:"left",borderLeft:`4px solid ${color}`,width:"100%",transition:"box-shadow .15s"}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.12)"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.06)"}>
                <div style={{width:44,height:44,borderRadius:12,background:`${color}15`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,color:"#111827",fontSize:14}}>{card.exam_type.charAt(0).toUpperCase()+card.exam_type.slice(1)}{card.exam_month?" — "+card.exam_month:""}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{new Date(card.published_at).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</div>
                </div>
                {sGrade!=="—"&&(
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontWeight:900,fontSize:26,color:GRADE_C(sGrade),lineHeight:1}}>{sGrade}</div>
                    <div style={{fontSize:12,color:"#6b7280"}}>{sPct}%</div>
                  </div>
                )}
                <span style={{fontSize:20,color:"#9ca3af",flexShrink:0}}>›</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── PARENTS MANAGEMENT ───────────────────────────────────────────
// ── Batch Parent Account Creation ────────────────────────────────────
function BatchParentCreation({ students, parents, onDone, showToast }) {
  const G = "#1a6b3c";
  const [open,       setOpen]       = React.useState(false);
  const [selected,   setSelected]   = React.useState({}); // {student_id: true}
  const [running,    setRunning]    = React.useState(false);
  const [progress,   setProgress]   = React.useState([]); // [{name,email,status}]
  const [filter,     setFilter]     = React.useState("");
  const [filterCls,  setFilterCls]  = React.useState("All");
  const DEFAULT_PASS = "QKA@1234";

  // Students that don't already have a parent account
  const existingEmails = new Set(parents.map(p=>p.email));
  const eligible = students.filter(s=>{
    const name=(s.full_name||s.name||"").trim().split(" ");
    const email=`${(name[0]||"").toLowerCase().replace(/[^a-z]/g,"")}.${(name[1]||"").toLowerCase().replace(/[^a-z]/g,"")}@qkacademy.org`.replace(/\.@/,"@");
    return !parents.find(p=>p.student_id===s.id); // not already has parent
  });

  const allClasses=[...new Set(eligible.map(s=>s.class||s["class"]).filter(Boolean))].sort();

  const filtered = eligible.filter(s=>{
    const name=(s.full_name||s.name||"").toLowerCase();
    const matchSearch=!filter||name.includes(filter.toLowerCase());
    const matchClass=filterCls==="All"||(s.class||s["class"])===filterCls;
    return matchSearch&&matchClass;
  });

  const genEmail = s => {
    const name=(s.full_name||s.name||"").trim().split(" ");
    return `${(name[0]||"").toLowerCase().replace(/[^a-z]/g,"")}.${(name[1]||"").toLowerCase().replace(/[^a-z]/g,"")}@qkacademy.org`.replace(/\.@/,"@");
  };

  const toggleAll = () => {
    const allSelected = filtered.every(s=>selected[s.id]);
    const next={...selected};
    filtered.forEach(s=>{ if(allSelected) delete next[s.id]; else next[s.id]=true; });
    setSelected(next);
  };

  const selCount = Object.keys(selected).filter(id=>students.find(s=>s.id===id)).length;

  const runBatch = async () => {
    const toCreate = students.filter(s=>selected[s.id]);
    if(!toCreate.length) return;
    if(!window.confirm(`Create parent accounts for ${toCreate.length} students?\n\nEmail format: firstname.lastname@qkacademy.org\nDefault password: ${DEFAULT_PASS}\n\nParents should change password after first login.`)) return;

    setRunning(true);
    setProgress([]);
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL||"";
    const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY||"";

    for(const s of toCreate) {
      const email = genEmail(s);
      const name  = s.full_name||s.name||"";
      setProgress(p=>[...p,{name,email,status:"creating"}]);
      try {
        // 1. Create auth user
        const authRes = await fetch(`${SUPABASE_URL}/auth/v1/admin/users`,{
          method:"POST",
          headers:{"Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${SUPABASE_KEY}`},
          body:JSON.stringify({email,password:DEFAULT_PASS,email_confirm:true})
        });
        const authData = await authRes.json();
        const authOk   = authRes.ok && authData.id;

        // 2. Insert into parents table
        const {error:dbErr} = await supabase.from("parents").insert({
          email, student_id:String(s.id), student_name:name
        });

        const status = dbErr?"db_error":authOk?"done":"auth_warn";
        const note   = dbErr?dbErr.message:authOk?"":"Auth creation failed — record saved, manual login needed";
        setProgress(p=>p.map(x=>x.email===email?{...x,status,note}:x));
      } catch(e) {
        setProgress(p=>p.map(x=>x.email===email?{...x,status:"error",note:e.message}:x));
      }
      // Small delay to avoid rate limiting
      await new Promise(r=>setTimeout(r,300));
    }
    setRunning(false);
    setSelected({});
    onDone();
    showToast(`Batch complete! ${toCreate.length} accounts processed.`);
  };

  if(!open) return (
    <div style={{background:"linear-gradient(135deg,#f0fdf4,#dcfce7)",borderRadius:14,padding:"16px 20px",border:"1.5px dashed #86efac",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
      <div style={{flex:1}}>
        <div style={{fontWeight:800,fontSize:14,color:G}}>⚡ Batch Parent Account Creation</div>
        <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
          Create parent portal access for multiple students at once. Emails auto-generated as firstname.lastname@qkacademy.org
        </div>
        {eligible.length>0&&<div style={{fontSize:12,color:G,marginTop:4,fontWeight:600}}>{eligible.length} students eligible (no parent account yet)</div>}
      </div>
      <button onClick={()=>setOpen(true)}
        style={{background:G,color:"white",border:"none",borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer",flexShrink:0}}>
        Open Batch Creator →
      </button>
    </div>
  );

  return (
    <div style={{background:"white",borderRadius:16,border:"1.5px solid #86efac",padding:22,boxShadow:"0 4px 24px rgba(0,0,0,0.07)"}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:16,color:G}}>⚡ Batch Parent Account Creation</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>Select students → Create accounts in one click</div>
        </div>
        <button onClick={()=>{setOpen(false);setProgress([]);setSelected({});}}
          style={{background:"#f3f4f6",color:"#374151",border:"none",borderRadius:8,padding:"7px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
          Close ✕
        </button>
      </div>

      {/* Progress results */}
      {progress.length>0&&(
        <div style={{marginBottom:16,background:"#f9fafb",borderRadius:10,border:"1px solid #e5e7eb",overflow:"hidden"}}>
          <div style={{background:G,padding:"8px 14px",fontWeight:700,color:"white",fontSize:13}}>
            📊 Batch Progress — {progress.filter(x=>x.status==="done").length} done · {progress.filter(x=>x.status==="creating").length} in progress · {progress.filter(x=>["error","db_error"].includes(x.status)).length} failed
          </div>
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {progress.map((x,i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"center",padding:"7px 14px",borderBottom:"1px solid #f3f4f6",fontSize:12}}>
                <span style={{
                  fontWeight:800,fontSize:13,
                  color:x.status==="done"?"#15803d":x.status==="creating"?"#d97706":x.status==="auth_warn"?"#d97706":"#dc2626"
                }}>
                  {x.status==="done"?"✅":x.status==="creating"?"⏳":x.status==="auth_warn"?"⚠️":"❌"}
                </span>
                <span style={{fontWeight:600,minWidth:160}}>{x.name}</span>
                <span style={{color:"#6b7280",flex:1}}>{x.email}</span>
                {x.note&&<span style={{color:"#dc2626",fontSize:11}}>{x.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
        <input placeholder="Search student..." value={filter} onChange={e=>setFilter(e.target.value)}
          style={{flex:1,minWidth:160,border:"1.5px solid #d1d5db",borderRadius:8,padding:"8px 12px",fontSize:13}}/>
        <select value={filterCls} onChange={e=>setFilterCls(e.target.value)}
          style={{border:"1.5px solid #d1d5db",borderRadius:8,padding:"8px 12px",fontSize:13}}>
          <option value="All">All Classes</option>
          {allClasses.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#6b7280"}}>
          <strong style={{color:G}}>{selCount}</strong> selected
        </div>
      </div>

      {/* Select all + action bar */}
      <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center",flexWrap:"wrap"}}>
        <button onClick={toggleAll}
          style={{background:"#f5f3ff",color:"#4c1d95",border:"1px solid #ddd6fe",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
          {filtered.every(s=>selected[s.id])?"☑ Deselect All":"☐ Select All"} ({filtered.length})
        </button>
        <button onClick={()=>{
          const next={...selected};
          filtered.filter(s=>(s.class||s["class"])===filterCls||filterCls==="All").forEach(s=>next[s.id]=true);
          setSelected(next);
        }} style={{background:"#f0fdf4",color:G,border:"1px solid #bbf7d0",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
          + Select Filtered
        </button>
        {selCount>0&&(
          <button onClick={runBatch} disabled={running}
            style={{marginLeft:"auto",background:running?"#9ca3af":G,color:"white",border:"none",borderRadius:9,padding:"8px 20px",fontWeight:800,fontSize:13,cursor:running?"not-allowed":"pointer"}}>
            {running?`Creating... (${progress.filter(x=>x.status==="done").length}/${selCount})`:`⚡ Create ${selCount} Account${selCount>1?"s":""}`}
          </button>
        )}
      </div>

      {/* Student list */}
      <div style={{maxHeight:340,overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
        {filtered.length===0?(
          <div style={{textAlign:"center",padding:32,color:"#9ca3af",fontSize:13}}>
            {eligible.length===0?"All students already have parent accounts!":"No students match your filter."}
          </div>
        ):(
          filtered.map((s,i)=>{
            const email=genEmail(s);
            const isSel=!!selected[s.id];
            return (
              <div key={s.id} onClick={()=>setSelected(p=>({...p,[s.id]:!p[s.id]}))}
                style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:isSel?"#f0fdf4":"white",borderBottom:"1px solid #f3f4f6",cursor:"pointer",transition:"background .1s"}}>
                <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${isSel?G:"#d1d5db"}`,background:isSel?G:"white",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {isSel&&<span style={{color:"white",fontSize:13,fontWeight:900,lineHeight:1}}>✓</span>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{s.full_name||s.name}</div>
                  <div style={{fontSize:11,color:"#6b7280"}}>{s.class||s["class"]} · {email}</div>
                </div>
                <span style={{fontSize:11,color:"#9ca3af",flexShrink:0}}>{s.id}</span>
              </div>
            );
          })
        )}
      </div>

      {/* Default password note */}
      <div style={{marginTop:12,background:"#fffbeb",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#92400e",border:"1px solid #fde68a"}}>
        🔑 All accounts created with default password: <strong>{DEFAULT_PASS}</strong> — parents must change after first login.
        Email format: <strong>firstname.lastname@qkacademy.org</strong>
      </div>
    </div>
  );
}

function PortalAccessManagement({ students }) {
  const G = "#1a6b3c";
  const P = "#4c1d95";
  const B = "#1d4ed8";
  const ROLE_COLORS = {
    parent:          {bg:"#f0fdf4",border:"#bbf7d0",badge:"#dcfce7",text:"#15803d",icon:"👪"},
    teacher:         {bg:"#f5f3ff",border:"#ddd6fe",badge:"#ede9fe",text:"#5b21b6",icon:"🎓"},
    admin:           {bg:"#eff6ff",border:"#bfdbfe",badge:"#dbeafe",text:"#1d4ed8",icon:"🔑"},
    facility_manager:{bg:"#fffbeb",border:"#fef3c7",badge:"#fef3c7",text:"#92400e",icon:"🏗️"},
    accountant:      {bg:"#fff7ed",border:"#fed7aa",badge:"#fed7aa",text:"#c2410c",icon:"💼"},
    vice_principal:  {bg:"#f0f9ff",border:"#bae6fd",badge:"#bae6fd",text:"#0369a1",icon:"👔"},
    principal:       {bg:"#fdf4ff",border:"#e9d5ff",badge:"#e9d5ff",text:"#7e22ce",icon:"🎩"},
    staff:           {bg:"#fffbeb",border:"#fef3c7",badge:"#fef3c7",text:"#92400e",icon:"🏗️"},
  };

  const [section,  setSection]  = React.useState("parent");
  const [parents,  setParents]  = React.useState([]);
  const [teachers, setTeachers] = React.useState([]);
  const [staff,    setStaff]    = React.useState([]);
  const [authUsers,setAuthUsers]= React.useState([]); // all Supabase auth users
  const [authLoading,setAuthLoading] = React.useState(false);
  const [loading,  setLoading]  = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [saving,   setSaving]   = React.useState(false);
  const [error,    setError]    = React.useState("");
  const [toast,    setToast]    = React.useState("");
  const [saved,    setSaved]    = React.useState(null);
  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(""),3500);};

  const PORTAL_CLASSES  = ALL_CLASSES.filter(c=>c!=="Hifz Only").concat(["Hifz Only"]);
  const PORTAL_SUBJECTS = ["Quran Nazra","Quran Hifz","Urdu","English","Mathematics","Science","Social Studies","Islamiat","Computer","General Knowledge","Tajweed","Memorization","Revision","Nazra","Manzil"];

  const blankParent  = {email:"",password:"QKA@1234",student_id:""};
  const blankTeacher = {staff_id:"",email:"",password:"QKA@1234",name:"",classes:[],subjects:[],is_hifz_teacher:false};
  const blankAdmin   = {email:"",password:"",role:"admin",name:""};
  const blankStaff   = {email:"",password:"QKA@1234",name:"",role:"facility_manager"};
  const [form, setForm] = React.useState(blankParent);
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  React.useEffect(()=>{ load(); },[]);

  const [adminAccounts, setAdminAccounts] = React.useState([]); // role records

  const load = async () => {
    setLoading(true);
    const [pRes,tRes,sRes,aRes] = await Promise.all([
      supabase.from("parents").select("*").order("created_at",{ascending:false}),
      supabase.from("teacher_accounts").select("*").order("created_at",{ascending:false}),
      supabase.from("staff").select("id,name,role,subject,email").order("name"),
      supabase.from("admin_accounts").select("*").order("created_at",{ascending:false}),
    ]);
    setAdminAccounts(aRes.data||[]);
    setParents(pRes.data||[]);
    setTeachers(tRes.data||[]);
    setStaff(sRes.data||[]);
    setLoading(false);
    // Also fetch auth users for admin section
    fetchAuthUsers();
  };

  const fetchAuthUsers = async () => {
    setAuthLoading(true);
    try {
      const URL = process.env.REACT_APP_SUPABASE_URL||"";
      const KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY||"";
      const res = await fetch(`${URL}/auth/v1/admin/users`,{
        headers:{"apikey":KEY,"Authorization":`Bearer ${KEY}`}
      });
      if(!res.ok){ console.warn("Auth users fetch failed:",res.status); setAuthLoading(false); return; }
      const data = await res.json();
      // Supabase returns { users: [...] } or directly an array depending on version
      const users = Array.isArray(data) ? data : (data?.users||[]);
      setAuthUsers(users);
    } catch(e) { console.warn("Could not fetch auth users:",e.message); }
    setAuthLoading(false);
  };

  // ── Shared: create Supabase Auth user ──────────────────────────
  const createAuthUser = async (email, password) => {
    try {
      const URL = process.env.REACT_APP_SUPABASE_URL||"";
      const KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY||"";
      const res = await fetch(`${URL}/auth/v1/admin/users`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":KEY,"Authorization":`Bearer ${KEY}`},
        body:JSON.stringify({email,password,email_confirm:true})
      });
      const data = await res.json();
      if(res.ok&&data.id) return {ok:true};
      return {ok:false,err:data.message||data.error_description||JSON.stringify(data)};
    } catch(e) { return {ok:false,err:e.message}; }
  };

  const deleteAuthUser = async (email) => {
    try {
      const URL = process.env.REACT_APP_SUPABASE_URL||"";
      const KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY||"";
      const res = await fetch(`${URL}/auth/v1/admin/users?email=${encodeURIComponent(email)}`,{headers:{"apikey":KEY,"Authorization":`Bearer ${KEY}`}});
      const data = await res.json();
      const uid = data?.users?.[0]?.id;
      if(uid) await fetch(`${URL}/auth/v1/admin/users/${uid}`,{method:"DELETE",headers:{"apikey":KEY,"Authorization":`Bearer ${KEY}`}});
    } catch(e) { console.warn("Auth delete failed:",e.message); }
  };

  const resetPassword = async (email) => {
    const newPass = window.prompt(`New password for ${email} (min 6 chars):`);
    if(!newPass||newPass.length<6) return;
    try {
      const URL = process.env.REACT_APP_SUPABASE_URL||"";
      const KEY = process.env.REACT_APP_SUPABASE_SERVICE_KEY||"";
      const res = await fetch(`${URL}/auth/v1/admin/users?email=${encodeURIComponent(email)}`,{headers:{"apikey":KEY,"Authorization":`Bearer ${KEY}`}});
      const data = await res.json();
      const uid = data?.users?.[0]?.id;
      if(!uid){alert("User not found in Auth. Reset manually in Supabase dashboard.");return;}
      const r2 = await fetch(`${URL}/auth/v1/admin/users/${uid}`,{
        method:"PUT",
        headers:{"Content-Type":"application/json","apikey":KEY,"Authorization":`Bearer ${KEY}`},
        body:JSON.stringify({password:newPass})
      });
      if(r2.ok) showToast(`Password reset for ${email}`);
      else alert("Reset failed: "+(await r2.text()));
    } catch(e){alert("Reset failed: "+e.message);}
  };

  // ── Save: Parent ───────────────────────────────────────────────
  const saveParent = async () => {
    if(!form.email||!form.password||!form.student_id){setError("All fields are required.");return;}
    setSaving(true);
    const stu = students.find(s=>s.id===form.student_id);
    const auth = await createAuthUser(form.email,form.password);
    const {error:dbErr} = await supabase.from("parents").insert({
      email:form.email, student_id:form.student_id, student_name:stu?.full_name||stu?.name||""
    });
    setSaving(false);
    if(dbErr){setError("DB Error: "+dbErr.message);return;}
    if(!auth.ok) setSaved({email:form.email,password:form.password,authErr:auth.err});
    else showToast("Parent account created!");
    setShowForm(false); setForm(blankParent); load();
  };

  // ── Save: Teacher ──────────────────────────────────────────────
  const saveTeacher = async () => {
    if(!form.email||!form.password||!form.name){setError("Name, email and password are required.");return;}
    setSaving(true);
    const auth = await createAuthUser(form.email,form.password);
    const {error:dbErr} = await supabase.from("teacher_accounts").insert({
      email:form.email, name:form.name,
      is_hifz_teacher:form.is_hifz_teacher||false,
      staff_id:form.staff_id||null,
    });
    setSaving(false);
    if(dbErr){setError("DB Error: "+dbErr.message);return;}
    if(!auth.ok) setSaved({email:form.email,password:form.password,authErr:auth.err});
    else showToast("Teacher account created!");
    setShowForm(false); setForm(blankTeacher); load();
  };

  // ── Remove ─────────────────────────────────────────────────────
  const remove = async (table, id, email) => {
    if(!window.confirm(`Remove ${email}? This removes login access.`)) return;
    await deleteAuthUser(email);
    await supabase.from(table).delete().eq("id",id);
    showToast("Removed.");
    load();
  };

  const sectionForms = {
    parent: (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>LINK TO STUDENT *</label>
          <select style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}
            value={form.student_id} onChange={e=>{
              const stu=students.find(s=>s.id===e.target.value);
              if(stu){
                const name=(stu.full_name||stu.name||"").trim().split(" ");
                const email=`${(name[0]||"").toLowerCase().replace(/[^a-z]/g,"")}.${(name[1]||"").toLowerCase().replace(/[^a-z]/g,"")}@qkacademy.org`.replace(/\.@/,"@");
                setForm(p=>({...p,student_id:e.target.value,email,password:"QKA@1234"}));
              } else setF("student_id",e.target.value);
            }}>
            <option value="">-- Select Student --</option>
            {students.map(s=><option key={s.id} value={s.id}>{s.full_name||s.name} — {s.class||s["class"]} ({s.id})</option>)}
          </select>
        </div>
        {[["EMAIL *","email","email","parent@gmail.com"],["TEMP PASSWORD *","password","text","Min 6 chars"]].map(([lbl,k,t,ph])=>(
          <div key={k}>
            <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{lbl}</label>
            <input style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}
              type={t} value={form[k]||""} onChange={e=>setF(k,e.target.value)} placeholder={ph}/>
          </div>
        ))}
      </div>
    ),
    teacher: (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* Select from staff */}
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:6}}>SELECT STAFF MEMBER *</label>
          <select value={form.staff_id||""} onChange={e=>{
            const sid = e.target.value;
            if(!sid){ setF("staff_id",""); return; }
            const s = staff.find(x=>x.id===sid);
            if(s){
              const email = s.email || genStaffEmail(s.name||"");
              setForm(p=>({...p, staff_id:sid, name:s.name||"", email}));
            }
          }}
            style={{width:"100%",padding:"10px 12px",border:"2px solid #ddd6fe",borderRadius:9,fontSize:14,boxSizing:"border-box",fontWeight:600}}>
            <option value="">-- Select from Staff Register --</option>
            {staff.length===0&&<option disabled>No staff found — add staff first</option>}
            <optgroup label="Teachers">
              {staff.filter(s=>(s.role||"").toLowerCase().includes("teach")||(s.role||"").toLowerCase().includes("hifz")).map(s=>(
                <option key={s.id} value={s.id}>{s.name} {s.role?`(${s.role})`:""} {s.email?`· ${s.email}`:""}</option>
              ))}
            </optgroup>
            <optgroup label="Other Staff">
              {staff.filter(s=>!((s.role||"").toLowerCase().includes("teach")||(s.role||"").toLowerCase().includes("hifz"))).map(s=>(
                <option key={s.id} value={s.id}>{s.name} {s.role?`(${s.role})`:""}</option>
              ))}
            </optgroup>
          </select>
          <div style={{fontSize:11,color:"#6b7280",marginTop:4}}>
            Name and email auto-fill from staff record. Classes & subjects are pulled automatically from the Timetable.
          </div>
        </div>

        {/* Email + password */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {[["EMAIL *","email","email","auto-filled from staff"],["TEMP PASSWORD *","password","text","Min 6 chars"]].map(([lbl,k,t,ph])=>(
            <div key={k}>
              <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{lbl}</label>
              <input style={{width:"100%",padding:"10px 12px",border:"1.5px solid #ddd6fe",borderRadius:8,fontSize:13,boxSizing:"border-box",background:k==="email"&&form.email?"#f5f3ff":"white"}}
                type={t} value={form[k]||""} onChange={e=>setF(k,e.target.value)} placeholder={ph}/>
            </div>
          ))}
        </div>

        {/* Info box explaining timetable-driven access */}
        <div style={{background:"#eff6ff",borderRadius:10,padding:"12px 16px",border:"1px solid #bfdbfe",fontSize:12,color:"#1e40af"}}>
          <div style={{fontWeight:700,marginBottom:4}}>📅 How class access works:</div>
          <div>When this teacher logs in, the portal automatically reads the <strong>Timetable</strong> to see which classes and subjects they're assigned to. Update the timetable → teacher sees the change immediately. No manual updates needed here.</div>
        </div>

        {/* Hifz toggle */}
        <button type="button" onClick={()=>setF("is_hifz_teacher",!form.is_hifz_teacher)}
          style={{display:"flex",alignItems:"center",gap:10,background:form.is_hifz_teacher?"#f5f3ff":"white",
            border:`2px solid ${form.is_hifz_teacher?"#7c3aed":"#ddd6fe"}`,
            borderRadius:10,padding:"12px 16px",cursor:"pointer",textAlign:"left",width:"100%"}}>
          <div style={{width:22,height:22,borderRadius:5,background:form.is_hifz_teacher?"#7c3aed":"white",
            border:`2px solid ${form.is_hifz_teacher?"#7c3aed":"#d1d5db"}`,
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {form.is_hifz_teacher&&<span style={{color:"white",fontSize:14,fontWeight:900}}>✓</span>}
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#4c1d95"}}>📖 Hifz Teacher</div>
            <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>Gives access to the Hifz Daily Log tab (Hifz sessions aren't in the timetable)</div>
          </div>
        </button>
      </div>
    ),
    admin: (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {[["FULL NAME *","name","text","e.g. Muhammad Ali"],["EMAIL *","email","email","admin@school.org"],["TEMP PASSWORD *","password","text","Min 8 chars"]].map(([lbl,k,t,ph])=>(
            <div key={k} style={{gridColumn:k==="name"?"1/-1":undefined}}>
              <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{lbl}</label>
              <input style={{width:"100%",padding:"10px 12px",border:"1.5px solid #bfdbfe",borderRadius:8,fontSize:13,boxSizing:"border-box"}}
                type={t} value={form[k]||""} onChange={e=>setF(k,e.target.value)} placeholder={ph}/>
            </div>
          ))}
        </div>
        <div style={{background:"#eff6ff",border:"1.5px solid #bfdbfe",borderRadius:10,padding:"14px 16px",fontSize:12,color:"#1d4ed8",display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:20,flexShrink:0}}>🔑</span>
          <div>
            <div style={{fontWeight:700,marginBottom:3,fontSize:13}}>System Administrator — Full Access</div>
            <div>This account has unrestricted access to <strong>all portal features</strong> including Settings, Portal Access Management, and all student/staff data. Only assign this to trusted IT administrators.</div>
          </div>
        </div>
      </div>
    ),
    staff: (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {[["FULL NAME *","name","text","e.g. Ahmed Khan"],["EMAIL *","email","email","auto-filled from name"],["TEMP PASSWORD *","password","text","Min 6 chars"]].map(([lbl,k,t,ph])=>(
            <div key={k} style={{gridColumn:k==="name"?"1/-1":undefined}}>
              <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{lbl}</label>
              <input style={{width:"100%",padding:"10px 12px",border:"1.5px solid #fcd34d",borderRadius:8,fontSize:13,boxSizing:"border-box",background:k==="email"&&form.email?"#fffbeb":"white"}}
                type={t} value={form[k]||""} onChange={e=>{
                  const val=e.target.value;
                  if(k==="name") setForm(p=>({...p,name:val,email:genStaffEmail(val)}));
                  else setF(k,val);
                }} placeholder={ph}/>
            </div>
          ))}
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:6}}>DESIGNATION & ACCESS LEVEL *</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[
              {v:"principal",       icon:"🎩",label:"Principal",        desc:"Full school access — all tabs & reports"},
              {v:"vice_principal",  icon:"👔",label:"Vice Principal",   desc:"Full access except Settings & Portal Management"},
              {v:"accountant",      icon:"💼",label:"Accountant",       desc:"Finance only — Fees, Payroll & Export"},
              {v:"facility_manager",icon:"🏗️",label:"Facility Manager", desc:"Facility inspections & maintenance only"},
            ].map(r=>(
              <button key={r.v} type="button" onClick={()=>setF("role",r.v)}
                style={{display:"flex",flexDirection:"column",gap:4,padding:"10px 14px",borderRadius:10,cursor:"pointer",textAlign:"left",
                  border:`2px solid ${form.role===r.v?"#b45309":"#fcd34d"}`,
                  background:form.role===r.v?"#fffbeb":"white",transition:"all .15s"}}>
                <div style={{fontWeight:700,fontSize:13,color:form.role===r.v?"#92400e":"#111827"}}>{r.icon} {r.label}</div>
                <div style={{fontSize:11,color:"#6b7280"}}>{r.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    ),
  };

  const RC = ROLE_COLORS[section] || {bg:"#fffbeb",border:"#fef3c7",badge:"#fef3c7",text:"#92400e",col:"#b45309",icon:"🏗️"};

  const SECTIONS = [
    {id:"parent",  label:"Parents",  singular:"Parent",       icon:"👪", col:G, border:"#bbf7d0", badge:"#dcfce7", badgeText:"#15803d"},
    {id:"teacher", label:"Teachers", singular:"Teacher",      icon:"🎓", col:P, border:"#ddd6fe", badge:"#ede9fe", badgeText:"#5b21b6"},
    {id:"admin",   label:"Admins",   singular:"Admin",        icon:"🔑", col:B, border:"#bfdbfe", badge:"#dbeafe", badgeText:"#1d4ed8"},
    {id:"staff",   label:"Staff",    singular:"Staff Member", icon:"🏗️", col:"#b45309", border:"#fef3c7", badge:"#fef3c7", badgeText:"#92400e"},
  ];

  return (
    <div style={{padding:24,display:"flex",flexDirection:"column",gap:20}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Manual auth fallback banner */}
      {saved&&(
        <div style={{background:"#fef9c3",border:"1.5px solid #d97706",borderRadius:14,padding:"18px 22px"}}>
          <div style={{fontWeight:800,color:"#92400e",fontSize:15,marginBottom:8}}>⚠️ Manual Step Required</div>
          <p style={{fontSize:13,color:"#92400e",margin:"0 0 10px",lineHeight:1.6}}>
            Record saved ✅ but auth account could not be auto-created{saved.authErr?` (${saved.authErr})`:""}.
            Create manually in Supabase Auth → Users:
          </p>
          {[["Email",saved.email],["Password",saved.password]].map(([l,v])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:10,background:"white",borderRadius:8,padding:"10px 14px",border:"1px solid #fcd34d",marginBottom:8}}>
              <span style={{fontSize:12,fontWeight:700,color:"#92400e",minWidth:70}}>{l}:</span>
              <span style={{fontSize:13,fontWeight:800,color:"#111827",flex:1}}>{v}</span>
              <button onClick={()=>navigator.clipboard.writeText(v)}
                style={{background:"#d97706",color:"white",border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Copy</button>
            </div>
          ))}
          <div style={{display:"flex",gap:10,marginTop:8}}>
            <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer"
              style={{background:"#1a1a1a",color:"white",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:13,textDecoration:"none"}}>Open Supabase →</a>
            <button onClick={()=>setSaved(null)} style={{background:"none",border:"none",color:"#9ca3af",fontSize:12,cursor:"pointer"}}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:"#111827"}}>🔐 Portal Access Management</div>
          <div style={{fontSize:13,color:"#6b7280",marginTop:2}}>Create and manage login accounts for parents, teachers, and admins</div>
        </div>
        {!showForm&&(
          <button onClick={()=>{setShowForm(true);setError("");
            setForm(section==="parent"?blankParent:section==="teacher"?blankTeacher:section==="staff"?blankStaff:blankAdmin);}}
            style={{background:RC.col||G,color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
            + Add {SECTIONS.find(s=>s.id===section)?.singular||"Account"}
          </button>
        )}
      </div>

      {/* 3-tab switcher */}
      <div style={{display:"flex",gap:0,background:"#f3f4f6",borderRadius:12,padding:4,alignSelf:"flex-start",flexWrap:"wrap",gap:4}}>
        {SECTIONS.map(s=>{
          const count = s.id==="parent"?parents.length:s.id==="teacher"?teachers.length:s.id==="staff"?adminAccounts.filter(a=>["facility_manager","accountant","vice_principal","principal"].includes(a.role)).length:authUsers.filter(u=>!parents.find(p=>p.email===u.email)&&!teachers.find(t=>t.email===u.email)).length;
          return (
            <button key={s.id} onClick={()=>{setSection(s.id);setShowForm(false);setError("");}}
              style={{
                background:section===s.id?"white":"transparent",
                color:section===s.id?s.col:"#6b7280",
                border:"none",borderRadius:9,padding:"8px 18px",
                fontWeight:section===s.id?800:600,fontSize:13,cursor:"pointer",
                boxShadow:section===s.id?"0 1px 6px rgba(0,0,0,0.10)":"none",
                transition:"all .15s",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6,
              }}>
              {s.icon} {s.label}
              {count>0&&<span style={{background:s.badge,color:s.badgeText,borderRadius:10,padding:"1px 7px",fontSize:11,fontWeight:700}}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Add form */}
      {showForm&&(
        <div style={{background:"white",borderRadius:16,border:`1.5px solid ${RC.border||"#ddd"}`,padding:24,boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
          <div style={{fontWeight:800,fontSize:16,color:RC.col||G,marginBottom:16}}>
            {SECTIONS.find(s=>s.id===section)?.icon} New {SECTIONS.find(s=>s.id===section)?.label.slice(0,-1)} Account
          </div>
          {error&&<div style={{background:"#fef2f2",color:"#dc2626",borderRadius:8,padding:"10px 14px",fontSize:13,marginBottom:14,fontWeight:600}}>{error}</div>}
          {sectionForms[section]}
          <div style={{background:"#f0fdf4",borderRadius:10,padding:"10px 14px",marginTop:14,fontSize:12,color:"#166534",border:"1px solid #bbf7d0"}}>
            ✅ Clicking Save will automatically create the Supabase login. If auto-creation fails, credentials appear for manual entry.
          </div>
          <div style={{display:"flex",gap:10,marginTop:18}}>
            <button onClick={section==="parent"?saveParent:section==="teacher"?saveTeacher:async()=>{
              if(!form.email||!form.password){setError("Email and password required.");return;}
              if(section==="staff"&&!form.role){setError("Please select a staff role.");return;}
              setSaving(true);
              const auth=await createAuthUser(form.email,form.password);
              await supabase.from("admin_accounts").upsert({
                email:form.email, name:form.name||"", role:form.role||(section==="staff"?"facility_manager":"admin")
              },{onConflict:"email"});
              setSaving(false);
              if(auth.ok){showToast(`${section==="staff"?"Staff":"Admin"} account created!`);load();}
              else setSaved({email:form.email,password:form.password,authErr:auth.err});
              setShowForm(false);
            }} disabled={saving}
              style={{background:saving?"#9ca3af":RC.col||G,color:"white",border:"none",borderRadius:9,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer",opacity:saving?0.7:1}}>
              {saving?"Saving...":"Save Account"}
            </button>
            <button onClick={()=>{setShowForm(false);setError("");}}
              style={{background:"#f3f4f6",color:"#374151",border:"1px solid #d1d5db",borderRadius:9,padding:"11px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Lists — per active tab */}
      {loading?<div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Loading...</div>:(
        <>
          {/* Parents list */}
          {section==="parent"&&(
            parents.length===0?(
              <div style={{textAlign:"center",padding:60,color:"#9ca3af",background:"white",borderRadius:14,border:"1px solid #e5e7eb"}}>
                <div style={{fontSize:44,marginBottom:10}}>👪</div>
                <div style={{fontSize:15,fontWeight:700}}>No parent accounts yet</div>
                <div style={{fontSize:13,marginTop:6}}>Click "+ Add Parent" or use Batch Creation below</div>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {parents.map(p=>{
                  const stu=students.find(s=>s.id===p.student_id);
                  return (
                    <div key={p.id} style={{background:"white",borderRadius:12,border:"1px solid #bbf7d0",padding:"14px 20px",display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
                      <div style={{width:40,height:40,borderRadius:"50%",background:"#f0fdf4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>👤</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{p.email}</div>
                        <div style={{fontSize:12,color:"#6b7280",marginTop:2,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                          <span style={{background:"#dcfce7",color:"#15803d",borderRadius:5,padding:"1px 8px",fontWeight:700,fontSize:11}}>Parent</span>
                          <span>Student: <strong style={{color:G}}>{p.student_name||stu?.full_name||"—"}</strong></span>
                          {stu&&<span style={{color:"#9ca3af"}}>{stu.class||stu["class"]} · {p.student_id}</span>}
                          {p.created_at&&<span style={{color:"#9ca3af"}}>{new Date(p.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={()=>resetPassword(p.email)} style={{background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🔑 Reset</button>
                        <button onClick={()=>remove("parents",p.id,p.email)} style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Remove</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Teachers list */}
          {section==="teacher"&&(
            teachers.length===0?(
              <div style={{textAlign:"center",padding:60,color:"#9ca3af",background:"white",borderRadius:14,border:"1px solid #e5e7eb"}}>
                <div style={{fontSize:44,marginBottom:10}}>🎓</div>
                <div style={{fontSize:15,fontWeight:700}}>No teacher accounts yet</div>
              </div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {teachers.map(t=>{
                  const authUT = authUsers.find(u=>u.email===t.email);
                  return (
                  <div key={t.id} style={{background:"white",borderRadius:12,border:"1px solid #ddd6fe",padding:"14px 20px",display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
                    <div style={{width:40,height:40,borderRadius:"50%",background:"#f5f3ff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🎓</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{t.name}</div>
                      <div style={{fontSize:12,color:"#6b7280",marginTop:3,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                        <span style={{background:"#ede9fe",color:"#5b21b6",borderRadius:5,padding:"1px 8px",fontWeight:700,fontSize:11}}>Teacher</span>
                        <span>{t.email}</span>
                        {t.is_hifz_teacher&&<span style={{background:"#fef9c3",color:"#92400e",borderRadius:5,padding:"1px 8px",fontWeight:700,fontSize:11}}>📖 Hifz</span>}
                        {(t.classes||[]).map(c=><span key={c} style={{background:"#f0fdf4",color:"#15803d",borderRadius:5,padding:"1px 8px",fontWeight:600,fontSize:11}}>{c}</span>)}
                        {(t.subjects||[]).map(s=><span key={s} style={{background:"#f5f3ff",color:"#5b21b6",borderRadius:5,padding:"1px 8px",fontWeight:600,fontSize:11}}>{s}</span>)}
                        {(t.created_at||authUT?.created_at)&&<span style={{color:"#9ca3af"}}>Joined: {new Date(t.created_at||authUT.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                        {authUT?.last_sign_in_at&&<span style={{color:"#9ca3af"}}>Last login: {new Date(authUT.last_sign_in_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>resetPassword(t.email)} style={{background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🔑 Reset</button>
                      <button onClick={()=>remove("teacher_accounts",t.id,t.email)} style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Remove</button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )
          )}

          {/* Admins list — fetched from Supabase Auth */}
          {section==="admin"&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {/* How roles work */}
              <div style={{background:"#eff6ff",borderRadius:12,padding:"12px 16px",border:"1px solid #bfdbfe",display:"flex",gap:16,flexWrap:"wrap",fontSize:12,color:"#1e40af"}}>
                <span>👪 <strong>Parent</strong> → parents table</span>
                <span>🎓 <strong>Teacher</strong> → teacher_accounts table</span>
                <span>🔑 <strong>Admin</strong> → any other auth user</span>
                <button onClick={fetchAuthUsers} disabled={authLoading}
                  style={{marginLeft:"auto",background:"#1d4ed8",color:"white",border:"none",borderRadius:7,padding:"4px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  {authLoading?"Loading...":"↻ Refresh"}
                </button>
              </div>
              {authLoading&&!authUsers.length?(
                <div style={{textAlign:"center",padding:32,color:"#6b7280",background:"white",borderRadius:12,border:"1px dashed #bfdbfe"}}>
                  Loading users from Supabase Auth...
                </div>
              ):(()=>{
                const admins=authUsers.filter(u=>!parents.find(p=>p.email===u.email)&&!teachers.find(t=>t.email===u.email));
                return admins.length===0?(
                  <div style={{textAlign:"center",padding:40,color:"#9ca3af",background:"white",borderRadius:14,border:"1px solid #e5e7eb"}}>
                    <div style={{fontSize:44,marginBottom:10}}>🔑</div>
                    <div style={{fontSize:15,fontWeight:700}}>No admin users found</div>
                    <div style={{fontSize:12,marginTop:6,color:"#9ca3af"}}>
                      {authUsers.length===0
                        ?"Click ↻ Refresh above. If still empty, ensure your Supabase service_role key is configured."
                        :"All auth users are assigned as Parents or Teachers."}
                    </div>
                  </div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {admins.map(u=>(
                      <div key={u.id} style={{background:"white",borderRadius:12,border:"1px solid #bfdbfe",padding:"14px 20px",display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
                        <div style={{width:40,height:40,borderRadius:"50%",background:"#eff6ff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🔑</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{u.email}</div>
                          <div style={{fontSize:12,color:"#6b7280",marginTop:2,display:"flex",gap:10,flexWrap:"wrap"}}>
                            {(()=>{
                            const rec = adminAccounts.find(a=>a.email===u.email);
                            const ROLE_BADGES = {admin:{bg:"#dbeafe",col:"#1d4ed8",icon:"🔑",lbl:"Admin"},principal:{bg:"#f0fdf4",col:"#15803d",icon:"🏫",lbl:"Principal"},vice_principal:{bg:"#f5f3ff",col:"#6d28d9",icon:"👔",lbl:"Vice Principal"},accountant:{bg:"#fffbeb",col:"#92400e",icon:"💼",lbl:"Accountant"}};
                            const b = ROLE_BADGES[rec?.role]||ROLE_BADGES.admin;
                            return <span style={{background:b.bg,color:b.col,borderRadius:5,padding:"1px 8px",fontWeight:700,fontSize:11}}>{b.icon} {b.lbl}</span>;
                          })()}
                            {u.created_at&&<span>Joined: {new Date(u.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                            {u.last_sign_in_at&&<span>Last login: {new Date(u.last_sign_in_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:6}}>
                          <button onClick={()=>resetPassword(u.email)} style={{background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🔑 Reset</button>
                          <button onClick={async()=>{
                            if(!window.confirm(`Remove admin access for ${u.email}?`)) return;
                            await deleteAuthUser(u.email);
                            showToast("Admin removed.");
                            fetchAuthUsers();
                          }} style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
    
      )}
      {/* ── STAFF ROLES ── */}
      {section==="staff"&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{background:"#fffbeb",borderRadius:12,padding:"12px 16px",border:"1px solid #fcd34d",fontSize:12,color:"#92400e"}}>
            <strong>🏗️ Staff Portal Roles</strong> — Facility Manager, Accountant, Vice Principal, Principal. Each gets a dedicated portal login with role-based access.
          </div>
          {adminAccounts.filter(a=>["facility_manager","accountant","vice_principal","principal"].includes(a.role)).length===0?(
            <div style={{textAlign:"center",padding:40,color:"#9ca3af",background:"white",borderRadius:14,border:"1px solid #e5e7eb"}}>
              <div style={{fontSize:44,marginBottom:10}}>🏗️</div>
              <div style={{fontSize:15,fontWeight:700}}>No staff accounts yet</div>
              <div style={{fontSize:12,marginTop:6}}>Click "+ Add Staff" above to create portal access for Facility Manager, Accountant, etc.</div>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {adminAccounts.filter(a=>["facility_manager","accountant","vice_principal","principal"].includes(a.role)).map(acc=>{
                const STAFF_BADGES = {
                  facility_manager:{bg:"#fef3c7",col:"#92400e",icon:"🏗️",lbl:"Facility Manager"},
                  accountant:      {bg:"#fed7aa",col:"#c2410c",icon:"💼",lbl:"Accountant"},
                  vice_principal:  {bg:"#bae6fd",col:"#0369a1",icon:"👔",lbl:"Vice Principal"},
                  principal:       {bg:"#e9d5ff",col:"#7e22ce",icon:"🏫",lbl:"Principal"},
                };
                const b = STAFF_BADGES[acc.role]||{bg:"#f3f4f6",col:"#374151",icon:"👤",lbl:acc.role};
                const authU = authUsers.find(u=>u.email===acc.email);
                return (
                  <div key={acc.id} style={{background:"white",borderRadius:12,border:"1px solid #fcd34d",padding:"14px 20px",display:"flex",gap:14,alignItems:"center",flexWrap:"wrap",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                    <div style={{width:40,height:40,borderRadius:"50%",background:b.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{b.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{acc.name||acc.email}</div>
                      <div style={{fontSize:12,color:"#6b7280",marginTop:2,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                        <span style={{background:b.bg,color:b.col,borderRadius:5,padding:"1px 8px",fontWeight:700,fontSize:11}}>{b.icon} {b.lbl}</span>
                        <span>{acc.email}</span>
                        {(acc.created_at||authU?.created_at)&&<span>Joined: {new Date(acc.created_at||authU.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                        {authU?.last_sign_in_at&&<span>Last login: {new Date(authU.last_sign_in_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>resetPassword(acc.email)} style={{background:"#fffbeb",color:"#92400e",border:"1px solid #fcd34d",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🔑 Reset</button>
                      <button onClick={async()=>{
                        if(!window.confirm(`Remove portal access for ${acc.name||acc.email}?`)) return;
                        await deleteAuthUser(acc.email);
                        await supabase.from("admin_accounts").delete().eq("id",acc.id);
                        showToast("Staff account removed.");
                        load();
                      }} style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"6px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>Remove</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* ── Batch Creation (Parents only — for all students) ── */}
      {section==="parent"&&<BatchParentCreation students={students} parents={parents} onDone={load} showToast={showToast}/>}
    </div>
  );
}


function HifzParentTab({ hifzProgress, hifzJourney, hifzLogs, weeklyReports=[] }) {
  const JUZ_PP = 20; // pages per Juz (15-line Quran)
  const now = new Date();

  // ── Progress source: hifz_journey.juz_completed (most accurate) ──
  const juzCompleted = hifzJourney?.juz_completed ?? (hifzProgress?.current_juz ? hifzProgress.current_juz - 1 : 0);
  const currentJuz   = hifzProgress?.current_juz || (juzCompleted + 1);
  const juzData      = QURAN_JUZ.find(j=>j.n===currentJuz) || QURAN_JUZ[0];
  const overallPct   = Math.min(100, Math.round((juzCompleted / 30) * 100));
  const isHafiz      = juzCompleted >= 30;

  // ── Stats ──
  const [selLog,       setSelLog]       = React.useState(null);
  const [expandedStat, setExpandedStat] = React.useState(null); // interactive stat cards
  const totalSessions = hifzLogs.length;
  const presentDays   = hifzLogs.filter(l => l.was_present !== false).length;
  const absentDays    = hifzLogs.filter(l => l.was_present === false).length;
  const attPct        = totalSessions > 0 ? Math.round((presentDays / totalSessions) * 100) : 0;

  // ── This month ──
  const monthStart      = new Date(now.getFullYear(), now.getMonth(), 1);
  const logsThisMonth   = hifzLogs.filter(l => new Date(l.date) >= monthStart);
  const monthPresent    = logsThisMonth.filter(l => l.was_present !== false).length;
  const monthAbsent     = logsThisMonth.filter(l => l.was_present === false).length;
  const sumP = (arr, k) => Math.round(arr.reduce((s, l) => s + (Number(l[k]) || 0), 0) * 10) / 10;
  const monthSabaq  = sumP(logsThisMonth, "sabaq_pages");
  const monthSabqi  = sumP(logsThisMonth, "sabqi_pages");
  const monthManzil = sumP(logsThisMonth, "manzil_pages");

  // ── Helper: pages → "X صفحات ≡ Y.Z جز" ──
  const pagesDisplay = (pages) => {
    if (!pages || pages <= 0) return null;
    const juzEq = (pages / JUZ_PP).toFixed(1);
    return { pages, juzEq };
  };

  // ── Last 14 days grid ──
  const last14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(now); d.setDate(now.getDate() - 13 + i);
    const ds = d.toISOString().split("T")[0];
    const log = hifzLogs.find(l => l.date === ds);
    return { ds, log, dayNum: d.getDate(), dayName: d.toLocaleDateString("en-GB", { weekday: "short" }) };
  });

  // ── Quran progress segment markers ──
  const segments = [5, 10, 15, 20, 25, 30];

  // ── Beautiful page+juz row component ──
  const PagesJuzRow = ({ pages, color, bg, label, arabicLabel }) => {
    const d = pagesDisplay(pages);
    if (!d) return <div style={{color:"#9ca3af",fontSize:13,fontStyle:"italic"}}>—</div>;
    return (
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <span style={{fontSize:22,fontWeight:900,color,letterSpacing:-0.5}}>{d.pages}</span>
        <span style={{fontSize:13,color,fontWeight:600,opacity:.7}}>صفحات</span>
        <span style={{fontSize:20,color,fontWeight:300,opacity:.5,margin:"0 2px"}}>≡</span>
        <span style={{fontSize:22,fontWeight:900,color}}>{d.juzEq}</span>
        <span style={{fontSize:13,color,fontWeight:600,opacity:.7}}>جز</span>
      </div>
    );
  };

  const P = "#4c1d95"; // purple
  const PL = "#7c3aed";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* ── Quran Kareem Full Header ── */}
      <div style={{background:"linear-gradient(160deg,#0a0a1a 0%,#1a0a3a 40%,#2d1060 100%)",borderRadius:20,overflow:"hidden",boxShadow:"0 8px 40px rgba(76,29,149,0.4)"}}>
        {/* Gold decorative top band */}
        <div style={{height:4,background:"linear-gradient(90deg,transparent,#d4af37,#f5d76e,#d4af37,transparent)"}}/>
        {/* Main content */}
        <div style={{padding:"22px 24px",textAlign:"center",position:"relative"}}>
          {/* Decorative corners */}
          <div style={{position:"absolute",top:12,left:14,fontSize:18,opacity:.4,color:"#d4af37"}}>❖</div>
          <div style={{position:"absolute",top:12,right:14,fontSize:18,opacity:.4,color:"#d4af37"}}>❖</div>
          {/* Bismillah */}
          <div style={{fontFamily:"'Noto Nastaliq Urdu', serif",fontSize:28,color:"#f5d76e",letterSpacing:2,marginBottom:10,lineHeight:1.6,direction:"rtl",textShadow:"0 2px 12px rgba(245,215,110,0.4)"}}>
            بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
          </div>
          {/* Surah Al-Qamar reference */}
          <div style={{fontSize:11,color:"rgba(245,215,110,0.7)",letterSpacing:1,textTransform:"uppercase",marginBottom:14,paddingTop:2,borderTop:"1px solid rgba(245,215,110,0.1)"}}>
            وَلَقَدْ يَسَّرْنَا ٱلْقُرْءَانَ لِلذِّكْرِ — Al-Qamar 54:17
          </div>
          {/* Progress headline */}
          <div style={{color:"white",fontSize:14,fontWeight:700,marginBottom:16,opacity:.9}}>
            {isHafiz ? "🎉 ماشاءاللہ — Hafiz-e-Quran!" : `حفظ قرآن کریم — ${juzCompleted}/30 Juz Completed`}
          </div>

          {/* Progress bar with Juz markers */}
          <div style={{position:"relative",marginBottom:8}}>
            {/* Juz markers */}
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              {segments.map(s => (
                <div key={s} style={{display:"flex",flexDirection:"column",alignItems:"center",width:20}}>
                  <div style={{fontSize:9,color:juzCompleted>=s?"#f5d76e":"rgba(255,255,255,0.3)",fontWeight:700,marginBottom:2}}>{s}</div>
                  <div style={{width:1,height:6,background:juzCompleted>=s?"#f5d76e":"rgba(255,255,255,0.2)"}}/>
                </div>
              ))}
            </div>
            {/* Bar */}
            <div style={{height:14,background:"rgba(255,255,255,0.1)",borderRadius:7,overflow:"hidden",border:"1px solid rgba(255,255,255,0.15)"}}>
              <div style={{
                width:`${overallPct}%`,height:"100%",borderRadius:7,transition:"width 1s ease",
                background:"linear-gradient(90deg,#d4af37,#f5d76e,#d4af37)",
                boxShadow:"0 0 12px rgba(245,215,110,0.6)"
              }}/>
            </div>
            {/* Percentage label */}
            <div style={{marginTop:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.6)"}}>0%</span>
              <span style={{fontSize:13,fontWeight:800,color:"#f5d76e"}}>{overallPct}% — {juzCompleted} of 30 Juz</span>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.6)"}}>100%</span>
            </div>
          </div>

          {/* Current Juz badge */}
          {!isHafiz && (
            <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.1)",borderRadius:20,padding:"8px 18px",border:"1px solid rgba(245,215,110,0.3)",marginTop:4}}>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.7)"}}>Currently on</span>
              <span style={{background:"#f5d76e",color:"#1a0a3a",borderRadius:12,padding:"3px 12px",fontSize:13,fontWeight:900}}>پارہ {currentJuz} — {juzData.ar}</span>
              {hifzProgress?.current_surah && <span style={{fontSize:12,color:"rgba(255,255,255,0.8)",fontWeight:600}}>{hifzProgress.current_surah}</span>}
            </div>
          )}
        </div>
        <div style={{height:4,background:"linear-gradient(90deg,transparent,#d4af37,#f5d76e,#d4af37,transparent)"}}/>
      </div>

      {/* ── Interactive Stat Cards ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
        {[
          {id:"sessions", lbl:"Sessions",    val:totalSessions,         color:P,       bg:"#f5f3ff",
           detail: `${totalSessions} total Hifz sessions recorded`},
          {id:"present",  lbl:"Present",     val:presentDays,           color:"#16a34a",bg:"#f0fdf4",
           detail: `${presentDays} days present out of ${totalSessions} sessions`},
          {id:"absent",   lbl:"Absent",      val:absentDays,            color:"#dc2626",bg:"#fef2f2",
           detail: `${absentDays} days missed — ${totalSessions>0?Math.round((absentDays/totalSessions)*100):0}% absence rate`},
          {id:"att",      lbl:"Attendance",  val:`${attPct}%`,          color:attPct>=80?"#16a34a":attPct>=60?"#d97706":"#dc2626",
           bg:attPct>=80?"#f0fdf4":attPct>=60?"#fffbeb":"#fef2f2",
           detail: `${attPct}% overall attendance — ${attPct>=80?"Excellent 🌟":attPct>=60?"Good — keep it up":"Needs improvement ⚠️"}`},
        ].map(k => (
          <button key={k.id} onClick={() => setExpandedStat(expandedStat===k.id?null:k.id)}
            style={{background:k.bg,borderRadius:12,padding:"12px 8px",textAlign:"center",border:`1.5px solid ${expandedStat===k.id?k.color:k.color+"20"}`,cursor:"pointer",transition:"all .2s",WebkitAppearance:"none",outline:"none"}}>
            <div style={{fontWeight:900,fontSize:22,color:k.color}}>{k.val}</div>
            <div style={{fontSize:10,color:k.color,opacity:.9,marginTop:2,fontWeight:700}}>{k.lbl}</div>
            <div style={{fontSize:9,color:k.color,opacity:.5,marginTop:3}}>{expandedStat===k.id?"▲ tap to close":"▼ tap for detail"}</div>
          </button>
        ))}
      </div>
      {expandedStat && (
        <div style={{background:"white",borderRadius:12,padding:"12px 16px",border:`1.5px solid ${
          expandedStat==="sessions"?P:expandedStat==="present"?"#16a34a":expandedStat==="absent"?"#dc2626":attPct>=80?"#16a34a":attPct>=60?"#d97706":"#dc2626"
        }20`,fontSize:13,color:"#374151",fontWeight:600,animation:"fadeIn .2s ease"}}>
          {expandedStat==="sessions" && `📖 ${totalSessions} sessions total — this month: ${logsThisMonth.length} sessions (${monthPresent} present, ${monthAbsent} absent)`}
          {expandedStat==="present"  && `✅ ${presentDays} days present overall — this month: ${monthPresent} days`}
          {expandedStat==="absent"   && `❌ ${absentDays} days missed — this month: ${monthAbsent} absent`}
          {expandedStat==="att"      && `📊 ${attPct}% overall · This month: ${logsThisMonth.length>0?Math.round((monthPresent/logsThisMonth.length)*100):0}% · ${attPct>=80?"Excellent 🌟":attPct>=60?"Good 👍":"Needs improvement ⚠️"}`}
        </div>
      )}

      {/* ── This Month Summary — beautiful Sabaq/Sabqi/Manzil ── */}
      {logsThisMonth.length > 0 && (
        <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(76,29,149,0.08)",border:"1px solid #ede9fe"}}>
          {/* Header */}
          <div style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",padding:"14px 20px"}}>
            <div style={{fontWeight:800,fontSize:14,color:"white"}}>
              📅 {new Date().toLocaleString('default',{month:'long'})} Summary
            </div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",marginTop:2}}>
              {monthPresent} sessions present · {monthAbsent} absent
            </div>
          </div>
          {/* Three rows */}
          {[
            {label:"📗 سبق", sublabel:"Sabaq — New Memorisation", pages:monthSabaq, color:"#1a6b3c", bg:"#f0fdf4", border:"#bbf7d0"},
            {label:"📘 سبقی", sublabel:"Sabqi — Recent Revision",  pages:monthSabqi, color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe"},
            {label:"📕 منزل", sublabel:"Manzil — Long Revision",   pages:monthManzil,color:"#5b21b6", bg:"#f5f3ff", border:"#ddd6fe"},
          ].map(({label, sublabel, pages, color, bg, border}) => {
            const d = pagesDisplay(pages);
            return (
              <div key={label} style={{padding:"16px 20px",borderBottom:`1px solid ${border}40`,background:bg+"60"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:800,fontSize:15,color,lineHeight:1}}>{label}</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:3,letterSpacing:.3}}>{sublabel}</div>
                  </div>
                  {d ? (
                    <div style={{display:"flex",alignItems:"center",gap:8,background:"white",borderRadius:14,padding:"10px 18px",border:`1.5px solid ${border}`,boxShadow:`0 2px 8px ${color}15`}}>
                      <span style={{fontSize:26,fontWeight:900,color,lineHeight:1}}>{d.pages}</span>
                      <div style={{display:"flex",flexDirection:"column",lineHeight:1.1}}>
                        <span style={{fontSize:11,color,fontWeight:700,opacity:.7}}>صفحات</span>
                        <span style={{fontSize:10,color:"#9ca3af"}}>pages</span>
                      </div>
                      <span style={{fontSize:22,color,opacity:.35,fontWeight:300,margin:"0 2px"}}>≡</span>
                      <span style={{fontSize:26,fontWeight:900,color,lineHeight:1}}>{d.juzEq}</span>
                      <div style={{display:"flex",flexDirection:"column",lineHeight:1.1}}>
                        <span style={{fontSize:11,color,fontWeight:700,opacity:.7}}>جز</span>
                        <span style={{fontSize:10,color:"#9ca3af"}}>Juz</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",background:"white",borderRadius:10,padding:"8px 14px",border:"1px solid #f3f4f6"}}>No entries yet</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Last 14 Days Grid ── */}
      <div style={{background:"white",borderRadius:14,padding:"16px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1px solid #f3f4f6"}}>
        <div style={{fontWeight:800,fontSize:13,color:"#111827",marginBottom:10}}>Last 14 Days — tap a day to view details</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(14,1fr)",gap:4,marginBottom:4}}>
          {last14.map(({ds,log,dayNum})=>{
            const absent=log&&log.was_present===false;
            const sp=Number(log?.sabaq_pages)||0;
            const bg=!log?"#f3f4f6":absent?"#fecaca":sp>=5?"#4c1d95":sp>0?"#a78bfa":"#ddd6fe";
            const isSel=selLog?.date===ds;
            return (
              <button key={ds} onClick={()=>setSelLog(isSel?null:(log||null))}
                style={{aspectRatio:"1",background:bg,borderRadius:5,border:isSel?"2.5px solid #111827":"2px solid transparent",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:log?"pointer":"default",padding:0,WebkitAppearance:"none",outline:"none"}}>
                <span style={{fontSize:9,color:!log?"#9ca3af":absent?"#7f1d1d":"white",fontWeight:800}}>{dayNum}</span>
                {log&&!absent&&sp>0&&<span style={{fontSize:7,color:"rgba(255,255,255,0.85)",fontWeight:700}}>{sp}p</span>}
                {absent&&<span style={{fontSize:7,color:"#7f1d1d",fontWeight:700}}>A</span>}
              </button>
            );
          })}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(14,1fr)",gap:4,marginBottom:8}}>
          {last14.map(({ds,dayName})=><div key={ds} style={{fontSize:8,color:"#9ca3af",textAlign:"center"}}>{dayName[0]}</div>)}
        </div>
        {/* Legend */}
        <div style={{display:"flex",gap:8,fontSize:10,flexWrap:"wrap",marginBottom:selLog?12:0}}>
          {[["#4c1d95","white","5+ pages"],["#a78bfa","white","1-4 pages"],["#ddd6fe","#374151","Present"],["#fecaca","#7f1d1d","Absent"],["#f3f4f6","#9ca3af","No log"]].map(([bg,tc,lbl])=>(
            <span key={lbl} style={{display:"flex",alignItems:"center",gap:3}}>
              <span style={{width:10,height:10,borderRadius:2,background:bg,display:"inline-block",flexShrink:0,border:`1px solid ${bg==="white"?"#e5e7eb":"transparent"}`}}/>
              <span style={{color:"#374151"}}>{lbl}</span>
            </span>
          ))}
        </div>
        {/* Selected day detail */}
        {selLog&&(()=>{
          const rich=selLog.rich_data||{};
          const wasPresent=selLog.was_present!==false;
          return (
            <div style={{background:"#f5f3ff",borderRadius:10,padding:"14px 16px",border:"1px solid #ddd6fe"}}>
              <div style={{fontWeight:800,color:"#4c1d95",fontSize:13,marginBottom:10}}>
                {new Date(selLog.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}
                {(rich.sabaq_juz||selLog.sabaq_juz)&&<span style={{marginLeft:8,fontWeight:600,fontSize:12,color:"#7c3aed"}}>· Juz {rich.sabaq_juz||selLog.sabaq_juz}</span>}
              </div>
              {!wasPresent
                ?<div style={{fontWeight:700,color:"#dc2626",fontSize:13}}>Absent — no session today</div>
                :<div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {[
                    {label:"📗 سبق (Sabaq)",     text:selLog.sabaq_label||selLog.hifz_lesson, pages:selLog.sabaq_pages,  color:"#1a6b3c", border:"#bbf7d0", bg:"#f0fdf4"},
                    {label:"📘 سبقی (Sabqi)",    text:selLog.sabqi_label||selLog.sabqi,       pages:selLog.sabqi_pages,  color:"#1d4ed8", border:"#bfdbfe", bg:"#eff6ff"},
                    {label:"📕 منزل (Manzil)",   text:selLog.manzil_label||selLog.manzil,     pages:selLog.manzil_pages, color:"#5b21b6", border:"#ddd6fe", bg:"#f5f3ff"},
                  ].filter(r=>r.pages>0||r.text).map(({label,text,pages,color,border,bg})=>{
                    const d=pagesDisplay(pages);
                    return (
                      <div key={label} style={{background:bg,borderRadius:9,padding:"10px 14px",border:`1px solid ${border}`}}>
                        <div style={{fontWeight:800,color,fontSize:12,marginBottom:4}}>{label}</div>
                        {text&&<div style={{fontSize:13,color:"#111827",fontWeight:600,marginBottom:d?6:0}}>{text}</div>}
                        {d&&(
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:20,fontWeight:900,color}}>{d.pages}</span>
                            <span style={{fontSize:11,color,opacity:.7,fontWeight:600}}>صفحات</span>
                            <span style={{fontSize:16,color,opacity:.3,margin:"0 2px"}}>≡</span>
                            <span style={{fontSize:20,fontWeight:900,color}}>{d.juzEq}</span>
                            <span style={{fontSize:11,color,opacity:.7,fontWeight:600}}>جز</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {selLog.note&&<div style={{fontSize:12,color:"#78350f",fontStyle:"italic",borderTop:"1px solid #ddd6fe",paddingTop:8,marginTop:2}}>
                    <strong style={{color:"#92400e",fontStyle:"normal"}}>💬 Teacher: </strong>{selLog.note}
                  </div>}
                </div>
              }
            </div>
          );
        })()}
      </div>

      {/* ── Weekly Reports Section ── */}
      {(()=>{
        const getWeekKey = (dateStr) => {
          const d = new Date(dateStr);
          const day = d.getDay();
          const diff = d.getDate() - day + (day === 0 ? -6 : 1);
          const mon = new Date(d.setDate(diff));
          return mon.toISOString().split("T")[0];
        };
        const getWeekEnd = (weekStart) => {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + 6);
          return d.toISOString().split("T")[0];
        };
        const weekMap = {};
        hifzLogs.forEach(l=>{
          const wk=getWeekKey(l.date);
          if(!weekMap[wk]) weekMap[wk]={week_start:wk,week_end:getWeekEnd(wk),logs:[]};
          weekMap[wk].logs.push(l);
        });
        const weeks = Object.values(weekMap).sort((a,b)=>b.week_start.localeCompare(a.week_start));
        if(!weeks.length&&!weeklyReports.length) return null;
        const reportMap = {};
        weeklyReports.forEach(r=>{ reportMap[r.week_start]=r; });
        return (
          <div style={{borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.10)"}}>
            <div style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",padding:"14px 20px"}}>
              <div style={{fontWeight:800,fontSize:15,color:"white"}}>📋 Weekly Hifz Progress</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",marginTop:2}}>ہفتہ وار حفظ رپورٹ</div>
            </div>
            <div style={{background:"white"}}>
              {weeks.map((wk,idx)=>{
                const report = reportMap[wk.week_start];
                const present = wk.logs.filter(l=>l.was_present!==false);
                const absent  = wk.logs.filter(l=>l.was_present===false);
                const lastClass = present.sort((a,b)=>b.date.localeCompare(a.date))[0];
                const lastSabaqLabel = lastClass?.sabaq_label||"";
                const lastSabaqPages = lastClass?.sabaq_pages||0;
                const lastSabaqJuz   = lastClass?.sabaq_juz||0;
                const lastJuzAr = lastSabaqJuz ? QURAN_JUZ.find(q=>q.n===lastSabaqJuz)?.ar||"" : "";
                const totalSabaq  = sumP(wk.logs,"sabaq_pages");
                const totalSabqi  = sumP(wk.logs,"sabqi_pages");
                const totalManzil = sumP(wk.logs,"manzil_pages");
                return (
                  <div key={wk.week_start} style={{borderBottom:idx<weeks.length-1?"1px solid #f3f4f6":"none"}}>
                    <div style={{background:report?"#f5f3ff":"#fafafa",padding:"14px 20px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",borderBottom:"1px solid #ede9fe"}}>
                      <div style={{background:"#4c1d95",color:"white",borderRadius:8,padding:"5px 14px",fontWeight:800,fontSize:13}}>
                        {new Date(wk.week_start).toLocaleDateString("en-GB",{day:"numeric",month:"long"})} – {new Date(wk.week_end).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}
                      </div>
                      <span style={{background:"#dcfce7",color:"#15803d",borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700}}>✅ {present.length} days present</span>
                      {absent.length>0&&<span style={{background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700}}>❌ {absent.length} absent</span>}
                      {report&&<span style={{background:"#4c1d95",color:"white",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700}}>📋 Report Available</span>}
                    </div>
                    <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:12}}>
                      {lastClass&&(
                        <div style={{background:"linear-gradient(135deg,#f5f3ff,#ede9fe)",borderRadius:10,padding:"12px 16px",border:"1px solid #ddd6fe",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                          <div style={{fontSize:13,fontWeight:800,color:"#4c1d95"}}>📖 Last Class Sabaq</div>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                            {lastSabaqJuz>0&&<span style={{background:"#4c1d95",color:"white",borderRadius:7,padding:"4px 12px",fontSize:12,fontWeight:700}}>پارہ {lastSabaqJuz} — {lastJuzAr}</span>}
                            {lastSabaqLabel&&<span style={{background:"white",color:"#7c3aed",borderRadius:7,padding:"4px 12px",fontSize:12,fontWeight:700,border:"1px solid #ddd6fe"}}>{lastSabaqLabel}</span>}
                            {lastSabaqPages>0&&<span style={{fontSize:12,color:"#6b7280",fontWeight:600}}>{lastSabaqPages} pages</span>}
                          </div>
                        </div>
                      )}
                      {/* Weekly totals — beautiful pages+juz rows */}
                      <div style={{display:"flex",flexDirection:"column",gap:8}}>
                        {[
                          {label:"📗 سبق (Sabaq)",   sublabel:"New Memorisation", pages:totalSabaq,  color:"#1a6b3c", bg:"#f0fdf4", border:"#bbf7d0"},
                          {label:"📘 سبقی (Sabqi)",  sublabel:"Recent Revision",  pages:totalSabqi,  color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe"},
                          {label:"📕 منزل (Manzil)", sublabel:"Long Revision",    pages:totalManzil, color:"#5b21b6", bg:"#f5f3ff", border:"#ddd6fe"},
                        ].map(({label,sublabel,pages,color,bg,border})=>{
                          const d=pagesDisplay(pages);
                          return (
                            <div key={label} style={{background:bg,borderRadius:10,padding:"10px 14px",border:`1px solid ${border}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                              <div>
                                <div style={{fontWeight:800,color,fontSize:13}}>{label}</div>
                                <div style={{fontSize:10,color:"#9ca3af",marginTop:1}}>{sublabel}</div>
                              </div>
                              {d ? (
                                <div style={{display:"flex",alignItems:"center",gap:6}}>
                                  <span style={{fontSize:22,fontWeight:900,color}}>{d.pages}</span>
                                  <span style={{fontSize:11,color,fontWeight:600,opacity:.7}}>صفحات</span>
                                  <span style={{fontSize:18,color,opacity:.3,margin:"0 3px"}}>≡</span>
                                  <span style={{fontSize:22,fontWeight:900,color}}>{d.juzEq}</span>
                                  <span style={{fontSize:11,color,fontWeight:600,opacity:.7}}>جز</span>
                                </div>
                              ) : <span style={{color:"#9ca3af",fontSize:12}}>—</span>}
                            </div>
                          );
                        })}
                      </div>
                      {report&&(
                        <div style={{display:"flex",flexDirection:"column",gap:8}}>
                          {report.teacher_comment&&(
                            <div style={{background:"#fffbeb",borderRadius:10,padding:"12px 16px",border:"1px solid #fde68a"}}>
                              <div style={{fontSize:10,fontWeight:800,color:"#92400e",marginBottom:4,textTransform:"uppercase",letterSpacing:.3}}>💬 Teacher's Comment</div>
                              <div style={{fontSize:14,color:"#78350f",lineHeight:1.7,fontStyle:"italic"}}>"{report.teacher_comment}"</div>
                            </div>
                          )}
                          {report.ai_generated_comment&&(
                            <div style={{background:"#f5f3ff",borderRadius:10,padding:"12px 16px",border:"1px solid #ddd6fe"}}>
                              <div style={{fontSize:10,fontWeight:800,color:"#5b21b6",marginBottom:4,textTransform:"uppercase",letterSpacing:.3}}>✨ Detailed Feedback</div>
                              <div style={{fontSize:14,color:"#4c1d95",lineHeight:1.7}}>{report.ai_generated_comment}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {!report&&<div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:"8px 0",fontStyle:"italic"}}>Teacher's report for this week is not yet published</div>}
                    </div>
                  </div>
                );
              })}
              {weeklyReports.filter(r=>!weeks.find(w=>w.week_start===r.week_start)).map(r=>(
                <div key={r.id} style={{padding:"16px 20px",borderTop:"1px solid #f3f4f6"}}>
                  <div style={{fontWeight:700,color:"#4c1d95",fontSize:13,marginBottom:8}}>
                    {new Date(r.week_start).toLocaleDateString("en-GB",{day:"numeric",month:"long"})} – {new Date(r.week_end).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}
                  </div>
                  {r.teacher_comment&&<div style={{background:"#fffbeb",borderRadius:10,padding:"12px 16px",border:"1px solid #fde68a",fontSize:13,color:"#78350f",fontStyle:"italic"}}>"{r.teacher_comment}"</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
// ── Parent Events Tab ─────────────────────────────────────────────────
function ParentEventsTab({ studentClass, session }) {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(()=>{
    supabase.from("school_events")
      .select("*")
      .or(`class.eq.all,class.eq.${studentClass}`)
      .in("visible_to",["all","parents"])
      .gte("date", new Date(Date.now()-30*86400000).toISOString().split("T")[0])
      .order("date",{ascending:true})
      .then(({data})=>{ setEvents(data||[]); setLoading(false); });
  },[studentClass]);

  const EVENT_ICONS = {exam:"📝",holiday:"🏖️",function:"🎓",meeting:"👥",assignment:"📋",milestone:"🎯",general:"📅"};
  const EVENT_COLORS = {exam:"#dc2626",holiday:"#d97706",function:"#7c3aed",meeting:"#2563eb",assignment:"#0d9488",milestone:"#1a6b3c",general:"#374151"};

  const upcoming = events.filter(e=>new Date(e.date)>=new Date());
  const past = events.filter(e=>new Date(e.date)<new Date());

  const EventCard = ({ev}) => {
    const col = EVENT_COLORS[ev.event_type]||"#374151";
    const icon = EVENT_ICONS[ev.event_type]||"📅";
    const isPast = new Date(ev.date)<new Date();
    return (
      <div style={{display:"flex",gap:12,padding:"14px 16px",background:isPast?"#fafafa":"white",borderRadius:12,border:`1px solid ${isPast?"#f3f4f6":col+"30"}`,borderLeft:`4px solid ${isPast?"#d1d5db":col}`,opacity:isPast?0.8:1}}>
        <div style={{width:40,height:40,borderRadius:10,background:`${col}15`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{icon}</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:14,color:"#111827"}}>{ev.title}</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:3,display:"flex",gap:8,flexWrap:"wrap"}}>
            <span>📅 {new Date(ev.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"long",year:"numeric"})}</span>
            {ev.end_date&&<span>→ {new Date(ev.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}</span>}
            {ev.class&&ev.class!=="all"&&<span style={{background:"#f3f4f6",borderRadius:4,padding:"1px 6px"}}>{ev.class}</span>}
          </div>
          {ev.description&&<div style={{fontSize:12,color:"#6b7280",marginTop:4}}>{ev.description}</div>}
        </div>
      </div>
    );
  };

  if(loading) return <div style={{textAlign:"center",padding:40,color:"#9ca3af"}}>Loading events...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{fontWeight:800,fontSize:16,color:"#111827"}}>🗓️ School Events</div>
      {upcoming.length>0?(
        <>
          <div style={{fontWeight:700,fontSize:13,color:"#1a6b3c"}}>📅 Upcoming</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {upcoming.map(ev=><EventCard key={ev.id} ev={ev}/>)}
          </div>
        </>
      ):(
        <div style={{textAlign:"center",padding:32,color:"#9ca3af",background:"white",borderRadius:14,border:"1px dashed #e5e7eb"}}>
          <div style={{fontSize:32,marginBottom:8}}>📅</div>
          <div style={{fontWeight:600}}>No upcoming events</div>
        </div>
      )}
      {past.length>0&&(
        <>
          <div style={{fontWeight:700,fontSize:13,color:"#9ca3af",marginTop:8}}>✅ Past Events</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {past.slice(-5).reverse().map(ev=><EventCard key={ev.id} ev={ev}/>)}
          </div>
        </>
      )}
    </div>
  );
}

// ── Parent Announcements Tab ──────────────────────────────────────────
function ParentAnnouncementsTab({ announcements, student, session }) {
  const G = "#1a6b3c";
  const CAT_COLOR = { News:"#0891b2", Event:"#7c3aed", Achievement:"#16a34a", Notice:"#d97706", Holiday:"#e11d48" };
  const [annFilter,   setAnnFilter]   = React.useState("all");
  const [openAnn,     setOpenAnn]     = React.useState(null);
  const [saves,       setSaves]       = React.useState(new Set());
  const [annReads,    setAnnReads]    = React.useState(new Set());
  const userEmail = session?.user?.email||"";
  const studentClass = student?.class||student?.["class"]||"";

  React.useEffect(()=>{
    if(!userEmail) return;
    Promise.all([
      supabase.from("announcement_saves").select("announcement_id").eq("user_email",userEmail),
      supabase.from("notification_reads").select("notification_id").eq("user_email",userEmail),
    ]).then(([sRes, rRes])=>{
      setSaves(new Set((sRes.data||[]).map(x=>x.announcement_id)));
      setAnnReads(new Set((rRes.data||[]).map(x=>x.notification_id)));
    });
  },[userEmail]);

  const toggleSave = async (annId, e) => {
    e.stopPropagation();
    const isSaved = saves.has(annId);
    if(isSaved) {
      await supabase.from("announcement_saves").delete().eq("announcement_id",annId).eq("user_email",userEmail);
      setSaves(p=>{ const n=new Set(p); n.delete(annId); return n; });
    } else {
      await supabase.from("announcement_saves").upsert({announcement_id:annId,user_email:userEmail},{onConflict:"announcement_id,user_email"});
      setSaves(p=>new Set([...p,annId]));
    }
  };

  const filtered = announcements.filter(a=>
    !a.target_class || a.target_class==="All" || a.target_class==="all" || a.target_class==="All Classes" || !studentClass || a.target_class===studentClass || a.target_class===""
  );

  const unreadIds = new Set(filtered.filter(a=>!annReads.has(a.id)).map(a=>a.id));

  const displayed = annFilter==="saved"
    ? filtered.filter(a=>saves.has(a.id))
    : annFilter==="unread"
    ? filtered.filter(a=>unreadIds.has(a.id))
    : filtered;

  const markAsRead = async (ann) => {
    setOpenAnn(ann);
    if(unreadIds.has(ann.id)) {
      try {
        await supabase.from("notification_reads").upsert(
          {notification_id:ann.id, user_email:userEmail},
          {onConflict:"notification_id,user_email"}
        );
      } catch(e) {}
      setAnnReads(p=>new Set([...p,ann.id]));
    }
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:0}}>
      {/* Full reading modal */}
      {openAnn&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setOpenAnn(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:580,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${CAT_COLOR[openAnn.category]||G},${CAT_COLOR[openAnn.category]||G}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                  <div style={{flex:1}}>
                    <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{openAnn.category||"General"}</span>
                    <div style={{fontWeight:900,fontSize:18,color:"white",lineHeight:1.3}}>{openAnn.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6}}>
                      {new Date(openAnn.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,flexShrink:0}}>
                    <button onClick={(e)=>toggleSave(openAnn.id,e)}
                      style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,padding:"7px 14px",color:"white",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                      {saves.has(openAnn.id)?"🔖 Saved":"🔖 Save"}
                    </button>
                    <button onClick={()=>setOpenAnn(null)}
                      style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:9,width:34,height:34,color:"white",fontWeight:900,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                  </div>
                </div>
              </div>
              <div style={{padding:"24px 26px"}}>
                <div style={{fontSize:15,color:"#374151",lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>
                  {openAnn.body||"No content available."}
                </div>
                {openAnn.file_url&&(
                  <a href={openAnn.file_url} target="_blank" rel="noopener noreferrer"
                    style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:20,fontSize:13,color:"#7c3aed",fontWeight:700,textDecoration:"none",background:"#f5f3ff",borderRadius:10,padding:"10px 16px",border:"1px solid #ddd6fe"}}>
                    📎 {openAnn.file_name||"View Attachment"}
                  </a>
                )}
                <div style={{marginTop:24,paddingTop:16,borderTop:"1px solid #f3f4f6",display:"flex",gap:10}}>
                  <button onClick={()=>setOpenAnn(null)}
                    style={{flex:1,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    Close
                  </button>
                  <button onClick={(e)=>toggleSave(openAnn.id,e)}
                    style={{flex:1,background:saves.has(openAnn.id)?"#fef9c3":"#f0fdf4",color:saves.has(openAnn.id)?"#92400e":G,border:`1px solid ${saves.has(openAnn.id)?"#fde68a":"#bbf7d0"}`,borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    {saves.has(openAnn.id)?"🔖 Unsave":"🔖 Save for Later"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Inbox header */}
      <div style={{background:"white",borderRadius:"16px 16px 0 0",padding:"18px 20px",borderBottom:"1px solid #f3f4f6",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
        <div style={{marginBottom:14}}>
          <div style={{fontWeight:900,fontSize:17,color:"#111827"}}>📢 News & Announcements</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{filtered.length} announcement{filtered.length!==1?"s":""} · {unreadIds.size} unread</div>
        </div>
        <div style={{display:"flex",gap:4,background:"#f3f4f6",borderRadius:10,padding:3}}>
          {[
            ["all",`All (${filtered.length})`],
            ["unread",`Unread${unreadIds.size>0?` (${unreadIds.size})`:""}`,],
            ["saved",`🔖 Saved${saves.size>0?` (${saves.size})`:""}`],
          ].map(([v,l])=>(
            <button key={v} onClick={()=>setAnnFilter(v)}
              style={{flex:1,background:annFilter===v?"white":"transparent",color:annFilter===v?G:"#6b7280",border:"none",borderRadius:8,padding:"7px 10px",fontWeight:annFilter===v?800:600,fontSize:12,cursor:"pointer",boxShadow:annFilter===v?"0 1px 4px rgba(0,0,0,0.08)":"none",transition:"all .15s"}}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div style={{background:"white",borderRadius:"0 0 16px 16px",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
        {displayed.length===0?(
          <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>
            <div style={{fontSize:36,marginBottom:10}}>{annFilter==="saved"?"🔖":annFilter==="unread"?"✅":"📢"}</div>
            <div style={{fontWeight:700,fontSize:14}}>
              {annFilter==="saved"?"Nothing saved yet — tap 🔖 on any announcement"
               :annFilter==="unread"?"All caught up! No unread announcements"
               :"No announcements yet"}
            </div>
          </div>
        ):displayed.map((a,i)=>{
          const isUnread = unreadIds.has(a.id);
          const isSaved  = saves.has(a.id);
          const col      = CAT_COLOR[a.category]||G;
          return (
            <div key={a.id} onClick={()=>markAsRead(a)}
              style={{display:"flex",gap:12,padding:"14px 18px",borderBottom:i<displayed.length-1?"1px solid #f9fafb":"none",background:isUnread?"#f0fdf4":"white",cursor:"pointer",alignItems:"flex-start",transition:"background .1s"}}
              onMouseEnter={e=>e.currentTarget.style.background=isUnread?"#dcfce7":"#f9fafb"}
              onMouseLeave={e=>e.currentTarget.style.background=isUnread?"#f0fdf4":"white"}>
              <div style={{width:42,height:42,borderRadius:12,background:`${col}15`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,marginTop:2}}>
                {a.category==="Event"?"📅":a.category==="Achievement"?"🏆":a.category==="Notice"?"📋":a.category==="Holiday"?"🏖️":"📢"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                  {isUnread&&<span style={{width:8,height:8,borderRadius:"50%",background:G,flexShrink:0,display:"inline-block"}}/>}
                  <span style={{fontWeight:isUnread?800:600,fontSize:14,color:"#111827"}}>{a.title}</span>
                </div>
                <div style={{fontSize:12,color:"#6b7280",marginBottom:4,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{background:`${col}15`,color:col,borderRadius:5,padding:"1px 7px",fontSize:11,fontWeight:700}}>{a.category||"General"}</span>
                  {a.target_class&&a.target_class!=="All"&&<span style={{background:"#f3f4f6",color:"#374151",borderRadius:5,padding:"1px 7px",fontSize:11}}>{a.target_class}</span>}
                  <span>{new Date(a.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                </div>
                {a.body&&<div style={{fontSize:12,color:"#9ca3af",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.body.slice(0,80)}{a.body.length>80?"...":""}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8,flexShrink:0}}>
                <button onClick={(e)=>toggleSave(a.id,e)}
                  style={{background:"none",border:"none",cursor:"pointer",fontSize:18,opacity:isSaved?1:0.3,padding:2}}
                  title={isSaved?"Unsave":"Save for later"}>🔖</button>
                <span style={{fontSize:18,color:"#d1d5db"}}>›</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// PARENT DASHBOARD — rich home screen
// ══════════════════════════════════════════════════════════════════════
function ParentDashboard({ student, results, attendance, fees, announcements, events, hifzProgress, hifzJourney, isHifz, session, setActiveTab, pct, overallPct, paidFees }) {
  const G = "#1a6b3c";
  const [calMonth, setCalMonth] = React.useState(new Date());
  const [selEvent, setSelEvent] = React.useState(null);
  const [selAnn,   setSelAnn]   = React.useState(null);
  const userEmail = session?.user?.email||"";
  const studentClass = student?.class||student?.["class"]||"";

  const EVENT_ICONS   = {exam:"📝",holiday:"🏖️",function:"🎓",meeting:"👥",assignment:"📋",milestone:"🎯",general:"📅"};
  const EVENT_COLORS  = {exam:"#dc2626",holiday:"#d97706",function:"#7c3aed",meeting:"#2563eb",assignment:"#0d9488",milestone:"#1a6b3c",general:"#374151"};
  const CAT_COLOR     = {News:"#0891b2",Event:"#7c3aed",Achievement:"#16a34a",Notice:"#d97706",Holiday:"#e11d48",General:"#1a6b3c"};

  const today = new Date(); today.setHours(0,0,0,0);
  const todayStr = today.toISOString().split("T")[0];

  // Upcoming events (next 30 days)
  const upcoming = events.filter(e=>{ const d=new Date(e.date); d.setHours(0,0,0,0); return d>=today; }).slice(0,10);

  // Deadlines/HW — assignment type events
  const deadlines = upcoming.filter(e=>e.event_type==="assignment"||e.event_type==="exam");

  // Days remaining helper
  const daysLeft = (dateStr) => {
    const d = new Date(dateStr); d.setHours(0,0,0,0);
    const diff = Math.round((d-today)/(1000*60*60*24));
    return diff;
  };
  const deadlineColor = (days) => days===0?"#dc2626":days<=2?"#ea580c":days<=5?"#d97706":"#16a34a";
  const deadlineBg    = (days) => days===0?"#fef2f2":days<=2?"#fff7ed":days<=5?"#fefce8":"#f0fdf4";
  const deadlineLabel = (days) => days===0?"TODAY":days===1?"Tomorrow":`${days}d left`;

  // Calendar helpers
  const calStart  = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
  const calEnd    = new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 0);
  const startDay  = calStart.getDay();
  const daysInMon = calEnd.getDate();
  const eventsOnDay = (day) => {
    const ds = `${calMonth.getFullYear()}-${String(calMonth.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return events.filter(e=>e.date===ds||e.date.startsWith(ds));
  };

  // Stats
  const attColor = pct>=75?"#16a34a":pct>=50?"#d97706":"#dc2626";
  const feesPending = fees.filter(f=>!f.method||f.method==="Unpaid").length;

  // Latest announcements for dashboard
  const latestAnn = announcements.filter(a=>
    !a.target_class||a.target_class==="All"||a.target_class==="all"||a.target_class===studentClass||a.target_class===""
  ).slice(0,4);

  const S = useSettings();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* ── Welcome Banner ── */}
      <div style={{background:"linear-gradient(135deg,#1a6b3c 0%,#0a4a24 100%)",borderRadius:16,padding:"20px 24px",color:"white",boxShadow:"0 4px 20px rgba(26,107,60,0.25)"}}>
        <div style={{fontWeight:900,fontSize:19,marginBottom:2}}>السلام علیکم 👋</div>
        <div style={{fontSize:14,fontWeight:700,opacity:.9}}>{student?.full_name}'s Parent Portal</div>
        <div style={{fontSize:12,opacity:.7,marginTop:2}}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
        <div style={{height:1,background:"rgba(255,255,255,0.18)",margin:"12px 0"}}/>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <span style={{background:"rgba(255,255,255,.18)",borderRadius:6,padding:"4px 11px",fontSize:12,fontWeight:700}}>🏫 {student?.class}</span>
          {student?.stream&&<span style={{background:"rgba(255,255,255,.12)",borderRadius:6,padding:"4px 11px",fontSize:11}}>{student?.stream}</span>}
          <span style={{background:pct>=75?"rgba(255,255,255,.18)":"rgba(239,68,68,.35)",borderRadius:6,padding:"4px 11px",fontSize:12,fontWeight:700}}>📅 {pct}% Attendance</span>
          {feesPending>0&&<span style={{background:"rgba(239,68,68,.35)",borderRadius:6,padding:"4px 11px",fontSize:12,fontWeight:700}}>⚠ {feesPending} Fee{feesPending>1?"s":""} Due</span>}
          {overallPct!=null&&<span style={{background:"rgba(255,255,255,.12)",borderRadius:6,padding:"4px 11px",fontSize:12}}>📊 {overallPct}% Avg Score</span>}
        </div>
      </div>

      {/* ── Smart Alerts (parent-specific) ── */}
      <SmartAlerts role="parent" student={student} attendance={attendance} fees={fees}/>

      {/* ── Event detail popup ── */}
      {selEvent&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setSelEvent(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:520,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${EVENT_COLORS[selEvent.event_type]||"#374151"},${EVENT_COLORS[selEvent.event_type]||"#374151"}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{EVENT_ICONS[selEvent.event_type]||"📅"} {selEvent.event_type}</span>
                    <div style={{fontWeight:900,fontSize:18,color:"white",lineHeight:1.3}}>{selEvent.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.8)",marginTop:6}}>📅 {new Date(selEvent.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}{selEvent.end_date?" → "+new Date(selEvent.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"}):""}</div>
                  </div>
                  <button onClick={()=>setSelEvent(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:32,height:32,color:"white",fontWeight:900,fontSize:15,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              <div style={{padding:"20px 22px"}}>
                {selEvent.description?<div style={{fontSize:14,color:"#374151",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif",marginBottom:16}}>{selEvent.description}</div>:<div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",marginBottom:16}}>No additional details.</div>}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[["🏫 Class",selEvent.class==="all"?"All Classes":selEvent.class||"All"],["👁 Visible",selEvent.visible_to||"Everyone"]].map(([l,v])=>(
                    <div key={l} style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px"}}>
                      <div style={{fontSize:10,color:"#9ca3af",fontWeight:700}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>{v}</div>
                    </div>
                  ))}
                </div>
                <button onClick={()=>setSelEvent(null)} style={{display:"block",width:"100%",marginTop:16,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* ── Announcement detail popup ── */}
      {selAnn&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setSelAnn(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:540,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${CAT_COLOR[selAnn.category]||G},${CAT_COLOR[selAnn.category]||G}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{selAnn.category||"General"}</span>
                    <div style={{fontWeight:900,fontSize:18,color:"white",lineHeight:1.3}}>{selAnn.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6}}>{new Date(selAnn.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
                  </div>
                  <button onClick={()=>setSelAnn(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:32,height:32,color:"white",fontWeight:900,fontSize:15,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              <div style={{padding:"20px 22px"}}>
                <div style={{fontSize:15,color:"#374151",lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>{selAnn.body||"No content."}</div>
                {selAnn.file_url&&<a href={selAnn.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:16,fontSize:13,color:"#7c3aed",fontWeight:700,textDecoration:"none",background:"#f5f3ff",borderRadius:10,padding:"10px 16px",border:"1px solid #ddd6fe"}}>📎 {selAnn.file_name||"View Attachment"}</a>}
                <button onClick={()=>setSelAnn(null)} style={{display:"block",width:"100%",marginTop:16,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* ══════════════════════════════════════════════════════
          STAT CARDS — quick overview, each tappable
          ══════════════════════════════════════════════════════ */}
      <div style={{display:"grid",gridTemplateColumns:`repeat(${isHifz&&(hifzProgress||hifzJourney)?5:4},1fr)`,gap:10}}>
        {[
          {icon:"📅",label:"Attendance",value:`${pct}%`,sub:pct>=75?"✅ Good":pct>=60?"⚠ Low":"🔴 At risk",color:pct>=75?"#16a34a":pct>=60?"#d97706":"#dc2626",bg:pct>=75?"#f0fdf4":pct>=60?"#fffbeb":"#fef2f2",border:pct>=75?"#bbf7d0":pct>=60?"#fde68a":"#fecaca",tab:"attendance"},
          {icon:"📊",label:"Latest Score",value:overallPct!=null?overallPct+"%":"—",sub:overallPct!=null?(overallPct>=80?"🌟 Excellent":overallPct>=60?"👍 Good":"📚 Study more"):"No exams yet",color:"#2563eb",bg:"#eff6ff",border:"#bfdbfe",tab:"performance"},
          {icon:"💰",label:"Fees",value:feesPending>0?feesPending+" Due":"All Paid",sub:feesPending>0?"Tap to view details":"✅ Up to date",color:feesPending>0?"#dc2626":"#16a34a",bg:feesPending>0?"#fef2f2":"#f0fdf4",border:feesPending>0?"#fecaca":"#bbf7d0",tab:"fees"},
          {icon:"📢",label:"News",value:latestAnn.length>0?latestAnn.length+" new":"Up to date",sub:latestAnn.length>0?"Tap to read":"No new news",color:"#7c3aed",bg:"#f5f3ff",border:"#ddd6fe",tab:"announcements"},
          ...(isHifz&&(hifzProgress||hifzJourney)?[{
            icon:"📖",
            label:"Hifz",
            value:(()=>{const j=hifzJourney?.juz_completed??(hifzProgress?.current_juz?hifzProgress.current_juz-1:0); return j+"/30"})(),
            sub:"Juz memorised",
            color:"#4c1d95",bg:"#f5f3ff",border:"#ddd6fe",tab:"hifzlog"
          }]:[]),
        ].map(c=>(
          <div key={c.label} onClick={()=>setActiveTab(c.tab)}
            style={{background:"white",borderRadius:16,padding:"16px 12px",textAlign:"center",border:`1.5px solid ${c.border}`,cursor:"pointer",transition:"transform .15s,box-shadow .15s",boxShadow:`0 2px 8px ${c.color}10`}}
            onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow=`0 8px 24px ${c.color}25`;}}
            onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow=`0 2px 8px ${c.color}10`;}}>
            <div style={{width:40,height:40,borderRadius:12,background:c.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,margin:"0 auto 8px"}}>
              {c.icon}
            </div>
            <div style={{fontWeight:900,fontSize:20,color:c.color,lineHeight:1}}>{c.value}</div>
            <div style={{fontSize:10,fontWeight:700,color:c.color,opacity:.75,marginTop:3,textTransform:"uppercase",letterSpacing:.3}}>{c.label}</div>
            <div style={{fontSize:9,color:"#9ca3af",marginTop:4,lineHeight:1.3}}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════
          UPCOMING ASSESSMENTS — always visible
          ══════════════════════════════════════════════════════ */}
      <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:"1px solid #f3f4f6"}}>
        <div style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${upcoming.length>0?"#f3f4f6":"transparent"}`}}>
          <div style={{fontWeight:800,fontSize:13,color:"#111827"}}>📋 Upcoming Assessments & Events</div>
          <button onClick={()=>setActiveTab("events")} style={{fontSize:11,color:G,fontWeight:700,background:"none",border:"none",cursor:"pointer"}}>View all →</button>
        </div>
        {upcoming.length===0 ? (
          <div style={{padding:"20px 18px",display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:36,height:36,borderRadius:10,background:"#f9fafb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🎉</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:"#374151"}}>Nothing upcoming — enjoy your time!</div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:1}}>No exams, assignments, or events in the next 30 days.</div>
            </div>
          </div>
        ) : (
          <div style={{padding:"12px 18px",display:"flex",flexDirection:"column",gap:8}}>
            {upcoming.slice(0,5).map(ev=>{
              const days = daysLeft(ev.date);
              const col  = deadlineColor(days);
              const bg   = deadlineBg(days);
              return (
                <div key={ev.id} onClick={()=>setSelEvent(ev)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,background:bg,border:`1px solid ${col}30`,cursor:"pointer",transition:"transform .15s"}}
                  onMouseOver={e=>e.currentTarget.style.transform="translateX(3px)"} onMouseOut={e=>e.currentTarget.style.transform="none"}>
                  <div style={{width:38,height:38,borderRadius:9,background:col+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                    {EVENT_ICONS[ev.event_type]||"📅"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.title}</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>
                      {new Date(ev.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}
                      {ev.subject&&<span style={{marginLeft:6,color:"#9ca3af"}}>· {ev.subject}</span>}
                    </div>
                  </div>
                  <div style={{flexShrink:0}}>
                    <span style={{background:col,color:"white",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:800,whiteSpace:"nowrap"}}>
                      {deadlineLabel(days)}
                    </span>
                  </div>
                </div>
              );
            })}
            {upcoming.length>5&&<div style={{fontSize:11,color:G,fontWeight:700,textAlign:"center",paddingTop:4}}>+{upcoming.length-5} more events →</div>}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          TWO-COL: Calendar + Announcements
          ══════════════════════════════════════════════════════ */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

        {/* Calendar */}
        <div style={{background:"white",borderRadius:14,padding:"16px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:"1px solid #f3f4f6"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:800,fontSize:13,color:"#111827"}}>🗓️ Calendar</div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()-1,1))}
                style={{background:"#f3f4f6",border:"none",borderRadius:6,width:26,height:26,cursor:"pointer",fontWeight:700,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
              <span style={{fontSize:12,fontWeight:700,color:"#374151",minWidth:80,textAlign:"center"}}>{calMonth.toLocaleDateString("en-GB",{month:"long",year:"numeric"})}</span>
              <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()+1,1))}
                style={{background:"#f3f4f6",border:"none",borderRadius:6,width:26,height:26,cursor:"pointer",fontWeight:700,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,marginBottom:4}}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=>(
              <div key={d} style={{textAlign:"center",fontSize:9,fontWeight:700,color:"#9ca3af",padding:"2px 0"}}>{d}</div>
            ))}
            {Array.from({length:startDay}).map((_,i)=><div key={"e"+i}/>)}
            {Array.from({length:daysInMon}).map((_,i)=>{
              const day=i+1;
              const evs=eventsOnDay(day);
              const ds=`${calMonth.getFullYear()}-${String(calMonth.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const isToday=ds===todayStr;
              return (
                <div key={day} onClick={()=>evs.length&&setSelEvent(evs[0])}
                  style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderRadius:7,cursor:evs.length?"pointer":"default",position:"relative",background:isToday?"#1a6b3c":evs.length?"#f0fdf4":"transparent",border:isToday?"none":evs.length?"1px solid #bbf7d0":"1px solid transparent",transition:"all .15s"}}
                  onMouseOver={e=>{if(evs.length&&!isToday)e.currentTarget.style.background="#dcfce7";}}
                  onMouseOut={e=>{if(evs.length&&!isToday)e.currentTarget.style.background="#f0fdf4";}}>
                  <span style={{fontSize:11,fontWeight:isToday||evs.length?700:400,color:isToday?"white":evs.length?"#1a6b3c":"#374151"}}>{day}</span>
                  {evs.length>0&&(
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1,width:"100%"}}>
                      <div style={{width:"90%",fontSize:8,fontWeight:700,color:isToday?"rgba(255,255,255,0.9)":EVENT_COLORS[evs[0].event_type]||"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"center",lineHeight:1.2}}>
                        {evs[0].title.length>6?evs[0].title.slice(0,5)+"…":evs[0].title}
                      </div>
                      {evs.length>1&&<div style={{fontSize:7,fontWeight:600,color:isToday?"rgba(255,255,255,0.7)":"#9ca3af"}}>+{evs.length-1}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{marginTop:10,display:"flex",gap:8,flexWrap:"wrap"}}>
            {[["exam","#dc2626","Exam"],["assignment","#0d9488","HW"],["function","#7c3aed","Event"],["holiday","#d97706","Holiday"]].map(([t,c,l])=>(
              <div key={t} style={{display:"flex",alignItems:"center",gap:3,fontSize:9,color:"#6b7280"}}>
                <div style={{width:6,height:6,borderRadius:"50%",background:c}}/>
                {l}
              </div>
            ))}
          </div>
        </div>

        {/* Announcements */}
        <div style={{background:"white",borderRadius:14,padding:"16px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)",border:"1px solid #f3f4f6",display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontWeight:800,fontSize:13,color:"#111827"}}>📢 News & Announcements</div>
            <button onClick={()=>setActiveTab("announcements")} style={{fontSize:11,color:G,fontWeight:700,background:"none",border:"none",cursor:"pointer"}}>See all →</button>
          </div>
          {latestAnn.length===0?(
            <div style={{textAlign:"center",padding:"24px 0",color:"#9ca3af",fontSize:13}}>No announcements yet</div>
          ):latestAnn.map(a=>{
            const col=CAT_COLOR[a.category]||G;
            return (
              <div key={a.id} onClick={()=>setSelAnn(a)} style={{borderRadius:10,overflow:"hidden",border:`1px solid ${col}20`,cursor:"pointer",transition:"transform .15s,box-shadow .15s"}}
                onMouseOver={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow=`0 4px 12px ${col}20`;}}
                onMouseOut={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
                <div style={{background:`linear-gradient(135deg,${col},${col}cc)`,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:10,fontWeight:700,color:"white"}}>{a.category||"General"}</span>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}>{new Date(a.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>
                </div>
                <div style={{padding:"10px 12px"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#111827",marginBottom:3,lineHeight:1.3}}>{a.title}</div>
                  {a.body&&<div style={{fontSize:11,color:"#6b7280",overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{a.body}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
      {selEvent&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setSelEvent(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:520,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${EVENT_COLORS[selEvent.event_type]||"#374151"},${EVENT_COLORS[selEvent.event_type]||"#374151"}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{EVENT_ICONS[selEvent.event_type]||"📅"} {selEvent.event_type}</span>
                    <div style={{fontWeight:900,fontSize:18,color:"white",lineHeight:1.3}}>{selEvent.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.8)",marginTop:6}}>📅 {new Date(selEvent.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}{selEvent.end_date?" → "+new Date(selEvent.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"}):""}</div>
                  </div>
                  <button onClick={()=>setSelEvent(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:32,height:32,color:"white",fontWeight:900,fontSize:15,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              <div style={{padding:"20px 22px"}}>
                {selEvent.description?(
                  <div style={{fontSize:14,color:"#374151",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif",marginBottom:16}}>{selEvent.description}</div>
                ):(
                  <div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",marginBottom:16}}>No additional details.</div>
                )}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[["🏫 Class",selEvent.class==="all"?"All Classes":selEvent.class||"All"],["👁 Visible",selEvent.visible_to||"Everyone"]].map(([l,v])=>(
                    <div key={l} style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px"}}>
                      <div style={{fontSize:10,color:"#9ca3af",fontWeight:700}}>{l}</div>
                      <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>{v}</div>
                    </div>
                  ))}
                </div>
                <button onClick={()=>setSelEvent(null)} style={{display:"block",width:"100%",marginTop:16,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* ── Announcement detail popup ── */}
      {selAnn&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setSelAnn(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:540,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:`linear-gradient(135deg,${CAT_COLOR[selAnn.category]||G},${CAT_COLOR[selAnn.category]||G}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 22px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{selAnn.category||"General"}</span>
                    <div style={{fontWeight:900,fontSize:18,color:"white",lineHeight:1.3}}>{selAnn.title}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6}}>{new Date(selAnn.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
                  </div>
                  <button onClick={()=>setSelAnn(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:32,height:32,color:"white",fontWeight:900,fontSize:15,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              </div>
              <div style={{padding:"20px 22px"}}>
                <div style={{fontSize:15,color:"#374151",lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>{selAnn.body||"No content."}</div>
                {selAnn.file_url&&<a href={selAnn.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:16,fontSize:13,color:"#7c3aed",fontWeight:700,textDecoration:"none",background:"#f5f3ff",borderRadius:10,padding:"10px 16px",border:"1px solid #ddd6fe"}}>📎 {selAnn.file_name||"View Attachment"}</a>}
                <button onClick={()=>setSelAnn(null)} style={{display:"block",width:"100%",marginTop:16,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* ── Letterhead card ── */}
      <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 16px rgba(26,107,60,0.10)",border:"1px solid #d1fae5"}}>
        <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",padding:"18px 20px 0"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
            <img src={typeof SCHOOL_LOGO!=="undefined"?SCHOOL_LOGO:""} style={{width:54,height:54,objectFit:"contain",background:"white",borderRadius:10,padding:4,flexShrink:0}} onError={e=>e.target.style.display="none"}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:900,fontSize:17,color:"white",lineHeight:1.2}}>{schoolName}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.8)",marginTop:2}}>{schoolSub}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.6)",marginTop:2}}>{schoolReg} · {schoolAddr}</div>
            </div>
          </div>
          {/* Student strip */}
          <div style={{marginTop:14,display:"flex",alignItems:"center",gap:12,paddingBottom:16,flexWrap:"wrap"}}>
            <div style={{width:46,height:46,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:900,color:"white",flexShrink:0,border:"2px solid rgba(255,255,255,0.4)"}}>
              {(student?.full_name||"?")[0]}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:900,fontSize:16,color:"white"}}>{student?.full_name}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.75)"}}>{student?.class} · {student?.stream} · Roll #{student?.roll_no||"N/A"}</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                [pct+"%", pct>=75?"✅":"⚠️", "Attendance"],
                [overallPct!=null?overallPct+"%":"—", "📊", "Avg Score"],
                [feesPending>0?feesPending+" Due":"All Paid", feesPending>0?"🔴":"✅", "Fees"],
              ].map(([val,icon,lbl])=>(
                <div key={lbl} style={{background:"rgba(255,255,255,0.15)",borderRadius:10,padding:"8px 12px",textAlign:"center",border:"1px solid rgba(255,255,255,0.2)"}}>
                  <div style={{fontSize:15,fontWeight:900,color:"white"}}>{icon} {val}</div>
                  <div style={{fontSize:9,color:"rgba(255,255,255,0.7)",marginTop:1,fontWeight:600}}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
}

function ParentHomeworkTab({ student }) {
  const [hwList, setHwList] = React.useState([]);
  const [hwSubs, setHwSubs] = React.useState([]);
  const [hwLoading, setHwLoading] = React.useState(true);
  const stuClass = student?.class||student?.["class"]||"";

  React.useEffect(()=>{
    const thirtyDaysAgo = new Date(Date.now()-30*86400000).toISOString().split("T")[0];
    supabase.from("school_events").select("*")
      .eq("event_type","assignment")
      .gte("date",thirtyDaysAgo)
      .or(`class.eq.all${stuClass?",class.eq."+stuClass:""}`)
      .order("date",{ascending:false})
      .then(({data})=>{
        setHwList(data||[]);
        setHwLoading(false);
        if(data&&data.length>0){
          const ids = data.map(a=>a.id);
          supabase.from("homework_submissions")
            .select("*").in("assignment_id",ids)
            .eq("student_id",String(student?.id||""))
            .then(({data:subs})=>setHwSubs(subs||[]));
        }
      });
  },[]);

  const getSub = (aid) => hwSubs.find(s=>s.assignment_id===aid);
  const today = new Date(); today.setHours(0,0,0,0);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{background:"white",borderRadius:14,padding:"14px 18px",border:"1px solid #e5e7eb",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
        <div style={{fontWeight:800,fontSize:15,color:"#1a6b3c",marginBottom:2}}>📚 Homework & Assignments</div>
        <div style={{fontSize:12,color:"#6b7280"}}>Last 30 days · {hwList.length} assignments</div>
      </div>
      {hwLoading ? (
        <div style={{textAlign:"center",padding:32,color:"#9ca3af"}}>Loading…</div>
      ) : hwList.length===0 ? (
        <div style={{background:"white",borderRadius:14,padding:40,textAlign:"center",color:"#9ca3af",border:"1px solid #e5e7eb"}}>No assignments in the last 30 days.</div>
      ) : hwList.map(a=>{
        const sub = getSub(a.id);
        const submitted = sub?.submitted??false;
        const dDate = new Date(a.date); dDate.setHours(0,0,0,0);
        const days = Math.round((dDate-today)/(1000*60*60*24));
        const overdue = days<0&&!a.is_done;
        return (
          <div key={a.id} style={{background:"white",borderRadius:14,padding:"14px 18px",border:`1.5px solid ${submitted?"#bbf7d0":overdue?"#fecaca":"#e5e7eb"}`,boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{a.title}</div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
                  {a.subject&&<span style={{background:"#eff6ff",color:"#2563eb",borderRadius:5,padding:"1px 8px",marginRight:6,fontSize:11,fontWeight:700}}>{a.subject}</span>}
                  <span style={{color:days<0?"#dc2626":days===0?"#ea580c":"#6b7280"}}>
                    Due: {new Date(a.date).toLocaleDateString("en-PK",{weekday:"short",day:"numeric",month:"short"})}
                    {days===0?" (Today!)":days<0?` (${Math.abs(days)}d overdue)`:`  (${days}d left)`}
                  </span>
                </div>
                {a.description&&<div style={{fontSize:12,color:"#374151",marginTop:6,lineHeight:1.5}}>{a.description}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                <span style={{background:submitted?"#dcfce7":overdue?"#fee2e2":"#f3f4f6",color:submitted?"#15803d":overdue?"#dc2626":"#6b7280",borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:800,whiteSpace:"nowrap"}}>
                  {submitted?"✅ Submitted":overdue?"⚠️ Not Submitted":"📝 Pending"}
                </span>
                {sub?.marked_at&&<span style={{fontSize:10,color:"#9ca3af"}}>Marked {new Date(sub.marked_at).toLocaleDateString("en-PK",{day:"numeric",month:"short"})}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const PARENT_TRANS = {
  // Tab labels
  home:         {en:"Home",          ur:"ہوم"},
  profile:      {en:"Profile",       ur:"پروفائل"},
  reportcards:  {en:"Report Cards",  ur:"رپورٹ کارڈ"},
  performance:  {en:"Performance",   ur:"کارکردگی"},
  hifzlog:      {en:"Hifz Log",      ur:"حفظ لاگ"},
  attendance:   {en:"Attendance",    ur:"حاضری"},
  fees:         {en:"Fees",          ur:"فیس"},
  events:       {en:"Events",        ur:"تقریبات"},
  announcements:{en:"News",          ur:"اعلانات"},
  leave:        {en:"Leave",         ur:"چھٹی"},
  homework:     {en:"Homework",      ur:"ہوم ورک"},
  settings:     {en:"Settings",      ur:"ترتیبات"},
  // Status
  Present:      {en:"Present",       ur:"حاضر"},
  Absent:       {en:"Absent",        ur:"غیر حاضر"},
  Late:         {en:"Late",          ur:"دیر سے"},
  Leave:        {en:"Leave",         ur:"چھٹی"},
  "No record":  {en:"No record",     ur:"کوئی ریکارڈ نہیں"},
  Paid:         {en:"Paid",          ur:"ادا"},
  Unpaid:       {en:"Unpaid",        ur:"باقی"},
  Partial:      {en:"Partial",       ur:"جزوی"},
  // Common labels
  "Sign Out":          {en:"Sign Out",         ur:"خروج"},
  "Student":           {en:"Student",          ur:"طالب علم"},
  "Class":             {en:"Class",            ur:"جماعت"},
  "Father":            {en:"Father",           ur:"والد"},
  "Today's Attendance":{en:"Today's Attendance",ur:"آج کی حاضری"},
  "Fee Summary":       {en:"Fee Summary",       ur:"فیس خلاصہ"},
  "Recent Results":    {en:"Recent Results",    ur:"حالیہ نتائج"},
  "Announcements":     {en:"Announcements",     ur:"اعلانات"},
  "Upcoming Events":   {en:"Upcoming Events",   ur:"آنے والی تقریبات"},
  "Total Days":        {en:"Total Days",        ur:"کل دن"},
  "Present Days":      {en:"Present Days",      ur:"حاضر دن"},
  "Absent Days":       {en:"Absent Days",       ur:"غیر حاضر دن"},
  "Attendance %":      {en:"Attendance %",      ur:"حاضری فیصد"},
  "Months Paid":       {en:"Months Paid",       ur:"ادا شدہ مہینے"},
  "Months Pending":    {en:"Months Pending",    ur:"باقی مہینے"},
  "Submit":            {en:"Submit",            ur:"جمع کریں"},
  "Apply for Leave":   {en:"Apply for Leave",   ur:"چھٹی کے لیے درخواست"},
  "From":              {en:"From",              ur:"سے"},
  "To":                {en:"To",               ur:"تک"},
  "Reason":            {en:"Reason",            ur:"وجہ"},
  "Status":            {en:"Status",            ur:"صورتحال"},
  "Date":              {en:"Date",              ur:"تاریخ"},
  "Amount":            {en:"Amount",            ur:"رقم"},
  "Subject":           {en:"Subject",           ur:"مضمون"},
  "Marks":             {en:"Marks",             ur:"نمبر"},
  "Grade":             {en:"Grade",             ur:"گریڈ"},
  "No data yet":       {en:"No data yet",       ur:"ابھی کوئی ڈیٹا نہیں"},
  "Change Password":   {en:"Change Password",   ur:"پاس ورڈ بدلیں"},
  "Current Password":  {en:"Current Password",  ur:"موجودہ پاس ورڈ"},
  "New Password":      {en:"New Password",      ur:"نیا پاس ورڈ"},
  "Confirm Password":  {en:"Confirm Password",  ur:"پاس ورڈ تصدیق"},
};

function ParentPortal({ session, onSignOut }) {
  const S = useSettings();
  const [student, setStudent] = React.useState(null);
  const [results, setResults] = React.useState([]);
  const [attendance, setAttendance] = React.useState([]);
  const [fees, setFees] = React.useState([]);
  const [announcements, setAnnouncements] = React.useState([]);
  const [hifzProgress, setHifzProgress] = React.useState(null);
  const [hifzJourney,  setHifzJourney]  = React.useState(null);
  const [hifzLogs, setHifzLogs] = React.useState([]);
  const [weeklyReports, setWeeklyReports] = React.useState([]);
  const [reportCards, setReportCards] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState("dashboard");
  const [expandedFeeMonth, setExpandedFeeMonth] = React.useState(null);
  const [viewingReceipt, setViewingReceipt] = React.useState(null); // {stu, fee}
  const [expandedAnn, setExpandedAnn] = React.useState({});
  const [oldPass, setOldPass] = React.useState("");
  const [newPass, setNewPass] = React.useState("");
  const [confirmPass, setConfirmPass] = React.useState("");
  const [passMsg, setPassMsg] = React.useState(null);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [lang, setLang] = React.useState(()=>localStorage.getItem("parent_lang")||"en");
  const t = (key) => PARENT_TRANS[key]?.[lang] || key;
  const isUrdu = lang==="ur";
  const toggleLang = () => { const nl=isUrdu?"en":"ur"; setLang(nl); localStorage.setItem("parent_lang",nl); };
  const G = "#1a6b3c";
  const CREAM = "#fdf8f0";
  const grad = "linear-gradient(135deg,#1a6b3c,#0a4a24)";
  const CAT_COLOR = { News:"#0891b2", Event:"#7c3aed", Achievement:"#16a34a", Notice:"#d97706", Holiday:"#e11d48" };

  React.useEffect(()=>{
    let channel = null;
    let cancelled = false;

    async function load() {
      const email = session?.user?.email;
      if(!email) return;
      const { data: parent } = await supabase.from("parents").select("*").eq("email", email).single();
      if(!parent || cancelled) return setLoading(false);
      const { data: stu } = await supabase.from("students").select("*").eq("id", parent.student_id).single();
      if(cancelled) return;
      setStudent(stu);
      const stuClass = stu?.class||stu?.["class"]||"";
      const [r, a, f, ann, hp, rc, hl, wr, ev, hj] = await Promise.all([
        supabase.from("results").select("*").eq("student_id", parent.student_id).order("created_at", {ascending:false}),
        supabase.from("attendance").select("*").eq("student_id", parent.student_id).order("date", {ascending:false}),
        supabase.from("fee_payments").select("*").eq("student_id", parent.student_id).order("created_at", {ascending:false}),
        supabase.from("announcements").select("*").eq("is_published", true).order("created_at", {ascending:false}),
        supabase.from("hifz_progress").select("*").eq("student_id", parent.student_id).maybeSingle(),
        supabase.from("published_report_cards").select("*").eq("student_id", parent.student_id).order("published_at", {ascending:false}),
        supabase.from("hifz_daily_log").select("*").eq("student_id", parent.student_id).order("date", {ascending:false}),
        supabase.from("hifz_weekly_reports").select("*").eq("student_id", parent.student_id).eq("is_published",true).order("week_start",{ascending:false}),
        supabase.from("school_events").select("*").or(`class.eq.all${stuClass?",class.eq."+stuClass:""}`).in("visible_to",["all","parents"]).gte("date", new Date(Date.now()-7*86400000).toISOString().split("T")[0]).order("date",{ascending:true}),
        supabase.from("hifz_journey").select("*").eq("student_id", parent.student_id).maybeSingle(),
      ]);
      if(cancelled) return;
      setResults(r.data||[]);
      setAttendance(a.data||[]);
      setFees(f.data||[]);
      setAnnouncements(ann.data||[]);
      setHifzProgress(hp.data||null);
      setReportCards(rc.data||[]);
      setHifzLogs(hl.data||[]);
      setWeeklyReports(wr.data||[]);
      setEvents(ev.data||[]);
      setHifzJourney(hj.data||null);
      setLoading(false);

      // Set up realtime subscription only once, after all data is loaded
      if(!cancelled) {
        channel = supabase
          .channel("parent-attendance-" + parent.student_id)
          .on("postgres_changes", { event: "*", schema: "public", table: "attendance", filter: "student_id=eq." + parent.student_id },
            () => {
              supabase.from("attendance").select("*").eq("student_id", parent.student_id).order("date", {ascending:false})
                .then(({data}) => { if(data) setAttendance(data); });
            }
          )
          .subscribe();
      }
    }
    load();
    return () => { cancelled = true; if(channel) supabase.removeChannel(channel); };
  },[session]);

  const isHifz = (student?.stream||"").includes("Hifz") || (student?.stream||"").includes("Both");

  // Stats
  const readAttendanceStatus = (row) => {
  const raw = String(
    row?.statuscode || row?.statuslabel || row?.status || ""
  ).trim().toUpperCase();

  if (raw === "P" || raw === "PRESENT") return "P";
  if (raw === "A" || raw === "ABSENT") return "A";
  if (raw === "L" || raw === "LATE") return "L";
  if (raw === "LV" || raw === "LEAVE") return "LV";
  return "";
};

const statusMeta = (code) =>
  code === "P"
    ? { label: t("Present"), color: "#15803d", bg: "#dcfce7" }
    : code === "A"
    ? { label: t("Absent"), color: "#dc2626", bg: "#fee2e2" }
    : code === "L"
    ? { label: t("Late"), color: "#b45309", bg: "#ffedd5" }
    : code === "LV"
    ? { label: t("Leave"), color: "#6d28d9", bg: "#ede9fe" }
    : { label: t("No record"), color: "#9ca3af", bg: "#f9fafb" };

const present = attendance.filter((a) => readAttendanceStatus(a) === "P").length;
const absent = attendance.filter((a) => readAttendanceStatus(a) === "A").length;
const late = attendance.filter((a) => readAttendanceStatus(a) === "L").length;
const leave = attendance.filter((a) => readAttendanceStatus(a) === "LV").length;
const pct = attendance.length
  ? Math.round(((present + late) / attendance.length) * 100)
  : 0;
  const paidFees = fees.filter(f=>f.method&&f.method!=="Unpaid").length;
  const feesPending = fees.filter(f=>!f.method||f.method==="Unpaid").length;

  // Results by exam
  const byExam = results.reduce((acc,r)=>{ if(!acc[r.exam_name]) acc[r.exam_name]=[]; acc[r.exam_name].push(r); return acc; },{});

  // Overall %
  const overallPct = results.length ? Math.round(results.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/results.length) : null;

  const gradeColor = g => g?.includes("A+")?"#16a34a":g?.includes("A")?"#16a34a":g==="B"?"#0891b2":g==="C"?"#d97706":"#e11d48";

  const TABS = [
    {id:"dashboard",    label:t("home"),         icon:"🏠"},
    {id:"profile",      label:t("profile"),      icon:"👤"},
    {id:"reportcards",  label:t("reportcards"),  icon:"📄"},
    {id:"performance",  label:t("performance"),  icon:"📊"},
    ...(isHifz?[{id:"hifzlog",label:t("hifzlog"),icon:"📖"}]:[]),
    {id:"attendance",   label:t("attendance"),   icon:"📅"},
    {id:"fees",         label:t("fees"),         icon:"💰"},
    {id:"events",       label:t("events"),       icon:"🗓️"},
    {id:"announcements",label:t("announcements"),icon:"📢"},
    {id:"leave",        label:t("leave"),        icon:"📝"},
    {id:"homework",     label:t("homework"),     icon:"📚"},
    {id:"settings",     label:t("settings"),     icon:"⚙️"},
  ];

  const inp = {width:"100%",border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 12px",fontSize:13,boxSizing:"border-box"};

  if(loading) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#1a6b3c 0%,#0a4a24 100%)"}}>
      <div style={{color:"white",textAlign:"center",padding:32}}>
        <img src={SCHOOL_LOGO} style={{width:70,height:70,objectFit:"contain",marginBottom:20,filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.3))"}} onError={e=>e.target.style.display="none"}/>
        <div style={{width:44,height:44,border:"3px solid rgba(255,255,255,0.25)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite",margin:"0 auto 20px"}}/>
        <div style={{fontWeight:800,fontSize:16,marginBottom:4}}>Parent Portal</div>
        <div style={{fontSize:12,opacity:.7}}>Loading your child's information…</div>
      </div>
    </div>
  );

  if(!student) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:CREAM}}>
      <div style={{textAlign:"center",padding:40}}>
        <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
        <div style={{fontSize:18,fontWeight:800,color:"#1a1a1a",marginBottom:8}}>No student linked to this account</div>
        <div style={{fontSize:14,color:"#6b7280",marginBottom:20}}>Please contact the school administration.</div>
        <button onClick={onSignOut} style={{background:G,color:"white",border:"none",borderRadius:10,padding:"10px 24px",fontWeight:700,cursor:"pointer"}}>Sign Out</button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"#f0f4f8",fontFamily:isUrdu?"'Noto Nastaliq Urdu','Segoe UI',sans-serif":"'Plus Jakarta Sans',system-ui,sans-serif",display:"flex",flexDirection:"column",direction:isUrdu?"rtl":"ltr"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&family=Noto+Nastaliq+Urdu:wght@400;700&display=swap');`}</style>

      {/* ── Letterhead Banner ── */}
      <div style={{background:`linear-gradient(135deg,#1a6b3c 0%,#0a4a24 100%)`,flexShrink:0,boxShadow:"0 4px 20px rgba(26,107,60,0.25)"}}>
        <div style={{padding:"14px 20px 0",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <div style={{width:52,height:52,borderRadius:12,background:"rgba(255,255,255,0.12)",
            border:"2px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",
            justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
            <img src={SCHOOL_LOGO} style={{width:44,height:44,objectFit:"contain"}} onError={e=>{e.target.style.display="none";}}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:"rgba(255,255,255,0.65)",fontSize:10,fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Parent Portal</div>
            <div style={{color:"white",fontWeight:900,fontSize:17,lineHeight:1.2,marginTop:1}}>{S?.school_name_en||"QUR'AN-E-KARIM ACADEMY"}</div>
            <div style={{color:"rgba(255,255,255,0.6)",fontSize:11,marginTop:1}}>{S?.school_name_en_sub||"Modern Islamic School System"}</div>
          </div>
          {/* Student name pill */}
          <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.18)",borderRadius:12,padding:"8px 14px",flexShrink:0}}>
            <div style={{width:34,height:34,borderRadius:9,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>
              {student.gender==="Female"?"👧":"👦"}
            </div>
            <div>
              <div style={{color:"white",fontWeight:800,fontSize:13,lineHeight:1.2}}>{student.full_name}</div>
              <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,marginTop:2}}>{student.class}{student.stream?" · "+student.stream:""}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
            <NotificationBell userEmail={session?.user?.email||""} userRole="parents" targetClass={student.class||student["class"]}/>
            <button onClick={toggleLang} style={{background:"rgba(255,255,255,0.15)",color:"white",
              border:"1px solid rgba(255,255,255,0.35)",borderRadius:8,padding:"7px 12px",
              fontSize:12,fontWeight:800,cursor:"pointer",letterSpacing:.5,
              fontFamily:isUrdu?"'Noto Nastaliq Urdu',serif":"inherit"}}>
              {isUrdu?"English":"اردو"}
            </button>
            <button onClick={onSignOut} style={{background:"rgba(255,255,255,0.12)",color:"white",
              border:"1px solid rgba(255,255,255,0.25)",borderRadius:8,padding:"7px 14px",
              fontSize:12,fontWeight:700,cursor:"pointer"}}>{t("Sign Out")}</button>
          </div>
        </div>
        {/* Multi-colour accent strip */}
        <div style={{height:3,background:"linear-gradient(90deg,#2563eb,#0d9488,#f0a500,#0d9488,#2563eb)",opacity:.8,marginTop:14}}/>
        {/* Subtitle bar */}
        <div style={{background:"rgba(0,0,0,0.18)",padding:"6px 20px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#ffd166",display:"flex",alignItems:"center",gap:8}}>
            <span style={{opacity:.6}}>📍</span>
            <span>{TABS.find(t=>t.id===activeTab)?.icon} {TABS.find(t=>t.id===activeTab)?.label||"Home"}</span>
          </div>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>
            {new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}
          </span>
        </div>
      </div>

      {/* ── Body: Sidebar + Content ── */}
      <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>

        {/* ── Left Sidebar ── */}
        <div style={{
          width: sidebarOpen ? 220 : 64,
          background:"linear-gradient(180deg,#0d3d1f 0%,#1a6b3c 60%,#0a4a24 100%)",
          display:"flex",flexDirection:"column",
          transition:"width .25s cubic-bezier(.4,0,.2,1)",
          overflow:"hidden",flexShrink:0,
          boxShadow:"3px 0 16px rgba(0,0,0,0.2)",
          position:"sticky",top:0,alignSelf:"flex-start",
          maxHeight:"calc(100vh - 90px)",
          scrollbarWidth:"thin",scrollbarColor:"rgba(255,255,255,0.15) transparent",
        }}>
          {/* Student card */}
          {sidebarOpen && (
            <div style={{padding:"14px 12px 12px",borderBottom:"1px solid rgba(255,255,255,0.1)"}}>
              <div style={{width:40,height:40,borderRadius:10,background:"rgba(255,255,255,0.15)",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,marginBottom:8}}>
                {student.gender==="Female"?"👧":"👦"}
              </div>
              <div style={{fontWeight:800,fontSize:13,color:"white",lineHeight:1.3}}>{student.full_name}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginTop:2}}>{student.class}{student.stream?" · "+student.stream:""}</div>
              <div style={{marginTop:8,background:"rgba(255,255,255,0.08)",borderRadius:8,padding:"7px 10px"}}>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",fontWeight:600}}>Attendance Rate</div>
                <div style={{fontWeight:900,fontSize:16,color:pct>=80?"#86efac":pct>=60?"#fde68a":"#fca5a5",marginTop:1}}>{pct}%</div>
                <div style={{height:4,background:"rgba(255,255,255,0.15)",borderRadius:2,marginTop:4}}>
                  <div style={{width:`${pct}%`,height:"100%",background:pct>=80?"#4ade80":pct>=60?"#facc15":"#f87171",borderRadius:2,transition:"width .4s"}}/>
                </div>
              </div>
            </div>
          )}

          {/* Nav */}
          <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
            {TABS.map(t=>{
              const active = activeTab===t.id;
              return (
                <button key={t.id} onClick={()=>setActiveTab(t.id)} title={t.label}
                  style={{
                    width:"100%",display:"flex",alignItems:"center",
                    gap:sidebarOpen?10:0,padding:sidebarOpen?"9px 12px":"11px 0",
                    justifyContent:sidebarOpen?"flex-start":"center",
                    background:active?"rgba(255,255,255,0.15)":"transparent",
                    color:active?"white":"rgba(255,255,255,0.65)",
                    border:"none",cursor:"pointer",
                    boxShadow:active?(sidebarOpen?"inset 3px 0 0 #f0a500":"inset 0 -3px 0 #f0a500"):"none",
                    fontWeight:active?700:500,fontSize:13,
                    transition:"all .15s",textAlign:"left",
                  }}
                  onMouseOver={e=>{if(!active){e.currentTarget.style.background="rgba(255,255,255,0.08)";e.currentTarget.style.color="white";}}}
                  onMouseOut={e=>{if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.65)";}}}
                >
                  <span style={{fontSize:16,flexShrink:0}}>{t.icon}</span>
                  {sidebarOpen&&<span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>}
                </button>
              );
            })}
          </div>

          {/* Collapse toggle */}
          <div style={{padding:"8px",borderTop:"1px solid rgba(255,255,255,0.1)"}}>
            <button onClick={()=>setSidebarOpen(p=>!p)}
              style={{width:"100%",display:"flex",alignItems:"center",gap:10,
                justifyContent:sidebarOpen?"flex-start":"center",
                padding:sidebarOpen?"8px 10px":"10px 0",
                background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",
                borderRadius:8,cursor:"pointer",color:"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600}}>
              <span style={{fontSize:14}}>{sidebarOpen?"◀":"▶"}</span>
              {sidebarOpen&&<span>Collapse</span>}
            </button>
          </div>
        </div>

        {/* ── Main content ── */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 20px",minWidth:0}}>
          {viewingReceipt&&<FeeReceipt student={viewingReceipt.stu} feeRecord={viewingReceipt.fee} copyType="parent" onClose={()=>setViewingReceipt(null)}/>}

        {/* ── Letterhead — appears on every tab ── */}
        {(()=>{
          const schoolName = S?.school_name_en||"QUR'AN-E-KARIM ACADEMY";
          const schoolSub  = S?.school_name_en_sub||"Modern Islamic School System";
          return (
            <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(26,107,60,0.08)",border:"1px solid #d1fae5",marginBottom:16}}>
              {/* Green top band */}
              <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",padding:"12px 18px",display:"flex",alignItems:"center",gap:12}}>
                <img src={typeof SCHOOL_LOGO!=="undefined"?SCHOOL_LOGO:""} style={{width:38,height:38,objectFit:"contain",background:"rgba(255,255,255,0.95)",borderRadius:8,padding:3,flexShrink:0}} onError={e=>e.target.style.display="none"}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:900,fontSize:14,color:"white",lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{schoolName}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.75)",marginTop:1}}>{schoolSub}</div>
                </div>

              </div>
              {/* Student strip */}
              <div style={{padding:"9px 18px",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#1a6b3c,#0d9488)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"white",flexShrink:0}}>
                  {student?.gender==="Female"?"👧":"👦"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,fontSize:14,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{student?.full_name}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginTop:1,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                    <span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:4,padding:"1px 6px",fontWeight:700,fontSize:10}}>{student?.class}</span>
                    <span>{student?.stream}</span>
                    <span style={{color:"#d1d5db"}}>·</span>
                    <span style={{color:"#9ca3af",fontSize:10}}>ID: {student?.id}</span>
                  </div>
                </div>
                {/* Mini stats */}
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[
                    {v:`${pct}%`, l:"Attendance", c:pct>=75?"#16a34a":pct>=60?"#d97706":"#dc2626", click:()=>setActiveTab("attendance")},
                    ...(overallPct!=null?[{v:`${overallPct}%`,l:"Avg Score",c:"#2563eb",click:()=>setActiveTab("performance")}]:[]),
                    {v:feesPending>0?`${feesPending} Due`:"Paid",l:"Fees",c:feesPending>0?"#dc2626":"#16a34a",click:()=>setActiveTab("fees")},
                  ].map(k=>(
                    <button key={k.l} onClick={k.click}
                      style={{background:"#f9fafb",border:`1px solid ${k.c}20`,borderRadius:8,padding:"5px 10px",textAlign:"center",cursor:"pointer",minWidth:60}}>
                      <div style={{fontWeight:800,fontSize:13,color:k.c,lineHeight:1}}>{k.v}</div>
                      <div style={{fontSize:9,color:"#9ca3af",marginTop:2}}>{k.l}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* DASHBOARD TAB */}
        {activeTab==="dashboard" && <ParentDashboard
          student={student} results={results} attendance={attendance}
          fees={fees} announcements={announcements} events={events}
          hifzProgress={hifzProgress} hifzJourney={hifzJourney} isHifz={isHifz}
          session={session} setActiveTab={setActiveTab}
          pct={pct} overallPct={overallPct} paidFees={paidFees}
        />}

        {/* PROFILE TAB */}
        {activeTab==="profile" && (
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            <div style={{background:"white",borderRadius:16,padding:"24px",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
            <div style={{fontWeight:800,fontSize:16,color:"#1a1a1a",marginBottom:18}}>Student Information</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
              {[
                ["Full Name", student.full_name],
                ["Father's Name", student.father_name||"—"],
                ["Class", student.class||"—"],
                ["Stream", student.stream||"—"],
                ["Gender", student.gender||"—"],
                ["Date of Birth", student.dob||"—"],
                ["Student ID", student.id],
                ["Enrolled", student.enrolled_date||"—"],
                ["Boarding", student.is_boarding?"Yes — Boarding":"No — Day Scholar"],
                ["Monthly Fee", student.monthly_fee?`PKR ${student.monthly_fee}`:"—"],
                ["Contact", student.contact||"—"],
                ["Address", student.address||"—"],
                ["Village", student.village||"—"],
              ].map(([l,v])=>(
                <div key={l} style={{background:CREAM,borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:4}}>{l.toUpperCase()}</div>
                  <div style={{fontSize:14,fontWeight:700,color:"#111827"}}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          </div>
        )}

        {/* REPORT CARDS TAB */}
        {activeTab==="reportcards" && <ReportCardsTab reportCards={reportCards} />}

                {/* PERFORMANCE TAB — Unified Report Cards */}
        {activeTab==="performance" && (()=>{
          const GRADE_COLOR=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
          const getGrade=(m,t)=>{const p=(m/t)*100;return [{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}].find(g=>p>=g.min)?.label||"F";};
          const EXAM_ICON={monthly:"📅",midterm:"📋",final:"🎓",quiz:"⚡"};
          const EXAM_COLOR={monthly:"#2563eb",midterm:"#d97706",final:"#1a6b3c",quiz:"#7c3aed"};
          // Sort report cards chronologically (oldest first for timeline)
          const sorted = [...reportCards].sort((a,b)=>new Date(a.published_at)-new Date(b.published_at));
          const chartData = sorted.map(c=>({
            name:(EXAM_ICON[c.exam_type]||"📝")+" "+(c.exam_month?c.exam_month.substring(0,3):c.exam_type.charAt(0).toUpperCase()+c.exam_type.slice(1)),
            Academic:c.school_max>0?Math.round((c.school_total/c.school_max)*100):0,
            Hifz:c.hifz_max>0?Math.round((c.hifz_total/c.hifz_max)*100):null,
          }));
          const bestScore = reportCards.length?Math.max(...reportCards.map(c=>c.school_max>0?Math.round((c.school_total/c.school_max)*100):0)):0;
          const latestCard = reportCards[0];
          const latestScore = latestCard?.school_max>0?Math.round((latestCard.school_total/latestCard.school_max)*100):0;

          return (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* KPIs */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:12}}>
                {[
                  ["Best Score",`${bestScore}%`,G],
                  ["Latest",`${latestScore}%`,"#2563eb"],
                  ["Exams",reportCards.length,"#7c3aed"],
                  ["Attendance",`${pct}%`,pct>=80?G:pct>=60?"#d97706":"#e11d48"],
                ].map(([l,v,c])=>(
                  <div key={l} style={{background:"white",borderRadius:14,padding:"14px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:`3px solid ${c}`}}>
                    <div style={{fontSize:22,fontWeight:900,color:c}}>{v}</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:3}}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Trend chart */}
              {chartData.length>0&&(
                <div style={{background:"white",borderRadius:16,padding:"20px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
                  <div style={{fontWeight:800,fontSize:14,color:"#1a1a1a",marginBottom:16}}>📈 Performance Trend</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={chartData} margin={{top:5,right:10,left:0,bottom:5}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                      <XAxis dataKey="name" tick={{fontSize:10}}/>
                      <YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                      <Tooltip formatter={(v,n)=>[`${v}%`,n]}/>
                      <Line type="monotone" dataKey="Academic" stroke={G} strokeWidth={2.5} dot={{r:4,fill:G}} name="Academic"/>
                      {chartData.some(d=>d.Hifz!==null)&&<Line type="monotone" dataKey="Hifz" stroke="#7c3aed" strokeWidth={2.5} dot={{r:4,fill:"#7c3aed"}} name="Hifz"/>}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Timeline of report cards */}
              <div style={{background:"white",borderRadius:16,padding:"20px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
                <div style={{fontWeight:800,fontSize:14,color:"#1a1a1a",marginBottom:16}}>🗂️ Report Cards — Chronological</div>
                {sorted.length===0?(
                  <div style={{textAlign:"center",padding:32,color:"#9ca3af",fontSize:13}}>No report cards published yet. Your child's teacher will publish them after each exam.</div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    {sorted.map((card,idx)=>{
                      const schPct=card.school_max>0?Math.round((card.school_total/card.school_max)*100):0;
                      const schGrade=card.school_max>0?getGrade(schPct,100):null;
                      const hifzPct=card.hifz_max>0?Math.round((card.hifz_total/card.hifz_max)*100):null;
                      const hifzGrade=hifzPct!==null?getGrade(hifzPct,100):null;
                      const color=EXAM_COLOR[card.exam_type]||"#374151";
                      const icon=EXAM_ICON[card.exam_type]||"📝";
                      const isLatest=idx===sorted.length-1;
                      return (
                        <div key={card.id} style={{borderRadius:14,border:`1.5px solid ${isLatest?color+"60":C.border}`,overflow:"hidden",boxShadow:isLatest?`0 4px 16px ${color}20`:"0 1px 4px rgba(0,0,0,0.04)"}}>
                          {/* Card header */}
                          <div style={{padding:"14px 18px",background:isLatest?color+"0f":"#fafafa",display:"flex",alignItems:"center",gap:12,borderBottom:`1px solid ${C.border}`}}>
                            <div style={{width:40,height:40,borderRadius:12,background:color+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{icon}</div>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:800,color:color,fontSize:14}}>{card.exam_type.charAt(0).toUpperCase()+card.exam_type.slice(1)}{card.exam_month?" — "+card.exam_month:""}</div>
                              <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>Published {new Date(card.published_at).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}{isLatest?" · Latest":""}</div>
                            </div>
                            {schGrade&&(
                              <div style={{textAlign:"center",flexShrink:0}}>
                                <div style={{fontWeight:900,fontSize:22,color:GRADE_COLOR(schGrade)}}>{schGrade}</div>
                                <div style={{fontSize:11,color:"#6b7280"}}>{schPct}%</div>
                              </div>
                            )}
                          </div>
                          {/* Results detail */}
                          <div style={{padding:"14px 18px"}}>
                            {/* Academic subjects */}
                            {card.school_results?.length>0&&(
                              <div style={{marginBottom:card.hifz_results?.length>0?12:0}}>
                                <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Academic</div>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:6}}>
                                  {card.school_results.map(r=>{
                                    const g=getGrade(r.marks_obtained,r.total_marks);
                                    return (
                                      <div key={r.subject} style={{background:C.bg,borderRadius:8,padding:"7px 10px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                        <span style={{fontSize:12,color:"#374151",fontWeight:600}}>{r.subject}</span>
                                        <span style={{fontWeight:800,color:GRADE_COLOR(g),fontSize:12}}>{r.marks_obtained}/{r.total_marks}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div style={{display:"flex",justifyContent:"space-between",marginTop:8,padding:"8px 10px",background:color+"10",borderRadius:8}}>
                                  <span style={{fontSize:12,fontWeight:700,color:color}}>Total Academic</span>
                                  <span style={{fontSize:13,fontWeight:900,color:color}}>{card.school_total}/{card.school_max} ({schPct}%) — {schGrade}</span>
                                </div>
                              </div>
                            )}
                            {/* Hifz subjects */}
                            {card.hifz_results?.length>0&&(
                              <div>
                                <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Hifz</div>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:6}}>
                                  {card.hifz_results.map(r=>{
                                    const g=getGrade(r.marks_obtained,r.total_marks);
                                    return (
                                      <div key={r.subject} style={{background:"#f5f3ff",borderRadius:8,padding:"7px 10px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                        <span style={{fontSize:12,color:"#5b21b6",fontWeight:600}}>{r.subject}</span>
                                        <span style={{fontWeight:800,color:GRADE_COLOR(g),fontSize:12}}>{r.marks_obtained}/{r.total_marks}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                {hifzPct!==null&&<div style={{display:"flex",justifyContent:"space-between",marginTop:8,padding:"8px 10px",background:"#f5f3ff",borderRadius:8}}>
                                  <span style={{fontSize:12,fontWeight:700,color:"#7c3aed"}}>Total Hifz</span>
                                  <span style={{fontSize:13,fontWeight:900,color:"#7c3aed"}}>{card.hifz_total}/{card.hifz_max} ({hifzPct}%) — {hifzGrade}</span>
                                </div>}
                              </div>
                            )}
                            {/* Hifz Summary (auto-attached) */}
                            {card.hifz_summary&&(card.hifz_summary.current_juz>0||card.hifz_summary.month_sabaq_pages>0)&&(()=>{
                              const hs = card.hifz_summary;
                              const JUZ_PP=20;
                              const toJuz=p=>p>=JUZ_PP?`${(p/JUZ_PP).toFixed(1)} Juz`:p>0?`${p}p`:"—";
                              return (
                                <div style={{marginTop:12,background:"linear-gradient(135deg,#f5f3ff,#ede9fe)",borderRadius:10,padding:"12px 14px",border:"1px solid #ddd6fe"}}>
                                  <div style={{fontSize:11,fontWeight:800,color:"#4c1d95",marginBottom:8}}>📖 Hifz Progress — {card.exam_month||"This Period"}</div>
                                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                                    <span style={{background:"#4c1d95",color:"white",borderRadius:8,padding:"3px 10px",fontSize:11,fontWeight:700}}>پارہ {hs.current_juz} — {hs.juz_ar}</span>
                                    {hs.current_surah&&<span style={{background:"white",color:"#7c3aed",borderRadius:8,padding:"3px 10px",fontSize:11,fontWeight:700,border:"1px solid #ddd6fe"}}>{hs.current_surah}</span>}
                                  </div>
                                  <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:6}}>
                                    {[["📗 سبق (Sabaq)",hs.month_sabaq_pages,"#1a6b3c"],["📘 سبقی (Sabqi)",hs.month_sabqi_pages,"#2563eb"],["📕 منزل (Manzil)",hs.month_manzil_pages,"#7c3aed"]].map(([lbl,pages,color])=>(
                                      <div key={lbl} style={{background:"white",borderRadius:8,padding:"6px 8px",textAlign:"center"}}>
                                        <div style={{fontSize:10,color,fontWeight:700}}>{lbl}</div>
                                        <div style={{fontWeight:900,color,fontSize:14}}>{pages||0} pages</div>
                                        <div style={{fontSize:9,color:"#9ca3af"}}>{toJuz(pages||0)}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <div style={{fontSize:11,color:"#6b7280"}}>
                                    {hs.month_sessions||0} sessions · {hs.month_absent||0} absent
                                  </div>
                                </div>
                              );
                            })()}
                            {/* Teacher comment */}
                            {card.teacher_comment&&(
                              <div style={{marginTop:12,padding:"10px 14px",background:"#fffbeb",borderRadius:8,border:"1px solid #fde68a"}}>
                                <div style={{fontSize:11,fontWeight:700,color:"#92400e",marginBottom:4}}>💬 Class Teacher's Comment</div>
                                <div style={{fontSize:13,color:"#78350f",fontStyle:"italic"}}>"{card.teacher_comment}"</div>
                              </div>
                            )}
                            {card.remarks&&(
                              <div style={{marginTop:8,fontSize:12,color:"#6b7280"}}>📝 Remarks: <span style={{fontWeight:700,color:"#374151"}}>{card.remarks}</span></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Hifz Monthly Summary — only for Hifz students ── */}
              {isHifz && hifzLogs.length > 0 && (()=>{
                const JUZ_PP = 20;
                const MONTHS_ORDER = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                const sumP = (arr, k) => Math.round(arr.reduce((s,l)=>s+(Number(l[k])||0),0)*10)/10;
                const byMonthH = {};
                hifzLogs.forEach(l=>{
                  if(!l.date) return;
                  const d = new Date(l.date);
                  const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                  if(!byMonthH[key]) byMonthH[key]={year:d.getFullYear(),month:MONTHS_ORDER[d.getMonth()],key,logs:[]};
                  byMonthH[key].logs.push(l);
                });
                const hifzMonths = Object.values(byMonthH).sort((a,b)=>b.key.localeCompare(a.key));
                if(!hifzMonths.length) return null;
                return (
                  <div style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
                    <div style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",padding:"14px 20px"}}>
                      <div style={{fontWeight:800,fontSize:14,color:"white"}}>📖 Hifz Monthly Progress</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",marginTop:2}}>سبق · سبقی · منزل — monthly breakdown</div>
                    </div>
                    {hifzMonths.map(({year,month,key,logs},idx)=>{
                      const present = logs.filter(l=>l.was_present!==false);
                      const absent  = logs.filter(l=>l.was_present===false);
                      const sabaq   = sumP(logs,"sabaq_pages");
                      const sabqi   = sumP(logs,"sabqi_pages");
                      const manzil  = sumP(logs,"manzil_pages");
                      return (
                        <div key={key} style={{padding:"16px 20px",borderBottom:idx<hifzMonths.length-1?"1px solid #f3f4f6":"none"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6}}>
                            <div style={{fontWeight:800,fontSize:14,color:"#4c1d95"}}>{month} {year}</div>
                            <div style={{display:"flex",gap:6}}>
                              <span style={{background:"#dcfce7",color:"#15803d",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>✅ {present.length}</span>
                              {absent.length>0&&<span style={{background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>❌ {absent.length}</span>}
                            </div>
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            {[
                              {label:"📗 سبق",en:"Sabaq",pages:sabaq, color:"#1a6b3c",bg:"#f0fdf4",border:"#bbf7d0"},
                              {label:"📘 سبقی",en:"Sabqi", pages:sabqi, color:"#1d4ed8",bg:"#eff6ff",border:"#bfdbfe"},
                              {label:"📕 منزل",en:"Manzil",pages:manzil,color:"#5b21b6",bg:"#f5f3ff",border:"#ddd6fe"},
                            ].map(({label,en,pages,color,bg,border})=>{
                              const juzEq = pages>0?(pages/JUZ_PP).toFixed(1):null;
                              return (
                                <div key={label} style={{background:bg,borderRadius:10,padding:"10px 14px",border:`1px solid ${border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                  <div><span style={{fontWeight:800,color,fontSize:13}}>{label}</span><span style={{fontSize:10,color:"#9ca3af",marginLeft:6}}>{en}</span></div>
                                  {juzEq ? (
                                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                                      <span style={{fontSize:22,fontWeight:900,color,lineHeight:1}}>{pages}</span>
                                      <span style={{fontSize:11,color,fontWeight:600,opacity:.7}}>صفحات</span>
                                      <span style={{fontSize:16,color,opacity:.3}}>≡</span>
                                      <span style={{fontSize:22,fontWeight:900,color,lineHeight:1}}>{juzEq}</span>
                                      <span style={{fontSize:11,color,fontWeight:600,opacity:.7}}>جز</span>
                                    </div>
                                  ) : <span style={{color:"#9ca3af",fontSize:12,fontStyle:"italic"}}>No entries</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

            </div>
          );
        })()}

        {/* ATTENDANCE TAB — Calendar View */}
        {activeTab==="attendance" && (()=>{
          const MONTHS_LIST = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          // Group by month
          const byMonth = attendance.reduce((acc, a) => {
  if (!a?.date) return acc;
  const d = new Date(a.date);
  if (Number.isNaN(d.getTime())) return acc;

  const key = `${d.getFullYear()}-${d.getMonth()}`;
  if (!acc[key]) {
    acc[key] = { year: d.getFullYear(), month: d.getMonth(), days: {} };
  }

  acc[key].days[d.getDate()] = readAttendanceStatus(a);
  return acc;
}, {});
        
          const months = Object.values(byMonth).sort((a,b)=>b.year-a.year||(b.month-a.month));
          return (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Refresh */}
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <button onClick={()=>{
                  const email = session?.user?.email;
                  if(!email) return;
                  supabase.from("parents").select("student_id").eq("email",email).single().then(({data:p})=>{
                    if(!p?.student_id) return;
                    supabase.from("attendance").select("*").eq("student_id",p.student_id).order("date",{ascending:false})
                      .then(({data})=>{ if(data) setAttendance(data); });
                  });
                }} style={{background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:8,
                  padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                  🔄 Refresh
                </button>
              </div>
              {/* Summary */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:12}}>
                {[
  ["Present", present, "#16a34a"],
  ["Absent", absent, "#e11d48"],
  ["Late", late, "#d97706"],
  ["Leave", leave, "#7c3aed"],
  ["Rate", `${pct}%`, pct >= 80 ? G : pct >= 60 ? "#d97706" : "#e11d48"]
].map(([l, v, c])=>(
                  <div key={l} style={{background:"white",borderRadius:14,padding:"16px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:`3px solid ${c}`}}>
                    <div style={{fontSize:26,fontWeight:900,color:c}}>{v}</div>
                    <div style={{fontSize:12,color:"#6b7280",marginTop:4}}>{l}</div>
                  </div>
                ))}
              </div>
              {/* Monthly calendars */}
              {months.length===0
                ? <div style={{background:"white",borderRadius:16,padding:40,textAlign:"center",color:"#9ca3af"}}>No attendance records yet.</div>
                : months.map(({year,month,days})=>{
                  const firstDay = new Date(year, month, 1).getDay();
                  const daysInMonth = new Date(year, month + 1, 0).getDate();
                  const mPresent = Object.values(days).filter((s) => s === "P").length;
                  const mAbsent = Object.values(days).filter((s) => s === "A").length;
                  const mLate = Object.values(days).filter((s) => s === "L").length;
                  const mLeave = Object.values(days).filter((s) => s === "LV").length;
                  const mTotal = Object.values(days).length;
                  const mPct = mTotal > 0 ? Math.round(((mPresent + mLate) / mTotal) * 100) : 0;
                  return (
                    <div key={`${year}-${month}`} style={{background:"white",borderRadius:16,padding:"20px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                        <div style={{fontWeight:800,fontSize:15,color:"#1a1a1a"}}>{MONTHS_LIST[month]} {year}</div>
                        <div style={{display:"flex",gap:8,fontSize:12,flexWrap:"wrap"}}>
                          <span style={{background:"#f0fdf4",color:"#16a34a",fontWeight:700,borderRadius:6,padding:"2px 8px"}}>✓ {mPresent} Present</span>
                          <span style={{background:"#fef2f2",color:"#e11d48",fontWeight:700,borderRadius:6,padding:"2px 8px"}}>✗ {mAbsent} Absent</span>
                          {mLate>0&&<span style={{background:"#fff7ed",color:"#c2410c",fontWeight:700,borderRadius:6,padding:"2px 8px"}}>🕐 {mLate} Late</span>}
                          {mLeave>0&&<span style={{background:"#f5f3ff",color:"#6d28d9",fontWeight:700,borderRadius:6,padding:"2px 8px"}}>📋 {mLeave} Leave</span>}
                          <span style={{background:"#f3f4f6",color:"#374151",fontWeight:700,borderRadius:6,padding:"2px 8px"}}>{mPct}%</span>
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
                        {["S","M","T","W","T","F","S"].map((d,i)=>(
                          <div key={i} style={{textAlign:"center",fontSize:10,fontWeight:700,color:"#9ca3af",padding:"2px 0"}}>{d}</div>
                        ))}
                        {Array.from({length:firstDay}).map((_,i)=><div key={`e${i}`}/>)}
                        {Array.from({length:daysInMonth},(_,i)=>i+1).map(day=>{
                          const status = days[day];
const meta = statusMeta(status);
const S2 = useSettings();
const offDaysList = (S2?.off_days||"Saturday,Sunday").split(",").map(d=>d.trim());
const cellDateObj = new Date(`${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`);
const cellDayName = cellDateObj.toLocaleDateString("en-GB",{weekday:"long"});
const isOffDayCell = offDaysList.includes(cellDayName);

const calBg =
  isOffDayCell && !status ? "#f3f4f6" :
  status === "P"  ? "#dcfce7" :
  status === "A"  ? "#fee2e2" :
  status === "L"  ? "#fed7aa" :
  status === "LV" ? "#ddd6fe" :
  "#f9fafb";
const calBorder =
  isOffDayCell && !status ? "1px solid #e5e7eb" :
  status === "P"  ? "2px solid #16a34a" :
  status === "A"  ? "2px solid #dc2626" :
  status === "L"  ? "2px solid #ea580c" :
  status === "LV" ? "2px solid #7c3aed" :
  "1px solid #e5e7eb";

return (
  <div
    key={day}
    title={meta.label}
    style={{
      textAlign: "center",
      fontSize: 10,
      fontWeight: 800,
      padding: "4px 2px 2px",
      borderRadius: 6,
      background: calBg,
      color: isOffDayCell&&!status?"#9ca3af":meta.color,
      border: calBorder,
      lineHeight: 1.2,
      opacity: isOffDayCell&&!status?0.5:1,
    }}
  >
    <div>{day}</div>
    {isOffDayCell&&!status ? <div style={{fontSize:7,color:"#9ca3af"}}>off</div>
      : status === "L"  ? <div style={{fontSize:8}}>🕐</div>
      : status === "LV" ? <div style={{fontSize:8}}>📋</div>
      : status === "A"  ? <div style={{fontSize:8}}>✗</div>
      : null}
  </div>
);
                        })}
                      </div>
                      <div style={{display:"flex",gap:12,fontSize:11,flexWrap:"wrap"}}>
  <span style={{display:"flex",alignItems:"center",gap:4}}>
    <span style={{width:10,height:10,background:"#dcfce7",border:"1px solid #bbf7d0",borderRadius:2,display:"inline-block"}} />
    Present
  </span>
  <span style={{display:"flex",alignItems:"center",gap:4}}>
    <span style={{width:10,height:10,background:"#fef2f2",border:"1px solid #fecaca",borderRadius:2,display:"inline-block"}} />
    Absent
  </span>
  <span style={{display:"flex",alignItems:"center",gap:4}}>
    <span style={{width:10,height:10,background:"#fff7ed",border:"1px solid #fdba74",borderRadius:2,display:"inline-block"}} />
    Late
  </span>
  <span style={{display:"flex",alignItems:"center",gap:4}}>
    <span style={{width:10,height:10,background:"#f5f3ff",border:"1px solid #c4b5fd",borderRadius:2,display:"inline-block"}} />
    Leave
  </span>
  <span style={{display:"flex",alignItems:"center",gap:4}}>
    <span style={{width:10,height:10,background:"#f9fafb",border:"1px solid #f3f4f6",borderRadius:2,display:"inline-block"}} />
    No record
  </span>
</div>
                    </div>
                  );
                })
              }
            </div>
          );
        })()}

        {/* FEES TAB — Interactive monthly calendar view */}
        {activeTab==="fees" && (()=>{
          const MONTHS_ORDER = ["January","February","March","April","May","June","July","August","September","October","November","December"];
          // Group fees by month+year
          const byMonth = {};
          fees.forEach(f=>{
            const key = `${f.year||new Date(f.created_at||Date.now()).getFullYear()}-${String(MONTHS_ORDER.indexOf(f.month)+1).padStart(2,"0")}`;
            if(!byMonth[key]) byMonth[key]={year:f.year||String(new Date(f.created_at||Date.now()).getFullYear()),month:f.month,key,records:[]};
            byMonth[key].records.push(f);
          });
          const months = Object.values(byMonth).sort((a,b)=>b.key.localeCompare(a.key));
          const totalPaid = fees.filter(f=>f.method&&f.method!=="Unpaid").reduce((s,f)=>s+(Number(f.amount)||0),0);
          const expandedMonth = expandedFeeMonth ?? (months[0]?.key||null);
          const setExpandedMonth = setExpandedFeeMonth;
          return (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              {/* Summary strip */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
                {[
                  {l:"✅ Paid",v:paidFees,sub:`PKR ${totalPaid.toLocaleString()}`,c:"#16a34a",bg:"#f0fdf4"},
                  {l:"❌ Unpaid",v:fees.length-paidFees,sub:`${fees.length} total months`,c:"#e11d48",bg:"#fef2f2"},
                  {l:"📊 Rate",v:fees.length?Math.round((paidFees/fees.length)*100)+"%":"—",sub:"payment rate",c:"#2563eb",bg:"#eff6ff"},
                ].map(k=>(
                  <div key={k.l} style={{background:"white",borderRadius:14,padding:"16px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:`3px solid ${k.c}`}}>
                    <div style={{fontSize:11,color:"#6b7280",fontWeight:600,marginBottom:4}}>{k.l}</div>
                    <div style={{fontSize:26,fontWeight:900,color:k.c,lineHeight:1}}>{k.v}</div>
                    <div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {fees.length===0 ? (
                <div style={{background:"white",borderRadius:16,padding:48,textAlign:"center",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
                  <div style={{fontSize:36,marginBottom:8}}>💰</div>
                  <div style={{fontWeight:700,color:"#374151",fontSize:15}}>No fee records yet.</div>
                  <div style={{fontSize:13,color:"#9ca3af",marginTop:4}}>Fee records will appear here once added by the school.</div>
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {months.map(({year,month,key,records})=>{
                    const paid = records.filter(f=>f.method&&f.method!=="Unpaid");
                    const unpaid = records.filter(f=>!f.method||f.method==="Unpaid");
                    const total = paid.reduce((s,f)=>s+(Number(f.amount)||0),0);
                    const isPaid = paid.length>0;
                    const isOpen = expandedMonth===key;
                    return (
                      <div key={key} style={{background:"white",borderRadius:16,overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:`1.5px solid ${isPaid?"#bbf7d0":"#fecaca"}`}}>
                        {/* Month header — clickable */}
                        <button onClick={()=>setExpandedMonth(isOpen?null:key)}
                          style={{width:"100%",background:isPaid?"#f0fdf4":"#fef2f2",padding:"14px 18px",border:"none",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",textAlign:"left"}}>
                          <div style={{display:"flex",alignItems:"center",gap:12}}>
                            <div style={{width:42,height:42,borderRadius:10,background:isPaid?"#16a34a":"#e11d48",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                              {isPaid?"✅":"❌"}
                            </div>
                            <div>
                              <div style={{fontWeight:800,fontSize:14,color:"#111827"}}>{month} {year}</div>
                              <div style={{fontSize:12,color:"#6b7280",marginTop:1}}>
                                {isPaid ? `PKR ${total.toLocaleString()} · ${paid[0]?.method||"Cash"}` : "Payment pending"}
                              </div>
                            </div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <span style={{background:isPaid?"#dcfce7":"#fef2f2",color:isPaid?"#16a34a":"#e11d48",borderRadius:20,padding:"4px 14px",fontSize:12,fontWeight:800}}>
                              {isPaid?"Paid":"Unpaid"}
                            </span>
                            <span style={{fontSize:14,color:"#9ca3af"}}>{isOpen?"▲":"▼"}</span>
                          </div>
                        </button>
                        {/* Expanded detail */}
                        {isOpen && (
                          <div style={{padding:"14px 18px",borderTop:`1px solid ${isPaid?"#bbf7d0":"#fecaca"}40`}}>
                            {records.map((f,i)=>(
                              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<records.length-1?"1px solid #f3f4f6":"none"}}>
                                <div>
                                  {f.amount&&<div style={{fontSize:14,fontWeight:800,color:"#111827"}}>PKR {Number(f.amount).toLocaleString()}</div>}
                                  {f.paid_date&&<div style={{fontSize:11,color:"#9ca3af",marginTop:1}}>📅 {f.paid_date}</div>}
                                  {f.notes&&<div style={{fontSize:11,color:"#6b7280",marginTop:1,fontStyle:"italic"}}>💬 {f.notes}</div>}
                                </div>
                                <div style={{textAlign:"right"}}>
                                  {f.method&&f.method!=="Unpaid"&&<span style={{background:"#eff6ff",color:"#2563eb",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:700}}>{f.method}</span>}
                                  {f.receipt_no&&(
                                  <button
                                    onClick={e=>{e.stopPropagation();setViewingReceipt({stu:student,fee:f});}}
                                    style={{
                                      marginTop:6,background:"#f0f7f2",color:"#1a6b3c",
                                      border:"1.5px solid #d1e8d8",borderRadius:8,
                                      padding:"5px 12px",fontSize:11,fontWeight:700,
                                      cursor:"pointer",display:"flex",alignItems:"center",gap:5,
                                      transition:"all .15s"
                                    }}
                                    onMouseEnter={e=>{e.currentTarget.style.background="#d1e8d8"}}
                                    onMouseLeave={e=>{e.currentTarget.style.background="#f0f7f2"}}>
                                    🧾 View Receipt · <span style={{opacity:.7}}>{f.receipt_no}</span>
                                  </button>
                                )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ANNOUNCEMENTS TAB */}
        {activeTab==="announcements" && <ParentAnnouncementsTab announcements={announcements} student={student} session={session}/>}

        {/* HIFZ UNIFIED TAB */}
        {activeTab==="hifzlog" && <HifzParentTab hifzProgress={hifzProgress} hifzJourney={hifzJourney} hifzLogs={hifzLogs} weeklyReports={weeklyReports}/>}

        {/* EVENTS TAB */}
        {activeTab==="events" && <ParentEventsTab studentClass={student.class||student["class"]} session={session}/>}

        {activeTab==="leave" && <LeaveRequestParent
  studentId={student?.id}
  studentName={student?.full_name || student?.name}
  parentEmail={session?.user?.email}/>}


        {/* HOMEWORK TAB */}
        {activeTab==="homework" && <ParentHomeworkTab student={student}/>}

        {/* SETTINGS TAB */}
        {activeTab==="settings" && (
          <div style={{maxWidth:480}}>
            <div style={{background:"white",borderRadius:16,padding:"24px",boxShadow:"0 2px 12px rgba(0,0,0,0.05)"}}>
              <div style={{fontWeight:800,fontSize:16,color:"#1a1a1a",marginBottom:20}}>Change Password</div>
              {passMsg && (
                <div style={{background:passMsg.ok?"#dcfce7":"#fef2f2",color:passMsg.ok?"#16a34a":"#dc2626",borderRadius:8,padding:"10px 14px",fontSize:13,fontWeight:600,marginBottom:16}}>
                  {passMsg.text}
                </div>
              )}
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:5}}>NEW PASSWORD</label>
                  <input type="password" value={newPass} onChange={e=>setNewPass(e.target.value)}
                    placeholder="Enter new password" style={{width:"100%",padding:"10px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:700,color:"#374151",display:"block",marginBottom:5}}>CONFIRM PASSWORD</label>
                  <input type="password" value={confirmPass} onChange={e=>setConfirmPass(e.target.value)}
                    placeholder="Confirm new password" style={{width:"100%",padding:"10px 12px",border:"1px solid #d1d5db",borderRadius:8,fontSize:14,boxSizing:"border-box"}}/>
                </div>
                <button onClick={async()=>{
                  setPassMsg(null);
                  if(!newPass||newPass.length<6) return setPassMsg({ok:false,text:"Password must be at least 6 characters."});
                  if(newPass!==confirmPass) return setPassMsg({ok:false,text:"Passwords do not match."});
                  const {error} = await supabase.auth.updateUser({password:newPass});
                  if(error) setPassMsg({ok:false,text:"Error: "+error.message});
                  else { setPassMsg({ok:true,text:"Password changed successfully!"}); setNewPass(""); setConfirmPass(""); }
                }} style={{background:G,color:"white",border:"none",borderRadius:9,padding:"12px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  Update Password
                </button>
              </div>
              <div style={{marginTop:16,fontSize:12,color:"#9ca3af",lineHeight:1.6}}>
                Choose a strong password. You will use it every time you log in.
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ── FEE MANAGEMENT — v3 (cascade: stream→class→student, scholarship linked) ──
// ── Fee constants ────────────────────────────────────────────────
const FEE_TYPES_LIST = [
  { id:"tuition",   label:"Tuition",         recurring:true,  streams:["School Only","Both (Hifz + School)","College"] },
  { id:"hifz",      label:"Hifz Fee",        recurring:true,  streams:["Both (Hifz + School)","Hifz Only"] },
  { id:"transport", label:"Transport/Van",   recurring:true,  streams:"all" },
  { id:"boarding",  label:"Boarding/Hostel", recurring:true,  streams:"all" },
  { id:"exam",      label:"Exam Fee",        recurring:false, streams:"all" },
  { id:"books",     label:"Books/Stationery",recurring:false, streams:"all" },
  { id:"admission", label:"Admission",       recurring:false, streams:"all" },
  { id:"custom",    label:"Custom Charge",   recurring:false, streams:"all" },
];

// Which fee types are relevant for a given stream
function feeTypesForStream(stream) {
  return FEE_TYPES_LIST.filter(f =>
    f.streams === "all" || f.streams.includes(stream)
  );
}

// Stream → default colour (for badges)
const STREAM_COLORS = {
  "School Only":            { bg:"#e8f5ee", color:"#1a6b3c", border:"#c3e6d0" },
  "Both (Hifz + School)":  { bg:"#ede9fe", color:"#5b21b6", border:"#c4b5fd" },
  "Hifz Only":             { bg:"#fef3c7", color:"#92400e", border:"#fde68a" },
  "College":               { bg:"#e0f2fe", color:"#075985", border:"#bae6fd" },
};

// All streams in the system
const ALL_STREAMS = ["School Only","Both (Hifz + School)","Hifz Only","College"];

// Classes per stream
const STREAM_CLASSES = {
  "School Only":           ["Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8"],
  "Both (Hifz + School)": ["Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8"],
  "Hifz Only":            [],
  "College":              ["Class 9","Class 10","Class 11","Class 12"],
};

// Scholarship badge text helper
function scholBadge(scholarshipStatus) {
  if(!scholarshipStatus || scholarshipStatus==="None") return null;
  if(scholarshipStatus.toLowerCase().includes("full")) return "Scholarship — Full Waiver";
  if(scholarshipStatus.includes("50%")) return "Scholarship — 50% Discount";
  if(scholarshipStatus.toLowerCase().includes("partial")) return "Scholarship — Partial";
  if(scholarshipStatus.toLowerCase().includes("active")) return "Scholarship — Active";
  return `Scholarship — ${scholarshipStatus}`;
}

// Resolve the cascaded fee for a student (stream defaults → class tuition → student override → scholarship)
function resolveFee(student, feeConfig, streamDefaults, classTuition) {
  const stream = student.stream || "School Only";
  const cls    = student.class  || student["class"] || "";
  const scStatus = student.scholarship_status || "None";
  const cfg    = feeConfig[student.id] || {};

  // Build per-type breakdown
  const types = feeTypesForStream(stream);
  const breakdown = {};

  types.forEach(ft => {
    let amount = 0;

    if(ft.id === "tuition") {
      // Class tuition grid — find matching range
      const ctEntry = (classTuition || []).find(ct => ct.class === cls || ct.classRange?.includes(cls));
      const streamTuition = (streamDefaults[stream]||{})[ft.id] || 0;
      amount = ctEntry?.amount ?? streamTuition;
    } else if(ft.id === "hifz") {
      amount = (streamDefaults[stream]||{})[ft.id] || 0;
    } else {
      amount = (streamDefaults[stream]||{})[ft.id] || 0;
    }

    // Student-level override wins
    if(cfg[ft.id] !== undefined) amount = cfg[ft.id];

    // Scholarship — apply discount to tuition (and hifz for full waiver)
    let scholarshipNote = null;
    let originalAmount  = null;
    if(scStatus && scStatus !== "None" && (ft.id === "tuition" || ft.id === "hifz")) {
      if(scStatus.toLowerCase().includes("full")) {
        originalAmount = amount; amount = 0; scholarshipNote = "Full waiver";
      } else if(scStatus.includes("50%")) {
        originalAmount = amount; amount = Math.round(amount/2); scholarshipNote = "50% discount";
      } else if(scStatus.toLowerCase().includes("partial") && cfg.scholarship_override_tuition !== undefined) {
        originalAmount = amount; amount = cfg.scholarship_override_tuition; scholarshipNote = "Partial scholarship";
      }
    }

    breakdown[ft.id] = { amount, originalAmount, scholarshipNote, enabled: amount > 0 || originalAmount > 0 };
  });

  // Custom charge always from student cfg
  if(cfg.custom_amount) breakdown.custom = { amount: cfg.custom_amount, label: cfg.custom_note || "Custom charge", enabled: true };

  const total = Object.values(breakdown).reduce((s,v) => s + (v.amount||0), 0);
  return { breakdown, total, types };
}

const FEE_MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FEE_NOW      = new Date();
const FEE_CUR_MONTH = FEE_NOW.getMonth();
const FEE_CUR_YEAR  = FEE_NOW.getFullYear();
const fmtPKR = n => "PKR " + Number(n||0).toLocaleString();
const feePct = (a,b) => b ? Math.round((a/b)*100) : 0;

function feeOpenWA(phone, msg){
  if(!phone) return;
  const d = String(phone).replace(/\D/g,"");
  const intl = d.startsWith("0")?"92"+d.slice(1):d.startsWith("92")?d:"92"+d;
  window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
}
function feeDownloadCSV(rows, filename){
  if(!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map(r=>headers.map(h=>{
    const v = r[h]??"";
    return typeof v==="string" && v.includes(",") ? `"${v}"` : v;
  }).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
  a.download = filename; a.click();
}

// ── Scholarship badge component ───────────────────────────────────
function ScholBadge({ status, small }){
  const label = scholBadge(status);
  if(!label) return null;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4,
      background:"#e0faea", color:"#14532d", border:"1px solid #86efac",
      borderRadius:20, padding: small ? "1px 8px" : "3px 10px",
      fontSize: small ? 10 : 11, fontWeight:700, whiteSpace:"nowrap",
    }}>
      🎓 {label}
    </span>
  );
}

function FeeManagement({ students, fees: legacyFees = [], onFeesSaved }) {
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const THIS_YEAR  = String(new Date().getFullYear());
  const THIS_MONTH = new Date().toLocaleString('default',{month:'long'});

  // ── State ────────────────────────────────────────────────────────
  const [fees, setFees]               = useState(legacyFees);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [activeTab, setActiveTab]     = useState("submit");
  const [waReceipt, setWaReceipt]     = useState(null);
  const [showPendingWa, setShowPendingWa] = useState(false);
  const [challanStudent, setChallanStudent]   = useState(null);
  const [challanFeeRecord, setChallanFeeRecord] = useState(null);
  const [feeReceiptRecord, setFeeReceiptRecord] = useState(null);
  const [filterMonth, setFilterMonth] = useState("All");
  const [filterClass, setFilterClass] = useState("All");
  const [toast, setToast]             = useState(null);

  // cascade config
  const [streamDefaults, setStreamDefaults] = useState({});    // { "School Only": { tuition:1500, transport:500 }, ... }
  const [classTuition, setClassTuition]     = useState([]);    // [{ class:"Class 1", amount:1200 }, ...]
  const [feeConfig, setFeeConfig]           = useState({});    // per-student overrides
  const [feePayments, setFeePayments]       = useState([]);    // new fee_payments rows

  // Fee Types tab sub-section
  const [typesSection, setTypesSection] = useState("stream"); // "stream" | "class" | "student"
  const [feeConfigEdit, setFeeConfigEdit]     = useState(null);
  const [feeConfigDraft, setFeeConfigDraft]   = useState({});
  const [streamDraft, setStreamDraft]         = useState({});
  const [streamEditTarget, setStreamEditTarget] = useState(null); // which stream being edited
  const [classTuitionDraft, setClassTuitionDraft] = useState([]);

  // Record payment
  const [collectModal, setCollectModal] = useState(null);
  const [collectDraft, setCollectDraft] = useState({});
  const [collectChecked, setCollectChecked] = useState({});  // which fee types checked

  // Analytics
  const [analyticsMonth, setAnalyticsMonth] = useState(FEE_CUR_MONTH);
  const [analyticsYear, setAnalyticsYear]   = useState(FEE_CUR_YEAR);

  const toastTimer = useRef(null);
  const showToast = (msg, ok=true) => { clearTimeout(toastTimer.current); setToast({msg,ok}); toastTimer.current = setTimeout(()=>setToast(null),3000); };

  const emptyForm = {studentId:"",amount:"",month:THIS_MONTH,year:THIS_YEAR,method:"Cash",notes:""};
  const [form, setForm] = useState(emptyForm);

  // Bulk fee recording
  const [bulkClass,    setBulkClass]    = useState("");
  const [bulkMonth,    setBulkMonth]    = useState(THIS_MONTH);
  const [bulkYear,     setBulkYear]     = useState(THIS_YEAR);
  const [bulkMethod,   setBulkMethod]   = useState("Cash");
  const [bulkChecked,  setBulkChecked]  = useState({});   // { studentId: true/false }
  const [bulkAmounts,  setBulkAmounts]  = useState({});   // { studentId: override amount }
  const [bulkSaving,   setBulkSaving]   = useState(false);

  // Custom charges (fines, challans, discipline)
  const CHARGE_TYPES = [
    { id:"challan",    label:"Bank Challan",      icon:"🏦", color:"#2563eb" },
    { id:"fine",       label:"Late Fee Fine",      icon:"⏰", color:"#dc2626" },
    { id:"discipline", label:"Discipline Fine",    icon:"📋", color:"#7c3aed" },
    { id:"exam_fee",   label:"Exam Fee",           icon:"📝", color:"#d97706" },
    { id:"books",      label:"Books / Stationery", icon:"📚", color:"#0d9488" },
    { id:"transport",  label:"Transport",          icon:"🚌", color:"#1a6b3c" },
    { id:"other",      label:"Other",              icon:"💼", color:"#374151" },
  ];
  const emptyCharge = { student_id:"", charge_type:"fine", amount:"", date:new Date().toISOString().split("T")[0], reason:"", method:"Cash", recorded_by:"Admin" };
  const [customCharges,    setCustomCharges]    = useState([]);
  const [chargeForm,       setChargeForm]       = useState(emptyCharge);
  const [chargeSaving,     setChargeSaving]     = useState(false);
  const [chargeFilter,     setChargeFilter]     = useState("all");
  const [chargesLoaded,    setChargesLoaded]    = useState(false);

  // ── Load data ────────────────────────────────────────────────────
  useEffect(()=>{
    sheetAPI({action:"getFees"}).then(r=>{ if(r.fees) setFees(r.fees); setLoading(false); });
    (async()=>{
      try {
        const { data:configs } = await supabase.from("fee_config").select("*");
        if(configs){ const m={}; configs.forEach(c=>{ m[c.student_id]=c.config; }); setFeeConfig(m); }
        const { data:payments } = await supabase.from("fee_payments").select("*");
        if(payments) setFeePayments(payments);
        // Load stream defaults + class tuition from fee_structure table
        const { data:fstruct } = await supabase.from("fee_structure").select("*");
        if(fstruct){
          const sd={}, ct=[];
          fstruct.forEach(r=>{
            if(r.type==="stream") { if(!sd[r.stream]) sd[r.stream]={}; sd[r.stream][r.fee_type]=Number(r.amount); }
            if(r.type==="class")  ct.push({ class:r.class_name, amount:Number(r.amount) });
          });
          setStreamDefaults(sd);
          setClassTuition(ct);
          setClassTuitionDraft(ct.length ? ct : STREAM_CLASSES["School Only"].concat(STREAM_CLASSES["College"]).map(c=>({class:c,amount:0})));
        } else {
          setClassTuitionDraft(STREAM_CLASSES["School Only"].concat(STREAM_CLASSES["College"]).map(c=>({class:c,amount:0})));
        }
      } catch(e){ console.warn("Fee config load:", e); }
    })();
  },[]);

  useEffect(()=>{ if(legacyFees.length>fees.length) setFees(legacyFees); },[legacyFees]);

  // Lazy-load custom charges when that tab is first opened
  useEffect(()=>{
    if(activeTab==="custom" && !chargesLoaded){
      supabase.from("custom_charges").select("*").order("date",{ascending:false}).then(({data})=>{
        setCustomCharges(data||[]); setChargesLoaded(true);
      }).catch(()=>setChargesLoaded(true));
    }
  },[activeTab,chargesLoaded]);

  const selectedStudent = students.find(s=>s.id===form.studentId);
  const selectedResolved = selectedStudent ? resolveFee(selectedStudent, feeConfig, streamDefaults, classTuition) : null;

  // Auto-init checkboxes when student or fee config changes
  useEffect(()=>{
    if(!selectedResolved) return;
    const checks = {};
    Object.entries(selectedResolved.breakdown).forEach(([id,v])=>{ checks[`checked_${id}`] = v.enabled && v.amount > 0; });
    setForm(f=>({ ...f, ...checks }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[form.studentId, feeConfig, streamDefaults, classTuition]);

  // ── Legacy stats ─────────────────────────────────────────────────
  const totalCollected   = fees.reduce((a,f)=>a+Number(f.amount||0),0);
  const thisMonthFees    = fees.filter(f=>f.month===THIS_MONTH&&f.year===THIS_YEAR);
  const thisMonthTotal   = thisMonthFees.reduce((a,f)=>a+Number(f.amount||0),0);
  const paidStudentIds   = new Set([
    ...fees.filter(f=>f.month===THIS_MONTH&&f.year===THIS_YEAR).map(f=>String(f.student_id)),
    ...feePayments.filter(r=>r.month===THIS_MONTH&&r.year===THIS_YEAR).map(r=>String(r.student_id))
  ]);
  const paidStudents     = paidStudentIds.size;
  // Exclude students whose resolved fee total is 0 (e.g. full scholarship) — nothing to collect
  const feeOwingStudents = students.filter(s=>resolveFee(s,feeConfig,streamDefaults,classTuition).total>0);
  const unpaidStudents   = feeOwingStudents.filter(s=>!paidStudentIds.has(String(s.id))).length;
  const monthBreakdown   = {};
  fees.forEach(f=>{ const k=`${f.month} ${f.year}`; monthBreakdown[k]=(monthBreakdown[k]||0)+Number(f.amount||0); });
  const monthData        = Object.entries(monthBreakdown).sort((a,b)=>new Date("1 "+b[0])-new Date("1 "+a[0])).slice(0,6);
  const filteredFees     = fees.filter(f=>{ if(filterMonth!=="All"&&f.month!==filterMonth)return false; if(filterClass!=="All"&&f.class!==filterClass)return false; return true; });
  const allClasses       = [...new Set(fees.map(f=>f.class).filter(Boolean))].sort();

  // ── Save stream defaults ─────────────────────────────────────────
  async function saveStreamDefaults(){
    // Strip internal scope keys before saving to streamDefaults
    const {_scopeMode, _scopeRange, _scopePick, ...feeAmounts} = streamDraft;
    const updated = {...streamDefaults, [streamEditTarget]: feeAmounts};
    setStreamDefaults(updated);

    // Resolve which classes are in scope
    const streamClasses = STREAM_CLASSES[streamEditTarget]||[];
    const scopeMode = _scopeMode||"all";
    const scopeRange = _scopeRange||{from:"",to:""};
    const scopePick = _scopePick||[];
    const inScope = (s) => {
      if((s.stream||"School Only")!==streamEditTarget) return false;
      if(streamClasses.length===0||scopeMode==="all") return true;
      const cls = s.class||s["class"]||"";
      if(scopeMode==="range"&&scopeRange.from&&scopeRange.to){
        const fi=streamClasses.indexOf(scopeRange.from), ti=streamClasses.indexOf(scopeRange.to);
        const sel=fi<=ti?streamClasses.slice(fi,ti+1):[];
        return sel.includes(cls);
      }
      if(scopeMode==="pick"&&scopePick.length>0) return scopePick.includes(cls);
      return true;
    };

    try {
      const rows = Object.entries(feeAmounts).map(([fee_type,amount])=>({
        type:"stream", stream:streamEditTarget, fee_type, amount, updated_at:new Date().toISOString()
      }));
      await supabase.from("fee_structure").upsert(rows, {onConflict:"type,stream,fee_type"});
      // Bulk apply only to students in scope
      const scopedStudents = students.filter(inScope);
      const cfgUpdates = scopedStudents.map(s=>{
        const existing = feeConfig[s.id]||{};
        const merged = {...feeAmounts, ...existing}; // student overrides win
        return { student_id:s.id, config:merged, updated_at:new Date().toISOString() };
      });
      if(cfgUpdates.length) await supabase.from("fee_config").upsert(cfgUpdates, {onConflict:"student_id"});
      const newFeeConfig = {...feeConfig};
      cfgUpdates.forEach(u=>{ newFeeConfig[u.student_id] = u.config; });
      setFeeConfig(newFeeConfig);
      showToast(`✅ Stream defaults saved — applied to ${scopedStudents.length} students`);
    } catch(e){
      console.warn("Stream save:", e);
      showToast("⚠️ Saved locally — DB sync failed","warn");
    }
    setStreamEditTarget(null);
  }

  // ── Save class tuition ───────────────────────────────────────────
  async function saveClassTuition(){
    setClassTuition(classTuitionDraft);
    try {
      const rows = classTuitionDraft.filter(ct=>ct.amount>0).map(ct=>({
        type:"class", class_name:ct.class, fee_type:"tuition", amount:ct.amount, updated_at:new Date().toISOString()
      }));
      await supabase.from("fee_structure").upsert(rows, {onConflict:"type,class_name,fee_type"});
      // Apply tuition to all students by their class
      const newFeeConfig = {...feeConfig};
      students.forEach(s=>{
        const cls = s.class||s["class"]||"";
        const ct  = classTuitionDraft.find(x=>x.class===cls);
        if(!ct||!ct.amount) return;
        const existing = newFeeConfig[s.id]||{};
        // Only auto-set tuition if student doesn't have a manual override flagged
        if(!existing._tuition_locked) { newFeeConfig[s.id] = {...existing, tuition:ct.amount}; }
      });
      setFeeConfig(newFeeConfig);
      const cfgRows = Object.entries(newFeeConfig).map(([student_id,config])=>({student_id,config,updated_at:new Date().toISOString()}));
      if(cfgRows.length) await supabase.from("fee_config").upsert(cfgRows, {onConflict:"student_id"});
    } catch(e){ console.warn("Class tuition save:", e); }
    showToast("✅ Class tuition saved and applied to all students");
  }

  // ── Save per-student config ──────────────────────────────────────
  async function saveStudentFeeConfig(){
    const updated = {...feeConfig, [feeConfigEdit]: {...feeConfigDraft, _tuition_locked:true}};
    setFeeConfig(updated);
    try { await supabase.from("fee_config").upsert({student_id:feeConfigEdit, config:updated[feeConfigEdit], updated_at:new Date().toISOString()}, {onConflict:"student_id"}); } catch(e){}
    setFeeConfigEdit(null);
    showToast("✅ Student fee config saved");
  }

  // ── Legacy submit (record payment) ──────────────────────────────
  const handleSubmit = async () => {
    if(!form.studentId) return showToast("Please select a student", false);

    const stu = selectedStudent;
    const finalMonth  = form.month;
    const finalYear   = form.year;
    const finalMethod = form.method;

    // Smart mode: fee structure configured — save per-type splits to fee_payments
    if(selectedResolved && selectedResolved.total > 0) {
      const finalAmount = Object.entries(selectedResolved.breakdown)
        .filter(([id])=>form[`checked_${id}`] !== false)
        .reduce((a,[,v])=>a+(v.amount||0), 0);
      if(finalAmount <= 0) return showToast("Please select at least one fee type to record", false);
      setSaving(true);
      const totalDue = selectedResolved.total;
      const status   = finalAmount >= totalDue ? "paid" : finalAmount > 0 ? "partial" : "unpaid";
      const splits   = {};
      Object.entries(selectedResolved.breakdown).forEach(([id,v])=>{ if(form[`checked_${id}`]!==false && v.amount>0) splits[id]=v.amount; });
      const receiptNo = "RCP-"+String(form.studentId||"0").padStart(6,"0").slice(-6)+"-"+finalMonth.slice(0,3).toUpperCase()+String(finalYear).slice(-2);
      const paidDate  = new Date().toISOString().split("T")[0];
      const payRec = { student_id:stu.id, month:finalMonth, year:String(finalYear), amount_paid:finalAmount,
        status, payment_method:finalMethod, splits, receipt_note:form.notes||"", paid_at:new Date().toISOString() };
      try {
        await supabase.from("fee_payments").upsert({...payRec,updated_at:new Date().toISOString()},{onConflict:"student_id,month,year"});
        setFeePayments(prev=>{ const idx=prev.findIndex(r=>r.student_id===stu.id&&r.month===finalMonth&&r.year===String(finalYear));
          return idx>=0?prev.map((r,i)=>i===idx?{...r,...payRec}:r):[...prev,{id:Date.now(),...payRec}]; });
      } catch(e){ console.warn("fee_payments save:", e); }
      // Also save a legacy record for history tab
      const legacyRec = { student_name:stu?.name, "class":stu?.class, amount:finalAmount, month:finalMonth,
        year:String(finalYear), method:finalMethod, notes:form.notes||"", paid_date:paidDate, receipt_no:receiptNo, student_id:stu.id };
      setFees(prev=>[legacyRec,...prev]);
      if(onFeesSaved) onFeesSaved(legacyRec);
      setWaReceipt({ name:stu?.name||stu?.full_name||"Student", phone:stu?.contact||stu?.phone||"", amount:finalAmount, month:finalMonth, year:String(finalYear), receiptNo });
      showToast("✅ Fee recorded! Receipt: "+receiptNo);
      setForm(emptyForm); setActiveTab("history");
      setSaving(false);
      return;
    }

    // Manual / legacy mode: no fee structure configured
    if(!form.amount||isNaN(form.amount)||Number(form.amount)<=0) return showToast("Enter a valid amount", false);
    const finalAmount = Number(form.amount);
    setSaving(true);
    const receiptNo = "RCP-"+String(form.studentId||"0").padStart(6,"0").slice(-6)+"-"+finalMonth.slice(0,3).toUpperCase()+String(finalYear).slice(-2);
    const paidDate  = new Date().toISOString().split("T")[0];
    const res = await sheetAPI({ action:"saveFee", studentId:form.studentId, studentName:stu?.name||"",
      "class":stu?.class||"", amount:finalAmount, month:finalMonth, year:String(finalYear),
      method:finalMethod, notes:form.notes, paidDate, receiptNo });
    if(res.status==="ok"){
      showToast("Fee recorded! Receipt: "+receiptNo);
      const newRec = { student_name:stu?.name, "class":stu?.class, amount:finalAmount, month:finalMonth,
        year:String(finalYear), method:finalMethod, notes:form.notes, paid_date:paidDate, receipt_no:receiptNo, student_id:form.studentId };
      setFees(prev=>[newRec,...prev]);
      if(onFeesSaved) onFeesSaved(newRec);
      setWaReceipt({ name:stu?.name||stu?.full_name||"Student", phone:stu?.contact||stu?.phone||"", amount:finalAmount, month:finalMonth, year:String(finalYear), receiptNo });
      setForm(emptyForm); setActiveTab("history");
    } else { showToast("Failed to save fee", false); }
    setSaving(false);
  };

  // ── Smart collect payment (cascade-aware) ────────────────────────
  function openCollectModal(s){
    const resolved = resolveFee(s, feeConfig, streamDefaults, classTuition);
    const checked = {};
    Object.entries(resolved.breakdown).forEach(([id, v])=>{ checked[id] = v.enabled && v.amount > 0; });
    setCollectChecked(checked);
    setCollectDraft({ payment_method:"Cash", receipt_note:"" });
    setCollectModal({ student:s, resolved });
  }

  async function saveCollectPayment(){
    const { student:s, resolved } = collectModal;
    const amtPaid = Object.entries(resolved.breakdown).filter(([id])=>collectChecked[id]).reduce((a,[,v])=>a+(v.amount||0),0);
    const totalDue = resolved.total;
    const status   = amtPaid>=totalDue?"paid":amtPaid>0?"partial":"unpaid";
    const splits   = {};
    Object.entries(resolved.breakdown).forEach(([id,v])=>{ if(collectChecked[id]) splits[id]=v.amount; });
    const monthName = MONTHS[analyticsMonth];
    const rec = { student_id:s.id, month:monthName, year:String(analyticsYear), amount_paid:amtPaid, status,
      payment_method:collectDraft.payment_method||"Cash", receipt_note:collectDraft.receipt_note||"", splits, paid_at:new Date().toISOString() };
    setFeePayments(prev=>{ const idx=prev.findIndex(r=>r.student_id===s.id&&r.month===monthName&&r.year===String(analyticsYear));
      return idx>=0?prev.map((r,i)=>i===idx?{...r,...rec}:r):[...prev,{id:Date.now(),...rec}]; });
    try { await supabase.from("fee_payments").upsert({...rec,updated_at:new Date().toISOString()},{onConflict:"student_id,month,year"}); } catch(e){}
    if((s.contact||s.phone) && status==="paid"){
      const lines = Object.entries(resolved.breakdown).filter(([id])=>collectChecked[id]).map(([id,v])=>{
        const ft = FEE_TYPES_LIST.find(f=>f.id===id);
        return `• ${ft?.label||id}: ${v.scholarshipNote?`Scholarship (${v.scholarshipNote})`:fmtPKR(v.amount)}`;
      });
      feeOpenWA(s.contact||s.phone,
        `✅ Fee Receipt — ${s.name||s.full_name}\nMonth: ${FEE_MONTHS_SHORT[analyticsMonth]} ${analyticsYear}\n${lines.join("\n")}\nTotal: ${fmtPKR(amtPaid)}\nMethod: ${collectDraft.payment_method||"Cash"}\nJazakAllah Khair 🌿`);
    }
    showToast(status==="paid"?"✅ Payment saved & WhatsApp receipt sent":"💾 Partial payment saved");
    if(onFeesSaved) onFeesSaved(rec);
    setCollectModal(null);
  }

  // ── Defaulters (cascade-aware) ───────────────────────────────────
  const defaulters = React.useMemo(()=>{
    const today=new Date(); const rows=[];
    for(let i=0;i<6;i++){
      let m=FEE_CUR_MONTH-i,y=FEE_CUR_YEAR; if(m<0){m+=12;y--;}
      const dueDate=new Date(y,m,10);
      const days=Math.floor((today-dueDate)/(1000*60*60*24));
      if(days<=0) continue;
      students.forEach(s=>{
        const resolved=resolveFee(s,feeConfig,streamDefaults,classTuition);
        if(!resolved.total) return;
        // Skip full scholarship students
        const sc=s.scholarship_status||"";
        if(sc.toLowerCase().includes("full")) return;
        const rec=feePayments.find(r=>r.student_id===s.id&&r.month===MONTHS[m]&&r.year===String(y));
        const paid=rec?.amount_paid||0;
        const bal=resolved.total-paid;
        if(bal<=0) return;
        const bucket=days<=30?"1–30 days":days<=60?"31–60 days":days<=90?"61–90 days":"90+ days";
        rows.push({s,m,y,due:resolved.total,paid,bal,days,bucket});
      });
    }
    return rows.sort((a,b)=>b.days-a.days);
  },[feeConfig,streamDefaults,classTuition,feePayments,students]);

  // ── Analytics trend ──────────────────────────────────────────────
  const trendData = React.useMemo(()=>{
    const rows=[];
    for(let i=5;i>=0;i--){
      let m=FEE_CUR_MONTH-i,y=FEE_CUR_YEAR; if(m<0){m+=12;y--;}
      const expected=students.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
      const legacyAmt=fees.filter(f=>f.month===MONTHS[m]&&f.year===String(y)).reduce((s,f)=>s+Number(f.amount||0),0);
      const newAmt=feePayments.filter(r=>r.month===MONTHS[m]&&r.year===String(y)).reduce((s,r)=>s+(r.amount_paid||0),0);
      rows.push({month:FEE_MONTHS_SHORT[m],expected:Math.round(expected/1000),collected:Math.round((legacyAmt+newAmt)/1000)});
    }
    return rows;
  },[fees,feePayments,feeConfig,streamDefaults,classTuition,students]);

  const monthRecs      = feePayments.filter(r=>r.month===MONTHS[analyticsMonth]&&r.year===analyticsYear);
  const monthExpected  = students.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
  const monthCollected = monthRecs.reduce((s,r)=>s+(r.amount_paid||0),0);

  // ── Export CSV ───────────────────────────────────────────────────
  function exportFeeCSV(){
    feeDownloadCSV(fees.map(f=>({Receipt:f.receipt_no||"",Student:f.student_name||"",Class:f.class||"",
      Month:f.month,Year:f.year,"Amount (PKR)":f.amount||0,Method:f.method||"Cash",Date:f.paid_date||"",Notes:f.notes||""})),
      `fee-history-${Date.now()}.csv`);
  }

  const tabBtn = (id,label,icon) => (
    <button onClick={()=>setActiveTab(id)} style={{padding:"8px 18px",borderRadius:10,border:"none",cursor:"pointer",fontWeight:700,fontSize:13,
      background:activeTab===id?C.primary:C.primaryPale, color:activeTab===id?C.white:C.primary, transition:"all .2s"}}>{icon} {label}</button>
  );

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>

      {/* Header */}
      <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{fontWeight:900,fontSize:20,color:C.primary,marginBottom:4}}>💰 Fee Management</div>
        <div style={{fontSize:13,color:C.muted}}>Cascade fee system — stream → class → student · Scholarship-linked</div>
      </div>

      {/* ── Monthly Collection Gauge (hero) ── */}
      {(()=>{
        const gaugeExpected = students.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
        const gaugeRecs     = feePayments.filter(r=>r.month===THIS_MONTH&&r.year===THIS_YEAR);
        const gaugeCollected= gaugeRecs.reduce((s,r)=>s+(r.amount_paid||0),0) + thisMonthTotal;
        const gaugePct      = gaugeExpected>0 ? Math.min(100,Math.round(gaugeCollected/gaugeExpected*100)) : 0;
        const gaugeColor    = gaugePct>=85?C.primary:gaugePct>=60?C.accent:C.rose;
        const gaugeLabel    = gaugePct>=85?"On track ✅":gaugePct>=60?"Needs push ⚡":"Urgent attention ⚠️";
        const paidThisMonth    = paidStudentIds.size;
        const pendingThisMonth = feeOwingStudents.filter(s=>!paidStudentIds.has(String(s.id))).length;
        // Class-wise breakdown
        const allCls = [...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort();
        const clsGauge = allCls.map(cls=>{
          const clsStu = students.filter(s=>(s.class||s["class"])===cls);
          const exp = clsStu.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
          const col = feePayments.filter(r=>r.month===THIS_MONTH&&r.year===THIS_YEAR&&clsStu.some(s=>s.id===r.student_id)).reduce((s,r)=>s+(r.amount_paid||0),0)
                    + fees.filter(f=>f.month===THIS_MONTH&&f.year===THIS_YEAR&&clsStu.some(s=>String(s.id)===String(f.student_id))).reduce((s,f)=>s+Number(f.amount||0),0);
          const pct = exp>0?Math.min(100,Math.round(col/exp*100)):0;
          return {cls,exp,col,pct};
        }).filter(d=>d.exp>0).slice(0,6);
        return (
          <div style={{background:C.white,borderRadius:18,padding:"22px 26px",boxShadow:`0 2px 16px ${C.shadow}`,border:`1.5px solid ${gaugeColor}22`}}>
            {/* Header row */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:16}}>
              <div>
                <div style={{fontWeight:900,fontSize:16,color:C.text}}>📅 {THIS_MONTH} {THIS_YEAR} — Collection Progress</div>
                <div style={{fontSize:12,color:C.muted,marginTop:3}}>Real-time snapshot · Due date 10th of each month</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{background:gaugeColor+"18",color:gaugeColor,border:`1px solid ${gaugeColor}44`,borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:800}}>{gaugeLabel}</span>
                <button onClick={()=>setShowPendingWa(true)}
                  style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:10,padding:"6px 14px",fontSize:12,fontWeight:700,color:"#92400e",cursor:"pointer"}}>
                  💬 Remind {pendingThisMonth} pending
                </button>
              </div>
            </div>
            {/* Big progress bar */}
            <div style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
                <span style={{fontSize:13,color:C.muted,fontWeight:600}}>Collected</span>
                <span style={{fontSize:15,fontWeight:900,color:gaugeColor}}>
                  {fmtPKR(gaugeCollected)} <span style={{fontSize:12,fontWeight:500,color:C.muted}}>of {fmtPKR(gaugeExpected)}</span>
                </span>
              </div>
              <div style={{height:28,background:C.bg,borderRadius:10,overflow:"hidden",position:"relative"}}>
                <div style={{height:"100%",width:`${gaugePct}%`,background:gaugeColor,borderRadius:10,transition:"width 1s ease",display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:10}}>
                  {gaugePct>12&&<span style={{color:"white",fontWeight:800,fontSize:13}}>{gaugePct}%</span>}
                </div>
                {gaugePct<=12&&<span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontWeight:800,fontSize:13,color:gaugeColor}}>{gaugePct}%</span>}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.muted,marginTop:4}}>
                <span>PKR 0</span>
                <span style={{color:gaugeColor,fontWeight:700}}>{fmtPKR(Math.max(0,gaugeExpected-gaugeCollected))} still outstanding</span>
                <span>{fmtPKR(gaugeExpected)}</span>
              </div>
            </div>
            {/* KPI strip */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,margin:"16px 0"}}>
              {[
                {icon:"✅",label:"Paid students",val:paidThisMonth,color:C.primary,bg:C.primaryPale,click:()=>setActiveTab("history")},
                {icon:"⏳",label:"Pending",val:pendingThisMonth,color:C.accent,bg:C.accentPale,click:()=>setShowPendingWa(true)},
                {icon:"⚠️",label:"Defaulters (90d+)",val:defaulters.filter(d=>d.days>=90).length,color:C.rose,bg:C.rosePale,click:()=>setActiveTab("defaulters")},
                {icon:"💵",label:"Total collected",val:fmtPKR(totalCollected),color:C.teal,bg:C.tealPale,click:null},
              ].map((k,i)=>(
                <div key={i} onClick={k.click||undefined}
                  style={{background:k.bg,borderRadius:12,padding:"12px 10px",textAlign:"center",cursor:k.click?"pointer":"default",border:`1px solid ${k.color}22`,transition:"transform .15s"}}
                  onMouseEnter={e=>{if(k.click)e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="";}}>
                  <div style={{fontSize:18,marginBottom:4}}>{k.icon}</div>
                  <div style={{fontWeight:900,fontSize:17,color:k.color,lineHeight:1}}>{k.val}</div>
                  <div style={{fontSize:10,color:C.muted,marginTop:3,fontWeight:600}}>{k.label}</div>
                  {k.click&&<div style={{fontSize:9,color:k.color,marginTop:3,opacity:.7}}>Tap to view →</div>}
                </div>
              ))}
            </div>
            {/* Class-wise mini bars */}
            {clsGauge.length>0&&(
              <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Collection by class</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:"6px 16px"}}>
                  {clsGauge.map(d=>{
                    const cc=d.pct>=80?C.primary:d.pct>=55?C.accent:C.rose;
                    return(
                      <div key={d.cls}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:11,fontWeight:600,color:C.text}}>{d.cls}</span>
                          <span style={{fontSize:11,fontWeight:800,color:cc}}>{d.pct}%</span>
                        </div>
                        <div style={{height:7,background:C.bg,borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${d.pct}%`,background:cc,borderRadius:4}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Pending WA panel — fixed overlay */}
      {showPendingWa&&(()=>{
        const unpaidList=feeOwingStudents.filter(s=>!paidStudentIds.has(String(s.id)));
        const buildMsg=(name,cls,amount,breakdown)=>{
          const parts=Object.entries(breakdown).filter(([,v])=>v.amount>0)
            .map(([id,v])=>{const ft=FEE_TYPES_LIST.find(f=>f.id===id);return `${ft?.label||v.label||id}: PKR ${v.amount.toLocaleString()}`;}).join(", ");
          return `Assalamu Alaikum! Dear parent of ${name} (${cls||"QK Academy"}), your fee payment of PKR ${amount.toLocaleString()} is pending for ${THIS_MONTH} ${THIS_YEAR}${parts?` (${parts})`:""}. Kindly arrange payment at your earliest. JazakAllah Khair — QK Academy`;
        };
        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}
            onClick={e=>{if(e.target===e.currentTarget)setShowPendingWa(false);}}>
            <div style={{background:"white",borderRadius:18,width:"100%",maxWidth:520,maxHeight:"85vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.25)",overflow:"hidden"}}>
              <div style={{background:"#fff7ed",borderBottom:"1px solid #fed7aa",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
                <div>
                  <div style={{fontWeight:800,fontSize:15,color:"#92400e"}}>💬 WhatsApp Fee Reminders</div>
                  <div style={{fontSize:12,color:"#b45309",marginTop:2}}>{unpaidList.length} pending · {unpaidList.filter(s=>s.contact||s.phone).length} have contact numbers</div>
                </div>
                <button onClick={()=>setShowPendingWa(false)}
                  style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"6px 14px",fontWeight:700,fontSize:13,color:"#92400e",cursor:"pointer"}}>✕ Close</button>
              </div>
              <div style={{overflowY:"auto",padding:"12px 16px",display:"flex",flexDirection:"column",gap:8}}>
                {unpaidList.length===0?(
                  <div style={{textAlign:"center",padding:"40px 0",color:"#6b7280"}}>
                    <div style={{fontSize:36,marginBottom:8}}>🎉</div>
                    <div style={{fontWeight:700}}>All fees collected for {THIS_MONTH}!</div>
                  </div>
                ):unpaidList.map(s=>{
                  const phone=s.contact||s.phone;
                  const resolved=resolveFee(s,feeConfig,streamDefaults,classTuition);
                  const feeTotal=resolved.total;
                  const breakdownParts=Object.entries(resolved.breakdown).filter(([,v])=>v.amount>0)
                    .map(([id,v])=>{const ft=FEE_TYPES_LIST.find(f=>f.id===id);return `${ft?.label||id}: PKR ${v.amount.toLocaleString()}`;});
                  const link=phone?waLink(phone,buildMsg(s.name||s.full_name,s.class||s["class"],feeTotal,resolved.breakdown)):null;
                  return (
                    <div key={s.id} style={{background:"#fffbf5",borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,border:"1px solid #fed7aa"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          {s.name||s.full_name}
                          {scholBadge(s.scholarship_status)&&<ScholBadge status={s.scholarship_status} small/>}
                          <span style={{fontWeight:900,color:"#dc2626",marginLeft:"auto"}}>{fmtPKR(feeTotal)}</span>
                        </div>
                        <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>{s.class||s["class"]} · {s.stream||""} · {phone||"No contact"}</div>
                        {breakdownParts.length>1&&<div style={{fontSize:10,color:"#9ca3af",marginTop:2}}>{breakdownParts.join(" · ")}</div>}
                      </div>
                      {link
                        ?<a href={link} target="_blank" rel="noopener noreferrer"
                            style={{background:"#16a34a",color:"white",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,textDecoration:"none",flexShrink:0}}>💬 Send</a>
                        :<span style={{fontSize:11,color:"#9ca3af",flexShrink:0}}>No number</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tab bar */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {tabBtn("submit","Record Payment","📝")}
        {tabBtn("history","Payment History","📋")}
        {tabBtn("challan","Fee Challan / Receipt","🧾")}
        {tabBtn("summary","Monthly Summary","📊")}
        {tabBtn("types","Fee Structure","⚙️")}
        {tabBtn("defaulters","Defaulters","⚠️")}
        {tabBtn("analytics","Analytics","📈")}
        {tabBtn("bulk","Bulk Record","📦")}
        {tabBtn("custom","Custom Charges","💸")}
      </div>

      {challanStudent&&<FeeChallan student={challanStudent} feeRecord={challanFeeRecord} onClose={()=>{setChallanStudent(null);setChallanFeeRecord(null);}}/>}
      {feeReceiptRecord&&<FeeReceipt student={feeReceiptRecord.stu} feeRecord={feeReceiptRecord.fee} copyType="school" onClose={()=>setFeeReceiptRecord(null)}/>}

      {/* ── Record Payment ── */}
      {activeTab==="submit"&&(
        <div style={{background:C.white,borderRadius:16,padding:28,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:20}}>📝 Record Fee Payment</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div style={{gridColumn:"1/-1"}}>
              <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Student *</label>
              <select value={form.studentId} onChange={e=>setForm(f=>({...f,studentId:e.target.value}))}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${form.studentId?C.primary:C.border}`,fontSize:14,background:C.white,color:C.text,outline:"none"}}>
                <option value="">-- Select Student --</option>
                {[...students].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(s=>(
                  <option key={s.id} value={s.id}>{s.name} — {s.class||"Hifz"} {s.stream?`(${s.stream})`:""}</option>
                ))}
              </select>
              {selectedStudent&&(
                <div style={{marginTop:8,padding:"10px 14px",background:C.primaryPale,borderRadius:10,fontSize:13,display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
                  <span style={{fontWeight:700,color:C.text}}>👤 {selectedStudent.name}</span>
                  <span style={{color:C.muted}}>{selectedStudent.class||"Hifz"}</span>
                  {selectedStudent.stream&&<span style={{background:STREAM_COLORS[selectedStudent.stream]?.bg||C.bg,color:STREAM_COLORS[selectedStudent.stream]?.color||C.muted,border:`1px solid ${STREAM_COLORS[selectedStudent.stream]?.border||C.border}`,borderRadius:20,padding:"1px 10px",fontSize:11,fontWeight:700}}>{selectedStudent.stream}</span>}
                  {selectedStudent.scholarship_status&&selectedStudent.scholarship_status!=="None"&&<ScholBadge status={selectedStudent.scholarship_status}/>}
                </div>
              )}
            </div>

            {/* If cascade config available — show smart breakdown */}
            {selectedResolved && selectedResolved.total > 0 ? (
              <div style={{gridColumn:"1/-1",background:C.bg,borderRadius:12,padding:16,border:`1.5px solid ${C.primaryMid}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.primary}}>💡 Auto-filled from fee structure — select what's being paid</div>
                  <button onClick={()=>setActiveTab("types")} style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    ⚙️ Edit fee structure
                  </button>
                </div>
                {Object.entries(selectedResolved.breakdown).map(([id,v])=>{
                  const ft = FEE_TYPES_LIST.find(f=>f.id===id) || {label:v.label||id};
                  return (
                    <div key={id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                      <input type="checkbox" checked={!!form[`checked_${id}`]} onChange={e=>setForm(f=>({...f,[`checked_${id}`]:e.target.checked}))}
                        style={{width:16,height:16,accentColor:C.primary,cursor:"pointer"}}/>
                      <span style={{flex:1,fontSize:13,fontWeight:500}}>{ft.label}</span>
                      {v.scholarshipNote ? (
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {v.originalAmount>0&&<span style={{textDecoration:"line-through",color:C.muted,fontSize:12}}>PKR {v.originalAmount.toLocaleString()}</span>}
                          <ScholBadge status={`${v.scholarshipNote}`} small/>
                          <span style={{fontWeight:700,color:"#16a34a"}}>PKR {v.amount.toLocaleString()}</span>
                        </div>
                      ) : (
                        <span style={{fontWeight:700,color:C.primary}}>PKR {v.amount.toLocaleString()}</span>
                      )}
                    </div>
                  );
                })}
                <div style={{display:"flex",justifyContent:"space-between",paddingTop:10,fontWeight:800,color:C.primary,fontSize:14}}>
                  <span>Total due this visit</span>
                  <span>PKR {Object.entries(selectedResolved.breakdown).filter(([id])=>form[`checked_${id}`]!==false).reduce((a,[,v])=>a+(v.amount||0),0).toLocaleString()}</span>
                </div>
                <div style={{marginTop:10,display:"flex",gap:8,flexWrap:"wrap"}}>
                  <select value={form.method} onChange={e=>setForm(f=>({...f,method:e.target.value}))}
                    style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white,flex:1,minWidth:140}}>
                    {["Cash","Bank Transfer","JazzCash","EasyPaisa","Cheque"].map(m=><option key={m}>{m}</option>)}
                  </select>
                  <select value={form.month} onChange={e=>setForm(f=>({...f,month:e.target.value}))}
                    style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white,flex:1,minWidth:110}}>
                    {MONTHS.map(m=><option key={m}>{m}</option>)}
                  </select>
                  <select value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))}
                    style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white,minWidth:80}}>
                    {["2024","2025","2026","2027"].map(y=><option key={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <>
                {selectedStudent && (
                  <div style={{gridColumn:"1/-1",background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span>⚠️ No fee structure configured for this student yet — you can enter an amount manually below, or</span>
                    <button onClick={()=>setActiveTab("types")} style={{background:"#f0a500",color:"#fff",border:"none",borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                      ⚙️ Set up Fee Structure
                    </button>
                  </div>
                )}
                <div>
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Amount (PKR) *</label>
                  <input type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} placeholder="e.g. 2500"
                    style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,outline:"none"}}/>
                </div>
                <div>
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Payment Method</label>
                  <select value={form.method} onChange={e=>setForm(f=>({...f,method:e.target.value}))}
                    style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,background:C.white,color:C.text,outline:"none"}}>
                    {["Cash","Bank Transfer","JazzCash","EasyPaisa","Cheque"].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Month</label>
                  <select value={form.month} onChange={e=>setForm(f=>({...f,month:e.target.value}))}
                    style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,background:C.white,color:C.text,outline:"none"}}>
                    {MONTHS.map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Year</label>
                  <select value={form.year} onChange={e=>setForm(f=>({...f,year:e.target.value}))}
                    style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,background:C.white,color:C.text,outline:"none"}}>
                    {["2024","2025","2026","2027"].map(y=><option key={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}

            <div style={{gridColumn:"1/-1"}}>
              <label style={{display:"block",fontSize:12,fontWeight:700,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:.5}}>Notes (optional)</label>
              <input value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="e.g. Partial payment, late fee waived..."
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${C.border}`,fontSize:14,outline:"none"}}/>
            </div>
          </div>
          <button onClick={handleSubmit} disabled={saving}
            style={{marginTop:20,padding:"12px 32px",borderRadius:12,border:"none",background:saving?"#9ca3af":C.primary,color:C.white,fontWeight:800,fontSize:15,cursor:saving?"not-allowed":"pointer",transition:"all .2s"}}>
            {saving?"⏳ Saving...":"💾 Record Payment"}
          </button>
        </div>
      )}

      {/* ── History ── */}
      {activeTab==="history"&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {waReceipt&&(()=>{
            const {name,phone,amount,month,year,receiptNo}=waReceipt;
            const link=waLink(phone,`Assalamu Alaikum! ${name}'s fee for ${month} ${year} of PKR ${amount.toLocaleString()} has been received. Receipt No: ${receiptNo}. JazakAllah Khair. — QK Academy`);
            return (
              <div style={{background:"#f0fdf4",border:"1.5px solid #86efac",borderRadius:14,padding:"14px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                <div style={{fontSize:24}}>🧾</div>
                <div style={{flex:1}}><div style={{fontWeight:800,fontSize:13,color:"#166534"}}>Fee recorded for {name}</div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{month} {year} · PKR {amount.toLocaleString()} · {receiptNo}</div></div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {link?<a href={link} target="_blank" rel="noopener noreferrer" style={{background:"#22c55e",color:"white",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:13,textDecoration:"none"}}>💬 Send Receipt on WhatsApp</a>
                    :<span style={{fontSize:12,color:"#9ca3af",fontStyle:"italic"}}>No phone number on record</span>}
                  <button onClick={()=>setWaReceipt(null)} style={{background:"white",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:9,padding:"8px 12px",fontSize:12,cursor:"pointer"}}>Dismiss</button>
                </div>
              </div>
            );
          })()}
          <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15}}>📋 Payment History</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.white}}>
                  <option value="All">All Months</option>{MONTHS.map(m=><option key={m}>{m}</option>)}
                </select>
                <select value={filterClass} onChange={e=>setFilterClass(e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.white}}>
                  <option value="All">All Classes</option>{allClasses.map(c=><option key={c}>{c}</option>)}
                </select>
                <button onClick={exportFeeCSV} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${C.primaryMid}`,background:C.primaryPale,color:C.primary,fontWeight:700,fontSize:12,cursor:"pointer"}}>⬇ Export CSV</button>
              </div>
            </div>
            {loading?<div style={{textAlign:"center",padding:40,color:C.muted}}>Loading...</div>
            :filteredFees.length===0?<div style={{textAlign:"center",padding:40}}><div style={{fontSize:48,marginBottom:12}}>📂</div><div style={{fontWeight:700,color:C.primary}}>No payments found</div></div>
            :(
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead><tr style={{background:C.primaryPale}}>
                    {["Receipt","Student","Class","Month/Year","Amount","Method","Date","Notes",""].map(h=>(
                      <th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:12,textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredFees.map((f,i)=>{
                      const stu=students.find(s=>String(s.id)===String(f.student_id));
                      const isSchol = f.method==="Scholarship";
                      return (
                        <tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                          <td style={{padding:"10px 12px",fontWeight:700,color:C.primary,whiteSpace:"nowrap"}}>{f.receipt_no||"—"}</td>
                          <td style={{padding:"10px 12px",fontWeight:600,color:C.text}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>{f.student_name}
                              {stu&&stu.scholarship_status&&stu.scholarship_status!=="None"&&<ScholBadge status={stu.scholarship_status} small/>}
                            </div>
                          </td>
                          <td style={{padding:"10px 12px",color:C.muted}}>{f.class}</td>
                          <td style={{padding:"10px 12px",color:C.muted,whiteSpace:"nowrap"}}>{f.month} {f.year}</td>
                          <td style={{padding:"10px 12px",fontWeight:800,color:isSchol?"#16a34a":C.primary,whiteSpace:"nowrap"}}>
                            {isSchol?<ScholBadge status={stu?.scholarship_status||"Active"}/>:`PKR ${Number(f.amount||0).toLocaleString()}`}
                          </td>
                          <td style={{padding:"10px 12px"}}><span style={{background:C.primaryPale,color:C.primary,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{f.method||"Cash"}</span></td>
                          <td style={{padding:"10px 12px",color:C.muted,whiteSpace:"nowrap"}}>{f.paid_date}</td>
                          <td style={{padding:"10px 12px",color:C.muted,maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.notes||"—"}</td>
                          <td style={{padding:"6px 8px"}}>
                            <button onClick={()=>setFeeReceiptRecord({stu:stu||{id:f.student_id,name:f.student_name,full_name:f.student_name,class:f.class},fee:f})}
                              style={{background:"#e8f5ee",color:"#1a6b3c",border:"1px solid #c3e6d0",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>🧾 Receipt</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr style={{background:C.primaryPale,borderTop:`2px solid ${C.primaryMid}`}}>
                    <td colSpan={4} style={{padding:"10px 12px",fontWeight:800,color:C.primary}}>Total ({filteredFees.length} payments)</td>
                    <td style={{padding:"10px 12px",fontWeight:900,color:C.primary}}>PKR {filteredFees.reduce((a,f)=>a+Number(f.amount||0),0).toLocaleString()}</td>
                    <td colSpan={4}/>
                  </tr></tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Challan ── */}
      {activeTab==="challan"&&(
        <div style={{background:"white",borderRadius:16,padding:28,boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:900,color:C.primary,fontSize:16,marginBottom:6}}>🧾 Fee Challan / Receipt Generator</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:20}}>Select a student to generate a printable fee challan with all three copies (Bank, School, Student)</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
            {[...students].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(s=>(
              <div key={s.id} style={{background:C.primaryPale,borderRadius:12,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,border:`1.5px solid ${C.border}`,cursor:"pointer",transition:"all .15s"}}
                onClick={()=>setChallanStudent(s)}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.primary;e.currentTarget.style.background="#d1f0e0";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.primaryPale;}}>
                <div style={{width:40,height:40,borderRadius:10,background:`linear-gradient(135deg,${C.primary},#0d9488)`,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:16,flexShrink:0}}>
                  {(s.name||s.full_name||"?")[0]}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
                    {s.name||s.full_name}
                    {s.scholarship_status&&s.scholarship_status!=="None"&&<ScholBadge status={s.scholarship_status} small/>}
                  </div>
                  <div style={{fontSize:11,color:C.muted}}>{s.class||"Hifz"} · {s.stream||""}</div>
                </div>
                <div style={{fontSize:18,flexShrink:0}}>🖨️</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Monthly Summary ── */}
      {activeTab==="summary"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:16}}>📊 Monthly Collection Overview</div>
            {monthData.length===0?<div style={{textAlign:"center",padding:40,color:C.muted}}><div style={{fontSize:36,marginBottom:8}}>📂</div><div style={{fontWeight:700}}>No payment data yet</div></div>:(
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthData.map(([k,v])=>({month:k,amount:v})).reverse()} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                  <XAxis dataKey="month" tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false}/>
                  <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} formatter={(v)=>[`PKR ${v.toLocaleString()}`,"Collected"]} cursor={{fill:C.primaryPale}}/>
                  <Bar dataKey="amount" fill={C.primary} radius={[6,6,0,0]} name="Collected"/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {(()=>{
            const MO=["January","February","March","April","May","June","July","August","September","October","November","December"];
            const allMonthKeys=new Set(); fees.forEach(f=>{if(f.month&&f.year)allMonthKeys.add(`${f.year}-${String(MO.indexOf(f.month)+1).padStart(2,"0")}`);});
            allMonthKeys.add("2026-03");
            const sortedMonths=[...allMonthKeys].sort();
            const monthLabels=sortedMonths.map(k=>{const[y,m]=k.split("-");return{key:k,label:MO[parseInt(m)-1].slice(0,3)+" "+y,month:MO[parseInt(m)-1],year:y};});
            const payMap={};
            fees.forEach(f=>{const mk=`${f.year}-${String(MO.indexOf(f.month)+1).padStart(2,"0")}`;payMap[`${f.student_id}||${mk}`]=f;});
            students.forEach(s=>{const mk="2026-03";const key=`${s.id}||${mk}`;if(!payMap[key]&&s.fees==="Paid")payMap[key]={amount:"—",method:"(recorded)",student_id:s.id,month:"March",year:"2026"};});
            const sorted=[...students].sort((a,b)=>{const ac=(a.class||"").replace(/\s*(Boys|Girls)\s*$/i,"").trim();const bc=(b.class||"").replace(/\s*(Boys|Girls)\s*$/i,"").trim();return ac.localeCompare(bc,undefined,{numeric:true})||(a.name||"").localeCompare(b.name||"");});
            return (
              <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:8}}>
                  <div style={{fontWeight:800,color:C.primary,fontSize:15}}>📋 Student Fee Ledger</div>
                  <div style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>✅ Paid &nbsp;❌ Unpaid &nbsp;🎓 Scholarship &nbsp;⬜ Not enrolled</div>
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{background:C.primaryPale}}>
                      <th style={{padding:"8px 12px",textAlign:"left",fontWeight:700,color:C.primary,whiteSpace:"nowrap",position:"sticky",left:0,background:C.primaryPale,zIndex:1}}>Student</th>
                      <th style={{padding:"8px 10px",textAlign:"left",fontWeight:700,color:C.primary,whiteSpace:"nowrap"}}>Class</th>
                      <th style={{padding:"8px 10px",textAlign:"left",fontWeight:700,color:C.primary,whiteSpace:"nowrap"}}>Stream</th>
                      {monthLabels.map(m=><th key={m.key} style={{padding:"8px 10px",textAlign:"center",fontWeight:700,color:C.primary,whiteSpace:"nowrap",minWidth:70}}>{m.label}</th>)}
                      <th style={{padding:"8px 10px",textAlign:"center",fontWeight:700,color:C.primary,whiteSpace:"nowrap"}}>Paid</th>
                    </tr></thead>
                    <tbody>
                      {sorted.map((s,i)=>{
                        const enrolled=s.enrolled?new Date(s.enrolled):null;
                        const isSchol=s.scholarship_status&&s.scholarship_status!=="None";
                        let paidCount=0;
                        return (
                          <tr key={i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                            <td style={{padding:"8px 12px",fontWeight:700,color:C.text,whiteSpace:"nowrap",position:"sticky",left:0,background:i%2===0?C.white:C.bg,zIndex:1}}>
                              <div style={{display:"flex",alignItems:"center",gap:5}}>{s.name}{isSchol&&<ScholBadge status={s.scholarship_status} small/>}</div>
                            </td>
                            <td style={{padding:"8px 10px",color:C.muted,whiteSpace:"nowrap"}}>{(s.class||"").replace(/\s*(Boys|Girls)\s*$/i,"").trim()||"Hifz"}</td>
                            <td style={{padding:"8px 10px",whiteSpace:"nowrap"}}>
                              {s.stream&&<span style={{background:STREAM_COLORS[s.stream]?.bg||C.bg,color:STREAM_COLORS[s.stream]?.color||C.muted,border:`1px solid ${STREAM_COLORS[s.stream]?.border||C.border}`,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>{s.stream}</span>}
                            </td>
                            {monthLabels.map(m=>{
                              const monthDate=new Date(`${m.year}-${m.key.split("-")[1]}-01`);
                              const notYetEnrolled=enrolled&&monthDate<new Date(enrolled.getFullYear(),enrolled.getMonth(),1);
                              const payment=payMap[`${s.id}||${m.key}`];
                              if(payment)paidCount++;
                              return (
                                <td key={m.key} style={{padding:"6px 8px",textAlign:"center"}}>
                                  {notYetEnrolled?<span style={{fontSize:14,opacity:.3}}>⬜</span>
                                  :payment?(payment.method==="Scholarship"?<span title="Scholarship">🎓</span>:<div title={`PKR ${payment.amount!=="—"?Number(payment.amount).toLocaleString():"recorded"} · ${payment.method||"Cash"}`}><span style={{fontSize:14}}>✅</span>{payment.amount!=="—"&&<div style={{fontSize:9,color:C.primary,fontWeight:700,marginTop:1}}>{Number(payment.amount).toLocaleString()}</div>}</div>)
                                  :<span style={{fontSize:14}}>❌</span>}
                                </td>
                              );
                            })}
                            <td style={{padding:"8px 10px",textAlign:"center",fontWeight:800,color:C.primary}}>{paidCount}/{monthLabels.length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Fee Structure ── */}
      {activeTab==="types"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Section picker */}
          <div style={{background:C.white,borderRadius:16,padding:"16px 20px",boxShadow:`0 2px 12px ${C.shadow}`,display:"flex",gap:8,flexWrap:"wrap"}}>
            {[["stream","🌊 Stream defaults","Set once per stream"],["class","📚 Class tuition","Tuition by class"],["student","👤 Student overrides","Individual exceptions"]].map(([id,label,sub])=>(
              <button key={id} onClick={()=>setTypesSection(id)}
                style={{flex:1,minWidth:160,padding:"12px 16px",borderRadius:12,border:`2px solid ${typesSection===id?C.primary:C.border}`,background:typesSection===id?C.primaryPale:C.white,cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
                <div style={{fontWeight:700,fontSize:13,color:typesSection===id?C.primary:C.text}}>{label}</div>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>{sub}</div>
              </button>
            ))}
          </div>

          {/* Stream defaults */}
          {typesSection==="stream"&&(
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>🌊 Stream defaults</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Set the base fee amounts for each stream. These apply to all students in the stream unless overridden at class or student level. Click a stream to edit and bulk-apply.</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:12}}>
                {ALL_STREAMS.map(stream=>{
                  const sd=streamDefaults[stream]||{};
                  const streamC=STREAM_COLORS[stream]||{bg:C.bg,color:C.muted,border:C.border};
                  const types=feeTypesForStream(stream).filter(f=>f.recurring);
                  return (
                    <div key={stream} style={{border:`1.5px solid ${streamC.border}`,borderRadius:14,padding:16,background:streamC.bg}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                        <span style={{fontWeight:700,fontSize:13,color:streamC.color}}>{stream}</span>
                        <button onClick={()=>{setStreamEditTarget(stream);setStreamDraft({...sd});}}
                          style={{background:C.white,color:streamC.color,border:`1px solid ${streamC.border}`,borderRadius:8,padding:"3px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Edit</button>
                      </div>
                      {types.length===0&&<div style={{fontSize:12,color:C.muted,fontStyle:"italic"}}>No recurring fees</div>}
                      {types.map(ft=>(
                        <div key={ft.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",borderBottom:`1px solid ${streamC.border}88`}}>
                          <span style={{color:streamC.color,opacity:.8}}>{ft.label}</span>
                          <span style={{fontWeight:700,color:streamC.color}}>{sd[ft.id]?`PKR ${Number(sd[ft.id]).toLocaleString()}`:"—"}</span>
                        </div>
                      ))}
                      <div style={{marginTop:8,fontSize:11,color:streamC.color,opacity:.7}}>
                        {students.filter(s=>(s.stream||"School Only")===stream).length} students
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Stream edit modal */}
              {streamEditTarget&&(
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                  <div style={{background:C.white,borderRadius:20,padding:28,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",boxShadow:`0 8px 40px ${C.shadow}`}}>
                    <div style={{fontWeight:800,fontSize:16,color:C.primary,marginBottom:4}}>Edit stream defaults — {streamEditTarget}</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Set base fee amounts for this stream. Use the class scope below to apply to all classes or specific ones.</div>

                    {/* ── Class Scope Selector ── */}
                    {(()=>{
                      const streamClasses = STREAM_CLASSES[streamEditTarget]||[];
                      const scopeMode     = streamDraft._scopeMode||"all";
                      const scopeRange    = streamDraft._scopeRange||{from:"",to:""};
                      const scopePick     = streamDraft._scopePick||[];
                      return streamClasses.length > 0 ? (
                        <div style={{background:"#f0f7f2",border:`1.5px solid ${C.primaryMid}`,borderRadius:12,padding:16,marginBottom:20}}>
                          <div style={{fontWeight:700,fontSize:13,color:C.primary,marginBottom:10}}>📚 Apply fee to which classes?</div>
                          {/* Mode selector */}
                          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                            {[["all","All Classes"],["range","Class Range"],["pick","Specific Classes"]].map(([mode,label])=>(
                              <button key={mode} onClick={()=>setStreamDraft(d=>({...d,_scopeMode:mode}))}
                                style={{padding:"5px 14px",borderRadius:20,border:`1.5px solid ${scopeMode===mode?C.primary:C.border}`,background:scopeMode===mode?C.primary:"white",color:scopeMode===mode?C.white:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",transition:"all .15s"}}>
                                {label}
                              </button>
                            ))}
                          </div>
                          {scopeMode==="all" && (
                            <div style={{fontSize:12,color:C.muted}}>✅ Will apply to <b>all {streamClasses.length} classes</b> in {streamEditTarget}: {streamClasses.join(", ")}</div>
                          )}
                          {scopeMode==="range" && (
                            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                              <div style={{fontSize:12,fontWeight:700,color:C.muted}}>From</div>
                              <select value={scopeRange.from} onChange={e=>setStreamDraft(d=>({...d,_scopeRange:{...d._scopeRange,from:e.target.value}}))}
                                style={{padding:"5px 10px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,background:"white"}}>
                                <option value="">— select —</option>
                                {streamClasses.map(c=><option key={c} value={c}>{c}</option>)}
                              </select>
                              <div style={{fontSize:12,fontWeight:700,color:C.muted}}>To</div>
                              <select value={scopeRange.to} onChange={e=>setStreamDraft(d=>({...d,_scopeRange:{...d._scopeRange,to:e.target.value}}))}
                                style={{padding:"5px 10px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,background:"white"}}>
                                <option value="">— select —</option>
                                {streamClasses.map(c=><option key={c} value={c}>{c}</option>)}
                              </select>
                              {scopeRange.from&&scopeRange.to&&(()=>{
                                const fi=streamClasses.indexOf(scopeRange.from),ti=streamClasses.indexOf(scopeRange.to);
                                const sel=fi<=ti?streamClasses.slice(fi,ti+1):[];
                                return sel.length?<div style={{fontSize:12,color:C.primary,fontWeight:700}}>→ {sel.join(", ")}</div>:null;
                              })()}
                            </div>
                          )}
                          {scopeMode==="pick" && (
                            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                              {streamClasses.map(c=>{
                                const on=scopePick.includes(c);
                                return (
                                  <button key={c} onClick={()=>setStreamDraft(d=>({...d,_scopePick:on?d._scopePick.filter(x=>x!==c):[...(d._scopePick||[]),c]}))}
                                    style={{padding:"4px 12px",borderRadius:20,border:`1.5px solid ${on?C.primary:C.border}`,background:on?C.primary:"white",color:on?C.white:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",transition:"all .15s"}}>
                                    {c}
                                  </button>
                                );
                              })}
                              {scopePick.length>0&&<div style={{fontSize:12,color:C.primary,fontWeight:700,alignSelf:"center"}}>→ {scopePick.length} class(es) selected</div>}
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}

                    {/* ── Fee fields — ALL recurring types including Tuition ── */}
                    {feeTypesForStream(streamEditTarget).filter(f=>f.recurring).map(ft=>(
                      <div key={ft.id} style={{marginBottom:14}}>
                        <label style={{fontSize:12,fontWeight:700,color:C.muted,display:"block",marginBottom:4}}>
                          {ft.label}{" "}
                          <span style={{background:C.primaryPale,color:C.primary,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>
                            {ft.id==="transport"?"Optional — per student":"Monthly"}
                          </span>
                        </label>
                        <input type="number" placeholder="0" value={streamDraft[ft.id]||""} onChange={e=>setStreamDraft(d=>({...d,[ft.id]:+e.target.value}))}
                          style={{border:`1.5px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:14,width:"100%"}}/>
                      </div>
                    ))}

                    {(()=>{
                      const streamClasses=STREAM_CLASSES[streamEditTarget]||[];
                      const scopeMode=streamDraft._scopeMode||"all";
                      const scopeRange=streamDraft._scopeRange||{from:"",to:""};
                      const scopePick=streamDraft._scopePick||[];
                      let scopeLabel="all students";
                      if(streamClasses.length>0){
                        if(scopeMode==="range"&&scopeRange.from&&scopeRange.to){
                          const fi=streamClasses.indexOf(scopeRange.from),ti=streamClasses.indexOf(scopeRange.to);
                          const sel=fi<=ti?streamClasses.slice(fi,ti+1):[];
                          scopeLabel=sel.length?`students in ${sel.join(", ")}`:"selected class range";
                        } else if(scopeMode==="pick"&&scopePick.length>0){
                          scopeLabel=`students in ${scopePick.join(", ")}`;
                        }
                      }
                      const count=students.filter(s=>{
                        if((s.stream||"School Only")!==streamEditTarget) return false;
                        if(streamClasses.length===0||scopeMode==="all") return true;
                        const cls=s.class||s["class"]||"";
                        if(scopeMode==="range"&&scopeRange.from&&scopeRange.to){
                          const fi=streamClasses.indexOf(scopeRange.from),ti=streamClasses.indexOf(scopeRange.to);
                          const sel=fi<=ti?streamClasses.slice(fi,ti+1):[];
                          return sel.includes(cls);
                        }
                        if(scopeMode==="pick"&&scopePick.length>0) return scopePick.includes(cls);
                        return true;
                      }).length;
                      return (
                        <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e",marginBottom:20}}>
                          ⚡ Clicking Save will bulk-apply to <b>{count} {scopeLabel}</b> in <b>{streamEditTarget}</b>. Students with manual overrides are unaffected.
                        </div>
                      );
                    })()}

                    <div style={{display:"flex",gap:10}}>
                      <button onClick={saveStreamDefaults} style={{background:C.primary,color:C.white,border:"none",borderRadius:10,padding:"10px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✅ Save & Apply</button>
                      <button onClick={()=>setStreamEditTarget(null)} style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Class tuition grid */}
          {typesSection==="class"&&(
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>📚 Class tuition grid</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Set tuition for each class. Editing a cell and saving will update all students in that class instantly. Hifz Only students use the stream default, not this grid.</div>
              {[["School (Class 1–8)", STREAM_CLASSES["School Only"]], ["College (Class 9–12)", STREAM_CLASSES["College"]]].map(([groupLabel, classes])=>(
                <div key={groupLabel} style={{marginBottom:24}}>
                  <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>{groupLabel}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10}}>
                    {classes.map(cls=>{
                      const ct=classTuitionDraft.find(x=>x.class===cls);
                      const stuCount=students.filter(s=>(s.class||s["class"]||"")==cls).length;
                      return (
                        <div key={cls} style={{border:`1px solid ${C.border}`,borderRadius:12,padding:12,background:C.bg}}>
                          <div style={{fontSize:11,fontWeight:700,color:C.muted,marginBottom:6}}>{cls} <span style={{fontWeight:400,opacity:.6}}>({stuCount} students)</span></div>
                          <input type="number" placeholder="Amount" value={ct?.amount||""}
                            onChange={e=>{const val=+e.target.value; setClassTuitionDraft(prev=>prev.map(x=>x.class===cls?{...x,amount:val}:x).concat(prev.find(x=>x.class===cls)?[]:[{class:cls,amount:val}]));}}
                            style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"7px 10px",fontSize:13,outline:"none"}}/>
                          <div style={{fontSize:10,color:C.muted,marginTop:4}}>PKR / month</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button onClick={saveClassTuition} style={{background:C.primary,color:C.white,border:"none",borderRadius:10,padding:"10px 28px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                ✅ Save & apply to all students
              </button>
            </div>
          )}

          {/* Student overrides */}
          {typesSection==="student"&&(
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>👤 Student overrides</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Individual exceptions — scholarship discounts, transport opt-outs, custom charges. Students with no override inherit from stream and class.</div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead><tr style={{background:C.primaryPale}}>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11}}>Student</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11}}>Stream / Class</th>
                    <th style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11}}>Scholarship</th>
                    <th style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:C.primary,fontSize:11}}>Effective monthly</th>
                    <th style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:C.primary,fontSize:11}}>Override</th>
                    <th style={{padding:"10px 12px"}}></th>
                  </tr></thead>
                  <tbody>
                    {[...students].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map((s,i)=>{
                      const resolved=resolveFee(s,feeConfig,streamDefaults,classTuition);
                      const hasOverride=!!feeConfig[s.id];
                      return (
                        <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                          <td style={{padding:"10px 12px",fontWeight:600}}>{s.name||s.full_name}</td>
                          <td style={{padding:"10px 12px"}}>
                            {s.stream&&<span style={{background:STREAM_COLORS[s.stream]?.bg||C.bg,color:STREAM_COLORS[s.stream]?.color||C.muted,border:`1px solid ${STREAM_COLORS[s.stream]?.border||C.border}`,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700,marginRight:4}}>{s.stream}</span>}
                            <span style={{color:C.muted,fontSize:12}}>{s.class||"—"}</span>
                          </td>
                          <td style={{padding:"10px 12px"}}>{s.scholarship_status&&s.scholarship_status!=="None"?<ScholBadge status={s.scholarship_status}/>:<span style={{color:C.muted,fontSize:12}}>—</span>}</td>
                          <td style={{padding:"10px 12px",textAlign:"right",fontWeight:700,color:C.primary}}>{resolved.total?`PKR ${resolved.total.toLocaleString()}`:"—"}</td>
                          <td style={{padding:"10px 12px",textAlign:"right"}}>{hasOverride?<span style={{background:C.accentPale,color:C.accent,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>Custom</span>:<span style={{color:C.muted,fontSize:12}}>Inherited</span>}</td>
                          <td style={{padding:"10px 12px"}}>
                            <button onClick={()=>{setFeeConfigEdit(s.id);setFeeConfigDraft({...(feeConfig[s.id]||{})});}}
                              style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Student edit modal */}
              {feeConfigEdit&&(()=>{
                const s=students.find(x=>x.id===feeConfigEdit);
                const types=feeTypesForStream(s?.stream||"School Only");
                return (
                  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                    <div style={{background:C.white,borderRadius:20,padding:28,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",boxShadow:`0 8px 40px ${C.shadow}`}}>
                      <div style={{fontWeight:800,fontSize:16,color:C.primary,marginBottom:2}}>Override fees — {s?.name||s?.full_name}</div>
                      <div style={{fontSize:12,color:C.muted,marginBottom:4}}>{s?.stream} · {s?.class||"No class"}</div>
                      {s?.scholarship_status&&s.scholarship_status!=="None"&&<div style={{marginBottom:16}}><ScholBadge status={s.scholarship_status}/></div>}
                      <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Leave blank to inherit from stream/class defaults. Any value you set here locks that fee type for this student.</div>
                      {types.map(ft=>(
                        <div key={ft.id} style={{marginBottom:14}}>
                          <label style={{fontSize:12,fontWeight:700,color:C.muted,display:"block",marginBottom:4}}>{ft.label}{ft.recurring&&<span style={{background:C.primaryPale,color:C.primary,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700,marginLeft:6}}>Monthly</span>}</label>
                          <input type="number" placeholder="Leave blank to inherit" value={feeConfigDraft[ft.id]||""}
                            onChange={e=>setFeeConfigDraft(d=>({...d,[ft.id]:e.target.value?+e.target.value:undefined}))}
                            style={{border:`1.5px solid ${C.border}`,borderRadius:10,padding:"8px 12px",fontSize:13,width:"100%"}}/>
                        </div>
                      ))}
                      <div style={{marginBottom:14}}>
                        <label style={{fontSize:12,fontWeight:700,color:C.muted,display:"block",marginBottom:4}}>Custom charge</label>
                        <div style={{display:"flex",gap:8}}>
                          <input type="number" placeholder="Amount" value={feeConfigDraft.custom_amount||""} onChange={e=>setFeeConfigDraft(d=>({...d,custom_amount:+e.target.value}))}
                            style={{border:`1.5px solid ${C.border}`,borderRadius:10,padding:"8px 10px",fontSize:13,flex:1}}/>
                          <input placeholder="Reason" value={feeConfigDraft.custom_note||""} onChange={e=>setFeeConfigDraft(d=>({...d,custom_note:e.target.value}))}
                            style={{border:`1.5px solid ${C.border}`,borderRadius:10,padding:"8px 10px",fontSize:13,flex:2}}/>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:10,marginTop:20}}>
                        <button onClick={saveStudentFeeConfig} style={{background:C.primary,color:C.white,border:"none",borderRadius:10,padding:"10px 24px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✅ Save override</button>
                        <button onClick={()=>setFeeConfigEdit(null)} style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Cancel</button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* ── Defaulters ── */}
      {activeTab==="defaulters"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
            {[
              {label:"Total outstanding",value:`PKR ${defaulters.reduce((a,d)=>a+d.bal,0).toLocaleString()}`,color:C.rose},
              {label:"1–30 days",value:`PKR ${defaulters.filter(d=>d.bucket==="1–30 days").reduce((a,d)=>a+d.bal,0).toLocaleString()}`,color:C.accent},
              {label:"31–60 days",value:`PKR ${defaulters.filter(d=>d.bucket==="31–60 days").reduce((a,d)=>a+d.bal,0).toLocaleString()}`,color:"#e97316"},
              {label:"90+ days",value:`PKR ${defaulters.filter(d=>d.bucket==="90+ days").reduce((a,d)=>a+d.bal,0).toLocaleString()}`,color:C.rose},
            ].map(c=>(
              <div key={c.label} style={{background:C.white,borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 12px ${C.shadow}`,borderTop:`3px solid ${c.color}`}}>
                <div style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>{c.label}</div>
                <div style={{fontSize:18,fontWeight:900,color:c.color}}>{c.value}</div>
              </div>
            ))}
          </div>
          {defaulters.length===0?(
            <div style={{background:C.white,borderRadius:16,padding:40,textAlign:"center",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontSize:40,marginBottom:12}}>🎉</div>
              <div style={{fontWeight:700,color:C.primary,fontSize:15}}>No defaulters — all fees up to date!</div>
              <div style={{fontSize:12,color:C.muted,marginTop:4}}>Full scholarship students are automatically excluded</div>
            </div>
          ):(
            <div style={{background:C.white,borderRadius:16,padding:24,boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:16}}>⚠️ Defaulter Aging Report</div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead><tr style={{background:C.primaryPale}}>
                    {["Student","Stream","Month","Due","Paid","Outstanding","Overdue","Action"].map(h=>(
                      <th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11,textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {defaulters.map((d,i)=>{
                      const bc=d.bucket==="1–30 days"?C.accent:d.bucket==="31–60 days"?"#e97316":C.rose;
                      return (
                        <tr key={`${d.s.id}-${d.m}-${d.y}`} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                          <td style={{padding:"10px 12px",fontWeight:600}}>{d.s.name||d.s.full_name}</td>
                          <td style={{padding:"10px 12px"}}>
                            {d.s.stream&&<span style={{background:STREAM_COLORS[d.s.stream]?.bg||C.bg,color:STREAM_COLORS[d.s.stream]?.color||C.muted,border:`1px solid ${STREAM_COLORS[d.s.stream]?.border||C.border}`,borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:700}}>{d.s.stream}</span>}
                          </td>
                          <td style={{padding:"10px 12px",whiteSpace:"nowrap"}}>{MONTHS[d.m]} {d.y}</td>
                          <td style={{padding:"10px 12px"}}>{fmtPKR(d.due)}</td>
                          <td style={{padding:"10px 12px",color:C.primary}}>{d.paid?fmtPKR(d.paid):"—"}</td>
                          <td style={{padding:"10px 12px",fontWeight:800,color:C.rose}}>{fmtPKR(d.bal)}</td>
                          <td style={{padding:"10px 12px"}}><span style={{background:bc+"22",color:bc,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{d.bucket}</span></td>
                          <td style={{padding:"10px 12px"}}>
                            {(d.s.contact||d.s.phone)&&(
                              <button onClick={()=>feeOpenWA(d.s.contact||d.s.phone,`Assalamu Alaikum, dear parent of ${d.s.name||d.s.full_name}. This is a reminder that ${fmtPKR(d.bal)} is outstanding for ${MONTHS[d.m]} ${d.y} (${d.days} days overdue). Please clear the dues at your earliest. JazakAllah.`)}
                                style={{background:"#25d36622",color:"#128c7e",border:"1px solid #25d366",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>📲 Remind</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Analytics ── */}
      {activeTab==="analytics"&&(()=>{
        // ── Computed data for fee analytics ──────────────────────────
        const monthName = MONTHS[analyticsMonth];

        // 1. Stream-wise collection breakdown for selected month
        const streamBreakdown = ALL_STREAMS.map(stream=>{
          const streamStudents = students.filter(s=>(s.stream||"School Only")===stream);
          const expected = streamStudents.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
          const collected = feePayments.filter(r=>r.month===monthName&&r.year===String(analyticsYear)&&streamStudents.some(s=>s.id===r.student_id)).reduce((s,r)=>s+(r.amount_paid||0),0);
          return {stream:stream.replace("Both (Hifz + School)","Hifz+School"), expected, collected, gap:Math.max(0,expected-collected), count:streamStudents.length};
        }).filter(d=>d.count>0);

        // 2. Class-wise expected vs collected
        const allClasses = [...new Set(students.map(s=>s.class||s["class"]||"").filter(Boolean))].sort();
        const classBreakdown = allClasses.map(cls=>{
          const clsStudents = students.filter(s=>(s.class||s["class"]||"")===cls);
          const expected = clsStudents.reduce((sum,s)=>sum+resolveFee(s,feeConfig,streamDefaults,classTuition).total,0);
          const collected = feePayments.filter(r=>r.month===monthName&&r.year===String(analyticsYear)&&clsStudents.some(s=>s.id===r.student_id)).reduce((s,r)=>s+(r.amount_paid||0),0);
          return {cls, expected, collected, rate:expected?Math.round(collected/expected*100):0};
        }).filter(d=>d.expected>0);

        // 3. Fee type composition (pie) — what makes up the expected total
        const feeTypeTotals = {};
        students.forEach(s=>{
          const resolved = resolveFee(s,feeConfig,streamDefaults,classTuition);
          Object.entries(resolved.breakdown).forEach(([type,v])=>{
            if(v && v.amount > 0) feeTypeTotals[type]=(feeTypeTotals[type]||0)+v.amount;
          });
        });
        const PIE_COLORS=["#1a6b3c","#0d9488","#7c3aed","#f0a500","#e11d48","#2563eb","#d97706","#059669"];
        const feeTypePie = Object.entries(feeTypeTotals).filter(([,v])=>v>0).map(([k,v],i)=>({
          name: FEE_TYPES_LIST.find(f=>f.id===k)?.label||k, value:v, color:PIE_COLORS[i%PIE_COLORS.length]
        }));

        // 4. Top 8 defaulters this month (highest outstanding)
        const defaulters = students.map(s=>{
          const resolved = resolveFee(s,feeConfig,streamDefaults,classTuition);
          const paid = feePayments.filter(r=>r.month===monthName&&r.year===String(analyticsYear)&&r.student_id===s.id).reduce((t,r)=>t+(r.amount_paid||0),0);
          const outstanding = Math.max(0, resolved.total - paid);
          return {name:(s.name||s.full_name||"").split(" ").slice(0,2).join(" "), cls:s.class||"", outstanding, paid, total:resolved.total};
        }).filter(d=>d.outstanding>0).sort((a,b)=>b.outstanding-a.outstanding).slice(0,8);

        // 5. Monthly gap trend (last 6 months)
        const gapTrend = trendData.map(d=>({...d, gap:Math.max(0,d.expected-d.collected)}));

        const totalExpected = streamBreakdown.reduce((s,d)=>s+d.expected,0);
        const totalCollected = streamBreakdown.reduce((s,d)=>s+d.collected,0);
        const collRate = totalExpected ? Math.round(totalCollected/totalExpected*100) : 0;
        const totalOutstanding = Math.max(0,totalExpected-totalCollected);

        return (
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            {/* Month/Year filter */}
            <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",background:C.white,padding:"12px 18px",borderRadius:14,boxShadow:`0 2px 8px ${C.shadow}`}}>
              <span style={{fontSize:13,fontWeight:700,color:C.muted}}>📅 Viewing:</span>
              <select value={analyticsMonth} onChange={e=>setAnalyticsMonth(+e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.white,fontWeight:600}}>
                {MONTHS.map((m,i)=><option key={i} value={i}>{m}</option>)}
              </select>
              <select value={analyticsYear} onChange={e=>setAnalyticsYear(+e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,background:C.white,fontWeight:600}}>
                {[FEE_CUR_YEAR-1,FEE_CUR_YEAR,FEE_CUR_YEAR+1].map(y=><option key={y} value={y}>{y}</option>)}
              </select>
              <span style={{marginLeft:"auto",fontSize:12,color:C.muted,fontWeight:600}}>{monthName} {analyticsYear} snapshot</span>
            </div>

            {/* KPI row — 5 cards unique to fee analytics */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12}}>
              {[
                {icon:"💵",label:"Monthly Expected",value:fmtPKR(totalExpected),color:C.teal,sub:"from all students"},
                {icon:"✅",label:"Collected",value:fmtPKR(totalCollected),color:C.primary,sub:`${collRate}% of target`},
                {icon:"⏳",label:"Outstanding",value:fmtPKR(totalOutstanding),color:totalOutstanding>0?C.rose:C.primary,sub:`${defaulters.length} students pending`},
                {icon:"👨‍🎓",label:"Paying Students",value:feePayments.filter(r=>r.month===monthName&&r.year===String(analyticsYear)).length,color:C.blue,sub:"paid this month"},
                {icon:"🎓",label:"Scholarship Load",value:fmtPKR(students.reduce((sum,s)=>{const r=resolveFee(s,feeConfig,streamDefaults,classTuition);const saving=Object.values(r.breakdown).reduce((a,v)=>a+(v.originalAmount&&v.originalAmount>v.amount?v.originalAmount-v.amount:0),0);return sum+saving;},0)),color:C.purple,sub:"total waived"},
              ].map(c=>(
                <div key={c.label} style={{background:C.white,borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 12px ${C.shadow}`,borderLeft:`4px solid ${c.color}`}}>
                  <div style={{fontSize:20,marginBottom:4}}>{c.icon}</div>
                  <div style={{fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>{c.label}</div>
                  <div style={{fontSize:20,fontWeight:900,color:c.color,marginTop:2}}>{c.value}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Row 1: Stream-wise bar + Fee type pie */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {/* Stream-wise collection */}
              <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:4}}>🌊 Collection by Stream</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:14}}>{monthName} {analyticsYear} — expected vs collected per stream</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={streamBreakdown} barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="stream" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false} tickFormatter={v=>`${Math.round(v/1000)}k`}/>
                    <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} formatter={v=>[fmtPKR(v)]}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="expected" name="Expected" fill={C.primaryMid} radius={[4,4,0,0]}/>
                    <Bar dataKey="collected" name="Collected" fill={C.primary} radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Fee type pie */}
              <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:4}}>🥧 Fee Type Composition</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:14}}>How monthly revenue is made up across all students</div>
                {feeTypePie.length ? (
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <ResponsiveContainer width="55%" height={180}>
                      <PieChart>
                        <Pie data={feeTypePie} dataKey="value" cx="50%" cy="50%" innerRadius={42} outerRadius={72} paddingAngle={3}>
                          {feeTypePie.map((d,i)=><Cell key={i} fill={d.color}/>)}
                        </Pie>
                        <Tooltip contentStyle={{borderRadius:10,border:"none"}} formatter={v=>[fmtPKR(v)]}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:5}}>
                      {feeTypePie.map(d=>(
                        <div key={d.name} style={{display:"flex",alignItems:"center",gap:6,fontSize:11}}>
                          <span style={{width:10,height:10,borderRadius:3,background:d.color,flexShrink:0}}/>
                          <span style={{color:C.muted,flex:1}}>{d.name}</span>
                          <span style={{fontWeight:700,color:C.text}}>{fmtPKR(d.value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div style={{color:C.muted,fontSize:13,paddingTop:20}}>No fee structure set yet</div>}
              </div>
            </div>

            {/* Row 2: Class-wise collection rate + Outstanding gap trend */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {/* Class-wise collection rate */}
              <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:4}}>📚 Collection Rate by Class</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:14}}>{monthName} — % collected per class</div>
                {classBreakdown.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={classBreakdown} layout="vertical" barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false}/>
                      <XAxis type="number" domain={[0,100]} tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false} tickFormatter={v=>`${v}%`}/>
                      <YAxis dataKey="cls" type="category" tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false} width={60}/>
                      <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} formatter={v=>[`${v}%`,"Collection Rate"]}/>
                      <Bar dataKey="rate" name="Collection %" radius={[0,4,4,0]}
                        fill={C.primary}
                        label={{position:"right",fontSize:10,fill:C.muted,formatter:v=>`${v}%`}}>
                        {classBreakdown.map((d,i)=><Cell key={i} fill={d.rate>=80?C.primary:d.rate>=50?C.accent:C.rose}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{color:C.muted,fontSize:13,paddingTop:20}}>No class data available</div>}
              </div>

              {/* 6-month outstanding gap trend */}
              <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.primary,fontSize:14,marginBottom:4}}>📉 Outstanding Gap — 6 Months</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Uncollected amount trend (lower is better)</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={gapTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="month" tick={{fontSize:11,fill:C.muted}} axisLine={false} tickLine={false}/>
                    <YAxis tick={{fontSize:10,fill:C.muted}} axisLine={false} tickLine={false} tickFormatter={v=>`${v}k`}/>
                    <Tooltip contentStyle={{borderRadius:10,border:"none",boxShadow:`0 4px 16px ${C.shadow}`}} formatter={v=>[`PKR ${v}k`,"Outstanding"]}/>
                    <Line type="monotone" dataKey="gap" name="Outstanding (k)" stroke={C.rose} strokeWidth={2.5} dot={{r:4,fill:C.rose}} activeDot={{r:6}}/>
                    <Line type="monotone" dataKey="collected" name="Collected (k)" stroke={C.primary} strokeWidth={2} strokeDasharray="4 2" dot={false}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Row 3: Top defaulters table */}
            {defaulters.length>0&&(
              <div style={{background:C.white,borderRadius:16,padding:22,boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{fontWeight:800,color:C.rose,fontSize:14,marginBottom:4}}>⚠️ Top Defaulters — {monthName} {analyticsYear}</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:14}}>Students with highest outstanding balance this month</div>
                <div style={{display:"grid",gap:8}}>
                  {defaulters.map((d,i)=>{
                    const pct = d.total ? Math.round(d.paid/d.total*100) : 0;
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:i%2===0?C.bg:C.white,borderRadius:10}}>
                        <span style={{width:24,height:24,borderRadius:"50%",background:C.rosePale,color:C.rose,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:11,flexShrink:0}}>{i+1}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:13,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{d.name}</div>
                          <div style={{fontSize:11,color:C.muted}}>{d.cls}</div>
                        </div>
                        <div style={{flex:2,minWidth:80}}>
                          <div style={{height:6,background:C.border,borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${pct}%`,background:pct>=80?C.primary:pct>=50?C.accent:C.rose,borderRadius:3,transition:"width .4s"}}/>
                          </div>
                          <div style={{fontSize:10,color:C.muted,marginTop:2}}>{pct}% paid</div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontWeight:800,color:C.rose,fontSize:13}}>{fmtPKR(d.outstanding)}</div>
                          <div style={{fontSize:10,color:C.muted}}>outstanding</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Bulk Fee Recording ── */}
      {activeTab==="bulk"&&(()=>{
        const allStudentClasses = [...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort((a,b)=>a.localeCompare(b,undefined,{numeric:true}));
        const bulkStudents = bulkClass ? students.filter(s=>(s.class||s["class"])===bulkClass) : [];

        const initBulkAmounts = (cls) => {
          const stu = students.filter(s=>(s.class||s["class"])===cls);
          const checked = {}, amounts = {};
          stu.forEach(s=>{
            const r = resolveFee(s, feeConfig, streamDefaults, classTuition);
            checked[s.id] = true;
            amounts[s.id] = r.total > 0 ? r.total : (s.monthly_fee||0);
          });
          setBulkChecked(checked);
          setBulkAmounts(amounts);
        };

        const toggleAll = (val) => {
          const next = {};
          bulkStudents.forEach(s=>{ next[s.id]=val; });
          setBulkChecked(next);
        };

        const checkedStudents = bulkStudents.filter(s=>bulkChecked[s.id]);
        const totalBulk = checkedStudents.reduce((a,s)=>a+Number(bulkAmounts[s.id]||0),0);

        const saveBulk = async () => {
          if(!bulkClass) return showToast("Please select a class",false);
          if(!checkedStudents.length) return showToast("Select at least one student",false);
          const hasAmounts = checkedStudents.every(s=>Number(bulkAmounts[s.id]||0)>0);
          if(!hasAmounts) return showToast("All selected students need an amount > 0",false);
          setBulkSaving(true);
          const paidDate = new Date().toISOString().split("T")[0];
          let saved = 0, failed = 0;
          for(const s of checkedStudents){
            const amt = Number(bulkAmounts[s.id]||0);
            const receiptNo = "BLK-"+String(s.id).padStart(5,"0").slice(-5)+"-"+bulkMonth.slice(0,3).toUpperCase()+String(bulkYear).slice(-2);
            try {
              await supabase.from("fee_payments").upsert({
                student_id:s.id, month:bulkMonth, year:String(bulkYear),
                amount_paid:amt, status:"paid", payment_method:bulkMethod,
                splits:{tuition:amt}, receipt_note:"Bulk entry", paid_at:new Date().toISOString(), updated_at:new Date().toISOString()
              },{onConflict:"student_id,month,year"});
              const legacyRec = { student_name:s.name||s.full_name, "class":s.class||s["class"], amount:amt,
                month:bulkMonth, year:String(bulkYear), method:bulkMethod, notes:"Bulk entry", paid_date:paidDate, receipt_no:receiptNo, student_id:s.id };
              setFees(prev=>[legacyRec,...prev]);
              saved++;
            } catch(e){ console.warn("Bulk save error for",s.name,e); failed++; }
          }
          setBulkSaving(false);
          if(failed===0) showToast(`✅ ${saved} payments recorded for ${bulkMonth} ${bulkYear}`);
          else showToast(`⚠️ ${saved} saved, ${failed} failed — check console`);
          if(onFeesSaved) onFeesSaved();
          setBulkChecked({}); setBulkClass("");
        };

        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>📦 Bulk Fee Recording</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:20}}>Select a class, review amounts (auto-filled from fee structure), tick students, and save all at once.</div>
              <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
                <div style={{flex:"1 1 160px"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Class *</label>
                  <select value={bulkClass} onChange={e=>{const c=e.target.value;setBulkClass(c);if(c)initBulkAmounts(c);}}
                    style={{width:"100%",padding:"9px 12px",borderRadius:9,border:`1.5px solid ${bulkClass?C.primary:C.border}`,fontSize:13,background:C.white}}>
                    <option value="">-- Select Class --</option>
                    {allStudentClasses.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{flex:"1 1 130px"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Month</label>
                  <select value={bulkMonth} onChange={e=>setBulkMonth(e.target.value)}
                    style={{width:"100%",padding:"9px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white}}>
                    {MONTHS.map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div style={{flex:"0 0 90px"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Year</label>
                  <select value={bulkYear} onChange={e=>setBulkYear(e.target.value)}
                    style={{width:"100%",padding:"9px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white}}>
                    {["2024","2025","2026","2027"].map(y=><option key={y}>{y}</option>)}
                  </select>
                </div>
                <div style={{flex:"1 1 140px"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Method</label>
                  <select value={bulkMethod} onChange={e=>setBulkMethod(e.target.value)}
                    style={{width:"100%",padding:"9px 12px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white}}>
                    {["Cash","Bank Transfer","JazzCash","EasyPaisa","Cheque"].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {bulkClass && bulkStudents.length > 0 && (
              <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{fontWeight:700,fontSize:13,color:C.text}}>{bulkStudents.length} students in {bulkClass}</span>
                    <span style={{background:C.primaryPale,color:C.primary,borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{checkedStudents.length} selected</span>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>toggleAll(true)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${C.primaryMid}`,background:C.primaryPale,color:C.primary,fontWeight:700,fontSize:11,cursor:"pointer"}}>✅ Select All</button>
                    <button onClick={()=>toggleAll(false)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${C.border}`,background:C.white,color:C.muted,fontWeight:700,fontSize:11,cursor:"pointer"}}>☐ Deselect All</button>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {bulkStudents.map(s=>{
                    const resolved = resolveFee(s, feeConfig, streamDefaults, classTuition);
                    const isChecked = !!bulkChecked[s.id];
                    return (
                      <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:isChecked?C.primaryPale:C.bg,borderRadius:10,border:`1.5px solid ${isChecked?C.primaryMid:C.border}`,transition:"all .15s"}}>
                        <input type="checkbox" checked={isChecked} onChange={e=>setBulkChecked(p=>({...p,[s.id]:e.target.checked}))}
                          style={{width:17,height:17,accentColor:C.primary,cursor:"pointer",flexShrink:0}}/>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name||s.full_name}</div>
                          <div style={{fontSize:11,color:C.muted,marginTop:1}}>{s.stream||"School Only"}{s.scholarship_status&&s.scholarship_status!=="None"?` · 🎓 ${s.scholarship_status}`:""}</div>
                        </div>
                        {resolved.total>0&&<span style={{fontSize:10,color:C.muted,background:C.bg,borderRadius:6,padding:"2px 7px",flexShrink:0,border:`1px solid ${C.border}`}}>struct: {fmtPKR(resolved.total)}</span>}
                        <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:12,color:C.muted,fontWeight:600}}>PKR</span>
                          <input type="number" value={bulkAmounts[s.id]||""} disabled={!isChecked}
                            onChange={e=>setBulkAmounts(p=>({...p,[s.id]:e.target.value}))}
                            style={{width:90,padding:"6px 9px",borderRadius:8,border:`1.5px solid ${isChecked?C.primary:C.border}`,fontSize:13,fontWeight:700,color:C.primary,background:isChecked?C.white:"#f9fafb",outline:"none",opacity:isChecked?1:0.5}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{marginTop:16,padding:"14px 18px",background:C.bg,borderRadius:12,border:`1.5px solid ${C.primaryMid}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                  <div>
                    <div style={{fontSize:12,color:C.muted}}>Total for {checkedStudents.length} students</div>
                    <div style={{fontWeight:900,fontSize:18,color:C.primary}}>{fmtPKR(totalBulk)}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>{bulkMonth} {bulkYear} · {bulkMethod}</div>
                  </div>
                  <button onClick={saveBulk} disabled={bulkSaving||!checkedStudents.length}
                    style={{background:bulkSaving||!checkedStudents.length?"#9ca3af":C.primary,color:C.white,border:"none",borderRadius:11,padding:"12px 28px",fontWeight:800,fontSize:14,cursor:bulkSaving||!checkedStudents.length?"not-allowed":"pointer",transition:"all .2s"}}>
                    {bulkSaving?`⏳ Saving ${checkedStudents.length}...`:`💾 Save ${checkedStudents.length} Payment${checkedStudents.length!==1?"s":""}`}
                  </button>
                </div>
              </div>
            )}

            {bulkClass && bulkStudents.length===0 && (
              <div style={{background:C.white,borderRadius:14,padding:"36px 24px",textAlign:"center",color:C.muted,boxShadow:`0 2px 8px ${C.shadow}`}}>
                <div style={{fontSize:36,marginBottom:8}}>🏫</div>
                <div style={{fontWeight:700}}>No students found in {bulkClass}</div>
              </div>
            )}

            {!bulkClass && (
              <div style={{background:C.white,borderRadius:14,padding:"36px 24px",textAlign:"center",color:C.muted,boxShadow:`0 2px 8px ${C.shadow}`}}>
                <div style={{fontSize:40,marginBottom:10}}>📦</div>
                <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:6}}>Select a class to begin bulk recording</div>
                <div style={{fontSize:12}}>Students appear with fee amounts pre-filled from the fee structure. You can edit individual amounts before saving.</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Custom Charges (Fines, Challans, Discipline) ── */}
      {activeTab==="custom"&&(()=>{
        const saveCharge = async () => {
          if(!chargeForm.student_id) return showToast("Please select a student",false);
          if(!chargeForm.amount||Number(chargeForm.amount)<=0) return showToast("Enter a valid amount",false);
          if(!chargeForm.reason.trim()) return showToast("Please enter a reason",false);
          setChargeSaving(true);
          const stu = students.find(s=>s.id===chargeForm.student_id);
          const receiptNo = "CHG-"+String(chargeForm.student_id).padStart(5,"0").slice(-5)+"-"+Date.now().toString().slice(-4);
          const row = { ...chargeForm, amount:Number(chargeForm.amount), student_name:stu?.name||stu?.full_name||"", class:stu?.class||stu?.["class"]||"", receipt_no:receiptNo, created_at:new Date().toISOString() };
          try {
            const {data,error} = await supabase.from("custom_charges").insert(row).select().single();
            if(error) throw error;
            setCustomCharges(p=>[{...row,id:data?.id||Date.now()},...p]);
            showToast("✅ Charge recorded — Receipt: "+receiptNo);
            setChargeForm(emptyCharge);
          } catch(e){
            setCustomCharges(p=>[{...row,id:Date.now()},...p]);
            showToast("⚠️ Saved locally (create custom_charges table in Supabase to persist)");
          }
          setChargeSaving(false);
        };

        const deleteCharge = async (id) => {
          if(!window.confirm("Delete this charge record?")) return;
          try { await supabase.from("custom_charges").delete().eq("id",id); } catch(e) {}
          setCustomCharges(p=>p.filter(c=>c.id!==id));
          showToast("Deleted");
        };

        const filtered = chargeFilter==="all" ? customCharges : customCharges.filter(c=>c.charge_type===chargeFilter);
        const totalFiltered = filtered.reduce((a,c)=>a+Number(c.amount||0),0);

        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* New charge form */}
            <div style={{background:C.white,borderRadius:16,padding:"22px 26px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>💸 Record Custom Charge</div>
              <div style={{fontSize:12,color:C.muted,marginBottom:18}}>Log one-off charges: late fines, bank challans, discipline penalties, exam fees, or any other custom amount.</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:14}}>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Student *</label>
                  <select value={chargeForm.student_id} onChange={e=>setChargeForm(p=>({...p,student_id:e.target.value}))}
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${chargeForm.student_id?C.primary:C.border}`,fontSize:14,background:C.white,outline:"none"}}>
                    <option value="">-- Select Student --</option>
                    {[...students].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(s=>(
                      <option key={s.id} value={s.id}>{s.name||s.full_name} — {s.class||"Hifz"}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Charge Type *</label>
                  <select value={chargeForm.charge_type} onChange={e=>setChargeForm(p=>({...p,charge_type:e.target.value}))}
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white,outline:"none"}}>
                    {CHARGE_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Amount (PKR) *</label>
                  <input type="number" value={chargeForm.amount} onChange={e=>setChargeForm(p=>({...p,amount:e.target.value}))} placeholder="e.g. 500"
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,outline:"none"}}/>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Date</label>
                  <input type="date" value={chargeForm.date} onChange={e=>setChargeForm(p=>({...p,date:e.target.value}))}
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,outline:"none"}}/>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Payment Method</label>
                  <select value={chargeForm.method} onChange={e=>setChargeForm(p=>({...p,method:e.target.value}))}
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,background:C.white,outline:"none"}}>
                    {["Cash","Bank Transfer","JazzCash","EasyPaisa","Cheque","Deducted"].map(m=><option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Recorded By</label>
                  <input type="text" value={chargeForm.recorded_by} onChange={e=>setChargeForm(p=>({...p,recorded_by:e.target.value}))} placeholder="Admin / Teacher name"
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,outline:"none"}}/>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:.5}}>Reason / Description *</label>
                  <input type="text" value={chargeForm.reason} onChange={e=>setChargeForm(p=>({...p,reason:e.target.value}))}
                    placeholder="e.g. Late payment fine — October 2025, Discipline penalty — fighting in class..."
                    style={{width:"100%",padding:"9px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,fontSize:13,outline:"none"}}/>
                </div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <button onClick={saveCharge} disabled={chargeSaving}
                  style={{padding:"11px 28px",borderRadius:11,border:"none",background:chargeSaving?"#9ca3af":C.primary,color:C.white,fontWeight:800,fontSize:14,cursor:chargeSaving?"not-allowed":"pointer",transition:"all .2s"}}>
                  {chargeSaving?"⏳ Saving...":"💾 Record Charge"}
                </button>
                <button onClick={()=>setChargeForm(emptyCharge)}
                  style={{padding:"11px 18px",borderRadius:11,border:`1px solid ${C.border}`,background:C.white,color:C.muted,fontWeight:600,fontSize:13,cursor:"pointer"}}>
                  Clear
                </button>
                <div style={{fontSize:11,color:C.muted}}>💡 Bank challans → "Bank Challan" · Discipline penalties → "Discipline Fine"</div>
              </div>
            </div>

            {/* History + filter */}
            <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{fontWeight:800,color:C.primary,fontSize:14}}>📋 Charge History</div>
                  {filtered.length>0&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>Total: {fmtPKR(totalFiltered)} · {filtered.length} record{filtered.length!==1?"s":""}</div>}
                </div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>setChargeFilter("all")}
                    style={{padding:"4px 12px",borderRadius:20,border:"none",fontSize:11,fontWeight:700,cursor:"pointer",background:chargeFilter==="all"?C.primary:C.primaryPale,color:chargeFilter==="all"?C.white:C.primary}}>
                    All
                  </button>
                  {CHARGE_TYPES.map(t=>(
                    <button key={t.id} onClick={()=>setChargeFilter(t.id)}
                      style={{padding:"4px 12px",borderRadius:20,border:"none",fontSize:11,fontWeight:700,cursor:"pointer",
                        background:chargeFilter===t.id?t.color:C.bg,color:chargeFilter===t.id?"white":C.text,transition:"all .15s"}}>
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {!chargesLoaded ? (
                <div style={{textAlign:"center",padding:40,color:C.muted}}>Loading...</div>
              ) : filtered.length===0 ? (
                <div style={{textAlign:"center",padding:"40px 24px",color:C.muted}}>
                  <div style={{fontSize:40,marginBottom:10}}>💸</div>
                  <div style={{fontWeight:700,color:C.text,marginBottom:4}}>No charges recorded yet</div>
                  <div style={{fontSize:12}}>Use the form above to log fines, challans, or other custom charges.</div>
                </div>
              ) : (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:C.primaryPale}}>
                        {["Receipt","Student","Class","Type","Amount","Date","Method","Reason",""].map(h=>(
                          <th key={h} style={{padding:"9px 12px",textAlign:"left",fontWeight:700,color:C.primary,fontSize:11,textTransform:"uppercase",letterSpacing:.5,whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c,i)=>{
                        const ct = CHARGE_TYPES.find(t=>t.id===c.charge_type)||CHARGE_TYPES[CHARGE_TYPES.length-1];
                        return (
                          <tr key={c.id||i} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.white:C.bg}}>
                            <td style={{padding:"9px 12px",fontWeight:700,color:C.primary,whiteSpace:"nowrap",fontSize:11}}>{c.receipt_no||"—"}</td>
                            <td style={{padding:"9px 12px",fontWeight:600,color:C.text}}>{c.student_name||"—"}</td>
                            <td style={{padding:"9px 12px",color:C.muted}}>{c.class||"—"}</td>
                            <td style={{padding:"9px 12px"}}>
                              <span style={{background:`${ct.color}18`,color:ct.color,borderRadius:7,padding:"2px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{ct.icon} {ct.label}</span>
                            </td>
                            <td style={{padding:"9px 12px",fontWeight:800,color:ct.color,whiteSpace:"nowrap"}}>{fmtPKR(c.amount)}</td>
                            <td style={{padding:"9px 12px",color:C.muted,whiteSpace:"nowrap"}}>{c.date||"—"}</td>
                            <td style={{padding:"9px 12px"}}><span style={{background:C.primaryPale,color:C.primary,borderRadius:6,padding:"2px 7px",fontSize:11,fontWeight:700}}>{c.method||"Cash"}</span></td>
                            <td style={{padding:"9px 12px",color:C.muted,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={c.reason}>{c.reason||"—"}</td>
                            <td style={{padding:"6px 8px"}}>
                              <button onClick={()=>deleteCharge(c.id)}
                                style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:7,padding:"4px 9px",fontSize:11,fontWeight:700,cursor:"pointer"}}>🗑</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{background:C.primaryPale,borderTop:`2px solid ${C.primaryMid}`}}>
                        <td colSpan={4} style={{padding:"9px 12px",fontWeight:800,color:C.primary}}>Total ({filtered.length})</td>
                        <td style={{padding:"9px 12px",fontWeight:900,color:C.primary}}>{fmtPKR(totalFiltered)}</td>
                        <td colSpan={4}/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* Toast */}
      {toast&&(
        <div style={{position:"fixed",bottom:24,right:24,background:toast.ok===false?C.rose:C.primary,color:C.white,padding:"12px 20px",borderRadius:12,fontWeight:700,fontSize:14,boxShadow:`0 4px 20px ${C.shadow}`,zIndex:999}} className="pop-in">
          {/^[\u2705\u274C\u26A0\uFE0F⏳💾]/.test(toast.msg) ? toast.msg : (toast.ok===false?"❌ ":"✅ ")+toast.msg}
        </div>
      )}
    </div>
  );
}

// ── REPORT CARD MODAL (Printable + PDF) ─────────────────────
function ReportCardModal({ student, exam, results, attendance, onClose, monthLabel, reportType }) {
  const S = useSettings();

  const GRADES=[{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}];
  const getGrade=(m,t)=>{const p=(m/t)*100;return GRADES.find(g=>p>=g.min)?.label||"F";};
  const gradeColor=g=>({"A+":"#1a6b3c","A":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");

  const isHifz = student.stream?.includes("Both") || student.stream?.includes("Hifz");
  const MONTHS_ORDER = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  // ── Build subject results depending on report type ──────────────
  let schoolResults = [];
  let hifzResults = [];
  let rcTitle = "";

  // Robust student match: by id OR by name (handles both UUID and custom IDs)
  const matchStudent = (r) =>
    r.student_id === student.id ||
    r.student_id === student.id?.toString() ||
    r.student_name?.toLowerCase().trim() === student.name?.toLowerCase().trim();

  const HIFZ_SUBS = ["Tajweed","Memorization","Revision","Nazra","Manzil","Hifz","Makharij"];
  const isHifzSub = (sub) => HIFZ_SUBS.some(h => sub?.toLowerCase().includes(h.toLowerCase()));

  // Deduplicate by subject — keep highest score if duplicate
  const dedup = (arr) => {
    const map = {};
    arr.forEach(r => {
      if (!map[r.subject] || r.marks_obtained > map[r.subject].marks_obtained) map[r.subject] = r;
    });
    return Object.values(map);
  };

  if (reportType === "monthly" && monthLabel) {
    const monthResults = results.filter(r =>
      matchStudent(r) &&
      r.exam_name?.toLowerCase().includes("monthly") &&
      r.exam_name?.toLowerCase().includes(monthLabel.toLowerCase())
    );
    schoolResults = dedup(monthResults.filter(r => !isHifzSub(r.subject)));
    hifzResults   = dedup(monthResults.filter(r =>  isHifzSub(r.subject)));
    rcTitle = `Monthly Test — ${monthLabel}`;

  } else if (reportType === "midterm" || reportType === "final") {
    const allRes = results.filter(r =>
      matchStudent(r) &&
      r.exam_name?.toLowerCase().includes(reportType)
    );
    // Deduplicate by subject for clean per-subject breakdown
    schoolResults = dedup(allRes.filter(r => !isHifzSub(r.subject)));
    hifzResults   = dedup(allRes.filter(r =>  isHifzSub(r.subject)));
    rcTitle = reportType === "midterm" ? "Midterm Exam Report" : "Final Exam Report";

  } else if (reportType === "comprehensive") {
    const allMonthly = results.filter(r =>
      matchStudent(r) &&
      r.exam_name?.toLowerCase().includes("monthly")
    );
    schoolResults = allMonthly.filter(r => !isHifzSub(r.subject));
    hifzResults   = allMonthly.filter(r =>  isHifzSub(r.subject));
    rcTitle = "Comprehensive Academic Report";

  } else {
    const allRes = exam
      ? results.filter(r => matchStudent(r) && r.exam_id===exam.id)
      : results.filter(r => matchStudent(r));
    schoolResults = allRes;
    rcTitle = exam ? exam.name : "Full Academic Report";
  }

  // ── Comprehensive mode: pivot subjects × months ─────────────────
  const buildComprehensiveTable = (resArr) => {
    const subjects = [...new Set(resArr.map(r => r.subject))];
    const months   = [...new Set(resArr.map(r => {
      const m = MONTHS_ORDER.find(mo => r.exam_name?.includes(mo));
      return m || "Unknown";
    }))].sort((a,b) => MONTHS_ORDER.indexOf(a) - MONTHS_ORDER.indexOf(b));
    const pivot = {};
    subjects.forEach(s => {
      pivot[s] = {};
      months.forEach(m => { pivot[s][m] = null; });
    });
    resArr.forEach(r => {
      const m = MONTHS_ORDER.find(mo => r.exam_name?.includes(mo)) || "Unknown";
      if (pivot[r.subject]) pivot[r.subject][m] = r;
    });
    return { subjects, months, pivot };
  };

  const attDates = Object.keys(attendance||{});
  const present  = attDates.filter(d=>(attendance||{})[d]?.[student.id]==="P").length;
  const absent   = attDates.filter(d=>(attendance||{})[d]?.[student.id]==="A").length;
  const attPct   = attDates.length ? Math.round((present/attDates.length)*100) : 0;

  const allResultsForAvg = [...schoolResults, ...hifzResults];
  const overallAvg = allResultsForAvg.length
    ? Math.round(allResultsForAvg.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/allResultsForAvg.length)
    : null;

  const openPrintWindow = () => {
    const el = document.getElementById("rc-print-area");
    const w = window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Report Card — ${student.name}</title>
    <link href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&family=Plus+Jakarta+Sans:wght@400;600;700;800;900&display=swap" rel="stylesheet">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      html,body{width:210mm;font-family:"Plus Jakarta Sans",sans-serif;background:#fff;color:#1a1a1a;font-size:11px;}
      table{border-collapse:collapse;width:100%;}
      th{background:#1a6b3c;color:white;padding:6px 9px;text-align:left;font-size:10px;font-weight:700;}
      td{border:1px solid #e5e7eb;padding:6px 9px;font-size:10px;}
      tr:nth-child(even) td{background:#f9fffe;}
      .hifz-th{background:#7c3aed!important;}
      img{max-width:60px!important;max-height:60px!important;}
      /* Shrink padding on all card sections */
      [style*="padding:20px"],[style*="padding: 20px"]{padding:10px!important;}
      [style*="padding:14px"],[style*="padding: 14px"]{padding:8px!important;}
      [style*="marginBottom:18"]{margin-bottom:10px!important;}
      [style*="marginBottom:16"]{margin-bottom:8px!important;}
      [style*="marginBottom:14"]{margin-bottom:6px!important;}
      [style*="fontSize:16"]{font-size:13px!important;}
      [style*="fontSize:14"]{font-size:11px!important;}
      [style*="fontSize:13"]{font-size:10px!important;}
      [style*="fontSize:30"],[style*="fontSize:36"]{font-size:20px!important;}
      @page{size:A4;margin:8mm;}
      @media print{
        html,body{width:auto;}
        body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
        .no-print{display:none!important;}
      }
    </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    return w;
  };

  const handlePrint = () => {
    const w = openPrintWindow();
    setTimeout(()=>{w.focus();w.print();},600);
  };

  const handlePDF = () => {
    const w = openPrintWindow();
    setTimeout(()=>{
      w.focus();
      // Trigger browser's Save as PDF via print dialog
      w.print();
      // Instruct user — browser print dialog has "Save as PDF" option
    },600);
  };

  // ── Inline editing state ───────────────────────────────────────
  // Reset inline mode every time the report card opens
  const [inlineEdits, setInlineEdits] = React.useState({});
  const [inlineMode, setInlineMode]   = React.useState(false);
  React.useEffect(()=>{ setInlineMode(false); setInlineEdits({}); setComment(""); setRemarks(""); },[student,reportType,monthLabel]);
  const [rcSaving, setRcSaving]       = React.useState(false);
  const [rcToast,  setRcToast]        = React.useState(null);
  const [comment,  setComment]        = React.useState("");
  const [remarks,  setRemarks]        = React.useState("");
  const [publishing, setPublishing]   = React.useState(false);

  const showRcToast = (msg,ok=true) => { setRcToast({msg,ok}); setTimeout(()=>setRcToast(null),3000); };

  const publishCard = async () => {
    setPublishing(true);
    try {
      const sRes = [...schoolResults,...hifzResults].filter(r=>!isHifzSub(r.subject));
      const hRes = [...schoolResults,...hifzResults].filter(r=> isHifzSub(r.subject));
      const sTotal = sRes.reduce((s,r)=>s+r.marks_obtained,0);
      const sMax   = sRes.reduce((s,r)=>s+r.total_marks,0);
      const hTotal = hRes.reduce((s,r)=>s+r.marks_obtained,0);
      const hMax   = hRes.reduce((s,r)=>s+r.total_marks,0);
      // Auto-attach Hifz progress summary
      let hifzSummary = null;
      const isHifzStu = (student.stream||"").includes("Hifz")||(student.stream||"").includes("Both");
      if(isHifzStu) {
        const sid = String(student.id);
        const [progRes, logsRes] = await Promise.all([
          supabase.from("hifz_progress").select("*").eq("student_id",sid).maybeSingle(),
          supabase.from("hifz_daily_log").select("*").eq("student_id",sid).order("date",{ascending:false}).limit(35),
        ]);
        if(progRes.error) console.warn("hifz_progress query error:", progRes.error);
        if(logsRes.error) console.warn("hifz_daily_log query error:", logsRes.error);
        const prog = progRes.data||null;
        const recentLogs = logsRes.data||[];
        const now2=new Date();
        const mStart=new Date(now2.getFullYear(),now2.getMonth(),1);
        const targetMonth=monthLabel
          ? new Date(now2.getFullYear(),["January","February","March","April","May","June","July","August","September","October","November","December"].indexOf(monthLabel),1)
          : mStart;
        const targetMonthEnd=new Date(targetMonth.getFullYear(),targetMonth.getMonth()+1,0);
        const mLogs=recentLogs.filter(l=>new Date(l.date)>=targetMonth&&new Date(l.date)<=targetMonthEnd);
        const sumP2=(arr,k)=>Math.round(arr.reduce((s,l)=>s+(Number(l[k])||0),0)*10)/10;
        hifzSummary={
          current_juz:prog?.current_juz||0, current_surah:prog?.current_surah||"",
          month_sabaq_pages:sumP2(mLogs,"sabaq_pages"), month_sabqi_pages:sumP2(mLogs,"sabqi_pages"),
          month_manzil_pages:sumP2(mLogs,"manzil_pages"),
          month_sessions:mLogs.filter(l=>l.was_present!==false).length,
          month_absent:mLogs.filter(l=>l.was_present===false).length,
          juz_name:QURAN_JUZ.find(j=>j.n===(prog?.current_juz||1))?.name||"",
          juz_ar:QURAN_JUZ.find(j=>j.n===(prog?.current_juz||1))?.ar||"",
        };
      }
      const {error} = await supabase.from("published_report_cards").insert({
        student_id:String(student.id), student_name:student.name||student.full_name,
        class:student.class||student["class"], stream:student.stream,
        exam_type:reportType||"general", exam_month:monthLabel||"",
        school_results:sRes, hifz_results:hRes,
        school_total:sTotal, school_max:sMax,
        hifz_total:hTotal, hifz_max:hMax,
        teacher_comment:comment, remarks, hifz_summary:hifzSummary,
        published_at:new Date().toISOString()
      });
      if(error) { showRcToast("Error: "+error.message+" | "+(error.details||"")+" | "+(error.hint||""), false); }
      else showRcToast("Published to parent portal!"+(isHifzStu?" (with Hifz summary)":""));
    } catch(e) {
      console.error("publishCard caught exception:", e);
      showRcToast("Error: "+e.message, false);
    }
    setPublishing(false);
  };

  const saveInlineEdits = async () => {
    if(!Object.keys(inlineEdits).length) { setInlineMode(false); return; }
    setRcSaving(true);
    for(const [rid, changes] of Object.entries(inlineEdits)) {
      const mo = Number(changes.marks_obtained);
      const tm = Number(changes.total_marks);
      const g  = getGrade(mo, tm);
      await supabase.from("results").update({
        marks_obtained:mo, total_marks:tm,
        subject:changes.subject, grade:g
      }).eq("id", rid);
    }
    setRcSaving(false); setInlineEdits({}); setInlineMode(false);
    showRcToast("✅ Results saved! Reopen report card to see updated data.");
  };

  // Totals for midterm/final professional summary row
  const calcTotals = (rows) => ({
    obtained: rows.reduce((s,r)=>s+Number((inlineEdits[r.id]?.marks_obtained??r.marks_obtained)),0),
    total:    rows.reduce((s,r)=>s+Number((inlineEdits[r.id]?.total_marks??r.total_marks)),0),
  });

  // Render a clean subject marks table with TOTAL row (monthly/midterm/final)
  const renderSubjectTable = (rows, heading, headingColor="#1a6b3c", headingBg="#f0fdf7") => {
    if(!rows.length) return null;
    const totals = calcTotals(rows);
    const totalPct = totals.total > 0 ? Math.round((totals.obtained/totals.total)*100) : 0;
    const totalG = getGrade(totalPct,100);
    return (
      <div style={{marginBottom:18}}>
        <div style={{background:headingBg,border:`1.5px solid ${headingColor}30`,borderRadius:10,padding:"9px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>{headingColor==="#7c3aed"?"📖":"📝"}</span>
          <span style={{fontWeight:800,color:headingColor,fontSize:13}}>{heading}</span>
          <span style={{marginLeft:"auto",fontSize:11,color:"#6b7280"}}>{rows.length} subject{rows.length!==1?"s":""}</span>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr>
              {["#","Subject","Obtained","Total","Percentage","Grade","Remarks"].map(h=>(
                <th key={h} style={{padding:"9px 12px",background:headingColor,color:"white",textAlign:"left",fontSize:11,fontWeight:700,border:`1px solid ${headingColor}`}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r,i)=>{
              const edit = inlineEdits[r.id]||{};
              const mo   = Number(edit.marks_obtained??r.marks_obtained);
              const tm   = Number(edit.total_marks??r.total_marks);
              const sub  = edit.subject??r.subject;
              const pct  = tm>0?Math.round((mo/tm)*100):0;
              const g    = r.grade||getGrade(mo,tm);
              const remark=pct>=90?"Excellent":pct>=80?"Very Good":pct>=70?"Good":pct>=60?"Satisfactory":pct>=50?"Average":pct>=40?"Below Average":"Needs Improvement";
              const setEdit = (k,v) => setInlineEdits(p=>({...p,[r.id]:{...p[r.id],marks_obtained:mo,total_marks:tm,subject:sub,[k]:v}}));
              return (
                <tr key={i}>
                  <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",color:"#6b7280",fontSize:11}}>{i+1}</td>
                  <td style={{padding:"7px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",fontWeight:700}}>
                    {inlineMode
                      ? <input value={sub} onChange={e=>setEdit("subject",e.target.value)} style={{border:"1.5px solid #d1fae5",borderRadius:6,padding:"4px 8px",fontSize:12,fontWeight:700,width:"100%"}}/>
                      : sub}
                  </td>
                  <td style={{padding:"7px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",fontWeight:800,color:headingColor,fontSize:14}}>
                    {inlineMode
                      ? <input type="number" value={mo} min="0" max={tm} onChange={e=>setEdit("marks_obtained",e.target.value)} style={{border:"1.5px solid #d1fae5",borderRadius:6,padding:"4px 8px",fontSize:13,fontWeight:700,width:60,textAlign:"center"}}/>
                      : mo}
                  </td>
                  <td style={{padding:"7px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",color:"#6b7280"}}>
                    {inlineMode
                      ? <input type="number" value={tm} min="1" onChange={e=>setEdit("total_marks",e.target.value)} style={{border:"1.5px solid #d1fae5",borderRadius:6,padding:"4px 8px",fontSize:13,width:60,textAlign:"center"}}/>
                      : tm}
                  </td>
                  <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe"}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:55,height:6,background:"#e5e7eb",borderRadius:4}}>
                        <div style={{height:6,borderRadius:4,background:gradeColor(g),width:`${Math.min(pct,100)}%`}}></div>
                      </div>
                      <span style={{fontWeight:700,fontSize:11,color:gradeColor(g),minWidth:32}}>{pct}%</span>
                    </div>
                  </td>
                  <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe"}}>
                    <span style={{fontWeight:800,color:gradeColor(g),background:`${gradeColor(g)}18`,borderRadius:6,padding:"2px 10px",fontSize:12}}>{g}</span>
                  </td>
                  <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",color:"#6b7280",fontSize:11}}>{remark}</td>
                </tr>
              );
            })}
            {/* TOTAL ROW */}
            <tr style={{background:`${headingColor}10`,borderTop:`2px solid ${headingColor}`}}>
              <td colSpan={2} style={{padding:"10px 12px",border:`1px solid ${headingColor}40`,fontWeight:800,fontSize:12,color:headingColor}}>TOTAL</td>
              <td style={{padding:"10px 12px",border:`1px solid ${headingColor}40`,fontWeight:900,fontSize:15,color:headingColor}}>{totals.obtained}</td>
              <td style={{padding:"10px 12px",border:`1px solid ${headingColor}40`,fontWeight:700,color:"#374151"}}>{totals.total}</td>
              <td style={{padding:"10px 12px",border:`1px solid ${headingColor}40`}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <div style={{width:55,height:7,background:"#e5e7eb",borderRadius:4}}>
                    <div style={{height:7,borderRadius:4,background:gradeColor(totalG),width:`${totalPct}%`}}></div>
                  </div>
                  <span style={{fontWeight:800,fontSize:12,color:gradeColor(totalG)}}>{totalPct}%</span>
                </div>
              </td>
              <td style={{padding:"10px 12px",border:`1px solid ${headingColor}40`}}>
                <span style={{fontWeight:900,color:gradeColor(totalG),background:`${gradeColor(totalG)}20`,borderRadius:6,padding:"3px 12px",fontSize:13}}>{totalG}</span>
              </td>
              <td style={{padding:"10px 12px",border:`1px solid ${headingColor}40`,fontWeight:700,color:totalPct>=50?"#1a6b3c":"#e11d48",fontSize:12}}>
                {totalPct>=50?"✅ Pass":"❌ Fail"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render comprehensive pivot table (subjects × months)
  const renderComprehensiveTable = (rows, heading, headingColor="#1a6b3c", headingBg="#f0fdf7") => {
    if(!rows.length) return null;
    const {subjects, months, pivot} = buildComprehensiveTable(rows);
    if(!subjects.length || !months.length) return null;
    return (
      <div style={{marginBottom:18}}>
        <div style={{background:headingBg,border:`1.5px solid ${headingColor}30`,borderRadius:10,padding:"9px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>{headingColor==="#7c3aed"?"📖":"📝"}</span>
          <span style={{fontWeight:800,color:headingColor,fontSize:13}}>{heading}</span>
          <span style={{fontSize:11,color:"#6b7280",marginLeft:"auto"}}>Scores per month</span>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:400}}>
            <thead>
              <tr>
                <th style={{padding:"9px 12px",background:headingColor,color:"white",textAlign:"left",fontSize:11,fontWeight:700,border:`1px solid ${headingColor}`,minWidth:120}}>Subject</th>
                {months.map(m=>(
                  <th key={m} style={{padding:"9px 12px",background:headingColor,color:"white",textAlign:"center",fontSize:11,fontWeight:700,border:`1px solid ${headingColor}`,minWidth:70}}>{m.slice(0,3)}</th>
                ))}
                <th style={{padding:"9px 12px",background:headingColor,color:"white",textAlign:"center",fontSize:11,fontWeight:700,border:`1px solid ${headingColor}`,minWidth:70}}>Avg</th>
              </tr>
            </thead>
            <tbody>
              {subjects.map((sub,i)=>{
                const subRows = months.map(m => pivot[sub][m]);
                const scored  = subRows.filter(Boolean);
                const avg     = scored.length ? Math.round(scored.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/scored.length) : null;
                const avgG    = avg !== null ? getGrade(avg,100) : null;
                return (
                  <tr key={sub}>
                    <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",fontWeight:700}}>{sub}</td>
                    {months.map(m=>{
                      const r = pivot[sub][m];
                      if(!r) return <td key={m} style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",textAlign:"center",color:"#9ca3af",fontSize:11}}>—</td>;
                      const pct=Math.round((r.marks_obtained/r.total_marks)*100);
                      const g=r.grade||getGrade(r.marks_obtained,r.total_marks);
                      return (
                        <td key={m} style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"white":"#f9fffe",textAlign:"center"}}>
                          <div style={{fontWeight:800,color:gradeColor(g),fontSize:13}}>{r.marks_obtained}<span style={{fontSize:9,color:"#9ca3af"}}>/{r.total_marks}</span></div>
                          <div style={{fontSize:10,color:gradeColor(g),fontWeight:700}}>{g}</div>
                        </td>
                      );
                    })}
                    <td style={{padding:"9px 12px",border:"1px solid #e5e7eb",background:i%2===0?"#f0fdf7":"#e8f5ee",textAlign:"center"}}>
                      {avg!==null ? (
                        <div>
                          <div style={{fontWeight:900,color:gradeColor(avgG),fontSize:14}}>{avg}%</div>
                          <div style={{fontSize:10,fontWeight:700,color:gradeColor(avgG)}}>{avgG}</div>
                        </div>
                      ) : <span style={{color:"#9ca3af",fontSize:11}}>—</span>}
                    </td>
                  </tr>
                );
              })}
              {/* TOTAL ROW for comprehensive */}
              {(() => {
                const allScored = subjects.flatMap(sub=>months.map(m=>pivot[sub][m]).filter(Boolean));
                if(!allScored.length) return null;
                const tObt=allScored.reduce((s,r)=>s+Number(r.marks_obtained),0);
                const tMax=allScored.reduce((s,r)=>s+Number(r.total_marks),0);
                const tPct=tMax>0?Math.round((tObt/tMax)*100):0;
                const tG=getGrade(tPct,100);
                const gc=gradeColor(tG);
                return (
                  <tr style={{background:`${headingColor}10`,borderTop:`2px solid ${headingColor}`}}>
                    <td style={{padding:"9px 12px",border:`1px solid ${headingColor}40`,fontWeight:800,fontSize:12,color:headingColor}}>TOTAL</td>
                    {months.map(m=>{
                      const mScored=subjects.map(s=>pivot[s][m]).filter(Boolean);
                      if(!mScored.length) return <td key={m} style={{padding:"9px 12px",border:`1px solid ${headingColor}40`,textAlign:"center",color:"#9ca3af",fontSize:11}}>—</td>;
                      const mObt=mScored.reduce((s,r)=>s+Number(r.marks_obtained),0);
                      const mMax=mScored.reduce((s,r)=>s+Number(r.total_marks),0);
                      const mPct=mMax>0?Math.round((mObt/mMax)*100):0;
                      const mG=getGrade(mPct,100);
                      const mgc=gradeColor(mG);
                      return (
                        <td key={m} style={{padding:"9px 12px",border:`1px solid ${headingColor}40`,textAlign:"center"}}>
                          <div style={{fontWeight:800,color:mgc,fontSize:12}}>{mObt}<span style={{fontSize:9,color:"#9ca3af"}}>/{mMax}</span></div>
                          <div style={{fontSize:10,color:mgc,fontWeight:700}}>{mPct}%</div>
                        </td>
                      );
                    })}
                    <td style={{padding:"9px 12px",border:`1px solid ${headingColor}40`,textAlign:"center",background:`${headingColor}18`}}>
                      <div style={{fontWeight:900,color:gc,fontSize:14}}>{tPct}%</div>
                      <div style={{fontSize:11,fontWeight:800,color:gc}}>{tG}</div>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!student) return null;

  return (
    <Portal>
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",backdropFilter:"blur(4px)",overflowY:"auto"}} onClick={onClose}>
      <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:820,maxHeight:"calc(100vh - 48px)",overflowY:"auto",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}} onClick={e=>e.stopPropagation()}>
        {/* Toolbar */}
        <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d9488)",padding:"12px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{color:"white",fontWeight:800,fontSize:14}}>📋 Report Card</div>
            <div style={{color:"rgba(255,255,255,0.75)",fontSize:11,marginTop:1}}>{rcTitle}</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {!inlineMode && <button onClick={handlePrint} style={{background:"#f0a500",color:"white",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>🖨️ Print</button>}
            {!inlineMode && <button onClick={handlePDF} style={{background:"#e11d48",color:"white",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}} title="Save as PDF">📄 PDF</button>}
            {!inlineMode && <button onClick={publishCard} disabled={publishing} style={{background:"#7c3aed",color:"white",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>{publishing?"Publishing...":"📤 Publish"}</button>}
            {!inlineMode && <button onClick={()=>setInlineMode(true)} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.4)",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>✏️ Edit Marks</button>}
            {inlineMode && <button onClick={saveInlineEdits} disabled={rcSaving} style={{background:"#22c55e",color:"white",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:rcSaving?"not-allowed":"pointer"}}>{rcSaving?"Saving...":"💾 Save"}</button>}
            {inlineMode && <button onClick={()=>{setInlineMode(false);setInlineEdits({});}} style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.4)",borderRadius:8,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer"}}>✕ Cancel</button>}
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"none",borderRadius:8,width:32,height:32,fontSize:18,cursor:"pointer"}}>×</button>
          </div>
        </div>

        {/* Scrollable Print Area */}
        <div style={{overflowY:"auto",maxHeight:"calc(90vh - 60px)"}} id="rc-print-area">
          {/* Inline edit mode banner */}
          {inlineMode && (
            <div style={{background:"#ecfdf5",borderBottom:"2px solid #22c55e",padding:"9px 20px",display:"flex",alignItems:"center",gap:10,fontSize:12,color:"#166534",fontWeight:600,flexShrink:0}}>
              <span>✏️ <strong>Edit Mode</strong> — click any subject name, obtained marks, or total marks to change them. Press Save when done.</span>
              {rcToast && <span style={{marginLeft:"auto",color:rcToast.ok?"#166534":"#e11d48"}}>{rcToast.msg}</span>}
            </div>
          )}
          {/* Academy Header */}
          <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",borderBottom:"4px solid #f0a500"}}>
            <div style={{display:"flex",alignItems:"center",padding:"14px 24px",gap:16}}>
              <img src={SCHOOL_LOGO} alt="Logo" style={{width:75,height:75,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 2px 6px rgba(0,0,0,0.3))"}}/> 
              <div style={{flex:1}}>
                <div style={{fontSize:16,fontWeight:900,color:"white",letterSpacing:.3}}>{(S.school_name_en||DEFAULT_SETTINGS.school_name_en).toUpperCase()}</div>
                <div style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.85)",marginTop:2}}>Modern Islamic School System</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.7)",marginTop:1}}>{S.affiliation||DEFAULT_SETTINGS.affiliation}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}>{S.address||DEFAULT_SETTINGS.address} &nbsp;|&nbsp; Reg. No: {S.reg_no||DEFAULT_SETTINGS.reg_no}</div>
              </div>
              <div style={{textAlign:"right",fontFamily:"Noto Nastaliq Urdu,serif"}}>
                <div style={{fontSize:22,fontWeight:900,color:"white",lineHeight:1.4}}>{S.school_name_ur||DEFAULT_SETTINGS.school_name_ur}</div>
                <div style={{fontSize:12,fontWeight:700,color:"#ffd166",marginTop:2}}>جدید اسلامی تعلیمی نظام</div>
              </div>
            </div>
            <div style={{height:3,background:"linear-gradient(90deg,#2563eb,#0d9488,#2563eb)"}}></div>
            <div style={{background:"rgba(0,0,0,0.18)",padding:"8px 24px",textAlign:"center",fontSize:13,fontWeight:800,color:"#ffd166",letterSpacing:.5}}>
              {rcTitle.toUpperCase()} — SESSION {S.academic_year||DEFAULT_SETTINGS.academic_year}
            </div>
          </div>

          <div style={{padding:"20px 24px"}}>
            {/* Student Info */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18,background:"#f0fdf7",borderRadius:12,padding:16,border:"1px solid #bbf7d0"}}>
              {[["Student Name",student.name],["Father's Name",student.father],["Class / Section",student.class||student["class"]],["Stream",student.stream],["Date of Birth",student.dob],["Roll / ID",student.id]].map(([l,v])=>(
                <div key={l} style={{display:"flex",gap:8,fontSize:12}}>
                  <span style={{color:"#6b7280",fontWeight:600,minWidth:110,flexShrink:0}}>{l}:</span>
                  <span style={{fontWeight:700,color:"#1a1a1a"}}>{v||"—"}</span>
                </div>
              ))}
            </div>

            {/* Attendance */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:18}}>
              {[{l:"Present",v:present,c:"#1a6b3c",bg:"#f0fdf7"},{l:"Absent",v:absent,c:"#e11d48",bg:"#fff0f3"},{l:"Total Days",v:attDates.length,c:"#374151",bg:"#f9fafb"},{l:"Attendance",v:`${attPct}%`,c:attPct>=85?"#1a6b3c":attPct>=70?"#d97706":"#e11d48",bg:attPct>=85?"#f0fdf7":attPct>=70?"#fffbeb":"#fff0f3"}].map(x=>(
                <div key={x.l} style={{background:x.bg,borderRadius:10,padding:"11px 14px",textAlign:"center",border:`1px solid ${x.c}20`}}>
                  <div style={{fontSize:20,fontWeight:900,color:x.c}}>{x.v}</div>
                  <div style={{fontSize:10,color:"#6b7280",fontWeight:600,marginTop:2}}>{x.l}</div>
                </div>
              ))}
            </div>

            {/* Overall Banner */}
            {overallAvg!==null && (
              <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d9488)",borderRadius:12,padding:"14px 20px",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div><div style={{color:"rgba(255,255,255,0.75)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Overall Average</div><div style={{color:"white",fontSize:30,fontWeight:900,lineHeight:1}}>{overallAvg}%</div></div>
                <div style={{textAlign:"right"}}><div style={{color:"rgba(255,255,255,0.75)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Overall Grade</div><div style={{color:"#ffd166",fontSize:36,fontWeight:900,lineHeight:1}}>{getGrade(overallAvg,100)}</div></div>
                <div style={{textAlign:"center"}}><div style={{color:"rgba(255,255,255,0.75)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>Status</div><div style={{color:overallAvg>=50?"#86efac":"#fca5a5",fontSize:16,fontWeight:900,marginTop:2}}>{overallAvg>=50?"✅ PASS":"❌ FAIL"}</div></div>
              </div>
            )}

            {/* MARKS — School Section */}
            {reportType==="comprehensive"
              ? renderComprehensiveTable(schoolResults,"📚 School Section — Monthly Scores by Subject","#1a6b3c","#f0fdf7")
              : renderSubjectTable(schoolResults,"📚 School Section","#1a6b3c","#f0fdf7")}

            {/* MARKS — Hifz Section (only for Hifz+School students) */}
            {isHifz && (
              reportType==="comprehensive"
                ? renderComprehensiveTable(hifzResults,"📖 Hifz Section — Monthly Scores","#7c3aed","#f5f3ff")
                : renderSubjectTable(hifzResults,"📖 Hifz Section (Tajweed · Memorization · Revision)","#7c3aed","#f5f3ff")
            )}

            {/* Empty state */}
            {schoolResults.length===0 && hifzResults.length===0 && (
              <div style={{textAlign:"center",padding:32,color:"#6b7280",background:"#f9fafb",borderRadius:12,fontSize:13}}>No marks recorded for this period yet.</div>
            )}

            {/* Signature Row */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginTop:24,paddingTop:16,borderTop:"2px dashed #e5e7eb"}}>
              {["Class Teacher","Principal","Parent / Guardian"].map(r=>(
                <div key={r} style={{textAlign:"center"}}>
                  <div style={{height:40,borderBottom:"1px solid #374151",marginBottom:6}}></div>
                  <div style={{fontSize:11,color:"#6b7280",fontWeight:600}}>{r}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:10,color:"#9ca3af"}}>Generated: {new Date().toLocaleDateString("en-PK",{day:"2-digit",month:"long",year:"numeric"})}</div>
              <div style={{fontSize:11,fontWeight:700,color:"#1a6b3c"}}>{S.school_name_en||DEFAULT_SETTINGS.school_name_en} · {S.address||DEFAULT_SETTINGS.address}</div>
            </div>
          </div>

          {/* Comment & Publish Section — no-print */}
          <div className="no-print" style={{padding:"16px 20px",borderTop:"2px dashed #e5e7eb",background:"#f8fafc"}}>
            {rcToast && <div style={{background:rcToast.ok?"#dcfce7":"#fef2f2",color:rcToast.ok?"#16a34a":"#e11d48",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600,marginBottom:12}}>{rcToast.msg}</div>}
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:10}}>Add Comments before Publishing</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>TEACHER COMMENT</label>
                {(()=>{
                const [rc, setRc] = React.useState(null);
                React.useEffect(()=>{
                  if(!student?.id) return;
                  supabase.from("student_conduct").select("type")
                    .eq("student_id",String(student.id))
                    .then(({data})=>setRc(data||[]));
                },[student?.id]);
                if(!rc||rc.length===0) return null;
                const comms=rc.filter(r=>r.type==="commendation").length;
                const warns=rc.filter(r=>r.type==="warning"||r.type==="incident").length;
                const det=rc.filter(r=>r.type==="detention"||r.type==="suspension").length;
                const summary = comms>0&&warns===0&&det===0
                  ? `Conduct: Excellent — ${comms} commendation${comms>1?"s":""}.`
                  : det>0 ? `Conduct: Needs improvement — ${det} serious incident${det>1?"s":""}.`
                  : warns>0 ? `Conduct: Fair — ${warns} warning${warns>1?"s":""}.`
                  : "Conduct: Satisfactory.";
                return (
                  <div style={{marginBottom:8,background:"#f0f7f2",borderRadius:8,padding:"7px 10px",
                    display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11}}>
                    <span style={{color:"#1a6b3c",fontWeight:700}}>📋 Conduct Summary: {summary}</span>
                    <button onClick={()=>setComment(p=>p?(p+" | "+summary):summary)}

                      style={{background:"#1a6b3c",color:"white",border:"none",borderRadius:6,
                        padding:"3px 10px",fontSize:10,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                      Insert
                    </button>
                  </div>
                );
              })()}
              <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={2}
                  placeholder="e.g. Student has shown great improvement this term..."
                  style={{width:"100%",border:"1.5px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:13,resize:"none",boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>REMARKS / CONDUCT</label>
                <textarea value={remarks} onChange={e=>setRemarks(e.target.value)} rows={2}
                  placeholder="e.g. Good conduct. Needs to improve punctuality."
                  style={{width:"100%",border:"1.5px solid #d1d5db",borderRadius:8,padding:"8px 10px",fontSize:13,resize:"none",boxSizing:"border-box"}}/>
              </div>
            </div>
            <button onClick={publishCard} disabled={publishing}
              style={{background:"#7c3aed",color:"white",border:"none",borderRadius:9,padding:"10px 24px",fontWeight:700,fontSize:13,cursor:"pointer",opacity:publishing?0.7:1}}>
              {publishing?"Publishing...":"📤 Publish to Parent Portal"}
            </button>
          </div>

        </div>
      </div>
    </div>
    </Portal>
  );
}

// ── SUBJECT MANAGEMENT ──────────────────────────────────────────
// ── SUBJECT MANAGEMENT ──────────────────────────────────────────
function SubjectManagement({ onSubjectsChange = null }) {
  const [subjects, setSubjects] = React.useState([]);
  const updSubjects = fn => setSubjects(fn);
  const [saving,   setSaving]   = React.useState(false);
  const [toast,    setToast]    = React.useState(null);
  const [editSub,  setEditSub]  = React.useState(null);
  const [confirmDel, setConfirmDel] = React.useState(null);
  const [newSchool,   setNewSchool]   = React.useState({name:"",default_marks:"100"});
  const [newHifz,     setNewHifz]     = React.useState({name:"",default_marks:"50"});
  const [newHifzComp, setNewHifzComp] = React.useState({name:"",default_marks:"10"});

  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  React.useEffect(()=>{
    supabase.from("subjects").select("*").order("type").order("sort_order").order("name")
      .then(({data})=>updSubjects(data||[]));
  },[]);

  const schoolSubs    = subjects.filter(s=>s.type==="School");
  const hifzSubs      = subjects.filter(s=>s.type==="Hifz");
  const hifzCompSubs  = subjects.filter(s=>s.type==="HifzComponent");

  const schoolInputRef   = React.useRef(null);
  const hifzInputRef     = React.useRef(null);
  const hifzCompInputRef = React.useRef(null);

  const addSubject = async (type) => {
    const form    = type==="School"?newSchool:type==="Hifz"?newHifz:newHifzComp;
    const setForm = type==="School"?setNewSchool:type==="Hifz"?setNewHifz:setNewHifzComp;
    const ref     = type==="School"?schoolInputRef:type==="Hifz"?hifzInputRef:hifzCompInputRef;
    if(!form.name.trim()) return;
    if(subjects.find(s=>s.name.toLowerCase()===form.name.toLowerCase()&&s.type===type))
      return showToast("Subject already exists",false);
    setSaving(true);
    const {data,error} = await supabase.from("subjects").insert([{
      name:form.name.trim(), type, default_marks:Number(form.default_marks)||100,
      sort_order:subjects.filter(s=>s.type===type).length+1, is_active:true
    }]).select();
    if(error) showToast("Error: "+error.message,false);
    else {
      updSubjects(p=>[...p,data[0]]);
      setForm({name:"",default_marks:type==="School"?"100":type==="Hifz"?"50":"10"});
      showToast(""+data[0].name+" added!");
      // Refocus the input so you can keep typing
      setTimeout(()=>ref.current?.focus(), 50);
    }
    setSaving(false);
  };

  const updateSubject = async () => {
    if(!editSub||!editSub.name.trim()) return showToast("Name required",false);
    setSaving(true);
    const {error} = await supabase.from("subjects").update({
      name:editSub.name.trim(), default_marks:Number(editSub.default_marks)||100, is_active:editSub.is_active
    }).eq("id",editSub.id);
    if(error) showToast("Error: "+error.message,false);
    else { updSubjects(p=>p.map(s=>s.id===editSub.id?{...s,...editSub}:s)); setEditSub(null); showToast("Updated!"); }
    setSaving(false);
  };

  const toggleActive = async (sub) => {
    const {error} = await supabase.from("subjects").update({is_active:!sub.is_active}).eq("id",sub.id);
    if(!error) updSubjects(p=>p.map(s=>s.id===sub.id?{...s,is_active:!s.is_active}:s));
  };

  const deleteSubject = async (sub) => {
    setSaving(true);
    const {error} = await supabase.from("subjects").delete().eq("id",sub.id);
    if(error) showToast("Error: "+error.message,false);
    else { updSubjects(p=>p.filter(s=>s.id!==sub.id)); setConfirmDel(null); showToast("Subject removed"); }
    setSaving(false);
  };

  const SubjectSection = ({type, subs, newForm, setNewForm, icon, color, bg, border}) => (
    <div style={{background:"white",borderRadius:16,padding:"22px 24px",boxShadow:`0 2px 12px ${C.shadow}`,flex:1}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
        <div style={{width:40,height:40,borderRadius:12,background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{icon}</div>
        <div>
          <div style={{fontWeight:800,color,fontSize:15}}>{type} Subjects</div>
          <div style={{fontSize:12,color:C.muted,marginTop:1}}>{subs.length} subjects · {subs.filter(s=>s.is_active).length} active</div>
        </div>
      </div>

      {/* Subject list */}
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
        {subs.length===0 && (
          <div style={{textAlign:"center",padding:"24px",color:C.muted,fontSize:13,background:C.bg,borderRadius:10}}>
            No {type.toLowerCase()} subjects yet. Add one below.
          </div>
        )}
        {subs.map(sub=>(
          <div key={sub.id} style={{
            background:sub.is_active?C.bg:"#f3f4f6",borderRadius:10,padding:"10px 14px",
            display:"flex",alignItems:"center",gap:10,border:`1px solid ${sub.is_active?border:"#e5e7eb"}`,
            opacity:sub.is_active?1:0.6,transition:"all .15s"
          }}>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:sub.is_active?C.text:"#9ca3af",fontSize:13}}>{sub.name}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:1}}>Default: {sub.default_marks} marks {!sub.is_active&&"· Hidden"}</div>
            </div>
            <div style={{display:"flex",gap:5}}>
              <button onClick={()=>setEditSub({...sub})}
                style={{background:"#eff6ff",color:"#2563eb",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>✏️</button>
              <button onClick={()=>toggleActive(sub)}
                style={{background:sub.is_active?"#fef3c7":"#f0fdf7",color:sub.is_active?"#d97706":C.primary,border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}
                title={sub.is_active?"Hide from dropdowns":"Show in dropdowns"}>
                {sub.is_active?"👁️":"🚫"}
              </button>
              <button onClick={()=>setConfirmDel(sub)}
                style={{background:"#fff0f3",color:"#e11d48",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🗑️</button>
            </div>
          </div>
        ))}
      </div>

      {/* Add new subject */}
      <div style={{background:bg,borderRadius:12,padding:"14px 16px",border:`1.5px dashed ${border}`}}>
        <div style={{fontSize:12,fontWeight:700,color,marginBottom:10}}>➕ Add {type} Subject</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input placeholder="Subject name (e.g. Drawing)"
            value={newForm.name} onChange={e=>setNewForm(p=>({...p,name:e.target.value}))}
            onKeyDown={e=>e.key==="Enter"&&addSubject(type)}
            style={{flex:1,border:`1.5px solid ${border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
            <label style={{fontSize:9,color:C.muted,fontWeight:600}}>MAX MARKS</label>
            <input type="number" min="1" max="1000"
              value={newForm.default_marks} onChange={e=>setNewForm(p=>({...p,default_marks:e.target.value}))}
              style={{width:70,border:`1.5px solid ${border}`,borderRadius:8,padding:"8px 8px",fontSize:13,textAlign:"center"}}/>
          </div>
          <button onClick={()=>addSubject(type)} disabled={saving||!newForm.name.trim()}
            style={{background:color,color:"white",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:saving?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
            {saving?"...":"Add"}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Edit modal */}
      {editSub && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setEditSub(null)}>
          <div style={{background:"white",borderRadius:16,padding:"28px 32px",maxWidth:400,width:"100%",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:800,fontSize:15,color:C.primary,marginBottom:18}}>✏️ Edit Subject</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.primary,display:"block",marginBottom:5}}>Subject Name</label>
                <input value={editSub.name} onChange={e=>setEditSub(p=>({...p,name:e.target.value}))}
                  style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:14,fontWeight:600}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:C.primary,display:"block",marginBottom:5}}>Default Max Marks</label>
                <input type="number" value={editSub.default_marks} onChange={e=>setEditSub(p=>({...p,default_marks:e.target.value}))}
                  style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:14}}/>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input type="checkbox" id="sub_active" checked={!!editSub.is_active} onChange={e=>setEditSub(p=>({...p,is_active:e.target.checked}))}/>
                <label htmlFor="sub_active" style={{fontSize:13,fontWeight:600,cursor:"pointer"}}>Active (shows in dropdowns)</label>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={updateSubject} disabled={saving}
                style={{flex:1,background:saving?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Saving...":"💾 Save Changes"}
              </button>
              <button onClick={()=>setEditSub(null)} style={{padding:"11px 18px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDel && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setConfirmDel(null)}>
          <div style={{background:"white",borderRadius:16,padding:"28px 32px",maxWidth:360,textAlign:"center",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:36,marginBottom:10}}>🗑️</div>
            <div style={{fontWeight:800,fontSize:15,marginBottom:8}}>Delete Subject?</div>
            <div style={{color:"#6b7280",fontSize:13,marginBottom:6}}>Delete <strong>{confirmDel.name}</strong>?</div>
            <div style={{color:"#d97706",fontSize:12,background:"#fffbeb",borderRadius:8,padding:"8px 12px",marginBottom:18}}>⚠️ Past exam records using this subject will keep their data. Only future exams are affected.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>deleteSubject(confirmDel)} disabled={saving}
                style={{flex:1,background:"#e11d48",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,cursor:saving?"not-allowed":"pointer"}}>
                {saving?"Deleting...":"Delete"}
              </button>
              <button onClick={()=>setConfirmDel(null)} style={{flex:1,background:"#f3f4f6",border:"none",borderRadius:10,padding:"11px",fontWeight:700,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"18px 24px",color:"white",boxShadow:`0 4px 20px ${C.primary}40`}}>
        <div style={{fontWeight:900,fontSize:18}}>📚 Subject Management</div>
        <div style={{fontSize:13,opacity:.85,marginTop:3}}>Add, rename, or deactivate subjects that appear on report cards and exam forms. Changes apply globally.</div>
      </div>

      {/* Info banner */}
      <div style={{background:"#eff6ff",borderRadius:12,padding:"12px 18px",border:"1px solid #bfdbfe",fontSize:13,color:"#1d4ed8"}}>
        💡 <strong>How it works:</strong> School subjects appear in the timetable and on report cards for school students. Hifz subjects appear on Hifz student report cards. <strong>Hifz Assessment Criteria</strong> (e.g. Memorization, Revision, Manzil) are scored on report cards but never appear in the timetable or auto-schedule — they are how you measure Hifz progress, not timetable subjects. Deactivating any subject hides it from new exams but keeps all historical data.
      </div>

      {/* Two sections — inlined to avoid focus loss on state update */}
      <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
        {[
          {type:"School",      label:"School Subjects",          subs:schoolSubs,   newForm:newSchool,   setNewForm:setNewSchool,   icon:"📝",color:C.primary, bg:C.primaryPale,border:C.primaryMid},
          {type:"Hifz",        label:"Hifz Subjects",            subs:hifzSubs,     newForm:newHifz,     setNewForm:setNewHifz,     icon:"📖",color:"#7c3aed",bg:"#f5f3ff",  border:"#c4b5fd"},
          {type:"HifzComponent",label:"Hifz Assessment Criteria",subs:hifzCompSubs, newForm:newHifzComp, setNewForm:setNewHifzComp, icon:"📿",color:"#0891b2",bg:"#ecfeff",  border:"#a5f3fc"}
        ].map(({type,label,subs,newForm,setNewForm,icon,color,bg,border})=>(
          <div key={type} style={{background:"white",borderRadius:16,padding:"22px 24px",boxShadow:`0 2px 12px ${C.shadow}`,flex:1,minWidth:280}}>
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
              <div style={{width:40,height:40,borderRadius:12,background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{icon}</div>
              <div>
                <div style={{fontWeight:800,color,fontSize:15}}>{label}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:1}}>{subs.length} · {subs.filter(s=>s.is_active).length} active{type==="HifzComponent"?" · not shown in timetable":""}</div>
              </div>
            </div>
            {/* Subject list */}
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
              {subs.length===0 && (
                <div style={{textAlign:"center",padding:"24px",color:C.muted,fontSize:13,background:C.bg,borderRadius:10}}>
                  No {label.toLowerCase()} added yet.
                </div>
              )}
              {subs.map(sub=>(
                <div key={sub.id} style={{
                  background:sub.is_active?C.bg:"#f3f4f6",borderRadius:10,padding:"10px 14px",
                  display:"flex",alignItems:"center",gap:10,border:`1px solid ${sub.is_active?border:"#e5e7eb"}`,
                  opacity:sub.is_active?1:0.6,transition:"all .15s"
                }}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:sub.is_active?C.text:"#9ca3af",fontSize:13}}>{sub.name}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:1}}>Default: {sub.default_marks} marks {!sub.is_active&&"· Hidden"}</div>
                  </div>
                  <div style={{display:"flex",gap:5}}>
                    <button onClick={()=>setEditSub({...sub})}
                      style={{background:"#eff6ff",color:"#2563eb",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>✏️</button>
                    <button onClick={()=>toggleActive(sub)}
                      style={{background:sub.is_active?"#fef3c7":"#f0fdf7",color:sub.is_active?"#d97706":C.primary,border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}
                      title={sub.is_active?"Hide from dropdowns":"Show in dropdowns"}>
                      {sub.is_active?"👁️":"🚫"}
                    </button>
                    <button onClick={()=>setConfirmDel(sub)}
                      style={{background:"#fff0f3",color:"#e11d48",border:"none",borderRadius:7,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
            {/* Add new subject — no component boundary so focus is kept */}
            <div style={{background:bg,borderRadius:12,padding:"14px 16px",border:`1.5px dashed ${border}`}}>
              <div style={{fontSize:12,fontWeight:700,color,marginBottom:10}}>➕ Add {type} Subject</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input
                  placeholder={`Subject name (e.g. ${type==="School"?"Drawing":"Tajweed"})`}
                  value={newForm.name}
                  onChange={e=>setNewForm(p=>({...p,name:e.target.value}))}
                  onKeyDown={e=>{if(e.key==="Enter"&&newForm.name.trim()){addSubject(type);}}}
                  ref={type==="School"?schoolInputRef:hifzInputRef}
                  style={{flex:1,border:`1.5px solid ${border}`,borderRadius:8,padding:"8px 12px",fontSize:13,outline:"none"}}
                  autoComplete="off"
                />
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <label style={{fontSize:9,color:C.muted,fontWeight:600,whiteSpace:"nowrap"}}>MAX MARKS</label>
                  <input
                    type="number" min="1" max="1000"
                    value={newForm.default_marks}
                    onChange={e=>setNewForm(p=>({...p,default_marks:e.target.value}))}
                    onKeyDown={e=>{if(e.key==="Enter"&&newForm.name.trim()){addSubject(type);}}}
                    style={{width:70,border:`1.5px solid ${border}`,borderRadius:8,padding:"8px 8px",fontSize:13,textAlign:"center",outline:"none"}}
                  />
                </div>
                <button
                  onClick={()=>addSubject(type)}
                  disabled={saving||!newForm.name.trim()}
                  style={{background:saving||!newForm.name.trim()?"#9ca3af":color,color:"white",border:"none",borderRadius:8,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:saving||!newForm.name.trim()?"not-allowed":"pointer",whiteSpace:"nowrap"}}>
                  {saving?"...":"Add"}
                </button>
              </div>
              <div style={{fontSize:11,color:C.muted,marginTop:8}}>💡 Press <kbd style={{background:"white",border:`1px solid ${border}`,borderRadius:4,padding:"1px 5px",fontSize:10}}>Enter</kbd> to add quickly</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── RESULTS MANAGEMENT ──────────────────────────────────────────
const SUBJECTS_LIST = ["Quran Nazra","Quran Hifz","Urdu","English","Mathematics","Science","Social Studies","Islamiat","Computer","General Knowledge"];
const HIFZ_SUBJECTS = ["Tajweed","Memorization","Revision","Nazra","Manzil"];
const ALLCLASSES = ["Nursery","KG","Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8","Class 9","Hifz Only"];
const MONTHS_ORDER = ["January","February","March","April","May","June","July","August","September","October","November","December"];

// Secondary school classes that get different subject sets
const SECONDARY_CLASSES = ["Class 9","Class 10","Class 11","Class 12"];

// Exam type definitions
const EXAM_TYPES = [
  {id:"quiz",     label:"Quiz",         icon:"⚡", color:"#7c3aed", bg:"#f5f3ff", desc:"Short quick assessment"},
  {id:"monthly",  label:"Monthly Test", icon:"📅", color:"#2563eb", bg:"#eff6ff", desc:"End-of-month evaluation"},
  {id:"midterm",  label:"Midterm",      icon:"📋", color:"#d97706", bg:"#fffbeb", desc:"Mid-semester examination"},
  {id:"final",    label:"Final Exam",   icon:"🎓", color:"#1a6b3c", bg:"#e8f5ee", desc:"Year-end final examination"},
  {id:"custom",   label:"Custom",       icon:"✏️", color:"#6b7280", bg:"#f9fafb", desc:"Name it yourself"},
];

// ── Exam Wizard (4-step) ─────────────────────────────────────────
function ExamWizard({ subjects, students, onDone, onCancel }) {
  const [step, setStep] = React.useState(1);
  const [cls, setCls]   = React.useState("");
  const [examType, setExamType] = React.useState(null);
  const [details, setDetails] = React.useState({name:"", date:"", note:""});
  const [selSubs, setSelSubs] = React.useState({}); // {subId: {checked, marks, name}}
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [slideDir, setSlideDir] = React.useState("right"); // animation direction

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const isSecondary = SECONDARY_CLASSES.includes(cls);
  const isHifz = cls === "Hifz Only";

  // Filter subjects relevant to the chosen class
  const relevantSubs = React.useMemo(() => {
    if (!subjects || !subjects.length) return [];
    return subjects.filter(s => {
      if (!s.is_active) return false;
      if (isHifz) return s.type === "Hifz" || s.type === "HifzComponent";
      return s.type === "School";
    });
  }, [subjects, isHifz]);

  // Auto-name based on type + month
  const autoName = (type) => {
    const now = new Date();
    const month = MONTHS_ORDER[now.getMonth()];
    const year = now.getFullYear();
    if (type.id === "quiz") return `Quiz - ${month} ${year}`;
    if (type.id === "monthly") return `${month} Monthly Test ${year}`;
    if (type.id === "midterm") return `Midterm Examination ${year}`;
    if (type.id === "final") return `Final Examination ${year}`;
    return "";
  };

  const goStep = (n) => {
    setSlideDir(n > step ? "right" : "left");
    setStep(n);
  };

  const toggleSub = (sub) => {
    setSelSubs(p => {
      const was = p[sub.id];
      if (was?.checked) return {...p, [sub.id]: {...was, checked: false}};
      return {...p, [sub.id]: {checked: true, marks: sub.default_marks || 100, name: sub.name}};
    });
  };

  const selectedSubs = Object.entries(selSubs).filter(([,v]) => v.checked);

  const saveExams = async () => {
    if (!selectedSubs.length) return showToast("Select at least one subject", false);
    if (!details.name.trim()) return showToast("Exam name is required", false);
    setSaving(true);
    const rows = selectedSubs.map(([,v]) => ({
      name: details.name.trim(),
      class: cls,
      subject: v.name,
      exam_date: details.date || null,
      total_marks: v.marks || 100,
      teacher_note: details.note || null,
      exam_type: examType.id,
    }));
    const {data, error} = await supabase.from("exams").insert(rows).select();
    if (error) { showToast("Error: " + error.message, false); setSaving(false); return; }
    fireConfetti();
    onDone(data, details.name, cls, examType);
    setSaving(false);
  };

  const stepStyle = (n) => ({
    display: "flex", alignItems: "center", gap: 6, position: "relative", zIndex: 1,
  });
  const dotStyle = (n) => ({
    width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0, transition: "all .3s",
    background: step > n ? C.primary : step === n ? C.blue : C.border,
    color: step >= n ? "white" : C.muted,
    boxShadow: step === n ? `0 0 0 3px ${C.bluePale}` : "none",
  });

  return (
    <div style={{background:"white",borderRadius:20,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 24px ${C.shadow}`}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d5e35)`,padding:"18px 24px",color:"white"}}>
        <div style={{fontWeight:900,fontSize:17}}>➕ Create New Exam</div>
        <div style={{fontSize:12,opacity:.8,marginTop:2}}>Step {step} of 4 — {["Choose Class","Exam Type","Details","Subjects"][step-1]}</div>
      </div>

      {/* Progress Steps */}
      <div style={{padding:"16px 24px",background:C.bg,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:0}}>
        {["Class","Type","Details","Subjects"].map((label,i) => (
          <React.Fragment key={i}>
            <div style={stepStyle(i+1)}>
              <div style={dotStyle(i+1)}>
                {step > i+1 ? "✓" : i+1}
              </div>
              <span style={{fontSize:12,fontWeight:step===i+1?800:500,color:step>=i+1?C.primary:C.muted,whiteSpace:"nowrap"}}>{label}</span>
            </div>
            {i < 3 && <div style={{flex:1,height:2,background:step>i+1?C.primaryMid:C.border,margin:"0 8px",borderRadius:1,transition:"background .3s"}}/>}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div style={{padding:"24px",minHeight:260}}>

        {/* STEP 1: Class */}
        {step === 1 && (
          <div className="fade-up">
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>Which class is this exam for?</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:18}}>Students will be auto-populated from Records.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {ALLCLASSES.map(c => {
                const count = c === "Hifz Only"
                  ? students.filter(s=>s.stream?.includes("Hifz")||s.stream?.includes("Both")).length
                  : students.filter(s=>(s.class||s["class"])===c).length;
                const isSelected = cls === c;
                const isSecClass = SECONDARY_CLASSES.includes(c);
                return (
                  <button key={c} onClick={() => { setCls(c); setSelSubs({}); }}
                    style={{
                      padding:"10px 16px", borderRadius:10, border:`2px solid ${isSelected?C.primary:C.border}`,
                      background:isSelected?C.primaryPale:"white", color:isSelected?C.primary:C.text,
                      fontWeight:isSelected?800:500, fontSize:13, cursor:"pointer", transition:"all .2s",
                      display:"flex", flexDirection:"column", alignItems:"center", gap:2, minWidth:80,
                    }}>
                    <span>{c==="Hifz Only"?"Hifz Exam":c}</span>
                    <span style={{fontSize:10,color:isSelected?C.primaryLight:C.muted,fontWeight:600}}>
                      {count} students{isSecClass?" · Secondary":""}
                    </span>
                  </button>
                );
              })}
            </div>
            {isSecondary && (
              <div style={{marginTop:14,padding:"10px 14px",background:"#f5f3ff",borderRadius:10,border:"1px solid #ddd6fe",fontSize:12,color:"#5b21b6",fontWeight:600}}>
                🎓 Secondary class detected — Physics, Chemistry, Biology subjects will be suggested in Step 4.
              </div>
            )}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:20}}>
              <button onClick={() => cls ? goStep(2) : null} disabled={!cls}
                style={{background:cls?C.primary:"#9ca3af",color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:800,fontSize:13,cursor:cls?"pointer":"not-allowed",transition:"all .2s"}}>
                Next → Exam Type
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Exam Type */}
        {step === 2 && (
          <div className="fade-up">
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>What kind of exam is this?</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:18}}>For <strong style={{color:C.text}}>{cls==="Hifz Only"?"Hifz Exam":cls}</strong> · {students.filter(s=>(s.class||s["class"])===cls||(cls==="Hifz Only"&&s.stream?.includes("Hifz"))).length} students</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:12}}>
              {EXAM_TYPES.map(et => (
                <button key={et.id} onClick={() => { setExamType(et); if(!details.name) setDetails(d=>({...d,name:autoName(et)})); }}
                  style={{
                    padding:"16px",borderRadius:14,border:`2px solid ${examType?.id===et.id?et.color:C.border}`,
                    background:examType?.id===et.id?et.bg:"white",cursor:"pointer",textAlign:"left",
                    transition:"all .2s",boxShadow:examType?.id===et.id?`0 4px 16px ${et.color}30`:"none",
                  }}>
                  <div style={{fontSize:26,marginBottom:8}}>{et.icon}</div>
                  <div style={{fontWeight:800,color:examType?.id===et.id?et.color:C.text,fontSize:14}}>{et.label}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:3}}>{et.desc}</div>
                </button>
              ))}
            </div>
            <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"space-between"}}>
              <button onClick={() => goStep(1)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 18px",fontWeight:600,fontSize:13,cursor:"pointer",color:C.muted}}>← Back</button>
              <button onClick={() => examType ? goStep(3) : null} disabled={!examType}
                style={{background:examType?C.primary:"#9ca3af",color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:800,fontSize:13,cursor:examType?"pointer":"not-allowed",transition:"all .2s"}}>
                Next → Details
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Details */}
        {step === 3 && (
          <div className="fade-up">
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:4}}>Exam Details</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:18}}>
              <span style={{background:examType.bg,color:examType.color,borderRadius:8,padding:"2px 10px",fontWeight:700,fontSize:12,marginRight:8}}>{examType.icon} {examType.label}</span>
              for {cls==="Hifz Only"?"Hifz Exam":cls}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Exam Name <span style={{color:C.rose}}>*</span></label>
                <input value={details.name} onChange={e=>setDetails(d=>({...d,name:e.target.value}))}
                  placeholder={`e.g. ${autoName(examType)}`}
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontSize:13,color:C.text}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Exam Date</label>
                  <input type="date" value={details.date} onChange={e=>setDetails(d=>({...d,date:e.target.value}))}
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontSize:13}}/>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Academic Year</label>
                  <input value={new Date().getFullYear()} readOnly
                    style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontSize:13,background:C.bg,color:C.muted}}/>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <label style={{fontSize:12,fontWeight:700,color:C.primary}}>Teacher's Note <span style={{color:C.muted,fontWeight:400}}>(optional)</span></label>
                <textarea value={details.note} onChange={e=>setDetails(d=>({...d,note:e.target.value}))} rows={2}
                  placeholder="e.g. Chapters 1–4, Focus on grammar..."
                  style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontSize:13,resize:"vertical"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"space-between"}}>
              <button onClick={() => goStep(2)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 18px",fontWeight:600,fontSize:13,cursor:"pointer",color:C.muted}}>← Back</button>
              <button onClick={() => details.name.trim() ? goStep(4) : showToast("Exam name is required",false)}
                style={{background:C.primary,color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                Next → Subjects
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Subjects */}
        {step === 4 && (
          <div className="fade-up">
            <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:2}}>Select Subjects & Marks</div>
            <div style={{fontSize:13,color:C.muted,marginBottom:4}}>
              Subjects loaded from your <strong style={{color:C.text}}>Subjects</strong> tab. Click to select, edit marks per subject.
            </div>
            {relevantSubs.length === 0 && (
              <div style={{padding:"20px",background:C.accentPale,borderRadius:12,border:`1px solid ${C.accentLight}`,color:"#92400e",fontSize:13,marginBottom:14}}>
                ⚠️ No active subjects found. Go to the <strong>Subjects</strong> tab and add some!
              </div>
            )}
            {isSecondary && (
              <div style={{padding:"8px 12px",background:"#f5f3ff",borderRadius:8,border:"1px solid #ddd6fe",fontSize:12,color:"#5b21b6",fontWeight:600,marginBottom:12}}>
                🎓 Secondary class: Physics, Chemistry, Biology etc. — add via Subjects tab if not listed.
              </div>
            )}
            {relevantSubs.some(s=>s.type==="HifzComponent")&&(
              <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:4,marginTop:2}}>SCHOOL SUBJECTS</div>
            )}
            <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:16}}>
              {relevantSubs.filter(s=>s.type!=="HifzComponent").map(sub => {
                const sel = selSubs[sub.id];
                const isOn = sel?.checked;
                return (
                  <div key={sub.id} style={{display:"flex",alignItems:"center",gap:0,borderRadius:10,border:`2px solid ${isOn?C.blue:C.border}`,background:isOn?C.bluePale:"white",overflow:"hidden",transition:"all .2s"}}>
                    <button onClick={() => toggleSub(sub)}
                      style={{background:"none",border:"none",padding:"8px 12px",cursor:"pointer",fontWeight:700,fontSize:13,color:isOn?C.blue:C.muted,display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:16}}>{isOn?"☑️":"⬜"}</span>
                      {sub.name}
                    </button>
                    {isOn && (
                      <div style={{display:"flex",alignItems:"center",gap:4,paddingRight:10,borderLeft:`1px solid ${C.bluePale}`}}>
                        <span style={{fontSize:10,color:C.muted,fontWeight:600}}>/ </span>
                        <input type="number" min="1" max="1000" value={sel.marks}
                          onChange={e => setSelSubs(p=>({...p,[sub.id]:{...p[sub.id],marks:Number(e.target.value)}}))}
                          style={{width:52,border:"none",background:"transparent",padding:"6px 4px",fontSize:13,fontWeight:700,color:C.blue,textAlign:"center"}}/>
                        <span style={{fontSize:10,color:C.muted,fontWeight:600}}>marks</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {relevantSubs.some(s=>s.type==="HifzComponent")&&(
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",marginBottom:6,marginTop:4}}>HIFZ ASSESSMENT CRITERIA</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
                  {relevantSubs.filter(s=>s.type==="HifzComponent").map(sub=>{
                    const sel=selSubs[sub.id];
                    const isOn=sel?.checked;
                    return (
                      <div key={sub.id} style={{display:"flex",alignItems:"center",gap:0,borderRadius:10,
                        border:`2px solid ${isOn?"#7c3aed":C.border}`,background:isOn?"#f5f3ff":"white",overflow:"hidden",transition:"all .2s"}}>
                        <button onClick={()=>toggleSub(sub)}
                          style={{background:"none",border:"none",padding:"8px 12px",cursor:"pointer",fontWeight:700,fontSize:13,color:isOn?"#7c3aed":C.muted,display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:16}}>{isOn?"☑️":"⬜"}</span>
                          {sub.name}
                        </button>
                        {isOn&&(
                          <div style={{display:"flex",alignItems:"center",gap:4,paddingRight:10,borderLeft:"1px solid #ede9fe"}}>
                            <span style={{fontSize:10,color:C.muted,fontWeight:600}}>/ </span>
                            <input type="number" min="1" max="100" value={sel.marks}
                              onChange={e=>setSelSubs(p=>({...p,[sub.id]:{...p[sub.id],marks:Number(e.target.value)}}))}
                              style={{width:44,border:"none",background:"transparent",padding:"6px 4px",fontSize:13,fontWeight:700,color:"#7c3aed",textAlign:"center"}}/>
                            <span style={{fontSize:10,color:C.muted,fontWeight:600}}>marks</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Select All / None shortcuts */}
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <button onClick={() => {
                const all = {};
                relevantSubs.forEach(s => { all[s.id] = {checked:true,marks:s.default_marks||100,name:s.name}; });
                setSelSubs(all);
              }} style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                ✅ Select All
              </button>
              <button onClick={() => setSelSubs({})}
                style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                Clear
              </button>
            </div>

            {selectedSubs.length > 0 && (
              <div style={{background:C.bg,borderRadius:10,padding:"10px 14px",fontSize:12,color:C.muted,marginBottom:16}}>
                <strong style={{color:C.primary}}>{selectedSubs.length}</strong> subject(s) selected ·
                Total max marks: <strong style={{color:C.primary}}>{selectedSubs.reduce((s,[,v])=>s+(v.marks||0),0)}</strong>
              </div>
            )}

            <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
              <button onClick={() => goStep(3)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 18px",fontWeight:600,fontSize:13,cursor:"pointer",color:C.muted}}>← Back</button>
              <button onClick={saveExams} disabled={saving||!selectedSubs.length}
                style={{background:saving||!selectedSubs.length?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:800,fontSize:13,cursor:saving||!selectedSubs.length?"not-allowed":"pointer",transition:"all .2s"}}>
                {saving?"Creating...":"🚀 Create "+selectedSubs.length+" Exam(s)"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel */}
      <div style={{padding:"12px 24px",borderTop:`1px solid ${C.border}`,background:C.bg,display:"flex",justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",fontWeight:600}}>✕ Cancel</button>
      </div>
    </div>
  );
}

// ── Enter Marks Panel — Spreadsheet Style ───────────────────────
function EnterMarksPanel({ exam, students, results, onSave, onClose }) {
  const GRADES=[{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}];
  const getGrade=(m,t)=>{const p=(m/t)*100;return GRADES.find(g=>p>=g.min)?.label||"F";};
  const gradeColor=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
  const [allExams, setAllExams] = React.useState([exam]);
  const [marks, setMarks] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(()=>{
    supabase.from("exams").select("*").eq("name",exam.name).eq("class",exam.class)
      .then(({data})=>setAllExams(data&&data.length>1?data:[exam]));
  },[exam.id]);

  const isHifzSub = sub => ["Tajweed","Memorization","Revision","Nazra","Manzil"].some(h=>sub?.toLowerCase().includes(h.toLowerCase()));
  const classStudents = (isHifzSub(exam.subject)
    ? students.filter(s=>s.stream?.includes("Hifz")||s.stream?.includes("Both"))
    : students.filter(s=>(s.class||s["class"])===exam.class)
  ).filter(s=>s.name||s.full_name);

  React.useEffect(()=>{
    const init={};
    results.forEach(r=>{ if(allExams.some(e=>e.id===r.exam_id)) init[r.student_id+"_"+r.exam_id]=String(r.marks_obtained); });
    setMarks(init);
  },[results,allExams]);

  const setMark=(sid,eid,val)=>setMarks(p=>({...p,[sid+"_"+eid]:val}));
  const getMark=(sid,eid)=>marks[sid+"_"+eid]??"";
  const totalCells=classStudents.length*allExams.length;
  const filled=Object.values(marks).filter(v=>v!=="").length;
  const pct=totalCells?Math.round((filled/totalCells)*100):0;

  const rowTotal=(sid)=>{
    let got=0,max=0;
    allExams.forEach(e=>{const v=getMark(sid,e.id);if(v!==""){got+=Number(v);max+=e.total_marks;}});
    return max>0?{got,max,pct:Math.round((got/max)*100)}:null;
  };

  const save=async()=>{
    const rows=[];
    classStudents.forEach(s=>allExams.forEach(e=>{
      const val=getMark(s.id,e.id);if(val==="")return;
      const mo=Number(val);
      rows.push({student_id:s.id,student_name:s.name||s.full_name||"",class:e.class,exam_id:e.id,exam_name:e.name,subject:e.subject,marks_obtained:mo,total_marks:e.total_marks,grade:getGrade(mo,e.total_marks),remarks:""});
    }));
    if(!rows.length)return;
    setSaving(true);
    const {error}=await supabase.from("results").upsert(rows,{onConflict:"student_id,exam_id"});
    setSaving(false);
    if(!error){onSave(rows);fireConfetti();}
    else alert("Save error: "+error.message);
  };

  const fillAbsent=()=>{
    const np={...marks};
    classStudents.forEach(s=>allExams.forEach(e=>{if(!np[s.id+"_"+e.id])np[s.id+"_"+e.id]="0";}));
    setMarks(np);
  };

  return (
    <div style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 20px ${C.shadow}`}}>
      <div style={{padding:"16px 20px",background:"linear-gradient(135deg,#1e3a5f,#2563eb)",color:"white",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
        <div style={{flex:1}}>
          <div style={{fontWeight:900,fontSize:15}}>📋 {exam.name} — {exam.class}</div>
          <div style={{fontSize:12,opacity:.85,marginTop:2}}>{allExams.length} subject{allExams.length>1?"s":""}: {allExams.map(e=>e.subject).join(" · ")}</div>
        </div>
        <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"none",color:"white",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontWeight:700,fontSize:12}}>✕ Close</button>
      </div>
      <div style={{padding:"10px 20px",background:"#eff6ff",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{flex:1,height:6,background:"#bfdbfe",borderRadius:3}}>
          <div style={{width:`${pct}%`,height:"100%",background:"#2563eb",borderRadius:3,transition:"width .3s"}}/>
        </div>
        <span style={{fontSize:12,fontWeight:700,color:"#1d4ed8",whiteSpace:"nowrap"}}>{filled}/{totalCells} ({pct}%)</span>
        <button onClick={fillAbsent} style={{background:"white",border:"1px solid #bfdbfe",color:"#2563eb",borderRadius:7,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Mark absent 0</button>
      </div>
      <div style={{overflowX:"auto",maxHeight:460,overflowY:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead style={{position:"sticky",top:0,zIndex:2}}>
            <tr style={{background:"#1e3a5f"}}>
              <th style={{padding:"10px 16px",textAlign:"left",color:"white",fontSize:11,fontWeight:700,whiteSpace:"nowrap",minWidth:160}}>Student</th>
              {allExams.map(e=>(
                <th key={e.id} style={{padding:"10px 12px",textAlign:"center",color:"white",fontSize:11,fontWeight:700,whiteSpace:"nowrap",minWidth:90}}>
                  {e.subject}<br/><span style={{opacity:.7,fontSize:10}}>/ {e.total_marks}</span>
                </th>
              ))}
              {allExams.length>1&&<th style={{padding:"10px 12px",textAlign:"center",color:"white",fontSize:11,fontWeight:700}}>Total</th>}
            </tr>
          </thead>
          <tbody>
            {classStudents.map((s,i)=>{
              const tot=rowTotal(s.id);
              const rowGrade=tot?getGrade(tot.pct,100):null;
              return (
                <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"white":"#f9fffe"}}>
                  <td style={{padding:"8px 16px",fontWeight:700,color:C.text,whiteSpace:"nowrap"}}>
                    <div style={{fontSize:13}}>{s.name||s.full_name}</div>
                  </td>
                  {allExams.map(e=>{
                    const val=getMark(s.id,e.id);
                    const mo=val!==""?Number(val):null;
                    const grade=mo!==null?getGrade(mo,e.total_marks):null;
                    const pctVal=mo!==null?Math.round((mo/e.total_marks)*100):null;
                    const cellBg=mo===null?"transparent":mo===0?"#fef2f2":pctVal>=80?C.primaryPale:pctVal>=50?"#fffbeb":"#fef2f2";
                    return (
                      <td key={e.id} style={{padding:"5px 8px",textAlign:"center",background:cellBg,transition:"background .15s"}}>
                        <input type="number" min="0" max={e.total_marks} value={val}
                          onChange={ev=>setMark(s.id,e.id,ev.target.value)} placeholder="—"
                          style={{width:56,border:`1.5px solid ${val!==""?C.primaryMid:C.border}`,borderRadius:6,padding:"5px 4px",fontSize:13,fontWeight:700,textAlign:"center",color:grade?gradeColor(grade):C.text,background:"transparent"}}/>
                        {grade&&<div style={{fontSize:9,color:gradeColor(grade),fontWeight:800,marginTop:1}}>{grade}</div>}
                      </td>
                    );
                  })}
                  {allExams.length>1&&(
                    <td style={{padding:"8px 10px",textAlign:"center",background:tot?C.primaryPale:"transparent"}}>
                      {tot?<div>
                        <div style={{fontWeight:900,color:gradeColor(rowGrade),fontSize:13}}>{tot.pct}%</div>
                        <div style={{fontSize:9,color:C.muted}}>{tot.got}/{tot.max}</div>
                        <div style={{fontSize:10,fontWeight:800,color:gradeColor(rowGrade)}}>{rowGrade}</div>
                      </div>:<span style={{color:C.muted,fontSize:11}}>—</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{padding:"14px 20px",borderTop:`1px solid ${C.border}`,display:"flex",gap:10,background:C.bg}}>
        <button onClick={save} disabled={saving||filled===0}
          style={{flex:1,background:saving||filled===0?C.muted:"#2563eb",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:14,cursor:saving||filled===0?"not-allowed":"pointer"}}>
          {saving?"Saving...":"💾 Save "+filled+" Mark"+(filled!==1?"s":"")}
        </button>
        <button onClick={onClose} style={{padding:"11px 20px",background:"white",border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
      </div>
    </div>
  );
}

// ── Main ResultsManagement ────────────────────────────────────────
function ResultsManagement({ students, attendance = {}, resultsData, onResultsChange }) {
  const GRADES=[{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}];
  const getGrade=(m,t)=>{const p=(m/t)*100;return GRADES.find(g=>p>=g.min)?.label||"F";};
  const gradeColor=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");

  const [exams,     setExams]     = React.useState([]);
  const [results,   setResults]   = React.useState(resultsData||[]);
  // Sync in if parent passes fresh data after mount
  React.useEffect(()=>{ if(resultsData?.length) setResults(resultsData); },[resultsData?.length]);
  const [subjects,  setSubjects]  = React.useState([]);
  const [publishedCardsCount, setPublishedCardsCount] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState("overview");
  const [toast,     setToast]     = React.useState(null);
  const [reportData,setReportData]= React.useState(null);
  const [drillView, setDrillView] = React.useState(null);
  const [enterExam, setEnterExam] = React.useState(null); // exam being marked
  const [showWizard,setShowWizard]= React.useState(false);

  // Manage exams filter
  const [mfClass,  setMfClass]  = React.useState("All");
  const [mfType,   setMfType]   = React.useState("All");
  const [mfSearch, setMfSearch] = React.useState("");
  const [mfMonth,  setMfMonth]  = React.useState("All");

  // Edit exam
  const [editExam, setEditExam] = React.useState(null);
  const [saving,   setSaving]   = React.useState(false);

  const showToast = (msg,ok=true)=>{ setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };
  const [aiAnalysis,  setAiAnalysis]  = React.useState("");
  const [aiAnalysisLoading, setAiAnalysisLoading] = React.useState(false);

  const generateClassAnalysis = async () => {
    if(aiAnalysisLoading) return;
    setAiAnalysisLoading(true); setAiAnalysis("");
    const classes = [...new Set(results.map(r=>r.class).filter(Boolean))].sort();
    const subjectList = [...new Set(results.map(r=>r.subject).filter(Boolean))].sort();
    const totalStudents = [...new Set(results.map(r=>r.student_id))].length;
    const overallAvg = results.length ? Math.round(results.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/results.length) : 0;
    const passing = results.filter(r=>(r.marks_obtained/r.total_marks)>=0.5).length;
    const failing = results.filter(r=>(r.marks_obtained/r.total_marks)<0.5).length;
    const subjectAvgs = subjectList.map(sub=>{
      const rows=results.filter(r=>r.subject===sub);
      return {sub, avg:rows.length?Math.round(rows.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/rows.length):null, count:rows.length};
    }).filter(x=>x.avg!=null).sort((a,b)=>a.avg-b.avg);
    const classAvgs = classes.map(cls=>{
      const rows=results.filter(r=>r.class===cls);
      return {cls, avg:rows.length?Math.round(rows.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/rows.length):null};
    }).filter(x=>x.avg!=null).sort((a,b)=>a.avg-b.avg);
    const prompt = `You are a senior academic analyst reviewing exam results for QK Academy (Islamic school). Be critical, concise, and direct. No fluff.

DATA SUMMARY:
Total students with results: ${totalStudents}
Overall average: ${overallAvg}%
Pass rate: ${Math.round((passing/(passing+failing||1))*100)}% (${passing} passed, ${failing} failed)
Classes: ${classes.join(", ")||"Mixed"}
Subjects: ${subjectList.join(", ")||"Mixed"}

Subject performance (low to high): ${subjectAvgs.map(x=>x.sub+": "+x.avg+"%").join(" | ")||"N/A"}
Class performance (low to high): ${classAvgs.map(x=>x.cls+": "+x.avg+"%").join(" | ")||"N/A"}
Exam types covered: Quizzes: ${exams.filter(e=>e.exam_type==="quiz").length}, Monthly: ${exams.filter(e=>e.exam_type==="monthly").length}, Midterm: ${exams.filter(e=>e.exam_type==="midterm").length}, Final: ${exams.filter(e=>e.exam_type==="final").length}

Write a sharp 5-6 sentence analysis covering:
1. Overall academic health verdict (one honest sentence)
2. Which subject(s) are the biggest problem and why it likely matters
3. Which class(es) are underperforming and need immediate attention
4. Any pattern worth flagging (e.g. pass/fail distribution, spread between classes)
5. Two concrete actions management should take this month

Plain text only. No markdown. No bullet points. Be direct.`;
    try {
      const text = await groqGenerate(prompt, 500);
      setAiAnalysis(text||"Could not generate analysis.");
    } catch(e) { setAiAnalysis("Error: "+e.message); }
    setAiAnalysisLoading(false);
  };

  React.useEffect(()=>{
    supabase.from("exams").select("*").order("exam_date",{ascending:true}).then(({data})=>setExams(data||[]));
    supabase.from("results").select("*").order("created_at",{ascending:false}).then(({data})=>setResults(data||[]));
    supabase.from("subjects").select("*").order("type").order("sort_order").order("name").then(({data})=>setSubjects(data||[]));
    supabase.from("published_report_cards").select("id",{count:"exact",head:true}).then(({count})=>setPublishedCardsCount(count||0));
  },[]);

  const refetchAll = async () => {
    const [{data:e},{data:r}] = await Promise.all([
      supabase.from("exams").select("*").order("exam_date",{ascending:true}),
      supabase.from("results").select("*").order("created_at",{ascending:false})
    ]);
    setExams(e||[]); setResults(r||[]);
    if(onResultsChange) onResultsChange(r||[]);
  };

  const updateExam = async () => {
    if(!editExam) return;
    setSaving(true);
    const {error} = await supabase.from("exams").update({
      name:editExam.name, class:editExam.class, subject:editExam.subject,
      exam_date:editExam.exam_date, total_marks:Number(editExam.total_marks)||100,
      teacher_note:editExam.teacher_note||null
    }).eq("id",editExam.id);
    if(error) showToast("Update failed",false);
    else { setExams(p=>p.map(e=>e.id===editExam.id?{...e,...editExam}:e)); setEditExam(null); showToast("Updated!"); }
    setSaving(false);
  };

  const deleteExam = async (id) => {
    if(!window.confirm("Delete this exam and all its results?")) return;
    await supabase.from("results").delete().eq("exam_id",id);
    const {error} = await supabase.from("exams").delete().eq("id",id);
    if(!error) { setExams(p=>p.filter(e=>e.id!==id)); setResults(p=>p.filter(r=>r.exam_id!==id)); showToast("Exam deleted"); }
  };

  // ── Exam categories ──
  const quizExams    = exams.filter(e=>e.exam_type==="quiz"    || e.name?.toLowerCase().includes("quiz"));
  const monthlyExams = exams.filter(e=>e.exam_type==="monthly" || (e.name?.toLowerCase().includes("monthly")&&!e.exam_type));
  const midtermExams = exams.filter(e=>e.exam_type==="midterm" || (e.name?.toLowerCase().includes("midterm")&&!e.exam_type));
  const finalExams   = exams.filter(e=>e.exam_type==="final"   || (e.name?.toLowerCase().includes("final")&&!e.exam_type));

  const totalResults = results.length;
  const avgScore     = totalResults ? Math.round(results.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/totalResults) : 0;
  const passing      = results.filter(r=>(r.marks_obtained/r.total_marks)>=0.5).length;
  const failing      = results.filter(r=>(r.marks_obtained/r.total_marks)<0.5).length;
  const [activeStat, setActiveStat] = React.useState(null);

  const tabBtn = (id,label,icon) => (
    <button onClick={()=>{setActiveTab(id);setDrillView(null);setEnterExam(null);setShowWizard(false);}} style={{
      padding:"8px 16px", borderRadius:10, border:"none", cursor:"pointer", fontWeight:700, fontSize:13,
      background:activeTab===id?C.primary:C.primaryPale,
      color:activeTab===id?"white":C.primary,
      transition:"all .2s",
      boxShadow:activeTab===id?`0 2px 10px ${C.shadow}`:"none",
    }}>{icon} {label}</button>
  );

  // ── Drill-down render (reused from previous, condensed) ──
  const renderDrillView = () => {
    if(!drillView) return null;

    if(drillView.type==="monthly_months") {
      const months=[...new Set(monthlyExams.map(e=>{const m=MONTHS_ORDER.find(mo=>e.name?.includes(mo));return m||null;}).filter(Boolean))].sort((a,b)=>MONTHS_ORDER.indexOf(a)-MONTHS_ORDER.indexOf(b));
      return (
        <div className="fade-up">
          <button onClick={()=>setDrillView(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.primary,fontWeight:700,fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>← Back to Overview</button>
          <div style={{fontWeight:800,color:C.primary,fontSize:16,marginBottom:4}}>📅 Monthly Tests</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Select a month to view results</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:12}}>
            {months.length===0 && <div style={{color:C.muted,fontSize:13,gridColumn:"1/-1",padding:32,textAlign:"center",background:C.bg,borderRadius:12}}>No monthly tests yet.</div>}
            {months.map(month=>{
              const mResults=results.filter(r=>r.exam_name?.includes(month)&&(r.exam_name?.toLowerCase().includes("monthly")||exams.find(e=>e.id===r.exam_id)?.exam_type==="monthly"));
              const avg=mResults.length?Math.round(mResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/mResults.length):null;
              return (
                <div key={month} onClick={()=>setDrillView({type:"monthly_classes",month})}
                  style={{background:"white",borderRadius:14,padding:"18px 20px",boxShadow:`0 2px 12px ${C.shadow}`,cursor:"pointer",border:`1px solid ${C.border}`,transition:"all .15s",textAlign:"center"}}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 6px 24px rgba(0,0,0,0.12)";e.currentTarget.style.borderColor=C.primaryMid;}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow=`0 2px 12px ${C.shadow}`;e.currentTarget.style.borderColor=C.border;}}>
                  <div style={{fontSize:28,marginBottom:6}}>📅</div>
                  <div style={{fontWeight:800,color:C.primary,fontSize:15}}>{month}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:4}}>{mResults.length} results</div>
                  {avg!==null&&<div style={{fontSize:18,fontWeight:900,color:gradeColor(getGrade(avg,100)),marginTop:6}}>{avg}%</div>}
                  <div style={{fontSize:11,color:C.primary,fontWeight:600,marginTop:4}}>View Classes →</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if(drillView.type==="monthly_classes") {
      const {month}=drillView;
      const mExams=monthlyExams.filter(e=>e.name?.includes(month));
      const classes=[...new Set(mExams.map(e=>e.class))].filter(Boolean).sort();
      return (
        <div className="fade-up">
          <button onClick={()=>setDrillView({type:"monthly_months"})} style={{background:"none",border:"none",cursor:"pointer",color:C.primary,fontWeight:700,fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>← Back to Months</button>
          <div style={{fontWeight:800,color:C.primary,fontSize:16,marginBottom:4}}>📅 {month}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
            {classes.map(cls=>{
              const clsStudents=students.filter(s=>(s.class||s["class"])===cls);
              const clsResults=results.filter(r=>r.exam_name?.includes(month)&&(r.exam_name?.toLowerCase().includes("monthly")||exams.find(e=>e.id===r.exam_id)?.exam_type==="monthly")&&r.class===cls);
              const avg=clsResults.length?Math.round(clsResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/clsResults.length):null;
              return (
                <div key={cls} onClick={()=>setDrillView({type:"monthly_roster",month,cls})}
                  style={{background:"white",borderRadius:14,padding:"18px 20px",boxShadow:`0 2px 12px ${C.shadow}`,cursor:"pointer",border:`1px solid ${C.border}`,transition:"all .15s",textAlign:"center"}}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 6px 24px rgba(0,0,0,0.12)";e.currentTarget.style.borderColor=C.primaryMid;}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow=`0 2px 12px ${C.shadow}`;e.currentTarget.style.borderColor=C.border;}}>
                  <div style={{fontSize:24,marginBottom:6}}>🏫</div>
                  <div style={{fontWeight:800,color:C.primary,fontSize:14}}>{cls}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:4}}>{clsStudents.length} students</div>
                  {avg!==null&&<div style={{fontSize:16,fontWeight:900,color:gradeColor(getGrade(avg,100)),marginTop:6}}>{avg}%</div>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if(drillView.type==="monthly_roster") {
      const {month,cls}=drillView;
      const clsStudents=students.filter(s=>(s.class||s["class"])===cls);
      const mExams=monthlyExams.filter(e=>e.name?.includes(month)&&e.class===cls);
      const subList=[...new Set(mExams.map(e=>e.subject))];
      return (
        <div className="fade-up">
          <button onClick={()=>setDrillView({type:"monthly_classes",month})} style={{background:"none",border:"none",cursor:"pointer",color:C.primary,fontWeight:700,fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>← Back to Classes</button>
          <div style={{fontWeight:800,color:C.primary,fontSize:16,marginBottom:2}}>📅 {month} — {cls}</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Subjects: {subList.join(", ")||"None"} · Tap student name to open report card</div>
          {/* Mobile card view — shown when table would be too wide */}
          <div style={{display:"none"}} className="mobile-cards">
            {clsStudents.map((s,i)=>{
              const sRes=results.filter(r=>(r.student_id===s.id||r.student_name===s.name)&&mExams.some(e=>e.id===r.exam_id));
              const avg=sRes.length?Math.round(sRes.reduce((a,r)=>a+(r.marks_obtained/r.total_marks)*100,0)/sRes.length):null;
              const g=avg!==null?getGrade(avg,100):null;
              return (
                <div key={s.id} style={{background:"white",borderRadius:12,padding:"14px 16px",marginBottom:8,border:`1px solid ${C.border}`,boxShadow:`0 1px 4px ${C.shadow}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontWeight:700,fontSize:14,color:C.text}}>{i+1}. {s.full_name||s.name}</div>
                    {g&&<span style={{fontWeight:800,color:gradeColor(g),background:`${gradeColor(g)}18`,borderRadius:6,padding:"3px 10px",fontSize:13}}>{g} {avg}%</span>}
                  </div>
                  {subList.length>0&&(
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                      {subList.map(sub=>{
                        const r=sRes.find(r=>r.subject===sub);
                        return (
                          <div key={sub} style={{background:C.bg,borderRadius:7,padding:"5px 10px",fontSize:12}}>
                            <span style={{color:C.muted,fontSize:11}}>{sub}: </span>
                            {r?<span style={{fontWeight:700,color:gradeColor(r.grade||getGrade(r.marks_obtained,r.total_marks))}}>{r.marks_obtained}/{r.total_marks}</span>:<span style={{color:C.muted}}>—</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button onClick={()=>setReportData({student:s,reportType:"monthly",monthLabel:month})}
                    style={{width:"100%",background:C.primary,color:"white",border:"none",borderRadius:8,padding:"9px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    📋 Open Report Card
                  </button>
                </div>
              );
            })}
          </div>
          {/* Desktop table — horizontally scrollable */}
          <div className="desktop-table" style={{
            background:"white",borderRadius:14,
            boxShadow:`0 2px 12px ${C.shadow}`,
            overflowX:"auto",
            WebkitOverflowScrolling:"touch", // smooth momentum scroll on iOS
            position:"relative",
          }}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:Math.max(500,subList.length*120+400)}}>
              <thead>
                <tr style={{background:C.primary}}>
                  {["#","Student","Stream",...subList,"Avg %","Grade","Report Card"].map((h,hi)=>(
                    <th key={h+hi} style={{
                      padding:"10px 14px",textAlign:hi<=2?"left":"center",
                      color:"white",fontSize:11,fontWeight:700,whiteSpace:"nowrap",
                      position:h==="Report Card"?"sticky":"static",
                      right:h==="Report Card"?0:"auto",
                      background:h==="Report Card"?C.primary:"transparent",
                      zIndex:h==="Report Card"?2:1,
                      boxShadow:h==="Report Card"?"-3px 0 8px rgba(0,0,0,0.15)":"none",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clsStudents.map((s,i)=>{
                  const sRes=results.filter(r=>(r.student_id===s.id||r.student_name===s.name)&&mExams.some(e=>e.id===r.exam_id));
                  const avg=sRes.length?Math.round(sRes.reduce((a,r)=>a+(r.marks_obtained/r.total_marks)*100,0)/sRes.length):null;
                  const g=avg!==null?getGrade(avg,100):null;
                  const rowBg=i%2===0?"white":"#f9fffe";
                  return (
                    <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:rowBg}}>
                      <td style={{padding:"9px 14px",color:C.muted,fontSize:11}}>{i+1}</td>
                      <td style={{padding:"9px 14px",fontWeight:700,color:C.text,whiteSpace:"nowrap"}}>{s.full_name||s.name}</td>
                      <td style={{padding:"9px 14px",fontSize:11}}>
                        <span style={{background:s.stream?.includes("Both")?C.purplePale:C.primaryPale,color:s.stream?.includes("Both")?C.purple:C.primary,borderRadius:6,padding:"2px 7px",fontWeight:700,fontSize:10}}>
                          {s.stream?.includes("Both")?"Hifz+School":"School"}
                        </span>
                      </td>
                      {subList.map(sub=>{
                        const r=sRes.find(r=>r.subject===sub);
                        return (
                          <td key={sub} style={{padding:"9px 14px",textAlign:"center"}}>
                            {r?<span style={{fontWeight:800,color:gradeColor(r.grade||getGrade(r.marks_obtained,r.total_marks))}}>{r.marks_obtained}<span style={{fontSize:9,color:C.muted}}>/{r.total_marks}</span></span>:<span style={{color:C.muted}}>—</span>}
                          </td>
                        );
                      })}
                      <td style={{padding:"9px 14px",textAlign:"center"}}>{avg!==null?<span style={{fontWeight:900,color:gradeColor(g)}}>{avg}%</span>:"—"}</td>
                      <td style={{padding:"9px 14px",textAlign:"center"}}>{g?<span style={{fontWeight:800,color:gradeColor(g),background:`${gradeColor(g)}18`,borderRadius:6,padding:"3px 8px",fontSize:11}}>{g}</span>:"—"}</td>
                      {/* Sticky RC button */}
                      <td style={{
                        padding:"9px 14px",textAlign:"center",
                        position:"sticky",right:0,background:rowBg,
                        boxShadow:"-3px 0 8px rgba(0,0,0,0.08)",zIndex:1
                      }}>
                        <button onClick={()=>setReportData({student:s,reportType:"monthly",monthLabel:month})}
                          style={{background:C.primary,color:"white",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                          📋 Report Card
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* CSS for responsive switch */}
          <style>{`
            @media (max-width: 640px) {
              .mobile-cards { display: flex !important; flex-direction: column; }
              .desktop-table { display: none !important; }
            }
          `}</style>
        </div>
      );
    }

    if(drillView.type==="term_classes") {
      const {examKey,label,icon,color,examsArr}=drillView;
      const classes=[...new Set(examsArr.map(e=>e.class))].filter(Boolean).sort();
      return (
        <div className="fade-up">
          <button onClick={()=>setDrillView(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.primary,fontWeight:700,fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>← Back</button>
          <div style={{fontWeight:800,color,fontSize:16,marginBottom:12}}>{icon} {label}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12}}>
            {classes.length===0&&<div style={{color:C.muted,fontSize:13,gridColumn:"1/-1",padding:32,textAlign:"center",background:C.bg,borderRadius:12}}>No {label.toLowerCase()} yet.</div>}
            {classes.map(cls=>{
              const clsStudents=students.filter(s=>(s.class||s["class"])===cls);
              const clsResults=results.filter(r=>examsArr.some(e=>e.id===r.exam_id)&&r.class===cls);
              const avg=clsResults.length?Math.round(clsResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/clsResults.length):null;
              return (
                <div key={cls} onClick={()=>setDrillView({...drillView,type:"term_roster",cls})}
                  style={{background:"white",borderRadius:14,padding:"18px 20px",boxShadow:`0 2px 12px ${C.shadow}`,cursor:"pointer",border:`1px solid ${C.border}`,transition:"all .15s",textAlign:"center"}}
                  onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 6px 24px rgba(0,0,0,0.12)";}}
                  onMouseLeave={e=>{e.currentTarget.style.boxShadow=`0 2px 12px ${C.shadow}`;}}>
                  <div style={{fontSize:24,marginBottom:6}}>🏫</div>
                  <div style={{fontWeight:800,color,fontSize:14}}>{cls}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:4}}>{clsStudents.length} students</div>
                  {avg!==null&&<div style={{fontSize:16,fontWeight:900,color:gradeColor(getGrade(avg,100)),marginTop:6}}>{avg}%</div>}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if(drillView.type==="term_roster") {
      const {examKey,label,icon,color,examsArr,cls}=drillView;
      const clsStudents=students.filter(s=>(s.class||s["class"])===cls);
      const subList=[...new Set(examsArr.filter(e=>e.class===cls).map(e=>e.subject))];
      return (
        <div className="fade-up">
          <button onClick={()=>setDrillView({...drillView,type:"term_classes"})} style={{background:"none",border:"none",cursor:"pointer",color,fontWeight:700,fontSize:13,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>← Back</button>
          <div style={{fontWeight:800,color,fontSize:16,marginBottom:2}}>{icon} {label} — {cls}</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:12}}>Subjects: {subList.join(", ")||"None"} · Click student for report card</div>
          <div style={{background:"white",borderRadius:14,overflow:"auto",boxShadow:`0 2px 12px ${C.shadow}`}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:color}}>
                  {["#","Student","Stream",...subList,"Avg %","Grade",""].map(h=>(
                    <th key={h} style={{padding:"10px 14px",textAlign:"left",color:"white",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {clsStudents.map((s,i)=>{
                  const sRes=results.filter(r=>(r.student_id===s.id||r.student_name===s.name)&&examsArr.some(e=>e.id===r.exam_id));
                  const avg=sRes.length?Math.round(sRes.reduce((a,r)=>a+(r.marks_obtained/r.total_marks)*100,0)/sRes.length):null;
                  const g=avg!==null?getGrade(avg,100):null;
                  return (
                    <tr key={s.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?"white":"#f9fffe"}}>
                      <td style={{padding:"8px 14px",color:C.muted,fontSize:11}}>{i+1}</td>
                      <td style={{padding:"8px 14px",fontWeight:700,color:C.text}}>{s.name}</td>
                      <td style={{padding:"8px 14px",fontSize:11}}>
                        <span style={{background:s.stream?.includes("Both")?C.purplePale:C.primaryPale,color:s.stream?.includes("Both")?C.purple:C.primary,borderRadius:6,padding:"2px 7px",fontWeight:700,fontSize:10}}>
                          {s.stream?.includes("Both")?"Hifz+School":"School"}
                        </span>
                      </td>
                      {subList.map(sub=>{
                        const r=sRes.find(r=>r.subject===sub);
                        return (
                          <td key={sub} style={{padding:"8px 14px",textAlign:"center"}}>
                            {r?<span style={{fontWeight:800,color:gradeColor(r.grade||getGrade(r.marks_obtained,r.total_marks))}}>{r.marks_obtained}<span style={{fontSize:9,color:C.muted}}>/{r.total_marks}</span></span>:<span style={{color:C.muted}}>—</span>}
                          </td>
                        );
                      })}
                      <td style={{padding:"8px 14px",textAlign:"center"}}>{avg!==null?<span style={{fontWeight:900,color:gradeColor(g)}}>{avg}%</span>:"—"}</td>
                      <td style={{padding:"8px 14px"}}>{g?<span style={{fontWeight:800,color:gradeColor(g),background:`${gradeColor(g)}18`,borderRadius:6,padding:"3px 8px",fontSize:11}}>{g}</span>:"—"}</td>
                      <td style={{padding:"8px 14px"}}>
                        <button onClick={()=>{window.scrollTo({top:0,behavior:"smooth"});setReportData({student:s,reportType:examKey,monthLabel:null});}}
                          style={{background:"white",color,border:`1px solid ${color}40`,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>📋 RC</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast && <Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Report Card Modal */}
      {reportData && (
        <ReportCardModal
          student={reportData.student} results={results} attendance={attendance}
          reportType={reportData.reportType} monthLabel={reportData.monthLabel}
          onClose={()=>setReportData(null)}
        />
      )}

      {/* Edit Exam Modal */}
      {editExam && (
        <Portal>
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setEditExam(null)}>
          <div style={{background:"white",borderRadius:20,padding:28,width:"100%",maxWidth:480,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:800,color:C.primary,fontSize:16,marginBottom:20}}>✏️ Edit Exam</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              {[{l:"Exam Name",k:"name",type:"text"},{l:"Exam Type",k:"exam_type",type:"select",opts:["quiz","monthly","midterm","final","custom"]},{l:"Subject",k:"subject",type:"select",opts:["--Select--",...(subjects.map(s=>s.name)||SUBJECTS_LIST),...HIFZ_SUBJECTS]},{l:"Class",k:"class",type:"select",opts:["--Select--",...ALLCLASSES]},{l:"Total Marks",k:"total_marks",type:"number"},{l:"Exam Date",k:"exam_date",type:"date"}].map(f=>(
                <div key={f.k} style={{display:"flex",flexDirection:"column",gap:5,gridColumn:f.k==="name"?"1/-1":"auto"}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.primary}}>{f.l}</label>
                  {f.type==="select"?(
                    <select value={editExam[f.k]||""} onChange={e=>setEditExam(p=>({...p,[f.k]:e.target.value}))} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13}}>
                      {f.opts.map(o=><option key={o}>{o}</option>)}
                    </select>
                  ):(
                    <input type={f.type} value={editExam[f.k]||""} onChange={e=>setEditExam(p=>({...p,[f.k]:e.target.value}))} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                  )}
                </div>
              ))}
            </div>
            <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:700,color:C.primary}}>Teacher's Note</label>
              <textarea value={editExam.teacher_note||""} onChange={e=>setEditExam(p=>({...p,teacher_note:e.target.value}))} rows={2} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,resize:"vertical"}}/>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={updateExam} disabled={saving} style={{flex:1,padding:10,background:saving?C.muted:C.primary,color:"white",border:"none",borderRadius:10,fontWeight:800,cursor:saving?"not-allowed":"pointer"}}>{saving?"Saving...":"💾 Save"}</button>
              <button onClick={()=>setEditExam(null)} style={{padding:"10px 20px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary},#0d9488)`,borderRadius:16,padding:"18px 24px",color:"white",boxShadow:`0 4px 20px ${C.primary}40`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10,marginBottom:exams.length>0?14:0}}>
          <div>
            <div style={{fontWeight:900,fontSize:20}}>📊 Results Management</div>
            <div style={{fontSize:13,opacity:.85,marginTop:2}}>{exams.length} exams · {totalResults} results · {avgScore}% avg</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>{setActiveTab("create");setShowWizard(true);setDrillView(null);setEnterExam(null);}}
              style={{background:"rgba(255,255,255,0.2)",border:"2px solid rgba(255,255,255,0.5)",color:"white",borderRadius:10,padding:"8px 16px",fontWeight:800,fontSize:12,cursor:"pointer"}}>
              ➕ New Exam
            </button>
            <button onClick={()=>{setActiveTab("enter");setEnterExam(null);setDrillView(null);setShowWizard(false);}}
              style={{background:"rgba(255,255,255,0.15)",border:"2px solid rgba(255,255,255,0.3)",color:"white",borderRadius:10,padding:"8px 16px",fontWeight:800,fontSize:12,cursor:"pointer"}}>
              ✏️ Enter Marks
            </button>
            <button onClick={()=>{setActiveTab("cards");setDrillView(null);setEnterExam(null);setShowWizard(false);}}
              style={{background:"rgba(255,255,255,0.15)",border:"2px solid rgba(255,255,255,0.3)",color:"white",borderRadius:10,padding:"8px 16px",fontWeight:800,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
              📄 Report Cards
              {publishedCardsCount>0&&<span style={{background:"rgba(255,255,255,0.3)",borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:900}}>{publishedCardsCount}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:12}}>
        {[
          {icon:"📝",label:"Total Exams",   value:exams.length,    color:C.primary,  bg:C.primaryPale, hint:"See all exams"},
          {icon:"✅",label:"Results In",    value:totalResults,    color:"#0d9488",  bg:"#f0fdfa",     hint:"By class"},
          {icon:"📈",label:"Class Average", value:`${avgScore}%`,  color:"#2563eb",  bg:"#eff6ff",     hint:"By subject"},
          {icon:"❌",label:"Failing",        value:failing,         color:"#e11d48",  bg:"#fff0f3",     hint:"See students"},
        ].map(k=>{
          const isActive = activeStat===k.label;
          return (
            <div key={k.label} onClick={()=>setActiveStat(isActive?null:k.label)} className="card-hover"
              style={{background:isActive?k.color:k.bg,borderRadius:14,padding:"14px 16px",
                border:`2px solid ${isActive?k.color:k.color+"20"}`,cursor:"pointer",
                transition:"all .18s",boxShadow:isActive?`0 4px 16px ${k.color}40`:"none"}}>
              <div style={{fontSize:20}}>{k.icon}</div>
              <div style={{fontSize:24,fontWeight:900,color:isActive?"white":k.color,marginTop:4}}>{k.value}</div>
              <div style={{fontSize:11,fontWeight:600,marginTop:2,color:isActive?"rgba(255,255,255,0.8)":C.muted}}>{k.label}</div>
              <div style={{fontSize:10,marginTop:3,color:isActive?"rgba(255,255,255,0.6)":k.color+"99",fontWeight:600}}>{isActive?"▲ collapse":"▼ "+k.hint}</div>
            </div>
          );
        })}
      </div>

      {/* Stat drill-down panel */}
      {activeStat&&(()=>{
        const GC=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
        const GR=(m,t)=>{const p=(m/t)*100;return [{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}].find(g=>p>=g.min)?.label||"F";};
        const Bar=({pct,color})=>(<div style={{flex:1,height:6,background:"#e5e7eb",borderRadius:3}}><div style={{width:`${Math.min(pct,100)}%`,height:"100%",background:color,borderRadius:3,transition:"width .4s"}}/></div>);

        if(activeStat==="Total Exams"){
          const sorted=[...exams].sort((a,b)=>new Date(b.exam_date||0)-new Date(a.exam_date||0));
          const ECOLOR={quiz:"#7c3aed",monthly:"#2563eb",midterm:"#d97706",final:C.primary};
          return(
            <div className="fade-up" style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 20px ${C.shadow}`}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontWeight:900,color:C.primary,fontSize:14}}>📝 All Exams</span>
                <span style={{marginLeft:"auto",fontSize:11,color:C.muted}}>{exams.length} total</span>
              </div>
              <div style={{maxHeight:320,overflowY:"auto"}}>
                {sorted.length===0&&<div style={{padding:32,textAlign:"center",color:C.muted}}>No exams created yet.</div>}
                {sorted.map((e,i)=>{
                  const ec=results.filter(r=>r.exam_id===e.id);
                  const avg=ec.length?Math.round(ec.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/ec.length):null;
                  const col=ECOLOR[e.exam_type]||C.muted;
                  return(
                    <div key={e.id} style={{display:"flex",alignItems:"center",gap:14,padding:"11px 20px",borderBottom:i<sorted.length-1?`1px solid ${C.border}`:"none",background:i%2?"#fafffe":"white"}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:col,flexShrink:0}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:C.text}}>{e.name}</div>
                        <div style={{fontSize:11,color:C.muted,marginTop:1}}>{e.class} · {e.exam_type||"—"}{e.exam_date?" · "+new Date(e.exam_date).toLocaleDateString("en-GB",{day:"numeric",month:"short"}):""}</div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:12,fontWeight:800,color:col}}>{ec.length} results</div>
                        {avg!==null&&<div style={{fontSize:11,color:GC(GR(avg,100))}}>{avg}% avg</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if(activeStat==="Results In"){
          const classes=[...new Set(results.map(r=>r.class).filter(Boolean))].sort();
          const rows=classes.map(cls=>{
            const cr=results.filter(r=>r.class===cls);
            const avg=cr.length?Math.round(cr.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/cr.length):0;
            const grade=GR(avg,100);
            return{cls,count:cr.length,avg,grade};
          }).sort((a,b)=>a.avg-b.avg);
          return(
            <div className="fade-up" style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 20px ${C.shadow}`}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center"}}>
                <span style={{fontWeight:900,color:"#0d9488",fontSize:14}}>✅ Results by Class</span>
                <span style={{marginLeft:"auto",fontSize:11,color:C.muted}}>{totalResults} results across {rows.length} classes</span>
              </div>
              <div style={{maxHeight:320,overflowY:"auto"}}>
                {rows.length===0&&<div style={{padding:32,textAlign:"center",color:C.muted}}>No results yet.</div>}
                {rows.map((r,i)=>(
                  <div key={r.cls} style={{display:"flex",alignItems:"center",gap:14,padding:"12px 20px",borderBottom:i<rows.length-1?`1px solid ${C.border}`:"none",background:i%2?"#fafffe":"white"}}>
                    <div style={{width:80,fontWeight:700,fontSize:13,color:C.text,flexShrink:0}}>{r.cls}</div>
                    <Bar pct={r.avg} color={GC(r.grade)}/>
                    <div style={{width:36,textAlign:"right",fontWeight:900,color:GC(r.grade),fontSize:13,flexShrink:0}}>{r.avg}%</div>
                    <div style={{width:26,textAlign:"center",fontWeight:800,fontSize:11,color:"white",background:GC(r.grade),borderRadius:6,padding:"2px 5px",flexShrink:0}}>{r.grade}</div>
                    <div style={{width:60,textAlign:"right",fontSize:11,color:C.muted,flexShrink:0}}>{r.count} results</div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if(activeStat==="Class Average"){
          const subs=[...new Set(results.map(r=>r.subject).filter(Boolean))].sort();
          const rows=subs.map(sub=>{
            const sr=results.filter(r=>r.subject===sub);
            const avg=sr.length?Math.round(sr.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/sr.length):0;
            return{sub,avg,count:sr.length,grade:GR(avg,100)};
          }).sort((a,b)=>a.avg-b.avg);
          return(
            <div className="fade-up" style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 20px ${C.shadow}`}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center"}}>
                <span style={{fontWeight:900,color:"#2563eb",fontSize:14}}>📈 Average by Subject</span>
                <span style={{marginLeft:"auto",fontSize:11,color:C.muted}}>Weakest first</span>
              </div>
              <div style={{maxHeight:320,overflowY:"auto"}}>
                {rows.length===0&&<div style={{padding:32,textAlign:"center",color:C.muted}}>No results yet.</div>}
                {rows.map((r,i)=>(
                  <div key={r.sub} style={{display:"flex",alignItems:"center",gap:14,padding:"11px 20px",borderBottom:i<rows.length-1?`1px solid ${C.border}`:"none",background:i%2?"#fafffe":"white"}}>
                    <div style={{width:130,fontWeight:700,fontSize:12,color:C.text,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.sub}</div>
                    <Bar pct={r.avg} color={GC(r.grade)}/>
                    <div style={{width:36,textAlign:"right",fontWeight:900,color:GC(r.grade),fontSize:13,flexShrink:0}}>{r.avg}%</div>
                    <div style={{width:26,textAlign:"center",fontWeight:800,fontSize:11,color:"white",background:GC(r.grade),borderRadius:6,padding:"2px 5px",flexShrink:0}}>{r.grade}</div>
                    <div style={{width:56,textAlign:"right",fontSize:11,color:C.muted,flexShrink:0}}>{r.count} results</div>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if(activeStat==="Failing"){
          const failRows=results
            .filter(r=>(r.marks_obtained/r.total_marks)<0.5)
            .map(r=>{
              const pct=Math.round((r.marks_obtained/r.total_marks)*100);
              const stu=students.find(s=>String(s.id)===String(r.student_id));
              return{...r,pct,grade:GR(r.marks_obtained,r.total_marks),name:stu?.full_name||stu?.name||"Unknown"};
            })
            .sort((a,b)=>a.pct-b.pct);
          return(
            <div className="fade-up" style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 4px 20px ${C.shadow}`}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontWeight:900,color:"#e11d48",fontSize:14}}>❌ Failing Results</span>
                <span style={{background:"#fee2e2",color:"#e11d48",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:800}}>{failRows.length}</span>
                <span style={{marginLeft:"auto",fontSize:11,color:C.muted}}>Worst first · pass mark 50%</span>
              </div>
              <div style={{maxHeight:360,overflowY:"auto"}}>
                {failRows.length===0&&<div style={{padding:32,textAlign:"center",color:C.muted}}>No failing results.</div>}
                {failRows.map((r,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 20px",borderBottom:i<failRows.length-1?`1px solid ${C.border}`:"none",background:i%2?"#fff5f5":"white"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</div>
                      <div style={{fontSize:11,color:C.muted,marginTop:1}}>{r.class} · {r.subject} · {r.exam_name||"—"}</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontWeight:900,fontSize:14,color:"#e11d48"}}>{r.pct}%</div>
                      <div style={{fontSize:10,color:"#e11d48",fontWeight:700}}>{r.marks_obtained}/{r.total_marks}</div>
                    </div>
                    <div style={{width:24,height:24,borderRadius:6,background:"#fee2e2",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:11,color:"#e11d48",flexShrink:0}}>{r.grade}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return null;
      })()}


      {/* Tabs */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {tabBtn("overview","Overview","📊")}
        {tabBtn("enter","Enter Marks","✏️")}
        {tabBtn("manage","Manage","⚙️")}
        {tabBtn("cards","Report Cards","📄")}
        <button onClick={()=>{setActiveTab("create");setShowWizard(true);setDrillView(null);setEnterExam(null);}}
          style={{padding:"8px 16px",borderRadius:10,border:`2px solid ${C.primary}`,background:C.primary,color:"white",fontWeight:800,fontSize:13,cursor:"pointer",marginLeft:"auto"}}>
          ➕ New Exam
        </button>
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab==="overview" && !drillView && (
        <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>

        {/* AI Class Analysis */}
        {results.length>0&&(
          <div style={{background:"white",borderRadius:16,padding:"18px 22px",boxShadow:`0 2px 12px ${C.shadow}`,border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:aiAnalysis?12:0}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:800,fontSize:14,color:C.text}}>🤖 AI Class Analysis</div>
                <div style={{fontSize:11,color:C.muted,marginTop:1}}>Critical overview of overall results — subjects, classes, pass rates</div>
              </div>
              <button onClick={generateClassAnalysis} disabled={aiAnalysisLoading}
                style={{background:aiAnalysisLoading?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:10,padding:"9px 18px",fontWeight:800,fontSize:12,cursor:aiAnalysisLoading?"not-allowed":"pointer",flexShrink:0,transition:"background .2s"}}>
                {aiAnalysisLoading?"⏳ Analysing…":"✨ Analyse Results"}
              </button>
            </div>
            {aiAnalysis&&(
              <div style={{background:C.bg,borderRadius:10,padding:"14px 16px",fontSize:13,lineHeight:1.7,color:C.text,borderLeft:`3px solid ${C.primary}`}}>
                {aiAnalysis}
              </div>
            )}
          </div>
        )}

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16}}>
          {/* Quiz card */}
          {[
            {key:"quiz",label:"Quizzes",icon:"⚡",color:"#7c3aed",bg:"#f5f3ff",arr:quizExams,examKey:"quiz"},
            {key:"monthly",label:"Monthly Tests",icon:"📅",color:"#2563eb",bg:"#eff6ff",arr:monthlyExams,examKey:"monthly"},
            {key:"midterm",label:"Midterm Exams",icon:"📋",color:"#d97706",bg:"#fffbeb",arr:midtermExams,examKey:"midterm"},
            {key:"final",label:"Final Exams",icon:"🎓",color:C.primary,bg:C.primaryPale,arr:finalExams,examKey:"final"},
          ].map(cat => {
            const catResults = results.filter(r => cat.arr.some(e=>e.id===r.exam_id));
            const avg = catResults.length ? Math.round(catResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/catResults.length) : null;
            const drillType = cat.key==="monthly" ? "monthly_months" : "term_classes";
            const drillPayload = cat.key==="monthly"
              ? {type:"monthly_months"}
              : {type:"term_classes",examKey:cat.examKey,label:cat.label,icon:cat.icon,color:cat.color,examsArr:cat.arr};
            return (
              <div key={cat.key} onClick={()=>cat.arr.length>0?setDrillView(drillPayload):null}
                style={{background:"white",borderRadius:16,padding:"20px 22px",boxShadow:`0 2px 12px ${C.shadow}`,border:`1px solid ${C.border}`,transition:"all .15s",cursor:cat.arr.length>0?"pointer":"default",opacity:cat.arr.length===0?0.65:1}}
                onMouseEnter={e=>{if(cat.arr.length>0){e.currentTarget.style.boxShadow="0 8px 28px rgba(0,0,0,0.1)";e.currentTarget.style.borderColor=cat.color+"60";}}}
                onMouseLeave={e=>{e.currentTarget.style.boxShadow=`0 2px 12px ${C.shadow}`;e.currentTarget.style.borderColor=C.border;}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                  <div style={{width:46,height:46,borderRadius:12,background:cat.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{cat.icon}</div>
                  <div>
                    <div style={{fontWeight:800,fontSize:15,color:cat.color}}>{cat.label}</div>
                    {cat.arr.length===0 && <div style={{fontSize:11,color:C.muted,marginTop:1}}>None created yet</div>}
                  </div>
                </div>
                <div style={{display:"flex",gap:16,alignItems:"flex-end"}}>
                  <div><div style={{fontSize:22,fontWeight:900,color:cat.color}}>{cat.arr.length}</div><div style={{fontSize:11,color:C.muted}}>Exams</div></div>
                  <div><div style={{fontSize:22,fontWeight:900,color:cat.color}}>{catResults.length}</div><div style={{fontSize:11,color:C.muted}}>Results</div></div>
                  {avg!==null&&<div><div style={{fontSize:22,fontWeight:900,color:gradeColor(getGrade(avg,100))}}>{avg}%</div><div style={{fontSize:11,color:C.muted}}>Avg</div></div>}
                  {cat.arr.length>0&&<div style={{marginLeft:"auto",fontSize:12,color:cat.color,fontWeight:700}}>Browse →</div>}
                  {cat.arr.length===0&&(
                    <button onClick={e=>{e.stopPropagation();setActiveTab("create");setShowWizard(true);}}
                      style={{marginLeft:"auto",background:cat.bg,color:cat.color,border:`1px solid ${cat.color}30`,borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>+ Create</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Drill-down */}
      {activeTab==="overview" && drillView && (
        <div style={{background:"white",borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
          {renderDrillView()}
        </div>
      )}

      {/* ── ENTER MARKS TAB ── */}
      {activeTab==="enter" && (
        <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>
          {!enterExam && (()=>{
            // Group exams by name+class so "Quiz 1" for Class 5 appears as ONE row (with all subjects inside)
            const MONTHS_LIST=["January","February","March","April","May","June","July","August","September","October","November","December"];
            const examMonth = e => {
              if(e.exam_date) { const d=new Date(e.exam_date); if(!isNaN(d)) return MONTHS_LIST[d.getMonth()]; }
              const m = MONTHS_LIST.find(mn=>e.name?.toLowerCase().includes(mn.toLowerCase()));
              return m||"";
            };
            const TYPE_ORDER={monthly:0,midterm:1,final:2,quiz:3};
            const filteredExams = exams.filter(e=>
              (mfType==="All"||(e.exam_type===mfType||(!e.exam_type&&e.name?.toLowerCase().includes(mfType))))&&
              (mfClass==="All"||e.class===mfClass)&&
              (mfMonth==="All"||examMonth(e)===mfMonth)
            );
            // Group: key = name__class
            const grouped = {};
            filteredExams.forEach(e=>{
              const key = e.name+"__"+e.class;
              if(!grouped[key]) grouped[key]={name:e.name,cls:e.class,exams:[],exam_type:e.exam_type,exam_date:e.exam_date,_month:examMonth(e)};
              grouped[key].exams.push(e);
            });
            const groups = Object.values(grouped).sort((a,b)=>{
              const ta=(TYPE_ORDER[a.exam_type]??9), tb=(TYPE_ORDER[b.exam_type]??9);
              if(ta!==tb) return ta-tb;
              const ma=MONTHS_LIST.indexOf(a._month), mb=MONTHS_LIST.indexOf(b._month);
              if(ma!==-1&&mb!==-1) return mb-ma;
              const da=new Date(a.exam_date||0), db=new Date(b.exam_date||0);
              return db-da||a.name.localeCompare(b.name);
            });
            const shownMonths = mfType==="monthly"||mfType==="All"
              ? [...new Set(Object.values(grouped).map(g=>g._month).filter(Boolean))].sort((a,b)=>MONTHS_LIST.indexOf(b)-MONTHS_LIST.indexOf(a))
              : [];
            return (
              <>
                <div style={{fontWeight:800,color:C.text,fontSize:15}}>Select an exam to enter marks:</div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:4}}>
                  {["All","monthly","midterm","final","quiz"].map(t=>(
                    <button key={t} onClick={()=>{setMfType(t);setMfMonth("All");}}
                      style={{padding:"6px 14px",borderRadius:8,border:`1.5px solid ${mfType===t?C.primary:C.border}`,background:mfType===t?C.primary:"white",color:mfType===t?"white":C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                      {t==="All"?"All Types":EXAM_TYPES.find(e=>e.id===t)?.icon+" "+EXAM_TYPES.find(e=>e.id===t)?.label}
                    </button>
                  ))}
                  <select value={mfClass} onChange={e=>setMfClass(e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12}}>
                    <option value="All">All Classes</option>
                    {ALLCLASSES.map(c=><option key={c}>{c}</option>)}
                  </select>
                  {shownMonths.length>0&&(
                    <select value={mfMonth} onChange={e=>setMfMonth(e.target.value)} style={{padding:"6px 12px",borderRadius:8,border:`1.5px solid ${mfMonth!=="All"?C.primary:C.border}`,fontSize:12,fontWeight:mfMonth!=="All"?700:400,color:mfMonth!=="All"?C.primary:undefined}}>
                      <option value="All">All Months</option>
                      {shownMonths.map(m=><option key={m}>{m}</option>)}
                    </select>
                  )}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {groups.map(g=>{
                    const et = EXAM_TYPES.find(t=>t.id===g.exam_type)||EXAM_TYPES[1];
                    const classCount = g.cls==="Hifz Only"
                      ? students.filter(s=>s.stream?.includes("Hifz")||s.stream?.includes("Both")).length
                      : students.filter(s=>(s.class||s["class"])===g.cls).length;
                    // Count unique students who have results in any exam in this group
                    const enteredStudents = new Set(results.filter(r=>g.exams.some(e=>e.id===r.exam_id)).map(r=>r.student_id)).size;
                    const pct = classCount ? Math.round((enteredStudents/classCount)*100) : 0;
                    return (
                      <div key={g.name+g.cls} style={{background:"white",borderRadius:14,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 2px 8px ${C.shadow}`}}>
                        <div style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
                          <div style={{width:44,height:44,borderRadius:12,background:et.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{et.icon}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontWeight:900,color:C.text,fontSize:14}}>{g.name}</div>
                            <div style={{fontSize:11,color:C.muted,marginTop:1}}>{g.cls} · {g.exams.length} subject{g.exams.length>1?"s":""}: {g.exams.map(e=>e.subject).join(", ")}</div>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                              <div style={{flex:1,height:5,background:C.border,borderRadius:3}}>
                                <div style={{width:`${pct}%`,height:"100%",background:pct===100?C.primary:pct>60?"#0d9488":"#d97706",borderRadius:3,transition:"width .3s"}}/>
                              </div>
                              <span style={{fontSize:10,color:C.muted,whiteSpace:"nowrap",fontWeight:700}}>{enteredStudents}/{classCount} students · {pct}%</span>
                            </div>
                          </div>
                          <button onClick={()=>setEnterExam(g.exams[0])}
                            style={{background:C.primary,color:"white",border:"none",borderRadius:10,padding:"9px 18px",fontWeight:800,fontSize:13,cursor:"pointer",flexShrink:0,boxShadow:`0 2px 8px ${C.shadow}`}}>
                            ✏️ Open Sheet
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {groups.length===0 && (
                    <div style={{padding:40,textAlign:"center",color:C.muted,fontSize:14,background:C.bg,borderRadius:12}}>
                      No exams found. <button onClick={()=>{setActiveTab("create");setShowWizard(true);}} style={{color:C.primary,background:"none",border:"none",fontWeight:700,cursor:"pointer"}}>Create one →</button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
          {enterExam && (
            <EnterMarksPanel
              exam={enterExam} students={students} results={results}
              onSave={async (rows) => { await refetchAll(); showToast(`${rows.length} marks saved`); setEnterExam(null); }}
              onClose={()=>setEnterExam(null)}
            />
          )}
        </div>
      )}

      {/* ── CREATE EXAM TAB ── */}
      {activeTab==="create" && (
        <div className="fade-up">
          {showWizard ? (
            <ExamWizard
              subjects={subjects} students={students}
              onDone={(data, name, cls, examType) => {
                setExams(p=>[...p,...data]);
                showToast(`${data.length} exam(s) created for "${name}"`);
                setShowWizard(false);
                setActiveTab("enter");
              }}
              onCancel={()=>setShowWizard(false)}
            />
          ) : (
            <div style={{textAlign:"center",padding:"40px 20px",background:"white",borderRadius:16,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:48,marginBottom:12}}>📝</div>
              <div style={{fontWeight:800,fontSize:18,color:C.text,marginBottom:8}}>Create a New Exam</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:24}}>4-step wizard: choose class → exam type → details → subjects</div>
              <button onClick={()=>setShowWizard(true)}
                style={{background:`linear-gradient(135deg,${C.primary},#0d5e35)`,color:"white",border:"none",borderRadius:12,padding:"12px 32px",fontWeight:800,fontSize:14,cursor:"pointer",boxShadow:`0 4px 16px ${C.shadow}`}}>
                ➕ Start Wizard
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── MANAGE EXAMS TAB ── */}
      {activeTab==="manage" && (
        <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Filters */}
          <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <input placeholder="🔍 Search exams..." value={mfSearch} onChange={e=>setMfSearch(e.target.value)}
              style={{flex:1,minWidth:180,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
            <select value={mfClass} onChange={e=>setMfClass(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}>
              <option value="All">All Classes</option>
              {ALLCLASSES.map(c=><option key={c}>{c}</option>)}
            </select>
            <select value={mfType} onChange={e=>setMfType(e.target.value)} style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}>
              <option value="All">All Types</option>
              {EXAM_TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
            </select>
          </div>

          {/* Exam list */}
          {exams
            .filter(e=>(mfClass==="All"||e.class===mfClass)&&(mfType==="All"||(e.exam_type===mfType))&&(!mfSearch||e.name?.toLowerCase().includes(mfSearch.toLowerCase())||e.subject?.toLowerCase().includes(mfSearch.toLowerCase())))
            .sort((a,b)=>new Date(b.exam_date||0)-new Date(a.exam_date||0))
            .map(exam => {
              const examResults = results.filter(r=>r.exam_id===exam.id);
              const classCount = exam.class==="Hifz Only"
                ? students.filter(s=>s.stream?.includes("Hifz")||s.stream?.includes("Both")).length
                : students.filter(s=>(s.class||s["class"])===exam.class).length;
              const pct = classCount ? Math.round((examResults.length/classCount)*100) : 0;
              const avg = examResults.length ? Math.round(examResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/examResults.length) : null;
              const et = EXAM_TYPES.find(t=>t.id===exam.exam_type)||EXAM_TYPES[1];
              return (
                <div key={exam.id} style={{background:"white",borderRadius:12,padding:"14px 18px",border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14,boxShadow:`0 1px 6px ${C.shadow}`}}>
                  <div style={{width:40,height:40,borderRadius:10,background:et.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{et.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:800,color:C.text,fontSize:13}}>{exam.name}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>{exam.class} · {exam.subject} · {exam.total_marks} marks{exam.exam_date&&" · "+exam.exam_date}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:5}}>
                      <div style={{flex:1,height:3,background:C.border,borderRadius:2}}>
                        <div style={{width:`${pct}%`,height:"100%",background:pct===100?C.primary:C.teal,borderRadius:2}}/>
                      </div>
                      <span style={{fontSize:10,color:C.muted,whiteSpace:"nowrap"}}>{pct}% entered</span>
                      {avg!==null&&<span style={{fontSize:11,fontWeight:800,color:gradeColor(getGrade(avg,100))}}>{avg}% avg</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,flexShrink:0}}>
                    <button onClick={()=>{setEnterExam(exam);setActiveTab("enter");}}
                      style={{background:C.primaryPale,color:C.primary,border:`1px solid ${C.primaryMid}`,borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✏️ Marks</button>
                    <button onClick={()=>setEditExam({...exam})}
                      style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>📝 Edit</button>
                    <button onClick={async()=>{ if(!window.confirm("Clear all marks for this exam? Exam is kept, only results removed.")) return; const {error}=await supabase.from("results").delete().eq("exam_id",exam.id); if(!error){await refetchAll();showToast("Marks cleared — re-enter anytime");}else showToast("Error: "+error.message,false); }}
                      style={{background:"#fffbeb",color:"#d97706",border:"1px solid #fde68a",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}} title="Clear marks (keeps exam)">🔄 Clear</button>
                    <button onClick={()=>deleteExam(exam.id)}
                      style={{background:"#fff0f3",color:C.rose,border:"1px solid #fda4af",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}} title="Delete exam and all marks">🗑️</button>
                  </div>
                </div>
              );
            })}
          {exams.filter(e=>(mfClass==="All"||e.class===mfClass)&&(mfType==="All"||(e.exam_type===mfType))&&(!mfSearch||e.name?.toLowerCase().includes(mfSearch.toLowerCase())||e.subject?.toLowerCase().includes(mfSearch.toLowerCase()))).length===0&&(
            <div style={{padding:32,textAlign:"center",color:C.muted,fontSize:14,background:C.bg,borderRadius:12}}>No exams found.</div>
          )}
        </div>
      )}

      {/* ── REPORT CARDS TAB ── */}
      {activeTab==="cards" && (
        <ReportCardManagement students={students} results={results} attendance={attendance} showToast={showToast} onView={(stu,type,month)=>setReportData({student:stu,reportType:type||"monthly",monthLabel:month||null})}/>
      )}

    </div>
  );
}

// -- HIFZ DATA -------------------------------------------------------
// Full 30 Juz with Arabic names, surah list, and page ranges (15-line Quran, 604 pages)
const QURAN_JUZ = [
  {n:1,  ar:"الم",         name:"Alif Lam Meem",        start_surah:1,  end_surah:2,  start_page:1,   end_page:21,  surahs:[{n:1,name:"Al-Fatihah",ar:"الفاتحة",ayat:7},{n:2,name:"Al-Baqarah",ar:"البقرة",ayat:286,partial:true}]},
  {n:2,  ar:"سَيَقُولُ",   name:"Sayaqool",              start_surah:2,  end_surah:2,  start_page:22,  end_page:41,  surahs:[{n:2,name:"Al-Baqarah",ar:"البقرة",ayat:286,partial:true}]},
  {n:3,  ar:"تِلْكَ الرُّسُلُ",name:"Tilka Ar-Rusul",  start_surah:2,  end_surah:3,  start_page:42,  end_page:61,  surahs:[{n:2,name:"Al-Baqarah",ar:"البقرة",ayat:286,partial:true},{n:3,name:"Al-Imran",ar:"آل عمران",ayat:200,partial:true}]},
  {n:4,  ar:"لَنْ تَنَالُوا",name:"Lan Tanaloo",        start_surah:3,  end_surah:4,  start_page:62,  end_page:81,  surahs:[{n:3,name:"Al-Imran",ar:"آل عمران",ayat:200,partial:true},{n:4,name:"An-Nisa",ar:"النساء",ayat:176,partial:true}]},
  {n:5,  ar:"وَالْمُحْصَنَاتُ",name:"Wal Mohsanat",   start_surah:4,  end_surah:4,  start_page:82,  end_page:101, surahs:[{n:4,name:"An-Nisa",ar:"النساء",ayat:176,partial:true}]},
  {n:6,  ar:"لَا يُحِبُّ اللَّهُ",name:"La Yuhibbullah",start_surah:4, end_surah:5,  start_page:102, end_page:121, surahs:[{n:4,name:"An-Nisa",ar:"النساء",ayat:176,partial:true},{n:5,name:"Al-Ma'idah",ar:"المائدة",ayat:120,partial:true}]},
  {n:7,  ar:"وَإِذَا سَمِعُوا",name:"Wa Itha Samiu",  start_surah:5,  end_surah:6,  start_page:122, end_page:141, surahs:[{n:5,name:"Al-Ma'idah",ar:"المائدة",ayat:120,partial:true},{n:6,name:"Al-An'am",ar:"الأنعام",ayat:165,partial:true}]},
  {n:8,  ar:"وَلَوْ أَنَّنَا",name:"Wa Law Annana",   start_surah:6,  end_surah:7,  start_page:142, end_page:161, surahs:[{n:6,name:"Al-An'am",ar:"الأنعام",ayat:165,partial:true},{n:7,name:"Al-A'raf",ar:"الأعراف",ayat:206,partial:true}]},
  {n:9,  ar:"قَالَ الْمَلَأُ",name:"Qalal Malao",     start_surah:7,  end_surah:8,  start_page:162, end_page:181, surahs:[{n:7,name:"Al-A'raf",ar:"الأعراف",ayat:206,partial:true},{n:8,name:"Al-Anfal",ar:"الأنفال",ayat:75,partial:true}]},
  {n:10, ar:"وَاعْلَمُوا",   name:"Wa Alamu",           start_surah:8,  end_surah:9,  start_page:182, end_page:201, surahs:[{n:8,name:"Al-Anfal",ar:"الأنفال",ayat:75,partial:true},{n:9,name:"At-Tawbah",ar:"التوبة",ayat:129,partial:true}]},
  {n:11, ar:"يَعْتَذِرُونَ",  name:"Yatazeroon",         start_surah:9,  end_surah:11, start_page:202, end_page:221, surahs:[{n:9,name:"At-Tawbah",ar:"التوبة",ayat:129,partial:true},{n:10,name:"Yunus",ar:"يونس",ayat:109},{n:11,name:"Hud",ar:"هود",ayat:123,partial:true}]},
  {n:12, ar:"وَمَا مِنْ دَابَّةٍ",name:"Wa Ma Min Dabbah",start_surah:11,end_surah:12,start_page:222,end_page:241, surahs:[{n:11,name:"Hud",ar:"هود",ayat:123,partial:true},{n:12,name:"Yusuf",ar:"يوسف",ayat:111,partial:true}]},
  {n:13, ar:"وَمَا أُبَرِّئُ",name:"Wa Ma Ubarrio",     start_surah:12, end_surah:14, start_page:242, end_page:261, surahs:[{n:12,name:"Yusuf",ar:"يوسف",ayat:111,partial:true},{n:13,name:"Ar-Ra'd",ar:"الرعد",ayat:43},{n:14,name:"Ibrahim",ar:"إبراهيم",ayat:52}]},
  {n:14, ar:"رُبَمَا",         name:"Rubama",              start_surah:15, end_surah:16, start_page:262, end_page:281, surahs:[{n:15,name:"Al-Hijr",ar:"الحجر",ayat:99},{n:16,name:"An-Nahl",ar:"النحل",ayat:128,partial:true}]},
  {n:15, ar:"سُبْحَانَ الَّذِي",name:"Subhanallazi",    start_surah:17, end_surah:18, start_page:282, end_page:301, surahs:[{n:17,name:"Al-Isra",ar:"الإسراء",ayat:111},{n:18,name:"Al-Kahf",ar:"الكهف",ayat:110,partial:true}]},
  {n:16, ar:"قَالَ أَلَمْ",    name:"Qal Alam",            start_surah:18, end_surah:20, start_page:302, end_page:321, surahs:[{n:18,name:"Al-Kahf",ar:"الكهف",ayat:110,partial:true},{n:19,name:"Maryam",ar:"مريم",ayat:98},{n:20,name:"Ta-Ha",ar:"طه",ayat:135,partial:true}]},
  {n:17, ar:"اقْتَرَبَ",       name:"Iqtaraba",            start_surah:21, end_surah:22, start_page:322, end_page:341, surahs:[{n:21,name:"Al-Anbiya",ar:"الأنبياء",ayat:112},{n:22,name:"Al-Hajj",ar:"الحج",ayat:78,partial:true}]},
  {n:18, ar:"قَدْ أَفْلَحَ",   name:"Qad Aflaha",          start_surah:23, end_surah:25, start_page:342, end_page:361, surahs:[{n:23,name:"Al-Muminun",ar:"المؤمنون",ayat:118},{n:24,name:"An-Nur",ar:"النور",ayat:64},{n:25,name:"Al-Furqan",ar:"الفرقان",ayat:77,partial:true}]},
  {n:19, ar:"وَقَالَ الَّذِينَ",name:"Wa Qalallazina",  start_surah:25, end_surah:27, start_page:362, end_page:381, surahs:[{n:25,name:"Al-Furqan",ar:"الفرقان",ayat:77,partial:true},{n:26,name:"Ash-Shu'ara",ar:"الشعراء",ayat:227},{n:27,name:"An-Naml",ar:"النمل",ayat:93,partial:true}]},
  {n:20, ar:"أَمَّنْ خَلَقَ",  name:"Amman Khalaq",        start_surah:27, end_surah:29, start_page:382, end_page:401, surahs:[{n:27,name:"An-Naml",ar:"النمل",ayat:93,partial:true},{n:28,name:"Al-Qasas",ar:"القصص",ayat:88},{n:29,name:"Al-Ankabut",ar:"العنكبوت",ayat:69,partial:true}]},
  {n:21, ar:"اتْلُ مَا أُوحِيَ",name:"Otlu Ma Oohiya", start_surah:29, end_surah:33, start_page:402, end_page:421, surahs:[{n:29,name:"Al-Ankabut",ar:"العنكبوت",ayat:69,partial:true},{n:30,name:"Ar-Rum",ar:"الروم",ayat:60},{n:31,name:"Luqman",ar:"لقمان",ayat:34},{n:32,name:"As-Sajdah",ar:"السجدة",ayat:30},{n:33,name:"Al-Ahzab",ar:"الأحزاب",ayat:73,partial:true}]},
  {n:22, ar:"وَمَنْ يَقْنُتْ",  name:"Wa Man Yaqnut",      start_surah:33, end_surah:36, start_page:422, end_page:441, surahs:[{n:33,name:"Al-Ahzab",ar:"الأحزاب",ayat:73,partial:true},{n:34,name:"Saba",ar:"سبأ",ayat:54},{n:35,name:"Fatir",ar:"فاطر",ayat:45},{n:36,name:"Ya-Sin",ar:"يس",ayat:83,partial:true}]},
  {n:23, ar:"وَمَا لِيَ",       name:"Wa Ma Liya",          start_surah:36, end_surah:39, start_page:442, end_page:461, surahs:[{n:36,name:"Ya-Sin",ar:"يس",ayat:83,partial:true},{n:37,name:"As-Saffat",ar:"الصافات",ayat:182},{n:38,name:"Sad",ar:"ص",ayat:88},{n:39,name:"Az-Zumar",ar:"الزمر",ayat:75,partial:true}]},
  {n:24, ar:"فَمَنْ أَظْلَمُ",  name:"Faman Azlam",         start_surah:39, end_surah:41, start_page:462, end_page:481, surahs:[{n:39,name:"Az-Zumar",ar:"الزمر",ayat:75,partial:true},{n:40,name:"Ghafir",ar:"غافر",ayat:85},{n:41,name:"Fussilat",ar:"فصلت",ayat:54,partial:true}]},
  {n:25, ar:"إِلَيْهِ يُرَدُّ",name:"Elahe Yuraddo",       start_surah:41, end_surah:45, start_page:482, end_page:501, surahs:[{n:41,name:"Fussilat",ar:"فصلت",ayat:54,partial:true},{n:42,name:"Ash-Shura",ar:"الشورى",ayat:53},{n:43,name:"Az-Zukhruf",ar:"الزخرف",ayat:89},{n:44,name:"Ad-Dukhan",ar:"الدخان",ayat:59},{n:45,name:"Al-Jathiyah",ar:"الجاثية",ayat:37,partial:true}]},
  {n:26, ar:"حم",              name:"Ha Meem",               start_surah:46, end_surah:51, start_page:502, end_page:521, surahs:[{n:46,name:"Al-Ahqaf",ar:"الأحقاف",ayat:35},{n:47,name:"Muhammad",ar:"محمد",ayat:38},{n:48,name:"Al-Fath",ar:"الفتح",ayat:29},{n:49,name:"Al-Hujurat",ar:"الحجرات",ayat:18},{n:50,name:"Qaf",ar:"ق",ayat:45},{n:51,name:"Adh-Dhariyat",ar:"الذاريات",ayat:60,partial:true}]},
  {n:27, ar:"قَالَ فَمَا خَطْبُكُمْ",name:"Qala Fama Khatbukum",start_surah:51,end_surah:57,start_page:522,end_page:541,surahs:[{n:51,name:"Adh-Dhariyat",ar:"الذاريات",ayat:60,partial:true},{n:52,name:"At-Tur",ar:"الطور",ayat:49},{n:53,name:"An-Najm",ar:"النجم",ayat:62},{n:54,name:"Al-Qamar",ar:"القمر",ayat:55},{n:55,name:"Ar-Rahman",ar:"الرحمن",ayat:78},{n:56,name:"Al-Waqiah",ar:"الواقعة",ayat:96},{n:57,name:"Al-Hadid",ar:"الحديد",ayat:29}]},
  {n:28, ar:"قَدْ سَمِعَ اللَّهُ",name:"Qad Sami Allah",   start_surah:58, end_surah:66, start_page:542, end_page:561, surahs:[{n:58,name:"Al-Mujadila",ar:"المجادلة",ayat:22},{n:59,name:"Al-Hashr",ar:"الحشر",ayat:24},{n:60,name:"Al-Mumtahanah",ar:"الممتحنة",ayat:13},{n:61,name:"As-Saf",ar:"الصف",ayat:14},{n:62,name:"Al-Jumuah",ar:"الجمعة",ayat:11},{n:63,name:"Al-Munafiqun",ar:"المنافقون",ayat:11},{n:64,name:"At-Taghabun",ar:"التغابن",ayat:18},{n:65,name:"At-Talaq",ar:"الطلاق",ayat:12},{n:66,name:"At-Tahrim",ar:"التحريم",ayat:12}]},
  {n:29, ar:"تَبَارَكَ الَّذِي",name:"Tabaraka Allazi",     start_surah:67, end_surah:77, start_page:562, end_page:581, surahs:[{n:67,name:"Al-Mulk",ar:"الملك",ayat:30},{n:68,name:"Al-Qalam",ar:"القلم",ayat:52},{n:69,name:"Al-Haqqah",ar:"الحاقة",ayat:52},{n:70,name:"Al-Maarij",ar:"المعارج",ayat:44},{n:71,name:"Nuh",ar:"نوح",ayat:28},{n:72,name:"Al-Jinn",ar:"الجن",ayat:28},{n:73,name:"Al-Muzzammil",ar:"المزمل",ayat:20},{n:74,name:"Al-Muddaththir",ar:"المدثر",ayat:56},{n:75,name:"Al-Qiyamah",ar:"القيامة",ayat:40},{n:76,name:"Al-Insan",ar:"الإنسان",ayat:31},{n:77,name:"Al-Mursalat",ar:"المرسلات",ayat:50}]},
  {n:30, ar:"عَمَّ",            name:"Amma",                  start_surah:78, end_surah:114,start_page:582, end_page:604, surahs:[{n:78,name:"An-Naba",ar:"النبأ",ayat:40},{n:79,name:"An-Naziat",ar:"النازعات",ayat:46},{n:80,name:"Abasa",ar:"عبس",ayat:42},{n:81,name:"At-Takwir",ar:"التكوير",ayat:29},{n:82,name:"Al-Infitar",ar:"الانفطار",ayat:19},{n:83,name:"Al-Mutaffifin",ar:"المطففين",ayat:36},{n:84,name:"Al-Inshiqaq",ar:"الانشقاق",ayat:25},{n:85,name:"Al-Buruj",ar:"البروج",ayat:22},{n:86,name:"At-Tariq",ar:"الطارق",ayat:17},{n:87,name:"Al-Ala",ar:"الأعلى",ayat:19},{n:88,name:"Al-Ghashiyah",ar:"الغاشية",ayat:26},{n:89,name:"Al-Fajr",ar:"الفجر",ayat:30},{n:90,name:"Al-Balad",ar:"البلد",ayat:20},{n:91,name:"Ash-Shams",ar:"الشمس",ayat:15},{n:92,name:"Al-Layl",ar:"الليل",ayat:21},{n:93,name:"Ad-Duha",ar:"الضحى",ayat:11},{n:94,name:"Ash-Sharh",ar:"الشرح",ayat:8},{n:95,name:"At-Tin",ar:"التين",ayat:8},{n:96,name:"Al-Alaq",ar:"العلق",ayat:19},{n:97,name:"Al-Qadr",ar:"القدر",ayat:5},{n:98,name:"Al-Bayyinah",ar:"البينة",ayat:8},{n:99,name:"Az-Zalzalah",ar:"الزلزلة",ayat:8},{n:100,name:"Al-Adiyat",ar:"العاديات",ayat:11},{n:101,name:"Al-Qariah",ar:"القارعة",ayat:11},{n:102,name:"At-Takathur",ar:"التكاثر",ayat:8},{n:103,name:"Al-Asr",ar:"العصر",ayat:3},{n:104,name:"Al-Humazah",ar:"الهمزة",ayat:9},{n:105,name:"Al-Fil",ar:"الفيل",ayat:5},{n:106,name:"Quraysh",ar:"قريش",ayat:4},{n:107,name:"Al-Maun",ar:"الماعون",ayat:7},{n:108,name:"Al-Kawthar",ar:"الكوثر",ayat:3},{n:109,name:"Al-Kafirun",ar:"الكافرون",ayat:6},{n:110,name:"An-Nasr",ar:"النصر",ayat:3},{n:111,name:"Al-Masad",ar:"المسد",ayat:5},{n:112,name:"Al-Ikhlas",ar:"الإخلاص",ayat:4},{n:113,name:"Al-Falaq",ar:"الفلق",ayat:5},{n:114,name:"An-Nas",ar:"الناس",ayat:6}]}
];

// Helper: get Juz from page number
const getJuzFromPage = (page) => QURAN_JUZ.find(j=>page>=j.start_page&&page<=j.end_page)||QURAN_JUZ[0];
// Quran line settings → pages per Juz
const QURAN_LINE_SETTINGS = [
  {lines:15, label:"15-line (standard)", pagesPerJuz:20, totalPages:604},
  {lines:16, label:"16-line",            pagesPerJuz:19, totalPages:570},
  {lines:18, label:"18-line",            pagesPerJuz:17, totalPages:507},
  {lines:13, label:"13-line",            pagesPerJuz:23, totalPages:604},
];
const JUZ_PAGES_15LINE = 20;
const JUZ_PAGES_13LINE = 23;
const juzPages = (juz) => JUZ_PAGES_15LINE;
const TOTAL_PAGES = 604;

// Juz boundary data: exact surah+ayat where each Juz starts
// Format: {juz, surah_n, surah_name, from_ayat, to_ayat (end of juz)}
const JUZ_BOUNDARIES = [
  {juz:1,  entries:[{s:1,name:"Al-Fatihah",from:1,to:7},{s:2,name:"Al-Baqarah",from:1,to:141}]},
  {juz:2,  entries:[{s:2,name:"Al-Baqarah",from:142,to:252}]},
  {juz:3,  entries:[{s:2,name:"Al-Baqarah",from:253,to:286},{s:3,name:"Al-Imran",from:1,to:92}]},
  {juz:4,  entries:[{s:3,name:"Al-Imran",from:93,to:200},{s:4,name:"An-Nisa",from:1,to:23}]},
  {juz:5,  entries:[{s:4,name:"An-Nisa",from:24,to:147}]},
  {juz:6,  entries:[{s:4,name:"An-Nisa",from:148,to:176},{s:5,name:"Al-Ma'idah",from:1,to:81}]},
  {juz:7,  entries:[{s:5,name:"Al-Ma'idah",from:82,to:120},{s:6,name:"Al-An'am",from:1,to:110}]},
  {juz:8,  entries:[{s:6,name:"Al-An'am",from:111,to:165},{s:7,name:"Al-A'raf",from:1,to:87}]},
  {juz:9,  entries:[{s:7,name:"Al-A'raf",from:88,to:206},{s:8,name:"Al-Anfal",from:1,to:40}]},
  {juz:10, entries:[{s:8,name:"Al-Anfal",from:41,to:75},{s:9,name:"At-Tawbah",from:1,to:92}]},
  {juz:11, entries:[{s:9,name:"At-Tawbah",from:93,to:129},{s:10,name:"Yunus",from:1,to:109},{s:11,name:"Hud",from:1,to:5}]},
  {juz:12, entries:[{s:11,name:"Hud",from:6,to:123},{s:12,name:"Yusuf",from:1,to:52}]},
  {juz:13, entries:[{s:12,name:"Yusuf",from:53,to:111},{s:13,name:"Ar-Ra'd",from:1,to:43},{s:14,name:"Ibrahim",from:1,to:52}]},
  {juz:14, entries:[{s:15,name:"Al-Hijr",from:1,to:99},{s:16,name:"An-Nahl",from:1,to:128}]},
  {juz:15, entries:[{s:17,name:"Al-Isra",from:1,to:111},{s:18,name:"Al-Kahf",from:1,to:74}]},
  {juz:16, entries:[{s:18,name:"Al-Kahf",from:75,to:110},{s:19,name:"Maryam",from:1,to:98},{s:20,name:"Ta-Ha",from:1,to:135}]},
  {juz:17, entries:[{s:21,name:"Al-Anbiya",from:1,to:112},{s:22,name:"Al-Hajj",from:1,to:78}]},
  {juz:18, entries:[{s:23,name:"Al-Muminun",from:1,to:118},{s:24,name:"An-Nur",from:1,to:64},{s:25,name:"Al-Furqan",from:1,to:20}]},
  {juz:19, entries:[{s:25,name:"Al-Furqan",from:21,to:77},{s:26,name:"Ash-Shu'ara",from:1,to:227},{s:27,name:"An-Naml",from:1,to:55}]},
  {juz:20, entries:[{s:27,name:"An-Naml",from:56,to:93},{s:28,name:"Al-Qasas",from:1,to:88},{s:29,name:"Al-Ankabut",from:1,to:45}]},
  {juz:21, entries:[{s:29,name:"Al-Ankabut",from:46,to:69},{s:30,name:"Ar-Rum",from:1,to:60},{s:31,name:"Luqman",from:1,to:34},{s:32,name:"As-Sajdah",from:1,to:30},{s:33,name:"Al-Ahzab",from:1,to:30}]},
  {juz:22, entries:[{s:33,name:"Al-Ahzab",from:31,to:73},{s:34,name:"Saba",from:1,to:54},{s:35,name:"Fatir",from:1,to:45},{s:36,name:"Ya-Sin",from:1,to:27}]},
  {juz:23, entries:[{s:36,name:"Ya-Sin",from:28,to:83},{s:37,name:"As-Saffat",from:1,to:182},{s:38,name:"Sad",from:1,to:88},{s:39,name:"Az-Zumar",from:1,to:31}]},
  {juz:24, entries:[{s:39,name:"Az-Zumar",from:32,to:75},{s:40,name:"Ghafir",from:1,to:85},{s:41,name:"Fussilat",from:1,to:46}]},
  {juz:25, entries:[{s:41,name:"Fussilat",from:47,to:54},{s:42,name:"Ash-Shura",from:1,to:53},{s:43,name:"Az-Zukhruf",from:1,to:89},{s:44,name:"Ad-Dukhan",from:1,to:59},{s:45,name:"Al-Jathiyah",from:1,to:37}]},
  {juz:26, entries:[{s:46,name:"Al-Ahqaf",from:1,to:35},{s:47,name:"Muhammad",from:1,to:38},{s:48,name:"Al-Fath",from:1,to:29},{s:49,name:"Al-Hujurat",from:1,to:18},{s:50,name:"Qaf",from:1,to:45},{s:51,name:"Adh-Dhariyat",from:1,to:30}]},
  {juz:27, entries:[{s:51,name:"Adh-Dhariyat",from:31,to:60},{s:52,name:"At-Tur",from:1,to:49},{s:53,name:"An-Najm",from:1,to:62},{s:54,name:"Al-Qamar",from:1,to:55},{s:55,name:"Ar-Rahman",from:1,to:78},{s:56,name:"Al-Waqiah",from:1,to:96},{s:57,name:"Al-Hadid",from:1,to:29}]},
  {juz:28, entries:[{s:58,name:"Al-Mujadila",from:1,to:22},{s:59,name:"Al-Hashr",from:1,to:24},{s:60,name:"Al-Mumtahanah",from:1,to:13},{s:61,name:"As-Saf",from:1,to:14},{s:62,name:"Al-Jumuah",from:1,to:11},{s:63,name:"Al-Munafiqun",from:1,to:11},{s:64,name:"At-Taghabun",from:1,to:18},{s:65,name:"At-Talaq",from:1,to:12},{s:66,name:"At-Tahrim",from:1,to:12}]},
  {juz:29, entries:[{s:67,name:"Al-Mulk",from:1,to:30},{s:68,name:"Al-Qalam",from:1,to:52},{s:69,name:"Al-Haqqah",from:1,to:52},{s:70,name:"Al-Maarij",from:1,to:44},{s:71,name:"Nuh",from:1,to:28},{s:72,name:"Al-Jinn",from:1,to:28},{s:73,name:"Al-Muzzammil",from:1,to:20},{s:74,name:"Al-Muddaththir",from:1,to:56},{s:75,name:"Al-Qiyamah",from:1,to:40},{s:76,name:"Al-Insan",from:1,to:31},{s:77,name:"Al-Mursalat",from:1,to:50}]},
  {juz:30, entries:[{s:78,name:"An-Naba",from:1,to:40},{s:79,name:"An-Naziat",from:1,to:46},{s:80,name:"Abasa",from:1,to:42},{s:81,name:"At-Takwir",from:1,to:29},{s:82,name:"Al-Infitar",from:1,to:19},{s:83,name:"Al-Mutaffifin",from:1,to:36},{s:84,name:"Al-Inshiqaq",from:1,to:25},{s:85,name:"Al-Buruj",from:1,to:22},{s:86,name:"At-Tariq",from:1,to:17},{s:87,name:"Al-Ala",from:1,to:19},{s:88,name:"Al-Ghashiyah",from:1,to:26},{s:89,name:"Al-Fajr",from:1,to:30},{s:90,name:"Al-Balad",from:1,to:20},{s:91,name:"Ash-Shams",from:1,to:15},{s:92,name:"Al-Layl",from:1,to:21},{s:93,name:"Ad-Duha",from:1,to:11},{s:94,name:"Ash-Sharh",from:1,to:8},{s:95,name:"At-Tin",from:1,to:8},{s:96,name:"Al-Alaq",from:1,to:19},{s:97,name:"Al-Qadr",from:1,to:5},{s:98,name:"Al-Bayyinah",from:1,to:8},{s:99,name:"Az-Zalzalah",from:1,to:8},{s:100,name:"Al-Adiyat",from:1,to:11},{s:101,name:"Al-Qariah",from:1,to:11},{s:102,name:"At-Takathur",from:1,to:8},{s:103,name:"Al-Asr",from:1,to:3},{s:104,name:"Al-Humazah",from:1,to:9},{s:105,name:"Al-Fil",from:1,to:5},{s:106,name:"Quraysh",from:1,to:4},{s:107,name:"Al-Maun",from:1,to:7},{s:108,name:"Al-Kawthar",from:1,to:3},{s:109,name:"Al-Kafirun",from:1,to:6},{s:110,name:"An-Nasr",from:1,to:3},{s:111,name:"Al-Masad",from:1,to:5},{s:112,name:"Al-Ikhlas",from:1,to:4},{s:113,name:"Al-Falaq",from:1,to:5},{s:114,name:"An-Nas",from:1,to:6}]},
];
// Get all ayat entries for a given Juz
const getJuzAyatEntries = (juzNum) => JUZ_BOUNDARIES.find(j=>j.juz===juzNum)?.entries||[];


// Default targets per Juz group (pages/day). Admin can override per-Juz.
// Default: Juz 1-10 lighter, 11-20 medium, 21-30 heavier
// 15-line Quran: 1 Juz = 20 pages, quarter = 5 pages, half = 10 pages
// Default targets (pages/day). Note: 20p target in manzil = 1 full Juz
const DEFAULT_JUZ_TARGETS = (juzNum) => ({
  sabaq:{value:5,unit:"pages"},
  sabqi:{value:10,unit:"pages"},
  manzil:{value:20,unit:"pages"}
});

// -- HIFZ DAILY LOG --------------------------------------------------
// -- HIFZ MANAGEMENT ------------------------------------------------
// ── Hifz Weekly Report ───────────────────────────────────────────
function HifzWeeklyReports({ students, getTarget }) {
  const P = "#4c1d95";
  const hifzStudents = students.filter(s=>(s.stream||"").includes("Hifz")||(s.stream||"").includes("Both"));
  const [selStu, setSelStu] = React.useState(null); // selected student object
  const [reports, setReports] = React.useState([]);
  const [logs, setLogs]       = React.useState([]);
  const [progress, setProgress] = React.useState(null);
  const [loading, setLoading]   = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving]     = React.useState(false);
  const [toast, setToast]       = React.useState(null);
  const [editReport, setEditReport] = React.useState(null); // report being written/edited
  const [expandedReport, setExpandedReport] = React.useState(null);
  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3000);};

  const loadStudent = async (s) => {
    setSelStu(s); setLoading(true); setEditReport(null); setExpandedReport(null);
    const [rRes,lRes,pRes] = await Promise.all([
      supabase.from("hifz_weekly_reports").select("*").eq("student_id",String(s.id)).order("week_start",{ascending:false}),
      supabase.from("hifz_daily_log").select("*").eq("student_id",String(s.id)).order("date",{ascending:false}),
      supabase.from("hifz_progress").select("*").eq("student_id",String(s.id)).maybeSingle(),
    ]);
    setReports(rRes.data||[]); setLogs(lRes.data||[]); setProgress(pRes.data||null);
    setLoading(false);
  };

  // Group logs by week (Mon–Sat)
  const getWeekKey = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDay();
    const mon = new Date(d); mon.setDate(d.getDate()-(day===0?6:day-1));
    return mon.toISOString().split("T")[0];
  };
  const getWeekEnd = (monStr) => {
    const sat = new Date(monStr); sat.setDate(sat.getDate()+5);
    return sat.toISOString().split("T")[0];
  };

  // Build week groups from logs
  const weekGroups = React.useMemo(()=>{
    const map = {};
    logs.forEach(l=>{
      const wk = getWeekKey(l.date);
      if(!map[wk]) map[wk]={week_start:wk,week_end:getWeekEnd(wk),logs:[]};
      map[wk].logs.push(l);
    });
    return Object.values(map).sort((a,b)=>b.week_start.localeCompare(a.week_start));
  },[logs]);

  const sumP=(arr,k)=>Math.round(arr.reduce((s,l)=>s+(Number(l[k])||0),0)*10)/10;

  const initWeek = () => {
    const now=new Date(), day=now.getDay();
    const mon=new Date(now); mon.setDate(now.getDate()-(day===0?6:day-1));
    const sat=new Date(mon); sat.setDate(mon.getDate()+5);
    const fmt=d=>d.toISOString().split("T")[0];
    return {start:fmt(mon),end:fmt(sat)};
  };

  const openEdit = (weekStart, weekEnd, existingReport=null) => {
    const wLogs = logs.filter(l=>{const d=new Date(l.date),s=new Date(weekStart),e=new Date(weekEnd);return d>=s&&d<=e;});
    setEditReport({
      id: existingReport?.id||null,
      week_start: weekStart,
      week_end: weekEnd,
      teacher_comment: existingReport?.teacher_comment||"",
      ai_comment: existingReport?.ai_generated_comment||"",
      logs: wLogs,
    });
  };

  const generateAI = async () => {
    if(!editReport?.logs?.length){showToast("No logs for this week",false);return;}
    setGenerating(true);
    const wLogs = editReport.logs;
    const present = wLogs.filter(l=>l.was_present!==false).length;
    const absent  = wLogs.filter(l=>l.was_present===false).length;
    const sabaq   = sumP(wLogs,"sabaq_pages");
    const sabqi   = sumP(wLogs,"sabqi_pages");
    const manzil  = sumP(wLogs,"manzil_pages");
    const juzNum  = progress?.current_juz||1;
    const juzAr   = QURAN_JUZ.find(q=>q.n===juzNum)?.ar||"";
    const target  = getTarget(juzNum);
    const tSabaq  = typeof target.sabaq==="object"?target.sabaq.value:target.sabaq;
    const tManzil = typeof target.manzil==="object"?target.manzil.value:target.manzil;
    const prompt  = `Hifz student: ${selStu?.full_name||selStu?.name}, پارہ ${juzNum} ${juzAr}. Week ${editReport.week_start} to ${editReport.week_end}: ${present} days present, ${absent} absent. سبق: ${sabaq} pages (target ${tSabaq*present}p), سبقی: ${sabqi}p, منزل: ${manzil}p (target ${tManzil*present}p). Write one concise weekly feedback paragraph (3-4 sentences) for parents: mention attendance, Sabaq progress vs target, Manzil revision, and one encouragement. Warm and professional.`;
    try {
      const text = await groqGenerate(prompt, 200);
      setEditReport(p=>({...p,ai_comment:text}));
    } catch(e){showToast("AI error: "+e.message,false);}
    setGenerating(false);
  };

  const saveReport = async (publish=false) => {
    if(!editReport||!selStu) return;
    setSaving(true);
    const wLogs = editReport.logs;
    const payload = {
      student_id:String(selStu.id), student_name:selStu.full_name||selStu.name||"",
      week_start:editReport.week_start, week_end:editReport.week_end,
      days_present:wLogs.filter(l=>l.was_present!==false).length,
      days_absent:wLogs.filter(l=>l.was_present===false).length,
      total_sabaq_pages:sumP(wLogs,"sabaq_pages"),
      total_sabqi_pages:sumP(wLogs,"sabqi_pages"),
      total_manzil_pages:sumP(wLogs,"manzil_pages"),
      sabaq_summary:wLogs.map(l=>l.sabaq_label||"").filter(Boolean).join("; "),
      teacher_comment:editReport.teacher_comment,
      ai_generated_comment:editReport.ai_comment,
      is_published:publish,
      published_at:publish?new Date().toISOString():null,
    };
    let error;
    if(editReport.id){
      ({error}=await supabase.from("hifz_weekly_reports").update(payload).eq("id",editReport.id));
    } else {
      ({error}=await supabase.from("hifz_weekly_reports").insert(payload));
    }
    setSaving(false);
    if(error){showToast("Error: "+error.message,false);return;}
    showToast(publish?"Published to parent portal!":"Saved as draft");
    // Send WhatsApp to parent if publishing
    if(publish){
      const phone = selStu?.contact||selStu?.phone;
      if(phone){
        const weekStr = editReport.week_start ? `${new Date(editReport.week_start).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}` : "this week";
        const studentName = selStu?.full_name||selStu?.name||"your child";
        const msg = `Assalamu Alaikum! The weekly Hifz report for ${studentName} (week of ${weekStr}) has been published. Please check the parent portal to view the teacher's feedback and progress. — QK Academy`;
        const link = waLink(phone, msg);
        if(link) window.open(link,"_blank");
      }
    }
    setEditReport(null);
    const {data:r}=await supabase.from("hifz_weekly_reports").select("*").eq("student_id",String(selStu.id)).order("week_start",{ascending:false});
    setReports(r||[]);
  };

  const deleteReport = async (id) => {
    if(!window.confirm("Delete this report?")) return;
    await supabase.from("hifz_weekly_reports").delete().eq("id",id);
    setReports(p=>p.filter(r=>r.id!==id));
    showToast("Deleted");
  };

  // If no student selected, show student grid
  if(!selStu) return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
      <div style={{fontWeight:800,fontSize:15,color:C.text}}>📝 Weekly Reports — Select a Student</div>
      {hifzStudents.length===0
        ? <div style={{textAlign:"center",padding:40,color:C.muted,background:"white",borderRadius:14}}>No Hifz students found.</div>
        : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
            {hifzStudents.map(s=>(
              <button key={s.id} onClick={()=>loadStudent(s)}
                style={{background:"white",borderRadius:14,padding:"16px",border:"1.5px solid #ddd6fe",textAlign:"left",cursor:"pointer",width:"100%",transition:"box-shadow .2s"}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 6px 20px #7c3aed25"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#4c1d95,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:15,fontWeight:900,flexShrink:0}}>
                    {(s.full_name||s.name||"?")[0]}
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:800,fontSize:13,color:"#4c1d95",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.full_name||s.name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{s.class||s["class"]}</div>
                  </div>
                </div>
                <div style={{fontSize:10,color:"#7c3aed",fontWeight:600,background:"#f5f3ff",borderRadius:6,padding:"4px 8px",textAlign:"center"}}>
                  View Reports & Logs →
                </div>
              </button>
            ))}
          </div>
      }
    </div>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
      {/* Back + header */}
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <button onClick={()=>{setSelStu(null);setEditReport(null);}}
          style={{background:"none",border:"none",color:P,fontWeight:700,fontSize:13,cursor:"pointer"}}>← All Students</button>
        <div style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",borderRadius:12,padding:"10px 18px",color:"white",display:"flex",alignItems:"center",gap:12,flex:1}}>
          <div style={{width:34,height:34,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:15,flexShrink:0}}>
            {(selStu.full_name||selStu.name||"?")[0]}
          </div>
          <div>
            <div style={{fontWeight:900,fontSize:15}}>{selStu.full_name||selStu.name}</div>
            <div style={{fontSize:11,opacity:.8}}>{selStu.class||selStu["class"]} · {selStu.stream} {progress?`· پارہ ${progress.current_juz} — ${QURAN_JUZ.find(q=>q.n===progress.current_juz)?.ar||""}`:""}</div>
          </div>
        </div>
        <button onClick={()=>{
          const {start,end}=initWeek();
          openEdit(start,end,null);
        }} style={{background:"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"10px 16px",fontWeight:800,fontSize:13,cursor:"pointer",flexShrink:0}}>
          + Write Report
        </button>
      </div>

      {loading&&<div style={{textAlign:"center",padding:32,color:P}}>Loading…</div>}

      {!loading&&(
        <>
          {/* Edit / write form */}
          {editReport&&(
            <div className="fade-up" style={{background:"white",borderRadius:14,padding:20,border:"1.5px solid #ddd6fe",boxShadow:"0 4px 20px #7c3aed15"}}>
              <div style={{fontWeight:800,color:P,fontSize:15,marginBottom:12}}>
                {editReport.id?"✏️ Edit Report":"📝 Write Report"}
              </div>
              {/* Editable week dates */}
              <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:2}}>WEEK START</label>
                  <input type="date" value={editReport.week_start}
                    onChange={e=>{const ws=e.target.value; const wLogs=logs.filter(l=>{const d=new Date(l.date),s=new Date(ws),en=new Date(editReport.week_end);return d>=s&&d<=en;}); setEditReport(p=>({...p,week_start:ws,logs:wLogs}));}}
                    style={{border:`1.5px solid #ddd6fe`,borderRadius:7,padding:"6px 10px",fontSize:13}}/>
                </div>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:2}}>WEEK END</label>
                  <input type="date" value={editReport.week_end}
                    onChange={e=>{const we=e.target.value; const wLogs=logs.filter(l=>{const d=new Date(l.date),s=new Date(editReport.week_start),en=new Date(we);return d>=s&&d<=en;}); setEditReport(p=>({...p,week_end:we,logs:wLogs}));}}
                    style={{border:`1.5px solid #ddd6fe`,borderRadius:7,padding:"6px 10px",fontSize:13}}/>
                </div>
              </div>
              {/* Auto-calculated summary */}
              {editReport.logs.length>0?(
                <div style={{background:"#f5f3ff",borderRadius:10,padding:"10px 14px",marginBottom:12,border:"1px solid #ddd6fe"}}>
                  <div style={{fontSize:11,fontWeight:700,color:P,marginBottom:6}}>📊 Auto-calculated from {editReport.logs.length} log entries</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:6}}>
                    {[
                      ["حاضر",editReport.logs.filter(l=>l.was_present!==false).length+"d","#1a6b3c"],
                      ["غیر حاضر",editReport.logs.filter(l=>l.was_present===false).length+"d","#dc2626"],
                      ["📗 سبق",sumP(editReport.logs,"sabaq_pages")+"p","#1a6b3c"],
                      ["📘 سبقی",sumP(editReport.logs,"sabqi_pages")+"p","#2563eb"],
                      ["📕 منزل",sumP(editReport.logs,"manzil_pages")+"p","#5b21b6"],
                    ].map(([l,v,c])=>(
                      <div key={l} style={{background:"white",borderRadius:6,padding:"6px 4px",textAlign:"center"}}>
                        <div style={{fontSize:9,color:"#9ca3af",fontWeight:700}}>{l}</div>
                        <div style={{fontWeight:900,fontSize:13,color:c}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ):<div style={{background:"#fef2f2",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#dc2626",marginBottom:12}}>⚠️ No logs found for this week.</div>}
              {/* Teacher comment */}
              <div style={{marginBottom:10}}>
                <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>TEACHER'S COMMENT</label>
                <textarea value={editReport.teacher_comment} onChange={e=>setEditReport(p=>({...p,teacher_comment:e.target.value}))} rows={2}
                  placeholder="Your observations for this week..."
                  style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
              </div>
              {/* AI feedback */}
              <div style={{marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <label style={{fontSize:11,fontWeight:700,color:C.muted}}>AI FEEDBACK FOR PARENTS</label>
                  <button onClick={generateAI} disabled={generating}
                    style={{background:"#4c1d95",color:"white",border:"none",borderRadius:7,padding:"3px 12px",fontSize:11,fontWeight:700,cursor:generating?"not-allowed":"pointer"}}>
                    {generating?"✨ Generating...":"✨ Generate"}
                  </button>
                </div>
                <textarea value={editReport.ai_comment} onChange={e=>setEditReport(p=>({...p,ai_comment:e.target.value}))} rows={3}
                  placeholder="Click Generate or write manually..."
                  style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical",background:"#faf5ff",boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>saveReport(true)} disabled={saving}
                  style={{flex:1,background:saving?"#9ca3af":"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer"}}>
                  {saving?"Saving...":"📤 Publish to Parent Portal"}
                </button>
                <button onClick={()=>saveReport(false)} disabled={saving}
                  style={{padding:"11px 16px",background:"white",border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer",fontSize:13}}>
                  💾 Draft
                </button>
                <button onClick={()=>setEditReport(null)}
                  style={{padding:"11px 14px",background:"white",border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>✕</button>
              </div>
            </div>
          )}

          {/* Week-by-week timeline */}
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {weekGroups.map(wk=>{
              const report = reports.find(r=>r.week_start===wk.week_start);
              const present = wk.logs.filter(l=>l.was_present!==false).length;
              const absent  = wk.logs.filter(l=>l.was_present===false).length;
              const sabaq   = sumP(wk.logs,"sabaq_pages");
              const sabqi   = sumP(wk.logs,"sabqi_pages");
              const manzil  = sumP(wk.logs,"manzil_pages");
              const isExpanded = expandedReport===wk.week_start;
              return (
                <div key={wk.week_start} style={{background:"white",borderRadius:14,border:`1.5px solid ${report?.is_published?"#ddd6fe":report?"#e5e7eb":C.border}`,overflow:"hidden"}}>
                  {/* Week header */}
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",background:report?.is_published?"#f5f3ff":report?"#f9fafb":"white",flexWrap:"wrap"}}>
                    <div style={{background:"#4c1d95",color:"white",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:800,flexShrink:0}}>
                      {new Date(wk.week_start).toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – {new Date(wk.week_end).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                    </div>
                    <div style={{display:"flex",gap:8,fontSize:11,fontWeight:700,flexWrap:"wrap"}}>
                      <span style={{color:"#1a6b3c"}}>📗 {sabaq}p</span>
                      <span style={{color:"#2563eb"}}>📘 {sabqi}p</span>
                      <span style={{color:"#5b21b6"}}>📕 {manzil}p</span>
                      <span style={{color:"#6b7280"}}>✅{present} ❌{absent}</span>
                    </div>
                    {report
                      ? <span style={{fontSize:10,fontWeight:700,color:report.is_published?"#7c3aed":"#6b7280",background:report.is_published?"#ede9fe":"#f3f4f6",borderRadius:5,padding:"2px 8px"}}>
                          {report.is_published?"📤 Published":"📝 Draft"}
                        </span>
                      : <span style={{fontSize:10,color:"#9ca3af"}}>No report yet</span>
                    }
                    <div style={{marginLeft:"auto",display:"flex",gap:6}}>
                      <button onClick={()=>setExpandedReport(isExpanded?null:wk.week_start)}
                        style={{background:"#f5f3ff",color:"#4c1d95",border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        {isExpanded?"▲ Collapse":"▼ Logs"}
                      </button>
                      <button onClick={()=>openEdit(wk.week_start, wk.week_end, report||null)}
                        style={{background:"#4c1d95",color:"white",border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        {report?"✏️ Edit":"+ Report"}
                      </button>
                      {report&&!report.is_published&&(
                        <button onClick={async()=>{
                          await supabase.from("hifz_weekly_reports").update({is_published:true,published_at:new Date().toISOString()}).eq("id",report.id);
                          setReports(p=>p.map(x=>x.id===report.id?{...x,is_published:true}:x));
                          showToast("Published!");
                          // WhatsApp parent notification
                          const phone = selStu?.contact||selStu?.phone;
                          if(phone){
                            const weekStr = `${new Date(wk.week_start).toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${new Date(wk.week_end).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`;
                            const studentName = selStu?.full_name||selStu?.name||"your child";
                            const msg = `Assalamu Alaikum! The weekly Hifz report for ${studentName} (${weekStr}) has been published. Please check the parent portal to view the teacher's feedback and progress details. — QK Academy`;
                            const link = waLink(phone, msg);
                            if(link) window.open(link,"_blank");
                          }
                        }}
                          style={{background:"#7c3aed",color:"white",border:"none",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>📤 + 💬</button>
                      )}
                      {report&&(
                        <button onClick={()=>deleteReport(report.id)}
                          style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:7,padding:"4px 8px",fontSize:11,cursor:"pointer"}}>🗑</button>
                      )}
                    </div>
                  </div>
                  {/* Teacher/AI comment preview */}
                  {report&&(report.teacher_comment||report.ai_generated_comment)&&!isExpanded&&(
                    <div style={{padding:"8px 16px",fontSize:11,color:"#6b7280",borderTop:`1px solid ${C.border}`,background:"#fafafa"}}>
                      {report.teacher_comment?`"${report.teacher_comment.slice(0,120)}${report.teacher_comment.length>120?"…":""}"`
                        :`✨ ${report.ai_generated_comment?.slice(0,120)||""}${(report.ai_generated_comment?.length||0)>120?"…":""}`}
                    </div>
                  )}
                  {/* Expanded logs */}
                  {isExpanded&&(
                    <div style={{padding:"12px 16px",borderTop:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:6}}>
                      {report&&(report.teacher_comment||report.ai_generated_comment)&&(
                        <div style={{background:"#f5f3ff",borderRadius:8,padding:"10px 12px",marginBottom:4,fontSize:12}}>
                          {report.teacher_comment&&<div style={{marginBottom:4}}><strong>استاد:</strong> {report.teacher_comment}</div>}
                          {report.ai_generated_comment&&<div style={{color:"#5b21b6"}}>✨ {report.ai_generated_comment}</div>}
                        </div>
                      )}
                      <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:4}}>Daily Logs ({wk.logs.length})</div>
                      {wk.logs.sort((a,b)=>a.date.localeCompare(b.date)).map(log=>(
                        <div key={log.id} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 10px",background:"#f9fafb",borderRadius:8,fontSize:11,flexWrap:"wrap"}}>
                          <span style={{background:"#4c1d95",color:"white",borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:800,flexShrink:0}}>
                            {new Date(log.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}
                          </span>
                          {log.was_present===false
                            ? <span style={{color:"#dc2626",fontWeight:700}}>غیر حاضر</span>
                            : <span>
                                <span style={{color:"#1a6b3c",fontWeight:700}}>سبق {log.sabaq_pages||0}p</span>
                                {log.sabaq_label&&<span style={{color:"#6b7280"}}> ({log.sabaq_label})</span>}
                                {" · "}<span style={{color:"#2563eb",fontWeight:700}}>سبقی {log.sabqi_pages||0}p</span>
                                {" · "}<span style={{color:"#5b21b6",fontWeight:700}}>منزل {log.manzil_pages||0}p</span>
                              </span>
                          }
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {weekGroups.length===0&&(
              <div style={{textAlign:"center",padding:32,color:C.muted,background:"white",borderRadius:14}}>
                No daily logs found for this student. Add logs in the Daily Log tab first.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function HifzStudentsTab({ hifzStudents, targetYears, setActiveTab }) {
  const [selProfile, setSelProfile] = React.useState(null);
  const [profileData, setProfileData] = React.useState({journey:null,logs:[]});
  const [loadingProfile, setLoadingProfile] = React.useState(false);

  const openProfile = async(s) => {
    setSelProfile(s);
    setLoadingProfile(true);
    const [j,l] = await Promise.all([
      supabase.from("hifz_journey").select("*").eq("student_id",s.id).maybeSingle(),
      supabase.from("hifz_daily_log").select("*").eq("student_id",s.id).order("date",{ascending:false}).limit(5),
    ]);
    setProfileData({journey:j.data||null,logs:l.data||[]});
    setLoadingProfile(false);
  };

  const juzLabel = (juzNum) => {
    const j = QURAN_JUZ.find(q=>q.n===juzNum);
    return j ? `پارہ ${juzNum} — ${j.ar}` : `پارہ ${juzNum}`;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {selProfile&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px",overflowY:"auto"}} onClick={()=>setSelProfile(null)}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.35)",animation:"fadeSlideUp .2s ease"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",justifyContent:"flex-end",padding:"12px 16px 0"}}>
                <button onClick={()=>setSelProfile(null)}
                  style={{background:"#f3f4f6",border:"none",borderRadius:8,width:30,height:30,fontSize:16,cursor:"pointer",color:"#6b7280",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>✕</button>
              </div>
              <div style={{background:"linear-gradient(135deg,#4c1d95,#7c3aed)",margin:"10px 16px 0",borderRadius:14,padding:"16px 18px",color:"white"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(255,255,255,.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontWeight:900,flexShrink:0}}>
                    {(selProfile.full_name||selProfile.name||"?")[0]}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:900,fontSize:16}}>{selProfile.full_name||selProfile.name}</div>
                    <div style={{fontSize:11,opacity:.85}}>{selProfile.class||selProfile["class"]} · {selProfile.stream}</div>
                    {(selProfile.father||selProfile.father_name)&&<div style={{fontSize:10,opacity:.7}}>والد: {selProfile.father||selProfile.father_name}</div>}
                  </div>
                  {!loadingProfile&&profileData.journey&&(
                    <div style={{background:"rgba(255,255,255,.2)",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:700,flexShrink:0,textAlign:"center"}}>
                      {profileData.journey.stage==="qaida"
                        ? `📗 سبق ${profileData.journey.qaida_lesson||1}`
                        : profileData.journey.stage==="hafiz"?"🎓 حافظ"
                        : juzLabel(profileData.journey.current_juz||1)}
                      {profileData.journey.hifz_start_date&&(
                        <div style={{fontSize:9,opacity:.8,marginTop:2}}>
                          {new Date(profileData.journey.hifz_start_date).toLocaleDateString("en-GB",{month:"short",year:"numeric"})}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div style={{padding:"14px 16px 28px",display:"flex",flexDirection:"column",gap:12}}>
                {loadingProfile
                  ? <div style={{textAlign:"center",padding:32,color:"#7c3aed"}}>Loading…</div>
                  : <>
                      <JourneyRoad journey={profileData.journey} targetYears={targetYears}/>
                      {profileData.logs.length>0&&(
                        <div>
                          <div style={{fontWeight:700,fontSize:12,color:"#374151",marginBottom:6}}>آخری سیشن</div>
                          {profileData.logs.map(log=>(
                            <div key={log.id} style={{display:"flex",gap:8,alignItems:"center",padding:"6px 10px",background:"#f5f3ff",borderRadius:8,marginBottom:4,fontSize:11,flexWrap:"wrap"}}>
                              <span style={{background:"#4c1d95",color:"white",borderRadius:5,padding:"2px 7px",fontSize:10,fontWeight:800,flexShrink:0}}>
                                {new Date(log.date).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                              </span>
                              {log.was_present===false
                                ? <span style={{color:"#dc2626",fontWeight:700}}>غیر حاضر</span>
                                : <span>
                                    <span style={{color:"#1a6b3c",fontWeight:700}}>Sabaq: {log.sabaq_pages||0} pages</span>
                                    {" · "}<span style={{color:"#7c3aed",fontWeight:700}}>Manzil: {log.manzil_pages||0} pages</span>
                                    {log.sabaq_label&&<span style={{color:"#9ca3af"}}> · {log.sabaq_label}</span>}
                                  </span>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{display:"flex",gap:10,marginTop:4}}>
                        <button onClick={()=>{setSelProfile(null);setActiveTab("log");}}
                          style={{flex:1,background:"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"12px",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                          📅 Log Session
                        </button>
                        <button onClick={()=>setSelProfile(null)}
                          style={{flex:1,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"12px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                          Close
                        </button>
                      </div>
                    </>
                }
              </div>
            </div>
          </div>
        </Portal>
      )}
      <div style={{fontWeight:800,fontSize:15,color:C.text}}>👥 Hifz Students ({hifzStudents.length})</div>
      {hifzStudents.length===0
        ? <div style={{textAlign:"center",padding:40,color:C.muted,background:"white",borderRadius:14}}>No Hifz students found.</div>
        : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
            {hifzStudents.map(s=>(
              <button key={s.id} onClick={()=>openProfile(s)}
                style={{background:"white",borderRadius:14,padding:"16px",border:"1.5px solid #ddd6fe",textAlign:"left",cursor:"pointer",width:"100%",transition:"box-shadow .2s"}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 6px 20px #7c3aed25"}
                onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#4c1d95,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:15,fontWeight:900,flexShrink:0}}>
                    {(s.full_name||s.name||"?")[0]}
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:800,fontSize:13,color:"#4c1d95",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.full_name||s.name}</div>
                    <div style={{fontSize:11,color:C.muted}}>{s.class||s["class"]}</div>
                  </div>
                </div>
                <div style={{fontSize:10,color:"#7c3aed",fontWeight:600,background:"#f5f3ff",borderRadius:6,padding:"4px 8px",textAlign:"center"}}>
                  Tap for pace & details →
                </div>
              </button>
            ))}
          </div>
      }
    </div>
  );
}

// ── Journey Road — visual pace tracker ───────────────────────────────
function JourneyRoad({ journey, targetYears=3.5 }) {
  if(!journey?.hifz_start_date) return null;
  const isQaida = journey.stage==="qaida";
  const startDate = new Date(journey.hifz_start_date);
  const now = new Date();
  const daysPassed = Math.max(0,(now-startDate)/(1000*86400));
  const totalDays = (targetYears||3.5)*365;
  const totalUnits = isQaida?(journey.qaida_total_lessons||20):30;
  const unitLabel = isQaida?"Lessons":"Juz";
  const actualUnits = isQaida?(journey.qaida_lesson||1):(journey.juz_completed||0);
  const targetDays = isQaida?(journey.qaida_weeks||4)*7:totalDays;
  const expectedUnits = Math.min(totalUnits,(daysPassed/targetDays)*totalUnits);
  const expectedPct = Math.min(100,(expectedUnits/totalUnits)*100);
  const actualPct   = Math.min(100,(actualUnits/totalUnits)*100);
  const isAhead = actualPct >= expectedPct;
  const delta = actualUnits - expectedUnits;
  const unitsPerDay = daysPassed>0?actualUnits/daysPassed:0;
  const daysToFinish = unitsPerDay>0?Math.round((totalUnits-actualUnits)/unitsPerDay):null;
  const finishDate = daysToFinish!=null?new Date(now.getTime()+daysToFinish*86400000):null;
  const targetFinishDate = new Date(startDate.getTime()+targetDays*86400000);
  const trackColor = isAhead?"#22c55e":"#f97316";
  const trackBg    = isAhead?"#f0fdf4":"#fff7ed";
  const trackBdr   = isAhead?"#bbf7d0":"#fed7aa";
  const textColor  = isAhead?"#15803d":"#c2410c";

  // Position as percentages
  const expLeft = `${expectedPct.toFixed(1)}%`;
  const actLeft = `${actualPct.toFixed(1)}%`;

  return (
    <div style={{background:trackBg,borderRadius:14,padding:"16px 18px",border:`1.5px solid ${trackBdr}`}}>
      {/* Status header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:6}}>
        <div style={{fontWeight:800,fontSize:14,color:textColor}}>
          {isAhead?"🚀 Ahead of Schedule":"⚠️ Behind Schedule"} — {isQaida?"Qaida":"Hifz"} Journey
        </div>
        <div style={{background:isAhead?"#dcfce7":"#ffedd5",borderRadius:20,padding:"4px 12px",fontSize:12,fontWeight:700,color:textColor}}>
          {delta>=0?"+":""}{delta.toFixed(1)} {unitLabel} {isAhead?"ahead":"behind"}
        </div>
      </div>

      {/* Track */}
      <div style={{position:"relative",height:40,marginBottom:8}}>
        {/* Background rail */}
        <div style={{position:"absolute",top:14,left:0,right:0,height:12,background:"#e5e7eb",borderRadius:99}}/>
        {/* Actual progress fill */}
        <div style={{position:"absolute",top:14,left:0,height:12,width:actLeft,background:`linear-gradient(90deg,${trackColor}80,${trackColor})`,borderRadius:99,transition:"width .5s"}}/>
        {/* Expected marker (vertical line through track) */}
        <div style={{position:"absolute",left:expLeft,top:8,transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",zIndex:3}}>
          <div style={{width:2,height:28,background:"#6b7280",borderRadius:1}}/>
        </div>
        {/* Actual position dot */}
        <div style={{position:"absolute",top:12,left:actLeft,transform:"translateX(-50%)",width:20,height:20,borderRadius:"50%",background:trackColor,border:"3px solid white",boxShadow:`0 0 0 2px ${trackColor},0 2px 6px rgba(0,0,0,0.2)`,zIndex:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9}}>📖</div>
        {/* Start dot */}
        <div style={{position:"absolute",top:17,left:0,width:10,height:10,borderRadius:"50%",background:"#9ca3af",border:"2px solid white",zIndex:2}}/>
        {/* End dot */}
        <div style={{position:"absolute",top:17,right:0,width:10,height:10,borderRadius:"50%",background:"#4c1d95",border:"2px solid white",zIndex:2}}/>
      </div>

      {/* Aligned labels below track */}
      <div style={{position:"relative",height:32,marginBottom:12}}>
        {/* Start label */}
        <div style={{position:"absolute",left:0,top:0,textAlign:"center",transform:"translateX(-2px)"}}>
          <div style={{fontSize:9,fontWeight:700,color:"#9ca3af",whiteSpace:"nowrap"}}>🏁 Start</div>
          <div style={{fontSize:8,color:"#9ca3af",whiteSpace:"nowrap"}}>{startDate.toLocaleDateString("en-GB",{day:"numeric",month:"short","year":"2-digit"})}</div>
        </div>
        {/* Expected label — positioned at expectedPct */}
        <div style={{position:"absolute",left:expLeft,top:0,textAlign:"center",transform:"translateX(-50%)"}}>
          <div style={{fontSize:9,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap",background:"white",borderRadius:4,padding:"1px 5px",border:"1px solid #d1d5db"}}>
            ▲ Expected: {expectedUnits.toFixed(0)} {unitLabel}
          </div>
        </div>
        {/* End label */}
        <div style={{position:"absolute",right:0,top:0,textAlign:"center",transform:"translateX(2px)"}}>
          <div style={{fontSize:9,fontWeight:700,color:"#4c1d95",whiteSpace:"nowrap"}}>🎓 Finish</div>
          <div style={{fontSize:8,color:"#6b7280",whiteSpace:"nowrap"}}>{targetFinishDate.toLocaleDateString("en-GB",{month:"short",year:"numeric"})}</div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
        {[
          ["Completed",`${actualUnits} ${unitLabel}`,trackColor,isAhead?"#dcfce7":"#ffedd5"],
          ["Expected Now",`${expectedUnits.toFixed(1)} ${unitLabel}`,"#6b7280","#f3f4f6"],
          finishDate
            ? ["Est. Finish",finishDate.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),textColor,isAhead?"#dcfce7":"#ffedd5"]
            : ["Pace","No data yet","#9ca3af","#f3f4f6"]
        ].map(([l,v,c,bg])=>(
          <div key={l} style={{background:bg,borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
            <div style={{fontSize:9,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>{l}</div>
            <div style={{fontWeight:900,fontSize:13,color:c,lineHeight:1.2}}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HifzManagement({ students }) {
  const [activeTab, setActiveTab] = React.useState("log");
  const hifzStudents = students.filter(s=>(s.stream||"").includes("Hifz")||(s.stream||"").includes("Both"));
  // Targets stored in supabase school_settings as JSON keyed "hifz_juz_targets"
  const [juzTargets, setJuzTargets] = React.useState({}); // {1: {sabaq:0.5,sabqi:1,manzil:2}, ...}
  const [targetYears, setTargetYears] = React.useState(3.5); // global expected Hifz duration
  const [savingTargets, setSavingTargets] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3000);};

  React.useEffect(()=>{
    supabase.from("school_settings").select("value").eq("key","hifz_juz_targets").single()
      .then(({data})=>{ if(data?.value){ try{setJuzTargets(JSON.parse(data.value));}catch(e){} }});
    supabase.from("school_settings").select("value").eq("key","hifz_target_years").single()
      .then(({data})=>{ if(data?.value) setTargetYears(Number(data.value)||3.5); });
  },[]);

  const getTarget = (juzNum) => juzTargets[juzNum]||DEFAULT_JUZ_TARGETS(juzNum);

  const saveTargets = async() => {
    setSavingTargets(true);
    const val = JSON.stringify(juzTargets);
    await Promise.all([
      supabase.from("school_settings").upsert({key:"hifz_juz_targets",value:val},{onConflict:"key"}),
      supabase.from("school_settings").upsert({key:"hifz_target_years",value:String(targetYears)},{onConflict:"key"}),
    ]);
    setSavingTargets(false);
    showToast("Targets saved!");
  };

  const tabBtn = (id,label,icon) => (
    <button key={id} onClick={()=>setActiveTab(id)} style={{
      background:activeTab===id?C.primary:"white",
      color:activeTab===id?"white":C.text,
      border:`1.5px solid ${activeTab===id?C.primary:C.border}`,
      borderRadius:10,padding:"9px 16px",fontWeight:700,fontSize:13,cursor:"pointer",transition:"all .2s"
    }}>{icon} {label}</button>
  );

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:20}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#7c3aed,#4c1d95)",borderRadius:16,padding:"18px 24px",color:"white",boxShadow:"0 4px 20px #7c3aed40"}}>
        <div style={{fontWeight:900,fontSize:20}}>📖 Hifz Management</div>
        <div style={{fontSize:13,opacity:.85,marginTop:2}}>{hifzStudents.length} students · سبق · سبقی · منزل · Progress tracking</div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {tabBtn("log","Daily Log","📅")}
        {tabBtn("progress","Progress","📈")}
        {tabBtn("weekly","Weekly Reports","📝")}
        {tabBtn("students","Students","👥")}
        {tabBtn("targets","Set Targets","🎯")}
        {tabBtn("wifaq","Wifaq Analytics","🕌")}
      </div>

      {activeTab==="log"      && <HifzDailyLog students={students} getTarget={getTarget}/>}
      {activeTab==="progress" && <HifzProgressTracker students={students} getTarget={getTarget} targetYears={targetYears}/>}
      {activeTab==="weekly"   && <HifzWeeklyReports students={students} getTarget={getTarget}/>}
      {activeTab==="targets"  && <HifzTargetSettings juzTargets={juzTargets} setJuzTargets={setJuzTargets} targetYears={targetYears} setTargetYears={setTargetYears} onSave={saveTargets} saving={savingTargets}/>}
      {activeTab==="wifaq" && <HifzWifaqAnalytics students={students} targetYears={targetYears}/>}    {/* ← add */}
      {activeTab==="students" && <HifzStudentsTab hifzStudents={hifzStudents} targetYears={targetYears} setActiveTab={setActiveTab}/>}
    </div>
  );
}

// -- HIFZ TARGET SETTINGS -------------------------------------------
function HifzTargetSettings({ juzTargets, setJuzTargets, targetYears, setTargetYears, onSave, saving }) {
  const UNITS = [{k:"pages",lbl:"Pages/day"},{k:"juz",lbl:"Juz/day"},{k:"ayat",lbl:"Ayat/day"},{k:"surah",lbl:"Surah/day"}];
  const [workingDays, setWorkingDays] = React.useState(300); // working days per year
  const [sabqiMult, setSabqiMult]     = React.useState(2);   // sabqi = N× sabaq
  const [manzilMult, setManzilMult]   = React.useState(4);   // manzil = N× sabaq

  const getVal = (t,k) => typeof t[k]==="object"?t[k].value:t[k]||0;
  const getUnit = (t,k) => typeof t[k]==="object"?t[k].unit:"pages";

  const setTU = (juz,k,field,v) => setJuzTargets(p=>{
    const cur=p[juz]||DEFAULT_JUZ_TARGETS(juz);
    const existing=cur[k]||{value:0,unit:"pages"};
    const obj=typeof existing==="object"?existing:{value:existing,unit:"pages"};
    const updated = {...p,[juz]:{...cur,[k]:{...obj,[field]:field==="value"?Number(v)||0:v}}};
    return updated;
  });

  // ── Core maths ──────────────────────────────────────────────────
  const totalWorkingDays = (targetYears||3.5) * workingDays;
  const totalPages       = 30 * 20; // 30 Juz × 20 pages
  const autoSabaqPerDay  = totalPages / totalWorkingDays;

  // Projected years from current manual targets (avg sabaq across all Juz)
  const avgSabaq = (() => {
    let total=0,count=0;
    QURAN_JUZ.forEach(j=>{
      const t=juzTargets[j.n]||DEFAULT_JUZ_TARGETS(j.n);
      const v=getVal(t,"sabaq"), u=getUnit(t,"sabaq");
      // convert to pages
      const pages = u==="pages"?v : u==="surah"?v*1.5 : u==="ayat"?v/7 : v*20;
      total+=pages; count++;
    });
    return count>0?total/count:0;
  })();
  const projectedYears = avgSabaq>0
    ? Math.round((totalPages/avgSabaq/workingDays)*10)/10
    : null;
  const projectedMatch = projectedYears&&Math.abs(projectedYears-targetYears)<0.3;

  // ── Auto-fill all Juz from targetYears ──────────────────────────
  const autoFill = () => {
    const sabaq = Math.round(autoSabaqPerDay*4)/4; // round to nearest 0.25
    const sabqi  = Math.round(sabaq*sabqiMult*4)/4;
    const manzil = Math.round(sabaq*manzilMult*4)/4;
    const newT = {};
    QURAN_JUZ.forEach(j=>{
      newT[j.n]={
        sabaq: {value:sabaq, unit:"pages"},
        sabqi: {value:sabqi, unit:"pages"},
        manzil:{value:manzil,unit:"pages"},
      };
    });
    setJuzTargets(newT);
  };

  // ── Back-calc years from avg sabaq when manually set ────────────
  const backCalcYears = () => {
    if(projectedYears&&projectedYears>0) setTargetYears(projectedYears);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* ── Header card — lighter, readable ── */}
      <div style={{background:"white",borderRadius:16,padding:"20px 22px",border:"2px solid #ddd6fe",boxShadow:"0 2px 12px rgba(76,29,149,0.08)"}}>
        <div style={{fontWeight:900,fontSize:17,color:"#4c1d95",marginBottom:2}}>⚙️ Hifz Pace Settings</div>
        <div style={{fontSize:13,color:"#6b7280",marginBottom:18}}>Everything is linked — change years to auto-fill targets, or edit targets to see projected years update.</div>

        {/* Row 1: Years + Working days */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:16}}>
          {/* Target years stepper */}
          <div style={{background:"#f5f3ff",borderRadius:12,padding:"14px 18px",flex:1,minWidth:160,border:"1.5px solid #ddd6fe"}}>
            <div style={{fontSize:11,color:"#7c3aed",fontWeight:700,marginBottom:8,textTransform:"uppercase"}}>🎯 Target Duration</div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <button onClick={()=>setTargetYears(y=>Math.max(1,Math.round((y-0.5)*2)/2))}
                style={{background:"#4c1d95",border:"none",color:"white",borderRadius:8,width:32,height:32,fontSize:20,cursor:"pointer",fontWeight:900,lineHeight:1}}>−</button>
              <div style={{textAlign:"center",minWidth:56}}>
                <div style={{fontWeight:900,fontSize:34,lineHeight:1,color:"#4c1d95"}}>{targetYears||3.5}</div>
                <div style={{fontSize:12,color:"#7c3aed",fontWeight:600}}>years</div>
              </div>
              <button onClick={()=>setTargetYears(y=>Math.min(10,Math.round((y+0.5)*2)/2))}
                style={{background:"#4c1d95",border:"none",color:"white",borderRadius:8,width:32,height:32,fontSize:20,cursor:"pointer",fontWeight:900,lineHeight:1}}>+</button>
            </div>
          </div>

          {/* Working days */}
          <div style={{background:"#f0fdf4",borderRadius:12,padding:"14px 18px",flex:1,minWidth:160,border:"1.5px solid #bbf7d0"}}>
            <div style={{fontSize:11,color:"#15803d",fontWeight:700,marginBottom:8,textTransform:"uppercase"}}>🗓️ Working Days / Year</div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <input type="number" min="200" max="365" value={workingDays}
                onChange={e=>setWorkingDays(Number(e.target.value)||300)}
                style={{width:80,border:"2px solid #bbf7d0",borderRadius:8,padding:"8px 10px",fontSize:20,fontWeight:900,color:"#15803d",textAlign:"center",background:"white"}}/>
              <div style={{fontSize:12,color:"#15803d",lineHeight:1.5}}>days per year<br/><span style={{fontSize:11,color:"#6b7280"}}>(excl. Fri & holidays)</span></div>
            </div>
          </div>

          {/* Derived stats */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            {[
              [`~${Math.round((targetYears||3.5)*workingDays)}`, "total working days","#4c1d95","#f5f3ff","#ddd6fe"],
              [`~${(30/((targetYears||3.5)*52)).toFixed(2)}`, "Juz/week","#15803d","#f0fdf4","#bbf7d0"],
              [`~${autoSabaqPerDay.toFixed(2)}p`, "Sabaq pages/day","#d97706","#fffbeb","#fde68a"],
            ].map(([v,l,col,bg,bdr])=>(
              <div key={l} style={{background:bg,borderRadius:10,padding:"10px 14px",textAlign:"center",border:`1.5px solid ${bdr}`,minWidth:90}}>
                <div style={{fontWeight:900,fontSize:18,color:col}}>{v}</div>
                <div style={{fontSize:10,color:"#6b7280",marginTop:2,fontWeight:600}}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2: Multipliers — range-based */}
        <div style={{background:"#fafafa",borderRadius:12,padding:"14px 18px",marginBottom:16,border:"1px solid #e5e7eb"}}>
          <div style={{fontSize:12,color:"#374151",fontWeight:800,marginBottom:4}}>📐 Default Multipliers (used by Auto-fill)</div>
          <div style={{fontSize:12,color:"#6b7280",marginBottom:12}}>Sabqi and Manzil targets are set as multiples of Sabaq. You can override any range below after auto-filling.</div>
          <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
            {[
              ["📘 Sabqi",sabqiMult,setSabqiMult,1,6,"Sabqi = Sabaq ×","#2563eb","#eff6ff"],
              ["📕 Manzil",manzilMult,setManzilMult,1,10,"Manzil = Sabaq ×","#5b21b6","#f5f3ff"],
            ].map(([icon,val,setter,min,max,desc,col,bg])=>(
              <div key={icon} style={{background:bg,borderRadius:10,padding:"12px 16px",border:`1px solid ${col}20`,flex:1,minWidth:180}}>
                <div style={{fontSize:12,color:col,fontWeight:700,marginBottom:8}}>{icon} {desc}</div>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <button onClick={()=>setter(v=>Math.max(min,Math.round((v-0.5)*2)/2))}
                    style={{background:col,border:"none",color:"white",borderRadius:7,width:28,height:28,fontSize:16,cursor:"pointer",fontWeight:900}}>−</button>
                  <div style={{textAlign:"center",minWidth:48}}>
                    <div style={{fontWeight:900,fontSize:26,color:col,lineHeight:1}}>{val}×</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:2}}>
                      = {(autoSabaqPerDay*val).toFixed(2)}p/day
                    </div>
                  </div>
                  <button onClick={()=>setter(v=>Math.min(max,Math.round((v+0.5)*2)/2))}
                    style={{background:col,border:"none",color:"white",borderRadius:7,width:28,height:28,fontSize:16,cursor:"pointer",fontWeight:900}}>+</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 3: Auto-fill + projected years */}
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <button onClick={autoFill}
            style={{background:"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"11px 22px",fontWeight:800,fontSize:14,cursor:"pointer"}}>
            ⚡ Auto-fill All Ranges from {targetYears} years
          </button>
          {projectedYears&&(
            <div style={{display:"flex",alignItems:"center",gap:10,background:projectedMatch?"#f0fdf4":"#fffbeb",borderRadius:10,padding:"10px 16px",border:`1px solid ${projectedMatch?"#bbf7d0":"#fde68a"}`}}>
              <span style={{fontSize:13,color:projectedMatch?"#15803d":"#92400e",fontWeight:700}}>
                {projectedMatch?"✅":"⚠️"} Current targets → <strong>{projectedYears} years projected</strong>
                {!projectedMatch&&` (target: ${targetYears}yr)`}
              </span>
              {!projectedMatch&&(
                <button onClick={backCalcYears}
                  style={{background:"#4c1d95",color:"white",border:"none",borderRadius:7,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  Sync →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Qaida settings ── */}
      <div style={{background:"white",borderRadius:12,padding:"14px 18px",border:"1px solid #fde68a",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontWeight:700,fontSize:14,color:"#92400e"}}>📗 Noorani Qaida Total Lessons:</span>
        <input type="number" min="10" max="60" defaultValue={20}
          onChange={e=>{ supabase.from("school_settings").upsert({key:"qaida_total_lessons",value:String(e.target.value)},{onConflict:"key"}); }}
          style={{width:70,border:"2px solid #fde68a",borderRadius:8,padding:"7px 10px",fontSize:16,textAlign:"center",fontWeight:800}}/>
        <span style={{fontSize:13,color:"#78350f"}}>lessons (adjust for your Qaida edition)</span>
      </div>

      {/* ── Per-Range table (Juz ranges instead of individual Juz) ── */}
      <div style={{background:"white",borderRadius:16,padding:20,boxShadow:`0 2px 12px ${C.shadow}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontWeight:900,fontSize:16,color:"#4c1d95"}}>🎯 Daily Targets — All 30 Juz</div>
            <div style={{fontSize:12,color:C.muted,marginTop:2}}>
              📗 Sabaq: set individually per Juz. &nbsp;📘 Sabqi & 📕 Manzil: set by range (header row) — applies instantly to all Juz in that range.
            </div>
          </div>
          <button onClick={autoFill}
            style={{background:"#f5f3ff",color:"#4c1d95",border:"1.5px solid #ddd6fe",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
            ⚡ Auto-fill
          </button>
        </div>

        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",borderRadius:12,overflow:"hidden",border:"1px solid #ddd6fe",fontSize:13}}>
            <thead>
              <tr style={{background:"#4c1d95"}}>
                {["پارہ","Arabic Name","📗 Sabaq (pages/day)","~Days for Juz","📘 Sabqi Range","📕 Manzil Range","Visual"].map(h=>(
                  <th key={h} style={{padding:"11px 10px",fontWeight:700,color:"white",textAlign:"center",fontSize:11,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
              {/* Sabqi/Manzil range setter row */}
              <tr style={{background:"#f5f3ff"}}>
                <td colSpan={2} style={{padding:"8px 12px",fontSize:11,color:"#4c1d95",fontWeight:700}}>
                  📐 Set Sabqi & Manzil by range →
                </td>
                {(()=>{
                  const RANGES=[
                    {label:"Juz 30–26",from:26,to:30},
                    {label:"Juz 25–21",from:21,to:25},
                    {label:"Juz 20–16",from:16,to:20},
                    {label:"Juz 15–11",from:11,to:15},
                    {label:"Juz 10–6", from:6, to:10},
                    {label:"Juz 5–1",  from:1, to:5 },
                  ];
                  return ["sabqi","manzil"].map(k=>(
                    <td key={k} colSpan={1} style={{padding:"6px 8px"}}>
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        {RANGES.map(range=>{
                          const repT = juzTargets[range.from]||DEFAULT_JUZ_TARGETS(range.from);
                          const val  = getVal(repT,k)||0;
                          const col  = k==="sabqi"?"#2563eb":"#5b21b6";
                          return (
                            <div key={range.label} style={{display:"flex",alignItems:"center",gap:4}}>
                              <span style={{fontSize:9,color:"#6b7280",minWidth:56,flexShrink:0}}>{range.label}:</span>
                              <input type="number" min="0" step="0.25" value={val}
                                onChange={e=>{
                                  const v=Number(e.target.value)||0;
                                  const unit=getUnit(repT,k);
                                  setJuzTargets(prev=>{
                                    const next={...prev};
                                    for(let j=range.from;j<=range.to;j++){
                                      const cur=next[j]||DEFAULT_JUZ_TARGETS(j);
                                      next[j]={...cur,[k]:{value:v,unit}};
                                    }
                                    return next;
                                  });
                                }}
                                style={{width:44,border:`1.5px solid ${col}40`,borderRadius:6,padding:"3px 5px",fontSize:12,textAlign:"center",fontWeight:700,color:col}}/>
                              <select value={getUnit(repT,k)} onChange={e=>{
                                const unit=e.target.value;
                                setJuzTargets(prev=>{
                                  const next={...prev};
                                  for(let j=range.from;j<=range.to;j++){
                                    const cur=next[j]||DEFAULT_JUZ_TARGETS(j);
                                    const ex=cur[k]||{value:0,unit:"pages"};
                                    const obj=typeof ex==="object"?ex:{value:ex,unit:"pages"};
                                    next[j]={...cur,[k]:{...obj,unit}};
                                  }
                                  return next;
                                });
                              }} style={{border:`1px solid ${col}20`,borderRadius:4,padding:"3px 2px",fontSize:9,color:col,background:"white"}}>
                                {UNITS.map(u=><option key={u.k} value={u.k}>{u.lbl}</option>)}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  ));
                })()}
                <td colSpan={2} style={{padding:"8px 12px",fontSize:10,color:"#9ca3af",fontStyle:"italic"}}>
                  Changes apply to all Juz in each range instantly
                </td>
              </tr>
            </thead>
            <tbody>
              {QURAN_JUZ.map((juz,i)=>{
                const t = juzTargets[juz.n]||DEFAULT_JUZ_TARGETS(juz.n);
                const sabaq  = getVal(t,"sabaq")||0;
                const sabqi  = getVal(t,"sabqi")||0;
                const manzil = getVal(t,"manzil")||0;
                const daysForJuz = sabaq>0?Math.round(20/sabaq):null;
                // Which range does this Juz belong to?
                const RANGE_COLORS=[
                  {from:26,to:30,col:"#7c3aed"},{from:21,to:25,col:"#2563eb"},
                  {from:16,to:20,col:"#0d9488"},{from:11,to:15,col:"#15803d"},
                  {from:6, to:10,col:"#d97706"},{from:1, to:5, col:"#dc2626"},
                ];
                const rangCol=RANGE_COLORS.find(r=>juz.n>=r.from&&juz.n<=r.to)?.col||"#6b7280";
                return (
                  <tr key={juz.n} style={{background:i%2===0?"white":"#faf5ff",borderTop:"1px solid #ede9fe"}}>
                    <td style={{padding:"7px 10px",textAlign:"center"}}>
                      <div style={{fontWeight:800,color:rangCol,fontSize:14}}>پارہ {juz.n}</div>
                    </td>
                    <td style={{padding:"7px 8px"}}>
                      <div style={{fontSize:12,color:"#7c3aed",fontWeight:600}}>{juz.ar}</div>
                    </td>
                    {/* Sabaq — individual per Juz */}
                    <td style={{padding:"7px 8px",textAlign:"center"}}>
                      <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"center"}}>
                        <input type="number" min="0" step="0.25" value={getVal(t,"sabaq")}
                          onChange={e=>setTU(juz.n,"sabaq","value",e.target.value)}
                          style={{width:58,border:"2px solid #1a6b3c30",borderRadius:7,padding:"5px 6px",fontSize:13,textAlign:"center",fontWeight:800,color:"#1a6b3c"}}/>
                        <select value={getUnit(t,"sabaq")} onChange={e=>setTU(juz.n,"sabaq","unit",e.target.value)}
                          style={{border:"1px solid #1a6b3c20",borderRadius:5,padding:"4px 2px",fontSize:9,color:"#1a6b3c",background:"white"}}>
                          {UNITS.map(u=><option key={u.k} value={u.k}>{u.lbl}</option>)}
                        </select>
                      </div>
                    </td>
                    {/* Days to finish */}
                    <td style={{padding:"7px 8px",textAlign:"center"}}>
                      <span style={{fontSize:12,color:daysForJuz?"#374151":"#9ca3af",fontWeight:600}}>
                        {daysForJuz?`~${daysForJuz}d`:"—"}
                      </span>
                    </td>
                    {/* Sabqi — read-only display (set via range above) */}
                    <td style={{padding:"7px 8px",textAlign:"center"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#2563eb",background:"#eff6ff",borderRadius:6,padding:"4px 10px",display:"inline-block"}}>
                        {sabqi} {getUnit(t,"sabqi")}
                      </div>
                    </td>
                    {/* Manzil — read-only display (set via range above) */}
                    <td style={{padding:"7px 8px",textAlign:"center"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#5b21b6",background:"#f5f3ff",borderRadius:6,padding:"4px 10px",display:"inline-block"}}>
                        {manzil} {getUnit(t,"manzil")}
                      </div>
                    </td>
                    {/* Visual */}
                    <td style={{padding:"7px 10px",minWidth:80}}>
                      <div style={{display:"flex",flexDirection:"column",gap:3}}>
                        {[[sabaq,20,"#1a6b3c"],[sabqi,80,"#2563eb"],[manzil,160,"#5b21b6"]].map(([v,max,c],idx)=>(
                          <div key={idx} style={{height:4,background:"#f3f4f6",borderRadius:2,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${Math.min(100,(v/max)*100)}%`,background:c,borderRadius:2}}/>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:16,flexWrap:"wrap",gap:10}}>
          {projectedYears&&(
            <div style={{fontSize:13,color:projectedMatch?"#15803d":"#d97706",fontWeight:700,background:projectedMatch?"#f0fdf4":"#fffbeb",borderRadius:8,padding:"8px 14px",border:`1px solid ${projectedMatch?"#bbf7d0":"#fde68a"}`}}>
              {projectedMatch?"✅":"⚠️"} Current targets project <strong>{projectedYears} years</strong> to finish
              {!projectedMatch&&` — target is ${targetYears}yr`}
            </div>
          )}
          <button onClick={onSave} disabled={saving}
            style={{background:saving?"#9ca3af":"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"11px 30px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
            {saving?"Saving...":"💾 Save All"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- HIFZ DAILY LOG --------------------------------------------------
function HifzDailyLog({ students, getTarget }) {
  const P = "#7c3aed"; // purple
  const hifzStudents = students.filter(s=>(s.stream||"").includes("Hifz")||(s.stream||"").includes("Both"));
  const [selStudent, setSelStudent] = React.useState("");
  const [logs, setLogs] = React.useState([]);
  const [progress, setProgress] = React.useState(null);
  const [journey, setJourney] = React.useState(null); // hifz_journey record
  const [loading, setLoading] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [showSetup, setShowSetup] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [editId, setEditId] = React.useState(null);
  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3000);};
  // Setup wizard state
  const [setup, setSetup] = React.useState({stage:"qaida",qaida_lesson:1,qaida_completed:false,juz_completed:0,current_juz:30,direction:"desc",is_hafiz:false,hifz_start_date:new Date().toISOString().split("T")[0],qaida_weeks:4});
  const [quranLines, setQuranLines] = React.useState(15);

  const blank = {
    date: new Date().toISOString().split("T")[0],
    // Sabaq
    sabaq_mode:"preset",
    sabaq_juz:1, sabaq_surah:"", sabaq_from_ayat:"", sabaq_to_ayat:"",
    sabaq_from_page:"", sabaq_to_page:"",
    sabaq_preset:"quarter_juz",
    sabaq_juz2:0, sabaq_preset2:"",   // second Juz for multi-Juz sessions
    // Sabqi
    sabqi_mode:"preset",
    sabqi_juz:1, sabqi_surah:"", sabqi_from_ayat:"", sabqi_to_ayat:"",
    sabqi_from_page:"", sabqi_to_page:"",
    sabqi_preset:"half_juz",
    sabqi_juz2:0, sabqi_preset2:"",
    // Manzil
    manzil_mode:"preset",
    manzil_juz:1, manzil_surah:"", manzil_from_ayat:"", manzil_to_ayat:"",
    manzil_from_page:"", manzil_to_page:"",
    manzil_preset:"full_juz",
    manzil_juz2:0, manzil_preset2:"",
    note:"", was_present:true,
    sabaq_label:"", sabqi_label:"", qaida_lesson_override:null
  };
  const [form, setForm] = React.useState(blank);
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  React.useEffect(()=>{
    if(!selStudent) return;
    setLoading(true);
    Promise.all([
      supabase.from("hifz_daily_log").select("*").eq("student_id",selStudent).order("date",{ascending:false}),
      supabase.from("hifz_progress").select("*").eq("student_id",selStudent).maybeSingle(),
      supabase.from("hifz_journey").select("*").eq("student_id",selStudent).maybeSingle()
    ]).then(([{data:logs},{data:prog},{data:jour}])=>{
      setLogs(logs||[]);
      setProgress(prog||null);
      setJourney(jour||null);
      // Pre-fill setup from existing journey
      if(jour) setSetup({stage:jour.stage||"qaida",qaida_lesson:jour.qaida_lesson||1,qaida_completed:jour.qaida_completed||false,juz_completed:jour.juz_completed||0,current_juz:jour.current_juz||30,direction:jour.direction||"desc",is_hafiz:jour.is_hafiz||false,hifz_start_date:jour.hifz_start_date||new Date().toISOString().split("T")[0],qaida_weeks:jour.qaida_weeks||4});
      // Show setup if no journey record
      if(!jour) setShowSetup(true);
      else setShowSetup(false);
      if(prog) setForm(p=>({...p,sabaq_juz:prog.current_juz||1,sabqi_juz:prog.current_juz||1,manzil_juz:prog.current_juz||1}));
      setLoading(false);
    });
  },[selStudent]);

  const lineSet = QURAN_LINE_SETTINGS.find(s=>s.lines===quranLines)||QURAN_LINE_SETTINGS[0];
  const PAGES_PER_JUZ = lineSet.pagesPerJuz;
  const PAGES_PER_QUARTER = Math.round(PAGES_PER_JUZ/4);

  const calcPresetPages = (presetKey, juzNum, PPJ=PAGES_PER_JUZ, PPQ=PAGES_PER_QUARTER) => {
    if(!presetKey) return 0;
    if(presetKey==="full_juz") return PPJ;
    if(presetKey==="h1"||presetKey==="h2"||presetKey==="half_juz") return Math.round(PPJ/2);
    if(presetKey==="q1"||presetKey==="q2"||presetKey==="q3"||presetKey==="quarter_juz") return PPQ;
    if(presetKey==="q4") return PPJ-PPQ*3;
    if(presetKey==="3_quarter") return PPJ-PPQ;
    return 0;
  };

  const calcPages = (prefix, f) => {
    const mode = f[prefix+"_mode"];
    if(mode==="page"){
      const fp=Number(f[prefix+"_from_page"])||0, tp=Number(f[prefix+"_to_page"])||0;
      return tp>=fp?tp-fp+1:0;
    }
    if(mode==="preset"){
      const p1 = calcPresetPages(f[prefix+"_preset"], f[prefix+"_juz"]);
      let p2 = 0;
      if(f[prefix+"_juz2"]&&f[prefix+"_preset2"]){
        if(f[prefix+"_preset2"]==="custom_page2"){
          p2=Math.max(0,(Number(f[prefix+"_to_page2"])||0)-(Number(f[prefix+"_from_page2"])||0)+1);
        } else {
          p2=calcPresetPages(f[prefix+"_preset2"], f[prefix+"_juz2"]);
        }
      }
      return p1+p2;
    }
    const fa=Number(f[prefix+"_from_ayat"])||0, ta=Number(f[prefix+"_to_ayat"])||0;
    if(ta>=fa&&ta>0) return Math.max(1,Math.round((ta-fa+1)/15));
    return 0;
  };

  const PRESET_NAMES = {q1:"1st ¼",q2:"2nd ¼",q3:"3rd ¼",q4:"4th ¼",h1:"1st ½",h2:"2nd ½",full_juz:"Full Juz",half_juz:"½ Juz",quarter_juz:"¼ Juz"};
  const labelFor = (prefix, f) => {
    const mode = f[prefix+"_mode"];
    const juzN = f[prefix+"_juz"]||1;
    const juz = QURAN_JUZ.find(j=>j.n===juzN);
    if(mode==="preset"){
      const pName = PRESET_NAMES[f[prefix+"_preset"]]||f[prefix+"_preset"]||"";
      let lbl = `${pName} — پارہ ${juzN} (${juz?.ar||""})`;
      if(f[prefix+"_juz2"]&&f[prefix+"_preset2"]){
        const j2=QURAN_JUZ.find(j=>j.n===f[prefix+"_juz2"]);
        const p2Name=PRESET_NAMES[f[prefix+"_preset2"]]||f[prefix+"_preset2"]||"";
        lbl += ` + ${p2Name} — پارہ ${f[prefix+"_juz2"]} (${j2?.ar||""})`;
      }
      return lbl;
    }
    if(mode==="page"){
      const fp=f[prefix+"_from_page"], tp=f[prefix+"_to_page"];
      if(fp&&tp) return `Pages ${fp}–${tp}`;
      return "";
    }
    const surah=f[prefix+"_surah"], fa=f[prefix+"_from_ayat"], ta=f[prefix+"_to_ayat"];
    if(surah&&fa&&ta) return `${surah} ${fa}–${ta} (پارہ ${juzN})`;
    return "";
  };

  async function save() {
    if(!selStudent) return;
    setSaving(true);
    const stu = students.find(s=>s.id===selStudent);
    const isQaida = journey?.stage==="qaida";
    const sabaqPages = isQaida ? 0 : calcPages("sabaq",form);
    const sabqiPages = isQaida ? 0 : calcPages("sabqi",form);
    const manzilPages = isQaida ? 0 : calcPages("manzil",form);
    const sabaqLabel = isQaida ? (form.sabaq_label||"") : labelFor("sabaq",form);
    const sabqiLabel = isQaida ? (form.sabqi_label||"") : labelFor("sabqi",form);
    const manzilLabel = isQaida ? "" : labelFor("manzil",form);
    // Full form state for edit restoration
    const richData = {
      sabaq_juz:form.sabaq_juz, sabaq_mode:form.sabaq_mode,
      sabaq_surah:form.sabaq_surah, sabaq_from_ayat:form.sabaq_from_ayat, sabaq_to_ayat:form.sabaq_to_ayat,
      sabaq_from_page:form.sabaq_from_page, sabaq_to_page:form.sabaq_to_page, sabaq_preset:form.sabaq_preset,
      sabaq_juz2:form.sabaq_juz2, sabaq_preset2:form.sabaq_preset2,
      sabqi_juz:form.sabqi_juz, sabqi_mode:form.sabqi_mode,
      sabqi_surah:form.sabqi_surah, sabqi_from_ayat:form.sabqi_from_ayat, sabqi_to_ayat:form.sabqi_to_ayat,
      sabqi_from_page:form.sabqi_from_page, sabqi_to_page:form.sabqi_to_page, sabqi_preset:form.sabqi_preset,
      sabqi_juz2:form.sabqi_juz2, sabqi_preset2:form.sabqi_preset2,
      manzil_juz:form.manzil_juz, manzil_mode:form.manzil_mode,
      manzil_surah:form.manzil_surah, manzil_from_ayat:form.manzil_from_ayat, manzil_to_ayat:form.manzil_to_ayat,
      manzil_from_page:form.manzil_from_page, manzil_to_page:form.manzil_to_page, manzil_preset:form.manzil_preset,
      manzil_juz2:form.manzil_juz2, manzil_preset2:form.manzil_preset2,
    };
    const payload = {
      date:form.date,
      hifz_lesson: sabaqLabel||"",
      sabqi: sabqiLabel||"",
      manzil: manzilLabel||"",
      note: form.note||"",
      student_id:selStudent,
      student_name:stu?.full_name||stu?.name||"",
      class:stu?.class||stu?.["class"]||"",
      // Proper new columns
      sabaq_juz: form.sabaq_juz||1,
      sabaq_pages: sabaqPages,
      sabaq_label: sabaqLabel,
      sabqi_pages: sabqiPages,
      sabqi_label: sabqiLabel,
      manzil_pages: manzilPages,
      manzil_label: manzilLabel,
      was_present: form.was_present!==false,
      rich_data: richData,
    };
    const res = editId
      ? await supabase.from("hifz_daily_log").update(payload).eq("id",editId)
      : await supabase.from("hifz_daily_log").insert(payload).select().single();

    const sabaqJuz = form.sabaq_juz||1;
    // Update hifz_progress (source for leaderboard/tracker)
    await supabase.from("hifz_progress").upsert({
      student_id:selStudent, student_name:stu?.full_name||stu?.name||"",
      current_juz:sabaqJuz,
      current_surah:form.sabaq_surah||"",
      updated_at:new Date().toISOString()
    },{onConflict:"student_id"});
    // Also update hifz_journey to keep both in sync
    if(journey) {
      const journeyUpdate = isQaida
        ? { qaida_lesson: form.qaida_lesson_override||journey.qaida_lesson, updated_at:new Date().toISOString() }
        : { current_juz:sabaqJuz, current_surah:form.sabaq_surah||"", updated_at:new Date().toISOString() };
      await supabase.from("hifz_journey").update(journeyUpdate).eq("id",journey.id);
      setJourney(p=>p?({...p,...journeyUpdate}):p);
    }

    setSaving(false);
    if(res.error) return showToast("Error: "+res.error.message, false);
    showToast(editId?"Updated":"Log saved");
    setShowForm(false); setEditId(null);
    setForm({...blank,sabaq_juz:sabaqJuz,sabqi_juz:sabaqJuz,manzil_juz:sabaqJuz});
    const {data} = await supabase.from("hifz_daily_log").select("*").eq("student_id",selStudent).order("date",{ascending:false});
    setLogs(data||[]);
    const {data:prog} = await supabase.from("hifz_progress").select("*").eq("student_id",selStudent).maybeSingle();
    setProgress(prog||null);
  }

  async function del(id) {
    if(!window.confirm("Delete this log entry?")) return;
    await supabase.from("hifz_daily_log").delete().eq("id",id);
    showToast("Deleted"); setLogs(p=>p.filter(l=>l.id!==id));
  }

  // Section input — visual strip quarters + proper ayat boundary dropdowns
  const SectionInput = ({prefix, label, color}) => {
    const mode = form[prefix+"_mode"];
    const juzN = form[prefix+"_juz"]||1;
    const juz = QURAN_JUZ.find(j=>j.n===juzN)||QURAN_JUZ[0];
    const juzEntries = getJuzAyatEntries(juzN);
    const pages = calcPages(prefix, form);
    const juz2N = form[prefix+"_juz2"]||0;
    const lineSet = QURAN_LINE_SETTINGS.find(s=>s.lines===quranLines)||QURAN_LINE_SETTINGS[0];
    const PPJ = lineSet.pagesPerJuz;
    const PPQ = Math.round(PPJ/4);

    const onJuzChange = (n) => {
      const scrollY = window.scrollY;
      const j = QURAN_JUZ.find(j=>j.n===n)||QURAN_JUZ[0];
      setF(prefix+"_juz", n);
      setF(prefix+"_surah","");
      setF(prefix+"_from_ayat","");
      setF(prefix+"_to_ayat","");
      setF(prefix+"_preset","");
      if(mode==="page"){
        setF(prefix+"_from_page", j.start_page);
        setF(prefix+"_to_page", j.start_page + PPJ - 1);
      }
      requestAnimationFrame(()=>window.scrollTo({top:scrollY,behavior:"instant"}));
    };

    // Quarter strip: 4 quarters + 2 halves + full, each showing page range
    const stripOpts = [
      {k:"q1", lbl:"1st ¼", pp:`pp ${1}–${PPQ}`, pages:PPQ, icon:"▓░░░"},
      {k:"q2", lbl:"2nd ¼", pp:`pp ${PPQ+1}–${PPQ*2}`, pages:PPQ, icon:"░▓░░"},
      {k:"q3", lbl:"3rd ¼", pp:`pp ${PPQ*2+1}–${PPQ*3}`, pages:PPQ, icon:"░░▓░"},
      {k:"q4", lbl:"4th ¼", pp:`pp ${PPQ*3+1}–${PPJ}`, pages:PPJ-PPQ*3, icon:"░░░▓"},
      {k:"h1", lbl:"1st ½", pp:`pp 1–${PPQ*2}`, pages:PPQ*2, icon:"▓▓░░"},
      {k:"h2", lbl:"2nd ½", pp:`pp ${PPQ*2+1}–${PPJ}`, pages:PPJ-PPQ*2, icon:"░░▓▓"},
      {k:"full_juz", lbl:"Full Juz", pp:`pp 1–${PPJ}`, pages:PPJ, icon:"▓▓▓▓"},
    ];

    const selStrip = form[prefix+"_preset"]||"";
    const selStrip2 = form[prefix+"_preset2"]||"";

    // Ayat from/to with boundary enforcement
    const selSurahEntry = juzEntries.find(e=>e.name===form[prefix+"_surah"]);
    const fromAyat = Number(form[prefix+"_from_ayat"])||0;
    const toAyatMin = fromAyat||1;

    return (
      <div style={{background:`${color}0d`,borderRadius:14,padding:"14px 16px",border:`1.5px solid ${color}30`}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
          <div style={{fontWeight:800,color,fontSize:14}}>{label}</div>
          {pages>0&&<span style={{background:color,color:"white",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>{pages} pages · {pages>=PPJ?`${(pages/PPJ).toFixed(1)} Juz`:`${Math.round((pages/PPJ)*100)}% Juz`}</span>}
          <div style={{marginLeft:"auto",display:"flex",gap:4}}>
            {[["preset","Quick"],["page","Pages"],["ayat","Surah/Ayat"]].map(([m,lbl])=>(
              <button key={m} onClick={()=>{setF(prefix+"_mode",m);
                if(m==="page"){setF(prefix+"_from_page",juz.start_page);setF(prefix+"_to_page",juz.start_page+PPJ-1);}
              }}
                style={{padding:"3px 9px",borderRadius:6,border:`1px solid ${mode===m?color:"#e5e7eb"}`,background:mode===m?color:"white",color:mode===m?"white":C.muted,fontSize:11,fontWeight:700,cursor:"pointer"}}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Juz selector */}
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:160}}>
            <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>JUZ</label>
            <select value={juzN} onChange={e=>onJuzChange(Number(e.target.value))}
              style={{width:"100%",border:`1.5px solid ${color}50`,borderRadius:8,padding:"7px 10px",fontSize:13}}>
              {QURAN_JUZ.map(j=><option key={j.n} value={j.n}>پارہ {j.n} — {j.ar}</option>)}
            </select>
          </div>
          <div style={{display:"flex",alignItems:"flex-end",paddingBottom:4}}>
            <span style={{fontFamily:"'Noto Nastaliq Urdu',serif",fontSize:16,color}}>{juz.ar}</span>
          </div>
        </div>

        {/* PRESET — Visual strip */}
        {mode==="preset"&&(
          <>
            {/* Visual Juz strip */}
            <div style={{marginBottom:8}}>
              <div style={{fontSize:10,fontWeight:700,color:C.muted,marginBottom:4}}>SELECT PORTION — Juz {juzN} ({PPJ} pages, {quranLines}-line)</div>
              {/* Visual bar */}
              <div style={{display:"flex",height:24,borderRadius:8,overflow:"hidden",border:`1px solid ${color}30`,marginBottom:8}}>
                {["q1","q2","q3","q4"].map((k,i)=>{
                  const isSel = selStrip===k || (selStrip==="h1"&&i<2) || (selStrip==="h2"&&i>=2) || selStrip==="full_juz";
                  return <div key={k} style={{flex:1,background:isSel?color:`${color}20`,borderRight:i<3?`1px solid ${color}30`:"none",transition:"background .15s"}}/>;
                })}
              </div>
              {/* Buttons grid */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,marginBottom:6}}>
                {stripOpts.slice(0,4).map(opt=>{
                  const sel=selStrip===opt.k;
                  return <button key={opt.k} onClick={()=>setF(prefix+"_preset",opt.k)}
                    style={{padding:"6px 4px",borderRadius:8,border:`1.5px solid ${sel?color:"#e5e7eb"}`,background:sel?`${color}15`:"white",color:sel?color:C.muted,fontWeight:700,fontSize:11,cursor:"pointer",textAlign:"center"}}>
                    <div>{opt.lbl}</div><div style={{fontSize:9,color:C.muted,fontWeight:400}}>{opt.pp}</div><div style={{fontWeight:900,fontSize:12}}>{opt.pages}p</div>
                  </button>;
                })}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4}}>
                {stripOpts.slice(4).map(opt=>{
                  const sel=selStrip===opt.k;
                  return <button key={opt.k} onClick={()=>setF(prefix+"_preset",opt.k)}
                    style={{padding:"6px 4px",borderRadius:8,border:`1.5px solid ${sel?color:"#e5e7eb"}`,background:sel?`${color}15`:"white",color:sel?color:C.muted,fontWeight:700,fontSize:11,cursor:"pointer",textAlign:"center"}}>
                    <div>{opt.lbl}</div><div style={{fontSize:9,color:C.muted,fontWeight:400}}>{opt.pp}</div><div style={{fontWeight:900,fontSize:12}}>{opt.pages}p</div>
                  </button>;
                })}
              </div>
            </div>

            {/* 2nd Juz for multi-Juz sessions */}
            {!juz2N?(
              <button onClick={()=>{setF(prefix+"_juz2",Math.min(30,juzN+1));setF(prefix+"_preset2","h2");}}
                style={{background:"white",border:`1px dashed ${color}`,color,borderRadius:8,padding:"5px 14px",fontSize:12,fontWeight:700,cursor:"pointer",marginTop:6}}>
                + Add 2nd Juz (split session)
              </button>
            ):(
              <div style={{background:`${color}08`,borderRadius:10,padding:"12px",border:`1px solid ${color}20`,marginTop:6}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:700,color}}>2nd Portion</span>
                  <button onClick={()=>{setF(prefix+"_juz2",0);setF(prefix+"_preset2","");setF(prefix+"_from_page2","");setF(prefix+"_to_page2","");}}
                    style={{background:"#fef2f2",color:"#dc2626",border:"none",borderRadius:6,padding:"2px 8px",fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  <select value={juz2N} onChange={e=>setF(prefix+"_juz2",Number(e.target.value))}
                    style={{flex:1,border:`1.5px solid ${color}40`,borderRadius:8,padding:"6px 10px",fontSize:12}}>
                    {QURAN_JUZ.map(j=><option key={j.n} value={j.n}>پارہ {j.n} — {j.ar}</option>)}
                  </select>
                </div>
                {/* All portion options for 2nd Juz */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:3,marginBottom:4}}>
                  {stripOpts.map(opt=>{
                    const sel=selStrip2===opt.k;
                    return <button key={opt.k} onClick={()=>{setF(prefix+"_preset2",opt.k);setF(prefix+"_from_page2","");setF(prefix+"_to_page2","");}}
                      style={{padding:"4px 2px",borderRadius:7,border:`1px solid ${sel?color:"#e5e7eb"}`,background:sel?`${color}15`:"white",color:sel?color:C.muted,fontWeight:700,fontSize:10,cursor:"pointer",textAlign:"center"}}>
                      <div>{opt.lbl}</div><div style={{fontSize:9,color:C.muted}}>{opt.pages}p</div>
                    </button>;
                  })}
                </div>
                {/* Or custom pages for 2nd Juz */}
                <div style={{display:"flex",gap:6,alignItems:"center",marginTop:6}}>
                  <span style={{fontSize:10,color:C.muted,fontWeight:700,flexShrink:0}}>Or pages:</span>
                  <input type="number" placeholder="From" value={form[prefix+"_from_page2"]||""}
                    onChange={e=>{setF(prefix+"_from_page2",e.target.value);setF(prefix+"_preset2","custom_page2");}}
                    style={{width:60,border:`1px solid ${color}30`,borderRadius:6,padding:"4px 6px",fontSize:12,textAlign:"center"}}/>
                  <span style={{fontSize:10,color:C.muted}}>–</span>
                  <input type="number" placeholder="To" value={form[prefix+"_to_page2"]||""}
                    onChange={e=>{setF(prefix+"_to_page2",e.target.value);setF(prefix+"_preset2","custom_page2");}}
                    style={{width:60,border:`1px solid ${color}30`,borderRadius:6,padding:"4px 6px",fontSize:12,textAlign:"center"}}/>
                </div>
                {(selStrip2||form[prefix+"_preset2"]==="custom_page2")&&(()=>{
                  const p1=calcPresetPages(selStrip,juzN,PPJ,PPQ);
                  const p2=selStrip2==="custom_page2"
                    ? Math.max(0,(Number(form[prefix+"_to_page2"])||0)-(Number(form[prefix+"_from_page2"])||0)+1)
                    : calcPresetPages(selStrip2,juz2N,PPJ,PPQ);
                  const total=p1+p2;
                  const fullJuz=total>=PPJ;
                  return <div style={{fontSize:11,color:fullJuz?"#16a34a":C.muted,fontWeight:700,marginTop:6}}>
                    {p1} + {p2} = {total} pages {fullJuz?"✅ = 1 complete Juz!":"("+Math.round((total/PPJ)*100)+"% of Juz)"}
                  </div>;
                })()}
              </div>
            )}
          </>
        )}

        {/* PAGE MODE */}
        {mode==="page"&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {[["From Page",prefix+"_from_page",juz.start_page],["To Page",prefix+"_to_page",juz.start_page+PPJ-1]].map(([lbl,k,def])=>(
              <div key={k}>
                <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>{lbl.toUpperCase()}</label>
                <input type="number" min={juz.start_page} max={juz.end_page} value={form[k]||def}
                  onChange={e=>setF(k,e.target.value)}
                  style={{width:"100%",border:`1.5px solid ${color}40`,borderRadius:8,padding:"7px 10px",fontSize:13,textAlign:"center"}}/>
              </div>
            ))}
            {form[prefix+"_from_page"]&&form[prefix+"_to_page"]&&(
              <div style={{gridColumn:"1/-1",fontSize:11,color,fontWeight:700,background:`${color}10`,borderRadius:8,padding:"6px 10px"}}>
                {pages} pages · {pages>=PPJ?"✅ Full Juz":pages>=PPQ*2?"½ Juz":pages>=PPQ?"¼ Juz":""}
              </div>
            )}
          </div>
        )}

        {/* AYAT MODE — boundary-enforced dropdowns */}
        {mode==="ayat"&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {/* Surah dropdown filtered to this Juz */}
            <div>
              <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>SURAH (Juz {juzN})</label>
              <select value={form[prefix+"_surah"]||""} onChange={e=>{
                  const entry=juzEntries.find(en=>en.name===e.target.value);
                  setF(prefix+"_surah",e.target.value);
                  setF(prefix+"_from_ayat", entry?String(entry.from):"");
                  setF(prefix+"_to_ayat","");
                }}
                style={{width:"100%",border:`1.5px solid ${color}40`,borderRadius:8,padding:"7px 10px",fontSize:13}}>
                <option value="">-- Select Surah in Juz {juzN} --</option>
                {juzEntries.map(e=><option key={e.s} value={e.name}>{e.s}. {e.name} (Ayat {e.from}–{e.to})</option>)}
              </select>
            </div>
            {/* From/To ayat — dropdown with boundary */}
            {selSurahEntry&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>FROM AYAT (min {selSurahEntry.from})</label>
                  <select value={form[prefix+"_from_ayat"]||""} onChange={e=>{setF(prefix+"_from_ayat",e.target.value);setF(prefix+"_to_ayat","");}}
                    style={{width:"100%",border:`1.5px solid ${color}40`,borderRadius:8,padding:"7px 8px",fontSize:13}}>
                    <option value="">—</option>
                    {Array.from({length:selSurahEntry.to-selSurahEntry.from+1},(_,i)=>selSurahEntry.from+i).map(n=>(
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:10,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>TO AYAT</label>
                  <select value={form[prefix+"_to_ayat"]||""} onChange={e=>setF(prefix+"_to_ayat",e.target.value)}
                    style={{width:"100%",border:`1.5px solid ${color}40`,borderRadius:8,padding:"7px 8px",fontSize:13}}
                    disabled={!fromAyat}>
                    <option value="">—</option>
                    {fromAyat>0&&Array.from({length:selSurahEntry.to-fromAyat+1},(_,i)=>fromAyat+i).map(n=>(
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {form[prefix+"_from_ayat"]&&form[prefix+"_to_ayat"]&&(
              <div style={{fontSize:11,color,background:`${color}10`,borderRadius:8,padding:"6px 10px",fontWeight:700}}>
                {Number(form[prefix+"_to_ayat"])-Number(form[prefix+"_from_ayat"])+1} ayat ≈ {pages} page{pages!==1?"s":""}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

    const stu = hifzStudents.find(s=>s.id===selStudent);
  const target = progress ? getTarget(progress.current_juz||1) : null;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* Student selector */}
      <div style={{background:"white",borderRadius:14,padding:"18px 20px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
        <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:6}}>SELECT STUDENT</label>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <select value={selStudent} onChange={e=>{setSelStudent(e.target.value);setShowForm(false);setShowSetup(false);}}
            style={{flex:1,minWidth:220,border:`1.5px solid ${C.border}`,borderRadius:8,padding:"9px 12px",fontSize:13}}>
            <option value="">-- Select Hifz Student --</option>
            {hifzStudents.map(s=><option key={s.id} value={s.id}>{s.full_name||s.name} — {s.class||s["class"]} ({s.stream})</option>)}
          </select>
          {selStudent&&!showSetup&&(
            <button onClick={()=>{setForm({...blank,sabaq_juz:journey?.current_juz||progress?.current_juz||30,sabqi_juz:journey?.current_juz||progress?.current_juz||30,manzil_juz:journey?.current_juz||progress?.current_juz||30});setEditId(null);setShowForm(true);}}
              style={{background:"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"9px 20px",fontWeight:800,fontSize:13,cursor:"pointer"}}>
              + Add Log
            </button>
          )}
          {selStudent&&journey&&(
            <button onClick={()=>setShowSetup(v=>!v)}
              style={{background:C.bg,color:C.muted,border:`1px solid ${C.border}`,borderRadius:10,padding:"9px 14px",fontWeight:600,fontSize:12,cursor:"pointer"}}>
              ⚙️ Setup
            </button>
          )}
        </div>
        {/* Setup Wizard */}
        {selStudent&&showSetup&&(()=>{
          const saveSetup = async() => {
            const stu = hifzStudents.find(s=>s.id===selStudent);
            const curJuz = Number(setup.current_juz)||(setup.direction==="desc"?30:1);
            const autoCompleted = setup.direction==="desc" ? Math.max(0,30-curJuz) : Math.max(0,curJuz-1);
            const juzCompleted = Number(setup.juz_completed)>=0 ? Number(setup.juz_completed) : autoCompleted;
            const payload = {
              student_id: selStudent,
              student_name: stu?.full_name||stu?.name||"",
              stage: setup.stage,
              qaida_lesson: Number(setup.qaida_lesson)||1,
              qaida_completed: setup.stage==="quran"||setup.stage==="hafiz",
              juz_completed: juzCompleted,
              current_juz: curJuz,
              direction: setup.direction||"desc",
              is_hafiz: setup.stage==="hafiz",
              setup_done: true,
              hifz_start_date: setup.hifz_start_date||new Date().toISOString().split("T")[0],
              qaida_weeks: Number(setup.qaida_weeks)||4,
              updated_at: new Date().toISOString()
            };
            // Single upsert keyed on student_id — works for both insert and update
            const {data, error} = await supabase
              .from("hifz_journey")
              .upsert(payload, {onConflict:"student_id"})
              .select().single();
            if(error){ showToast("Error: "+error.message,false); return; }
            setJourney(data);
            setShowSetup(false);
            // Keep hifz_progress in sync
            await supabase.from("hifz_progress").upsert({
              student_id: selStudent,
              student_name: stu?.full_name||stu?.name||"",
              current_juz: curJuz,
              updated_at: new Date().toISOString()
            },{onConflict:"student_id"});
            const {data:prog2} = await supabase.from("hifz_progress").select("*").eq("student_id",selStudent).maybeSingle();
            setProgress(prog2||null);
            showToast("Journey saved!");
          };
          return (
            <div className="fade-up" style={{marginTop:14,background:"#f5f3ff",borderRadius:12,padding:"16px",border:"1.5px solid #ddd6fe"}}>
              <div style={{fontWeight:800,color:"#4c1d95",fontSize:14,marginBottom:12}}>🎓 Student Hifz Journey Setup</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:12}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>CURRENT STAGE</label>
                  <select value={setup.stage} onChange={e=>setSetup(p=>({...p,stage:e.target.value}))}
                    style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}>
                    <option value="qaida">📗 Noorani Qaida</option>
                    <option value="quran">📖 Quran (Active Hifz)</option>
                    <option value="hafiz">🎓 Hafiz (Revision only)</option>
                  </select>
                </div>
                {setup.stage==="qaida"&&(
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>CURRENT LESSON</label>
                    <input type="number" min="1" value={setup.qaida_lesson} onChange={e=>setSetup(p=>({...p,qaida_lesson:Number(e.target.value)}))}
                      style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                  </div>
                )}
                {setup.stage==="qaida"&&(
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>QAIDA TARGET WEEKS</label>
                    <input type="number" min="1" max="52" value={setup.qaida_weeks||4} onChange={e=>setSetup(p=>({...p,qaida_weeks:Number(e.target.value)}))}
                      style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                    <div style={{fontSize:10,color:"#7c3aed",marginTop:2}}>How many weeks to complete Noorani Qaida</div>
                  </div>
                )}
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>HIFZ START DATE</label>
                  <input type="date" value={setup.hifz_start_date||new Date().toISOString().split("T")[0]} onChange={e=>setSetup(p=>({...p,hifz_start_date:e.target.value}))}
                    style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                  <div style={{fontSize:10,color:"#7c3aed",marginTop:2}}>Used to calculate pace vs expected progress</div>
                </div>
                {(setup.stage==="quran"||setup.stage==="hafiz")&&(
                  <>
                    <div style={{gridColumn:"1/-1"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,background:"#ede9fe",borderRadius:8,padding:"8px 12px"}}>
                        <span style={{fontSize:12,color:"#4c1d95",fontWeight:600}}>📖 Memorization direction:</span>
                        <label style={{fontSize:12,color:"#4c1d95",display:"flex",alignItems:"center",gap:4,cursor:"pointer"}}>
                          <input type="radio" name="dir" value="desc" checked={(setup.direction||"desc")==="desc"} onChange={()=>setSetup(p=>({...p,direction:"desc",current_juz:30,juz_completed:0}))}/>
                          Juz 30 → 1 <span style={{color:"#7c3aed",fontSize:11}}>(standard)</span>
                        </label>
                        <label style={{fontSize:12,color:"#4c1d95",display:"flex",alignItems:"center",gap:4,cursor:"pointer",marginLeft:12}}>
                          <input type="radio" name="dir" value="asc" checked={setup.direction==="asc"} onChange={()=>setSetup(p=>({...p,direction:"asc",current_juz:1,juz_completed:0}))}/>
                          Juz 1 → 30 <span style={{color:"#9ca3af",fontSize:11}}>(rare/exceptional)</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>CURRENT JUZ</label>
                      <select value={setup.current_juz} onChange={e=>{
                        const juz=Number(e.target.value);
                        const auto=setup.direction==="desc"?Math.max(0,30-juz):Math.max(0,juz-1);
                        setSetup(p=>({...p,current_juz:juz,juz_completed:auto}));
                      }}
                        style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}>
                        {QURAN_JUZ.map(j=><option key={j.n} value={j.n}>پارہ {j.n} — {j.ar}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:3}}>JUZ ALREADY MEMORIZED</label>
                      <input type="number" min="0" max="30" value={setup.juz_completed} onChange={e=>setSetup(p=>({...p,juz_completed:Number(e.target.value)}))}
                        style={{width:"100%",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 10px",fontSize:13}}/>
                      <div style={{fontSize:10,color:"#7c3aed",marginTop:2}}>Auto-calculated · edit if needed</div>
                    </div>
                  </>
                )}
              </div>
              {setup.stage==="quran"&&(
                <div style={{background:"#ede9fe",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#4c1d95",marginBottom:10,fontWeight:600}}>
                  {setup.direction==="desc"?"📖 Starting from Juz 30 (standard — memorizing backwards to Juz 1)":"📖 Starting from Juz 1 (memorizing forward to Juz 30)"}
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                <button onClick={saveSetup} style={{background:"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"9px 20px",fontWeight:800,fontSize:13,cursor:"pointer"}}>💾 Save Journey</button>
                <button onClick={()=>setShowSetup(false)} style={{background:"white",border:"1px solid #ddd6fe",borderRadius:10,padding:"9px 16px",fontWeight:600,cursor:"pointer",color:C.muted}}>Cancel</button>
              </div>
            </div>
          );
        })()}
        {/* Student current status */}
        {selStudent&&!showSetup&&(journey||progress)&&(()=>{
          const isQaida = journey?.stage==="qaida";
          const isHafiz = journey?.stage==="hafiz";
          const curJuz = journey?.current_juz||progress?.current_juz||1;
          const juzData = QURAN_JUZ.find(j=>j.n===curJuz);
          const lastLog = logs[0];
          return (
            <div style={{display:"flex",gap:10,marginTop:12,flexWrap:"wrap"}}>
              {/* Stage badge */}
              <div style={{background:isQaida?"#fef9c3":isHafiz?"#f0fdf4":"#f5f3ff",borderRadius:10,padding:"10px 14px",flexShrink:0}}>
                <div style={{fontSize:10,fontWeight:700,color:isQaida?"#92400e":isHafiz?"#1a6b3c":"#4c1d95"}}>STAGE</div>
                <div style={{fontWeight:900,fontSize:14,color:isQaida?"#78350f":isHafiz?"#166534":"#4c1d95"}}>
                  {isQaida?"📗 Qaida L"+journey.qaida_lesson:isHafiz?"🎓 Hafiz":"📖 Quran"}
                </div>
              </div>
              {!isQaida&&<div style={{background:"#f5f3ff",borderRadius:10,padding:"10px 14px",flex:1,minWidth:140}}>
                <div style={{fontSize:10,color:"#7c3aed",fontWeight:700}}>CURRENT JUZ {journey?.direction==="desc"?"(30→1)":"(1→30)"}</div>
                <div style={{fontWeight:900,color:"#4c1d95",fontSize:17}}>پارہ {curJuz} — {QURAN_JUZ.find(q=>q.n===curJuz)?.ar||""}</div>
                <div style={{fontSize:10,color:"#7c3aed"}}>{juzData?.ar}</div>
              </div>}
              {!isQaida&&journey?.juz_completed>0&&<div style={{background:"#ecfdf5",borderRadius:10,padding:"10px 14px",flexShrink:0}}>
                <div style={{fontSize:10,color:C.primary,fontWeight:700}}>COMPLETED</div>
                <div style={{fontWeight:900,color:C.primary,fontSize:17}}>{journey.juz_completed} Juz</div>
              </div>}
              {lastLog&&!isQaida&&(
                <div style={{background:"#fffbeb",borderRadius:10,padding:"10px 14px",flex:1,minWidth:140}}>
                  <div style={{fontSize:10,color:"#92400e",fontWeight:700}}>LAST SESSION</div>
                  <div style={{fontSize:11,color:"#78350f",fontWeight:600,marginTop:2}}>سبق: {lastLog.sabaq_label||lastLog.hifz_lesson||"—"}</div>
                  <div style={{fontSize:10,color:"#92400e"}}>منزل: {lastLog.manzil_label||lastLog.manzil||"—"}</div>
                </div>
              )}
              {target&&!isQaida&&<div style={{background:"#f0fdf4",borderRadius:10,padding:"10px 14px",flex:1,minWidth:140}}>
                <div style={{fontSize:10,color:C.primary,fontWeight:700}}>TODAY'S TARGETS</div>
                <div style={{fontSize:11,color:C.text,marginTop:2}}>سبق: <strong>{typeof target.sabaq==="object"?target.sabaq.value:target.sabaq}p</strong> · منزل: <strong>{typeof target.manzil==="object"?target.manzil.value:target.manzil}p</strong></div>
              </div>}
            </div>
          );
        })()}
      </div>

      {/* Log entry form */}
      {showForm&&(
        <div className="fade-up" style={{background:"white",borderRadius:16,padding:24,border:"1.5px solid #ddd6fe",boxShadow:"0 4px 20px #7c3aed20"}}>
          <div style={{fontWeight:800,color:"#4c1d95",fontSize:16,marginBottom:12}}>{editId?"Edit":"New"} {journey?.stage==="qaida"?"Qaida":"Hifz"} Daily Log</div>
          {/* Qaida quick note */}
          {journey?.stage==="qaida"&&(
            <div style={{background:"#fef9c3",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e",fontWeight:600,marginBottom:12}}>
              📗 نورانی قاعدہ — Currently on Lesson {journey.qaida_lesson}. Log as usual using سبق/سبقی fields below. Use سبق label field for lesson description.
            </div>
          )}

          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap",alignItems:"flex-end"}}>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,fontWeight:700,color:C.muted}}>DATE</label>
              <input type="date" value={form.date} onChange={e=>setF("date",e.target.value)}
                style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
            </div>
            {journey?.stage!=="qaida"&&<div style={{display:"flex",flexDirection:"column",gap:4}}>
              <label style={{fontSize:11,fontWeight:700,color:C.muted}}>QURAN SETTING</label>
              <select value={quranLines} onChange={e=>setQuranLines(Number(e.target.value))}
                style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}>
                {QURAN_LINE_SETTINGS.map(s=><option key={s.lines} value={s.lines}>{s.label}</option>)}
              </select>
            </div>}
            <div style={{display:"flex",alignItems:"center",gap:8,paddingBottom:8}}>
              <label style={{fontSize:13,fontWeight:700,color:C.text,display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
                <input type="checkbox" checked={form.was_present} onChange={e=>setF("was_present",e.target.checked)}/>
                Present
              </label>
            </div>
          </div>

          {/* Qaida simple fields */}
          {journey?.stage==="qaida"&&(
            <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#92400e",display:"block",marginBottom:4}}>📗 سبق (SABAQ) — LESSON DESCRIPTION</label>
                  <input value={form.sabaq_label||""} onChange={e=>setF("sabaq_label",e.target.value)}
                    placeholder={`e.g. Lesson ${journey.qaida_lesson} — letters ب ت ث`}
                    style={{width:"100%",border:"1.5px solid #fde68a",borderRadius:8,padding:"8px 12px",fontSize:13,background:"#fffbeb"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#1e40af",display:"block",marginBottom:4}}>📘 سبقی (SABQI) — REVISION (yesterday's lesson)</label>
                  <input value={form.sabqi_label||""} onChange={e=>setF("sabqi_label",e.target.value)}
                    placeholder={`e.g. Lesson ${Math.max(1,journey.qaida_lesson-1)} revision`}
                    style={{width:"100%",border:"1.5px solid #bfdbfe",borderRadius:8,padding:"8px 12px",fontSize:13,background:"#eff6ff"}}/>
                </div>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#4c1d95",display:"block",marginBottom:4}}>📕 NORANI QAIDA LESSON # (updates student lesson)</label>
                <input type="number" min="1" max="40" value={form.qaida_lesson_override||journey.qaida_lesson||1}
                  onChange={e=>setF("qaida_lesson_override",Number(e.target.value))}
                  style={{width:"120px",border:"1.5px solid #ddd6fe",borderRadius:8,padding:"8px 12px",fontSize:13,background:"#f5f3ff"}}/>
              </div>
            </div>
          )}

          {journey?.stage!=="qaida"&&<div style={{display:"flex",flexDirection:"column",gap:14}}>
            {SectionInput({prefix:"sabaq", label:"📗 سبق (Sabaq) — نئی سبق", color:"#1a6b3c"})}
            {SectionInput({prefix:"sabqi", label:"📘 سبقی (Sabqi) — کل کی سبق", color:"#2563eb"})}
            {journey?.stage!=="hafiz"&&SectionInput({prefix:"manzil", label:"📕 منزل (Manzil) — طویل ترمیم", color:"#7c3aed"})}
            {journey?.stage==="hafiz"&&SectionInput({prefix:"manzil", label:"📕 منزل (Manzil) — مراجعہ", color:"#7c3aed"})}
          </div>}

          <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:4}}>
            <label style={{fontSize:11,fontWeight:700,color:C.muted}}>TEACHER'S NOTE</label>
            <textarea value={form.note} onChange={e=>setF("note",e.target.value)} rows={2}
              placeholder="Observations, issues, praise..."
              style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical"}}/>
          </div>

          {/* Summary before saving */}
          {(calcPages("sabaq",form)||calcPages("sabqi",form)||calcPages("manzil",form))>0&&target&&(
            <div style={{marginTop:14,background:"#f5f3ff",borderRadius:10,padding:"12px 16px",border:"1px solid #ddd6fe"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#4c1d95",marginBottom:6}}>📊 Today's Summary vs Target</div>
              <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                {[["سبق","sabaq","#1a6b3c"],["سبقی","sabqi","#2563eb"],["منزل","manzil","#7c3aed"]].map(([lbl,pfx,col])=>{
                  const actual=calcPages(pfx,form);
                  const rawT=target?.[pfx]||0;
                  const tgt=typeof rawT==="object"?(rawT.unit==="juz"?(rawT.value||0)*20:rawT.unit==="ayat"?Math.round((rawT.value||0)/15):rawT.unit==="surah"?Math.round((rawT.value||0)*1.5):rawT.value||0):rawT;
                  const ok=actual>=tgt;
                  return (
                    <div key={pfx} style={{textAlign:"center"}}>
                      <div style={{fontSize:11,color:col,fontWeight:700}}>{lbl}</div>
                      <div style={{fontWeight:900,fontSize:16,color:ok?"#1a6b3c":"#e11d48"}}>{actual} pages</div>
                      <div style={{fontSize:10,color:C.muted}}>Target: {tgt} pages</div>
                      <div style={{fontSize:10,fontWeight:700,color:ok?"#1a6b3c":"#e11d48"}}>{ok?"✅ On track":"⚠️ Behind"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{display:"flex",gap:10,marginTop:16}}>
            <button onClick={save} disabled={saving}
              style={{flex:1,background:saving?"#9ca3af":"#4c1d95",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:800,fontSize:14,cursor:saving?"not-allowed":"pointer"}}>
              {saving?"Saving...":"💾 Save Log"}
            </button>
            <button onClick={()=>{setShowForm(false);setEditId(null);}}
              style={{padding:"11px 20px",background:"white",border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Log history */}
      {selStudent&&!loading&&(
        <div style={{background:"white",borderRadius:16,padding:"20px 24px",boxShadow:`0 2px 12px ${C.shadow}`}}>
          <div style={{fontWeight:800,fontSize:15,color:C.text,marginBottom:14}}>{logs.length} Log Entries</div>
          {logs.length===0
            ? <div style={{textAlign:"center",padding:32,color:C.muted,fontSize:13}}>No logs yet. Add the first one above.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {logs.map(log=>{
                  const rich = log.rich_data||{};
                  const displayNote = log.note||"";
                  const sabaqLbl = log.sabaq_label||log.hifz_lesson||"";
                  const sabqiLbl = log.sabqi_label||log.sabqi||"";
                  const manzilLbl = log.manzil_label||log.manzil||"";
                  const sabaqPg = log.sabaq_pages||0;
                  const sabqiPg = log.sabqi_pages||0;
                  const manzilPg = log.manzil_pages||0;
                  const wasPresent = log.was_present!==false;
                  return (
                  <div key={log.id} style={{borderRadius:12,border:`1px solid ${C.border}`,overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",background:"#faf5ff",flexWrap:"wrap"}}>
                      <div style={{background:"#4c1d95",color:"white",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:800,flexShrink:0}}>
                        {new Date(log.date).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                      </div>
                      {rich.sabaq_juz&&<div style={{fontSize:11,color:"#7c3aed",fontWeight:700}}>پارہ {rich.sabaq_juz} — {QURAN_JUZ.find(q=>q.n===rich.sabaq_juz)?.ar||""}</div>}
                      {!wasPresent&&<span style={{background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>Absent</span>}
                      <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
                        <button onClick={()=>{
                          const restored = {...blank,...rich,date:log.date,note:log.note||"",
                            sabaq_juz:log.sabaq_juz||rich.sabaq_juz||1,
                            was_present:log.was_present!==false};
                          setForm(restored);setEditId(log.id);setShowForm(true);
                        }} style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Edit</button>
                        <button onClick={()=>del(log.id)}
                          style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Del</button>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:0,borderTop:`1px solid ${C.border}`}}>
                      {[
                        {label:"📗 سبق (Sabaq)",lbl:sabaqLbl,pages:sabaqPg,color:"#1a6b3c",bg:"#f0fdf4"},
                        {label:"📘 سبقی (Sabqi)",lbl:sabqiLbl,pages:sabqiPg,color:"#2563eb",bg:"#eff6ff"},
                        {label:"📕 منزل (Manzil)",lbl:manzilLbl,pages:manzilPg,color:"#7c3aed",bg:"#f5f3ff"},
                      ].map((s,i)=>(
                        <div key={s.label} style={{padding:"10px 14px",background:s.bg,borderRight:i<2?`1px solid ${C.border}`:"none"}}>
                          <div style={{fontSize:11,fontWeight:700,color:s.color,marginBottom:2}}>{s.label}</div>
                          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{s.lbl||"—"}</div>
                          {s.pages>0&&<div style={{fontSize:10,color:s.color,marginTop:2}}>{s.pages} page{s.pages!==1?"s":""}</div>}
                        </div>
                      ))}
                    </div>
                    {displayNote&&<div style={{padding:"8px 16px",fontSize:12,color:C.muted,fontStyle:"italic",borderTop:`1px solid ${C.border}`}}>{displayNote}</div>}
                  </div>
                  );
                })}
              </div>
          }
        </div>
      )}
    </div>
  );
}

// -- HIFZ PROGRESS TRACKER ------------------------------------------
// ── HifzChartView — switchable chart panel ──────────────────────────
// Proper React component so hooks work correctly
function HifzChartView({ entry, last14, target, period, stuLogs, JUZ_PP, sumP, toJuz }) {
  const [chartMode, setChartMode] = React.useState(0); // 0=heatmap, 1=bars, 2=rings, 3=pace
  const [hoveredDay, setHoveredDay] = React.useState(null);

  const MODES = [
    {id:0, icon:"🔥", label:"Heatmap"},
    {id:1, icon:"📊", label:"Bar Chart"},
    {id:2, icon:"⭕", label:"Progress Rings"},
    {id:3, icon:"🎯", label:"Pace vs Target"},
  ];

  const tSabaq  = typeof target.sabaq==="object"  ? target.sabaq.value  : target.sabaq||0;
  const tSabqi  = typeof target.sabqi==="object"  ? target.sabqi.value  : target.sabqi||0;
  const tManzil = typeof target.manzil==="object" ? target.manzil.value : target.manzil||0;

  const periodLogs  = entry.periodLogs||[];
  const sabaqPages  = sumP(periodLogs,"sabaq_pages");
  const sabqiPages  = sumP(periodLogs,"sabqi_pages");
  const manzilPages = sumP(periodLogs,"manzil_pages");
  const periodDays  = period==="week"?7:30;
  const tSabaqP = tSabaq*periodDays, tSabqiP = tSabqi*periodDays, tManzilP = tManzil*periodDays;

  // Pace data
  const nowD = new Date();
  const monthStart2 = new Date(nowD.getFullYear(),nowD.getMonth(),1);
  const daysPassed = Math.max(1,nowD.getDate());
  const monthLogs2 = periodLogs.filter(l=>new Date(l.date)>=monthStart2);
  const actualSabaq  = sumP(monthLogs2,"sabaq_pages");
  const actualSabqi  = sumP(monthLogs2,"sabqi_pages");
  const actualManzil = sumP(monthLogs2,"manzil_pages");
  const expSabaq  = tSabaq*daysPassed;
  const expSabqi  = tSabqi*daysPassed;
  const expManzil = tManzil*daysPassed;
  const delta = actualSabaq - expSabaq;
  const isAhead = delta>=0;

  const BARS = [
    {label:"Sabaq",  actual:actualSabaq,  expected:expSabaq,  total:sabaqPages,  target:tSabaqP,  color:"#4c1d95",light:"#ddd6fe",bg:"#f5f3ff"},
    {label:"Sabqi",  actual:actualSabqi,  expected:expSabqi,  total:sabqiPages,  target:tSabqiP,  color:"#2563eb",light:"#bfdbfe",bg:"#eff6ff"},
    {label:"Manzil", actual:actualManzil, expected:expManzil, total:manzilPages, target:tManzilP, color:"#0d9488",light:"#99f6e4",bg:"#f0fdfa"},
  ];

  // ── View: Heatmap ──────────────────────────────────────────────
  const ViewHeatmap = () => (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(14,1fr)",gap:4,marginBottom:4}}>
        {last14.map(({ds,log,dayNum,dayName})=>{
          const absent=log&&log.was_present===false;
          const sp=Number(log?.sabaq_pages)||0;
          const isHov=hoveredDay===ds;
          const bg=!log?"#f3f4f6":absent?"#fecaca":sp>=tSabaq?"#4c1d95":sp>0?"#a78bfa":"#ddd6fe";
          return (
            <div key={ds} onClick={()=>setHoveredDay(isHov?null:ds)}
              style={{cursor:"pointer",transition:"transform .12s",transform:isHov?"scale(1.18)":"scale(1)",zIndex:isHov?10:1,position:"relative"}}>
              <div style={{aspectRatio:"1",background:bg,borderRadius:5,border:`2px solid ${isHov?"#1f2937":absent?"#fca5a5":!log?"#e5e7eb":"transparent"}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:isHov?"0 3px 10px rgba(0,0,0,0.25)":"none"}}>
                <span style={{fontSize:8,color:!log||absent?"#9ca3af":"white",fontWeight:700}}>{dayNum}</span>
              </div>
              <div style={{fontSize:7,color:"#9ca3af",textAlign:"center",marginTop:2}}>{dayName.slice(0,2)}</div>
            </div>
          );
        })}
      </div>
      {/* Clicked day detail */}
      {hoveredDay&&(()=>{
        const d=last14.find(x=>x.ds===hoveredDay);
        const log=d?.log;
        return (
          <div style={{background:"#f5f3ff",borderRadius:10,padding:"12px 14px",marginTop:8,border:"1px solid #ddd6fe"}}>
            <div style={{fontWeight:700,color:"#4c1d95",fontSize:13,marginBottom:8}}>
              {new Date(hoveredDay).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}
            </div>
            {!log?<div style={{fontSize:12,color:"#9ca3af"}}>No log recorded</div>
            :log.was_present===false?<div style={{fontSize:14,fontWeight:700,color:"#dc2626"}}>❌ Absent</div>
            :<div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              {[["📗 Sabaq",log.sabaq_pages||0,log.sabaq_label,"#4c1d95","#f5f3ff"],
                ["📘 Sabqi",log.sabqi_pages||0,log.sabqi_label,"#2563eb","#eff6ff"],
                ["📕 Manzil",log.manzil_pages||0,log.manzil_label,"#0d9488","#f0fdfa"]
              ].map(([lbl,pages,lbl2,col,bg])=>(
                <div key={lbl} style={{background:bg,borderRadius:8,padding:"10px 8px",textAlign:"center"}}>
                  <div style={{fontSize:11,color:col,fontWeight:700}}>{lbl}</div>
                  <div style={{fontWeight:900,fontSize:22,color:col,lineHeight:1.1}}>{pages}</div>
                  <div style={{fontSize:10,color:"#9ca3af"}}>pages</div>
                  {lbl2&&<div style={{fontSize:9,color:col,marginTop:3,fontStyle:"italic"}}>{lbl2}</div>}
                </div>
              ))}
              {log.note&&<div style={{gridColumn:"1/-1",fontSize:11,color:"#6b7280",background:"white",borderRadius:6,padding:"6px 10px"}}>📝 {log.note}</div>}
            </div>}
          </div>
        );
      })()}
      <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
        {[["#4c1d95","On/Above Target"],["#a78bfa","Below Target"],["#ddd6fe","Present"],["#fecaca","Absent"],["#f3f4f6","No Log"]].map(([c,l])=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:4}}>
            <div style={{width:10,height:10,borderRadius:2,background:c,border:c==="#f3f4f6"?"1px solid #d1d5db":"none",flexShrink:0}}/>
            <span style={{fontSize:9,color:"#6b7280"}}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── View: Grouped Bar Chart (period totals) ────────────────────
  const ViewBars = () => {
    const maxVal = Math.max(...BARS.map(b=>Math.max(b.total,b.target||0)),1);
    return (
      <div>
        <div style={{display:"flex",gap:12,alignItems:"flex-end",height:160,marginBottom:8}}>
          {BARS.map(bar=>{
            const actH = Math.max(4,Math.round((bar.total/maxVal)*140));
            const tgtH = Math.max(4,Math.round(((bar.target||0)/maxVal)*140));
            const ok = bar.target>0 ? bar.total>=bar.target : true;
            return (
              <div key={bar.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",height:"100%",justifyContent:"flex-end",gap:3}}>
                <div style={{fontSize:11,fontWeight:800,color:ok?"#15803d":"#dc2626"}}>{ok?"✅":"⚠️"}</div>
                <div style={{display:"flex",gap:4,alignItems:"flex-end",width:"100%",height:140,justifyContent:"center"}}>
                  <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",alignItems:"center",gap:2}}>
                    <div style={{fontSize:10,fontWeight:700,color:bar.color}}>{bar.total}</div>
                    <div style={{width:"100%",background:bar.color,borderRadius:"6px 6px 0 0",minHeight:4,height:actH}}/>
                  </div>
                  {bar.target>0&&(
                    <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",alignItems:"center",gap:2}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#9ca3af"}}>{bar.target}</div>
                      <div style={{width:"100%",background:bar.light,border:`2px dashed ${bar.color}`,borderRadius:"6px 6px 0 0",minHeight:4,height:tgtH}}/>
                    </div>
                  )}
                </div>
                <div style={{fontWeight:800,fontSize:12,color:"#374151"}}>{bar.label}</div>
                <div style={{fontSize:9,color:"#9ca3af"}}>pages</div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:14,justifyContent:"center",marginTop:4}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:14,height:10,background:"#4c1d95",borderRadius:2}}/><span style={{fontSize:10,color:"#6b7280"}}>Actual</span></div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:14,height:10,background:"#ddd6fe",border:"2px dashed #4c1d95",borderRadius:2}}/><span style={{fontSize:10,color:"#6b7280"}}>Target for period</span></div>
        </div>
      </div>
    );
  };

  // ── View: Progress Rings ───────────────────────────────────────
  const ViewRings = () => {
    const Ring = ({color,pct,label,actual,tgt,size=80,stroke=10}) => {
      const r = (size-stroke)/2;
      const circ = 2*Math.PI*r;
      const dash = Math.min(pct/100,1)*circ;
      const cx=size/2, cy=size/2;
      return (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
          <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth={stroke}/>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={stroke}
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
              style={{transition:"stroke-dasharray .5s ease"}}/>
          </svg>
          <div style={{textAlign:"center",marginTop:-size/2-6}}>
            <div style={{fontWeight:900,fontSize:16,color,lineHeight:1}}>{pct}%</div>
          </div>
          <div style={{textAlign:"center",marginTop:size/2-8}}>
            <div style={{fontWeight:700,fontSize:12,color:"#374151"}}>{label}</div>
            <div style={{fontSize:11,color:"#6b7280"}}>{actual} / {tgt} pages</div>
            <div style={{fontSize:10,color:pct>=100?"#15803d":"#f97316",fontWeight:700}}>{pct>=100?"✅ On target":"⚠️ "+Math.max(0,tgt-actual)+" pages to go"}</div>
          </div>
        </div>
      );
    };
    return (
      <div style={{display:"flex",justifyContent:"space-around",gap:12,flexWrap:"wrap",padding:"8px 0"}}>
        {BARS.map(b=>{
          const tgt = Math.round(b.target)||0;
          const pct = tgt>0?Math.min(150,Math.round((b.total/tgt)*100)):0;
          return <Ring key={b.label} color={b.color} pct={pct} label={b.label} actual={b.total} tgt={tgt} size={90} stroke={10}/>;
        })}
      </div>
    );
  };

  // ── View: Pace vs Target (today's progress vs expected) ─────────
  const ViewPace = () => {
    const nowD2 = new Date();
    if(period!=="month") return <div style={{textAlign:"center",padding:20,color:"#9ca3af",fontSize:13}}>Switch to Month view to see Pace vs Target</div>;
    const maxVal = Math.max(...BARS.map(b=>Math.max(b.actual,b.expected)),1);
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:6}}>
          <div style={{fontSize:12,color:"#6b7280"}}>Day {daysPassed} of {nowD2.toLocaleString("default",{month:"long"})} — {Math.round((daysPassed/new Date(nowD2.getFullYear(),nowD2.getMonth()+1,0).getDate())*100)}% through month</div>
          <div style={{background:isAhead?"#dcfce7":"#fef2f2",color:isAhead?"#15803d":"#dc2626",borderRadius:20,padding:"4px 14px",fontSize:12,fontWeight:800}}>
            {isAhead?"✅ Ahead":"⚠️ Behind"} by {Math.abs(delta).toFixed(1)} pages (Sabaq)
          </div>
        </div>
        <div style={{display:"flex",gap:14,alignItems:"flex-end",height:150,marginBottom:8}}>
          {BARS.map(bar=>{
            const actH=Math.max(4,Math.round((bar.actual/maxVal)*130));
            const expH=Math.max(4,Math.round((bar.expected/maxVal)*130));
            const ok=bar.actual>=bar.expected;
            return (
              <div key={bar.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",height:"100%",justifyContent:"flex-end",gap:3}}>
                <div style={{fontSize:11,fontWeight:800,color:ok?"#15803d":"#dc2626"}}>{ok?"✅":"⚠️"}</div>
                <div style={{display:"flex",gap:3,alignItems:"flex-end",width:"100%",height:130,justifyContent:"center"}}>
                  <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",alignItems:"center",gap:2}}>
                    <div style={{fontSize:10,fontWeight:700,color:bar.color}}>{bar.actual}</div>
                    <div style={{width:"100%",background:bar.color,borderRadius:"5px 5px 0 0",minHeight:4,height:actH}}/>
                  </div>
                  <div style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",alignItems:"center",gap:2}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#9ca3af"}}>{bar.expected.toFixed(0)}</div>
                    <div style={{width:"100%",background:bar.light,border:`2px dashed ${bar.color}`,borderRadius:"5px 5px 0 0",minHeight:4,height:expH}}/>
                  </div>
                </div>
                <div style={{fontWeight:800,fontSize:12,color:"#374151"}}>{bar.label}</div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:14,justifyContent:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:14,height:10,background:"#4c1d95",borderRadius:2}}/><span style={{fontSize:10,color:"#6b7280"}}>Done so far</span></div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:14,height:10,background:"#ddd6fe",border:"2px dashed #4c1d95",borderRadius:2}}/><span style={{fontSize:10,color:"#6b7280"}}>Expected by day {daysPassed}</span></div>
        </div>
      </div>
    );
  };

  const views = [<ViewHeatmap/>, <ViewBars/>, <ViewRings/>, <ViewPace/>];
  const bgColors = ["white","white","white","white"];

  return (
    <div style={{background:"white",borderRadius:14,border:`1px solid ${C.border}`,overflow:"hidden"}}>
      {/* Switcher header */}
      <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8,background:"#fafafa"}}>
        <div style={{fontWeight:700,color:"#374151",fontSize:13}}>
          {MODES[chartMode].icon} {MODES[chartMode].label}
          {chartMode===0&&" — tap any day for details"}
        </div>
        {/* Pill switcher */}
        <div style={{display:"flex",gap:4,background:"#f3f4f6",borderRadius:10,padding:3}}>
          {MODES.map(m=>(
            <button key={m.id} onClick={()=>{setChartMode(m.id);setHoveredDay(null);}}
              style={{
                background:chartMode===m.id?"white":"transparent",
                color:chartMode===m.id?"#4c1d95":"#6b7280",
                border:"none",borderRadius:7,
                padding:"5px 10px",
                fontWeight:chartMode===m.id?800:600,
                fontSize:12,cursor:"pointer",
                boxShadow:chartMode===m.id?"0 1px 4px rgba(0,0,0,0.12)":"none",
                transition:"all .15s",
                whiteSpace:"nowrap",
                display:"flex",alignItems:"center",gap:4,
              }}>
              <span>{m.icon}</span>
              <span style={{fontSize:11}}>{m.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Chart body */}
      <div style={{padding:"14px 16px"}}>
        {views[chartMode]}
      </div>
    </div>
  );
}

function HifzProgressTracker({ students, getTarget, targetYears=3.5 }) {
  const P = "#7c3aed";
  const hifzStudents = students.filter(s=>(s.stream||"").includes("Hifz")||(s.stream||"").includes("Both"));
  const [progressList, setProgressList] = React.useState([]);
  const [journeyList, setJourneyList] = React.useState([]);
  const [logs, setLogs] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState("leaderboard");
  const [detailStu, setDetailStu] = React.useState(null);
  const [period, setPeriod] = React.useState("month");
  const [prevMonth, setPrevMonth] = React.useState(0); // 0=current, 1=last month, 2=2 months ago
  const [lbView, setLbView] = React.useState("list"); // list | chart

  React.useEffect(()=>{
    Promise.all([
      supabase.from("hifz_progress").select("*"),
      supabase.from("hifz_journey").select("*"),
      supabase.from("hifz_daily_log").select("*").order("date",{ascending:false})
    ]).then(([{data:prog},{data:jour},{data:allLogs}])=>{
      setProgressList(prog||[]);
      setJourneyList(jour||[]);
      const byStudent = {};
      (allLogs||[]).forEach(l=>{ if(!byStudent[l.student_id]) byStudent[l.student_id]=[]; byStudent[l.student_id].push(l); });
      setLogs(byStudent);
      setLoading(false);
    });
  },[]);

  if(loading) return <div style={{textAlign:"center",padding:40,color:C.muted}}>Loading...</div>;

  const now = new Date();
  const JUZ_PP = 20;
  const weekAgo = new Date(now); weekAgo.setDate(now.getDate()-7);

  // Period cutoff — supports previous months
  const getPeriodRange = (monthsBack) => {
    const start = new Date(now.getFullYear(), now.getMonth()-monthsBack, 1);
    const end   = monthsBack===0 ? now : new Date(now.getFullYear(), now.getMonth()-monthsBack+1, 0);
    return {start, end, label: start.toLocaleString("default",{month:"long",year:"numeric"})};
  };
  const range = period==="week" ? {start:weekAgo,end:now,label:"This Week"} : getPeriodRange(prevMonth);

  const sumP = (arr,key) => Math.round(arr.reduce((s,l)=>s+(Number(l[key])||0),0)*10)/10;
  const toJuz = p => p>=JUZ_PP?`${(p/JUZ_PP).toFixed(1)} Juz`:`${p}p`;

  const normalizeTarget = (t) => {
    const n = (v) => typeof v==="object"?(v.unit==="juz"?(v.value||0)*20:v.unit==="ayat"?Math.round((v.value||0)/15):v.unit==="surah"?Math.round((v.value||0)*1.5):v.value||0):v||0;
    return {sabaq:n(t.sabaq),sabqi:n(t.sabqi),manzil:n(t.manzil)};
  };

  // Build leaderboard — merge hifz_progress + hifz_journey
  const board = hifzStudents.map(stu=>{
    const prog    = progressList.find(p=>p.student_id===stu.id);
    const journey = journeyList.find(j=>j.student_id===stu.id);
    const stuLogs = logs[stu.id]||[];
    const periodLogs = stuLogs.filter(l=>{const d=new Date(l.date);return d>=range.start&&d<=range.end;});
    const allLast7 = stuLogs.slice(0,7);

    // Current Juz: prefer hifz_journey (most up to date), fallback to hifz_progress
    // If neither exists yet, use direction default (30 for desc, 1 for asc)
    const direction0 = journey?.direction || "desc";
    const juzNum = journey?.current_juz || prog?.current_juz || (direction0==="desc" ? 30 : 1);
    const direction = direction0;
    const juzCompleted = journey?.juz_completed || 0;
    const stage = journey?.stage || "quran";

    // Overall progress % — based solely on juz_completed (already-finished juz)
    // plus sabaq log pages within current juz for fractional progress
    const sabaqLogPages = sumP(stuLogs.filter(l=>l.sabaq_juz===juzNum),"sabaq_pages");
    const fracJuz = Math.min(1, sabaqLogPages / JUZ_PP);
    const completedJuz = Math.min(30, juzCompleted + fracJuz);
    const overallPct = Math.round((completedJuz/30)*100);

    const sabaqPages  = sumP(periodLogs,"sabaq_pages");
    const sabqiPages  = sumP(periodLogs,"sabqi_pages");
    const manzilPages = sumP(periodLogs,"manzil_pages");
    const sessions    = periodLogs.filter(l=>l.was_present!==false).length;

    const rawTarget = getTarget(juzNum);
    const target    = normalizeTarget(rawTarget);
    const avgSabaq  = allLast7.length ? sumP(allLast7,"sabaq_pages")/allLast7.length : 0;
    const onTrack   = avgSabaq>0 && avgSabaq>=target.sabaq;
    const score     = sabaqPages*3 + sabqiPages + manzilPages*2 + sessions*2;

    return {stu,prog,journey,juzNum,direction,juzCompleted,completedJuz,overallPct,stage,
            sabaqPages,sabqiPages,manzilPages,sessions,avgSabaq,onTrack,score,target,stuLogs,periodLogs};
  }).sort((a,b)=>b.score-a.score);

  // Detail view for one student
  if(view==="detail" && detailStu) {
    const entry = board.find(e=>e.stu.id===detailStu.id)||board[0];
    if(!entry) return null;
    const {stu, prog, journey, juzNum, direction, stage, completedJuz, juzCompleted, overallPct, stuLogs, target} = entry;
    const juz = QURAN_JUZ.find(j=>j.n===juzNum)||QURAN_JUZ[0];
    const last14 = Array.from({length:14},(_,i)=>{
      const d=new Date(now); d.setDate(now.getDate()-13+i);
      const ds=d.toISOString().split("T")[0];
      const log=stuLogs.find(l=>l.date===ds);
      return {ds,log,dayNum:d.getDate(),dayName:d.toLocaleDateString("en-GB",{weekday:"short"})};
    });
    return (
      <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:14}}>
        <button onClick={()=>setView("leaderboard")} style={{background:"none",border:"none",color:P,fontWeight:700,fontSize:13,cursor:"pointer",alignSelf:"flex-start"}}>← Back to Leaderboard</button>
        <div style={{background:`linear-gradient(135deg,#4c1d95,#7c3aed)`,borderRadius:16,padding:"18px 22px",color:"white"}}>
          <div style={{fontWeight:900,fontSize:17}}>{stu.full_name||stu.name}</div>
          <div style={{fontSize:12,opacity:.85}}>{stu.class||stu["class"]} · {stu.stream} · پارہ {juzNum} — {juz.ar}</div>
          <div style={{fontSize:11,opacity:.7,marginTop:2}}>Direction: {journey?.direction==="desc"?"Juz 30→1 (standard)":"Juz 1→30"} · {journey?.juz_completed||0} Juz completed previously</div>
          <div style={{marginTop:10,height:8,background:"rgba(255,255,255,0.2)",borderRadius:4,overflow:"hidden"}}>
            <div style={{width:`${overallPct||0}%`,height:"100%",background:"rgba(255,255,255,0.85)",borderRadius:4}}/>
          </div>
          <div style={{fontSize:11,opacity:.7,marginTop:3}}>{juzCompleted||0}/30 Juz memorized · {(direction||"desc")==="desc"?"30→1":"1→30"}</div>
        </div>
        {/* Journey Road — pace vs schedule */}
        <JourneyRoad journey={journey} targetYears={targetYears}/>
        {/* ── Chart Switcher ── */}
        <HifzChartView entry={entry} last14={last14} target={target} period={period} stuLogs={stuLogs} JUZ_PP={JUZ_PP} sumP={sumP} toJuz={toJuz}/>
        {/* Recent logs */}
        <div style={{background:"white",borderRadius:14,padding:"16px 18px",border:`1px solid ${C.border}`}}>
          <div style={{fontWeight:700,color:"#374151",fontSize:13,marginBottom:8}}>Recent Logs</div>
          {stuLogs.slice(0,8).map(log=>(
            <div key={log.id} style={{display:"flex",gap:10,padding:"7px 0",borderBottom:`1px solid ${C.border}`,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{background:"#4c1d95",color:"white",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:800,flexShrink:0}}>{new Date(log.date).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>
              {log.was_present===false
                ? <span style={{color:"#dc2626",fontSize:11,fontWeight:700}}>Absent</span>
                : <span style={{fontSize:12,color:C.muted}}>
                    <span style={{color:"#1a6b3c",fontWeight:700}}>Sabaq: {log.sabaq_pages||0} pages</span> · <span style={{color:"#2563eb",fontWeight:700}}>Sabqi: {log.sabqi_pages||0} pages</span> · <span style={{color:"#7c3aed",fontWeight:700}}>Manzil: {log.manzil_pages||0} pages</span>
                    {log.sabaq_label&&<span style={{color:"#9ca3af"}}> · {log.sabaq_label}</span>}
                  </span>
              }
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Leaderboard view
  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Controls */}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{fontWeight:800,color:P,fontSize:16,flex:1}}>🏆 Hifz Leaderboard</div>
        {["week","month"].map(p=>(
          <button key={p} onClick={()=>{setPeriod(p);setPrevMonth(0);}}
            style={{padding:"6px 14px",borderRadius:8,border:`1.5px solid ${period===p?P:C.border}`,background:period===p?P:"white",color:period===p?"white":C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>
            {p==="week"?"This Week":"Monthly"}
          </button>
        ))}
        {period==="month"&&(
          <select value={prevMonth} onChange={e=>setPrevMonth(Number(e.target.value))}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"5px 10px",fontSize:12,color:C.text}}>
            {[0,1,2,3].map(n=>{
              const d=new Date(now.getFullYear(),now.getMonth()-n,1);
              return <option key={n} value={n}>{n===0?"This month":d.toLocaleString("default",{month:"long",year:"numeric"})}</option>;
            })}
          </select>
        )}
        <div style={{fontSize:12,color:C.muted,fontWeight:600}}>{range.label}</div>
        {/* View toggle */}
        <div style={{display:"flex",gap:2,background:"#f3f4f6",borderRadius:8,padding:3}}>
          {[["list","☰ List"],["chart","📊 Chart"]].map(([v,lbl])=>(
            <button key={v} onClick={()=>setLbView(v)}
              style={{background:lbView===v?"white":"transparent",color:lbView===v?P:"#6b7280",border:"none",borderRadius:6,padding:"5px 12px",fontWeight:700,fontSize:11,cursor:"pointer",boxShadow:lbView===v?"0 1px 4px rgba(0,0,0,0.1)":"none",transition:"all .12s"}}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {board.length===0&&<div style={{textAlign:"center",padding:40,color:C.muted,background:"white",borderRadius:14}}>No Hifz students enrolled.</div>}

      {/* Chart view */}
      {lbView==="chart"&&board.length>0&&(()=>{
        const maxSabaq = Math.max(...board.map(e=>e.sabaqPages),1);
        return (
          <div style={{background:"white",borderRadius:14,padding:"18px 20px",border:`1px solid ${C.border}`,boxShadow:`0 2px 8px ${C.shadow}`}}>
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:14}}>📊 Pages Comparison — {range.label}</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {board.map((entry,rank)=>{
                const {stu,sabaqPages,sabqiPages,manzilPages,onTrack} = entry;
                const pct = Math.round((sabaqPages/maxSabaq)*100);
                const medal = rank===0?"🥇":rank===1?"🥈":rank===2?"🥉":"";
                return (
                  <div key={stu.id} style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:22,fontSize:13,textAlign:"center",flexShrink:0}}>{medal||<span style={{fontSize:11,color:"#9ca3af",fontWeight:700}}>#{rank+1}</span>}</div>
                    <div style={{minWidth:120,maxWidth:140,fontSize:12,fontWeight:600,color:"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{stu.full_name||stu.name}</div>
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:2}}>
                      {[["Sabaq",sabaqPages,maxSabaq,"#4c1d95"],["Sabqi",sabqiPages,maxSabaq,"#2563eb"],["Manzil",manzilPages,maxSabaq,"#0d9488"]].map(([lbl,val,max,col])=>(
                        <div key={lbl} style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:9,color:"#9ca3af",minWidth:32}}>{lbl}</span>
                          <div style={{flex:1,height:5,background:"#f3f4f6",borderRadius:3,overflow:"hidden"}}>
                            <div style={{width:`${Math.round((val/max)*100)}%`,height:"100%",background:col,borderRadius:3,transition:"width .5s"}}/>
                          </div>
                          <span style={{fontSize:10,fontWeight:700,color:col,minWidth:28,textAlign:"right"}}>{val}p</span>
                        </div>
                      ))}
                    </div>
                    <span style={{fontSize:10,fontWeight:700,color:onTrack?"#15803d":"#d97706",background:onTrack?"#dcfce7":"#fef9c3",borderRadius:6,padding:"2px 8px",flexShrink:0}}>
                      {onTrack?"✅ On":"⚠️ Low"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* List view */}
      {lbView==="list"&&board.map((entry,rank)=>{
        const {stu, prog, journey, juzNum, direction, stage, completedJuz, juzCompleted, overallPct,
               sabaqPages, sabqiPages, manzilPages, sessions, avgSabaq, onTrack, score, target, periodLogs} = entry;
        const juz = QURAN_JUZ.find(j=>j.n===juzNum)||QURAN_JUZ[0];
        const medal = rank===0?"🥇":rank===1?"🥈":rank===2?"🥉":null;
        const statusColor = onTrack?"#16a34a":avgSabaq>0?"#d97706":"#e11d48";
        const statusBg = onTrack?"#f0fdf4":avgSabaq>0?"#fffbeb":"#fef2f2";
        const statusLabel = onTrack?"✅ On Target":avgSabaq>0?"⚠️ Below Target":"🔴 No Activity";
        return (
          <div key={stu.id} style={{background:"white",borderRadius:14,overflow:"hidden",border:`1px solid ${rank<3?"#ddd6fe":C.border}`,boxShadow:rank<3?"0 4px 16px #7c3aed20":`0 2px 8px ${C.shadow}`}}>
            <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",flexWrap:"wrap"}}>
              {/* Rank */}
              <div style={{width:36,height:36,borderRadius:"50%",background:rank<3?"linear-gradient(135deg,#7c3aed,#4c1d95)":"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontWeight:900,fontSize:rank<3?18:13,color:rank<3?"white":C.muted}}>
                {medal||`#${rank+1}`}
              </div>
              {/* Student info */}
              <div style={{flex:1,minWidth:140}}>
                <button onClick={()=>{setDetailStu(stu);setView("detail");}}
                  style={{fontWeight:900,color:"#4c1d95",fontSize:14,background:"none",border:"none",cursor:"pointer",padding:0,textAlign:"left",textDecoration:"underline",textDecorationStyle:"dotted"}}>
                  {stu.full_name||stu.name}
                </button>
                <div style={{fontSize:11,color:C.muted,marginTop:1}}>
                  {stage==="qaida"?"📗 نورانی قاعدہ":stage==="hafiz"?"🎓 حافظ":
                    `پارہ ${juzNum} — ${juz.ar}`} · {(direction||"desc")==="desc"?"30→1":"1→30"} · {stu.class||stu["class"]}
                </div>
                {stage!=="qaida"&&(
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:5}}>
                    <div style={{flex:1,height:5,background:"#f3e8ff",borderRadius:3}}>
                      <div style={{width:`${overallPct}%`,height:"100%",background:"linear-gradient(90deg,#7c3aed,#4c1d95)",borderRadius:3,transition:"width .4s"}}/>
                    </div>
                    <span style={{fontSize:10,color:P,fontWeight:700,whiteSpace:"nowrap"}}>{juzCompleted}/30 Juz ({overallPct}%)</span>
                  </div>
                )}
              </div>
              {/* Period stats */}
              <div style={{display:"flex",gap:8,flexShrink:0,flexWrap:"wrap"}}>
                {[["📗","Sabaq",sabaqPages,"#1a6b3c"],["📘","Sabqi",sabqiPages,"#2563eb"],["📕","Manzil",manzilPages,"#7c3aed"]].map(([icon,lbl,pages,color])=>(
                  <div key={icon} style={{textAlign:"center",minWidth:52,background:`${color}0d`,borderRadius:8,padding:"5px 8px"}}>
                    <div style={{fontSize:10,color,fontWeight:700,marginBottom:1}}>{lbl}</div>
                    <div style={{fontWeight:900,color,fontSize:15}}>{pages}p</div>
                    <div style={{fontSize:9,color:"#9ca3af"}}>{toJuz(pages)}</div>
                  </div>
                ))}
              </div>
              {/* Status + sessions */}
              <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end",flexShrink:0}}>
                <span style={{background:statusBg,color:statusColor,borderRadius:20,padding:"3px 10px",fontSize:10,fontWeight:800}}>{statusLabel}</span>
                <span style={{fontSize:11,color:C.muted}}>{sessions} sessions · avg {Math.round(avgSabaq*10)/10}p/day</span>
              </div>
              <button onClick={()=>{setDetailStu(stu);setView("detail");}}
                style={{background:"#f5f3ff",color:P,border:`1px solid #ddd6fe`,borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                Details →
              </button>
            </div>
            {/* Pace strip — only for current month, Quran students */}
            {period==="month"&&prevMonth===0&&stage!=="qaida"&&(()=>{
              const tSabaq = typeof target.sabaq==="object"?target.sabaq.value:target.sabaq||0;
              const daysInM = new Date(now.getFullYear(),now.getMonth()+1,0).getDate();
              const dayNow = now.getDate();
              const expectedNow = tSabaq*dayNow;
              const delta2 = sabaqPages - expectedNow;
              const isAh = delta2>=0;
              const paceColor = isAh?"#16a34a":"#ef4444";
              const pctActual = Math.min(100,(sabaqPages/(tSabaq*daysInM||1))*100);
              const pctExpected = Math.min(100,(dayNow/daysInM)*100);
              return (
                <div style={{padding:"6px 18px 10px",borderTop:`1px solid ${C.border}`}}>
                  <div style={{fontSize:10,color:"#9ca3af",fontWeight:600,marginBottom:3}}>
                    MONTHLY PACE · Day {dayNow}/{daysInM} ·
                    <span style={{color:paceColor,fontWeight:800}}> {isAh?"↑":"↓"} {isAh?"":"-"}{Math.abs(delta2).toFixed(1)}p {isAh?"ahead of":"behind"} target</span>
                  </div>
                  <div style={{position:"relative",height:6,background:"#f3f4f6",borderRadius:3,overflow:"visible"}}>
                    <div style={{width:`${pctActual}%`,height:"100%",background:isAh?"#22c55e":"#ef4444",borderRadius:3,transition:"width .4s"}}/>
                    {/* Expected marker */}
                    <div style={{position:"absolute",top:-2,left:`${pctExpected}%`,width:2,height:10,background:"#6b7280",borderRadius:1,transform:"translateX(-50%)"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#9ca3af",marginTop:2}}>
                    <span>{sabaqPages}p done</span><span style={{fontWeight:600}}>▲ today's pace mark</span><span>{tSabaq*daysInM}p target</span>
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}
// -- REPORT CARD MANAGEMENT ------------------------------------------
function ReportCardManagement({ students, results, attendance, showToast, onView }) {
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const HIFZ_SUBS = ["Tajweed","Memorization","Revision","Nazra","Manzil","Hifz","Makharij"];
  const isHifzSub = sub=>HIFZ_SUBS.some(h=>sub?.toLowerCase().includes(h.toLowerCase()));
  const GRADE_COLOR = g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
  const getGrade=(m,t)=>{const p=(m/t)*100;return [{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}].find(g=>p>=g.min)?.label||"F";};
  const dedup = arr=>{ const m={}; arr.forEach(r=>{ if(!m[r.subject]||r.marks_obtained>m[r.subject].marks_obtained) m[r.subject]=r; }); return Object.values(m); };

  const [rcView, setRcView] = React.useState("grid"); // grid | manage
  const [filterClass, setFilterClass] = React.useState("All");
  const [filterExam, setFilterExam] = React.useState("All");
  const [filterMonth, setFilterMonth] = React.useState("All");
  const [publishedCards, setPublishedCards] = React.useState([]);
  const [loadingCards, setLoadingCards] = React.useState(true);
  const [publishingSid, setPublishingSid] = React.useState(null);
  const [pubForm, setPubForm] = React.useState({examType:"monthly",examMonth:"",teacherComment:"",remarks:""});
  const [publishing, setPublishing] = React.useState(false);
  const [editingCard, setEditingCard] = React.useState(null);
  const [savingEdit, setSavingEdit] = React.useState(false);

  React.useEffect(()=>{
    supabase.from("published_report_cards").select("*").order("created_at",{ascending:false})
      .then(({data})=>{ setPublishedCards(data||[]); setLoadingCards(false); });
  },[]);

  const unpublish = async(id) => {
    await supabase.from("published_report_cards").delete().eq("id",id);
    setPublishedCards(p=>p.filter(c=>c.id!==id));
    showToast("Removed from parent portal");
  };

  const saveEdit = async() => {
    if(!editingCard) return;
    setSavingEdit(true);
    const {error} = await supabase.from("published_report_cards").update({
      teacher_comment:editingCard.teacher_comment,
      remarks:editingCard.remarks,
      exam_month:editingCard.exam_month,
    }).eq("id",editingCard.id);
    setSavingEdit(false);
    if(!error){
      setPublishedCards(p=>p.map(c=>c.id===editingCard.id?{...c,...editingCard}:c));
      setEditingCard(null);
      showToast("Card updated");
    } else showToast("Error: "+error.message,false);
  };

  const classes = ["All",...ALLCLASSES.filter(c=>students.some(s=>(s.class||s["class"])===c))];

  const getStudentResults = (sid, etype, month) => results.filter(r=>{
    const match = r.student_id===sid;
    if(!match) return false;
    if(etype==="monthly") return (r.exam_name?.toLowerCase().includes("monthly")||r.exam_type==="monthly")&&(month?r.exam_name?.toLowerCase().includes(month.toLowerCase()):true);
    return r.exam_name?.toLowerCase().includes(etype)||r.exam_type===etype;
  });

  const hasResults = (sid, etype, month="") => getStudentResults(sid, etype||"", month).length > 0 || (etype===""&&!month&&results.some(r=>String(r.student_id)===String(sid)));
  const isPublished = (sid, etype, month) => publishedCards.some(c=>String(c.student_id)===String(sid)&&(etype==="All"||!etype||c.exam_type===etype)&&(!month||month==="All"||c.exam_month===month));

  const doPublish = async (stu) => {
    const stuResults = getStudentResults(stu.id, pubForm.examType, pubForm.examMonth);
    if(!stuResults.length) { showToast("No results found for this exam type — enter marks first", false); return; }
    setPublishing(true);
    const schoolDedup = dedup(stuResults.filter(r=>!isHifzSub(r.subject)));
    const hifzDedup   = dedup(stuResults.filter(r=> isHifzSub(r.subject)));
    const schoolTotal = schoolDedup.reduce((s,r)=>s+r.marks_obtained,0);
    const schoolMax   = schoolDedup.reduce((s,r)=>s+r.total_marks,0);
    const hifzTotal   = hifzDedup.reduce((s,r)=>s+r.marks_obtained,0);
    const hifzMax     = hifzDedup.reduce((s,r)=>s+r.total_marks,0);

    // Auto-attach Hifz progress summary for Hifz students
    let hifzSummary = null;
    const isHifzStu = (stu.stream||"").includes("Hifz")||(stu.stream||"").includes("Both");
    if(isHifzStu) {
      const [{data:prog},{data:recentLogs}] = await Promise.all([
        supabase.from("hifz_progress").select("*").eq("student_id",stu.id).maybeSingle(),
        supabase.from("hifz_daily_log").select("*").eq("student_id",stu.id).order("date",{ascending:false}).limit(35),
      ]);
      const JUZ_PP = 20;
      const now = new Date();
      const monthStart = new Date(now.getFullYear(),now.getMonth(),1);
      // Use exam month if provided, else current month
      const targetMonth = pubForm.examMonth
        ? new Date(now.getFullYear(), ["January","February","March","April","May","June","July","August","September","October","November","December"].indexOf(pubForm.examMonth), 1)
        : monthStart;
      const targetMonthEnd = new Date(targetMonth.getFullYear(), targetMonth.getMonth()+1, 0);
      const monthLogs = (recentLogs||[]).filter(l=>new Date(l.date)>=targetMonth&&new Date(l.date)<=targetMonthEnd);
      const sumP = (arr,k)=>Math.round(arr.reduce((s,l)=>s+(Number(l[k])||0),0)*10)/10;
      hifzSummary = {
        current_juz: prog?.current_juz||0,
        current_surah: prog?.current_surah||"",
        month_sabaq_pages: sumP(monthLogs,"sabaq_pages"),
        month_sabqi_pages: sumP(monthLogs,"sabqi_pages"),
        month_manzil_pages: sumP(monthLogs,"manzil_pages"),
        month_sessions: monthLogs.filter(l=>l.was_present!==false).length,
        month_absent: monthLogs.filter(l=>l.was_present===false).length,
        juz_name: QURAN_JUZ.find(j=>j.n===(prog?.current_juz||1))?.name||"",
        juz_ar: QURAN_JUZ.find(j=>j.n===(prog?.current_juz||1))?.ar||"",
      };
    }

    const card = {
      student_id:String(stu.id), student_name:stu.full_name||stu.name,
      class:stu.class||stu["class"], stream:stu.stream,
      exam_type:pubForm.examType, exam_month:pubForm.examMonth,
      school_results:schoolDedup, hifz_results:hifzDedup,
      school_total:schoolTotal, school_max:schoolMax,
      hifz_total:hifzTotal, hifz_max:hifzMax,
      teacher_comment:pubForm.teacherComment, remarks:pubForm.remarks,
      hifz_summary:hifzSummary,
      published_at:new Date().toISOString()
    };
    const {data,error} = await supabase.from("published_report_cards").insert(card).select().single();
    setPublishing(false);
    if(error) { console.error("doPublish error details:", JSON.stringify(error)); showToast("Error: "+error.message+" | "+error.details+" | "+error.hint, false); return; }
    setPublishedCards(p=>[data,...p]);
    showToast("Published"+(isHifzStu?" with Hifz summary":""));
    setPublishingSid(null);
    setPubForm({examType:"monthly",examMonth:"",teacherComment:"",remarks:""});
    fireConfetti();
  };

  const displayStudents = students.filter(s=>filterClass==="All"||(s.class||s["class"])===filterClass);
  const etype = filterExam==="All"?"":filterExam;
  const activeMonth = filterExam==="monthly"&&filterMonth!=="All"?filterMonth:"";
  const availMonths = React.useMemo(()=>{
    if(filterExam!=="monthly") return [];
    const found=new Set();
    results.forEach(r=>{
      if(r.exam_type==="monthly"||(r.exam_name||"").toLowerCase().includes("monthly")){
        const m=MONTHS.find(mn=>(r.exam_name||"").toLowerCase().includes(mn.toLowerCase()));
        if(m) found.add(m);
      }
    });
    return MONTHS.filter(m=>found.has(m));
  },[results,filterExam]);
  const readyCount     = displayStudents.filter(s=>hasResults(s.id,etype,activeMonth)&&!isPublished(s.id,filterExam,activeMonth)).length;
  const publishedCount = displayStudents.filter(s=>isPublished(s.id,filterExam,activeMonth)).length;
  const notReadyCount  = displayStudents.filter(s=>!hasResults(s.id,etype,activeMonth)&&!isPublished(s.id,filterExam,activeMonth)).length;

  return (
    <div className="fade-up" style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:17,color:C.primary}}>📄 Report Cards</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>{rcView==="grid"?"Click Publish on any student row — no extra steps":"All published cards · edit comments or unpublish"}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setRcView("grid")} style={{padding:"7px 14px",borderRadius:8,border:`1.5px solid ${rcView==="grid"?C.primary:C.border}`,background:rcView==="grid"?C.primary:"white",color:rcView==="grid"?"white":C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>📊 Class View</button>
          <button onClick={()=>setRcView("manage")} style={{padding:"7px 14px",borderRadius:8,border:`1.5px solid ${rcView==="manage"?C.primary:C.border}`,background:rcView==="manage"?C.primary:"white",color:rcView==="manage"?"white":C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>⚙️ Manage ({publishedCards.length})</button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:12}}>
        {[
          {label:"Published",value:publishedCount,color:C.primary,bg:C.primaryPale},
          {label:"Ready",value:readyCount,color:"#0d9488",bg:"#f0fdfa"},
          {label:"Needs Marks",value:notReadyCount,color:"#d97706",bg:"#fffbeb"},
          {label:"Total Cards",value:publishedCards.length,color:"#2563eb",bg:"#eff6ff"},
        ].map(k=>(
          <div key={k.label} style={{background:k.bg,borderRadius:14,padding:"12px 14px",border:`1px solid ${k.color}20`}}>
            <div style={{fontSize:22,fontWeight:900,color:k.color}}>{k.value}</div>
            <div style={{fontSize:11,color:C.muted,fontWeight:600,marginTop:1}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6,flex:1,flexWrap:"wrap"}}>
          {classes.map(c=>(
            <button key={c} onClick={()=>{setFilterClass(c);setPublishingSid(null);}}
              style={{padding:"6px 14px",borderRadius:8,border:`1.5px solid ${filterClass===c?C.primary:C.border}`,background:filterClass===c?C.primary:"white",color:filterClass===c?"white":C.muted,fontWeight:700,fontSize:12,cursor:"pointer"}}>
              {c}
            </button>
          ))}
        </div>
        <select value={filterExam} onChange={e=>{setFilterExam(e.target.value);setFilterMonth("All");setPublishingSid(null);}}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 12px",fontSize:12}}>
          <option value="All">All Exams</option>
          {["monthly","midterm","final","quiz"].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
        </select>
        {filterExam==="monthly"&&availMonths.length>0&&(
          <select value={filterMonth} onChange={e=>{setFilterMonth(e.target.value);setPublishingSid(null);}}
            style={{border:`1.5px solid ${filterMonth!=="All"?C.primary:C.border}`,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:filterMonth!=="All"?700:400,color:filterMonth!=="All"?C.primary:undefined}}>
            <option value="All">All Months</option>
            {availMonths.map(m=><option key={m}>{m}</option>)}
          </select>
        )}
      </div>

      {/* MANAGE VIEW */}
      {rcView==="manage"&&(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {/* Edit modal */}
          {editingCard&&(
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setEditingCard(null)}>
              <div style={{background:"white",borderRadius:16,padding:24,width:"100%",maxWidth:440,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
                <div style={{fontWeight:800,color:C.primary,fontSize:15,marginBottom:16}}>✏️ Edit Report Card</div>
                <div style={{display:"flex",flexDirection:"column",gap:12}}>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>EXAM MONTH</label>
                    <input value={editingCard.exam_month||""} onChange={e=>setEditingCard(p=>({...p,exam_month:e.target.value}))}
                      style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>TEACHER COMMENT</label>
                    <textarea value={editingCard.teacher_comment||""} onChange={e=>setEditingCard(p=>({...p,teacher_comment:e.target.value}))} rows={3}
                      style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,resize:"vertical"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:3}}>REMARKS</label>
                    <input value={editingCard.remarks||""} onChange={e=>setEditingCard(p=>({...p,remarks:e.target.value}))}
                      style={{width:"100%",border:`1.5px solid ${C.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,marginTop:16}}>
                  <button onClick={saveEdit} disabled={savingEdit} style={{flex:1,background:savingEdit?C.muted:C.primary,color:"white",border:"none",borderRadius:10,padding:"10px",fontWeight:800,cursor:savingEdit?"not-allowed":"pointer"}}>
                    {savingEdit?"Saving...":"💾 Save"}
                  </button>
                  <button onClick={()=>setEditingCard(null)} style={{padding:"10px 18px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            </div>
          )}
          {publishedCards.length===0&&<div style={{textAlign:"center",padding:40,color:C.muted,background:"white",borderRadius:14}}>No published cards yet.</div>}
          {publishedCards.map(card=>{
            const GRADE_C=g=>({A:"#1a6b3c","A+":"#1a6b3c","B+":"#0d9488","B":"#0d9488","C":"#d97706","D":"#ea580c","F":"#e11d48"}[g]||"#6b7280");
            const getGr=(m,t)=>{const p=(m/t)*100;return [{label:"A+",min:90},{label:"A",min:80},{label:"B+",min:70},{label:"B",min:60},{label:"C",min:50},{label:"D",min:40},{label:"F",min:0}].find(g=>p>=g.min)?.label||"F";};
            const schPct = card.school_max>0?Math.round((card.school_total/card.school_max)*100):0;
            const schGrade = card.school_max>0?getGr(schPct,100):null;
            const EXAM_COLOR={monthly:"#2563eb",midterm:"#d97706",final:"#1a6b3c",quiz:"#7c3aed"};
            const color = EXAM_COLOR[card.exam_type]||"#374151";
            return (
              <div key={card.id} style={{background:"white",borderRadius:12,padding:"14px 18px",border:`1px solid ${C.border}`,boxShadow:`0 2px 6px ${C.shadow}`,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{fontWeight:800,color:C.text,fontSize:13}}>{card.student_name} — {card.class}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>
                    <span style={{color,fontWeight:700}}>{card.exam_type}{card.exam_month?" — "+card.exam_month:""}</span>
                    {" · "}{new Date(card.published_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                  </div>
                  {card.teacher_comment&&<div style={{fontSize:11,color:"#6b7280",marginTop:2,fontStyle:"italic"}}>"{card.teacher_comment.slice(0,60)}{card.teacher_comment.length>60?"...":""}"</div>}
                </div>
                {schGrade&&<div style={{textAlign:"center",flexShrink:0}}>
                  <div style={{fontWeight:900,fontSize:22,color:GRADE_C(schGrade)}}>{schGrade}</div>
                  <div style={{fontSize:11,color:C.muted}}>{schPct}%</div>
                </div>}
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  {onView&&<button onClick={()=>{ const stu=students.find(s=>String(s.id)===String(card.student_id)); if(stu) onView(stu,card.exam_type,card.exam_month||null); }}
                    style={{background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>👁 View</button>}
                  <button onClick={()=>setEditingCard({...card})}
                    style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✏️ Edit</button>
                  <button onClick={()=>{ if(window.confirm("Remove from parent portal?")) unpublish(card.id); }}
                    style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>🗑️ Unpublish</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Class sections — only when grid view */}
      {rcView==="grid"&&(filterClass==="All"?ALLCLASSES.filter(c=>students.some(s=>(s.class||s["class"])===c)):[filterClass]).map(cls=>{
        const classStu = students.filter(s=>(s.class||s["class"])===cls);
        if(!classStu.length) return null;
        const clsPub = classStu.filter(s=>isPublished(s.id,filterExam));
        const pct = classStu.length ? Math.round((clsPub.length/classStu.length)*100) : 0;
        return (
          <div key={cls} style={{background:"white",borderRadius:16,border:`1px solid ${C.border}`,overflow:"hidden",boxShadow:`0 2px 8px ${C.shadow}`}}>
            {/* Class header */}
            <div style={{padding:"12px 20px",background:C.bg,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:14}}>
              <div style={{flex:1}}>
                <span style={{fontWeight:900,color:C.primary,fontSize:14}}>{cls}</span>
                <span style={{fontSize:11,color:C.muted,marginLeft:10}}>{classStu.length} students · {clsPub.length} published</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:80,height:6,background:C.border,borderRadius:3}}>
                  <div style={{width:`${pct}%`,height:"100%",background:pct===100?C.primary:pct>60?"#0d9488":"#d97706",borderRadius:3}}/>
                </div>
                <span style={{fontSize:11,fontWeight:800,color:pct===100?C.primary:C.muted}}>{pct}%</span>
              </div>
            </div>

            {/* Student rows */}
            {classStu.map((s,i)=>{
              const stuPub = publishedCards.filter(c=>c.student_id===s.id).sort((a,b)=>new Date(b.published_at)-new Date(a.published_at));
              const hasMks = hasResults(s.id, etype, activeMonth);
              const isPub  = isPublished(s.id, filterExam, activeMonth);
              const latest = stuPub[0];
              const stuPct = latest&&latest.school_max>0?Math.round((latest.school_total/latest.school_max)*100):null;
              const grade  = stuPct!==null?getGrade(stuPct,100):null;
              const isOpen = publishingSid===s.id;

              // Auto-detect available exam types for this student
              const availExamTypes = ["monthly","midterm","final","quiz"].filter(t=>getStudentResults(s.id,t,"").length>0);

              return (
                <div key={s.id} style={{borderBottom:i<classStu.length-1?`1px solid ${C.border}`:"none"}}>
                  {/* Main row */}
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"11px 20px",background:isOpen?C.primaryPale:i%2===0?"white":"#fafffe",flexWrap:"wrap",transition:"background .2s"}}>
                    <div style={{width:9,height:9,borderRadius:"50%",flexShrink:0,background:isPub?C.primary:hasMks?"#0d9488":"#d97706"}}/>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontWeight:700,color:C.text,fontSize:13}}>{s.full_name||s.name}</div>
                      {latest&&<div style={{fontSize:10,color:C.muted,marginTop:1}}>{latest.exam_type}{latest.exam_month?" — "+latest.exam_month:""} · {new Date(latest.published_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>}
                    </div>
                    <span style={{background:isPub?C.primaryPale:hasMks?"#f0fdfa":"#fffbeb",color:isPub?C.primary:hasMks?"#0d9488":"#d97706",border:`1px solid ${isPub?C.primaryMid:hasMks?"#5eead4":"#fde68a"}`,borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:800,flexShrink:0}}>
                      {isPub?"✅ Published":hasMks?"📋 Ready":"⚠️ No Marks"}
                    </span>
                    {stuPct!==null&&<span style={{fontWeight:900,color:GRADE_COLOR(grade),fontSize:12,flexShrink:0}}>{stuPct}% ({grade})</span>}
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      {stuPub.length>0&&<span style={{fontSize:10,color:C.muted,alignSelf:"center"}}>{stuPub.length} card{stuPub.length>1?"s":""}</span>}
                      {hasMks&&(
                        <button onClick={()=>{ setPublishingSid(isOpen?null:s.id); if(!isOpen) setPubForm(p=>({...p,examType:filterExam!=="All"?filterExam:(availExamTypes[0]||"monthly"),examMonth:activeMonth||""})); }}
                          style={{background:isOpen?C.primary:C.primaryPale,color:isOpen?"white":C.primary,border:`1.5px solid ${C.primary}`,borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:800,cursor:"pointer",transition:"all .2s"}}>
                          {isOpen?"▲ Close":"📤 Publish"}
                        </button>
                      )}
                      {!hasMks&&<span style={{fontSize:10,color:C.muted,padding:"5px 0"}}>Enter marks first</span>}
                      {hasMks&&onView&&<button onClick={()=>onView(s,filterExam!=="All"?filterExam:(latest?.exam_type||availExamTypes[0]||"monthly"),activeMonth||latest?.exam_month||null)}
                        style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:8,padding:"5px 10px",fontSize:10,fontWeight:700,cursor:"pointer"}}>👁 View</button>}
                      {latest&&<button onClick={async()=>{if(!window.confirm("Remove latest card?"))return;await supabase.from("published_report_cards").delete().eq("id",latest.id);setPublishedCards(p=>p.filter(c=>c.id!==latest.id));showToast("Removed");}}
                        style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:8,padding:"5px 9px",fontSize:10,fontWeight:700,cursor:"pointer"}}>🗑️</button>}
                    </div>
                  </div>

                  {/* Inline publish form — slides open */}
                  {isOpen && (
                    <div className="fade-up" style={{padding:"16px 20px 20px",background:"#f0f9ff",borderTop:`1px solid #bae6fd`}}>
                      <div style={{fontWeight:700,color:"#0369a1",fontSize:13,marginBottom:12}}>📤 Publishing card for {s.full_name||s.name}</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:12}}>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          <label style={{fontSize:11,fontWeight:700,color:"#0369a1"}}>Exam Type</label>
                          <select value={pubForm.examType} onChange={e=>setPubForm(p=>({...p,examType:e.target.value,examMonth:""}))}
                            style={{border:"1.5px solid #7dd3fc",borderRadius:8,padding:"8px 10px",fontSize:13,background:"white"}}>
                            {availExamTypes.length>0
                              ? availExamTypes.map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)
                              : ["monthly","midterm","final","quiz"].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)
                            }
                          </select>
                        </div>
                        {pubForm.examType==="monthly"&&(
                          <div style={{display:"flex",flexDirection:"column",gap:4}}>
                            <label style={{fontSize:11,fontWeight:700,color:"#0369a1"}}>Month</label>
                            <select value={pubForm.examMonth} onChange={e=>setPubForm(p=>({...p,examMonth:e.target.value}))}
                              style={{border:"1.5px solid #7dd3fc",borderRadius:8,padding:"8px 10px",fontSize:13,background:"white"}}>
                              <option value="">All months</option>
                              {MONTHS.map(m=><option key={m}>{m}</option>)}
                            </select>
                          </div>
                        )}
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          <label style={{fontSize:11,fontWeight:700,color:"#0369a1"}}>Teacher Comment</label>
                          <input value={pubForm.teacherComment} onChange={e=>setPubForm(p=>({...p,teacherComment:e.target.value}))}
                            placeholder="e.g. Great improvement!" style={{border:"1.5px solid #7dd3fc",borderRadius:8,padding:"8px 10px",fontSize:13,background:"white"}}/>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          <label style={{fontSize:11,fontWeight:700,color:"#0369a1"}}>Remarks</label>
                          <input value={pubForm.remarks} onChange={e=>setPubForm(p=>({...p,remarks:e.target.value}))}
                            placeholder="e.g. Good conduct" style={{border:"1.5px solid #7dd3fc",borderRadius:8,padding:"8px 10px",fontSize:13,background:"white"}}/>
                        </div>
                      </div>
                      {/* Preview score */}
                      {(()=>{
                        const sr = getStudentResults(s.id, pubForm.examType, pubForm.examMonth);
                        const sd = dedup(sr.filter(r=>!isHifzSub(r.subject)));
                        const tot = sd.reduce((a,r)=>a+r.marks_obtained,0);
                        const max = sd.reduce((a,r)=>a+r.total_marks,0);
                        const pctV = max>0?Math.round((tot/max)*100):null;
                        const g = pctV!==null?getGrade(pctV,100):null;
                        return sr.length>0?(
                          <div style={{background:"white",borderRadius:10,padding:"10px 14px",marginBottom:12,border:"1px solid #7dd3fc",fontSize:12}}>
                            <span style={{color:"#0369a1",fontWeight:700}}>Preview: </span>
                            {sd.map(r=>`${r.subject} ${r.marks_obtained}/${r.total_marks}`).join(" · ")}
                            {pctV!==null&&<span style={{fontWeight:900,color:GRADE_COLOR(g),marginLeft:8}}>{pctV}% ({g})</span>}
                          </div>
                        ):(
                          <div style={{background:"#fef3c7",borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#92400e",border:"1px solid #fde68a"}}>
                            ⚠️ No marks found for this exam type/month. Enter marks first.
                          </div>
                        );
                      })()}
                      <button onClick={()=>doPublish(s)} disabled={publishing}
                        style={{background:publishing?C.muted:C.primary,color:"white",border:"none",borderRadius:10,padding:"10px 24px",fontWeight:800,fontSize:13,cursor:publishing?"not-allowed":"pointer"}}>
                        {publishing?"Publishing...":"📤 Publish to Parent Portal"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// TEACHER PORTAL
// teacher_accounts table: id, email, name, classes (text[]), subjects (text[]),
//                         is_hifz_teacher (bool), staff_id (uuid, optional)
// ══════════════════════════════════════════════════════════════════════
function TeacherPortal({ session, teacher, onSignOut }) {
  const S = useSettings();
  const C2 = {
    primary:"#1a6b3c", light:"#f0fdf4", border:"#d1fae5",
    purple:"#4c1d95",  purpleLight:"#f5f3ff",
    text:"#111827", muted:"#6b7280", bg:"#f9fafb",
    shadow:"rgba(0,0,0,0.06)",
  };
  const [activeTab, setActiveTab]   = React.useState("dashboard");
  const [focusClass, setFocusClass] = React.useState(null); // pre-select a class in performance tab
  const [atRiskPanel,setAtRiskPanel]= React.useState(false); // show at-risk inline panel
  const [students,  setStudents]    = React.useState([]);
  const [results,   setResults]     = React.useState([]);
  const [attendance,setAttendance]  = React.useState([]);
  const [exams,     setExams]       = React.useState([]);
  const [timetable, setTimetable]   = React.useState([]);
  const [hifzStudents, setHifzStudents] = React.useState([]);
  const [announcements, setAnnouncements] = React.useState([]);
  const [teacherEvents, setTeacherEvents] = React.useState([]);
  const [loading,   setLoading]     = React.useState(true);
  const [toast,     setToast]       = React.useState(null);
  const [todayBoardDone, setTodayBoardDone] = React.useState({});
  const [coverDuties,    setCoverDuties]    = React.useState([]);
  const showToast = (msg,ok=true)=>{setToast({msg,ok});setTimeout(()=>setToast(null),3500);};
  const [myPrimaryClasses, setMyPrimaryClasses] = React.useState([]); // classes where this teacher is CLASS TEACHER
  const [isLibrarian, setIsLibrarian] = React.useState(false); // currently holds an active "Library" staff_responsibilities assignment

  // Teacher's assigned classes & subjects — derived dynamically from timetable
  // This means timetable changes instantly reflect in teacher portal — no manual updates needed
  const isHifz    = teacher.is_hifz_teacher || false;
  const todayStr  = new Date().toISOString().split("T")[0];
  const todayName = new Date().toLocaleDateString("en-GB",{weekday:"long"});

  // These are computed after timetable loads — see below
  const myTimetable = timetable.filter(t=>
    t.teacher_name && teacher.name &&
    t.teacher_name.toLowerCase().trim() === teacher.name.toLowerCase().trim()
  );
  const myClasses  = [...new Set(myTimetable.map(t=>t.class).filter(Boolean))].sort();
  const mySubjects = [...new Set(myTimetable.map(t=>t.subject).filter(Boolean))].sort();

  React.useEffect(()=>{
    const load = async () => {
      setLoading(true);
      const [stuRes, resRes, attRes, exRes, ttRes, annRes, evRes, ctRes, cdRes] = await Promise.all([
        supabase.from("students").select("*").order("full_name"),
        supabase.from("results").select("*").order("created_at",{ascending:false}),
        supabase.from("attendance").select("*").order("date",{ascending:false}).limit(500),
        supabase.from("exams").select("*").order("created_at",{ascending:false}),
        supabase.from("timetable").select("*").order("period_no"),
        supabase.from("announcements").select("*").eq("is_published",true).order("created_at",{ascending:false}).limit(5),
        supabase.from("school_events").select("*").in("visible_to",["all","teachers"]).gte("date",new Date(Date.now()-7*86400000).toISOString().split("T")[0]).order("date",{ascending:true}),
        supabase.from("class_teachers").select("*"),
        // Cover duties assigned to this teacher (last 60 days + future)
        teacher.staff_id
          ? supabase.from("cover_duties").select("*").eq("cover_staff_id",teacher.staff_id).gte("date",new Date(Date.now()-60*86400000).toISOString().split("T")[0]).order("date",{ascending:false})
          : Promise.resolve({data:[]}),
      ]);
      if(isHifz){ const hl=await supabase.from("hifz_daily_log").select("*").order("date",{ascending:false}).limit(300); setHifzStudents(hl.data||[]); }
      setStudents(stuRes.data||[]);
      setResults(resRes.data||[]);
      setAttendance(attRes.data||[]);
      setExams(exRes.data||[]);
      setTimetable(ttRes.data||[]);
      setAnnouncements(annRes.data||[]);
      setTeacherEvents(evRes.data||[]);
      setCoverDuties(cdRes.data||[]);
      // Determine which classes this teacher is the PRIMARY class teacher for
      const myClassTeacherOf = (ctRes.data||[]).filter(ct=>ct.staff_id===teacher.staff_id||(ct.teacher_email&&ct.teacher_email===teacher.email)).map(ct=>ct.class_name);
      setMyPrimaryClasses(myClassTeacherOf);
      setLoading(false);
    };
    load();

    // Realtime: refresh when attendance changes
    const attChannel = supabase
      .channel("teacher-attendance-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "attendance" },
        (payload) => {
          const r = payload.new;
          if (!r?.date || !r?.student_id) return;
          setAttendance(prev => {
            const filtered = prev.filter(a => !(a.date === r.date && String(a.student_id) === String(r.student_id)));
            return [...filtered, r];
          });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(attChannel); };
  },[]);

  // Library tab only shows up for whoever currently holds the "Library" responsibility —
  // so reassigning the librarian in Staff Responsibilities instantly moves this tab too.
  React.useEffect(()=>{
    supabase.from("staff_responsibilities").select("id").eq("staff_name",teacher.name)
      .eq("responsibility_type","Library").eq("is_active",true).limit(1)
      .then(({data})=>setIsLibrarian((data||[]).length>0));
  },[teacher.name]);

  // Filter students for this teacher's classes (from timetable)
  const myStudents = students.filter(s=>
    myClasses.length===0 || myClasses.includes(s.class||s["class"])
  );
  // Today's schedule — their periods today
  const todaySchedule = myTimetable.filter(t=>t.day===todayName).sort((a,b)=>a.period_no-b.period_no);
  // Cover duties for today that still need action
  const todayCoverDuties = coverDuties.filter(d=>d.date===todayStr&&["open","assigned","acknowledged"].includes(d.status));
  const hasCoverToday    = todayCoverDuties.length > 0;
  // Helper: does teacher have an active cover duty for a given class on a given date?
  const hasCoverForClass = (cls, date) => coverDuties.some(d=>d.date===date&&d.class_name===cls&&["assigned","acknowledged"].includes(d.status));
  // Update a cover duty's status locally + in DB
  const updateCoverStatus = async (id, status) => {
    const extra = status==="acknowledged"?{acknowledged_at:new Date().toISOString()}:status==="done"?{done_at:new Date().toISOString()}:{};
    await supabase.from("cover_duties").update({status,...extra}).eq("id",id);
    setCoverDuties(p=>p.map(d=>d.id===id?{...d,status,...extra}:d));
  };

  // Attendance for today by class
  const todayAttendance = attendance.filter(a=>a.date===todayStr);
  const markedToday = (cls) => {
    const classStu = myStudents.filter(s=>(s.class||s["class"])===cls);
    return classStu.length>0 && classStu.every(s=>todayAttendance.find(a=>a.student_id===String(s.id)));
  };

  // ── Tab: Dashboard ──────────────────────────────────────────────
  const TabDashboard = () => {
    const totalStudents = myStudents.length;
    const todayPresent  = todayAttendance.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length;
    const myResults     = results.filter(r=>mySubjects.includes(r.subject)||mySubjects.length===0);
    const avgPct        = myResults.length ? Math.round(myResults.reduce((s,r)=>s+(r.marks_obtained/r.total_marks)*100,0)/myResults.length) : null;

    const [selAnn,   setSelAnn]   = React.useState(null);
    const [selEvent, setSelEvent] = React.useState(null);
    const [calMonth, setCalMonth] = React.useState(new Date());

    const EVENT_ICONS  = {exam:"📝",holiday:"🏖️",function:"🎓",meeting:"👥",assignment:"📋",milestone:"🎯",general:"📅"};
    const EVENT_COLORS = {exam:"#dc2626",holiday:"#d97706",function:"#7c3aed",meeting:"#2563eb",assignment:"#0d9488",milestone:"#1a6b3c",general:"#374151"};
    const CAT_COLOR    = {News:"#0891b2",Event:"#7c3aed",Achievement:"#16a34a",Notice:"#d97706",Holiday:"#e11d48",General:"#1a6b3c"};

    const today = new Date(); today.setHours(0,0,0,0);
    const todayStr2 = today.toISOString().split("T")[0];
    const upcoming  = teacherEvents.filter(e=>{ const d=new Date(e.date); d.setHours(0,0,0,0); return d>=today; });
    const deadlines = upcoming.filter(e=>e.event_type==="assignment"||e.event_type==="exam");

    const daysLeft = (ds) => { const d=new Date(ds); d.setHours(0,0,0,0); return Math.round((d-today)/(1000*60*60*24)); };
    const dlColor  = (n) => n===0?"#dc2626":n<=2?"#ea580c":n<=5?"#d97706":"#16a34a";
    const dlBg     = (n) => n===0?"#fef2f2":n<=2?"#fff7ed":n<=5?"#fefce8":"#f0fdf4";
    const dlLabel  = (n) => n===0?"TODAY":n===1?"Tomorrow":`${n}d left`;

    // Calendar
    const calStart  = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
    const startDay  = calStart.getDay();
    const daysInMon = new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 0).getDate();
    const evOnDay   = (day) => {
      const ds = `${calMonth.getFullYear()}-${String(calMonth.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
      return teacherEvents.filter(e=>e.date===ds||e.date.startsWith(ds));
    };

    // At-risk students across my classes
    const atRisk = myStudents.filter(s=>{
      const r=attendance.filter(a=>String(a.student_id)===String(s.id));
      if(!r.length) return false;
      return Math.round(r.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/r.length*100)<75;
    });

    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>

        {/* Event popup */}
        {selEvent&&(
          <Portal>
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setSelEvent(null)}>
              <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:500,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
                <div style={{background:`linear-gradient(135deg,${EVENT_COLORS[selEvent.event_type]||"#374151"},${EVENT_COLORS[selEvent.event_type]||"#374151"}cc)`,borderRadius:"20px 20px 0 0",padding:"18px 22px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{EVENT_ICONS[selEvent.event_type]||"📅"} {selEvent.event_type}</span>
                      <div style={{fontWeight:900,fontSize:17,color:"white"}}>{selEvent.title}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.8)",marginTop:4}}>📅 {new Date(selEvent.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
                    </div>
                    <button onClick={()=>setSelEvent(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:8,width:30,height:30,color:"white",fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                  </div>
                </div>
                <div style={{padding:"18px 22px"}}>
                  {selEvent.description?<div style={{fontSize:14,color:"#374151",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif",marginBottom:14}}>{selEvent.description}</div>:<div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",marginBottom:14}}>No description.</div>}
                  {selEvent.class&&selEvent.class!=="all"&&<div style={{background:"#f3f4f6",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#374151",fontWeight:600,marginBottom:12}}>🏫 {selEvent.class}</div>}
                  <button onClick={()=>setSelEvent(null)} style={{display:"block",width:"100%",background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"10px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
                </div>
              </div>
            </div>
          </Portal>
        )}

        {/* Announcement popup */}
        {selAnn&&(
          <Portal>
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setSelAnn(null)}>
              <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:520,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>
                <div style={{background:`linear-gradient(135deg,${CAT_COLOR[selAnn.category]||C2.primary},${CAT_COLOR[selAnn.category]||C2.primary}cc)`,borderRadius:"20px 20px 0 0",padding:"18px 22px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>{selAnn.category||"General"}</span>
                      <div style={{fontWeight:900,fontSize:17,color:"white"}}>{selAnn.title}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.75)",marginTop:4}}>{new Date(selAnn.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
                    </div>
                    <button onClick={()=>setSelAnn(null)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:8,width:30,height:30,color:"white",fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                  </div>
                </div>
                <div style={{padding:"18px 22px"}}>
                  <div style={{fontSize:14,color:"#374151",lineHeight:1.85,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>{selAnn.body||"No content."}</div>
                  <button onClick={()=>setSelAnn(null)} style={{display:"block",width:"100%",marginTop:16,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"10px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
                </div>
              </div>
            </div>
          </Portal>
        )}

        {/* ── Welcome header ── */}
        <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,borderRadius:16,padding:"20px 24px",color:"white"}}>
          <AcademyHeader/>
          <div style={{height:1,background:"rgba(255,255,255,0.2)",margin:"12px 0"}}/>
          <div style={{fontWeight:900,fontSize:18}}>السلام علیکم، {teacher.name}</div>
          <div style={{fontSize:13,opacity:.85,marginTop:2}}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
          <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
            {myClasses.length>0
              ? myClasses.map(c=><span key={c} style={{background:"rgba(255,255,255,.18)",borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700}}>{c}</span>)
              : <span style={{background:"rgba(255,255,255,.12)",borderRadius:6,padding:"3px 10px",fontSize:11,fontStyle:"italic"}}>No classes assigned yet</span>}
            {mySubjects.map(s=><span key={s} style={{background:"rgba(255,255,255,.10)",borderRadius:6,padding:"3px 10px",fontSize:11}}>{s}</span>)}
            {isHifz&&<span style={{background:"rgba(255,255,255,.18)",borderRadius:6,padding:"3px 10px",fontSize:12,fontWeight:700}}>📖 Hifz Teacher</span>}
          </div>
        </div>

        <SmartAlerts role="teacher" students={myStudents} attendance={attendance} fees={[]} myClasses={myClasses}/>

        {/* ── Quick stats ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:12}}>
          {[
            ["👥","My Students",totalStudents,C2.primary,"#f0fdf4"],
            ["✅","Present Today",todayPresent,"#2563eb","#eff6ff"],
            ["📝","My Classes",myClasses.length||"All","#7c3aed","#f5f3ff"],
            avgPct!=null?["📊","Avg Score",avgPct+"%","#d97706","#fffbeb"]:["📅","Periods Today",todaySchedule.length,"#0d9488","#f0fdfa"],
            atRisk.length>0?["⚠️","At-Risk Students",atRisk.length,"#dc2626","#fef2f2"]:["🎯","On Track",totalStudents,"#16a34a","#f0fdf4"],
          ].map(([icon,lbl,val,col,bg])=>(
            <div key={lbl} style={{background:lbl==="At-Risk Students"&&atRiskPanel?"#fef2f2":bg,borderRadius:14,padding:"14px 12px",border:`1.5px solid ${lbl==="At-Risk Students"&&atRiskPanel?"#dc2626":col+"33"}`,cursor:lbl==="At-Risk Students"?"pointer":"default",transition:"all .2s"}}
              onClick={()=>{ if(lbl==="At-Risk Students") setAtRiskPanel(v=>!v); }}>
              <div style={{fontSize:20,marginBottom:4}}>{icon}</div>
              <div style={{fontWeight:900,fontSize:20,color:col,lineHeight:1}}>{val}</div>
              <div style={{fontSize:10,color:C2.muted,marginTop:3,fontWeight:600}}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* ── At-Risk Inline Panel ── */}
        {atRiskPanel && atRisk.length>0 && (
          <div className="fade-up" style={{background:"#fef2f2",borderRadius:14,padding:"14px 18px",border:"1.5px solid #fecaca"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontWeight:800,fontSize:13,color:"#dc2626"}}>⚠️ At-Risk Students — Below 75% Attendance</div>
              <button onClick={()=>{setFocusClass(null);setActiveTab("performance");setAtRiskPanel(false);}}
                style={{background:"#dc2626",color:"white",border:"none",borderRadius:8,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                View Full Performance →
              </button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {atRisk.map(s=>{
                const r=attendance.filter(a=>String(a.student_id)===String(s.id));
                const pct=Math.round(r.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/r.length*100);
                const myRes=results.filter(x=>String(x.student_id)===String(s.id)&&(mySubjects.includes(x.subject)||mySubjects.length===0));
                const avg=myRes.length?Math.round(myRes.reduce((t,x)=>t+(x.marks_obtained/x.total_marks)*100,0)/myRes.length):null;
                return (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"white",borderRadius:10,border:"1px solid #fecaca"}}>
                    <div style={{width:36,height:36,borderRadius:"50%",background:"#fef2f2",border:"2px solid #fca5a5",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                      {(s.full_name||s.name||"?")[0].toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.full_name||s.name}</div>
                      <div style={{fontSize:11,color:"#6b7280"}}>{s.class||s["class"]} {s.roll_no?"· Roll "+s.roll_no:""}</div>
                    </div>
                    <div style={{textAlign:"center",flexShrink:0}}>
                      <div style={{fontWeight:900,fontSize:16,color:"#dc2626"}}>{pct}%</div>
                      <div style={{fontSize:10,color:"#6b7280"}}>Attendance</div>
                    </div>
                    {avg!=null&&(
                      <div style={{textAlign:"center",flexShrink:0}}>
                        <div style={{fontWeight:900,fontSize:16,color:avg>=60?"#d97706":"#dc2626"}}>{avg}%</div>
                        <div style={{fontSize:10,color:"#6b7280"}}>Avg Score</div>
                      </div>
                    )}
                    <button onClick={()=>{setFocusClass(s.class||s["class"]);setActiveTab("performance");setAtRiskPanel(false);}}
                      style={{background:"#dc2626",color:"white",border:"none",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                      View →
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Cover Duty Alert Banner ── */}
        {hasCoverToday&&(
          <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d5c2f)",borderRadius:14,padding:"16px 20px",color:"white",boxShadow:"0 4px 18px rgba(26,107,60,.35)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontWeight:900,fontSize:15,marginBottom:4}}>🔄 You have cover duties today</div>
                <div style={{fontSize:12,opacity:.85}}>You are covering {todayCoverDuties.length} class{todayCoverDuties.length!==1?"es":""} in place of absent staff</div>
              </div>
              <button onClick={()=>setActiveTab("coverduty")}
                style={{background:"rgba(255,255,255,.2)",color:"white",border:"1px solid rgba(255,255,255,.35)",borderRadius:9,padding:"7px 16px",fontWeight:800,fontSize:12,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}>
                View Cover Duties →
              </button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7,marginTop:12}}>
              {todayCoverDuties.map(d=>(
                <div key={d.id} style={{background:"rgba(255,255,255,.12)",borderRadius:9,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:800,fontSize:13}}>{d.class_name||"?"} · {d.subject||"?"}</div>
                    {d.period_time&&<div style={{fontSize:11,opacity:.75}}>{d.period_time}</div>}
                    <div style={{fontSize:11,opacity:.75}}>For: {d.absent_staff_name}</div>
                  </div>
                  <div style={{display:"flex",gap:7}}>
                    {d.status==="assigned"&&(
                      <button onClick={()=>updateCoverStatus(d.id,"acknowledged")}
                        style={{background:"rgba(255,255,255,.25)",color:"white",border:"1px solid rgba(255,255,255,.4)",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                        👁 Acknowledge
                      </button>
                    )}
                    <button onClick={()=>setActiveTab("coverduty")}
                      style={{background:"white",color:"#1a6b3c",border:"none",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                      📝 Take Attendance
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Today's Board ── */}
        {(()=>{
          const tasks = [
            ...myClasses.filter(cls=>!markedToday(cls)).map(cls=>({
              id:"att_"+cls, icon:"✅", label:`Mark attendance — ${cls}`,
              action:()=>setActiveTab("attendance"), actionLabel:"Mark Now", urgent:true
            })),
            // Cover duties as urgent tasks
            ...todayCoverDuties.filter(d=>d.status==="assigned").map(d=>({
              id:"cover_"+d.id, icon:"🔄",
              label:`Cover duty — ${d.class_name||"?"} · ${d.subject||"?"}${d.period_time?" ("+d.period_time+")":""}`,
              action:()=>setActiveTab("coverduty"), actionLabel:"Open", urgent:true,
            })),
            ...myClasses.map(cls=>{
              const clsStu=myStudents.filter(s=>(s.class||s["class"])===cls);
              const lowAtt=clsStu.filter(s=>{
                const r=attendance.filter(a=>String(a.student_id)===String(s.id));
                if(!r.length) return false;
                return Math.round(r.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/r.length*100)<75;
              });
              if(!lowAtt.length) return null;
              return {id:"low_"+cls,icon:"⚠️",label:`${lowAtt.length} student(s) in ${cls} below 75% attendance`,action:()=>{setFocusClass(cls);setActiveTab("performance");},actionLabel:"View",urgent:false};
            }).filter(Boolean),
            {id:"review_results",icon:"📝",label:"Review latest exam results",action:()=>setActiveTab("reports"),actionLabel:"View Reports",urgent:false},
            {id:"check_events",icon:"📅",label:"Check upcoming events & deadlines",action:()=>setActiveTab("events"),actionLabel:"Open Events",urgent:false},
          ];
          const pending=tasks.filter(t=>!todayBoardDone[t.id]);
          if(!pending.length) return (
            <div style={{background:"#f0fdf4",borderRadius:14,padding:"14px 18px",border:"1px solid #bbf7d0",display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:22}}>🎉</span>
              <div><div style={{fontWeight:800,color:"#15803d",fontSize:14}}>All done for today!</div><div style={{fontSize:12,color:"#4ade80"}}>Nothing pending on your board.</div></div>
            </div>
          );
          return (
            <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,border:"1px solid #e0f2fe"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div>
                  <div style={{fontWeight:800,fontSize:13,color:C2.text}}>📋 Today's Board</div>
                  <div style={{fontSize:11,color:C2.muted}}>{pending.length} task{pending.length!==1?"s":""} remaining</div>
                </div>
                <div style={{height:5,width:90,background:"#f3f4f6",borderRadius:3,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.round((tasks.length-pending.length)/tasks.length*100)}%`,background:C2.primary,borderRadius:3,transition:"width .4s"}}/>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {pending.map(t=>(
                  <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,background:t.urgent?"#fef2f2":"#f9fafb",border:`1px solid ${t.urgent?"#fecaca":"#f3f4f6"}`}}>
                    <span style={{fontSize:15,flexShrink:0}}>{t.icon}</span>
                    <div style={{flex:1,fontSize:12,fontWeight:600,color:t.urgent?"#dc2626":C2.text}}>{t.label}</div>
                    <div style={{display:"flex",gap:5}}>
                      <button onClick={t.action} style={{background:t.urgent?"#dc2626":C2.primary,color:"white",border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{t.actionLabel}</button>
                      <button onClick={()=>setTodayBoardDone(p=>({...p,[t.id]:true}))} style={{background:"#f3f4f6",color:"#6b7280",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 8px",fontSize:11,fontWeight:700,cursor:"pointer"}}>✓</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Two column: Today's Schedule + Deadlines/Calendar/Announcements ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

          {/* LEFT: Today's schedule + Classes snapshot */}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Today's schedule */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:10}}>📅 Today's Schedule</div>
              {todaySchedule.length===0?(
                <div style={{fontSize:12,color:C2.muted,fontStyle:"italic",padding:"8px 0"}}>No classes scheduled today.</div>
              ):todaySchedule.map(t=>(
                <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:C2.bg,borderRadius:9,marginBottom:6,flexWrap:"wrap"}}>
                  <div style={{background:C2.primary,color:"white",borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:800,flexShrink:0}}>P{t.period_no}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:C2.text,fontSize:12}}>{t.subject} — {t.class}</div>
                    {t.start_time&&<div style={{fontSize:10,color:C2.muted}}>{t.start_time}{t.end_time?` – ${t.end_time}`:""}</div>}
                  </div>
                  {markedToday(t.class)
                    ?<span style={{background:"#dcfce7",color:"#15803d",borderRadius:5,padding:"2px 8px",fontSize:10,fontWeight:700}}>✅ Marked</span>
                    :<button onClick={()=>setActiveTab("attendance")} style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:5,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer"}}>⚠️ Mark</button>
                  }
                </div>
              ))}
            </div>

            {/* Mini calendar */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontWeight:800,fontSize:12,color:C2.text}}>🗓️ Calendar</div>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()-1,1))} style={{background:"#f3f4f6",border:"none",borderRadius:5,width:22,height:22,cursor:"pointer",fontWeight:700,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
                  <span style={{fontSize:10,fontWeight:700,color:"#374151",minWidth:70,textAlign:"center"}}>{calMonth.toLocaleDateString("en-GB",{month:"short",year:"numeric"})}</span>
                  <button onClick={()=>setCalMonth(new Date(calMonth.getFullYear(),calMonth.getMonth()+1,1))} style={{background:"#f3f4f6",border:"none",borderRadius:5,width:22,height:22,cursor:"pointer",fontWeight:700,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,marginBottom:3}}>
                {["S","M","T","W","T","F","S"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:9,fontWeight:700,color:"#9ca3af"}}>{d}</div>)}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1}}>
                {Array.from({length:startDay}).map((_,i)=><div key={"e"+i}/>)}
                {Array.from({length:daysInMon}).map((_,i)=>{
                  const day=i+1;
                  const evs=evOnDay(day);
                  const ds=`${calMonth.getFullYear()}-${String(calMonth.getMonth()+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                  const isToday2=ds===todayStr2;
                  return (
                    <div key={day} onClick={()=>evs.length&&setSelEvent(evs[0])}
                      style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderRadius:5,cursor:evs.length?"pointer":"default",background:isToday2?"#1a6b3c":evs.length?"#f0fdf4":"transparent",border:isToday2?"none":evs.length?"1px solid #bbf7d0":"1px solid transparent"}}>
                      <span style={{fontSize:9,fontWeight:isToday2||evs.length?700:400,color:isToday2?"white":evs.length?"#1a6b3c":"#374151"}}>{day}</span>
                      {evs.length>0&&(
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:"100%"}}>
                          <div style={{width:"92%",fontSize:7,fontWeight:700,color:isToday2?"rgba(255,255,255,0.9)":EVENT_COLORS[evs[0].event_type]||"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"center",lineHeight:1.2}}>
                            {evs[0].title.length>6?evs[0].title.slice(0,5)+"…":evs[0].title}
                          </div>
                          {evs.length>1&&<div style={{fontSize:6,fontWeight:600,color:isToday2?"rgba(255,255,255,0.6)":"#9ca3af"}}>+{evs.length-1}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: Deadlines strip + Announcements */}
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {/* Upcoming deadlines */}
            {deadlines.length>0&&(
              <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:10}}>📋 Upcoming Deadlines</div>
                <div style={{display:"flex",flexDirection:"column",gap:7}}>
                  {deadlines.slice(0,4).map(ev=>{
                    const days=daysLeft(ev.date);
                    const col=dlColor(days);
                    return (
                      <div key={ev.id} onClick={()=>setSelEvent(ev)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,background:dlBg(days),border:`1px solid ${col}30`,cursor:"pointer"}}>
                        <span style={{fontSize:16,flexShrink:0}}>{EVENT_ICONS[ev.event_type]||"📅"}</span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:12,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.title}</div>
                          {ev.class&&ev.class!=="all"&&<div style={{fontSize:10,color:"#6b7280"}}>{ev.class}</div>}
                        </div>
                        <span style={{background:col,color:"white",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:800,flexShrink:0}}>{dlLabel(days)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Announcements */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`,flex:1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontWeight:800,fontSize:13,color:C2.text}}>📢 Announcements</div>
              </div>
              {announcements.length===0?(
                <div style={{fontSize:12,color:C2.muted,fontStyle:"italic",padding:"8px 0"}}>No announcements yet.</div>
              ):announcements.slice(0,4).map(a=>{
                const col=CAT_COLOR[a.category]||C2.primary;
                return (
                  <div key={a.id} onClick={()=>setSelAnn(a)} style={{borderRadius:9,overflow:"hidden",border:`1px solid ${col}20`,marginBottom:8,cursor:"pointer",transition:"transform .15s"}}
                    onMouseOver={e=>e.currentTarget.style.transform="translateY(-1px)"} onMouseOut={e=>e.currentTarget.style.transform="none"}>
                    <div style={{background:`linear-gradient(135deg,${col},${col}cc)`,padding:"6px 10px",display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:9,fontWeight:700,color:"white"}}>{a.category||"General"}</span>
                      <span style={{fontSize:9,color:"rgba(255,255,255,0.7)"}}>{new Date(a.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>
                    </div>
                    <div style={{padding:"8px 10px"}}>
                      <div style={{fontWeight:700,fontSize:11,color:"#111827",marginBottom:2}}>{a.title}</div>
                      {a.body&&<div style={{fontSize:10,color:"#6b7280",overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{a.body}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── My classes snapshot ── */}
        {myClasses.length>0&&(
          <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
            <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:10}}>🏫 My Classes</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:10}}>
              {myClasses.map(cls=>{
                const clsStu=myStudents.filter(s=>(s.class||s["class"])===cls);
                const clsPresent=todayAttendance.filter(a=>clsStu.find(s=>String(s.id)===String(a.student_id))&&(a.status==="P"||a.status==="Present")).length;
                const clsSubjects=[...new Set(myTimetable.filter(t=>t.class===cls).map(t=>t.subject).filter(Boolean))];
                const clsAtRisk=clsStu.filter(s=>{
                  const r=attendance.filter(a=>String(a.student_id)===String(s.id));
                  if(!r.length) return false;
                  return Math.round(r.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/r.length*100)<75;
                });
                return (
                  <div key={cls} style={{background:"#f9fafb",borderRadius:10,padding:"12px 14px",border:`1px solid ${C2.border}`,cursor:"pointer"}}
                    onClick={()=>{setFocusClass(cls);setActiveTab("performance");}}>
                    <div style={{fontWeight:800,color:C2.primary,fontSize:14}}>{cls}</div>
                    <div style={{fontSize:10,color:C2.muted,marginTop:1}}>{clsSubjects.join(", ")||"—"}</div>
                    <div style={{display:"flex",gap:8,marginTop:8}}>
                      <div style={{fontSize:11,color:"#15803d",fontWeight:700}}>✅ {clsPresent}/{clsStu.length} today</div>
                      {clsAtRisk.length>0&&<div style={{fontSize:11,color:"#dc2626",fontWeight:700}}>⚠️ {clsAtRisk.length} at risk</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };
  // ── Tab: Attendance ─────────────────────────────────────────────
  const TabAttendance = () => {
    const [selClass,    setSelClass]    = React.useState(myClasses[0]||"");
    const [selDate,     setSelDate]     = React.useState(todayStr);
    const [saving,      setSaving]      = React.useState(false);
    const [activeView,  setActiveView]  = React.useState("mark"); // "mark" | "report"
    const [reportMonth, setReportMonth] = React.useState(todayStr.slice(0,7));
    const [notes,       setNotes]       = React.useState({}); // {studentId: note}
    const [lateTimes,   setLateTimes]   = React.useState({}); // {studentId: "09:15"}
    const [logFilter,    setLogFilter]    = React.useState("all");
    const [editLogDate,  setEditLogDate]  = React.useState(null);
    const [editLogClass, setEditLogClass] = React.useState(null);

    const classStu = myStudents.filter(s=>(s.class||s["class"])===selClass);

    const STATUSES = [
      {v:"P", lbl:"Present", icon:"✅", col:"#15803d", bg:"#dcfce7", border:"#86efac"},
      {v:"A", lbl:"Absent",  icon:"❌", col:"#dc2626", bg:"#fef2f2", border:"#fca5a5"},
      {v:"L", lbl:"Late",    icon:"🕐", col:"#d97706", bg:"#fffbeb", border:"#fde68a"},
      {v:"LV",lbl:"Leave",   icon:"📋", col:"#7c3aed", bg:"#f0ebff", border:"#c4b5fd"},
    ];

    const [marks, setMarks] = React.useState({});

    React.useEffect(()=>{
      const init={};
      classStu.forEach(s=>{
        const existing=attendance.find(a=>a.date===selDate&&String(a.student_id)===String(s.id));
        init[s.id]=existing?.status||"P";
      });
      setMarks(init);
    },[selClass,selDate,attendance.length]);

    const bulkMark = (status) => {
      const all={};
      classStu.forEach(s=>{ all[s.id]=status; });
      setMarks(all);
    };

    const save = async () => {
      setSaving(true);
      const rows = classStu.map((s) => {
  const st = marks[s.id] || "P";
  return {
    student_id: String(s.id),
    student_name: s.full_name || s.name,
    class: selClass,
    date: selDate,
    status: st,
    statuscode: st,
    statuslabel:
      st === "P" ? "Present" :
      st === "A" ? "Absent" :
      st === "L" ? "Late" :
      st === "LV" ? "Leave" : "",
    note: notes[s.id] || null,
    latetime: st === "L" ? (lateTimes[s.id] || null) : null,
  };
});
      const {error} = await supabase.from("attendance").upsert(rows,{onConflict:"date,student_id"});
      if(error) showToast("Error: "+error.message,false);
      else {
        setAttendance(prev=>{
          const filtered=prev.filter(a=>!(a.date===selDate&&classStu.find(s=>String(s.id)===String(a.student_id))));
          return [...filtered,...rows];
        });
        // If this was a cover class, auto-mark the cover duty as done
        const coverDuty = coverDuties.find(d=>d.date===selDate&&d.class_name===selClass&&["assigned","acknowledged"].includes(d.status));
        if(coverDuty){
          await updateCoverStatus(coverDuty.id,"done");
          showToast("✅ Attendance saved & cover duty marked as Done!");
        } else {
          showToast("✅ Attendance saved for "+selDate);
        }
      }
      setSaving(false);
    };

    const sendAbsentWA = (s) => {
      const phone = s.contact||s.phone||"";
      if(!phone){showToast("No phone number for "+( s.full_name||s.name),false);return;}
      const schoolName = S?.school_name_en||"QK Academy";
      const msg = `Assalamu Alaikum! This is a notification from ${schoolName}. Your child ${s.full_name||s.name} (${selClass}) was marked ABSENT on ${selDate}. Please contact the school if needed. JazakAllah Khair.`;
      openWhatsApp(phone, msg);
    };

    // Monthly report data
    const monthDates = [...new Set(attendance.filter(a=>{
      const cls=(s=>s.class||s["class"])(classStu.find(s=>String(s.id)===String(a.student_id))||{});
      return a.date&&a.date.startsWith(reportMonth)&&(selClass==="all"||cls===selClass);
    }).map(a=>a.date))].sort();

    const reportData = classStu.map(s=>{
      const recs = attendance.filter(a=>a.date.startsWith(reportMonth)&&String(a.student_id)===String(s.id));
      const P=recs.filter(a=>a.status==="P").length;
      const A=recs.filter(a=>a.status==="A").length;
      const L=recs.filter(a=>a.status==="L").length;
      const LV=recs.filter(a=>a.status==="LV").length;
      const total=recs.length||1;
      const pct=Math.round(((P+L*0.5+LV)/total)*100);
      return {s,P,A,L,LV,total:recs.length,pct,recs};
    }).sort((a,b)=>b.pct-a.pct);

    const presentCount = Object.values(marks).filter(v=>v==="P").length;
    const absentCount  = Object.values(marks).filter(v=>v==="A").length;
    const lateCount    = Object.values(marks).filter(v=>v==="L").length;

    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* ── Header card ── */}
        <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontWeight:900,fontSize:16,color:C2.primary}}>📋 Attendance Register</div>
              <div style={{fontSize:12,color:C2.muted,marginTop:2}}>{S?.school_name_en||"QK Academy"} · {selClass}</div>
            </div>
            {/* View toggle */}
            <div style={{display:"flex",gap:2,background:"#f3f4f6",borderRadius:10,padding:3}}>
              {[["mark","📝 Mark","mark"],["report","📊 Report","report"]].map(([v,lbl,id])=>(
                <button key={v} onClick={()=>setActiveView(v)}
                  style={{background:activeView===v?"white":"transparent",border:"none",borderRadius:8,
                    padding:"7px 16px",fontWeight:700,fontSize:12,
                    color:activeView===v?C2.primary:"#6b7280",cursor:"pointer",transition:"all .15s",
                    boxShadow:activeView===v?"0 1px 4px rgba(0,0,0,0.1)":"none"}}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeView==="mark" && (<>
          {/* Controls row */}
          {(()=>{
            // Class teacher = can mark. Subject teacher = view only.
            // Exception: cover teacher can mark attendance for their assigned cover class on that specific date only.
            const coverOverride = hasCoverForClass(selClass, selDate);
            const isViewOnly = myPrimaryClasses.length>0 && !myPrimaryClasses.includes(selClass) && !coverOverride;
            return (
          <>
          {coverOverride && (
            <div style={{background:"#f0fdf4",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#15803d",border:"1px solid #bbf7d0",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:16}}>🔄</span>
              <span><strong>Cover Mode</strong> — You are the cover teacher for {selClass} today. Attendance saved will auto-mark your cover duty as Done.</span>
            </div>
          )}
          {isViewOnly && (
            <div style={{background:"#fffbeb",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#92400e",border:"1px solid #fde68a",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:16}}>👁️</span>
              <span><strong>View Only</strong> — You are a subject teacher for {selClass}, not the class teacher. Only the assigned class teacher can mark attendance.</span>
            </div>
          )}
          <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:14,flexWrap:"wrap",alignItems:"flex-end"}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>CLASS</label>
              <select value={selClass} onChange={e=>setSelClass(e.target.value)}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:130}}>
                {(myClasses.length>0?myClasses:[...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort()).map(c=>(
                  <option key={c} value={c}>{c}{myPrimaryClasses.includes(c)?" 🏫":""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>DATE</label>
              <input type="date" value={selDate} onChange={e=>setSelDate(e.target.value)} max={todayStr}
                style={{border:`1.5px solid ${(()=>{const off=(S?.off_days||"Saturday,Sunday").split(",").map(d=>d.trim());return off.includes(new Date(selDate).toLocaleDateString("en-GB",{weekday:"long"}))?"#f97316":C2.border;})()}`,borderRadius:8,padding:"8px 12px",fontSize:13,
                background:(()=>{const off=(S?.off_days||"Saturday,Sunday").split(",").map(d=>d.trim());return off.includes(new Date(selDate).toLocaleDateString("en-GB",{weekday:"long"}))?"#fff7ed":"white";})()}}/>
              {(()=>{const off=(S?.off_days||"Saturday,Sunday").split(",").map(d=>d.trim()); const day=new Date(selDate).toLocaleDateString("en-GB",{weekday:"long"}); return off.includes(day)?<div style={{fontSize:10,color:"#c2410c",fontWeight:600,marginTop:2}}>⚠️ {day} is an off day</div>:null;})()}
            </div>
            {/* Bulk actions */}
            <div style={{display:"flex",gap:6,alignItems:"flex-end"}}>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>BULK MARK</label>
              <div style={{display:"flex",gap:6}}>
                {STATUSES.map(st=>(
                  <button key={st.v} onClick={()=>bulkMark(st.v)}
                    style={{background:st.bg,color:st.col,border:`1.5px solid ${st.border}`,borderRadius:8,
                      padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                    {st.icon} All {st.lbl}
                  </button>
                ))}
              </div>
            </div>
            {/* Stats pills */}
            <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              {[["✅",presentCount,"#15803d","#dcfce7"],["❌",absentCount,"#dc2626","#fef2f2"],["🕐",lateCount,"#d97706","#fffbeb"]].map(([icon,n,col,bg])=>(
                <div key={icon} style={{background:bg,color:col,borderRadius:20,padding:"4px 12px",fontWeight:800,fontSize:13}}>
                  {icon} {n}
                </div>
              ))}
              <div style={{color:C2.muted,fontSize:12,fontWeight:600}}>{classStu.length} students</div>
            </div>
          </div>

          {/* Student list */}
          {classStu.length===0 ? (
            <div style={{textAlign:"center",padding:48,color:C2.muted,background:"white",borderRadius:14,
              boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontSize:32,marginBottom:8}}>👥</div>
              No students found for this class.
            </div>
          ) : (
            <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden"}}>
              {/* Table header */}
              <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,padding:"10px 18px",display:"flex",alignItems:"center",gap:10}}>
                <div style={{flex:1,color:"white",fontWeight:700,fontSize:12}}>STUDENT</div>
                <div style={{width:260,color:"white",fontWeight:700,fontSize:12}}>STATUS</div>
                <div style={{width:90,color:"white",fontWeight:700,fontSize:12}}>LATE TIME</div>
                <div style={{flex:1,color:"white",fontWeight:700,fontSize:12}}>NOTE</div>
                <div style={{width:36}}/>
              </div>

              {classStu.map((s,i)=>{
                const status = marks[s.id]||"P";
                const st = STATUSES.find(x=>x.v===status)||STATUSES[0];
                return (
                  <div key={s.id} style={{
                    display:"flex",alignItems:"center",gap:10,padding:"10px 18px",
                    background:i%2===0?"white":"#fafafa",
                    borderBottom:`1px solid ${C2.border}`,
                    borderLeft:`4px solid ${status==="P"?"#86efac":status==="A"?"#fca5a5":status==="LV"?"#c4b5fd":"#fde68a"}`,
                    transition:"background .15s"
                  }}>
                    {/* Student info */}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:C2.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {s.full_name||s.name}
                      </div>
                      <div style={{fontSize:11,color:C2.muted}}>{s.roll_no?"Roll "+s.roll_no:s.id}</div>
                    </div>

                    {/* Status buttons */}
                    <div style={{display:"flex",gap:5,width:260}}>
                      {STATUSES.map(st=>(
                        <button key={st.v} onClick={()=>setMarks(p=>({...p,[s.id]:st.v}))}
                          style={{
                            background:marks[s.id]===st.v?st.bg:"white",
                            color:marks[s.id]===st.v?st.col:"#9ca3af",
                            border:`1.5px solid ${marks[s.id]===st.v?st.border:"#e5e7eb"}`,
                            borderRadius:8,padding:"5px 10px",fontWeight:700,fontSize:11,cursor:"pointer",
                            transform:marks[s.id]===st.v?"scale(1.05)":"scale(1)",transition:"all .12s",
                            flex:1
                          }}>
                          {st.icon} {st.lbl}
                        </button>
                      ))}
                    </div>

                    {/* Late time */}
                    <input type="time" value={lateTimes[s.id]||""} disabled={marks[s.id]!=="L"}
                      onChange={e=>setLateTimes(p=>({...p,[s.id]:e.target.value}))}
                      style={{width:90,border:`1.5px solid ${marks[s.id]==="L"?"#fde68a":"#f3f4f6"}`,
                        borderRadius:8,padding:"6px 8px",fontSize:12,
                        background:marks[s.id]==="L"?"#fffbeb":"#f9fafb",
                        color:marks[s.id]==="L"?"#92400e":"#9ca3af"}}/>

                    {/* Note */}
                    <input type="text" value={notes[s.id]||""} placeholder="Note…"
                      onChange={e=>setNotes(p=>({...p,[s.id]:e.target.value}))}
                      style={{flex:1,border:`1.5px solid #f3f4f6`,borderRadius:8,padding:"6px 10px",
                        fontSize:12,color:C2.text,background:"white"}}/>

                    {/* WA absent alert */}
                    {(marks[s.id]==="A"||marks[s.id]==="L") ? (
                      <button onClick={()=>sendAbsentWA(s)} title="WhatsApp parent"
                        style={{width:34,height:34,borderRadius:8,border:"none",background:"#dcfce7",
                          color:"#15803d",fontSize:16,cursor:"pointer",flexShrink:0,display:"flex",
                          alignItems:"center",justifyContent:"center"}}>
                        💬
                      </button>
                    ) : <div style={{width:34}}/>}
                  </div>
                );
              })}

              {/* Footer */}
              <div style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",
                alignItems:"center",background:"#f9fafb",borderTop:`1.5px solid ${C2.border}`}}>
                <div style={{fontSize:12,color:C2.muted,fontWeight:600}}>
                  ✅ {presentCount} Present · ❌ {absentCount} Absent · 🕐 {lateCount} Late
                  {" · "}{classStu.length>0?Math.round((presentCount/classStu.length)*100):0}% rate
                </div>
                <button onClick={save} disabled={saving}
                  style={{background:saving?"#9ca3af":`linear-gradient(135deg,${C2.primary},#0a4a24)`,
                    color:"white",border:"none",borderRadius:10,padding:"10px 28px",
                    fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",
                    boxShadow:"0 2px 8px rgba(26,107,60,0.3)"}}>
                  {saving?"💾 Saving…":"💾 Save Attendance"}
                </button>
              </div>
              {isViewOnly && (
                <div style={{background:"#f3f4f6",borderRadius:10,padding:"10px 18px",fontSize:12,color:"#9ca3af",fontWeight:600}}>
                  👁️ View only — contact the class teacher to update attendance
                </div>
              )}
            </div>
          )}
        </>);
          })()}
        </>)}

        {activeView==="report" && (<>
          {/* Monthly report */}
          <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:14,alignItems:"flex-end",flexWrap:"wrap"}}>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>CLASS</label>
              <select value={selClass} onChange={e=>setSelClass(e.target.value)}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:130}}>
                {(myClasses.length>0?myClasses:[...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort()).map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>MONTH</label>
              <input type="month" value={reportMonth} onChange={e=>setReportMonth(e.target.value)}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}/>
            </div>
            <button onClick={()=>window.print()}
              style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,color:"white",border:"none",
                borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer",
                display:"flex",alignItems:"center",gap:6}}>
              🖨️ Print Report
            </button>
          </div>

          {/* Report card */}
          <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden"}} id="att-report-print">
            {/* Letterhead */}
            <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,padding:"18px 24px"}}>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <img src={SCHOOL_LOGO} style={{width:48,height:48,objectFit:"contain",filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.3))"}}/>
                <div>
                  <div style={{color:"white",fontWeight:900,fontSize:16,letterSpacing:0.5}}>{S?.school_name_en||"QUR'AN-E-KARIM ACADEMY"}</div>
                  <div style={{color:"rgba(255,255,255,0.75)",fontSize:11,marginTop:1}}>{S?.school_name_en_sub||"Modern Islamic School System"}</div>
                  <div style={{color:"rgba(255,255,255,0.6)",fontSize:10,marginTop:1}}>{S?.address||""} · Reg. {S?.reg_no||""}</div>
                </div>
                <div style={{marginLeft:"auto",textAlign:"right"}}>
                  <div style={{color:"rgba(255,255,255,0.8)",fontSize:11,fontWeight:600}}>MONTHLY ATTENDANCE REPORT</div>
                  <div style={{color:"white",fontWeight:800,fontSize:14,marginTop:2}}>{selClass}</div>
                  <div style={{color:"rgba(255,255,255,0.7)",fontSize:11,marginTop:1}}>
                    {new Date(reportMonth+"-01").toLocaleDateString("en-PK",{month:"long",year:"numeric"})}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary row */}
            <div style={{display:"flex",gap:0,borderBottom:`2px solid ${C2.border}`}}>
              {[
                ["Total Students",classStu.length,"#1a6b3c","#e8f5ee"],
                ["Working Days",monthDates.length,"#2563eb","#eff6ff"],
                ["Avg Attendance",reportData.length?Math.round(reportData.reduce((s,r)=>s+r.pct,0)/reportData.length)+"%":"—","#0d9488","#e0f7f5"],
                ["Below 75%",reportData.filter(r=>r.pct<75).length,"#dc2626","#fef2f2"],
              ].map(([lbl,val,col,bg])=>(
                <div key={lbl} style={{flex:1,padding:"14px 16px",background:bg,textAlign:"center",borderRight:`1px solid ${C2.border}`}}>
                  <div style={{fontWeight:900,fontSize:20,color:col}}>{val}</div>
                  <div style={{fontSize:11,color:col,fontWeight:600,marginTop:2}}>{lbl}</div>
                </div>
              ))}
            </div>

            {/* Table */}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#f9fafb"}}>
                    <th style={{padding:"10px 14px",textAlign:"left",fontWeight:700,color:C2.muted,borderBottom:`2px solid ${C2.border}`,minWidth:160}}>Student</th>
                    <th style={{padding:"10px 10px",textAlign:"center",fontWeight:700,color:"#15803d",borderBottom:`2px solid ${C2.border}`}}>✅ Present</th>
                    <th style={{padding:"10px 10px",textAlign:"center",fontWeight:700,color:"#dc2626",borderBottom:`2px solid ${C2.border}`}}>❌ Absent</th>
                    <th style={{padding:"10px 10px",textAlign:"center",fontWeight:700,color:"#d97706",borderBottom:`2px solid ${C2.border}`}}>🕐 Late</th>
                    <th style={{padding:"10px 10px",textAlign:"center",fontWeight:700,color:"#7c3aed",borderBottom:`2px solid ${C2.border}`}}>📋 Leave</th>
                    <th style={{padding:"10px 10px",textAlign:"center",fontWeight:700,color:C2.muted,borderBottom:`2px solid ${C2.border}`}}>Total Days</th>
                    <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:C2.primary,borderBottom:`2px solid ${C2.border}`,minWidth:100}}>Attendance %</th>
                    <th style={{padding:"10px 14px",textAlign:"center",fontWeight:700,color:C2.muted,borderBottom:`2px solid ${C2.border}`}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map(({s,P,A,L,LV,total,pct},i)=>{
                    const col = pct>=85?"#15803d":pct>=75?"#0d9488":pct>=50?"#d97706":"#dc2626";
                    const bg  = pct>=85?"#f0fdf4":pct>=75?"#e0f7f5":pct>=50?"#fffbeb":"#fef2f2";
                    const status = pct>=75?"✅ Good":pct>=50?"⚠️ Warning":"🚨 Critical";
                    return (
                      <tr key={s.id} style={{borderBottom:`1px solid ${C2.border}`,background:i%2===0?"white":"#fafafa"}}>
                        <td style={{padding:"10px 14px",fontWeight:700,color:C2.text}}>
                          {s.full_name||s.name}
                          <div style={{fontSize:10,color:C2.muted,fontWeight:400}}>{s.roll_no?"Roll "+s.roll_no:""}</div>
                        </td>
                        <td style={{padding:"10px",textAlign:"center",fontWeight:700,color:"#15803d"}}>{P}</td>
                        <td style={{padding:"10px",textAlign:"center",fontWeight:700,color:"#dc2626"}}>{A}</td>
                        <td style={{padding:"10px",textAlign:"center",fontWeight:700,color:"#d97706"}}>{L}</td>
                        <td style={{padding:"10px",textAlign:"center",fontWeight:700,color:"#7c3aed"}}>{LV||0}</td>
                        <td style={{padding:"10px",textAlign:"center",color:C2.muted,fontWeight:600}}>{total}</td>
                        <td style={{padding:"10px 14px",textAlign:"center"}}>
                          <div style={{display:"inline-flex",alignItems:"center",gap:6}}>
                            <div style={{width:50,height:7,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                              <div style={{width:`${pct}%`,height:"100%",background:col,borderRadius:4}}/>
                            </div>
                            <span style={{fontWeight:900,color:col,fontSize:13}}>{pct}%</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 14px",textAlign:"center"}}>
                          <span style={{background:bg,color:col,borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700}}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:C2.light,borderTop:`2px solid ${C2.border}`}}>
                    <td style={{padding:"10px 14px",fontWeight:800,color:C2.primary}}>Class Average</td>
                    <td style={{padding:"10px",textAlign:"center",fontWeight:800,color:"#15803d"}}>
                      {reportData.length?Math.round(reportData.reduce((s,r)=>s+r.P,0)/reportData.length):0}
                    </td>
                    <td style={{padding:"10px",textAlign:"center",fontWeight:800,color:"#dc2626"}}>
                      {reportData.length?Math.round(reportData.reduce((s,r)=>s+r.A,0)/reportData.length):0}
                    </td>
                    <td style={{padding:"10px",textAlign:"center",fontWeight:800,color:"#d97706"}}>
                      {reportData.length?Math.round(reportData.reduce((s,r)=>s+r.L,0)/reportData.length):0}
                    </td>
                    <td style={{padding:"10px",textAlign:"center",fontWeight:800,color:"#7c3aed"}}>
                      {reportData.length?Math.round(reportData.reduce((s,r)=>s+(r.LV||0),0)/reportData.length):0}
                    </td>
                    <td colSpan={2} style={{padding:"10px 14px",textAlign:"center",fontWeight:900,color:C2.primary,fontSize:14}}>
                      {reportData.length?Math.round(reportData.reduce((s,r)=>s+r.pct,0)/reportData.length):0}% avg
                    </td>
                    <td style={{padding:"10px 14px",textAlign:"center"}}>
                      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
                        <span style={{fontSize:11,color:"#15803d",fontWeight:700}}>✅ Good: {reportData.filter(r=>r.pct>=75).length}</span>
                        <span style={{fontSize:11,color:"#dc2626",fontWeight:700}}>🚨 Below 75%: {reportData.filter(r=>r.pct<75).length}</span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Signature row */}
            <div style={{padding:"20px 24px",display:"flex",justifyContent:"space-between",borderTop:`1px solid ${C2.border}`}}>
              {["Class Teacher","Principal / Head"].map(lbl=>(
                <div key={lbl} style={{textAlign:"center",minWidth:160}}>
                  <div style={{height:40,borderBottom:"1.5px dashed #9ca3af",marginBottom:6}}/>
                  <div style={{fontSize:11,color:C2.muted,fontWeight:600}}>{lbl}</div>
                </div>
              ))}
              <div style={{textAlign:"right",fontSize:10,color:C2.muted}}>
                Generated: {new Date().toLocaleDateString("en-PK",{dateStyle:"long"})}<br/>
                {S?.school_name_en||"QK Academy"} · Reg. {S?.reg_no||""}
              </div>
            </div>
          </div>
        </>)}

        {activeView==="log" && (()=>{
          const myAttDates = [...new Set(
            attendance
              .filter(a => myStudents.find(s => String(s.id) === String(a.student_id)))
              .map(a => a.date + "|||" + (a.class || ""))
          )].sort((a,b)=>b.localeCompare(a));

          const sessions = myAttDates.map(key => {
            const [date, cls] = key.split("|||");
            const recs = attendance.filter(a =>
              a.date === date &&
              myStudents.find(s => String(s.id) === String(a.student_id)) &&
              (!cls || (a.class || "") === cls)
            );
            const P = recs.filter(a=>a.status==="P").length;
            const A = recs.filter(a=>a.status==="A").length;
            const L = recs.filter(a=>a.status==="L").length;
            const LV = recs.filter(a=>a.status==="LV").length;
            return { date, cls, recs, P, A, L, LV, total: recs.length };
          });

          const logFiltered = logFilter === "all" ? sessions
            : sessions.filter(s => s.cls === logFilter);
          const logClasses = [...new Set(sessions.map(s=>s.cls).filter(Boolean))].sort();

          return (
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{background:"white",borderRadius:14,padding:"14px 18px",
                boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{fontWeight:800,fontSize:13,color:C2.text}}>📋 Attendance Log</div>
                <div style={{flex:1}}/>
                <select value={logFilter} onChange={e=>setLogFilter(e.target.value)}
                  style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,color:C2.text}}>
                  <option value="all">All Classes</option>
                  {logClasses.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{fontSize:12,color:C2.muted,fontWeight:600}}>{logFiltered.length} session{logFiltered.length!==1?"s":""}</div>
              </div>

              {logFiltered.length===0 ? (
                <div style={{background:"white",borderRadius:14,padding:48,textAlign:"center",
                  boxShadow:`0 2px 8px ${C2.shadow}`,color:C2.muted}}>
                  <div style={{fontSize:36,marginBottom:12}}>📋</div>
                  <div style={{fontWeight:700,marginBottom:6}}>No attendance records yet</div>
                  <div style={{fontSize:12}}>Sessions you submit will appear here.</div>
                </div>
              ) : logFiltered.map(({date,cls,recs,P,A,L,LV,total})=>(
                <div key={date+"|||"+cls} style={{background:"white",borderRadius:14,
                  boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden",
                  border:`1.5px solid ${A>0?"#fecaca":L>0?"#fde68a":C2.border}`}}>
                  <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,padding:"12px 18px",
                    display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div style={{flex:1}}>
                      <div style={{color:"white",fontWeight:900,fontSize:14}}>
                        {new Date(date).toLocaleDateString("en-PK",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
                      </div>
                      <div style={{color:"rgba(255,255,255,0.75)",fontSize:11,marginTop:2}}>{cls} · {total} students</div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {[["✅",P,"#dcfce7","#15803d"],["❌",A,"#fee2e2","#dc2626"],
                        ["🕐",L,"#fed7aa","#c2410c"],["📋",LV,"#ddd6fe","#7c3aed"]].map(([icon,n,bg,col])=>
                        n>0&&<span key={icon} style={{background:bg,color:col,borderRadius:20,
                          padding:"3px 10px",fontSize:12,fontWeight:800}}>{icon} {n}</span>
                      )}
                    </div>
                    <button onClick={()=>{ setEditLogDate(editLogDate===date&&editLogClass===cls?null:date); setEditLogClass(cls); }}
                      style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.35)",
                        borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>
                      {editLogDate===date&&editLogClass===cls?"✕ Close":"✏️ Edit"}
                    </button>
                  </div>

                  {editLogDate!==date||editLogClass!==cls ? (
                    <div style={{padding:"10px 18px",display:"flex",gap:6,flexWrap:"wrap"}}>
                      {recs.map(r=>{
                        const stu = myStudents.find(s=>String(s.id)===String(r.student_id));
                        const name = stu?.full_name||stu?.name||r.student_name||String(r.student_id);
                        const col = r.status==="P"?"#15803d":r.status==="A"?"#dc2626":r.status==="L"?"#c2410c":"#7c3aed";
                        const bg  = r.status==="P"?"#f0fdf4":r.status==="A"?"#fef2f2":r.status==="L"?"#fff7ed":"#f5f3ff";
                        const icon= r.status==="P"?"✅":r.status==="A"?"❌":r.status==="L"?"🕐":"📋";
                        return (
                          <span key={r.student_id} title={r.note||""} style={{background:bg,color:col,
                            borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:700,border:`1px solid ${col}22`}}>
                            {icon} {name.split(" ")[0]}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div>
                      <div style={{padding:"10px 18px",background:"#fffbeb",borderBottom:"1px solid #fde68a",
                        display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:12,color:"#92400e",fontWeight:700}}>✏️ Editing — {date} · {cls}</span>
                        <div style={{flex:1}}/>
                        <button onClick={async()=>{
                          const editRows = recs.map(r=>({
                            student_id:r.student_id, student_name:r.student_name,
                            class:r.class, date:r.date, status:r.status,
                            statuscode:r.status,
                            statuslabel:r.status==="P"?"Present":r.status==="A"?"Absent":r.status==="L"?"Late":"Leave",
                            note:r.note, latetime:r.latetime
                          }));
                          const {error} = await supabase.from("attendance").upsert(editRows,{onConflict:"date,student_id"});
                          if(error) showToast("Error: "+error.message,false);
                          else { showToast("✅ Changes saved"); setEditLogDate(null); setEditLogClass(null); }
                        }} style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,color:"white",
                          border:"none",borderRadius:8,padding:"6px 18px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                          💾 Save Changes
                        </button>
                      </div>
                      {recs.map((r,i)=>{
                        const stu = myStudents.find(s=>String(s.id)===String(r.student_id));
                        const name = stu?.full_name||stu?.name||r.student_name||String(r.student_id);
                        return (
                          <div key={r.student_id} style={{display:"flex",alignItems:"center",gap:10,
                            padding:"10px 18px",background:i%2===0?"white":"#fafafa",
                            borderBottom:`1px solid ${C2.border}`,flexWrap:"wrap"}}>
                            <div style={{minWidth:140,fontWeight:700,fontSize:13,color:C2.text}}>{name}</div>
                            <div style={{display:"flex",gap:5,flex:1,flexWrap:"wrap"}}>
                              {[["P","✅ Present","#15803d","#dcfce7","#86efac"],
                                ["A","❌ Absent","#dc2626","#fef2f2","#fca5a5"],
                                ["L","🕐 Late","#c2410c","#fff7ed","#fdba74"],
                                ["LV","📋 Leave","#7c3aed","#f5f3ff","#c4b5fd"]].map(([v,lbl,col,bg,border])=>(
                                <button key={v} onClick={()=>{
                                  setAttendance(prev=>prev.map(a=>
                                    a.date===r.date&&String(a.student_id)===String(r.student_id)
                                      ?{...a,status:v,statuscode:v}:a
                                  ));
                                }} style={{background:r.status===v?bg:"white",color:r.status===v?col:"#9ca3af",
                                  border:`1.5px solid ${r.status===v?border:"#e5e7eb"}`,
                                  borderRadius:8,padding:"5px 10px",fontWeight:700,fontSize:11,cursor:"pointer",
                                  transform:r.status===v?"scale(1.05)":"scale(1)",transition:"all .12s"}}>
                                  {lbl}
                                </button>
                              ))}
                            </div>
                            {r.note&&<div style={{fontSize:11,color:C2.muted,fontStyle:"italic"}}>📝 {r.note}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    );
  };

  const TabResults = () => {
    const [selClass,    setSelClass]    = React.useState(focusClass||myClasses[0]||"");
    const [selSubject,  setSelSubject]  = React.useState(mySubjects[0]||"");
    const [examType,    setExamType]    = React.useState("quiz");
    const [selExam,     setSelExam]     = React.useState("");
    const [totalMarks,  setTotalMarks]  = React.useState(100);
    const [marks,       setMarks]       = React.useState({});
    const [remarks,     setRemarks]     = React.useState({});
    const [saving,      setSaving]      = React.useState(false);
    const [hwAssign,    setHwAssign]    = React.useState([]); // homework assignments

    const EXAM_TYPES = [
      {v:"quiz",     lbl:"Quiz",             icon:"📝", maxDefault:20},
      {v:"monthly",  lbl:"Monthly Test",     icon:"📅", maxDefault:50},
      {v:"midterm",  lbl:"Mid-Term Exam",    icon:"📋", maxDefault:100},
      {v:"final",    lbl:"Final Exam",       icon:"🎓", maxDefault:100},
      {v:"homework", lbl:"Homework / Assignment", icon:"📚", maxDefault:10},
    ];

    const classStu = myStudents.filter(s=>(s.class||s["class"])===selClass);

    // Load homework assignments for this class when type=homework
    React.useEffect(()=>{
      if(examType!=="homework") return;
      supabase.from("school_events")
        .select("*")
        .eq("event_type","assignment")
        .order("date",{ascending:false})
        .then(({data})=>{
          const filtered=(data||[]).filter(e=>e.class==="all"||e.class===selClass);
          setHwAssign(filtered);
          if(filtered.length>0&&!selExam) setSelExam(filtered[0].title);
        });
    },[examType,selClass]);

    // Auto-set total marks when type changes
    React.useEffect(()=>{
      const t = EXAM_TYPES.find(e=>e.v===examType);
      if(t) setTotalMarks(t.maxDefault);
    },[examType]);

    const myExams = examType==="homework"
      ? hwAssign.map(h=>h.title)
      : [...new Set(exams.filter(e=>!examType||e.exam_type===examType).map(e=>e.name)
          .concat(results.filter(r=>!examType||r.exam_type===examType).map(r=>r.exam_name)))].filter(Boolean).sort();

    React.useEffect(()=>{
      if(!selExam||!selSubject) return;
      const init={},initR={};
      classStu.forEach(s=>{
        const existing=results.find(r=>
          String(r.student_id)===String(s.id)&&
          r.exam_name===selExam&&
          r.subject===selSubject
        );
        if(existing){init[s.id]=existing.marks_obtained;initR[s.id]=existing.remarks||"";}
      });
      setMarks(init);
      setRemarks(initR);
    },[selExam,selSubject,selClass]);

    const GRADE = (pct) => pct>=90?{g:"A+",c:"#15803d",bg:"#dcfce7"}:pct>=80?{g:"A",c:"#15803d",bg:"#dcfce7"}
      :pct>=70?{g:"B+",c:"#0d9488",bg:"#e0f7f5"}:pct>=60?{g:"B",c:"#2563eb",bg:"#eff6ff"}
      :pct>=50?{g:"C",c:"#d97706",bg:"#fffbeb"}:pct>=40?{g:"D",c:"#ea580c",bg:"#fff7ed"}:{g:"F",c:"#dc2626",bg:"#fef2f2"};

    const entered = classStu.filter(s=>marks[s.id]!==undefined&&marks[s.id]!=="");
    const avgMark = entered.length ? Math.round(entered.reduce((a,s)=>a+(Number(marks[s.id])||0),0)/entered.length) : 0;
    const avgPct  = totalMarks>0 ? Math.round((avgMark/totalMarks)*100) : 0;
    const passCount = entered.filter(s=>totalMarks>0&&(Number(marks[s.id])||0)/totalMarks>=0.5).length;

    const save = async () => {
      if(!selExam||!selSubject) return showToast("Select exam and subject first",false);
      setSaving(true);
      const rows = classStu.filter(s=>marks[s.id]!==undefined&&marks[s.id]!=="").map(s=>({
        student_id:String(s.id), student_name:s.full_name||s.name,
        class:selClass, exam_name:selExam,
        subject:selSubject, marks_obtained:Number(marks[s.id])||0,
        total_marks:totalMarks, remarks:remarks[s.id]||"",
        grade:GRADE(totalMarks>0?Math.round((Number(marks[s.id])||0)/totalMarks*100):0).g,
      }));
      if(!rows.length) return showToast("No marks entered",false);
      let error = null;
      try {
        const res = await supabase.from("results").upsert(rows,{onConflict:"student_id,exam_name,subject"});
        error = res.error;
      } catch(e) { error = e; }
      if(error){
        for(const row of rows){
          await supabase.from("results").delete().match({student_id:row.student_id,exam_name:row.exam_name,subject:row.subject});
          await supabase.from("results").insert(row);
        }
      }
      if(error){ setSaving(false); return showToast("❌ Save failed: "+(error.message||JSON.stringify(error)),false); }
      showToast(`✅ ${rows.length} results saved!`);
      const {data} = await supabase.from("results").select("*").order("created_at",{ascending:false});
      setResults(data||[]);
      setSaving(false);
    };

    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* Header */}
        <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:`0 2px 8px ${C2.shadow}`,
          display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontWeight:900,fontSize:16,color:C2.primary}}>📒 Grade Book</div>
            <div style={{fontSize:12,color:C2.muted,marginTop:2}}>
              Record marks for exams, tests, quizzes and homework assignments
            </div>
          </div>
          {entered.length>0&&(
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {[
                [`${entered.length}/${classStu.length}`,C2.primary,"#e8f5ee","Entered"],
                [`${avgPct}%`,avgPct>=50?"#15803d":"#dc2626",avgPct>=50?"#dcfce7":"#fef2f2","Class Avg"],
                [`${passCount}/${entered.length}`,"#0d9488","#e0f7f5","Passing"],
              ].map(([val,col,bg,lbl])=>(
                <div key={lbl} style={{background:bg,borderRadius:10,padding:"8px 14px",textAlign:"center"}}>
                  <div style={{fontWeight:900,fontSize:15,color:col}}>{val}</div>
                  <div style={{fontSize:10,color:col,fontWeight:600}}>{lbl}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
          {/* Exam type pills */}
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:8}}>ASSESSMENT TYPE</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {EXAM_TYPES.map(t=>(
                <button key={t.v} onClick={()=>{setExamType(t.v);setSelExam("");setMarks({});}}
                  style={{
                    background:examType===t.v?C2.primary:"white",
                    color:examType===t.v?"white":C2.muted,
                    border:`1.5px solid ${examType===t.v?C2.primary:C2.border}`,
                    borderRadius:20,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:"pointer",
                    transition:"all .15s",display:"flex",alignItems:"center",gap:5
                  }}>
                  {t.icon} {t.lbl}
                </button>
              ))}
            </div>
          </div>

          <div style={{display:"flex",gap:14,flexWrap:"wrap",alignItems:"flex-end"}}>
            {[
              ["CLASS",<select value={selClass} onChange={e=>{setSelClass(e.target.value);setMarks({});}}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:130}}>
                {(myClasses.length>0?myClasses:[...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort()).map(c=><option key={c} value={c}>{c}</option>)}
              </select>],
              ["SUBJECT",<select value={selSubject} onChange={e=>setSelSubject(e.target.value)}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:130}}>
                {(mySubjects.length>0?mySubjects:[...new Set(results.map(r=>r.subject).filter(Boolean))].sort()).map(s=><option key={s} value={s}>{s}</option>)}
              </select>],
              [examType==="homework"?"ASSIGNMENT":"EXAM",
                <select value={selExam} onChange={e=>setSelExam(e.target.value)}
                  style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:180}}>
                  <option value="">-- Select {examType==="homework"?"Assignment":"Exam"} --</option>
                  {myExams.map(e=><option key={e} value={e}>{e}</option>)}
                </select>],
              ["TOTAL MARKS",<input type="number" value={totalMarks} min={1}
                onChange={e=>setTotalMarks(Number(e.target.value))}
                style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 10px",fontSize:13,width:90}}/>],
            ].map(([lbl,el])=>(
              <div key={lbl}>
                <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>{lbl}</label>
                {el}
              </div>
            ))}
          </div>
        </div>

        {/* Grade sheet */}
        {selExam&&selSubject&&classStu.length>0&&(
          <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden"}}>
            {/* Letterhead strip */}
            <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,padding:"12px 20px",display:"flex",alignItems:"center",gap:12}}>
              <img src={SCHOOL_LOGO} style={{width:32,height:32,objectFit:"contain",opacity:0.9}}/>
              <div>
                <div style={{color:"white",fontWeight:800,fontSize:13}}>{S?.school_name_en||"QK Academy"} — Grade Book</div>
                <div style={{color:"rgba(255,255,255,0.7)",fontSize:11,marginTop:1}}>
                  {selClass} · {selSubject} · {EXAM_TYPES.find(t=>t.v===examType)?.lbl} · {selExam} · Out of {totalMarks}
                </div>
              </div>
            </div>

            {/* Column headers */}
            <div style={{display:"flex",alignItems:"center",padding:"8px 18px",background:"#f9fafb",
              borderBottom:`1.5px solid ${C2.border}`,gap:10}}>
              <div style={{flex:1,fontSize:11,fontWeight:700,color:C2.muted}}># STUDENT</div>
              <div style={{width:120,fontSize:11,fontWeight:700,color:C2.muted,textAlign:"center"}}>MARKS / {totalMarks}</div>
              <div style={{flex:1,minWidth:80}}>
                <div style={{height:6,background:"#e5e7eb",borderRadius:3}}/>
              </div>
              <div style={{width:70,fontSize:11,fontWeight:700,color:C2.muted,textAlign:"center"}}>GRADE</div>
              <div style={{width:160,fontSize:11,fontWeight:700,color:C2.muted}}>REMARKS</div>
            </div>

            {classStu.map((s,i)=>{
              const m = marks[s.id];
              const num = Number(m)||0;
              const pct = m!==undefined&&m!==""&&totalMarks>0 ? Math.round((num/totalMarks)*100) : null;
              const gr = pct!==null ? GRADE(pct) : null;
              return (
                <div key={s.id} style={{
                  display:"flex",alignItems:"center",gap:10,padding:"10px 18px",
                  background:i%2===0?"white":"#fafafa",
                  borderBottom:`1px solid ${C2.border}`,
                  borderLeft:`4px solid ${gr?gr.c:"#e5e7eb"}`,
                }}>
                  {/* Student */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{
                        width:28,height:28,borderRadius:"50%",
                        background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,
                        color:"white",fontWeight:800,fontSize:11,
                        display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0
                      }}>{i+1}</div>
                      <div>
                        <div style={{fontWeight:700,fontSize:13,color:C2.text}}>{s.full_name||s.name}</div>
                        <div style={{fontSize:10,color:C2.muted}}>{s.roll_no?"Roll "+s.roll_no:s.id}</div>
                      </div>
                    </div>
                  </div>

                  {/* Marks input */}
                  <div style={{width:120,display:"flex",alignItems:"center",gap:6}}>
                    <input type="number" min="0" max={totalMarks}
                      value={m??""} placeholder="—"
                      onChange={e=>setMarks(p=>({...p,[s.id]:e.target.value}))}
                      style={{
                        width:72,border:`1.5px solid ${gr?gr.c:C2.border}`,borderRadius:8,
                        padding:"7px 10px",fontSize:15,fontWeight:800,textAlign:"center",
                        background:gr?gr.bg:"white",color:gr?gr.c:C2.text,
                        outline:"none",transition:"all .15s"
                      }}/>
                    <span style={{fontSize:11,color:C2.muted,fontWeight:600}}>/{totalMarks}</span>
                  </div>

                  {/* Progress bar */}
                  <div style={{flex:1,minWidth:80}}>
                    {pct!==null&&(
                      <div style={{height:8,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                        <div style={{
                          width:`${pct}%`,height:"100%",
                          background:`linear-gradient(90deg,${gr.c},${gr.c}99)`,
                          borderRadius:4,transition:"width .3s ease"
                        }}/>
                      </div>
                    )}
                    {pct!==null&&<div style={{fontSize:10,color:gr.c,fontWeight:600,marginTop:2,textAlign:"right"}}>{pct}%</div>}
                  </div>

                  {/* Grade badge */}
                  <div style={{width:70,textAlign:"center"}}>
                    {gr&&(
                      <span style={{
                        background:gr.bg,color:gr.c,borderRadius:8,
                        padding:"4px 10px",fontWeight:900,fontSize:14,
                        display:"inline-block"
                      }}>{gr.g}</span>
                    )}
                  </div>

                  {/* Remarks */}
                  <input type="text" value={remarks[s.id]||""} placeholder="Remarks…"
                    onChange={e=>setRemarks(p=>({...p,[s.id]:e.target.value}))}
                    style={{width:160,border:`1.5px solid #f3f4f6`,borderRadius:8,
                      padding:"6px 10px",fontSize:12,color:C2.text,background:"white"}}/>
                </div>
              );
            })}

            {/* Footer */}
            <div style={{padding:"14px 20px",display:"flex",justifyContent:"space-between",
              alignItems:"center",background:"#f9fafb",borderTop:`1.5px solid ${C2.border}`}}>
              <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                <span style={{fontSize:12,color:C2.muted,fontWeight:600}}>
                  📝 {entered.length}/{classStu.length} marks entered
                </span>
                {entered.length>0&&<>
                  <span style={{fontSize:12,color:C2.primary,fontWeight:700}}>Class avg: {avgPct}%</span>
                  <span style={{fontSize:12,color:"#15803d",fontWeight:700}}>✅ {passCount} passing</span>
                  <span style={{fontSize:12,color:"#dc2626",fontWeight:700}}>❌ {entered.length-passCount} below 50%</span>
                </>}
              </div>
              <button onClick={save} disabled={saving}
                style={{background:saving?"#9ca3af":`linear-gradient(135deg,${C2.primary},#0a4a24)`,
                  color:"white",border:"none",borderRadius:10,padding:"10px 28px",
                  fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",
                  boxShadow:"0 2px 8px rgba(26,107,60,0.3)"}}>
                {saving?"💾 Saving…":"💾 Save to Grade Book"}
              </button>
            </div>
          </div>
        )}

        {!selExam&&(
          <div style={{background:"white",borderRadius:14,padding:40,textAlign:"center",
            boxShadow:`0 2px 8px ${C2.shadow}`,color:C2.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>📒</div>
            <div style={{fontWeight:700,fontSize:15,color:C2.text,marginBottom:6}}>Grade Book Ready</div>
            <div style={{fontSize:13}}>
              Select an assessment type, then choose a class, subject and {examType==="homework"?"assignment":"exam"} above to start entering marks.
            </div>
            {examType==="homework"&&hwAssign.length===0&&(
              <div style={{marginTop:12,background:"#fffbeb",borderRadius:10,padding:"10px 16px",
                fontSize:12,color:"#92400e",display:"inline-block"}}>
                ⚠️ No homework assignments found for {selClass}. Create assignments in the Homework tab first.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const TabStudents = () => {
    const [selClass, setSelClass] = React.useState(myClasses[0]||"all");
    const [search,   setSearch]   = React.useState("");
    const filtered = myStudents
      .filter(s=>selClass==="all"||(s.class||s["class"])===selClass)
      .filter(s=>!search||(s.full_name||s.name||"").toLowerCase().includes(search.toLowerCase()));
    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <select value={selClass} onChange={e=>setSelClass(e.target.value)}
            style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}>
            <option value="all">All Classes</option>
            {myClasses.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Search student..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{flex:1,border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,minWidth:160}}/>
          <span style={{fontSize:12,color:C2.muted}}>{filtered.length} students</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
          {filtered.map(s=>{
            const attRecords = attendance.filter(a=>String(a.student_id)===String(s.id));
            const present = attRecords.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length;
            const pct = attRecords.length?Math.round((present/attRecords.length)*100):null;
            return (
              <div key={s.id} style={{background:"white",borderRadius:12,padding:"14px",border:`1.5px solid ${C2.border}`,boxShadow:`0 1px 4px ${C2.shadow}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:`linear-gradient(135deg,${C2.primary},#2d9a5f)`,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:900,fontSize:15,flexShrink:0}}>
                    {(s.full_name||s.name||"?")[0]}
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:C2.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.full_name||s.name}</div>
                    <div style={{fontSize:11,color:C2.muted}}>{s.class||s["class"]} · {s.roll_no||""}</div>
                  </div>
                </div>
                {pct!==null&&(
                  <div style={{fontSize:11,color:pct>=75?"#15803d":pct>=50?"#d97706":"#dc2626",fontWeight:700,background:pct>=75?"#dcfce7":pct>=50?"#fef9c3":"#fef2f2",borderRadius:6,padding:"3px 8px",display:"inline-block"}}>
                    Attendance: {pct}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Tab: Reports ────────────────────────────────────────────────
  const TabReports = () => {
    // Derive available classes from results (not timetable) so it works even without timetable
    const resultClasses = [...new Set(results.map(r=>r.class).filter(Boolean))].sort();
    const classOptions  = resultClasses.length ? resultClasses : myClasses;

    const [selClass, setSelClass] = React.useState("");
    const [selExam,  setSelExam]  = React.useState("");
    const [editRow,  setEditRow]  = React.useState(null); // {id, marks_obtained, total_marks}
    const [editVal,  setEditVal]  = React.useState("");
    const [saving,   setSaving]   = React.useState(false);
    const [view,     setView]     = React.useState("table"); // "table" | "log"

    const allExamNames = [...new Set(results.map(r=>r.exam_name).filter(Boolean))].sort();
    // No class filter if selClass is empty — show all
    const classStu = selClass
      ? (myStudents.length ? myStudents.filter(s=>(s.class||s["class"])===selClass) : results.filter(r=>r.class===selClass).map(r=>({id:r.student_id,full_name:r.student_name,class:r.class})).filter((v,i,a)=>a.findIndex(x=>x.id===v.id)===i))
      : myStudents;

    // Filter results to teacher's classes only, then by selected exam/class
    const examRes = results.filter(r=>
      (myClasses.length===0 || myClasses.includes(r.class)) &&
      (!selExam  || r.exam_name===selExam) &&
      (!selClass || r.class===selClass)
    );

    const examSubjects = [...new Set(examRes.map(r=>r.subject).filter(Boolean))].sort();
    const GCOL = pct=>pct>=75?"#15803d":pct>=50?"#d97706":"#dc2626";

    const saveEdit = async () => {
      if(!editRow) return;
      setSaving(true);
      const mo = Number(editVal);
      const grade = GRADE(editRow.total_marks>0?Math.round(mo/editRow.total_marks*100):0).g;
      await supabase.from("results").update({marks_obtained:mo,grade}).eq("id",editRow.id);
      const {data} = await supabase.from("results").select("*").order("created_at",{ascending:false});
      setResults(data||[]);
      setEditRow(null); setEditVal(""); setSaving(false);
    };

    const SS = {border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13,background:"white"};

    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* Edit modal */}
        {editRow && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
            onClick={e=>{if(e.target===e.currentTarget){setEditRow(null);setEditVal("");}}}>
            <div style={{background:"white",borderRadius:16,padding:28,width:320,boxShadow:"0 8px 40px rgba(0,0,0,0.18)"}}>
              <div style={{fontWeight:800,fontSize:15,color:C2.primary,marginBottom:4}}>✏️ Edit Marks</div>
              <div style={{fontSize:12,color:C2.muted,marginBottom:16}}>{editRow.student_name} · {editRow.subject} · {editRow.exam_name}</div>
              <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:6}}>MARKS OBTAINED (out of {editRow.total_marks})</label>
              <input type="number" min={0} max={editRow.total_marks} value={editVal}
                onChange={e=>setEditVal(e.target.value)}
                style={{...SS,width:"100%",boxSizing:"border-box",fontSize:18,fontWeight:700,textAlign:"center",marginBottom:16}}/>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>{setEditRow(null);setEditVal("");}}
                  style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${C2.border}`,background:"white",cursor:"pointer",fontSize:13}}>Cancel</button>
                <button onClick={saveEdit} disabled={saving||editVal===""}
                  style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:C2.primary,color:"white",cursor:"pointer",fontWeight:700,fontSize:13,opacity:saving?0.7:1}}>
                  {saving?"Saving...":"Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters + view toggle */}
        <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div><label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>CLASS</label>
            <select value={selClass} onChange={e=>setSelClass(e.target.value)} style={SS}>
              <option value="">All Classes</option>
              {classOptions.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>EXAM</label>
            <select value={selExam} onChange={e=>setSelExam(e.target.value)} style={{...SS,minWidth:160}}>
              <option value="">All Exams</option>
              {allExamNames.map(e=><option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div style={{marginLeft:"auto",display:"flex",gap:6}}>
            {["table","log"].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${C2.border}`,background:view===v?C2.primary:"white",color:view===v?"white":C2.text,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                {v==="table"?"📊 Table":"📋 Log"}
              </button>
            ))}
          </div>
        </div>

        {/* ── TABLE VIEW ── */}
        {view==="table" && (
          <>
            {examRes.length===0 && (
              <div style={{background:"white",borderRadius:14,padding:40,textAlign:"center",color:C2.muted,fontSize:13,boxShadow:`0 2px 8px ${C2.shadow}`}}>
                {results.length===0 ? "No marks entered yet — use Grade Book to add marks" : "No results match the selected filters"}
              </div>
            )}
            {examRes.length>0 && examSubjects.length>0 && (
              <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"auto"}}>
                <div style={{background:C2.primary,padding:"10px 18px",display:"flex",gap:8,minWidth:400}}>
                  <div style={{minWidth:140,color:"white",fontWeight:700,fontSize:11,flexShrink:0}}>STUDENT</div>
                  {examSubjects.map(h=><div key={h} style={{flex:1,color:"white",fontWeight:700,fontSize:11,minWidth:80,textAlign:"center"}}>{h}</div>)}
                  <div style={{minWidth:64,color:"white",fontWeight:700,fontSize:11,textAlign:"center"}}>AVG %</div>
                </div>
                {(()=>{
                  // Build unique student list from examRes
                  const stuMap = {};
                  examRes.forEach(r=>{ if(!stuMap[r.student_id]) stuMap[r.student_id]={id:r.student_id,name:r.student_name}; });
                  const stuList = Object.values(stuMap).sort((a,b)=>(a.name||"").localeCompare(b.name||""));
                  return stuList.map((s,i)=>{
                    const stuRes = examRes.filter(r=>String(r.student_id)===String(s.id));
                    const total  = stuRes.reduce((sum,r)=>sum+(Number(r.marks_obtained)||0),0);
                    const max    = stuRes.reduce((sum,r)=>sum+(Number(r.total_marks)||0),0);
                    const pct    = max>0?Math.round((total/max)*100):null;
                    return (
                      <div key={s.id} style={{display:"flex",gap:8,padding:"10px 18px",background:i%2===0?"white":C2.bg,borderBottom:`1px solid ${C2.border}`,alignItems:"center",minWidth:400}}>
                        <div style={{minWidth:140,fontWeight:600,fontSize:13,flexShrink:0}}>{s.name}</div>
                        {examSubjects.map(sub=>{
                          const r=stuRes.find(x=>x.subject===sub);
                          const sp=r&&r.total_marks>0?Math.round(r.marks_obtained/r.total_marks*100):null;
                          return (
                            <div key={sub} style={{flex:1,minWidth:80,textAlign:"center"}}>
                              {r ? (
                                <span onClick={()=>{setEditRow(r);setEditVal(String(r.marks_obtained));}}
                                  style={{fontSize:13,fontWeight:700,color:GCOL(sp),cursor:"pointer",borderBottom:`1px dashed ${GCOL(sp)}`}}
                                  title="Click to edit">
                                  {r.marks_obtained}<span style={{fontWeight:400,color:C2.muted,fontSize:11}}>/{r.total_marks}</span>
                                </span>
                              ) : <span style={{color:C2.muted,fontSize:13}}>—</span>}
                            </div>
                          );
                        })}
                        <div style={{minWidth:64,textAlign:"center",fontWeight:800,fontSize:13,color:pct!==null?GCOL(pct):C2.muted}}>
                          {pct!==null?pct+"%":"—"}
                        </div>
                      </div>
                    );
                  });
                })()}
                {/* Class avg */}
                {(()=>{
                  const totals=examRes.map(r=>r.total_marks>0?Math.round(r.marks_obtained/r.total_marks*100):null).filter(v=>v!==null);
                  if(!totals.length) return null;
                  const avg=Math.round(totals.reduce((a,v)=>a+v,0)/totals.length);
                  return (
                    <div style={{display:"flex",gap:8,padding:"10px 18px",background:"#f0fdf4",borderTop:`2px solid ${C2.border}`,alignItems:"center",minWidth:400}}>
                      <div style={{minWidth:140,fontWeight:800,fontSize:13,color:C2.primary,flexShrink:0}}>Class Average</div>
                      {examSubjects.map(sub=>{
                        const sr=examRes.filter(r=>r.subject===sub);
                        const sm=sr.reduce((a,r)=>a+(Number(r.marks_obtained)||0),0);
                        const mx=sr.reduce((a,r)=>a+(Number(r.total_marks)||0),0);
                        const sp=mx>0?Math.round(sm/mx*100):null;
                        return <div key={sub} style={{flex:1,minWidth:80,textAlign:"center",fontWeight:700,fontSize:13,color:sp!==null?GCOL(sp):C2.muted}}>{sp!==null?sp+"%":"—"}</div>;
                      })}
                      <div style={{minWidth:64,textAlign:"center",fontWeight:900,fontSize:14,color:GCOL(Math.round(examRes.reduce((a,r)=>a+(r.total_marks>0?r.marks_obtained/r.total_marks*100:0),0)/examRes.length))}}>{Math.round(examRes.reduce((a,r)=>a+(r.total_marks>0?r.marks_obtained/r.total_marks*100:0),0)/examRes.length)}%</div>
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {/* ── LOG VIEW ── */}
        {view==="log" && (
          <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden"}}>
            <div style={{background:C2.primary,padding:"10px 18px",display:"flex",gap:8}}>
              {["STUDENT","CLASS","EXAM","SUBJECT","MARKS","GRADE",""].map(h=>(
                <div key={h} style={{flex:h===""?"0 0 60px":1,color:"white",fontWeight:700,fontSize:11,minWidth:h===""?60:80}}>{h}</div>
              ))}
            </div>
            {examRes.length===0 && (
              <div style={{padding:"30px",textAlign:"center",color:C2.muted,fontSize:13}}>No records match filters</div>
            )}
            {[...examRes].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map((r,i)=>{
              const pct=r.total_marks>0?Math.round(r.marks_obtained/r.total_marks*100):null;
              return (
                <div key={r.id||i} style={{display:"flex",gap:8,padding:"10px 18px",background:i%2===0?"white":C2.bg,borderBottom:`1px solid ${C2.border}`,alignItems:"center"}}>
                  <div style={{flex:1,fontWeight:600,fontSize:13,minWidth:80}}>{r.student_name}</div>
                  <div style={{flex:1,fontSize:12,color:C2.muted,minWidth:80}}>{r.class}</div>
                  <div style={{flex:1,fontSize:12,minWidth:80}}>{r.exam_name}</div>
                  <div style={{flex:1,fontSize:12,minWidth:80}}>{r.subject}</div>
                  <div style={{flex:1,fontWeight:700,fontSize:13,color:pct!==null?GCOL(pct):C2.muted,minWidth:80}}>{r.marks_obtained}/{r.total_marks}</div>
                  <div style={{flex:1,fontWeight:800,fontSize:13,color:pct!==null?GCOL(pct):C2.muted,minWidth:80}}>{r.grade||"—"}</div>
                  <div style={{flex:"0 0 60px"}}>
                    <button onClick={()=>{setEditRow(r);setEditVal(String(r.marks_obtained));}}
                      style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${C2.border}`,background:"white",cursor:"pointer",fontSize:11,fontWeight:700,color:C2.primary}}>
                      ✏️ Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    );
  };

  // ── Tab: Cover Duty ─────────────────────────────────────────────
  const TabCoverDuty = () => {
    const [attModal,  setAttModal]  = React.useState(null); // duty object currently marking attendance for
    const [marks,     setMarks]     = React.useState({});
    const [saving,    setSaving]    = React.useState(false);
    const [subTab,    setSubTab]    = React.useState("today");

    const STATUSES = [
      {v:"P", lbl:"Present", icon:"✅", col:"#15803d", bg:"#dcfce7", bd:"#86efac"},
      {v:"A", lbl:"Absent",  icon:"❌", col:"#dc2626", bg:"#fef2f2", bd:"#fca5a5"},
      {v:"L", lbl:"Late",    icon:"🕐", col:"#d97706", bg:"#fffbeb", bd:"#fde68a"},
      {v:"LV",lbl:"Leave",   icon:"📋", col:"#7c3aed", bg:"#f0ebff", bd:"#c4b5fd"},
    ];

    const openAttendance = (duty) => {
      const classStu = students.filter(s=>(s.class||s["class"])===duty.class_name);
      const init={};
      classStu.forEach(s=>{
        const ex = attendance.find(a=>a.date===duty.date&&String(a.student_id)===String(s.id));
        init[s.id] = ex?.status || "P";
      });
      setMarks(init);
      setAttModal(duty);
    };

    const bulkMark = (status) => {
      const cls = students.filter(s=>(s.class||s["class"])===attModal?.class_name);
      const all={}; cls.forEach(s=>{all[s.id]=status;}); setMarks(all);
    };

    const saveAttendance = async () => {
      if(!attModal) return;
      setSaving(true);
      const classStu = students.filter(s=>(s.class||s["class"])===attModal.class_name);
      const rows = classStu.map(s=>{
        const st=marks[s.id]||"P";
        return {
          student_id:String(s.id), student_name:s.full_name||s.name,
          class:attModal.class_name, date:attModal.date, status:st,
          statuscode:st,
          statuslabel:st==="P"?"Present":st==="A"?"Absent":st==="L"?"Late":"Leave",
          note:`Cover class — teacher: ${teacher.name}`,
        };
      });
      const {error} = await supabase.from("attendance").upsert(rows,{onConflict:"date,student_id"});
      if(error){ showToast("Error: "+error.message,false); setSaving(false); return; }
      // Update local attendance state
      setAttendance(prev=>{
        const filtered=prev.filter(a=>!(a.date===attModal.date&&classStu.find(s=>String(s.id)===String(a.student_id))));
        return [...filtered,...rows];
      });
      // Auto-mark cover duty as done
      await updateCoverStatus(attModal.id,"done");
      showToast("✅ Attendance saved & cover duty marked as Done!");
      setAttModal(null);
      setSaving(false);
    };

    const past = coverDuties.filter(d=>["done","not_done"].includes(d.status));

    const DutyRow = ({d}) => {
      const isPast   = ["done","not_done"].includes(d.status);
      const isToday  = d.date === todayStr;
      const attDone  = isToday && attendance.some(a=>a.date===d.date&&(students.filter(s=>(s.class||s["class"])===d.class_name)).some(s=>String(s.id)===String(a.student_id)));
      return (
        <div style={{background:"white",borderRadius:12,padding:"13px 16px",border:`1px solid ${d.status==="done"?"#bbf7d0":d.status==="not_done"?"#fca5a5":d.status==="acknowledged"?"#bfdbfe":"#fde68a"}`,borderLeft:`4px solid ${d.status==="done"?"#16a34a":d.status==="not_done"?"#dc2626":d.status==="acknowledged"?"#2563eb":"#d97706"}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:160}}>
              <div style={{fontWeight:800,fontSize:13,color:"#111827",marginBottom:3}}>
                {d.class_name||"?"} · {d.subject||"?"}
              </div>
              <div style={{fontSize:11,color:"#6b7280"}}>
                {new Date(d.date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}
                {d.period_time&&<> · {d.period_time}</>}
                {" · "}For <strong>{d.absent_staff_name}</strong>
              </div>
              {attDone&&<div style={{fontSize:11,color:"#16a34a",fontWeight:700,marginTop:3}}>✅ Attendance already marked</div>}
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",alignItems:"center"}}>
              {d.status==="assigned"&&(
                <button onClick={()=>updateCoverStatus(d.id,"acknowledged")}
                  style={{background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  👁 Acknowledge
                </button>
              )}
              {(d.status==="assigned"||d.status==="acknowledged")&&!attDone&&(
                <button onClick={()=>openAttendance(d)}
                  style={{background:"#1a6b3c",color:"white",border:"none",borderRadius:7,padding:"5px 13px",fontSize:11,fontWeight:800,cursor:"pointer"}}>
                  📝 Take Attendance
                </button>
              )}
              {d.status==="done"&&<span style={{fontSize:12,fontWeight:700,color:"#16a34a"}}>✅ Done</span>}
              {d.status==="not_done"&&<span style={{fontSize:12,fontWeight:700,color:"#dc2626"}}>❌ Not Done</span>}
            </div>
          </div>
        </div>
      );
    };

    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* Header */}
        <div style={{background:"white",borderRadius:14,padding:"16px 20px",boxShadow:"0 2px 8px rgba(0,0,0,.06)",border:"1px solid #d1fae5"}}>
          <div style={{fontWeight:900,fontSize:16,color:"#1a6b3c",marginBottom:4}}>🔄 My Cover Duties</div>
          <div style={{fontSize:12,color:"#6b7280",marginBottom:12}}>Classes you are covering for absent colleagues</div>
          <div style={{display:"flex",gap:8}}>
            {[["today","Today",todayCoverDuties.length],["history","History",past.length]].map(([id,label,cnt])=>(
              <button key={id} onClick={()=>setSubTab(id)}
                style={{background:subTab===id?"#1a6b3c":"white",color:subTab===id?"white":"#111827",border:`1px solid ${subTab===id?"#1a6b3c":"#d1fae5"}`,borderRadius:8,padding:"6px 16px",fontWeight:700,fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                {label}
                {cnt>0&&<span style={{background:subTab===id?"rgba(255,255,255,.25)":"#e8f5ee",color:subTab===id?"white":"#1a6b3c",borderRadius:20,padding:"0 7px",fontSize:11,fontWeight:800}}>{cnt}</span>}
              </button>
            ))}
          </div>
        </div>

        {subTab==="today"&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {todayCoverDuties.length===0&&(
              <div style={{background:"white",borderRadius:12,padding:"28px",textAlign:"center",color:"#6b7280",fontSize:13,border:"1px solid #d1fae5"}}>
                🎉 No cover duties assigned for today.
              </div>
            )}
            {todayCoverDuties.map(d=><DutyRow key={d.id} d={d}/>)}
          </div>
        )}

        {subTab==="history"&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {past.length===0&&<div style={{color:"#6b7280",fontSize:13,textAlign:"center",padding:20}}>No past cover duties yet.</div>}
            {past.map(d=><DutyRow key={d.id} d={d}/>)}
          </div>
        )}

        {/* Quick Attendance Modal */}
        {attModal&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:"20px"}}>
            <div style={{background:"white",borderRadius:18,width:"100%",maxWidth:520,boxShadow:"0 20px 60px rgba(0,0,0,.25)",overflow:"hidden"}}>
              {/* Modal header */}
              <div style={{background:"linear-gradient(135deg,#1a6b3c,#0d5c2f)",padding:"18px 22px",color:"white"}}>
                <div style={{fontWeight:900,fontSize:16,marginBottom:4}}>📝 Mark Attendance</div>
                <div style={{fontSize:12,opacity:.85}}>
                  {attModal.class_name} · {attModal.subject} · {new Date(attModal.date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"})}
                </div>
                <div style={{fontSize:11,opacity:.7,marginTop:2}}>Cover for {attModal.absent_staff_name}</div>
              </div>
              {/* Bulk actions */}
              <div style={{padding:"12px 20px",borderBottom:"1px solid #f3f4f6",display:"flex",gap:7,flexWrap:"wrap"}}>
                {STATUSES.map(st=>(
                  <button key={st.v} onClick={()=>bulkMark(st.v)}
                    style={{background:st.bg,color:st.col,border:`1px solid ${st.bd}`,borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                    {st.icon} All {st.lbl}
                  </button>
                ))}
              </div>
              {/* Student list */}
              <div style={{maxHeight:"42vh",overflowY:"auto",padding:"12px 20px",display:"flex",flexDirection:"column",gap:8}}>
                {students.filter(s=>(s.class||s["class"])===attModal.class_name).map(s=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,background:"#f9fafb"}}>
                    <div style={{width:28,height:28,borderRadius:7,background:"#e8f5ee",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:12,color:"#1a6b3c",flexShrink:0}}>
                      {(s.full_name||s.name||"?")[0]}
                    </div>
                    <div style={{flex:1,fontSize:12,fontWeight:600,color:"#111827"}}>{s.full_name||s.name}</div>
                    <div style={{display:"flex",gap:4}}>
                      {STATUSES.map(st=>(
                        <button key={st.v} onClick={()=>setMarks(p=>({...p,[s.id]:st.v}))}
                          style={{background:marks[s.id]===st.v?st.bg:"#f3f4f6",color:marks[s.id]===st.v?st.col:"#9ca3af",border:`1.5px solid ${marks[s.id]===st.v?st.bd:"#e5e7eb"}`,borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:700,cursor:"pointer",transition:"all .1s"}}>
                          {st.icon}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {students.filter(s=>(s.class||s["class"])===attModal.class_name).length===0&&(
                  <div style={{color:"#6b7280",fontSize:13,textAlign:"center",padding:20}}>No students found for {attModal.class_name}.</div>
                )}
              </div>
              {/* Footer */}
              <div style={{padding:"14px 20px",borderTop:"1px solid #f3f4f6",display:"flex",gap:8,justifyContent:"flex-end",background:"#fafafa"}}>
                <button onClick={()=>setAttModal(null)} style={{background:"white",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:9,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  Cancel
                </button>
                <button onClick={saveAttendance} disabled={saving}
                  style={{background:saving?"#9ca3af":"#1a6b3c",color:"white",border:"none",borderRadius:9,padding:"9px 22px",fontWeight:800,fontSize:13,cursor:saving?"not-allowed":"pointer",boxShadow:"0 4px 14px rgba(26,107,60,.35)"}}>
                  {saving?"💾 Saving…":"💾 Save & Mark Done"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const TAB_SECTIONS = [
    { title:"Overview", tabs:[
      {id:"dashboard",    label:"Dashboard",   icon:"🏠"},
    ]},
    { title:"Teaching", tabs:[
      {id:"attendance",   label:"Attendance",  icon:"✅"},
      {id:"leave",        label:"Student Leave",icon:"📝"},
      {id:"results",      label:"Grade Book",  icon:"📒"},
      {id:"courseplan",   label:"Course Plan", icon:"📘"},
      {id:"homework",     label:"Homework",    icon:"📚"},
    ]},
    { title:"Duties & Leave", tabs:[
      {id:"coverduty",    label:"Cover Duty",  icon:"🔄", badge: todayCoverDuties.length||null},
      {id:"myleave",      label:"My Leave",    icon:"🏖️"},
      {id:"responsibilities", label:"My Duties", icon:"🗂️"},
      ...(isLibrarian ? [{id:"library", label:"Library", icon:"📚"}] : []),
    ]},
    { title:"Students", tabs:[
      {id:"students",     label:"My Students", icon:"👥"},
    ]},
    { title:"Insights & Reports", tabs:[
      {id:"performance",  label:"Performance", icon:"📈"},
      {id:"reports",      label:"Reports",     icon:"📊"},
    ]},
    { title:"Planning", tabs:[
      {id:"events",       label:"Events",      icon:"📅"},
    ]},
  ];
  const TABS = TAB_SECTIONS.flatMap(s=>s.tabs);

  const TabEvents = () => {
    const [events, setEvents] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [showForm, setShowForm] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [form, setForm] = React.useState({title:"",event_type:"assignment",date:"",class:myClasses[0]||"all",description:"",visible_to:"all"});
    const EVENT_ICONS = {exam:"📝",holiday:"🏖️",function:"🎓",meeting:"👥",assignment:"📋",milestone:"🎯",general:"📅"};
    const EVENT_COLORS = {exam:"#dc2626",holiday:"#d97706",function:"#7c3aed",meeting:"#2563eb",assignment:"#0d9488",milestone:"#1a6b3c",general:"#374151"};

    React.useEffect(()=>{
      supabase.from("school_events").select("*")
        .gte("date", new Date(Date.now()-7*86400000).toISOString().split("T")[0])
        .order("date",{ascending:true})
        .then(({data})=>{ setEvents(data||[]); setLoading(false); });
    },[]);

    const save = async () => {
      if(!form.title||!form.date) return;
      setSaving(true);
      const {data,error} = await supabase.from("school_events").insert({
        ...form, created_by:teacher.name, created_by_role:"teacher", color:EVENT_COLORS[form.event_type]||"#374151"
      }).select().single();
      if(!error) {
        await supabase.from("notifications").insert({
          title:`${EVENT_ICONS[form.event_type]||"📅"} ${form.title}`,
          body:`${form.date}${form.description?" — "+form.description.slice(0,80):""}`,
          type:"event", target_role:"all",
          target_class:form.class!=="all"?form.class:null,
          source_id:data.id, source_type:"school_events", created_by:teacher.name
        });
        setEvents(p=>[...p,data].sort((a,b)=>a.date.localeCompare(b.date)));
        setShowForm(false);
      }
      setSaving(false);
    };

    return (
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div style={{fontWeight:800,fontSize:15,color:C2.text}}>📅 Events & Deadlines</div>
          <button onClick={()=>setShowForm(true)} style={{background:C2.primary,color:"white",border:"none",borderRadius:9,padding:"8px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>+ Add Event</button>
        </div>
        {showForm&&(
          <div style={{background:"white",borderRadius:14,padding:20,border:`1.5px solid ${C2.border}`,boxShadow:`0 4px 16px ${C2.shadow}`}}>
            <div style={{fontWeight:700,fontSize:14,color:C2.text,marginBottom:14}}>New Event / Deadline</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>TITLE *</label>
                <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Maths Assignment Due"
                  style={{width:"100%",padding:"9px 12px",border:`1.5px solid ${C2.border}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              </div>
              {[["TYPE","event_type","select"],["CLASS","class","select"],["DATE *","date","date"]].map(([lbl,k,t])=>(
                <div key={k}>
                  <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>{lbl}</label>
                  {t==="select"?(<select value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}
                    style={{width:"100%",padding:"9px 12px",border:`1.5px solid ${C2.border}`,borderRadius:8,fontSize:13}}>
                    {k==="event_type"
                      ?[["assignment","📋 Assignment"],["milestone","🎯 Milestone"],["general","📅 General"]].map(([v,l])=><option key={v} value={v}>{l}</option>)
                      :[["all","All My Classes"],...myClasses.map(c=>[c,c])].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>):(
                  <input type={t} value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))}
                    style={{width:"100%",padding:"9px 12px",border:`1.5px solid ${C2.border}`,borderRadius:8,fontSize:13}}/>
                  )}
                </div>
              ))}
              <div style={{gridColumn:"1/-1"}}>
                <label style={{fontSize:11,fontWeight:700,color:C2.muted,display:"block",marginBottom:4}}>DESCRIPTION</label>
                <input value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Details..."
                  style={{width:"100%",padding:"9px 12px",border:`1.5px solid ${C2.border}`,borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{fontSize:11,color:C2.muted,marginTop:10,background:"#eff6ff",borderRadius:7,padding:"8px 12px"}}>
              ℹ️ This event will appear in your calendar and notify relevant parents/students automatically.
            </div>
            <div style={{display:"flex",gap:8,marginTop:12}}>
              <button onClick={save} disabled={saving} style={{background:saving?"#9ca3af":C2.primary,color:"white",border:"none",borderRadius:8,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>{saving?"Saving...":"Save"}</button>
              <button onClick={()=>setShowForm(false)} style={{background:"#f3f4f6",color:"#374151",border:"1px solid #d1d5db",borderRadius:8,padding:"9px 20px",fontWeight:600,fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        )}
        {loading?<div style={{textAlign:"center",padding:32,color:C2.muted}}>Loading...</div>:(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {events.filter(e=>myClasses.length===0||e.class==="all"||myClasses.includes(e.class)||e.created_by===teacher.name).map(ev=>{
              const col=EVENT_COLORS[ev.event_type]||"#374151";
              const icon=EVENT_ICONS[ev.event_type]||"📅";
              const isPast=new Date(ev.date)<new Date();
              const isMine=ev.created_by===teacher.name;
              return (
                <div key={ev.id} style={{display:"flex",gap:12,padding:"12px 16px",background:isPast?"#fafafa":"white",borderRadius:10,border:`1px solid ${isPast?"#f3f4f6":col+"30"}`,borderLeft:`4px solid ${isPast?"#d1d5db":col}`,opacity:isPast?0.75:1}}>
                  <div style={{width:36,height:36,borderRadius:8,background:`${col}15`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{ev.title}</div>
                    <div style={{fontSize:11,color:"#6b7280",marginTop:2,display:"flex",gap:8,flexWrap:"wrap"}}>
                      <span>{new Date(ev.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}</span>
                      {ev.class&&ev.class!=="all"&&<span style={{background:"#f3f4f6",borderRadius:4,padding:"1px 6px"}}>{ev.class}</span>}
                      <span style={{background:isMine?"#dcfce7":"#eff6ff",color:isMine?"#15803d":"#1d4ed8",borderRadius:4,padding:"1px 6px",fontWeight:700}}>{isMine?"You":"Admin"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {events.length===0&&<div style={{textAlign:"center",padding:32,color:C2.muted,background:"white",borderRadius:12,border:"1px dashed #e5e7eb"}}>No events yet. Add one above.</div>}
          </div>
        )}
      </div>
    );
  };

  // ── Tab: Performance Dashboard ─────────────────────────────────
  const TabPerformance = () => {
    const [selClass,   setSelClass]   = React.useState(myClasses[0]||"");
    const [selStudent, setSelStudent] = React.useState(null);
    const [aiReport,   setAiReport]   = React.useState("");
    const [aiLoading,  setAiLoading]  = React.useState(false);
    const [view,       setView]       = React.useState("class"); // "class" | "student"
    const [vizMode,     setVizMode]     = React.useState("radar"); // radar | heatmap | bubble

    const classStu = myStudents.filter(s=>(s.class||s["class"])===selClass);
    const myExams  = [...new Set(results.map(r=>r.exam_name).filter(Boolean))].sort();

    // ── Helpers ──
    const stuResults  = (sid) => results.filter(r=>String(r.student_id)===String(sid)&&(mySubjects.includes(r.subject)||mySubjects.length===0));
    const stuAtt      = (sid) => attendance.filter(a=>String(a.student_id)===String(sid));
    const attPct      = (sid) => { const r=stuAtt(sid); if(!r.length) return null; return Math.round(r.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length/r.length*100); };
    const avgScore    = (sid) => { const r=stuResults(sid); if(!r.length) return null; return Math.round(r.reduce((s,x)=>s+(x.marks_obtained/x.total_marks)*100,0)/r.length); };
    const gradeColor  = (p) => p>=85?"#16a34a":p>=70?"#0891b2":p>=50?"#d97706":"#dc2626";
    const gradeRing   = (p) => p>=85?C2.primary:p>=70?"#0891b2":p>=55?"#d97706":"#dc2626";
    const gradeBg     = (p) => p>=85?"#dcfce7":p>=70?"#dbeafe":p>=50?"#fef9c3":"#fef2f2";
    const gradeLabel  = (p) => p>=85?"Excellent":p>=70?"Good":p>=50?"Average":"Needs Help";

    // Class overview: each student's avg per subject + attendance
    const classRows = classStu.map(s=>{
      const subScores = mySubjects.map(sub=>{
        const subRes = stuResults(s.id).filter(r=>r.subject===sub);
        if(!subRes.length) return {sub,pct:null};
        return {sub, pct:Math.round(subRes.reduce((t,r)=>t+(r.marks_obtained/r.total_marks)*100,0)/subRes.length)};
      });
      return {s, subScores, avg:avgScore(s.id), att:attPct(s.id)};
    }).sort((a,b)=>(b.avg||0)-(a.avg||0));

    // Class-level subject averages for heatmap
    const classSubjectAvg = mySubjects.map(sub=>{
      const allScores = classRows.map(row=>{const sc=row.subScores.find(x=>x.sub===sub);return sc?.pct;}).filter(v=>v!=null);
      return {sub, avg:allScores.length?Math.round(allScores.reduce((a,b)=>a+b,0)/allScores.length):null};
    });

    // Bubble chart data: each student = bubble (x=attendance, y=avg, size=rank)
    const bubbleData = classRows.filter(r=>r.avg!=null&&r.att!=null).map((r,i)=>({
      name:(r.s.full_name||r.s.name||"?").split(" ")[0],
      x: r.att, y: r.avg, rank: classRows.length-i,
      color: gradeRing(r.avg), sid: r.s.id,
    }));

    // For selected student: exam trend data
    const examTrend = selStudent ? myExams.map(ex=>{
      const r = stuResults(selStudent.id).filter(x=>x.exam_name===ex);
      if(!r.length) return null;
      const pct = Math.round(r.reduce((s,x)=>s+(x.marks_obtained/x.total_marks)*100,0)/r.length);
      return {exam:ex.length>10?ex.slice(0,10)+"…":ex, pct};
    }).filter(Boolean) : [];

    // Per-subject breakdown for selected student
    const subBreakdown = selStudent ? mySubjects.map(sub=>{
      const r = stuResults(selStudent.id).filter(x=>x.subject===sub);
      if(!r.length) return null;
      const pct = Math.round(r.reduce((s,x)=>s+(x.marks_obtained/x.total_marks)*100,0)/r.length);
      const latest = r[0];
      return {sub, pct, latest};
    }).filter(Boolean) : [];

    // Strengths/weaknesses
    const strengths   = subBreakdown.filter(x=>x.pct>=75).map(x=>x.sub);
    const weaknesses  = subBreakdown.filter(x=>x.pct<60).map(x=>x.sub);
    const attVal      = selStudent ? attPct(selStudent.id) : null;
    const avgVal      = selStudent ? avgScore(selStudent.id) : null;

    // AI report generator
    const generateAI = async () => {
      if(!selStudent) return;
      setAiLoading(true); setAiReport("");
      const classAvg = classRows.filter(r=>r.avg!=null).length ? Math.round(classRows.filter(r=>r.avg!=null).reduce((s,r)=>s+r.avg,0)/classRows.filter(r=>r.avg!=null).length) : null;
      const classRank = classRows.findIndex(r=>String(r.s.id)===String(selStudent.id))+1;
      const totalInClass = classRows.length;
      const prompt = `You are a strict, experienced academic advisor at QK Academy (Islamic school). Your job is to give a brutally honest, actionable performance report. No sugar-coating. Be clinical and precise.

STUDENT DATA:
Name: ${selStudent.full_name||selStudent.name}
Class: ${selStudent.class||selStudent["class"]} | Class Rank: ${classRank}/${totalInClass}
Overall Average: ${avgVal!=null?avgVal+"%":"No exam data"}
Class Average: ${classAvg!=null?classAvg+"%":"N/A"}
${classAvg!=null&&avgVal!=null?`Performance vs Class: ${avgVal>classAvg?"Above average by "+(avgVal-classAvg)+"%":avgVal<classAvg?"Below average by "+(classAvg-avgVal)+"%":"Exactly at class average"}`:""} 
Attendance: ${attVal!=null?attVal+"%":"No attendance data"}
Subject Scores: ${subBreakdown.map(x=>x.sub+": "+x.pct+"%").join(" | ")||"No subject data"}
Strong (≥75%): ${strengths.join(", ")||"None"}
Weak (<60%): ${weaknesses.join(", ")||"None"}
Average (60-74%): ${subBreakdown.filter(x=>x.pct>=60&&x.pct<75).map(x=>x.sub).join(", ")||"None"}

Write a structured report. Be direct. Use plain text, no markdown symbols.

1. VERDICT (1 sentence: overall honest assessment of this student's situation right now)

2. CRITICAL FLAGS (list only real problems — low scores, attendance gaps, dangerous trends. Skip if none)

3. SUBJECT ANALYSIS (for each subject: state score, whether it's a problem or asset, and WHY it matters)

4. ATTENDANCE IMPACT (if attendance is below 85%, state the exact academic risk this creates)

5. TEACHER ACTION PLAN (3 specific, concrete steps the teacher must take this week — not generic advice)

6. PARENT COMMUNICATION (one paragraph: what to tell the parents at the next meeting, honest and specific)

7. 30-DAY FORECAST (if current trends continue unchanged, what happens? Be realistic.)

Do not repeat data. Do not be vague. Every sentence must be actionable or diagnostic.`;

      try {
        const text = await groqGenerate(prompt, 1000);
        setAiReport(text||"Could not generate report.");
      } catch(e) { setAiReport("Error generating report: "+e.message); }
      setAiLoading(false);
    };

    const RADAR_COLS = ["#1a6b3c","#2563eb","#d97706","#7c3aed","#dc2626","#0d9488","#ea580c"];

    const RingGauge = ({value, label, color, size=80}) => {
      const r=30, circ=2*Math.PI*r;
      const pct = value!=null ? Math.min(100,Math.max(0,value)) : 0;
      return (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          <svg width={size} height={size} viewBox="0 0 70 70">
            <circle cx="35" cy="35" r={r} fill="none" stroke="#f3f4f6" strokeWidth="8"/>
            <circle cx="35" cy="35" r={r} fill="none" stroke={value!=null?color:"#e5e7eb"} strokeWidth="8"
              strokeDasharray={circ} strokeDashoffset={circ*(1-pct/100)}
              strokeLinecap="round" transform="rotate(-90 35 35)" style={{transition:"stroke-dashoffset .6s ease"}}/>
            <text x="35" y="38" textAnchor="middle" fontSize="14" fontWeight="900" fill={value!=null?color:"#9ca3af"}>
              {value!=null?value+"%":"—"}
            </text>
          </svg>
          <div style={{fontSize:10,color:"#6b7280",fontWeight:600,textAlign:"center"}}>{label}</div>
        </div>
      );
    };
    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* Header controls */}
        <div style={{background:"white",borderRadius:14,padding:"14px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{fontWeight:800,fontSize:15,color:C2.text,flex:1}}>📈 Student Performance Analytics</div>
          <select value={selClass} onChange={e=>{setSelClass(e.target.value);setSelStudent(null);setAiReport("");setView("class");}}
            style={{border:`1.5px solid ${C2.border}`,borderRadius:8,padding:"8px 12px",fontSize:13}}>
            {(myClasses.length?myClasses:[...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort()).map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {view==="class"&&(
            <div style={{display:"flex",gap:4,background:"#f3f4f6",borderRadius:8,padding:3}}>
              {[["radar","🕸 Radar"],["heatmap","🔥 Heat Map"],["bubble","💬 Scatter"]].map(([m,l])=>(
                <button key={m} onClick={()=>setVizMode(m)}
                  style={{background:vizMode===m?"white":"transparent",border:"none",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:700,color:vizMode===m?C2.primary:"#6b7280",cursor:"pointer",transition:"all .15s"}}>
                  {l}
                </button>
              ))}
            </div>
          )}
          {selStudent&&(
            <button onClick={()=>{setSelStudent(null);setAiReport("");setView("class");}}
              style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
              ← Back to Class
            </button>
          )}
        </div>

        {/* CLASS ANALYTICS — Charts above the table */}
          {vizMode==="heatmap"&&classRows.length>0&&mySubjects.length>0&&(
            <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,overflowX:"auto"}}>
              <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:14}}>🔥 Class Performance Heat Map — {selClass}</div>
              <div style={{fontSize:11,color:C2.muted,marginBottom:12}}>Each cell = student × subject. Darker green = better score.</div>
              <table style={{borderCollapse:"collapse",minWidth:"100%",fontSize:11}}>
                <thead>
                  <tr>
                    <th style={{padding:"6px 12px",textAlign:"left",fontWeight:700,color:C2.muted,borderBottom:"2px solid #f3f4f6",minWidth:120}}>Student</th>
                    {mySubjects.map(s=><th key={s} style={{padding:"6px 10px",textAlign:"center",fontWeight:700,color:C2.muted,borderBottom:"2px solid #f3f4f6",minWidth:70}}>{s}</th>)}
                    <th style={{padding:"6px 10px",textAlign:"center",fontWeight:700,color:C2.muted,borderBottom:"2px solid #f3f4f6"}}>Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {classRows.map(({s,subScores,avg},i)=>(
                    <tr key={s.id} style={{cursor:"pointer"}} onClick={()=>{setSelStudent(s);setView("student");setAiReport("");}}>
                      <td style={{padding:"6px 12px",fontWeight:700,color:C2.text,borderBottom:"1px solid #f9fafb",background:i%2===0?"white":"#fafafa"}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{width:20,height:20,borderRadius:"50%",background:i===0?"#f0a500":i===1?"#9ca3af":i===2?"#cd7c2f":"#f3f4f6",color:i<3?"white":"#9ca3af",fontSize:9,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>{i+1}</div>
                          {(s.full_name||s.name||"?").split(" ")[0]}
                        </div>
                      </td>
                      {mySubjects.map(sub=>{
                        const sc=subScores.find(x=>x.sub===sub);
                        const p=sc?.pct;
                        const intensity = p!=null ? Math.round(p*1.5+10) : 0;
                        const bg = p==null?"#f9fafb":p>=85?"#166534":p>=70?"#15803d":p>=55?"#4ade80":p>=40?"#fde68a":"#fca5a5";
                        const fg = p==null?"#d1d5db":p>=70?"white":p>=40?"#92400e":"#991b1b";
                        return (
                          <td key={sub} style={{padding:"6px 10px",textAlign:"center",background:bg,borderBottom:"1px solid rgba(255,255,255,0.3)",transition:"opacity .15s"}}>
                            <span style={{fontWeight:700,fontSize:11,color:fg}}>{p!=null?p+"%":"—"}</span>
                          </td>
                        );
                      })}
                      <td style={{padding:"6px 10px",textAlign:"center",background:avg!=null?gradeRing(avg)+"20":"#f9fafb",borderBottom:"1px solid #f3f4f6"}}>
                        <span style={{fontWeight:900,fontSize:12,color:avg!=null?gradeRing(avg):"#9ca3af"}}>{avg!=null?avg+"%":"—"}</span>
                      </td>
                    </tr>
                  ))}
                  {/* Subject averages row */}
                  <tr style={{background:C2.primary+"08"}}>
                    <td style={{padding:"7px 12px",fontWeight:800,color:C2.primary,fontSize:11}}>Class Avg</td>
                    {classSubjectAvg.map(({sub,avg})=>(
                      <td key={sub} style={{padding:"7px 10px",textAlign:"center"}}>
                        <span style={{fontWeight:800,color:avg!=null?gradeRing(avg):"#9ca3af",fontSize:11}}>{avg!=null?avg+"%":"—"}</span>
                      </td>
                    ))}
                    <td style={{padding:"7px 10px",textAlign:"center"}}>
                      <span style={{fontWeight:900,color:C2.primary,fontSize:12}}>
                        {classRows.filter(r=>r.avg!=null).length?Math.round(classRows.filter(r=>r.avg!=null).reduce((s,r)=>s+r.avg,0)/classRows.filter(r=>r.avg!=null).length)+"%":"—"}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {vizMode==="bubble"&&bubbleData.length>0&&(
            <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:4}}>💬 Attendance vs. Academic Performance</div>
              <div style={{fontSize:11,color:C2.muted,marginBottom:12}}>Each bubble = one student. X = Attendance %, Y = Academic Avg. Click to view details.</div>
              <div style={{position:"relative",width:"100%",height:260,background:"#fafafa",borderRadius:10,overflow:"hidden"}}>
                {/* Quadrant lines */}
                <div style={{position:"absolute",left:"50%",top:0,bottom:0,width:1,background:"#e5e7eb"}}/>
                <div style={{position:"absolute",top:"50%",left:0,right:0,height:1,background:"#e5e7eb"}}/>
                {/* Quadrant labels */}
                <div style={{position:"absolute",top:8,right:8,fontSize:9,color:"#16a34a",fontWeight:700,background:"#f0fdf4",borderRadius:4,padding:"2px 6px"}}>High Att + High Score ✓</div>
                <div style={{position:"absolute",top:8,left:8,fontSize:9,color:"#d97706",fontWeight:700,background:"#fef9c3",borderRadius:4,padding:"2px 6px"}}>Low Att, High Score</div>
                <div style={{position:"absolute",bottom:8,right:8,fontSize:9,color:"#0891b2",fontWeight:700,background:"#dbeafe",borderRadius:4,padding:"2px 6px"}}>High Att, Low Score</div>
                <div style={{position:"absolute",bottom:8,left:8,fontSize:9,color:"#dc2626",fontWeight:700,background:"#fef2f2",borderRadius:4,padding:"2px 6px"}}>⚠️ At Risk Zone</div>
                {/* Student bubbles */}
                {bubbleData.map(b=>{
                  const left = `${Math.max(5,Math.min(90,(b.x/100)*90))}%`;
                  const bottom = `${Math.max(5,Math.min(90,(b.y/100)*85+5))}%`;
                  return (
                    <div key={b.sid} onClick={()=>{const row=classRows.find(r=>String(r.s.id)===String(b.sid));if(row){setSelStudent(row.s);setView("student");setAiReport("");}}}
                      style={{position:"absolute",left,bottom,transform:"translate(-50%,50%)",
                        width:36,height:36,borderRadius:"50%",background:b.color+"22",border:`2px solid ${b.color}`,
                        display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",
                        transition:"transform .15s",zIndex:10}}
                      title={`${b.name} — Att: ${b.x}%, Avg: ${b.y}%`}
                      onMouseEnter={e=>e.currentTarget.style.transform="translate(-50%,50%) scale(1.3)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="translate(-50%,50%)"}>
                      <div style={{fontSize:8,fontWeight:900,color:b.color,textAlign:"center",lineHeight:1.1}}>
                        <div>{b.name.slice(0,4)}</div>
                        <div>{b.y}%</div>
                      </div>
                    </div>
                  );
                })}
                {/* Axis labels */}
                <div style={{position:"absolute",bottom:2,left:"50%",transform:"translateX(-50%)",fontSize:9,color:"#9ca3af"}}>Attendance % →</div>
                <div style={{position:"absolute",left:2,top:"50%",transform:"translateY(-50%) rotate(-90deg)",fontSize:9,color:"#9ca3af"}}>Academic Avg %</div>
              </div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:2}}>
            {/* Score distribution bar chart */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontWeight:800,fontSize:12,color:C2.text,marginBottom:10}}>📊 Score Distribution</div>
              {(()=>{
                const buckets=[{l:"A+ (90+)",min:90,max:101,col:"#16a34a"},{l:"A (80-89)",min:80,max:90,col:"#22c55e"},{l:"B (65-79)",min:65,max:80,col:"#2563eb"},{l:"C (50-64)",min:50,max:65,col:"#d97706"},{l:"F (<50)",min:0,max:50,col:"#dc2626"}];
                const data=buckets.map(b=>({...b,count:classRows.filter(r=>r.avg!=null&&r.avg>=b.min&&r.avg<b.max).length}));
                const max=Math.max(...data.map(d=>d.count),1);
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {data.map(b=>(
                      <div key={b.l} style={{display:"flex",alignItems:"center",gap:6}}>
                        <div style={{width:58,fontSize:10,fontWeight:700,color:b.col,textAlign:"right",flexShrink:0}}>{b.l}</div>
                        <div style={{flex:1,height:14,background:"#f3f4f6",borderRadius:3,overflow:"hidden"}}>
                          <div style={{width:`${Math.round((b.count/max)*100)}%`,height:"100%",background:b.col,borderRadius:3,minWidth:b.count>0?4:0,transition:"width .5s"}}/>
                        </div>
                        <div style={{fontSize:11,fontWeight:800,color:b.col,width:16,textAlign:"right",flexShrink:0}}>{b.count}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Subject averages */}
            {mySubjects.length>0&&(
              <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                <div style={{fontWeight:800,fontSize:12,color:C2.text,marginBottom:10}}>📚 Subject Averages</div>
                {(()=>{
                  const subAvgs=mySubjects.map(sub=>{
                    const rows=classRows.map(({s})=>{const r=stuResults(s.id).filter(x=>x.subject===sub);return r.length?Math.round(r.reduce((t,x)=>t+(x.marks_obtained/x.total_marks)*100,0)/r.length):null;}).filter(v=>v!=null);
                    return {sub,avg:rows.length?Math.round(rows.reduce((s,v)=>s+v,0)/rows.length):null};
                  }).filter(x=>x.avg!=null);
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {subAvgs.map((x,i)=>(
                        <div key={x.sub}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                            <span style={{fontSize:11,fontWeight:600,color:C2.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:100}}>{x.sub}</span>
                            <span style={{fontSize:11,fontWeight:800,color:gradeColor(x.avg)}}>{x.avg}%</span>
                          </div>
                          <div style={{height:7,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                            <div style={{width:`${x.avg}%`,height:"100%",background:RADAR_COLS[i%RADAR_COLS.length],borderRadius:4}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Attendance risk gauge */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontWeight:800,fontSize:12,color:C2.text,marginBottom:10}}>📅 Attendance Health</div>
              {(()=>{
                const good=classRows.filter(r=>r.att!=null&&r.att>=85).length;
                const ok=classRows.filter(r=>r.att!=null&&r.att>=75&&r.att<85).length;
                const risk=classRows.filter(r=>r.att!=null&&r.att>=50&&r.att<75).length;
                const crit=classRows.filter(r=>r.att!=null&&r.att<50).length;
                const noData=classRows.filter(r=>r.att==null).length;
                const total=classRows.length||1;
                const bars=[{l:"≥85% Good",v:good,col:"#16a34a"},{l:"75-84% OK",v:ok,col:"#2563eb"},{l:"50-74% Risk",v:risk,col:"#d97706"},{l:"<50% Critical",v:crit,col:"#dc2626"},{l:"No data",v:noData,col:"#d1d5db"}];
                return (
                  <>
                    <div style={{display:"flex",height:16,borderRadius:6,overflow:"hidden",marginBottom:10}}>
                      {bars.filter(b=>b.v>0).map(b=>(
                        <div key={b.l} title={b.l+": "+b.v} style={{flex:b.v,background:b.col,minWidth:b.v>0?4:0}}/>
                      ))}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      {bars.filter(b=>b.v>0).map(b=>(
                        <div key={b.l} style={{display:"flex",alignItems:"center",gap:6,fontSize:11}}>
                          <div style={{width:9,height:9,borderRadius:"50%",background:b.col,flexShrink:0}}/>
                          <span style={{color:"#374151",flex:1}}>{b.l}</span>
                          <span style={{fontWeight:800,color:b.col}}>{b.v}</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Exam Timeline + Top/Bottom 3 ── */}
        {view==="class"&&classRows.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:14}}>

            {/* Exam Performance Timeline */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
              <div style={{fontWeight:800,fontSize:12,color:C2.text,marginBottom:10}}>📈 Class Performance Over Time</div>
              {(()=>{
                // Get all exams that have results for this class, sorted by date
                const classExams = [...new Set(results.filter(r=>classStu.find(s=>String(s.id)===String(r.student_id))).map(r=>r.exam_name).filter(Boolean))].sort();
                if(classExams.length===0) return <div style={{fontSize:11,color:C2.muted,padding:"20px 0",textAlign:"center"}}>No exam results yet for this class.</div>;
                // For each exam, get avg per subject
                const timelineData = classExams.map(ex=>{
                  const point = {exam: ex.length>12?ex.slice(0,11)+"…":ex};
                  mySubjects.forEach(sub=>{
                    const rows = results.filter(r=>r.exam_name===ex&&r.subject===sub&&classStu.find(s=>String(s.id)===String(r.student_id)));
                    point[sub] = rows.length ? Math.round(rows.reduce((t,r)=>t+(r.marks_obtained/r.total_marks)*100,0)/rows.length) : null;
                  });
                  // Overall class avg for this exam
                  const allRows = results.filter(r=>r.exam_name===ex&&classStu.find(s=>String(s.id)===String(r.student_id)));
                  point["Overall"] = allRows.length ? Math.round(allRows.reduce((t,r)=>t+(r.marks_obtained/r.total_marks)*100,0)/allRows.length) : null;
                  return point;
                });
                const subjectsToShow = mySubjects.length>0 ? mySubjects.slice(0,4) : [];
                const COLORS = ["#1a6b3c","#2563eb","#d97706","#7c3aed","#dc2626"];
                const allLines = subjectsToShow.length>0 ? subjectsToShow : ["Overall"];
                return (
                  <div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={timelineData} margin={{top:4,right:8,left:-20,bottom:0}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                        <XAxis dataKey="exam" tick={{fontSize:9,fill:"#6b7280"}} interval={0} angle={-25} textAnchor="end" height={36}/>
                        <YAxis domain={[0,100]} tick={{fontSize:9,fill:"#6b7280"}} tickFormatter={v=>v+"%"}/>
                        <Tooltip formatter={(v,n)=>[v!=null?v+"%":"—",n]} contentStyle={{fontSize:11,borderRadius:8,border:"1px solid #e5e7eb"}}/>
                        {allLines.map((sub,i)=>(
                          <Line key={sub} type="monotone" dataKey={sub} stroke={COLORS[i%COLORS.length]} strokeWidth={2} dot={{r:3,fill:COLORS[i%COLORS.length]}} connectNulls activeDot={{r:5}}/>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    {subjectsToShow.length>1&&(
                      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:6,justifyContent:"center"}}>
                        {subjectsToShow.map((sub,i)=>(
                          <div key={sub} style={{display:"flex",alignItems:"center",gap:4,fontSize:10}}>
                            <div style={{width:16,height:3,background:COLORS[i%COLORS.length],borderRadius:2}}/>
                            <span style={{color:"#374151",fontWeight:600}}>{sub}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Top 3 / Bottom 3 */}
            <div style={{background:"white",borderRadius:14,padding:"14px 16px",boxShadow:`0 2px 8px ${C2.shadow}`,display:"flex",flexDirection:"column",gap:10}}>
              <div style={{fontWeight:800,fontSize:12,color:C2.text}}>🏆 Leaderboard</div>
              {(()=>{
                const ranked = classRows.filter(r=>r.avg!=null).sort((a,b)=>b.avg-a.avg);
                if(ranked.length===0) return <div style={{fontSize:11,color:C2.muted,textAlign:"center",padding:"16px 0"}}>No scores yet.</div>;
                const top = ranked.slice(0,3);
                const bottom = ranked.length>3 ? ranked.slice(-3).reverse() : [];
                const Row = ({r,i,medal,color,bg}) => (
                  <div key={r.s.id} onClick={()=>{setSelStudent(r.s);setView("student");setAiReport("");}}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",borderRadius:9,background:bg,cursor:"pointer",border:`1px solid ${color}25`,transition:"opacity .15s"}}
                    onMouseOver={e=>e.currentTarget.style.opacity=".85"} onMouseOut={e=>e.currentTarget.style.opacity="1"}>
                    <span style={{fontSize:14,flexShrink:0}}>{medal}</span>
                    <div style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:11,fontWeight:700,color:"#111827"}}>{r.s.full_name||r.s.name}</div>
                    <span style={{fontWeight:900,fontSize:13,color,flexShrink:0}}>{r.avg}%</span>
                  </div>
                );
                return (
                  <>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:"#16a34a",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Top Performers</div>
                      {top.map((r,i)=><Row key={r.s.id} r={r} i={i} medal={["🥇","🥈","🥉"][i]} color="#16a34a" bg="#f0fdf4"/>)}
                    </div>
                    {bottom.length>0&&(
                      <div>
                        <div style={{fontSize:10,fontWeight:700,color:"#dc2626",marginBottom:4,textTransform:"uppercase",letterSpacing:.5}}>Needs Attention</div>
                        {bottom.map((r,i)=><Row key={r.s.id} r={r} i={i} medal="⚠️" color="#dc2626" bg="#fef2f2"/>)}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* CLASS VIEW — ranked table with sparkbars */}
        {view==="class"&&(
          <div style={{background:"white",borderRadius:14,boxShadow:`0 2px 8px ${C2.shadow}`,overflow:"hidden"}}>
            <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,padding:"12px 18px",display:"flex",gap:8,alignItems:"center"}}>
              <div style={{color:"white",fontWeight:800,fontSize:13,flex:2}}>Student</div>
              {mySubjects.slice(0,4).map(s=><div key={s} style={{color:"rgba(255,255,255,0.85)",fontWeight:700,fontSize:11,flex:1,minWidth:50}}>{s}</div>)}
              <div style={{color:"white",fontWeight:800,fontSize:12,flex:1,minWidth:60,textAlign:"center"}}>Avg</div>
              <div style={{color:"white",fontWeight:800,fontSize:12,flex:1,minWidth:60,textAlign:"center"}}>Att.</div>
              <div style={{color:"white",fontWeight:700,fontSize:11,width:60}}></div>
            </div>
            {classRows.length===0&&<div style={{padding:32,textAlign:"center",color:C2.muted}}>No students or results found for this class.</div>}
            {classRows.map(({s,subScores,avg,att},i)=>(
              <div key={s.id} style={{display:"flex",gap:8,padding:"10px 18px",background:i%2===0?"white":"#fafafa",borderBottom:`1px solid #f3f4f6`,alignItems:"center",flexWrap:"wrap"}}>
                {/* Rank badge */}
                <div style={{width:24,height:24,borderRadius:"50%",background:i===0?"#f0a500":i===1?"#9ca3af":i===2?"#cd7c2f":"#f3f4f6",color:i<3?"white":"#6b7280",fontSize:10,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {i+1}
                </div>
                <div style={{flex:2,fontWeight:700,fontSize:13,color:C2.text,minWidth:80,cursor:"pointer"}} onClick={()=>{setSelStudent(s);setView("student");setAiReport("");}}>
                  {s.full_name||s.name}
                  <div style={{fontSize:10,color:C2.muted,fontWeight:400}}>{s.roll_no||""}</div>
                </div>
                {mySubjects.slice(0,4).map(sub=>{
                  const sc=subScores.find(x=>x.sub===sub);
                  return (
                    <div key={sub} style={{flex:1,minWidth:50}}>
                      {sc?.pct!=null?(
                        <>
                          <div style={{fontSize:12,fontWeight:700,color:gradeColor(sc.pct)}}>{sc.pct}%</div>
                          <div style={{height:3,background:"#f3f4f6",borderRadius:2,marginTop:2}}><div style={{width:`${sc.pct}%`,height:"100%",background:gradeColor(sc.pct),borderRadius:2}}/></div>
                        </>
                      ):<div style={{color:"#d1d5db",fontSize:11}}>—</div>}
                    </div>
                  );
                })}
                <div style={{flex:1,minWidth:60,textAlign:"center"}}>
                  {avg!=null?(
                    <span style={{background:gradeBg(avg),color:gradeColor(avg),borderRadius:6,padding:"3px 8px",fontSize:12,fontWeight:800}}>{avg}%</span>
                  ):<span style={{color:"#d1d5db"}}>—</span>}
                </div>
                <div style={{flex:1,minWidth:60,textAlign:"center"}}>
                  {att!=null?(
                    <span style={{color:att>=75?"#15803d":att>=50?"#d97706":"#dc2626",fontWeight:700,fontSize:12}}>{att}%</span>
                  ):<span style={{color:"#d1d5db"}}>—</span>}
                </div>
                <button onClick={()=>{setSelStudent(s);setView("student");setAiReport("");}}
                  style={{width:60,background:C2.primary,color:"white",border:"none",borderRadius:7,padding:"5px 0",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  Details
                </button>
              </div>
            ))}
            {/* Class stats footer */}
            {classRows.length>0&&(
              <div style={{padding:"10px 18px",background:"#f0fdf4",borderTop:`1px solid #d1fae5`,display:"flex",gap:16,flexWrap:"wrap"}}>
                {[
                  ["Class Avg", classRows.filter(r=>r.avg!=null).length ? Math.round(classRows.filter(r=>r.avg!=null).reduce((s,r)=>s+r.avg,0)/classRows.filter(r=>r.avg!=null).length)+"%" : "—"],
                  ["Excellent (≥85%)", classRows.filter(r=>r.avg>=85).length],
                  ["Needs Help (<50%)", classRows.filter(r=>r.avg!=null&&r.avg<50).length],
                  ["Low Attendance (<75%)", classRows.filter(r=>r.att!=null&&r.att<75).length],
                ].map(([l,v])=>(
                  <div key={l} style={{fontSize:11,color:C2.muted}}>
                    <span style={{fontWeight:700,color:C2.text,fontSize:13,marginRight:4}}>{v}</span>{l}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STUDENT DETAIL VIEW */}
        {view==="student"&&selStudent&&(()=>{
          const att2=attPct(selStudent.id);
          const avg2=avgScore(selStudent.id);
          const attColor = att2!=null?(att2>=75?"#16a34a":att2>=50?"#d97706":"#dc2626"):"#9ca3af";
          const avgColor = avg2!=null?gradeColor(avg2):"#9ca3af";
          return (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Student hero card */}
              <div style={{background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,borderRadius:16,padding:"20px 24px",color:"white"}}>
                <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <div style={{width:54,height:54,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:900,flexShrink:0}}>
                    {(selStudent.full_name||selStudent.name||"?")[0]}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:900,fontSize:18}}>{selStudent.full_name||selStudent.name}</div>
                    <div style={{fontSize:12,opacity:.8}}>{selStudent.class||selStudent["class"]} · Roll #{selStudent.roll_no||"N/A"}</div>
                  </div>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                    <div style={{textAlign:"center",background:"rgba(255,255,255,0.15)",borderRadius:10,padding:"10px 16px"}}>
                      <div style={{fontWeight:900,fontSize:22}}>{avg2!=null?avg2+"%":"—"}</div>
                      <div style={{fontSize:10,opacity:.8}}>Overall Avg</div>
                    </div>
                    <div style={{textAlign:"center",background:"rgba(255,255,255,0.15)",borderRadius:10,padding:"10px 16px"}}>
                      <div style={{fontWeight:900,fontSize:22}}>{att2!=null?att2+"%":"—"}</div>
                      <div style={{fontSize:10,opacity:.8}}>Attendance</div>
                    </div>
                    <div style={{textAlign:"center",background:"rgba(255,255,255,0.15)",borderRadius:10,padding:"10px 16px"}}>
                      <div style={{fontWeight:900,fontSize:22}}>#{classRows.findIndex(r=>String(r.s.id)===String(selStudent.id))+1}</div>
                      <div style={{fontSize:10,opacity:.8}}>Class Rank</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Strengths & Weaknesses pills */}
              {(strengths.length>0||weaknesses.length>0)&&(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div style={{background:"#f0fdf4",borderRadius:12,padding:"14px 16px",border:"1px solid #bbf7d0"}}>
                    <div style={{fontWeight:800,fontSize:12,color:"#15803d",marginBottom:8}}>✅ STRENGTHS</div>
                    {strengths.length>0?strengths.map(s=>(
                      <div key={s} style={{background:"#dcfce7",color:"#15803d",borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:700,marginBottom:4,display:"inline-block",marginRight:4}}>{s}</div>
                    )):<div style={{fontSize:12,color:"#9ca3af"}}>None identified yet</div>}
                  </div>
                  <div style={{background:"#fff7ed",borderRadius:12,padding:"14px 16px",border:"1px solid #fed7aa"}}>
                    <div style={{fontWeight:800,fontSize:12,color:"#c2410c",marginBottom:8}}>⚠️ NEEDS ATTENTION</div>
                    {weaknesses.length>0?weaknesses.map(s=>(
                      <div key={s} style={{background:"#fef2f2",color:"#dc2626",borderRadius:6,padding:"4px 10px",fontSize:12,fontWeight:700,marginBottom:4,display:"inline-block",marginRight:4}}>{s}</div>
                    )):<div style={{fontSize:12,color:"#9ca3af"}}>All subjects OK</div>}
                  </div>
                </div>
              )}

              {/* Subject bar chart */}
              {subBreakdown.length>0&&(
                <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                  <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:14}}>📊 Subject Performance</div>
                  {subBreakdown.map((x,i)=>(
                    <div key={x.sub} style={{marginBottom:12}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                        <span style={{fontSize:12,fontWeight:700,color:C2.text}}>{x.sub}</span>
                        <span style={{fontSize:12,fontWeight:800,color:gradeColor(x.pct)}}>{x.pct}% · {gradeLabel(x.pct)}</span>
                      </div>
                      <div style={{height:10,background:"#f3f4f6",borderRadius:5,overflow:"hidden"}}>
                        <div style={{width:`${x.pct}%`,height:"100%",background:RADAR_COLS[i%RADAR_COLS.length],borderRadius:5,transition:"width .5s ease"}}/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Exam comparison: student vs class avg */}
              {examTrend.length>0&&(
                <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                  <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:12}}>📊 Exam Performance vs Class</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={examTrend.map(e=>{
                      const classAvgForExam = classRows.map(({s})=>{
                        const r=stuResults(s.id).filter(x=>x.exam_name&&(x.exam_name.length>10?x.exam_name.slice(0,10)+"…":x.exam_name)===e.exam);
                        return r.length?Math.round(r.reduce((s,x)=>s+(x.marks_obtained/x.total_marks)*100,0)/r.length):null;
                      }).filter(v=>v!=null);
                      return {...e, classAvg: classAvgForExam.length?Math.round(classAvgForExam.reduce((s,v)=>s+v,0)/classAvgForExam.length):null};
                    })} margin={{top:4,right:8,left:-20,bottom:4}} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                      <XAxis dataKey="exam" tick={{fontSize:9,fill:"#6b7280"}}/>
                      <YAxis domain={[0,100]} tick={{fontSize:9,fill:"#6b7280"}}/>
                      <Tooltip formatter={(v,n)=>[v+"%",n==="pct"?"Student":"Class Avg"]} contentStyle={{borderRadius:8,fontSize:11}}/>
                      <Bar dataKey="pct" name="pct" fill={C2.primary} radius={[3,3,0,0]} barSize={18}/>
                      <Bar dataKey="classAvg" name="classAvg" fill="#94a3b8" radius={[3,3,0,0]} barSize={14}/>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{display:"flex",gap:12,marginTop:4}}>
                    <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#374151"}}><div style={{width:10,height:10,borderRadius:2,background:C2.primary}}/> This Student</div>
                    <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#374151"}}><div style={{width:10,height:10,borderRadius:2,background:"#94a3b8"}}/> Class Average</div>
                  </div>
                </div>
              )}

              {/* Exam trend line */}
              {examTrend.length>1&&(
                <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                  <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:12}}>📈 Progress Trend</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={examTrend} margin={{top:4,right:12,left:-20,bottom:4}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                      <XAxis dataKey="exam" tick={{fontSize:10,fill:"#6b7280"}}/>
                      <YAxis domain={[0,100]} tick={{fontSize:10,fill:"#6b7280"}}/>
                      <Tooltip formatter={(v)=>[v+"%","Score"]} contentStyle={{borderRadius:8,fontSize:12}}/>
                      <Line type="monotone" dataKey="pct" stroke={C2.primary} strokeWidth={2.5} dot={{r:4,fill:C2.primary}} activeDot={{r:6}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Attendance breakdown */}
              {(()=>{
                const attRecs=stuAtt(selStudent.id);
                if(!attRecs.length) return null;
                const P=attRecs.filter(a=>a.status==="P"||a.status==="Present"||a.status==="L"||a.status==="Late").length;
                const A=attRecs.filter(a=>a.status==="A"||a.status==="Absent").length;
                const L=attRecs.filter(a=>a.status==="L"||a.status==="Late"||a.status==="Leave").length;
                const pctVal=Math.round(P/attRecs.length*100);
                return (
                  <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`}}>
                    <div style={{fontWeight:800,fontSize:13,color:C2.text,marginBottom:12}}>📅 Attendance Record</div>
                    <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                      {[["Present",P,"#16a34a","#dcfce7"],["Late",L,"#f59e0b","#fffbeb"],["Absent",A,"#dc2626","#fef2f2"]].map(([lbl,v,col,bg])=>(
                        <div key={lbl} style={{flex:1,background:bg,borderRadius:10,padding:"10px 14px",textAlign:"center",minWidth:70}}>
                          <div style={{fontWeight:900,fontSize:20,color:col}}>{v}</div>
                          <div style={{fontSize:11,color:col,fontWeight:600}}>{lbl}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{height:10,background:"#f3f4f6",borderRadius:5,overflow:"hidden",display:"flex",gap:1}}>
                      <div style={{width:`${Math.round((attRecs.filter(a=>a.status==="P"||a.status==="Present").length/attRecs.length)*100)}%`,background:"#16a34a",borderRadius:"5px 0 0 5px",transition:"width .4s"}}/>
                      <div style={{width:`${Math.round((L/attRecs.length)*100)}%`,background:"#f59e0b",transition:"width .4s"}}/>
                      <div style={{width:`${Math.round((A/attRecs.length)*100)}%`,background:"#dc2626",borderRadius:"0 5px 5px 0",transition:"width .4s"}}/>
                     </div>
                    <div style={{fontSize:11,color:C2.muted,marginTop:6}}>Overall: <strong style={{color:attColor}}>{pctVal}%</strong> · {attRecs.length} total records</div>
                    {pctVal<75&&<div style={{marginTop:8,background:"#fef2f2",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#dc2626",fontWeight:600}}>⚠️ Attendance below 75% — intervention recommended</div>}
                  </div>
                );
              })()}

              {/* AI Report Generator */}
              <div style={{background:"white",borderRadius:14,padding:"16px 18px",boxShadow:`0 2px 8px ${C2.shadow}`,border:"1px solid #e0f2fe"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:aiReport?14:0,flexWrap:"wrap",gap:10}}>
                  <div>
                    <div style={{fontWeight:800,fontSize:13,color:C2.text}}>🤖 AI Performance Report</div>
                    <div style={{fontSize:11,color:C2.muted,marginTop:2}}>Critical analysis based on all available data</div>
                  </div>
                  <button onClick={generateAI} disabled={aiLoading}
                    style={{background:aiLoading?"#9ca3af":"linear-gradient(135deg,#1a6b3c,#0a4a24)",color:"white",border:"none",borderRadius:9,padding:"10px 20px",fontWeight:800,fontSize:13,cursor:aiLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8}}>
                    {aiLoading?(<><div style={{width:14,height:14,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite"}}/>Analyzing...</>):"✨ Generate Report"}
                  </button>
                </div>
                {aiReport&&(
                  <div style={{background:"#f8fafc",borderRadius:10,padding:"16px",border:"1px solid #e2e8f0",fontSize:13,color:"#374151",lineHeight:1.8,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif",marginTop:12}}>
                    {aiReport}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };



  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const sidebarRef = React.useRef(null);

  return (
    <SettingsContext.Consumer>{()=>(
      <div style={{minHeight:"100vh",background:"#f0f4f8",fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif",display:"flex",flexDirection:"column"}}>
        {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>

        {/* ── Letterhead / Top Banner ── */}
        <div style={{background:`linear-gradient(135deg,${C2.primary} 0%,#0a4a24 100%)`,flexShrink:0,boxShadow:"0 4px 20px rgba(26,107,60,0.25)"}}>
          <div style={{padding:"14px 20px 0",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
            <div style={{width:52,height:52,borderRadius:12,background:"rgba(255,255,255,0.12)",
              border:"2px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",
              justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
              <img src={SCHOOL_LOGO} style={{width:44,height:44,objectFit:"contain"}} onError={e=>{e.target.style.display="none";}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:"rgba(255,255,255,0.65)",fontSize:10,fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Teacher Portal</div>
              <div style={{color:"white",fontWeight:900,fontSize:17,lineHeight:1.2,marginTop:1}}>{S?.school_name_en||"QUR'AN-E-KARIM ACADEMY"}</div>
              <div style={{color:"rgba(255,255,255,0.6)",fontSize:11,marginTop:1}}>{S?.school_name_en_sub||"Modern Islamic School System"}</div>
            </div>
            {/* Teacher name pill — always visible */}
            <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.18)",borderRadius:12,padding:"8px 14px",flexShrink:0}}>
              <div style={{width:34,height:34,borderRadius:9,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>👨‍🏫</div>
              <div>
                <div style={{color:"white",fontWeight:800,fontSize:13,lineHeight:1.2}}>{teacher.name}</div>
                <div style={{color:"rgba(255,255,255,0.5)",fontSize:10,marginTop:2}}>{myClasses.join(", ")||"Teacher"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
              <NotificationBell userEmail={session?.user?.email||""} userRole="teachers" targetClass={myClasses[0]||null}/>
              <button onClick={onSignOut} style={{background:"rgba(255,255,255,0.12)",color:"white",
                border:"1px solid rgba(255,255,255,0.25)",borderRadius:8,padding:"7px 14px",
                fontSize:12,fontWeight:700,cursor:"pointer"}}>Sign Out</button>
            </div>
          </div>
          {/* Multi-colour accent strip */}
          <div style={{height:3,background:"linear-gradient(90deg,#2563eb,#0d9488,#f0a500,#0d9488,#2563eb)",opacity:.8,marginTop:14}}/>
          {/* Subtitle bar */}
          <div style={{background:"rgba(0,0,0,0.18)",padding:"6px 20px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#ffd166",display:"flex",alignItems:"center",gap:8}}>
              <span style={{opacity:.6}}>📍</span>
              <span>{TABS.find(t=>t.id===activeTab)?.icon} {TABS.find(t=>t.id===activeTab)?.label||"Dashboard"}</span>
            </div>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>
              {new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}
            </span>
          </div>
        </div>

        {/* ── Body: Sidebar + Content ── */}
        <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>

          {/* ── Left Sidebar ── */}
          <div ref={sidebarRef} style={{
            width: sidebarCollapsed ? 64 : 220,
            background:"linear-gradient(180deg,#0d3d1f 0%,#1a6b3c 60%,#0a4a24 100%)",
            display:"flex",flexDirection:"column",
            transition:"width .25s cubic-bezier(.4,0,.2,1)",
            overflow:"hidden",flexShrink:0,
            boxShadow:"3px 0 16px rgba(0,0,0,0.2)",
            position:"sticky",top:0,alignSelf:"flex-start",
            maxHeight:"calc(100vh - 90px)",
            scrollbarWidth:"thin",scrollbarColor:"rgba(255,255,255,0.15) transparent",
          }}>
            {/* Teacher card */}
            {!sidebarCollapsed && (
              <div style={{padding:"16px 14px 12px",borderBottom:"1px solid rgba(255,255,255,0.1)"}}>
                <div style={{width:40,height:40,borderRadius:10,background:"rgba(255,255,255,0.15)",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,marginBottom:8}}>👨‍🏫</div>
                <div style={{fontWeight:800,fontSize:13,color:"white",lineHeight:1.2}}>{teacher.name}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginTop:2}}>{myClasses.join(", ")||"All Classes"}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.38)",marginTop:1}}>{mySubjects.join(", ")||"All Subjects"}</div>
              </div>
            )}

            {/* Nav items - scrollable */}
            <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
              {TAB_SECTIONS.map(sec=>(
                <React.Fragment key={sec.title}>
                  {!sidebarCollapsed && (
                    <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,0.4)",letterSpacing:1.2,textTransform:"uppercase",padding:"12px 12px 4px",whiteSpace:"nowrap",overflow:"hidden"}}>
                      {sec.title}
                    </div>
                  )}
                  {sec.tabs.map(t=>{
                    const active = activeTab===t.id;
                    return (
                      <button key={t.id} onClick={()=>setActiveTab(t.id)} title={t.label}
                        style={{
                          width:"100%",display:"flex",alignItems:"center",
                          gap:sidebarCollapsed?0:10,
                          padding:sidebarCollapsed?"11px 0":"9px 12px",
                          justifyContent:sidebarCollapsed?"center":"flex-start",
                          background:active?"rgba(255,255,255,0.15)":"transparent",
                          color:active?"white":"rgba(255,255,255,0.65)",
                          border:"none",cursor:"pointer",
                          boxShadow:active?(sidebarCollapsed?"inset 0 -3px 0 #f0a500":"inset 3px 0 0 #f0a500"):"none",
                          fontWeight:active?700:500,fontSize:13,
                          transition:"all .15s",textAlign:"left",
                        }}
                        onMouseOver={e=>{if(!active){e.currentTarget.style.background="rgba(255,255,255,0.08)";e.currentTarget.style.color="white";}}}
                        onMouseOut={e=>{if(!active){e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.65)";}}}
                      >
                        <span style={{fontSize:16,flexShrink:0}}>{t.icon}</span>
                        {!sidebarCollapsed&&<span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t.label}</span>}
                        {t.badge&&<span style={{background:"#f0a500",color:"white",borderRadius:20,padding:"0 6px",fontSize:10,fontWeight:900,flexShrink:0,minWidth:16,textAlign:"center"}}>{t.badge}</span>}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>

            {/* Collapse toggle at bottom */}
            <div style={{padding:"8px",borderTop:"1px solid rgba(255,255,255,0.1)"}}>
              <button onClick={()=>setSidebarCollapsed(p=>!p)}
                style={{width:"100%",display:"flex",alignItems:"center",
                  justifyContent:sidebarCollapsed?"center":"flex-start",
                  gap:10,padding:sidebarCollapsed?"10px 0":"8px 10px",
                  background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",
                  borderRadius:8,cursor:"pointer",color:"rgba(255,255,255,0.6)",fontSize:12,fontWeight:600}}>
                <span style={{fontSize:14}}>{sidebarCollapsed?"▶":"◀"}</span>
                {!sidebarCollapsed&&<span>Collapse</span>}
              </button>
            </div>
          </div>

          {/* ── Main content ── */}
          <div style={{flex:1,overflowY:"auto",padding:"20px 20px",minWidth:0}}>
            {loading?(
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:320,gap:16}}>
                <div style={{width:52,height:52,background:`linear-gradient(135deg,${C2.primary},#0a4a24)`,borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,boxShadow:`0 8px 24px ${C2.primary}40`}}>👨‍🏫</div>
                <div style={{width:40,height:40,border:`3px solid ${C2.border}`,borderTopColor:C2.primary,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
                <div style={{color:C2.text,fontWeight:700,fontSize:15}}>Loading your portal…</div>
                <div style={{color:C2.muted,fontSize:12}}>Fetching students, attendance and results</div>
              </div>
            ):activeTab==="dashboard"   ? <TabDashboard/>
            :activeTab==="attendance"  ? <TabAttendance/>
            :activeTab==="coverduty"   ? <TabCoverDuty/>
            :activeTab==="results"     ? <TabResults/>
            :activeTab==="courseplan"  ? <CoursePlanTeacherTab teacher={teacher} subjects={mySubjects} classes={myClasses}/>
            :activeTab==="responsibilities" ? <StaffResponsibilityTab teacher={teacher}/>
            :activeTab==="library"     ? <LibraryTab teacher={teacher}/>
            :activeTab==="students"    ? <TabStudents/>
            :activeTab==="performance" ? <TabPerformance/>
            :activeTab==="events"      ? <TabEvents/>
            :activeTab==="homework"    ? <HomeworkTracker students={myStudents}/>
            :activeTab==="reports"     ? <TabReports/>
            :activeTab==="leave"       ? <LeaveRequestAdmin scopeStudentIds={myStudents.map(s=>s.id)}/>
            :activeTab==="myleave"     ? <StaffLeaveRequest staffId={teacher.staff_id||null} staffName={teacher.name}/>
            : null}
          </div>
        </div>
      </div>
    )}</SettingsContext.Consumer>
  );
}

// ── Insights & Reports — consolidated sub-tab wrapper ────────────────────────
function InsightsTab({ students, results, attendance, fees, conductData, onShowToast }) {
  const [sub, setSub] = React.useState("analytics");
  const C2 = { primary:"#1a6b3c", primaryPale:"#e8f5ee", white:"#ffffff", border:"#d1fae5", text:"#111827" };
  const tabs = [
    { id:"analytics",    label:"Analytics & AI", icon:"📊" },
    { id:"reports",      label:"Reports",         icon:"📋" },
    { id:"achievements", label:"Achievements",    icon:"🏆" },
    { id:"timeline",     label:"Student Timeline",icon:"📜" },
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ background:C2.white, borderRadius:14, padding:"14px 18px", border:`1px solid ${C2.border}`, display:"flex", gap:8, flexWrap:"wrap" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            style={{ background: sub===t.id ? C2.primary : C2.primaryPale, color: sub===t.id ? C2.white : C2.primary, border:"none", borderRadius:10, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all .15s" }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {sub==="analytics"    && <SchoolAnalyticsTab students={students} results={results} attendance={attendance} fees={fees}/>}
      {sub==="reports"      && <ReportsTab students={students} attendance={attendance} results={results}/>}
      {sub==="achievements" && <AchievementBoard students={students} results={results} attendance={attendance} conductData={conductData||[]} onShowToast={onShowToast}/>}
      {sub==="timeline"     && <SchoolAnalyticsTab students={students} results={results} attendance={attendance} fees={fees} initialSubTab="timeline"/>}
    </div>
  );
}

// ── Communications — consolidated sub-tab wrapper ─────────────────────────────
function CommunicationsTab({ students }) {
  const [sub, setSub] = React.useState("announcements");
  const C2 = { primary:"#1a6b3c", primaryPale:"#e8f5ee", white:"#ffffff", border:"#d1fae5" };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ background:C2.white, borderRadius:14, padding:"14px 18px", border:`1px solid ${C2.border}`, display:"flex", gap:8, flexWrap:"wrap" }}>
        {[{id:"announcements",label:"Announcements",icon:"📢"},{id:"calendar",label:"Academic Calendar",icon:"🗓️"}].map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            style={{ background: sub===t.id ? C2.primary : C2.primaryPale, color: sub===t.id ? C2.white : C2.primary, border:"none", borderRadius:10, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all .15s" }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      {sub==="announcements" && <AnnouncementsManagement/>}
      {sub==="calendar"      && <CalendarManagement students={students}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// ACCOUNTANT PORTAL — dedicated finance dashboard
// ═══════════════════════════════════════════════════════════════════════
function AccountantPortal({ session, onSignOut }) {
  const S = useSettings();
  const [tab, setTab] = React.useState("dashboard");
  const [fees, setFees] = React.useState([]);
  const [students, setStudents] = React.useState([]);
  const [salaries, setSalaries] = React.useState([]);
  const [staff, setStaff] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [toast, setToast] = React.useState(null);
  const [feeSearch, setFeeSearch] = React.useState("");
  const [feeClass, setFeeClass] = React.useState("All");
  const [feeStatus, setFeeStatus] = React.useState("All");
  const [payForm, setPayForm] = React.useState({student_id:"",amount:"",month:"",year:String(new Date().getFullYear()),method:"Cash",notes:""});
  const [savingPay, setSavingPay] = React.useState(false);
  const [showPayForm, setShowPayForm] = React.useState(false);

  const G = "#1a6b3c";
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };
  const thisMonth = MONTHS[new Date().getMonth()];
  const thisYear  = String(new Date().getFullYear());

  const load = React.useCallback(async()=>{
    const [fRes,sRes,salRes,stRes] = await Promise.all([
      supabase.from("fee_payments").select("*").order("created_at",{ascending:false}),
      supabase.from("students").select("*").order("full_name",{ascending:true}),
      supabase.from("salary_payments").select("*").order("created_at",{ascending:false}),
      supabase.from("staff").select("*").order("name",{ascending:true}),
    ]);
    setFees(fRes.data||[]);
    setStudents(sRes.data||[]);
    setSalaries(salRes.data||[]);
    setStaff(stRes.data||[]);
    setLoading(false);
  },[]);

  React.useEffect(()=>{ load(); },[load]);

  // ── KPIs ──
  const thisMonthFees = fees.filter(f=>f.month===thisMonth&&f.year===thisYear);
  const collectedThisMonth = thisMonthFees.reduce((s,f)=>s+(Number(f.amount)||0),0);
  const totalStudents = students.length;
  const studentsWithPaymentThisMonth = new Set(thisMonthFees.map(f=>f.student_id)).size;
  const studentsWithArrears = students.filter(s=>s.fees_status==="Unpaid"||s.fees_status==="Partial").length;
  const salaryThisMonth = salaries.filter(s=>s.month===thisMonth&&s.year===thisYear).reduce((a,s)=>a+(Number(s.amount)||0)+(Number(s.bonus)||0),0);

  // ── Monthly trend (last 6 months) ──
  const trend = [];
  for(let i=5;i>=0;i--){
    const d=new Date(); d.setMonth(d.getMonth()-i);
    const m=MONTHS[d.getMonth()], y=String(d.getFullYear());
    const total=fees.filter(f=>f.month===m&&f.year===y).reduce((s,f)=>s+(Number(f.amount)||0),0);
    trend.push({label:`${m} ${y.slice(2)}`,total});
  }
  const trendMax = Math.max(...trend.map(t=>t.total),1);

  // ── Fee filter ──
  const allClasses = ["All",...new Set(students.map(s=>s.class||"").filter(Boolean))];
  const filteredFees = fees.filter(f=>{
    const stu = students.find(s=>s.id===f.student_id);
    const matchSearch = !feeSearch || (stu?.full_name||stu?.name||"").toLowerCase().includes(feeSearch.toLowerCase()) || (f.student_id||"").toLowerCase().includes(feeSearch.toLowerCase());
    const matchClass = feeClass==="All" || (stu?.class||"")=== feeClass;
    const matchStatus = feeStatus==="All" || (feeStatus==="Paid"?(f.method&&f.method!=="Unpaid"):(f.method==="Unpaid"||!f.method));
    return matchSearch&&matchClass&&matchStatus;
  });

  // ── Record fee payment ──
  const recordPayment = async()=>{
    if(!payForm.student_id||!payForm.amount||!payForm.month) return showToast("Fill all required fields",false);
    setSavingPay(true);
    const stu = students.find(s=>s.id===payForm.student_id);
    const receiptNo = `RCP-${Date.now().toString().slice(-6)}`;
    await supabase.from("fee_payments").insert({
      student_id:payForm.student_id, student_name:stu?.full_name||stu?.name||"",
      class:stu?.class||"", amount:Number(payForm.amount),
      month:payForm.month, year:payForm.year, method:payForm.method,
      notes:payForm.notes, receipt_no:receiptNo, created_at:new Date().toISOString(),
    });
    setSavingPay(false);
    showToast(`Payment recorded — Receipt: ${receiptNo}`);
    setPayForm(p=>({...p,student_id:"",amount:"",notes:""}));
    setShowPayForm(false);
    load();
  };

  const dlCSV = (rows, fname) => {
    if(!rows.length) return;
    const keys=Object.keys(rows[0]);
    const csv=[keys.join(","),...rows.map(r=>keys.map(k=>JSON.stringify(r[k]??"")).join(","))].join("\n");
    const a=document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
    a.download=fname; a.click();
  };

  const TABS_ACC = [
    {id:"dashboard",icon:"📊",label:"Dashboard"},
    {id:"fees",     icon:"💰",label:"Fee Management"},
    {id:"payroll",  icon:"💼",label:"Payroll"},
    {id:"export",   icon:"📥",label:"Export"},
  ];

  const C = {pri:"#0f5132",pale:"#d1fae5",border:"#6ee7b7",text:"#111827",muted:"#6b7280",shadow:"rgba(0,0,0,0.08)"};

  if(loading) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"linear-gradient(135deg,#0f5132,#1a6b3c)"}}>
      <div style={{color:"white",textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>💼</div>
        <div style={{fontWeight:700,fontSize:15,marginBottom:8}}>Accountant Portal</div>
        <div style={{width:36,height:36,border:"3px solid rgba(255,255,255,.25)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite",margin:"0 auto"}}/>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"#f0fdf4",fontFamily:"system-ui,sans-serif",display:"flex",flexDirection:"column"}}>
      {toast&&<div style={{position:"fixed",top:16,right:16,left:16,zIndex:9999,background:toast.ok?"#dcfce7":"#fee2e2",color:toast.ok?"#15803d":"#dc2626",borderRadius:10,padding:"12px 18px",fontWeight:700,fontSize:13,boxShadow:"0 4px 16px rgba(0,0,0,.15)",textAlign:"center"}}>{toast.msg}</div>}

      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#0f5132,#1a6b3c)",color:"white",padding:"14px 20px",display:"flex",alignItems:"center",gap:14,boxShadow:"0 2px 12px rgba(0,0,0,.2)",flexShrink:0}}>
        <div style={{width:42,height:42,borderRadius:10,background:"rgba(255,255,255,.12)",border:"2px solid rgba(255,255,255,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>💼</div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,opacity:.65,fontWeight:700,letterSpacing:2,textTransform:"uppercase"}}>Accountant Portal</div>
          <div style={{fontWeight:900,fontSize:16}}>{S?.school_name_en||"QK Academy"}</div>
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,.6)",marginLeft:"auto"}}>{session?.user?.email}</div>
        <button onClick={onSignOut} style={{background:"rgba(255,255,255,.12)",color:"white",border:"1px solid rgba(255,255,255,.25)",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Sign Out</button>
      </div>

      {/* Tab bar */}
      <div style={{background:"white",borderBottom:"1px solid #e5e7eb",display:"flex",gap:0,padding:"0 16px",flexShrink:0,overflowX:"auto"}}>
        {TABS_ACC.map(tb=>(
          <button key={tb.id} onClick={()=>setTab(tb.id)}
            style={{padding:"12px 18px",border:"none",borderBottom:`2px solid ${tab===tb.id?"#0f5132":"transparent"}`,
              background:"transparent",color:tab===tb.id?"#0f5132":"#6b7280",fontWeight:tab===tb.id?800:500,
              fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",gap:6,flexShrink:0,whiteSpace:"nowrap"}}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"20px 16px"}}>

        {/* ── DASHBOARD ── */}
        {tab==="dashboard"&&(
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {/* KPI cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
              {[
                {icon:"💵",label:"Collected This Month",val:`PKR ${collectedThisMonth.toLocaleString()}`,color:"#0f5132",bg:"#d1fae5",border:"#6ee7b7"},
                {icon:"👥",label:"Students Paid",val:`${studentsWithPaymentThisMonth} / ${totalStudents}`,color:"#1d4ed8",bg:"#dbeafe",border:"#93c5fd"},
                {icon:"⚠️",label:"Students with Arrears",val:studentsWithArrears,color:"#b45309",bg:"#fef3c7",border:"#fde68a"},
                {icon:"💼",label:"Salary Paid This Month",val:`PKR ${salaryThisMonth.toLocaleString()}`,color:"#7c3aed",bg:"#ede9fe",border:"#c4b5fd"},
              ].map(k=>(
                <div key={k.label} style={{background:k.bg,borderRadius:14,padding:"18px 16px",border:`1.5px solid ${k.border}`,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                  <div style={{fontSize:24,marginBottom:6}}>{k.icon}</div>
                  <div style={{fontSize:24,fontWeight:900,color:k.color,lineHeight:1}}>{k.val}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginTop:6,fontWeight:600}}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Monthly trend */}
            <div style={{background:"white",borderRadius:16,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,.06)",border:"1px solid #e5e7eb"}}>
              <div style={{fontWeight:800,fontSize:15,color:"#111827",marginBottom:4}}>Fee Collection — Last 6 Months</div>
              <div style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Monthly fee collected (PKR)</div>
              <div style={{display:"flex",alignItems:"flex-end",gap:8,height:110}}>
                {trend.map(m=>{
                  const pct=trendMax>0?Math.max(4,Math.round((m.total/trendMax)*100)):4;
                  const isThis=m.label.startsWith(thisMonth);
                  return (
                    <div key={m.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                      <div style={{fontSize:10,color:"#374151",fontWeight:600}}>{m.total?`${Math.round(m.total/1000)}k`:""}</div>
                      <div style={{width:"100%",background:isThis?"#0f5132":"#6ee7b7",borderRadius:"4px 4px 0 0",height:`${pct}%`,minHeight:4,transition:"height .3s"}}/>
                      <div style={{fontSize:10,color:isThis?"#0f5132":"#9ca3af",fontWeight:isThis?800:400,textAlign:"center"}}>{m.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent transactions */}
            <div style={{background:"white",borderRadius:16,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,.06)",border:"1px solid #e5e7eb"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontWeight:800,fontSize:15,color:"#111827"}}>Recent Transactions</div>
                <button onClick={()=>setTab("fees")} style={{background:"#f0fdf4",color:"#0f5132",border:"1px solid #6ee7b7",borderRadius:7,padding:"5px 12px",fontSize:12,fontWeight:700,cursor:"pointer"}}>View All →</button>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{borderBottom:"2px solid #f3f4f6"}}>
                      {["Student","Class","Month","Amount","Method","Date","Receipt"].map(h=>(
                        <th key={h} style={{padding:"8px 10px",textAlign:"left",fontWeight:700,color:"#6b7280",fontSize:11,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fees.slice(0,10).map((f,i)=>{
                      const stu=students.find(s=>s.id===f.student_id);
                      return (
                        <tr key={f.id||i} style={{borderBottom:"1px solid #f9fafb",background:i%2===0?"white":"#fafafa"}}>
                          <td style={{padding:"9px 10px",fontWeight:600,color:"#111827"}}>{f.student_name||stu?.full_name||stu?.name||f.student_id}</td>
                          <td style={{padding:"9px 10px",color:"#6b7280"}}>{f.class||stu?.class||"—"}</td>
                          <td style={{padding:"9px 10px",color:"#374151"}}>{f.month} {f.year}</td>
                          <td style={{padding:"9px 10px",fontWeight:700,color:"#0f5132"}}>PKR {Number(f.amount||0).toLocaleString()}</td>
                          <td style={{padding:"9px 10px"}}>
                            <span style={{background:f.method&&f.method!=="Unpaid"?"#d1fae5":"#fef3c7",color:f.method&&f.method!=="Unpaid"?"#0f5132":"#92400e",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700}}>{f.method||"Unpaid"}</span>
                          </td>
                          <td style={{padding:"9px 10px",color:"#9ca3af",fontSize:11}}>{f.created_at?new Date(f.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):""}</td>
                          <td style={{padding:"9px 10px",color:"#9ca3af",fontSize:11}}>{f.receipt_no||"—"}</td>
                        </tr>
                      );
                    })}
                    {fees.length===0&&<tr><td colSpan={7} style={{textAlign:"center",padding:24,color:"#9ca3af"}}>No fee records yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Students with arrears */}
            {studentsWithArrears>0&&(
              <div style={{background:"white",borderRadius:16,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,.06)",border:"1.5px solid #fde68a"}}>
                <div style={{fontWeight:800,fontSize:15,color:"#92400e",marginBottom:12}}>⚠️ Students with Outstanding Fees</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {students.filter(s=>s.fees_status==="Unpaid"||s.fees_status==="Partial").slice(0,8).map(s=>(
                    <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",background:"#fffbeb",borderRadius:8,border:"1px solid #fde68a"}}>
                      <div style={{flex:1}}>
                        <span style={{fontWeight:700,color:"#111827",fontSize:13}}>{s.full_name||s.name}</span>
                        <span style={{color:"#9ca3af",fontSize:12,marginRight:6}}> · {s.class}</span>
                        <span style={{background:s.fees_status==="Partial"?"#fef3c7":"#fee2e2",color:s.fees_status==="Partial"?"#92400e":"#dc2626",borderRadius:5,padding:"1px 7px",fontSize:11,fontWeight:700}}>{s.fees_status}</span>
                      </div>
                      <div style={{fontSize:12,color:"#92400e",fontWeight:700}}>PKR {Number(s.monthly_fee||0).toLocaleString()}/mo</div>
                    </div>
                  ))}
                  {studentsWithArrears>8&&<div style={{textAlign:"center",fontSize:12,color:"#9ca3af",padding:6}}>+{studentsWithArrears-8} more — go to Fee Management</div>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── FEE MANAGEMENT ── */}
        {tab==="fees"&&(
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Record payment button */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
              <div style={{fontWeight:800,fontSize:18,color:"#111827"}}>💰 Fee Management</div>
              <button onClick={()=>setShowPayForm(v=>!v)}
                style={{background:"#0f5132",color:"white",border:"none",borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                {showPayForm?"✕ Cancel":"+ Record Payment"}
              </button>
            </div>

            {/* Payment form */}
            {showPayForm&&(
              <div style={{background:"white",borderRadius:14,padding:"18px 20px",border:"1.5px solid #6ee7b7",boxShadow:"0 2px 8px rgba(0,0,0,.06)"}}>
                <div style={{fontWeight:800,fontSize:14,color:"#111827",marginBottom:14}}>New Fee Payment</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>STUDENT *</label>
                    <select value={payForm.student_id} onChange={e=>{
                      const stu=students.find(s=>s.id===e.target.value);
                      setPayForm(p=>({...p,student_id:e.target.value,amount:String(stu?.monthly_fee||"")}));
                    }} style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                      <option value="">-- Select Student --</option>
                      {students.map(s=><option key={s.id} value={s.id}>{s.full_name||s.name} — {s.class} ({s.id})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>AMOUNT (PKR) *</label>
                    <input type="number" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))}
                      style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}} placeholder="e.g. 2500"/>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>METHOD *</label>
                    <select value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}
                      style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                      {["Cash","Online Transfer","Bank","JazzCash","EasyPaisa","Cheque"].map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>MONTH *</label>
                    <select value={payForm.month} onChange={e=>setPayForm(p=>({...p,month:e.target.value}))}
                      style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                      <option value="">-- Select --</option>
                      {MONTHS.map(m=><option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>YEAR</label>
                    <select value={payForm.year} onChange={e=>setPayForm(p=>({...p,year:e.target.value}))}
                      style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                      {[thisYear,String(Number(thisYear)-1)].map(y=><option key={y}>{y}</option>)}
                    </select>
                  </div>
                  <div style={{gridColumn:"1/-1"}}>
                    <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>NOTES (optional)</label>
                    <input value={payForm.notes} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))}
                      style={{width:"100%",padding:"10px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}} placeholder="Optional notes"/>
                  </div>
                </div>
                <button onClick={recordPayment} disabled={savingPay}
                  style={{background:"#0f5132",color:"white",border:"none",borderRadius:9,padding:"11px 24px",fontWeight:700,fontSize:14,cursor:"pointer"}}>
                  {savingPay?"Saving…":"✓ Record Payment"}
                </button>
              </div>
            )}

            {/* Filters */}
            <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
              <input value={feeSearch} onChange={e=>setFeeSearch(e.target.value)} placeholder="Search student…"
                style={{padding:"9px 13px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,flex:1,minWidth:160,boxSizing:"border-box"}}/>
              <select value={feeClass} onChange={e=>setFeeClass(e.target.value)}
                style={{padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                {allClasses.map(c=><option key={c}>{c}</option>)}
              </select>
              <select value={feeStatus} onChange={e=>setFeeStatus(e.target.value)}
                style={{padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}>
                <option>All</option><option>Paid</option><option>Unpaid</option>
              </select>
            </div>

            {/* Fee table */}
            <div style={{background:"white",borderRadius:14,overflow:"hidden",border:"1px solid #e5e7eb",boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"#f0fdf4",borderBottom:"2px solid #6ee7b7"}}>
                      {["Student","Class","Month/Year","Amount","Method","Receipt","Date"].map(h=>(
                        <th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#0f5132",fontSize:11,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFees.slice(0,50).map((f,i)=>{
                      const stu=students.find(s=>s.id===f.student_id);
                      const paid=f.method&&f.method!=="Unpaid";
                      return (
                        <tr key={f.id||i} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"white":"#fafafa"}}>
                          <td style={{padding:"10px 12px",fontWeight:600,color:"#111827"}}>{f.student_name||stu?.full_name||stu?.name||f.student_id}</td>
                          <td style={{padding:"10px 12px",color:"#6b7280"}}>{f.class||stu?.class||"—"}</td>
                          <td style={{padding:"10px 12px",color:"#374151",fontWeight:600}}>{f.month} {f.year}</td>
                          <td style={{padding:"10px 12px",fontWeight:700,color:"#0f5132"}}>PKR {Number(f.amount||0).toLocaleString()}</td>
                          <td style={{padding:"10px 12px"}}>
                            <span style={{background:paid?"#d1fae5":"#fef3c7",color:paid?"#0f5132":"#92400e",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700}}>{f.method||"Unpaid"}</span>
                          </td>
                          <td style={{padding:"10px 12px",color:"#9ca3af",fontSize:11}}>{f.receipt_no||"—"}</td>
                          <td style={{padding:"10px 12px",color:"#9ca3af",fontSize:11}}>{f.created_at?new Date(f.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short"}):""}</td>
                        </tr>
                      );
                    })}
                    {filteredFees.length===0&&<tr><td colSpan={7} style={{textAlign:"center",padding:28,color:"#9ca3af"}}>No records match your filters</td></tr>}
                  </tbody>
                </table>
              </div>
              {filteredFees.length>50&&<div style={{padding:"10px 16px",fontSize:12,color:"#9ca3af",background:"#f9fafb",borderTop:"1px solid #e5e7eb"}}>Showing 50 of {filteredFees.length} records. Use Export to get full data.</div>}
            </div>
          </div>
        )}

        {/* ── PAYROLL ── */}
        {tab==="payroll"&&(
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,fontSize:18,color:"#111827"}}>💼 Payroll History</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10}}>
              {[
                {label:"Staff Members",val:staff.length,icon:"👥",color:"#1d4ed8",bg:"#dbeafe"},
                {label:"Paid This Month",val:salaries.filter(s=>s.month===thisMonth&&s.year===thisYear).length,icon:"✅",color:"#0f5132",bg:"#d1fae5"},
                {label:"Total This Month",val:`PKR ${salaryThisMonth.toLocaleString()}`,icon:"💵",color:"#7c3aed",bg:"#ede9fe"},
              ].map(k=>(
                <div key={k.label} style={{background:k.bg,borderRadius:12,padding:"16px",border:"1.5px solid rgba(0,0,0,.06)"}}>
                  <div style={{fontSize:22,marginBottom:4}}>{k.icon}</div>
                  <div style={{fontWeight:900,fontSize:20,color:k.color}}>{k.val}</div>
                  <div style={{fontSize:11,color:"#6b7280",marginTop:4}}>{k.label}</div>
                </div>
              ))}
            </div>
            <div style={{background:"white",borderRadius:14,overflow:"hidden",border:"1px solid #e5e7eb",boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"#f5f3ff",borderBottom:"2px solid #c4b5fd"}}>
                      {["Staff Name","Month/Year","Amount","Bonus","Deduction","Method","Date"].map(h=>(
                        <th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,color:"#5b21b6",fontSize:11,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {salaries.slice(0,50).map((s,i)=>(
                      <tr key={s.id||i} style={{borderBottom:"1px solid #f3f4f6",background:i%2===0?"white":"#fafafa"}}>
                        <td style={{padding:"10px 12px",fontWeight:600,color:"#111827"}}>{s.staff_name||"—"}</td>
                        <td style={{padding:"10px 12px",color:"#374151",fontWeight:600}}>{s.month} {s.year}</td>
                        <td style={{padding:"10px 12px",fontWeight:700,color:"#0f5132"}}>PKR {Number(s.amount||0).toLocaleString()}</td>
                        <td style={{padding:"10px 12px",color:"#d97706"}}>{s.bonus>0?`+PKR ${Number(s.bonus).toLocaleString()}`:"—"}</td>
                        <td style={{padding:"10px 12px",color:"#dc2626"}}>{s.deduction>0?`-PKR ${Number(s.deduction).toLocaleString()}`:"—"}</td>
                        <td style={{padding:"10px 12px"}}><span style={{background:"#ede9fe",color:"#5b21b6",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700}}>{s.method||"Cash"}</span></td>
                        <td style={{padding:"10px 12px",color:"#9ca3af",fontSize:11}}>{s.created_at?new Date(s.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):""}</td>
                      </tr>
                    ))}
                    {salaries.length===0&&<tr><td colSpan={7} style={{textAlign:"center",padding:28,color:"#9ca3af"}}>No payroll records yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── EXPORT ── */}
        {tab==="export"&&(
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontWeight:800,fontSize:18,color:"#111827"}}>📥 Export Reports</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:14}}>
              {[
                {icon:"💰",title:"All Fee Records",desc:"Every fee payment — student, class, month, amount, method, receipt",fn:()=>dlCSV(fees.map(f=>({Student:f.student_name||f.student_id,Class:f.class||"","Month/Year":`${f.month} ${f.year}`,Amount:f.amount||0,Method:f.method||"Unpaid",Receipt:f.receipt_no||"","Date Recorded":f.created_at?.split("T")[0]||""})),`fee-records-${Date.now()}.csv`)},
                {icon:"⚠️",title:"Outstanding Fees",desc:"Students with unpaid or partial fee status",fn:()=>dlCSV(students.filter(s=>s.fees_status==="Unpaid"||s.fees_status==="Partial").map(s=>({Name:s.full_name||s.name,Class:s.class||"",Status:s.fees_status||"","Monthly Fee (PKR)":s.monthly_fee||0,ID:s.id})),`outstanding-fees-${Date.now()}.csv`)},
                {icon:"📊",title:"This Month's Collections",desc:`Fee payments for ${thisMonth} ${thisYear}`,fn:()=>dlCSV(fees.filter(f=>f.month===thisMonth&&f.year===thisYear).map(f=>({Student:f.student_name||f.student_id,Class:f.class||"",Amount:f.amount||0,Method:f.method||"",Receipt:f.receipt_no||""})),`fees-${thisMonth}-${thisYear}.csv`)},
                {icon:"💼",title:"Payroll History",desc:"All salary payments — staff, month, amount, bonus, deductions",fn:()=>dlCSV(salaries.map(s=>({Staff:s.staff_name||"",Month:s.month||"",Year:s.year||"",Amount:s.amount||0,Bonus:s.bonus||0,Deduction:s.deduction||0,Method:s.method||"Cash","Date Paid":s.payment_date||s.created_at?.split("T")[0]||""})),`payroll-${Date.now()}.csv`)},
                {icon:"📋",title:"Student Fee Summary",desc:"One row per student — name, class, fee status, monthly fee",fn:()=>dlCSV(students.map(s=>({Name:s.full_name||s.name,Class:s.class||"",Status:s.fees_status||"","Monthly Fee (PKR)":s.monthly_fee||0,ID:s.id})),`student-fee-summary-${Date.now()}.csv`)},
              ].map(exp=>(
                <div key={exp.title} style={{background:"white",borderRadius:14,padding:"18px 20px",border:"1px solid #e5e7eb",boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                  <div style={{fontSize:28,marginBottom:8}}>{exp.icon}</div>
                  <div style={{fontWeight:800,fontSize:14,color:"#111827",marginBottom:4}}>{exp.title}</div>
                  <div style={{fontSize:12,color:"#6b7280",marginBottom:14,lineHeight:1.5}}>{exp.desc}</div>
                  <button onClick={exp.fn} style={{background:"#0f5132",color:"white",border:"none",borderRadius:8,padding:"9px 18px",fontWeight:700,fontSize:13,cursor:"pointer"}}>📥 Download CSV</button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function AppWithAuth() {
  const [session,   setSession]   = React.useState(undefined);
  const [settings,  setSettings]  = React.useState(() => {
    try { const c = localStorage.getItem("qk_school_settings"); return c ? {...DEFAULT_SETTINGS, ...JSON.parse(c)} : DEFAULT_SETTINGS; }
    catch { return DEFAULT_SETTINGS; }
  });
  const [isParent,  setIsParent]  = React.useState(null);
  const [isTeacher, setIsTeacher] = React.useState(null); // teacher account data
  const [roleChecked, setRoleChecked] = React.useState(false);

  const isPortalRoute = () =>
    window.location.pathname === "/portal" ||
    window.location.pathname.startsWith("/portal/") ||
    window.location.hash === "#/portal";

  const [onPortal, setOnPortal] = React.useState(isPortalRoute());

  React.useEffect(()=>{
    supabase.from("school_settings").select("*").then(({data})=>{
      if(data?.length){
        const m={}; data.forEach(r=>{m[r.key]=r.value;});
        setSettings(prev=>{
          const changed = Object.keys(m).some(k=>String(prev[k])!==String(m[k]));
          if(!changed) return prev; // same ref → no re-render
          try{ localStorage.setItem("qk_school_settings", JSON.stringify(m)); }catch{}
          return {...prev,...m};
        });
      }
    });
    // onAuthStateChange fires INITIAL_SESSION immediately — no need for separate getSession()
    const {data:{subscription}} = supabase.auth.onAuthStateChange((_,s)=>{
      setSession(s||null);
      if(s){ window.history.pushState({},"","/portal"); setOnPortal(true); }
    });
    const onPop = () => setOnPortal(isPortalRoute());
    window.addEventListener("popstate", onPop);
    return ()=>{ subscription.unsubscribe(); window.removeEventListener("popstate", onPop); };
  },[]);

  const [adminRole, setAdminRole] = React.useState(null); // null | "admin" | "principal" | "vice_principal" | "accountant"

  // Check role whenever session changes
  React.useEffect(()=>{
    if(session === undefined) return;
    if(!session?.user?.email) { setIsParent(false); setIsTeacher(null); setAdminRole(null); setRoleChecked(true); return; }
    const email = session.user.email;
    Promise.all([
      supabase.from("parents").select("id").eq("email", email).maybeSingle(),
      supabase.from("teacher_accounts").select("*").eq("email", email).maybeSingle(),
      supabase.from("admin_accounts").select("role").eq("email", email).maybeSingle(),
    ]).then(([pRes, tRes, aRes])=>{
      setIsParent(!!pRes.data);
      setIsTeacher(tRes.data||null);
      setAdminRole(aRes.data?.role || "admin"); // default to full admin if no record
      setRoleChecked(true);
    }).catch(()=>{
      supabase.from("parents").select("id").eq("email", email).maybeSingle()
        .then(({data})=>{ setIsParent(!!data); setIsTeacher(null); setAdminRole("admin"); setRoleChecked(true); });
    });
  },[session]);

  const goPortal = React.useCallback(() => { window.history.pushState({},"","/portal"); setOnPortal(true); }, []);
  const goPublic = React.useCallback(() => { window.history.pushState({},"","/"); setOnPortal(false); }, []);
  const signOut  = React.useCallback(() => { supabase.auth.signOut(); setIsParent(null); setIsTeacher(null); setRoleChecked(false); setSession(null); setOnPortal(false); window.history.pushState({},"","/"); }, []);
  const handlePublicLogin = React.useCallback(s => { setSession(s); setOnPortal(true); window.history.pushState({},"","/portal"); }, []);

  const gradArr = (settings.header_gradient||"#1a6b3c,#0a4a24").split(",");
  const grad = `linear-gradient(135deg,${gradArr[0]},${gradArr[1]||"#0a4a24"})`;

  // Only spinner when a session exists but role isn't resolved yet
  if(session && !roleChecked) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:grad}}>
      <div style={{textAlign:"center"}}>
        <img src={SCHOOL_LOGO} style={{width:80,height:80,objectFit:"contain",marginBottom:16,opacity:.9}}/>
        <div style={{color:"rgba(255,255,255,0.8)",fontSize:14,fontWeight:600}}>Loading portal...</div>
        <div style={{width:40,height:40,border:"3px solid rgba(255,255,255,0.3)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite",margin:"16px auto 0"}}/>
      </div>
    </div>
  );

  if(onPortal) {
    if(session) {
      if(isParent)   return <SettingsContext.Provider value={settings}><ParentPortal  session={session} onSignOut={signOut}/></SettingsContext.Provider>;
      if(isTeacher)  return <SettingsContext.Provider value={settings}><TeacherPortal session={session} teacher={isTeacher} onSignOut={signOut}/></SettingsContext.Provider>;
      if(adminRole==="facility_manager") return <SettingsContext.Provider value={settings}><FacilityManagerPortal onSignOut={signOut}/></SettingsContext.Provider>;
      if(adminRole==="accountant") return <SettingsContext.Provider value={settings}><AccountantPortal session={session} onSignOut={signOut}/></SettingsContext.Provider>;
      return <QKAcademy session={session} onSignOut={signOut} adminRole={adminRole||"admin"}/>;
    }
    // session===undefined means still loading — show login page immediately, transition to portal once auth resolves
    return (
      <SettingsContext.Provider value={settings}>
        <PortalLoginPage onLogin={handlePublicLogin} settings={settings} onGoPublic={goPublic}/>
      </SettingsContext.Provider>
    );
  }

  return (
    <SettingsContext.Provider value={settings}>
      <SafePublicSite
        onLogin={handlePublicLogin}
        settings={settings}
        onPortalClick={goPortal}
      />
    </SettingsContext.Provider>
  );
}


// ─────────────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════
// NOTIFICATION BELL — shared across Admin, Teacher, Parent portals
// ══════════════════════════════════════════════════════════════════════
function NotificationBell({ userEmail, userRole, targetClass }) {
  const [notifications, setNotifications] = React.useState([]);
  const [reads,         setReads]         = React.useState(new Set());
  const [open,          setOpen]          = React.useState(false);
  const [loading,       setLoading]       = React.useState(true);
  const ref = React.useRef(null);

  // Persist clear timestamp per user in localStorage
  const clearKey = `notif_cleared_${userEmail}_${userRole}`;

  const load = async () => {
    setLoading(true);
    const clearedBefore = localStorage.getItem(clearKey) || null;

    const { data: notifs } = await supabase
      .from("notifications")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);

    const { data: readData } = await supabase
      .from("notification_reads")
      .select("notification_id")
      .eq("user_email", userEmail);

    const readSet = new Set((readData||[]).map(r => r.notification_id));

    // Filter by role, class, and cleared timestamp
    // target_class can also hold a staff email for personal notifications
    const filtered = (notifs||[]).filter(n => {
      const roleMatch = n.target_role === "all" || n.target_role === userRole;
      const classMatch = !n.target_class || n.target_class === "all" ||
        n.target_class === userEmail ||
        !targetClass || n.target_class === targetClass;
      const notCleared = !clearedBefore || new Date(n.created_at) > new Date(clearedBefore);
      return roleMatch && classMatch && notCleared;
    });

    setNotifications(filtered);
    setReads(readSet);
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  // Close when clicking outside
  React.useEffect(() => {
    const handler = (e) => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unreadCount = notifications.filter(n => !reads.has(n.id)).length;

  const [openNotif, setOpenNotif] = React.useState(null);
  const [notifSource, setNotifSource] = React.useState(null); // full linked record
  const [loadingSource, setLoadingSource] = React.useState(false);

  const openNotification = async (n) => {
    markRead(n.id);
    setOpenNotif(n);
    setNotifSource(null);
    if(n.source_id && n.source_type) {
      setLoadingSource(true);
      const table = n.source_type === "school_events" ? "school_events" : "announcements";
      const { data } = await supabase.from(table).select("*").eq("id", n.source_id).maybeSingle();
      setNotifSource(data||null);
      setLoadingSource(false);
    }
  };

  const markRead = async (id) => {
    if(!reads.has(id)) {
      try {
        await supabase.from("notification_reads").upsert(
          { notification_id: id, user_email: userEmail },
          { onConflict: "notification_id,user_email" }
        );
      } catch(e) { console.warn("markRead error:", e.message); }
      setReads(p => new Set([...p, id]));
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !reads.has(n.id));
    if(!unread.length) return;
    try {
      await supabase.from("notification_reads").upsert(
        unread.map(n => ({ notification_id: n.id, user_email: userEmail })),
        { onConflict: "notification_id,user_email" }
      );
    } catch(e) { console.warn("markAllRead error:", e.message); }
    setReads(new Set(notifications.map(n => n.id)));
  };

  const dismissOne = async (id, e) => {
    e.stopPropagation();
    setNotifications(p => p.filter(n => n.id !== id));
    try {
      await supabase.from("notification_reads").upsert(
        { notification_id: id, user_email: userEmail },
        { onConflict: "notification_id,user_email" }
      );
    } catch(err) { console.warn("dismissOne error:", err.message); }
    setReads(p => new Set([...p, id]));
  };

  const clearAll = async () => {
    if(!notifications.length) return;
    if(!window.confirm("Clear all notifications from this view?")) return;
    // Persist cleared timestamp so notifications don't return on reload
    const clearedAt = new Date().toISOString();
    localStorage.setItem(clearKey, clearedAt);
    try {
      const ids = notifications.map(n => n.id);
      await supabase.from("notification_reads").upsert(
        ids.map(id => ({ notification_id: id, user_email: userEmail })),
        { onConflict: "notification_id,user_email" }
      );
    } catch(e) { console.warn("clearAll error:", e.message); }
    setNotifications([]);
    setReads(new Set());
  };

  const TYPE_ICON = { event:"📅", assignment:"📝", announcement:"📣", alert:"⚠️", hifz:"📖", general:"🔔" };
  const TYPE_COLOR = { event:"#1a6b3c", assignment:"#2563eb", announcement:"#d97706", alert:"#dc2626", hifz:"#7c3aed", general:"#374151" };

  const timeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts)) / 1000;
    if(diff < 60) return "just now";
    if(diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if(diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/86400)}d ago`;
  };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      {/* Notification detail popup — shows full linked content */}
      {openNotif&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>{setOpenNotif(null);setNotifSource(null);}}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:560,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              {(()=>{
                const isEvent = openNotif.source_type==="school_events";
                const isAnn   = openNotif.source_type==="announcements";
                const CAT_COLORS = {News:"#0891b2",Event:"#7c3aed",Achievement:"#16a34a",Notice:"#d97706",Holiday:"#e11d48",General:"#1a6b3c"};
                const EV_COLORS  = {exam:"#dc2626",holiday:"#d97706",function:"#7c3aed",meeting:"#2563eb",assignment:"#0d9488",milestone:"#1a6b3c",general:"#374151"};
                const headerCol = isAnn ? (notifSource ? CAT_COLORS[notifSource.category]||"#1a6b3c" : "#1a6b3c") : isEvent ? (notifSource ? EV_COLORS[notifSource.event_type]||"#1a6b3c" : "#1a6b3c") : TYPE_COLOR[openNotif.type]||"#374151";
                const title   = notifSource?.title || openNotif.title;
                const body    = isAnn ? (notifSource?.body||"") : isEvent ? (notifSource?.description||"") : (openNotif.body||"");
                const dateStr = isEvent && notifSource?.date ? new Date(notifSource.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"}) : new Date(openNotif.created_at).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                const category = isAnn ? (notifSource?.category||"Announcement") : isEvent ? "Event" : "Notification";
                return (
                  <>
                    <div style={{background:`linear-gradient(135deg,${headerCol},${headerCol}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 24px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap"}}>
                            <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700}}>{TYPE_ICON[openNotif.type]||"🔔"} {category}</span>
                            {(notifSource?.target_class||openNotif.target_class)&&(notifSource?.target_class||openNotif.target_class)!=="all"&&(notifSource?.target_class||openNotif.target_class)!=="All"&&(
                              <span style={{background:"rgba(255,255,255,0.2)",color:"white",borderRadius:20,padding:"3px 10px",fontSize:11}}>{notifSource?.target_class||openNotif.target_class}</span>
                            )}
                          </div>
                          <div style={{fontWeight:900,fontSize:19,color:"white",lineHeight:1.3}}>{title}</div>
                          <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",marginTop:6}}>{dateStr}</div>
                        </div>
                        <button onClick={()=>{setOpenNotif(null);setNotifSource(null);}} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:34,height:34,color:"white",fontWeight:900,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                      </div>
                    </div>
                    <div style={{padding:"24px 26px"}}>
                      {loadingSource?(
                        <div style={{textAlign:"center",padding:32,color:"#9ca3af"}}>Loading full content...</div>
                      ):body?(
                        <div style={{fontSize:15,color:"#374151",lineHeight:1.9,whiteSpace:"pre-wrap",fontFamily:"Georgia,serif"}}>{body}</div>
                      ):(
                        <div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",textAlign:"center",padding:"20px 0"}}>No additional details.</div>
                      )}
                      {isEvent&&notifSource&&(
                        <div style={{marginTop:18,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          {[["📅 Date",new Date(notifSource.date).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})],
                            ...(notifSource.end_date?[["📅 Until",new Date(notifSource.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})]]:[]),
                            ["🏫 Class",notifSource.class==="all"?"All Classes":notifSource.class||"All Classes"],
                            ["👁 Visible to",notifSource.visible_to||"Everyone"],
                          ].map(([l,v])=>(
                            <div key={l} style={{background:"#f9fafb",borderRadius:8,padding:"9px 12px"}}>
                              <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,marginBottom:2}}>{l}</div>
                              <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>{v}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isAnn&&notifSource?.file_url&&(
                        <a href={notifSource.file_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:16,fontSize:13,color:"#7c3aed",fontWeight:700,textDecoration:"none",background:"#f5f3ff",borderRadius:10,padding:"10px 16px",border:"1px solid #ddd6fe"}}>📎 {notifSource.file_name||"View Attachment"}</a>
                      )}
                      <button onClick={()=>{setOpenNotif(null);setNotifSource(null);}} style={{display:"block",width:"100%",marginTop:20,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </Portal>
      )}
      {/* Bell button */}
      <button onClick={()=>{ setOpen(o=>!o); if(!open) load(); }}
        style={{ background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:10, padding:"6px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, position:"relative" }}>
        <span style={{ fontSize:18 }}>🔔</span>
        {unreadCount>0&&(
          <span style={{ position:"absolute", top:-6, right:-6, background:"#dc2626", color:"white", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid white" }}>
            {unreadCount>9?"9+":unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open&&(
        <div style={{ position:"absolute", right:0, top:"calc(100% + 8px)", width:340, maxHeight:480, background:"white", borderRadius:14, boxShadow:"0 8px 32px rgba(0,0,0,0.18)", zIndex:99999, overflow:"hidden", border:"1px solid #e5e7eb" }}>
          {/* Header */}
          <div style={{ padding:"14px 16px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontWeight:800, fontSize:14, color:"#111827" }}>🔔 Notifications {unreadCount>0&&<span style={{ background:"#dc2626", color:"white", borderRadius:10, padding:"1px 7px", fontSize:11, marginLeft:6 }}>{unreadCount}</span>}</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              {unreadCount>0&&<button onClick={markAllRead} style={{ background:"none", border:"none", color:"#2563eb", fontSize:12, fontWeight:600, cursor:"pointer" }}>Mark all read</button>}
              {notifications.length>0&&<button onClick={clearAll} style={{ background:"none", border:"1px solid #fca5a5", color:"#dc2626", fontSize:11, fontWeight:600, cursor:"pointer", padding:"2px 8px", borderRadius:6 }}>Clear all</button>}
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY:"auto", maxHeight:380 }}>
            {loading?(
              <div style={{ padding:32, textAlign:"center", color:"#9ca3af" }}>Loading...</div>
            ):notifications.length===0?(
              <div style={{ padding:32, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:32, marginBottom:8 }}>🔔</div>
                <div style={{ fontWeight:600 }}>No notifications yet</div>
              </div>
            ):notifications.map(n=>{
              const isRead = reads.has(n.id);
              const col = TYPE_COLOR[n.type]||"#374151";
              return (
                <div key={n.id} onClick={()=>{ openNotification(n); setOpen(false); }}
                  style={{ padding:"12px 16px", borderBottom:"1px solid #f9fafb", background:isRead?"white":"#f0fdf4", cursor:"pointer", display:"flex", gap:10, alignItems:"flex-start", transition:"background .1s" }}
                  onMouseEnter={e=>e.currentTarget.style.background=isRead?"#f9fafb":"#dcfce7"}
                  onMouseLeave={e=>e.currentTarget.style.background=isRead?"white":"#f0fdf4"}>
                  <div style={{ width:34, height:34, borderRadius:10, background:`${col}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                    {TYPE_ICON[n.type]||"🔔"}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:isRead?600:800, fontSize:13, color:"#111827", marginBottom:2 }}>{n.title}</div>
                    {n.body&&<div style={{ fontSize:11, color:"#6b7280", lineHeight:1.4, marginBottom:3 }}>{n.body.slice(0,80)}{n.body.length>80?"...":""}</div>}
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <span style={{ fontSize:10, color:"#9ca3af" }}>{timeAgo(n.created_at)}</span>
                      {n.target_class&&n.target_class!=="all"&&<span style={{ fontSize:10, background:"#f3f4f6", color:"#6b7280", borderRadius:4, padding:"1px 6px" }}>{n.target_class}</span>}
                      {!isRead&&<span style={{ width:6, height:6, borderRadius:"50%", background:"#1a6b3c", flexShrink:0 }}/>}
                    </div>
                  </div>
                  <button onClick={e=>dismissOne(n.id,e)} title="Dismiss"
                    style={{background:"none",border:"none",color:"#d1d5db",fontSize:16,cursor:"pointer",padding:"0 4px",flexShrink:0}}
                    onMouseEnter={e=>e.currentTarget.style.color="#dc2626"}
                    onMouseLeave={e=>e.currentTarget.style.color="#d1d5db"}>×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CALENDAR MANAGEMENT — Admin tab
// ══════════════════════════════════════════════════════════════════════
function CalendarManagement({ students }) {
  const EVENT_TYPES = [
    { id:"exam",       label:"Exam",            icon:"📝", color:"#dc2626" },
    { id:"holiday",    label:"Holiday",         icon:"🏖️", color:"#d97706" },
    { id:"function",   label:"School Function", icon:"🎓", color:"#7c3aed" },
    { id:"meeting",    label:"Meeting",         icon:"👥", color:"#2563eb" },
    { id:"assignment", label:"Assignment",      icon:"📋", color:"#0d9488" },
    { id:"milestone",  label:"Milestone",       icon:"🎯", color:"#1a6b3c" },
    { id:"general",    label:"General",         icon:"📅", color:"#374151" },
  ];

  const [events,     setEvents]     = React.useState([]);
  const [loading,    setLoading]    = React.useState(true);
  const [showForm,   setShowForm]   = React.useState(false);
  const [saving,     setSaving]     = React.useState(false);
  const [toast,      setToast]      = React.useState(null);
  const [viewMonth,  setViewMonth]  = React.useState(new Date());
  const [filterType, setFilterType] = React.useState("all");
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [editingEvent,  setEditingEvent]  = React.useState(false);
  const [announcementDraft, setAnnouncementDraft] = React.useState(null); // kept for legacy cleanup only
  const [publishingAnn, setPublishingAnn] = React.useState(false);
  const [waEvent, setWaEvent] = React.useState(null); // {title,date,class,type} for WA share
  const showToast = (msg,ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };

  const blank = { title:"", event_type:"general", date:"", end_date:"", class:"all", subject:"", description:"", visible_to:"all", auto_announce:true, created_by:"Admin" };
  const [form, setForm] = React.useState(blank);
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("school_events").select("*").order("date", { ascending:true });
    setEvents(data||[]);
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const save = async () => {
    if(!form.title||!form.date) return showToast("Title and date are required", false);
    setSaving(true);
    const et = EVENT_TYPES.find(e=>e.id===form.event_type);
    const { data, error } = await supabase.from("school_events").insert({
      ...form,
      end_date: form.end_date||null,
      subject: form.subject||null,
      color: et?.color||"#1a6b3c",
      created_by_role:"admin"
    }).select().single();

    if(error) { showToast("Error: "+error.message, false); setSaving(false); return; }

    // One notification per event — description included cleanly, no separate announcement row
    const _dateStr = new Date(form.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    const _notifParts = [
      `📅 ${_dateStr}`,
      form.end_date?`Until: ${new Date(form.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}`:null,
      form.class!=="all"?`For: ${form.class}`:null,
      form.description?(form.description.replace(/\s+/g," ").trim().slice(0,100)+(form.description.length>100?"…":"")):null,
    ].filter(Boolean);
    await supabase.from("notifications").insert({
      title: `${et?.icon||"📅"} ${form.title}`,
      body: _notifParts.join(" · "),
      type: "event",
      target_role: form.visible_to==="parents"?"parents":form.visible_to==="teachers"?"teachers":"all",
      target_class: form.class!=="all"?form.class:null,
      source_id: data.id, source_type:"school_events",
      created_by:"Admin"
    });

    // AI auto-announce: if ticked, generate announcement and show preview modal
    if(form.auto_announce) {
      const et2 = EVENT_TYPES.find(e=>e.id===form.event_type);
      const etLabel = et2?.label||"Event";
      const dateStr2 = new Date(form.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
      const endStr2  = form.end_date ? new Date(form.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"}) : null;
      // Build a sensible fallback body using event description
      let annBody = `Dear Parents,

We would like to inform you about the upcoming ${etLabel.toLowerCase()} at QK Academy.

📅 Date: ${dateStr2}${endStr2?" to "+endStr2:""}
${form.class!=="all"?`🏫 For: ${form.class}\n`:""}${form.description||""}

Please ensure your child is prepared accordingly.

Warm regards,
QK Academy Administration`;
      try {
        const prompt = `Write a warm, professional 2-3 paragraph school announcement for parents about this event at QK Academy:
Event: ${form.title}
Type: ${etLabel}
Date: ${dateStr2}${endStr2?" to "+endStr2:""}
For: ${form.class!=="all"?form.class:"All Students"}
${form.description?"Details: "+form.description:""}

Start with "Dear Parents," and end with "Warm regards, QK Academy Administration". Be concise and clear. Do NOT add extra commentary outside the announcement text.`;
        const aiText = await groqGenerate(prompt, 300);
        if(aiText&&aiText.length>40) annBody = aiText;
      } catch(e) { console.warn("AI failed, using fallback", e.message); }
      setAnnouncementDraft({
        title: `${et2?.icon||"📅"} ${form.title}`,
        content: annBody,
        target_class: form.class!=="all"?form.class:"All",
        event_id: data.id,
      });
      showToast("Event created! Review the announcement below.");
    } else {
      showToast("Event created!");
    }
    // Set WhatsApp share data
    setWaEvent({
      title: form.title,
      date: form.date,
      end_date: form.end_date||"",
      cls: form.class!=="all"?form.class:"All Students",
      type: form.event_type,
      desc: form.description||"",
      icon: EVENT_TYPES.find(e=>e.id===form.event_type)?.icon||"📅"
    });
    setShowForm(false); setForm(blank); load();
    setSaving(false);
  };

  const deleteEvent = async (id) => {
    if(!window.confirm("Delete this event?")) return;
    await supabase.from("school_events").delete().eq("id", id);
    await supabase.from("notifications").delete().eq("source_id", id);
    setEvents(p=>p.filter(e=>e.id!==id));
    showToast("Deleted");
  };

  // Calendar grid helpers
  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd   = new Date(viewMonth.getFullYear(), viewMonth.getMonth()+1, 0);
  const startDay   = monthStart.getDay(); // 0=Sun
  const daysInMonth = monthEnd.getDate();

  const monthEvents = events.filter(e => {
    const d = new Date(e.date);
    return d.getFullYear()===viewMonth.getFullYear() && d.getMonth()===viewMonth.getMonth()
      && (filterType==="all"||e.event_type===filterType);
  });

  const eventsOnDay = (day) => monthEvents.filter(e => new Date(e.date).getDate()===day);

  const ET = (id) => EVENT_TYPES.find(e=>e.id===id)||EVENT_TYPES[EVENT_TYPES.length-1];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {toast&&<Toast msg={toast.msg} type={toast.ok?"success":"error"}/>}

      {/* ── WhatsApp Event Share Banner ── */}
      {waEvent && (()=>{
        const {title,date,end_date,cls,type,desc,icon} = waEvent;
        const dateStr = new Date(date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
        const endStr  = end_date ? ` to ${new Date(end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}` : "";
        const msgLines = [
          `${icon} *${title}*`,
          `📅 Date: ${dateStr}${endStr}`,
          `🏫 For: ${cls}`,
          desc ? `\n${desc}` : "",
          `\n— QK Academy`
        ].filter(Boolean).join("\n");
        const broadcastLink = `https://wa.me/?text=${encodeURIComponent(msgLines)}`;
        return (
          <div style={{background:"#eff6ff",border:"1.5px solid #bfdbfe",borderRadius:14,padding:"14px 18px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",boxShadow:"0 2px 8px rgba(37,99,235,.08)"}}>
            <div style={{fontSize:24}}>{icon}</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:13,color:"#1d4ed8"}}>Event Created — Notify Parents on WhatsApp?</div>
              <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{title} · {dateStr} · {cls}</div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <a href={broadcastLink} target="_blank" rel="noopener noreferrer"
                style={{background:"#22c55e",color:"white",borderRadius:9,padding:"8px 16px",fontWeight:700,fontSize:13,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}>
                💬 Share Event
              </a>
              <button onClick={()=>setWaEvent(null)}
                style={{background:"white",color:"#6b7280",border:"1px solid #d1d5db",borderRadius:9,padding:"8px 12px",fontSize:12,cursor:"pointer"}}>
                Dismiss
              </button>
            </div>
          </div>
        );
      })()}

      {/* Event detail/edit modal */}
      {selectedEvent&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>{setSelectedEvent(null);setEditingEvent(false);}}>
            <div style={{background:"white",borderRadius:20,width:"100%",maxWidth:560,maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              {!editingEvent?(
                <>
                  {/* View mode */}
                  <div style={{background:`linear-gradient(135deg,${ET(selectedEvent.event_type).color},${ET(selectedEvent.event_type).color}cc)`,borderRadius:"20px 20px 0 0",padding:"20px 24px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                      <div style={{flex:1}}>
                        <span style={{background:"rgba(255,255,255,0.25)",color:"white",borderRadius:20,padding:"3px 12px",fontSize:11,fontWeight:700,display:"inline-block",marginBottom:8}}>
                          {ET(selectedEvent.event_type).icon} {ET(selectedEvent.event_type).label}
                        </span>
                        <div style={{fontWeight:900,fontSize:20,color:"white",lineHeight:1.3}}>{selectedEvent.title}</div>
                        <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginTop:6,display:"flex",gap:12,flexWrap:"wrap"}}>
                          <span>📅 {new Date(selectedEvent.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</span>
                          {selectedEvent.end_date&&<span>→ {new Date(selectedEvent.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}</span>}
                        </div>
                        <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
                          {selectedEvent.class&&selectedEvent.class!=="all"&&<span style={{background:"rgba(255,255,255,0.2)",color:"white",borderRadius:20,padding:"3px 10px",fontSize:11}}>{selectedEvent.class}</span>}
                          <span style={{background:"rgba(255,255,255,0.2)",color:"white",borderRadius:20,padding:"3px 10px",fontSize:11}}>👁 {selectedEvent.visible_to||"all"}</span>
                          
                          {selectedEvent.created_by&&<span style={{background:"rgba(255,255,255,0.15)",color:"white",borderRadius:20,padding:"3px 10px",fontSize:11}}>By: {selectedEvent.created_by}</span>}
                        </div>
                      </div>
                      <button onClick={()=>{setSelectedEvent(null);setEditingEvent(false);}} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:34,height:34,color:"white",fontWeight:900,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                    </div>
                  </div>
                  <div style={{padding:"22px 24px",display:"flex",flexDirection:"column",gap:14}}>
                    {selectedEvent.description?(
                      <div>
                        <div style={{fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",marginBottom:6}}>Description</div>
                        <div style={{fontSize:14,color:"#374151",lineHeight:1.8,whiteSpace:"pre-wrap"}}>{selectedEvent.description}</div>
                      </div>
                    ):(
                      <div style={{fontSize:13,color:"#9ca3af",fontStyle:"italic",textAlign:"center",padding:"12px 0"}}>No description added for this event.</div>
                    )}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,paddingTop:12,borderTop:"1px solid #f3f4f6"}}>
                      {[
                        ["📅 Date",new Date(selectedEvent.date).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})],
                        ["🏫 Class",selectedEvent.class==="all"?"All Classes":selectedEvent.class||"All Classes"],
                        ["👁 Visible to",selectedEvent.visible_to||"Everyone"],
                        ["📝 Type",ET(selectedEvent.event_type).label],
                      ].map(([l,v])=>(
                        <div key={l} style={{background:"#f9fafb",borderRadius:8,padding:"10px 12px"}}>
                          <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,marginBottom:2}}>{l}</div>
                          <div style={{fontSize:13,fontWeight:600,color:"#374151"}}>{v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:10,marginTop:4}}>
                      <button onClick={()=>setEditingEvent(true)} style={{flex:1,background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>✏️ Edit Event</button>
                      <button onClick={async()=>{ if(window.confirm(`Delete "${selectedEvent.title}"?`)){await deleteEvent(selectedEvent.id);setSelectedEvent(null);}}} style={{flex:1,background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>🗑 Delete</button>
                      <button onClick={()=>{setSelectedEvent(null);setEditingEvent(false);}} style={{flex:1,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Close</button>
                    </div>
                  </div>
                </>
              ):(
                <>
                  {/* Edit mode */}
                  <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",borderRadius:"20px 20px 0 0",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontWeight:800,fontSize:16,color:"white"}}>✏️ Edit Event</div>
                    <button onClick={()=>setEditingEvent(false)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:8,width:30,height:30,color:"white",fontWeight:900,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                  </div>
                  <div style={{padding:"20px 22px",display:"flex",flexDirection:"column",gap:14}}>
                    {[["TITLE","title","text"],["DATE","date","date"],["END DATE","end_date","date"],["DESCRIPTION","description","textarea"]].map(([lbl,k,t])=>(
                      <div key={k}>
                        <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>{lbl}</label>
                        {t==="textarea"?(
                          <textarea value={selectedEvent[k]||""} onChange={e=>setSelectedEvent(p=>({...p,[k]:e.target.value}))} rows={3}
                            style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
                        ):(
                          <input type={t} value={selectedEvent[k]||""} onChange={e=>setSelectedEvent(p=>({...p,[k]:e.target.value}))}
                            style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,boxSizing:"border-box"}}/>
                        )}
                      </div>
                    ))}
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>CLASS</label>
                        <select value={selectedEvent.class||"all"} onChange={e=>setSelectedEvent(p=>({...p,class:e.target.value}))}
                          style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13}}>
                          <option value="all">All Classes</option>
                          {ALL_CLASSES.map(c=><option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>VISIBLE TO</label>
                        <select value={selectedEvent.visible_to||"all"} onChange={e=>setSelectedEvent(p=>({...p,visible_to:e.target.value}))}
                          style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13}}>
                          {[["all","Everyone"],["parents","Parents only"],["teachers","Teachers only"],["admin","Admin only"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:10,marginTop:4}}>
                      <button onClick={async()=>{
                        setSaving(true);
                        const et2 = EVENT_TYPES.find(e=>e.id===selectedEvent.event_type)||EVENT_TYPES[EVENT_TYPES.length-1];
                        const {error} = await supabase.from("school_events").update({
                          title:selectedEvent.title, date:selectedEvent.date,
                          end_date:selectedEvent.end_date||null, description:selectedEvent.description,
                          class:selectedEvent.class, visible_to:selectedEvent.visible_to,
                        }).eq("id",selectedEvent.id);
                        if(error){setSaving(false);showToast("Error: "+error.message,false);return;}
                        // Sync notification body with updated event
                        const _ds = new Date(selectedEvent.date).toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
                        const _parts = [
                          `📅 ${_ds}`,
                          selectedEvent.end_date?`Until: ${new Date(selectedEvent.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}`:null,
                          selectedEvent.class!=="all"?`For: ${selectedEvent.class}`:null,
                          selectedEvent.description?(selectedEvent.description.replace(/\s+/g," ").trim().slice(0,100)+(selectedEvent.description.length>100?"…":"")):null,
                        ].filter(Boolean);
                        await supabase.from("notifications").update({
                          title: `${et2.icon} ${selectedEvent.title}`,
                          body: _parts.join(" · "),
                          target_class: selectedEvent.class!=="all"?selectedEvent.class:null,
                          target_role: selectedEvent.visible_to==="parents"?"parents":selectedEvent.visible_to==="teachers"?"teachers":"all",
                        }).eq("source_id", selectedEvent.id).eq("source_type","school_events");
                        setSaving(false);
                        showToast("Event updated!");
                        setEditingEvent(false);
                        load();
                      }} disabled={saving} style={{flex:1,background:saving?"#9ca3af":"#1a6b3c",color:"white",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                        {saving?"Saving...":"💾 Save Changes"}
                      </button>
                      <button onClick={()=>setEditingEvent(false)} style={{flex:1,background:"#f3f4f6",color:"#374151",border:"none",borderRadius:10,padding:"11px",fontWeight:700,fontSize:13,cursor:"pointer"}}>Cancel</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </Portal>
      )}


      {/* ── AI Announcement Draft Modal ── */}
      {announcementDraft&&(
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setAnnouncementDraft(null)}>
            <div style={{background:"white",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"calc(100vh - 48px)",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",padding:"18px 22px",color:"white"}}>
                <div style={{fontWeight:900,fontSize:16}}>✨ AI-Generated Announcement</div>
                <div style={{fontSize:12,opacity:.8,marginTop:2}}>AI wrote this from your event details — review, edit, then publish to parent portal</div>
              </div>
              <div style={{padding:"20px 22px",display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>TITLE</label>
                  <input value={announcementDraft.title} onChange={e=>setAnnouncementDraft(p=>({...p,title:e.target.value}))}
                    style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:14,fontWeight:600,boxSizing:"border-box"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>ANNOUNCEMENT TEXT <span style={{fontWeight:400,color:"#9ca3af"}}>(editable — AI-generated from your event + description)</span></label>
                  <textarea value={announcementDraft.content} onChange={e=>setAnnouncementDraft(p=>({...p,content:e.target.value}))} rows={10}
                    style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13,resize:"vertical",lineHeight:1.7,boxSizing:"border-box",fontFamily:"Georgia,serif"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,fontWeight:700,color:"#374151",display:"block",marginBottom:4}}>TARGET AUDIENCE</label>
                  <select value={announcementDraft.target_class||"All"} onChange={e=>setAnnouncementDraft(p=>({...p,target_class:e.target.value}))}
                    style={{width:"100%",padding:"9px 12px",border:"1.5px solid #d1d5db",borderRadius:8,fontSize:13}}>
                    <option value="All">All Classes (Everyone)</option>
                    {ALL_CLASSES.map(c=><option key={c} value={c}>{c} only</option>)}
                  </select>
                </div>
                <div style={{background:"#f0fdf4",borderRadius:8,padding:"10px 12px",fontSize:12,color:"#15803d",border:"1px solid #bbf7d0"}}>
                  ℹ️ This will save to the Announcements tab AND send a notification bell alert to parents/teachers. The event itself has already been saved.
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={async()=>{
                    setPublishingAnn(true);
                    const {data:annData, error} = await supabase.from("announcements").insert({
                      title: announcementDraft.title,
                      body: announcementDraft.content,
                      is_published: true,
                      target_class: announcementDraft.target_class||"All",
                      category: "Event",
                    }).select().single();
                    if(error){ showToast("Error: "+error.message, false); setPublishingAnn(false); return; }
                    if(annData?.id) {
                      const tc = announcementDraft.target_class;
                      const safeBody = (announcementDraft.content||"").replace(/\s+/g," ").trim().slice(0,120);
                      await supabase.from("notifications").delete().eq("source_id",annData.id).eq("source_type","announcements");
                      await supabase.from("notifications").insert({
                        title: announcementDraft.title,
                        body: safeBody,
                        type: "announcement",
                        target_role: "all",
                        target_class: (!tc||tc==="All"||tc==="All Classes") ? null : tc,
                        source_id: annData.id,
                        source_type: "announcements",
                        created_by: "Admin"
                      });
                    }
                    setPublishingAnn(false);
                    setAnnouncementDraft(null);
                    showToast("📣 Announcement published! Parents will see it in their News tab and bell.");
                  }} disabled={publishingAnn}
                    style={{flex:1,background:publishingAnn?"#9ca3af":"#1a6b3c",color:"white",border:"none",borderRadius:9,padding:"11px",fontWeight:800,fontSize:13,cursor:publishingAnn?"not-allowed":"pointer"}}>
                    {publishingAnn?"Publishing...":"📣 Publish Announcement"}
                  </button>
                  <button onClick={()=>setAnnouncementDraft(null)}
                    style={{padding:"11px 18px",background:"#f3f4f6",color:"#374151",border:"1px solid #d1d5db",borderRadius:9,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                    Skip
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:900, fontSize:20, color:"#111827" }}>📅 School Calendar</div>
          <div style={{ fontSize:13, color:"#6b7280", marginTop:2 }}>Manage school events, exams, holidays and functions</div>
        </div>
        <button onClick={()=>setShowForm(true)}
          style={{ background:"#1a6b3c", color:"white", border:"none", borderRadius:10, padding:"10px 22px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
          + Add Event
        </button>
      </div>

      {/* Add Event Form */}
      {showForm&&(
        <div style={{ background:"white", borderRadius:16, padding:24, border:"1.5px solid #d1fae5", boxShadow:"0 4px 20px rgba(0,0,0,0.08)" }}>
          <div style={{ fontWeight:800, fontSize:16, color:"#1a6b3c", marginBottom:16 }}>📅 New Event</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* Title */}
            <div style={{ gridColumn:"1/-1" }}>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>TITLE *</label>
              <input value={form.title} onChange={e=>setF("title",e.target.value)} placeholder="e.g. Annual Prize Ceremony"
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:14, boxSizing:"border-box" }}/>
            </div>
            {/* Event type */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>EVENT TYPE</label>
              <select value={form.event_type} onChange={e=>setF("event_type",e.target.value)}
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13 }}>
                {EVENT_TYPES.map(et=><option key={et.id} value={et.id}>{et.icon} {et.label}</option>)}
              </select>
            </div>
            {/* Visible to */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>VISIBLE TO</label>
              <select value={form.visible_to} onChange={e=>setF("visible_to",e.target.value)}
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13 }}>
                {[["all","Everyone"],["parents","Parents only"],["teachers","Teachers only"],["admin","Admin only"]].map(([v,l])=>(
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            {/* Date */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>DATE *</label>
              <input type="date" value={form.date} onChange={e=>setF("date",e.target.value)}
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13 }}/>
            </div>
            {/* End date */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>END DATE (optional)</label>
              <input type="date" value={form.end_date} onChange={e=>setF("end_date",e.target.value)}
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13 }}/>
            </div>
            {/* Class */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>CLASS (or All)</label>
              <select value={form.class} onChange={e=>setF("class",e.target.value)}
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13 }}>
                <option value="all">All Classes</option>
                {ALL_CLASSES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Description */}
            <div style={{ gridColumn:"1/-1" }}>
              <label style={{ fontSize:11, fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>DESCRIPTION</label>
              <textarea value={form.description} onChange={e=>setF("description",e.target.value)} rows={2}
                placeholder="Additional details..."
                style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #d1d5db", borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box" }}/>
            </div>
            {/* Auto-announce toggle */}
            <div style={{ gridColumn:"1/-1" }}>
              <button type="button" onClick={()=>setF("auto_announce",!form.auto_announce)}
                style={{ display:"flex", alignItems:"center", gap:10, background:form.auto_announce?"#f0fdf4":"white", border:`2px solid ${form.auto_announce?"#1a6b3c":"#d1d5db"}`, borderRadius:10, padding:"10px 16px", cursor:"pointer", width:"100%" }}>
                <div style={{ width:20, height:20, borderRadius:5, background:form.auto_announce?"#1a6b3c":"white", border:`2px solid ${form.auto_announce?"#1a6b3c":"#d1d5db"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {form.auto_announce&&<span style={{ color:"white", fontSize:13, fontWeight:900 }}>✓</span>}
                </div>
                <div style={{ textAlign:"left" }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1a6b3c" }}>📣 Auto-generate announcement for parents</div>
                  <div style={{ fontSize:11, color:"#6b7280" }}>AI writes a parent-friendly announcement using the event details + description — you review before publishing</div>
                </div>
              </button>
            </div>
          </div>
          <div style={{ display:"flex", gap:10, marginTop:16 }}>
            <button onClick={save} disabled={saving}
              style={{ background:saving?"#9ca3af":"#1a6b3c", color:"white", border:"none", borderRadius:9, padding:"10px 24px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              {saving?"Saving...":"Save Event"}
            </button>
            <button onClick={()=>{ setShowForm(false); setForm(blank); }}
              style={{ background:"#f3f4f6", color:"#374151", border:"1px solid #d1d5db", borderRadius:9, padding:"10px 24px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
        <button onClick={()=>setFilterType("all")}
          style={{ background:filterType==="all"?"#1a6b3c":"white", color:filterType==="all"?"white":"#374151", border:"1.5px solid #d1d5db", borderRadius:8, padding:"5px 14px", fontWeight:700, fontSize:12, cursor:"pointer" }}>
          All
        </button>
        {EVENT_TYPES.map(et=>(
          <button key={et.id} onClick={()=>setFilterType(et.id)}
            style={{ background:filterType===et.id?et.color:"white", color:filterType===et.id?"white":"#374151", border:`1.5px solid ${filterType===et.id?et.color:"#d1d5db"}`, borderRadius:8, padding:"5px 14px", fontWeight:700, fontSize:12, cursor:"pointer" }}>
            {et.icon} {et.label}
          </button>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ background:"white", borderRadius:16, padding:20, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
        {/* Month nav */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <button onClick={()=>setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth()-1, 1))}
            style={{ background:"#f3f4f6", border:"none", borderRadius:8, padding:"6px 14px", fontWeight:700, cursor:"pointer", fontSize:16 }}>‹</button>
          <div style={{ fontWeight:900, fontSize:18, color:"#111827" }}>
            {viewMonth.toLocaleString("default",{ month:"long", year:"numeric" })}
          </div>
          <button onClick={()=>setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth()+1, 1))}
            style={{ background:"#f3f4f6", border:"none", borderRadius:8, padding:"6px 14px", fontWeight:700, cursor:"pointer", fontSize:16 }}>›</button>
        </div>

        {/* Day headers */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:4 }}>
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
            <div key={d} style={{ textAlign:"center", fontSize:11, fontWeight:700, color:"#9ca3af", padding:"4px 0" }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
          {/* Empty cells before month start */}
          {Array.from({length:startDay}).map((_,i)=>(
            <div key={"e"+i} style={{ minHeight:80, background:"#fafafa", borderRadius:6 }}/>
          ))}
          {/* Day cells */}
          {Array.from({length:daysInMonth}).map((_,i)=>{
            const day = i+1;
            const dayEvents = eventsOnDay(day);
            const isToday = new Date().getDate()===day && new Date().getMonth()===viewMonth.getMonth() && new Date().getFullYear()===viewMonth.getFullYear();
            return (
              <div key={day} style={{ minHeight:80, background:isToday?"#f0fdf4":"white", border:isToday?"2px solid #1a6b3c":"1px solid #f3f4f6", borderRadius:6, padding:"4px 5px" }}>
                <div style={{ fontWeight:isToday?800:600, fontSize:12, color:isToday?"#1a6b3c":"#374151", marginBottom:3 }}>{day}</div>
                {dayEvents.slice(0,3).map(ev=>(
                  <div key={ev.id} title={ev.title}
                    style={{ background:ET(ev.event_type).color, color:"white", borderRadius:4, padding:"2px 5px", fontSize:9, fontWeight:700, marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"pointer" }}
                    onClick={e=>{ e.stopPropagation(); setSelectedEvent(ev); setEditingEvent(false); }}>
                    {ET(ev.event_type).icon} {ev.title}
                  </div>
                ))}
                {dayEvents.length>3&&<div style={{ fontSize:9, color:"#6b7280" }}>+{dayEvents.length-3} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming events list */}
      <div style={{ background:"white", borderRadius:16, padding:20, boxShadow:"0 2px 12px rgba(0,0,0,0.06)" }}>
        <div style={{ fontWeight:800, fontSize:15, color:"#111827", marginBottom:12 }}>📋 All Events</div>
        {loading?<div style={{ textAlign:"center", padding:32, color:"#9ca3af" }}>Loading...</div>
        :events.filter(e=>filterType==="all"||e.event_type===filterType).length===0?(
          <div style={{ textAlign:"center", padding:32, color:"#9ca3af" }}>No events yet. Click "+ Add Event" to create the first one.</div>
        ):(
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {events.filter(e=>filterType==="all"||e.event_type===filterType).map(ev=>{
              const et = ET(ev.event_type);
              const isPast = new Date(ev.date) < new Date();
              return (
                <div key={ev.id} onClick={()=>{ setSelectedEvent(ev); setEditingEvent(false); }} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", background:isPast?"#fafafa":"white", borderRadius:10, border:`1px solid ${isPast?"#f3f4f6":et.color+"30"}`, borderLeft:`4px solid ${isPast?"#d1d5db":et.color}`, opacity:isPast?0.7:1, cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.1)"}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                  <div style={{ width:38, height:38, borderRadius:10, background:`${et.color}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                    {et.icon}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#111827" }}>{ev.title}</div>
                    <div style={{ fontSize:11, color:"#6b7280", marginTop:2, display:"flex", gap:8, flexWrap:"wrap" }}>
                      <span>📅 {new Date(ev.date).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</span>
                      {ev.end_date&&<span>→ {new Date(ev.end_date).toLocaleDateString("en-GB",{day:"numeric",month:"long"})}</span>}
                      {ev.class&&ev.class!=="all"&&<span style={{ background:"#f3f4f6", borderRadius:4, padding:"1px 6px" }}>{ev.class}</span>}
                      <span style={{ background:`${et.color}15`, color:et.color, borderRadius:4, padding:"1px 6px", fontWeight:700 }}>{et.label}</span>
                      
                      {ev.visible_to&&ev.visible_to!=="all"&&<span style={{ background:"#eff6ff", color:"#1d4ed8", borderRadius:4, padding:"1px 6px" }}>👁 {ev.visible_to}</span>}
                    </div>
                    {ev.description&&<div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>{ev.description}</div>}
                  </div>
                  <button onClick={()=>deleteEvent(ev.id)}
                    style={{ background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:7, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── ACHIEVEMENT BOARD ─────────────────────────────────────────────
function AchievementBoard({ students, results, attendance, conductData, onShowToast }) {
  const [termMode,    setTermMode]   = React.useState("month");
  const [month,       setMonth]      = React.useState(0);
  const [classFilter, setClassFilter]= React.useState("All");
  const [publishing,  setPublishing] = React.useState(false);
  const [aiMsg,       setAiMsg]      = React.useState(null);
  const [aiLoading,   setAiLoading]  = React.useState(false);
  const [published,   setPublished]  = React.useState(false);

  const now = new Date();
  const monthOptions = Array.from({length:12},(_,n)=>{
    const d=new Date(now.getFullYear(),now.getMonth()-n,1);
    return {n,label:n===0?"This Month":d.toLocaleString("default",{month:"long",year:"numeric"})};
  });

  const TERM_MONTHS={term1:[8,9,10,11],term2:[0,1,2,3],term3:[4,5,6,7]};
  const TERM_LABELS={term1:"Term 1 (Autumn)",term2:"Term 2 (Spring)",term3:"Term 3 (Summer)"};

  const inPeriod=(dateStr)=>{
    if(!dateStr) return false;
    const d=new Date(dateStr); if(isNaN(d)) return false;
    if(termMode==="year") return true;
    if(termMode==="month"){const t=new Date(now.getFullYear(),now.getMonth()-month,1);return d>=t&&d<new Date(t.getFullYear(),t.getMonth()+1,1);}
    return (TERM_MONTHS[termMode]||[]).includes(d.getMonth());
  };

  const periodLabel=termMode==="year"?"Full Year":termMode==="month"?(monthOptions.find(m=>m.n===month)?.label||"This Month"):TERM_LABELS[termMode]||"";

  const allClasses=React.useMemo(()=>["All",...[...new Set(students.map(s=>s.class).filter(Boolean))].sort()],[students]);

  const calcConductScore=(sid)=>{
    const recs=(conductData||[]).filter(r=>String(r.student_id)===String(sid)&&inPeriod(r.date));
    const incidents=recs.filter(r=>["incident","warning","detention","suspension"].includes(r.type)).length;
    if(incidents===0) return 100; if(incidents===1) return 80; if(incidents===2) return 60;
    return Math.max(20,40-(incidents-3)*10);
  };

  const calcAttScore=(sid)=>{
    const recs=attendance.filter(a=>String(a.student_id)===String(sid)&&inPeriod(a.date));
    if(!recs.length) return null;
    return Math.round(recs.filter(a=>a.status==="P"||a.status==="Present").length/recs.length*100);
  };

  const HIFZ_SUBS=["Tajweed","Memorization","Revision","Nazra","Manzil","Hifz","Makharij"];
  const isHifzSub=sub=>HIFZ_SUBS.some(h=>sub?.toLowerCase().includes(h.toLowerCase()));

  const calcAcadScore=(sid)=>{
    const recs=results.filter(r=>String(r.student_id)===String(sid)&&!isHifzSub(r.subject));
    const pr=recs.filter(r=>inPeriod(r.created_at));
    const use=pr.length?pr:recs;
    if(!use.length) return null;
    return Math.round(use.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/use.length);
  };

  const calcHifzScore=(sid)=>{
    const recs=results.filter(r=>String(r.student_id)===String(sid)&&isHifzSub(r.subject)&&inPeriod(r.created_at));
    if(!recs.length) return null;
    return Math.round(recs.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/recs.length);
  };

  const buildScore=(s)=>{
    const isHifzStu=(s.stream||"").toLowerCase().includes("hifz")||(s.stream||"").toLowerCase().includes("both");
    const acad=calcAcadScore(s.id); const hifz=isHifzStu?calcHifzScore(s.id):null;
    const att=calcAttScore(s.id); const conduct=calcConductScore(s.id);
    const score=isHifzStu&&hifz!==null?(acad??0)*0.40+hifz*0.30+(att??0)*0.20+conduct*0.10:(acad??0)*0.55+(att??0)*0.25+conduct*0.20;
    return {s,acad,hifz,att,conduct,score:Math.round(score),isHifzStu};
  };

  const getRanked=(streamFilter)=>{
    let pool=streamFilter==="All"?students:students.filter(s=>{
      const st=(s.stream||"").toLowerCase();
      if(streamFilter==="School") return !st.includes("hifz");
      if(streamFilter==="Hifz") return st.includes("hifz")&&!st.includes("both")&&!st.includes("school");
      if(streamFilter==="Both") return st.includes("both")||(st.includes("hifz")&&st.includes("school"));
      return true;
    });
    if(classFilter!=="All") pool=pool.filter(s=>s.class===classFilter);
    return pool.map(buildScore).filter(x=>x.score>0).sort((a,b)=>b.score-a.score).slice(0,3);
  };

  const STREAM_CONFIGS=[
    {id:"School",label:"School Stream", icon:"📚",color:"#2563eb",bg:"#eff6ff",border:"#bfdbfe"},
    {id:"Hifz",  label:"Hifz Stream",  icon:"📖",color:"#7c3aed",bg:"#f5f3ff",border:"#ddd6fe"},
    {id:"Both",  label:"School + Hifz",icon:"🌟",color:"#1a6b3c",bg:"#f0fdf4",border:"#bbf7d0"},
    {id:"All",   label:"Overall Best", icon:"🏆",color:"#b45309",bg:"#fffbeb",border:"#fde68a"},
  ];
  const MEDALS=["🥇","🥈","🥉"];

  const allTop3=(()=>{
    const pool=classFilter==="All"?students:students.filter(s=>s.class===classFilter);
    return pool.map(buildScore).filter(x=>x.score>0).sort((a,b)=>b.score-a.score).slice(0,3);
  })();

  const resetAi=()=>{setAiMsg(null);setPublished(false);};

  const generateAiMessage=async(top)=>{
    setAiLoading(true);
    try {
      const names=top.map((x,i)=>`${i+1}. ${x.s.full_name||x.s.name} (Score: ${x.score}%)`).join("\n");
      const msg = await groqGenerate(`Write a warm, motivating 2-3 sentence school announcement congratulating these top 3 students of the month at an Islamic school. Encourage all students too. Keep under 80 words.\n${names}`, 150);
      setAiMsg(msg||"Congratulations to our outstanding students!");
    } catch{ setAiMsg("Congratulations to our top performers this month! Your dedication and hard work inspire us all. Keep striving for excellence!"); }
    setAiLoading(false);
  };

  const publishAnnouncement=async(top)=>{
    if(!aiMsg) return; setPublishing(true);
    const body=`${aiMsg}\n\nTop Students – ${periodLabel}:\n${top.map((x,i)=>`${MEDALS[i]} ${x.s.full_name||x.s.name} (Class ${x.s.class})`).join("\n")}`;
    const {error}=await supabase.from("announcements").insert({
      title:`🏆 Students of the Month – ${periodLabel}`,body,category:"Achievement",target_class:"All",is_published:true,
    });
    setPublishing(false);
    if(!error){setPublished(true);onShowToast?.("Achievement announcement published!","success");}
    else onShowToast?.("Error: "+error.message,"error");
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:17,color:C.primary}}>{"🏆"} Achievement Board</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Auto-ranked by Academics {"·"} Hifz {"·"} Attendance {"·"} Conduct</div>
        </div>
        <button onClick={()=>{resetAi();generateAiMessage(allTop3);}} disabled={aiLoading||!allTop3.length}
          style={{background:aiLoading?"#9ca3af":"#7c3aed",color:"white",border:"none",borderRadius:9,padding:"7px 16px",fontWeight:700,fontSize:12,cursor:aiLoading?"not-allowed":"pointer"}}>
          {aiLoading?"✨ Generating...":"✨ AI Message"}
        </button>
      </div>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
        {[{id:"month",label:"Month"},{id:"term1",label:"Term 1"},{id:"term2",label:"Term 2"},{id:"term3",label:"Term 3"},{id:"year",label:"Full Year"}].map(t=>(
          <button key={t.id} onClick={()=>{setTermMode(t.id);resetAi();}}
            style={{background:termMode===t.id?C.primary:"#f3f4f6",color:termMode===t.id?"white":"#374151",border:"none",borderRadius:8,padding:"5px 12px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
            {t.label}
          </button>
        ))}
        {termMode==="month"&&(
          <select value={month} onChange={e=>{setMonth(Number(e.target.value));resetAi();}}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"5px 10px",fontSize:12}}>
            {monthOptions.map(m=><option key={m.n} value={m.n}>{m.label}</option>)}
          </select>
        )}
        <select value={classFilter} onChange={e=>{setClassFilter(e.target.value);resetAi();}}
          style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"5px 10px",fontSize:12}}>
          {allClasses.map(c=><option key={c} value={c}>{c==="All"?"All Classes":c}</option>)}
        </select>
        <span style={{fontSize:11,fontWeight:700,background:C.primaryPale,color:C.primary,borderRadius:8,padding:"4px 10px",border:`1px solid ${C.primaryMid}`}}>
          {periodLabel}{classFilter!=="All"?` · ${classFilter}`:""}
        </span>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:14}}>
        {STREAM_CONFIGS.map(cfg=>{
          const ranked=getRanked(cfg.id);
          return (
            <div key={cfg.id} style={{background:"white",borderRadius:14,border:`1.5px solid ${cfg.border}`,overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
              <div style={{background:cfg.bg,padding:"10px 14px",borderBottom:`1px solid ${cfg.border}`,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{cfg.icon}</span>
                <div>
                  <div style={{fontWeight:800,fontSize:13,color:cfg.color}}>{cfg.label}</div>
                  <div style={{fontSize:10,color:C.muted}}>{periodLabel}</div>
                </div>
              </div>
              {ranked.length===0?(
                <div style={{padding:"24px 14px",textAlign:"center",color:"#9ca3af",fontSize:12}}>No data for this filter & period</div>
              ):(
                <div style={{padding:"10px 14px",display:"flex",flexDirection:"column",gap:10}}>
                  {ranked.map((entry,i)=>(
                    <div key={entry.s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:i===0?cfg.bg:"transparent",borderRadius:10,border:i===0?`1px solid ${cfg.border}`:"none"}}>
                      <span style={{fontSize:i===0?22:18,flexShrink:0}}>{MEDALS[i]}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.s.full_name||entry.s.name}</div>
                        <div style={{fontSize:10,color:C.muted,marginTop:2}}>Class {entry.s.class} {"·"} {entry.s.stream}</div>
                        <div style={{display:"flex",gap:4,marginTop:6,flexWrap:"wrap"}}>
                          {[["Acad",entry.acad,"#2563eb"],entry.isHifzStu?["Hifz",entry.hifz,"#7c3aed"]:null,["Att",entry.att,"#1a6b3c"],["Cond",entry.conduct,"#d97706"]].filter(Boolean).map(([lbl,val,col])=>(
                            <div key={lbl} style={{display:"flex",flexDirection:"column",alignItems:"center",minWidth:36}}>
                              <div style={{fontSize:9,color:"#9ca3af",fontWeight:600}}>{lbl}</div>
                              <div style={{height:4,width:36,background:"#f3f4f6",borderRadius:2,overflow:"hidden",marginTop:2}}>
                                <div style={{height:"100%",width:`${val??0}%`,background:col,borderRadius:2}}/>
                              </div>
                              <div style={{fontSize:9,fontWeight:800,color:col,marginTop:1}}>{val??"-"}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{textAlign:"center",flexShrink:0}}>
                        <div style={{fontWeight:900,fontSize:20,color:cfg.color}}>{entry.score}</div>
                        <div style={{fontSize:9,color:"#9ca3af"}}>score</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {aiMsg&&(
        <div style={{background:"#fffbeb",borderRadius:14,border:"1.5px solid #fde68a",padding:"16px 20px"}}>
          <div style={{fontWeight:700,fontSize:12,color:"#92400e",marginBottom:8}}>{"✨"} AI-Generated Announcement</div>
          <textarea value={aiMsg} onChange={e=>setAiMsg(e.target.value)} rows={4}
            style={{width:"100%",border:"1px solid #fde68a",borderRadius:8,padding:"8px 10px",fontSize:13,resize:"vertical",boxSizing:"border-box",background:"white"}}/>
          <div style={{marginTop:10,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            {published
              ?<span style={{color:"#16a34a",fontWeight:700,fontSize:13}}>{"✅"} Published to Announcements!</span>
              :<button onClick={()=>publishAnnouncement(allTop3)} disabled={publishing}
                  style={{background:publishing?"#9ca3af":C.primary,color:"white",border:"none",borderRadius:9,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:publishing?"not-allowed":"pointer"}}>
                  {publishing?"Publishing...":"📣 Publish to Website"}
                </button>
            }
            <button onClick={()=>{setAiMsg(null);setPublished(false);}}
              style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:9,padding:"8px 14px",fontWeight:600,fontSize:12,cursor:"pointer"}}>Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ── SCHOOL ANALYTICS TAB (merged: SchoolAnalytics + StudentTimeline + AIReport) ──
function SchoolAnalyticsTab({ students, results, attendance, fees, initialSubTab="overview" }) {
  const MONTHS_LIST=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const MONTHS_SHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const NOW=new Date();
  // Renders AI markdown-like report with bold, bullets and copy button
  const renderAiReport = (text, copiedKey, setCopiedMap) => {
    if(!text) return null;
    const copy = () => {
      navigator.clipboard?.writeText(text);
      setCopiedMap(p=>({...p,[copiedKey]:true}));
      setTimeout(()=>setCopiedMap(p=>({...p,[copiedKey]:false})),2500);
    };
    const lines = text.split("\n");
    const renderLine = (line, i) => {
      // Apply **bold** inline
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const content = parts.map((p,j)=>p.startsWith("**")&&p.endsWith("**")
        ? <strong key={j} style={{color:"#111827"}}>{p.slice(2,-2)}</strong>
        : <React.Fragment key={j}>{p}</React.Fragment>);
      const isBullet = line.trimStart().startsWith("•");
      const isHeader = line.startsWith("**") && line.includes(":**");
      if(isBullet) return <div key={i} style={{display:"flex",gap:8,marginBottom:3}}><span style={{color:"#7c3aed",fontWeight:700,flexShrink:0}}>•</span><span style={{lineHeight:1.6}}>{content}</span></div>;
      if(isHeader) return <div key={i} style={{fontWeight:800,fontSize:13,color:"#4c1d95",marginTop:10,marginBottom:4,borderBottom:"1px solid #e9d5ff",paddingBottom:2}}>{content}</div>;
      if(!line.trim()) return <div key={i} style={{height:6}}/>;
      return <div key={i} style={{marginBottom:2,lineHeight:1.6}}>{content}</div>;
    };
    return (
      <div style={{background:"#f5f3ff",borderRadius:10,padding:"16px 18px",fontSize:13,color:"#374151"}}>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
          <button onClick={copy} style={{background:copiedKey&&setCopiedMap?"#7c3aed":"#ede9fe",color:copiedKey&&setCopiedMap?"white":"#7c3aed",border:"none",borderRadius:7,padding:"4px 12px",fontWeight:700,fontSize:11,cursor:"pointer"}}>
            📋 Copy
          </button>
        </div>
        {lines.map((l,i)=>renderLine(l,i))}
      </div>
    );
  };
  const [aiCopied, setAiCopied] = React.useState({});
  const [selMonth,setSelMonth]=React.useState(NOW.getMonth());
  const [selYear,setSelYear]=React.useState(NOW.getFullYear());
  const [selStream,setSelStream]=React.useState("All");
  const [subTab,setSubTab]=React.useState(initialSubTab);
  const [selExam,setSelExam]=React.useState("All");
  const [showRiskModal,setShowRiskModal]=React.useState(false);

  // ── AI Readiness state ──
  const [aiLoading,setAiLoading]=React.useState(false);
  const [aiResults,setAiResults]=React.useState([]);
  const [aiError,setAiError]=React.useState("");
  const [aiFilter,setAiFilter]=React.useState("all");

  // ── Student Timeline state ──
  const [tlSearch,setTlSearch]=React.useState("");
  const [tlClass,setTlClass]=React.useState("All");
  const [selStudent,setSelStudent]=React.useState(null);
  const [tlAiLoading,setTlAiLoading]=React.useState(false);
  const [tlAiInsight,setTlAiInsight]=React.useState("");
  const [tlAiError,setTlAiError]=React.useState("");

  // ── At-Risk interactive state ──
  const [riskFilter,   setRiskFilter]   = React.useState("all"); // "all"|"critical"|"watch"
  const [riskInsights, setRiskInsights] = React.useState({});
  const [riskAiRunning,setRiskAiRunning]= React.useState(false);

  // ── Overview drill-down state ──
  const [gradeFilter,  setGradeFilter]  = React.useState(null); // grade string or null
  const [subjectFocus, setSubjectFocus] = React.useState(null); // subject string or null

  // ── Report Card AI state ──
  const [rcStudentId,  setRcStudentId]  = React.useState("");
  const [rcComment,    setRcComment]    = React.useState("");
  const [rcGenerating, setRcGenerating] = React.useState(false);
  const [rcTone,       setRcTone]       = React.useState("encouraging");
  const [rcCopied,     setRcCopied]     = React.useState(false);
  const [conductData,  setConductData]  = React.useState([]);
  const [conductLoaded,setConductLoaded]= React.useState(false);

  // ── AI Insights state ──
  const [aiInsightClass,  setAiInsightClass] = React.useState("All");
  const [healthReport,    setHealthReport]   = React.useState("");
  const [healthLoading,   setHealthLoading]  = React.useState(false);
  const [healthError,     setHealthError]    = React.useState("");
  const [subjAiReport,    setSubjAiReport]   = React.useState("");
  const [subjAiLoading,   setSubjAiLoading]  = React.useState(false);
  const [warnAiReport,    setWarnAiReport]   = React.useState("");
  const [warnAiLoading,   setWarnAiLoading]  = React.useState(false);
  const [profStudentId,   setProfStudentId]  = React.useState("");
  const [profReport,      setProfReport]     = React.useState("");
  const [profLoading,     setProfLoading]    = React.useState(false);

  const allClasses=["All",...[...new Set(students.map(s=>s.class||s["class"]).filter(Boolean))].sort()];
  const allStreams=[...new Set(students.map(s=>s.stream).filter(Boolean))].sort();
  const COLORS=["#1a6b3c","#2563eb","#7c3aed","#d97706","#0891b2","#dc2626","#0d9488","#ea580c"];
  const HIFZ_SUBS=["Tajweed","Memorization","Revision","Nazra","Manzil","Hifz","Makharij"];
  const isHifzSub=sub=>HIFZ_SUBS.some(h=>sub?.toLowerCase().includes(h.toLowerCase()));
  const normAtt=s=>s==="P"||s==="Present"?"P":s==="A"||s==="Absent"?"A":"L";

  const allExams=React.useMemo(()=>["All",...[...new Set(results.map(r=>r.exam_name).filter(Boolean))].sort()],[results]);

  const attForStudent=React.useCallback((id)=>{
    if(Array.isArray(attendance)){
      const recs=attendance.filter(a=>String(a.student_id)===String(id));
      const p=recs.filter(a=>normAtt(a.status)==="P").length;
      return {present:p,total:recs.length,pct:recs.length?Math.round(p/recs.length*100):0};
    }
    const dates=Object.keys(attendance||{});
    const p=dates.filter(d=>(attendance||{})[d]?.[id]==="P").length;
    return {present:p,total:dates.length,pct:dates.length?Math.round(p/dates.length*100):0};
  },[attendance]);

  const attForStudentMonth=React.useCallback((id,m,y)=>{
    if(Array.isArray(attendance)){
      const recs=attendance.filter(a=>String(a.student_id)===String(id)&&a.date&&a.date.startsWith(`${y}-${String(m+1).padStart(2,"0")}`));
      const p=recs.filter(a=>normAtt(a.status)==="P").length;
      return {present:p,total:recs.length,pct:recs.length?Math.round(p/recs.length*100):0};
    }
    const dates=Object.keys(attendance||{}).filter(d=>d.startsWith(`${y}-${String(m+1).padStart(2,"0")}`));
    const p=dates.filter(d=>(attendance||{})[d]?.[id]==="P").length;
    return {present:p,total:dates.length,pct:dates.length?Math.round(p/dates.length*100):0};
  },[attendance]);

  const subjectPerfData=React.useMemo(()=>{
    if(subTab!=="ai_insights") return [];
    const HIFZ=["Tajweed","Memorization","Revision","Nazra","Manzil","Hifz","Makharij"];
    const isH=s=>HIFZ.some(h=>s?.toLowerCase().includes(h.toLowerCase()));
    const subs=[...new Set(results.filter(r=>!isH(r.subject)).map(r=>r.subject).filter(Boolean))];
    return subs.map(sub=>{
      const recs=results.filter(r=>r.subject===sub&&r.total_marks>0);
      const avg=recs.length?Math.round(recs.reduce((a,r)=>a+(r.marks_obtained/r.total_marks*100),0)/recs.length):0;
      return {subject:sub.length>18?sub.slice(0,17)+"…":sub,avg,count:recs.length};
    }).sort((a,b)=>a.avg-b.avg);
  },[subTab,results]);

  const warnStudentsData=React.useMemo(()=>{
    if(subTab!=="ai_insights") return [];
    return students.map(s=>{
      const att=attForStudent(s.id);
      const sRes=results.filter(r=>String(r.student_id)===String(s.id)&&r.total_marks>0);
      const avgMark=sRes.length?Math.round(sRes.reduce((a,r)=>a+(r.marks_obtained/r.total_marks*100),0)/sRes.length):null;
      const flags=[];
      if(att.total>0&&att.pct<70) flags.push(`Attendance ${att.pct}%`);
      if(avgMark!==null&&avgMark<40) flags.push(`Avg marks ${avgMark}%`);
      return {...s,attPct:att.pct,attTotal:att.total,avgMark,flags};
    }).filter(s=>s.flags.length>0).sort((a,b)=>b.flags.length-a.flags.length);
  },[subTab,students,results,attForStudent]);

  const filtStu=selStream==="All"?students:students.filter(s=>(s.stream||"").includes(selStream));
  const filtResults=selExam==="All"?results:results.filter(r=>r.exam_name===selExam);

  const classStats=React.useMemo(()=>allClasses.filter(c=>c!=="All").map((cls,i)=>{
    const stu=filtStu.filter(s=>(s.class||s["class"])===cls);
    if(!stu.length) return null;
    const ids=new Set(stu.map(s=>String(s.id)));
    const cr=filtResults.filter(r=>ids.has(String(r.student_id)));
    const avgMarks=cr.length?Math.round(cr.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/cr.length):null;
    const attArr=stu.map(s=>attForStudentMonth(s.id,selMonth,selYear));
    const attFiltered=attArr.filter(a=>a.total>0);
    const avgAtt=attFiltered.length?Math.round(attFiltered.reduce((s,a)=>s+a.pct,0)/attFiltered.length):null;
    return {cls,avgMarks,avgAtt,count:stu.length,color:COLORS[i%COLORS.length]};
  }).filter(Boolean),[filtStu,filtResults,selMonth,selYear,attForStudentMonth,allClasses]);

  // Radar data: per-class per-subject averages
  const radarData=React.useMemo(()=>{
    const subjects=[...new Set(filtResults.filter(r=>!isHifzSub(r.subject)).map(r=>r.subject).filter(Boolean))].slice(0,7);
    const classes=allClasses.filter(c=>c!=="All");
    return subjects.map(sub=>{
      const row={subject:sub.length>10?sub.slice(0,9)+"…":sub};
      classes.forEach(cls=>{
        const stu=filtStu.filter(s=>(s.class||s["class"])===cls);
        const ids=new Set(stu.map(s=>String(s.id)));
        const recs=filtResults.filter(r=>r.subject===sub&&ids.has(String(r.student_id)));
        row[cls]=recs.length?Math.round(recs.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/recs.length):0;
      });
      return row;
    });
  },[filtStu,filtResults,allClasses]);

  const radarClasses=allClasses.filter(c=>c!=="All");

  // Exam trend: avg marks per class per exam over time
  const examTrendData=React.useMemo(()=>{
    const classes=radarClasses.slice(0,5);
    const exams=[...new Set(results.map(r=>r.exam_name).filter(Boolean))];
    return exams.map(exam=>{
      const pt={exam:exam.length>14?exam.slice(0,13)+"…":exam};
      classes.forEach(cls=>{
        const stuIds=new Set(filtStu.filter(s=>(s.class||s["class"])===cls).map(s=>String(s.id)));
        const res=filtResults.filter(r=>stuIds.has(String(r.student_id))&&r.exam_name===exam);
        const marks=res.map(r=>r.total_marks>0?Math.round(r.marks_obtained/r.total_marks*100):null).filter(v=>v!==null);
        pt[cls]=marks.length?Math.round(marks.reduce((a,b)=>a+b,0)/marks.length):null;
      });
      return pt;
    });
  },[filtStu,filtResults,radarClasses,results]);
  const trendClasses=radarClasses.slice(0,5);

  const gradeDistrib=React.useMemo(()=>{
    const scored=filtResults.filter(r=>filtStu.some(s=>String(s.id)===String(r.student_id))&&!isHifzSub(r.subject));
    const b={"A+ (90+)":0,"A (80-89)":0,"B (70-79)":0,"C (60-69)":0,"D (50-59)":0,"F (<50)":0};
    scored.forEach(r=>{
      const p=Math.round((r.marks_obtained/(r.total_marks||1))*100);
      if(p>=90)b["A+ (90+)"]++;else if(p>=80)b["A (80-89)"]++;else if(p>=70)b["B (70-79)"]++;
      else if(p>=60)b["C (60-69)"]++;else if(p>=50)b["D (50-59)"]++;else b["F (<50)"]++;
    });
    return Object.entries(b).map(([grade,count])=>({grade,count}));
  },[filtResults,filtStu]);

  const subjectIndex=React.useMemo(()=>{
    const subs=[...new Set(filtResults.filter(r=>!isHifzSub(r.subject)).map(r=>r.subject).filter(Boolean))];
    return subs.map(sub=>{
      const recs=filtResults.filter(r=>r.subject===sub&&filtStu.some(s=>String(s.id)===String(r.student_id)));
      if(!recs.length) return null;
      return {sub,avg:Math.round(recs.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/recs.length)};
    }).filter(Boolean).sort((a,b)=>a.avg-b.avg);
  },[filtResults,filtStu]);

  const riskStudents=React.useMemo(()=>{
    return filtStu.map(s=>{
      const att=attForStudentMonth(s.id,selMonth,selYear);
      const attAll=attForStudent(s.id);
      const res=filtResults.filter(r=>String(r.student_id)===String(s.id)&&!isHifzSub(r.subject));
      const avgMarks=res.length?Math.round(res.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/res.length):null;
      const flags=[];
      const attPct=att.total>0?att.pct:attAll.pct;
      if(attPct>0&&attPct<75) flags.push({l:`Att ${attPct}%`,s:"critical"});
      else if(attPct>0&&attPct<85) flags.push({l:`Att ${attPct}%`,s:"warn"});
      if(avgMarks!==null&&avgMarks<50) flags.push({l:`Score ${avgMarks}%`,s:"critical"});
      else if(avgMarks!==null&&avgMarks<65) flags.push({l:`Score ${avgMarks}%`,s:"warn"});
      return {...s,attPct,avgMarks,flags};
    }).filter(s=>s.flags.length>0).sort((a,b)=>b.flags.filter(f=>f.s==="critical").length-a.flags.filter(f=>f.s==="critical").length||b.flags.length-a.flags.length);
  },[filtStu,filtResults,selMonth,selYear,attForStudentMonth,attForStudent]);

  const trendMonths=React.useMemo(()=>{
    const rows=[];
    for(let i=5;i>=0;i--){
      let m=selMonth-i,y=selYear; if(m<0){m+=12;y--;}
      const attArr=Array.isArray(attendance)
        ?attendance.filter(a=>a.date&&a.date.startsWith(`${y}-${String(m+1).padStart(2,"0")}`))
        :Object.keys(attendance||{}).filter(d=>d.startsWith(`${y}-${String(m+1).padStart(2,"0")}`)).flatMap(d=>Object.entries((attendance||{})[d]||{}).map(([sid,st])=>({student_id:sid,status:st})));
      const pPct=attArr.length?Math.round(attArr.filter(a=>normAtt(a.status)==="P").length/attArr.length*100):null;
      const resMonth=results.filter(r=>r.exam_name?.toLowerCase().includes(MONTHS_LIST[m].toLowerCase())||r.created_at?.startsWith(`${y}-${String(m+1).padStart(2,"0")}`));
      const avgM=resMonth.length?Math.round(resMonth.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/resMonth.length):null;
      rows.push({month:MONTHS_SHORT[m],year:y,attPct:pPct,avgMarks:avgM});
    }
    return rows;
  },[attendance,results,selMonth,selYear]);

  const hifzByClass=React.useMemo(()=>allClasses.filter(c=>c!=="All").map(cls=>{
    const stu=filtStu.filter(s=>(s.class||s["class"])===cls);
    const ids=new Set(stu.map(s=>String(s.id)));
    const hr=results.filter(r=>ids.has(String(r.student_id))&&isHifzSub(r.subject));
    if(!hr.length) return null;
    return {cls,avg:Math.round(hr.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/hr.length)};
  }).filter(Boolean),[filtStu,results,allClasses]);

  const schoolAvgMarks=results.filter(r=>!isHifzSub(r.subject)).length?Math.round(results.filter(r=>!isHifzSub(r.subject)).reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/results.filter(r=>!isHifzSub(r.subject)).length):null;
  const schoolAvgAtt=React.useMemo(()=>{
    const all=students.map(s=>attForStudent(s.id)).filter(a=>a.total>0);
    return all.length?Math.round(all.reduce((s,a)=>s+a.pct,0)/all.length):null;
  },[students,attForStudent]);
  const hifzStudentCount=students.filter(s=>(s.stream||"").toLowerCase().includes("hifz")).length;

  // ── AI Readiness scoring ──
  const computeReadiness=(s)=>{
    const stream=(s.stream||"School Only").toLowerCase();
    const isHifz=stream.includes("hifz");
    const isCollege=stream.includes("college");
    const att=attForStudent(s.id);
    const res=results.filter(r=>String(r.student_id)===String(s.id));
    const schoolRes=res.filter(r=>!isHifzSub(r.subject));
    const hifzRes=res.filter(r=>isHifzSub(r.subject));
    const avgMarks=schoolRes.length?Math.round(schoolRes.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/schoolRes.length):null;
    const hifzAvg=hifzRes.length?Math.round(hifzRes.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/hifzRes.length):null;
    let score=0,maxScore=0;
    const factors=[];
    const attWeight=isHifz?20:30;
    if(att.total>0){
      score+=Math.min(100,att.pct)*attWeight/100; maxScore+=attWeight;
      if(att.pct<75) factors.push({l:`Att ${att.pct}% ⚠️`,s:"critical",tip:"Below BISE 75% threshold"});
      else if(att.pct<85) factors.push({l:`Att ${att.pct}%`,s:"warn",tip:"Could be better"});
      else factors.push({l:`Att ${att.pct}% ✓`,s:"ok",tip:"Good attendance"});
    }
    if(!isHifz&&avgMarks!==null){
      score+=avgMarks*40/100; maxScore+=40;
      const weakSubs=schoolRes.filter(r=>(r.marks_obtained/(r.total_marks||1))*100<50).map(r=>r.subject);
      if(weakSubs.length>0) factors.push({l:`Weak: ${[...new Set(weakSubs)].slice(0,2).join(", ")} ⚠️`,s:"critical",tip:"Below 50%"});
      if(avgMarks>=80) factors.push({l:`Avg ${avgMarks}% ✓`,s:"ok",tip:"Strong"});
      else if(avgMarks>=60) factors.push({l:`Avg ${avgMarks}%`,s:"warn",tip:"Moderate"});
      else factors.push({l:`Avg ${avgMarks}% ⚠️`,s:"critical",tip:"Concerning"});
    }
    if(isHifz&&hifzAvg!==null){
      score+=hifzAvg*50/100; maxScore+=50;
      const tajweed=hifzRes.find(r=>r.subject?.toLowerCase().includes("tajweed"));
      if(tajweed){const tp=Math.round((tajweed.marks_obtained/(tajweed.total_marks||1))*100);factors.push({l:`Tajweed ${tp}%${tp<60?" ⚠️":" ✓"}`,s:tp<60?"critical":"ok",tip:"Critical for Wifaq"});}
      else factors.push({l:`Hifz Avg ${hifzAvg}%`,s:hifzAvg>=70?"ok":"warn",tip:"Hifz score"});
    }
    if(schoolRes.length>=4){
      const recent=schoolRes.slice(-3).reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/3;
      const earlier=schoolRes.slice(0,3).reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/3;
      const diff=Math.round(recent-earlier);
      if(diff>=5) factors.push({l:`Trend ↑ +${diff}%`,s:"ok",tip:"Improving"});
      else if(diff<=-5) factors.push({l:`Trend ↓ ${diff}%`,s:"warn",tip:"Declining"});
    }
    const readinessPct=maxScore>0?Math.min(100,Math.round(score/maxScore*100)):null;
    const readinessLabel=readinessPct===null?"No data":readinessPct>=80?"Exam ready":readinessPct>=65?"On track":readinessPct>=50?"Needs focus":"High risk";
    const readinessColor=readinessPct===null?"#9ca3af":readinessPct>=80?C.primary:readinessPct>=65?"#2563eb":readinessPct>=50?C.accent:C.rose;
    const examType=isHifz?"Wifaq ul Madaris":isCollege?"BISE Board (FSc/FA)":"BISE Board (Matric)";
    return {name:s.full_name||s.name,cls:s.class||"",stream:s.stream||"School Only",readinessPct,readinessLabel,readinessColor,factors,examType,avgMarks,hifzAvg,attPct:att.pct};
  };

  const runAiReadiness=async()=>{
    setAiLoading(true); setAiError(""); setAiResults([]);
    const filterFn=aiFilter==="all"?()=>true:aiFilter==="hifz"?s=>(s.stream||"").toLowerCase().includes("hifz"):aiFilter==="college"?s=>(s.stream||"").toLowerCase().includes("college"):s=>!(s.stream||"").toLowerCase().includes("hifz")&&!(s.stream||"").toLowerCase().includes("college");
    const targetStudents=students.filter(filterFn).slice(0,30);
    const scored=targetStudents.map(computeReadiness).filter(r=>r.readinessPct!==null).sort((a,b)=>a.readinessPct-b.readinessPct);
    const atRisk=scored.filter(r=>r.readinessPct<65).slice(0,5);
    const strong=scored.filter(r=>r.readinessPct>=80).slice(-3);
    const enriched=await Promise.all([...atRisk,...strong].map(async(r)=>{
      try{
        const res=await fetch("https://api.groq.com/openai/v1/chat/completions",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+GROQ_KEY_PLACEHOLDER},body:JSON.stringify({model:"llama-3.3-70b-versatile",max_tokens:120,messages:[{role:"user",content:`Student: ${r.name} | Readiness: ${r.readinessPct}% | Risks: ${r.factors.filter(f=>f.s!=="ok").map(f=>f.l).join(", ")||"None"}
Write EXACTLY 2 sentences: biggest risk or strength, then one specific action for teacher this week.`}]})});
        const d=await res.json();
        return {...r,advice:d.choices?.[0]?.message?.content||""};
      }catch(e){return {...r,advice:""};}
    }));
    const enrichMap=Object.fromEntries(enriched.map(r=>[r.name,r.advice]));
    setAiResults(scored.map(r=>({...r,advice:enrichMap[r.name]||""})));
    setAiLoading(false);
  };

  // ── Timeline helpers ──
  const tlStudents=React.useMemo(()=>{
    let list=students;
    if(tlClass!=="All") list=list.filter(s=>(s.class||s["class"])===tlClass);
    if(tlSearch) list=list.filter(s=>(s.full_name||s.name||"").toLowerCase().includes(tlSearch.toLowerCase()));
    return list;
  },[students,tlClass,tlSearch]);

  const buildTimeline=(stu)=>{
    const sid=String(stu.id);
    const events=[];

    // ── Enrollment ──
    if(stu.enrolled_date||stu.enrolled) events.push({date:stu.enrolled_date||stu.enrolled,icon:"🎒",title:"Enrolled at QK Academy",detail:`Class ${stu.class||stu["class"]||"—"} · ${stu.stream||""}`,color:"#1a6b3c",bg:"#f0fdf4",tag:"Milestone"});

    // ── Exam results (group by exam name) ──
    const stuRes=results.filter(r=>String(r.student_id)===sid);
    const byExam={};
    stuRes.forEach(r=>{
      const key=r.exam_name||"Exam";
      if(!byExam[key]) byExam[key]={name:key,date:r.exam_date||r.created_at,subjects:[]};
      byExam[key].subjects.push(r);
    });
    Object.values(byExam).forEach(ex=>{
      const tot=ex.subjects.reduce((a,r)=>a+Number(r.marks_obtained||0),0);
      const max=ex.subjects.reduce((a,r)=>a+Number(r.total_marks||0),0);
      const pct=max>0?Math.round(tot/max*100):null;
      const grade=pct===null?"—":pct>=90?"A+":pct>=80?"A":pct>=70?"B+":pct>=60?"B":pct>=50?"C":"F";
      const icon=pct===null?"📝":pct>=75?"📗":pct>=50?"📙":"📕";
      events.push({date:ex.date,icon,title:`${ex.name} — Grade ${grade}`,
        detail:`${ex.subjects.length} subjects · ${tot}/${max} marks (${pct!==null?pct+"%":"?"})`,
        color:pct>=75?"#15803d":pct>=50?"#d97706":"#dc2626",
        bg:pct>=75?"#f0fdf4":pct>=50?"#fffbeb":"#fef2f2",tag:"Exam"});
    });

    // ── Attendance: flag every month with < 85% or > 0 absences ──
    const attByMonth={};
    attendance.filter(a=>String(a.student_id)===sid&&a.date).forEach(a=>{
      const key=a.date.slice(0,7);
      if(!attByMonth[key]) attByMonth[key]={month:key,P:0,A:0,L:0,LV:0,total:0};
      const s=a.status||a.statuscode||"";
      attByMonth[key].total++;
      if(s==="P"||s==="Present") attByMonth[key].P++;
      else if(s==="A"||s==="Absent") attByMonth[key].A++;
      else if(s==="L"||s==="Late") attByMonth[key].L++;
      else if(s==="LV"||s==="Leave") attByMonth[key].LV++;
    });
    Object.values(attByMonth).forEach(m=>{
      const pct=m.total>0?Math.round(((m.P+m.L)/m.total)*100):null;
      if(pct===null) return;
      const [y,mo]=m.month.split("-");
      const monthLabel=new Date(m.month+"-01").toLocaleDateString("en-PK",{month:"long",year:"numeric"});
      if(pct>=95){
        events.push({date:m.month+"-01",icon:"🌟",title:`Perfect attendance — ${monthLabel}`,detail:`${m.P}/${m.total} days · 0 absences`,color:"#15803d",bg:"#f0fdf4",tag:"Attendance"});
      } else if(pct<75){
        events.push({date:m.month+"-01",icon:"⚠️",title:`Low attendance — ${monthLabel}`,detail:`${pct}% · ${m.A} absent, ${m.L} late`,color:"#dc2626",bg:"#fef2f2",tag:"Attendance"});
      } else if(m.A>=3){
        events.push({date:m.month+"-01",icon:"📅",title:`Attendance — ${monthLabel}`,detail:`${pct}% · ${m.A} absences`,color:"#d97706",bg:"#fffbeb",tag:"Attendance"});
      }
    });

    // ── Fee payments ──
    const stuFees=Array.isArray(fees)?fees.filter(f=>String(f.student_id)===sid):[];
    stuFees.filter(f=>f.method&&f.method!=="Unpaid").forEach(f=>{
      events.push({date:f.paid_date||f.due_date||f.created_at,icon:"💰",
        title:`Fee paid — ${f.month||""} ${f.year||""}`,
        detail:`PKR ${Number(f.amount||0).toLocaleString()} · ${f.method||""}`,
        color:"#0891b2",bg:"#f0f9ff",tag:"Finance"});
    });
    stuFees.filter(f=>!f.method||f.method==="Unpaid").forEach(f=>{
      events.push({date:f.due_date||f.created_at,icon:"🔴",
        title:`Fee overdue — ${f.month||""} ${f.year||""}`,
        detail:`PKR ${Number(f.amount||0).toLocaleString()} unpaid`,
        color:"#dc2626",bg:"#fef2f2",tag:"Finance"});
    });

    return events.filter(e=>e.date).sort((a,b)=>new Date(a.date||0)-new Date(b.date||0));
  };

  // Student score sparkline data
  const getStuScoreHistory=(sid)=>{
    return results.filter(r=>String(r.student_id)===String(sid)&&!isHifzSub(r.subject))
      .sort((a,b)=>new Date(a.exam_date||a.created_at||0)-new Date(b.exam_date||b.created_at||0))
      .slice(-8).map((r,i)=>({i,pct:Math.round((r.marks_obtained/(r.total_marks||1))*100),name:r.exam_name||r.subject||""}));
  };

  // Monthly attendance sparkline
  const getStuAttHistory=(sid)=>{
    return Array.from({length:6},(_,i)=>{
      let m=selMonth-5+i,y=selYear; if(m<0){m+=12;y--;}
      const a=attForStudentMonth(sid,m,y);
      return {month:MONTHS_SHORT[m],pct:a.total>0?a.pct:null};
    });
  };

  // ── Fetch conduct data when Report Card sub-tab opens ──
  React.useEffect(()=>{
    if(subTab!=="reportcard"||conductLoaded) return;
    supabase.from("student_conduct").select("student_id,type,description,date")
      .then(({data})=>{setConductData(data||[]);setConductLoaded(true);});
  },[subTab,conductLoaded]);

  // ── At-Risk AI batch insights ──
  const runRiskAI=async()=>{
    if(riskAiRunning||riskStudents.length===0) return;
    setRiskAiRunning(true);
    const batch=riskStudents.slice(0,10);
    const map={};
    await Promise.all(batch.map(async s=>{
      const prompt=`Student ${s.full_name||s.name}, Class ${s.class}: Attendance ${s.attPct}%, Score ${s.avgMarks!=null?s.avgMarks+"%":"N/A"}. Flags: ${s.flags.map(f=>f.l).join(", ")}.
Write exactly 2 sentences: first, explain the main risk clearly; second, one specific action for the teacher this week.`;
      try{
        const txt=await groqGenerate(prompt,100);
        map[String(s.id)]=txt||"";
      }catch(e){map[String(s.id)]="";}
    }));
    setRiskInsights(map);
    setRiskAiRunning(false);
  };

  // ── Report Card AI ──
  const runReportCardAI=async()=>{
    const stu=students.find(s=>String(s.id)===rcStudentId);
    if(!stu) return;
    setRcGenerating(true); setRcComment(""); setRcCopied(false);
    const att=attForStudent(stu.id);
    const stuRes=results.filter(r=>String(r.student_id)===String(stu.id)&&!isHifzSub(r.subject));
    const hRes=results.filter(r=>String(r.student_id)===String(stu.id)&&isHifzSub(r.subject));
    const avgM=stuRes.length?Math.round(stuRes.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/stuRes.length):null;
    const hAvg=hRes.length?Math.round(hRes.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/hRes.length):null;
    const conduct=conductData.filter(c=>String(c.student_id)===String(stu.id));
    const weakSubs=stuRes.filter(r=>(r.marks_obtained/(r.total_marks||1))*100<60).map(r=>r.subject);
    const strongSubs=stuRes.filter(r=>(r.marks_obtained/(r.total_marks||1))*100>=80).map(r=>r.subject);
    const toneGuide={encouraging:"warm, encouraging, and positive",formal:"formal and professional",detailed:"detailed and analytical with specific observations"}[rcTone];
    const prompt=`Write a ${toneGuide} school report card comment for ${stu.full_name||stu.name} (Class ${stu.class||stu["class"]||"?"}, ${stu.stream||"School"}).
Data:
- Attendance: ${att.pct}% (${att.present} of ${att.total} days)
${avgM!==null?`- Academic average: ${avgM}%`:""}
${hAvg!==null?`- Islamic/Hifz average: ${hAvg}%`:""}
${strongSubs.length?`- Strong subjects: ${[...new Set(strongSubs)].slice(0,3).join(", ")}`:""}
${weakSubs.length?`- Needs improvement: ${[...new Set(weakSubs)].slice(0,3).join(", ")}`:""}
${conduct.filter(c=>c.type==="commendation").length?`- Commendations: ${conduct.filter(c=>c.type==="commendation").length}`:""}
${conduct.filter(c=>["warning","incident","detention"].includes(c.type)).length?`- Conduct concerns: ${conduct.filter(c=>["warning","incident","detention"].includes(c.type)).length} recorded`:""}
Write 3-4 sentences suitable for parents to read. Be specific. Do not begin with the student's name.`;
    try{
      const txt=await groqGenerate(prompt,220);
      setRcComment(txt||"Could not generate — please try again.");
    }catch(e){setRcComment("Error: "+e.message);}
    setRcGenerating(false);
  };

  // AI grade prediction for timeline (combined: Groq text + readiness badge)
  const runTlAI=async(stu)=>{
    setTlAiLoading(true); setTlAiInsight(""); setTlAiError("");
    const readiness=computeReadiness(stu);
    const sid=String(stu.id);
    const stuResults=results.filter(r=>String(r.student_id)===sid);
    const att=attForStudent(sid);
    const avgScore=stuResults.length>0?Math.round(stuResults.reduce((s,r)=>s+Number(r.marks_obtained||0)/Number(r.total_marks||1)*100,0)/stuResults.length):null;
    const recentResults=stuResults.slice(-6).map(r=>`${r.subject||"?"}: ${r.marks_obtained}/${r.total_marks}`).join(", ");
    const prompt=`Student: ${stu.full_name||stu.name}, Class: ${stu.class||stu["class"]||"?"}, Stream: ${stu.stream||"School"}
Attendance: ${att.pct}% (${att.present} present, ${att.total-att.present} absent)
Average Score: ${avgScore!==null?avgScore+"%":"no data"}
Recent Results: ${recentResults||"no results yet"}
Exam Readiness Score: ${readiness.readinessPct!==null?readiness.readinessPct+"%":"no data"} (${readiness.readinessLabel})

Respond in exactly this format:
**Predicted Grade:** [A+/A/B+/B/C/D/F] with brief 1-line justification
**Key Strengths:** [2-3 bullet points]
**Recommendations:** [2-3 actionable steps]

Under 150 words. Be honest but constructive.`;
    try{
      const text=await groqGenerate(prompt,400);
      if(text) setTlAiInsight({text,readiness});
      else setTlAiError("No response. Check Groq API key.");
    }catch(e){setTlAiError("AI unavailable: "+e.message);}
    setTlAiLoading(false);
  };

  // Shared card wrapper
  const W=({title,children,wide,accent})=>(
    <div style={{background:C.white,borderRadius:14,padding:"16px 18px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",gridColumn:wide?"1 / -1":undefined,borderLeft:accent?`3px solid ${accent}`:undefined}}>
      <div style={{fontWeight:800,fontSize:13,color:C.text,marginBottom:14}}>{title}</div>
      {children}
    </div>
  );

  const SubTabBtn=({id,label,icon})=>(
    <button onClick={()=>setSubTab(id)}
      style={{padding:"7px 16px",borderRadius:20,border:"none",cursor:"pointer",fontWeight:700,fontSize:12,
        background:subTab===id?C.primary:C.primaryPale,color:subTab===id?C.white:C.primary,transition:"all .2s"}}>
      {icon} {label}
    </button>
  );

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
    <HifzWifaqAnalytics students={students} compact />

      {/* Header + global controls */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:900,fontSize:17,color:C.text}}>📊 School Analytics</div>
          <div style={{fontSize:12,color:C.muted,marginTop:2}}>Interactive insights · AI-powered predictions · student timelines</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <select value={selMonth} onChange={e=>setSelMonth(+e.target.value)}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:12,background:C.white}}>
            {MONTHS_LIST.map((m,i)=><option key={i} value={i}>{m}</option>)}
          </select>
          <select value={selYear} onChange={e=>setSelYear(+e.target.value)}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:12,background:C.white}}>
            {[selYear-1,selYear,selYear+1].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select value={selStream} onChange={e=>setSelStream(e.target.value)}
            style={{border:`1.5px solid ${C.border}`,borderRadius:8,padding:"6px 10px",fontSize:12,background:C.white}}>
            {["All",...allStreams].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* KPI strip — each card drills into the relevant view */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:10}}>
        {[
          {label:"Total Students",val:students.length,color:"#1a6b3c",bg:"#f0fdf4",onClick:()=>setSubTab("timeline")},
          {label:"Hifz Students",val:hifzStudentCount,color:"#7c3aed",bg:"#f5f3ff",onClick:()=>setSubTab("hifz")},
          {label:"School Avg Score",val:schoolAvgMarks!==null?`${schoolAvgMarks}%`:"—",color:"#2563eb",bg:"#eff6ff",onClick:()=>setSubTab("compare")},
          {label:"School Avg Att.",val:schoolAvgAtt!==null?`${schoolAvgAtt}%`:"—",color:schoolAvgAtt>80?"#1a6b3c":schoolAvgAtt>65?"#d97706":"#dc2626",bg:"#f0fdf4",onClick:()=>setSubTab("trends")},
          {label:"Risk Flags",val:riskStudents.length,color:riskStudents.length>0?"#dc2626":"#1a6b3c",bg:riskStudents.length>0?"#fef2f2":"#f0fdf4",onClick:()=>setShowRiskModal(true)},
        ].map(k=>(
          <div key={k.label} onClick={k.onClick} title="Click for details"
            style={{background:k.bg,borderRadius:12,padding:"12px 10px",textAlign:"center",border:`1px solid ${k.color}22`,cursor:"pointer",transition:"transform .12s, box-shadow .12s"}}
            onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 4px 12px ${k.color}33`;}}
            onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="none";}}>
            <div style={{fontWeight:900,fontSize:20,color:k.color}}>{k.val}</div>
            <div style={{fontSize:10,color:C.muted,marginTop:3,fontWeight:600}}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Risk Flags drill-down — KPI only shows a count, this surfaces the full flagged list */}
      {showRiskModal && (
        <Portal>
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,overflowY:"auto"}} onClick={()=>setShowRiskModal(false)}>
            <div style={{background:"white",borderRadius:18,width:"100%",maxWidth:640,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 24px 60px rgba(0,0,0,0.3)"}} onClick={e=>e.stopPropagation()}>
              <div style={{background:"linear-gradient(135deg,#dc2626,#991b1b)",borderRadius:"18px 18px 0 0",padding:"18px 22px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontWeight:900,fontSize:16,color:"white"}}>⚠️ At-Risk Students</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,.85)",marginTop:2}}>{riskStudents.length} student{riskStudents.length!==1?"s":""} flagged for low attendance or scores</div>
                </div>
                <button onClick={()=>setShowRiskModal(false)} style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:9,width:32,height:32,color:"white",fontWeight:900,fontSize:15,cursor:"pointer"}}>✕</button>
              </div>
              <div style={{padding:"12px 20px 6px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid #fee2e2"}}>
                <span style={{fontSize:12,color:"rgba(255,255,255,.0)",pointerEvents:"none"}}> </span>
                <button onClick={runRiskAI} disabled={riskAiRunning||riskStudents.length===0}
                  style={{background:riskAiRunning?"#9ca3af":"white",color:riskAiRunning?"white":"#dc2626",border:"none",borderRadius:8,padding:"7px 16px",fontWeight:800,fontSize:12,cursor:riskAiRunning?"not-allowed":"pointer"}}>
                  {riskAiRunning?"🤖 Generating insights…":"✨ Generate AI Insights"}
                </button>
              </div>
              <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:8}}>
                {riskStudents.length===0 ? (
                  <div style={{textAlign:"center",color:"#9ca3af",padding:30}}>No students currently flagged — nice work! 🎉</div>
                ) : riskStudents.map(s=>(
                  <div key={s.id} style={{background:"#f9fafb",borderRadius:10,padding:"10px 14px",border:"1px solid #e5e7eb",display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <div style={{flex:1,minWidth:140}}>
                        <button onClick={()=>{setShowRiskModal(false);setSelStudent(s);setSubTab("timeline");}} style={{background:"none",border:"none",cursor:"pointer",fontWeight:700,fontSize:13,color:"#1a6b3c",padding:0,textDecoration:"underline",textAlign:"left"}}>{s.full_name||s.name}</button>
                        <div style={{fontSize:11,color:"#6b7280"}}>Class {s.class} · {s.stream}</div>
                      </div>
                      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                        {s.flags.map((f,i)=><span key={i} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:f.s==="critical"?"#fef2f2":"#fffbeb",color:f.s==="critical"?"#dc2626":"#d97706",border:`1px solid ${f.s==="critical"?"#fca5a5":"#fde68a"}`}}>{f.l}</span>)}
                      </div>
                    </div>
                    {riskInsights[String(s.id)]&&(
                      <div style={{background:"#eff6ff",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#1e40af",borderLeft:"3px solid #2563eb",lineHeight:1.6}}>
                        🤖 {riskInsights[String(s.id)]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Sub-tab navigation */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <SubTabBtn id="overview"  label="Overview"       icon="📊"/>
        <SubTabBtn id="compare"   label="Class compare"  icon="🏆"/>
        <SubTabBtn id="trends"    label="6-Month trends" icon="📈"/>
        <SubTabBtn id="hifz"      label="Hifz"           icon="📖"/>
        <SubTabBtn id="timeline"  label="Student timeline + AI" icon="✨"/>
        <SubTabBtn id="reportcard"  label="Report Card AI" icon="📝"/>
        <SubTabBtn id="ai_insights" label="AI Insights"   icon="🤖"/>
      </div>

      {/* ── OVERVIEW ── */}
      {subTab==="overview"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
          <W title={`📊 Grade Distribution (All Time) — click a bar`}>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={gradeDistrib} margin={{top:5,right:10,left:-10,bottom:5}}
                onClick={d=>{if(d?.activePayload?.[0])setGradeFilter(f=>f===d.activePayload[0].payload.grade?null:d.activePayload[0].payload.grade);}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="grade" tick={{fontSize:9}}/><YAxis tick={{fontSize:10}}/>
                <Tooltip cursor={{fill:"#f0fdf4"}}/>
                <Bar dataKey="count" name="Students" radius={[4,4,0,0]} style={{cursor:"pointer"}}>
                  {gradeDistrib.map((e,i)=>{const cols=["#1a6b3c","#2563eb","#0891b2","#d97706","#ea580c","#dc2626"];const dimmed=gradeFilter&&gradeFilter!==e.grade;return<Cell key={i} fill={cols[i]} opacity={dimmed?0.4:1}/>;})}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {gradeFilter&&(()=>{
              const [lo,hi]=gradeFilter==="A+ (90+)"?[90,101]:gradeFilter==="A (80-89)"?[80,90]:gradeFilter==="B (70-79)"?[70,80]:gradeFilter==="C (60-69)"?[60,70]:gradeFilter==="D (50-59)"?[50,60]:[0,50];
              const matched=filtStu.filter(s=>{const res=filtResults.filter(r=>String(r.student_id)===String(s.id)&&!isHifzSub(r.subject));if(!res.length)return false;const avg=Math.round(res.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/res.length);return avg>=lo&&avg<hi;});
              return(<div style={{marginTop:10,background:"#f9fafb",borderRadius:9,padding:"10px 12px",border:"1px solid #e5e7eb"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:800,color:C.text}}>{gradeFilter} — {matched.length} student{matched.length!==1?"s":""}</span>
                  <button onClick={()=>setGradeFilter(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.muted}}>✕ Clear</button>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:160,overflowY:"auto"}}>
                  {matched.map(s=><div key={s.id} onClick={()=>{setSelStudent(s);setSubTab("timeline");}} style={{fontSize:12,color:C.text,cursor:"pointer",padding:"4px 6px",borderRadius:5,display:"flex",justifyContent:"space-between"}} onMouseEnter={e=>e.currentTarget.style.background="#e8f5ee"} onMouseLeave={e=>e.currentTarget.style.background="none"}><span>{s.full_name||s.name}</span><span style={{color:C.muted}}>Class {s.class} →</span></div>)}
                </div>
              </div>);
            })()}
          </W>

          <W title="🎯 Subject Difficulty Index — click to see weakest students">
            {subjectIndex.length===0?<div style={{textAlign:"center",color:"#9ca3af",padding:20}}>No results data</div>:(
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {subjectIndex.slice(0,8).map(x=>(
                  <div key={x.sub} onClick={()=>setSubjectFocus(f=>f===x.sub?null:x.sub)} style={{cursor:"pointer",borderRadius:6,padding:"3px 4px",transition:"background .12s"}} onMouseEnter={e=>e.currentTarget.style.background="#f0fdf4"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,fontWeight:600,color:subjectFocus===x.sub?C.primary:"#374151",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:140}}>{x.sub}{subjectFocus===x.sub?" ▲":""}</span>
                      <span style={{fontSize:11,fontWeight:800,color:x.avg>=70?"#1a6b3c":x.avg>=50?"#d97706":"#dc2626"}}>{x.avg}%</span>
                    </div>
                    <div style={{height:6,background:"#f3f4f6",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${x.avg}%`,background:x.avg>=70?"#1a6b3c":x.avg>=50?"#d97706":"#dc2626",borderRadius:3}}/>
                    </div>
                  </div>
                ))}
                {subjectFocus&&(()=>{
                  const weak=filtStu.map(s=>{const r=filtResults.filter(rr=>String(rr.student_id)===String(s.id)&&rr.subject===subjectFocus);if(!r.length)return null;const pct=Math.round(r.reduce((a,rr)=>a+(rr.marks_obtained/(rr.total_marks||1))*100,0)/r.length);return{...s,pct};}).filter(Boolean).sort((a,b)=>a.pct-b.pct).slice(0,8);
                  return(<div style={{marginTop:8,background:"#fef2f2",borderRadius:9,padding:"10px 12px",border:"1px solid #fecaca"}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                      <span style={{fontSize:12,fontWeight:800,color:"#dc2626"}}>Weakest in {subjectFocus}</span>
                      <button onClick={e=>{e.stopPropagation();setSubjectFocus(null);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:C.muted}}>✕</button>
                    </div>
                    {weak.map(s=><div key={s.id} onClick={()=>{setSelStudent(s);setSubTab("timeline");}} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0",cursor:"pointer",color:C.text,borderBottom:"1px solid #fee2e2"}}><span>{s.full_name||s.name}</span><span style={{color:s.pct<50?"#dc2626":"#d97706",fontWeight:700}}>{s.pct}% →</span></div>)}
                  </div>);
                })()}
              </div>
            )}
          </W>

          <W title={`⚠️ At-Risk Students`}>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[["🔴 Critical","critical",riskStudents.filter(s=>s.flags.some(f=>f.s==="critical")).length,"#dc2626","#fef2f2"],
                ["🟡 Watch","watch",riskStudents.filter(s=>!s.flags.some(f=>f.s==="critical")).length,"#d97706","#fffbeb"],
                ["✅ On Track","ontrack",filtStu.length-riskStudents.length,"#1a6b3c","#f0fdf4"]].map(([l,key,v,col,bg])=>(
                <div key={l} onClick={()=>key!=="ontrack"&&setRiskFilter(riskFilter===key?"all":key)}
                  style={{background:riskFilter===key?bg:"#f9fafb",borderRadius:8,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",border:`1.5px solid ${riskFilter===key?col:col+"22"}`,cursor:key!=="ontrack"?"pointer":"default",transition:"all .15s"}}>
                  <span style={{fontSize:12,fontWeight:700,color:col}}>{l}</span>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <span style={{fontWeight:900,fontSize:18,color:col}}>{v}</span>
                    {key!=="ontrack"&&<span style={{fontSize:9,color:col,opacity:.7}}>{riskFilter===key?"▲ active":"click to filter"}</span>}
                  </div>
                </div>
              ))}
              {(riskFilter==="all"?riskStudents.slice(0,5):riskFilter==="critical"?riskStudents.filter(s=>s.flags.some(f=>f.s==="critical")):riskStudents.filter(s=>!s.flags.some(f=>f.s==="critical"))).map(s=>(
                <div key={s.id} onClick={()=>{setSelStudent(s);setSubTab("timeline");}}
                  style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px",border:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:8,cursor:"pointer",transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#f0fdf4"}
                  onMouseLeave={e=>e.currentTarget.style.background="#f9fafb"}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:12,color:C.text}}>{s.full_name||s.name}</div>
                    <div style={{fontSize:10,color:C.muted}}>Class {s.class} · {s.stream}</div>
                  </div>
                  <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                    {s.flags.map((f,i)=><span key={i} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:f.s==="critical"?"#fef2f2":"#fffbeb",color:f.s==="critical"?"#dc2626":"#d97706",border:`1px solid ${f.s==="critical"?"#fca5a5":"#fde68a"}`}}>{f.l}</span>)}
                    <span style={{fontSize:10,color:C.primary,fontWeight:700}}>View →</span>
                  </div>
                </div>
              ))}
              {riskStudents.length>0&&<button onClick={()=>setShowRiskModal(true)} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:7,padding:"5px 10px",fontSize:11,color:C.muted,cursor:"pointer",fontWeight:600}}>View all {riskStudents.length} flagged students + AI insights →</button>}
            </div>
          </W>

          {allStreams.length>1&&(
            <W title="🔀 Stream Comparison" wide>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:10}}>
                {allStreams.map(stream=>{
                  const stu=students.filter(s=>(s.stream||"").includes(stream));
                  if(!stu.length) return null;
                  const ids=new Set(stu.map(s=>String(s.id)));
                  const sr=results.filter(r=>ids.has(String(r.student_id))&&!isHifzSub(r.subject));
                  const avgM=sr.length?Math.round(sr.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/sr.length):null;
                  const attAll=stu.map(s=>attForStudent(s.id)).filter(a=>a.total>0);
                  const avgA=attAll.length?Math.round(attAll.reduce((s,a)=>s+a.pct,0)/attAll.length):null;
                  return (
                    <div key={stream} style={{background:"#f9fafb",borderRadius:10,padding:"12px 14px",border:"1px solid #e5e7eb"}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:8}}>{stream} <span style={{fontWeight:500,color:C.muted,fontSize:11}}>({stu.length})</span></div>
                      <div style={{display:"flex",gap:16}}>
                        {[["Avg Marks",avgM,"#2563eb"],["Avg Att.",avgA,"#1a6b3c"]].map(([lbl,val,col])=>(
                          <div key={lbl}><div style={{fontSize:10,color:C.muted}}>{lbl}</div>
                          <div style={{fontWeight:900,fontSize:20,color:col}}>{val!==null?`${val}%`:"—"}</div></div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </W>
          )}
        </div>
      )}

      {/* ── CLASS COMPARE ── */}
      {subTab==="compare"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Exam filter */}
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:12,fontWeight:700,color:C.muted}}>Filter by exam:</span>
            {allExams.map(e=>(
              <button key={e} onClick={()=>setSelExam(e)}
                style={{padding:"5px 14px",borderRadius:20,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
                  background:selExam===e?"#2563eb":"#eff6ff",color:selExam===e?"white":"#2563eb",transition:"all .2s"}}>
                {e}
              </button>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
            {/* Bar chart */}
            <W title="📊 Avg marks + attendance per class">
              {classStats.length===0?<div style={{textAlign:"center",color:"#9ca3af",padding:20}}>No data</div>:(
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={classStats} margin={{top:5,right:10,left:-10,bottom:5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                    <XAxis dataKey="cls" tick={{fontSize:10}}/><YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                    <Tooltip formatter={(v,n)=>[`${v}%`,n]}/><Legend wrapperStyle={{fontSize:11}}/>
                    <Bar dataKey="avgMarks" name="Avg Marks" radius={[4,4,0,0]}>
                      {classStats.map((e,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                    </Bar>
                    <Bar dataKey="avgAtt" name="Attendance" fill="#1a6b3c" radius={[4,4,0,0]} opacity={0.5}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </W>

            {/* Radar chart */}
            <W title="🕸 Subject radar — class vs class">
              {radarData.length===0?<div style={{textAlign:"center",color:"#9ca3af",padding:20}}>No subject data</div>:(
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={radarData} margin={{top:10,right:20,left:20,bottom:10}}>
                    <PolarGrid/>
                    <PolarAngleAxis dataKey="subject" tick={{fontSize:10}}/>
                    <PolarRadiusAxis angle={30} domain={[0,100]} tick={{fontSize:9}} tickFormatter={v=>`${v}`}/>
                    {radarClasses.slice(0,4).map((cls,i)=>(
                      <Radar key={cls} name={cls} dataKey={cls} stroke={COLORS[i%COLORS.length]} fill={COLORS[i%COLORS.length]} fillOpacity={0.15}/>
                    ))}
                    <Legend wrapperStyle={{fontSize:11}}/><Tooltip formatter={v=>[`${v}%`]}/>
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </W>

            {/* Exam trend chart */}
            <W title="📈 Score trend across exams — per class" wide>
              {examTrendData.length<2?<div style={{textAlign:"center",color:"#9ca3af",padding:20,fontSize:13}}>Need at least 2 exams for trend data.</div>:(
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={examTrendData} margin={{top:5,right:10,left:-10,bottom:5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                    <XAxis dataKey="exam" tick={{fontSize:9}} interval={0}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                    <Tooltip formatter={(v,n)=>[v!==null?`${v}%`:"—",n]}/>
                    <Legend wrapperStyle={{fontSize:11}}/>
                    {trendClasses.map((cls,i)=>(
                      <Line key={cls} type="monotone" dataKey={cls} name={cls}
                        stroke={COLORS[i%COLORS.length]} strokeWidth={2} dot={{r:4}}
                        connectNulls={true}/>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </W>

            {/* Class ranking table */}
            <W title="🏆 Class rankings" wide>
              {classStats.length===0?<div style={{textAlign:"center",color:"#9ca3af",padding:20}}>No data</div>:(
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{borderBottom:"2px solid #e5e7eb"}}>
                        {["Rank","Class","Students","Avg Marks","Avg Att."].map(h=>(
                          <th key={h} style={{padding:"8px 10px",textAlign:"left",fontWeight:700,color:C.muted,fontSize:11}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...classStats].filter(d=>d.avgMarks!==null).sort((a,b)=>(b.avgMarks||0)-(a.avgMarks||0)).map((d,i)=>{
                        const medal=i===0?"🥇":i===1?"🥈":i===2?"🥉":"";
                        return (
                          <tr key={d.cls} style={{borderBottom:"1px solid #f3f4f6",background:i<3?"#fafffe":"white"}}>
                            <td style={{padding:"10px 10px",fontWeight:700,color:C.muted}}>{medal||`#${i+1}`}</td>
                            <td style={{padding:"10px 10px",fontWeight:700,color:C.text}}>{d.cls}</td>
                            <td style={{padding:"10px 10px",color:C.muted}}>{d.count}</td>
                            <td style={{padding:"10px 10px"}}>
                              <span style={{fontWeight:800,color:d.avgMarks>=70?"#1a6b3c":d.avgMarks>=50?"#d97706":"#dc2626"}}>{d.avgMarks}%</span>
                              <div style={{height:4,background:"#f3f4f6",borderRadius:2,marginTop:4,width:80,overflow:"hidden"}}>
                                <div style={{height:"100%",width:`${d.avgMarks}%`,background:d.avgMarks>=70?"#1a6b3c":d.avgMarks>=50?"#d97706":"#dc2626",borderRadius:2}}/>
                              </div>
                            </td>
                            <td style={{padding:"10px 10px",fontWeight:600,color:(d.avgAtt??0)>=80?"#1a6b3c":(d.avgAtt??0)>=65?"#d97706":"#dc2626"}}>{d.avgAtt??"-"}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </W>
          </div>
        </div>
      )}

      {/* ── TRENDS ── */}
      {subTab==="trends"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
          <W title="📈 6-Month Performance Trend" wide>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendMonths} margin={{top:5,right:20,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="month" tick={{fontSize:11}}/><YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                <Tooltip formatter={(v,n)=>[v!==null?`${v}%`:"—",n]}/>
                <Legend wrapperStyle={{fontSize:11}}/>
                <Line type="monotone" dataKey="attPct" name="Attendance %" stroke="#1a6b3c" strokeWidth={2.5} dot={{r:4,fill:"#1a6b3c"}} connectNulls/>
                <Line type="monotone" dataKey="avgMarks" name="Avg Marks %" stroke="#2563eb" strokeWidth={2} strokeDasharray="5 3" dot={{r:4,fill:"#2563eb"}} connectNulls/>
              </LineChart>
            </ResponsiveContainer>
          </W>
          <W title="📊 Class Performance">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={classStats} margin={{top:5,right:10,left:-10,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="cls" tick={{fontSize:10}}/><YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                <Tooltip formatter={v=>[`${v}%`,"Avg Marks"]}/>
                <Bar dataKey="avgMarks" name="Avg Marks" radius={[6,6,0,0]}>
                  {classStats.map((e,i)=><Cell key={i} fill={e.avgMarks>=70?"#1a6b3c":e.avgMarks>=50?"#d97706":"#dc2626"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </W>
          <W title="📉 Attendance by Class">
            {classStats.length===0?<div style={{color:C.muted,padding:20,textAlign:"center"}}>No data</div>:(
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {classStats.map(d=>(
                  <div key={d.cls}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:600,color:"#374151"}}>{d.cls}</span>
                      <span style={{fontSize:12,fontWeight:800,color:(d.avgAtt??0)>80?"#1a6b3c":(d.avgAtt??0)>65?"#d97706":"#dc2626"}}>{d.avgAtt??"-"}%</span>
                    </div>
                    <div style={{height:8,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${d.avgAtt??0}%`,background:(d.avgAtt??0)>80?"#1a6b3c":(d.avgAtt??0)>65?"#d97706":"#dc2626",borderRadius:4}}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </W>
        </div>
      )}

      {/* ── HIFZ ── */}
      {subTab==="hifz"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:14}}>
          {hifzByClass.length>0?(
            <W title="📖 Hifz Performance by Class" wide>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hifzByClass} margin={{top:5,right:10,left:-10,bottom:5}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                  <XAxis dataKey="cls" tick={{fontSize:10}}/><YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                  <Tooltip formatter={v=>[`${v}%`,"Hifz Avg"]}/>
                  <Bar dataKey="avg" name="Hifz Avg" fill="#7c3aed" radius={[4,4,0,0]}>
                    {hifzByClass.map((e,i)=><Cell key={i} fill={e.avg>=75?"#7c3aed":e.avg>=55?"#d97706":"#dc2626"}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </W>
          ):(
            <W title="📖 Hifz Performance" wide>
              <div style={{textAlign:"center",color:"#9ca3af",padding:30,fontSize:13}}>No Hifz results recorded yet.</div>
            </W>
          )}
          <W title="🕌 Hifz Subject Breakdown">
            {(()=>{
              const hSubs=HIFZ_SUBS.map(sub=>{
                const recs=results.filter(r=>r.subject?.toLowerCase().includes(sub.toLowerCase())&&students.some(s=>String(s.id)===String(r.student_id)&&(s.stream||"").toLowerCase().includes("hifz")));
                if(!recs.length) return null;
                return {sub,avg:Math.round(recs.reduce((s,r)=>s+(r.marks_obtained/(r.total_marks||1))*100,0)/recs.length),count:recs.length};
              }).filter(Boolean);
              if(!hSubs.length) return <div style={{color:C.muted,padding:20,textAlign:"center"}}>No Hifz subject data</div>;
              return (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {hSubs.map(x=>(
                    <div key={x.sub}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:12,fontWeight:600,color:"#374151"}}>{x.sub}</span>
                        <span style={{fontSize:12,fontWeight:800,color:x.avg>=70?"#7c3aed":x.avg>=50?"#d97706":"#dc2626"}}>{x.avg}% <span style={{fontWeight:400,color:C.muted,fontSize:10}}>({x.count})</span></span>
                      </div>
                      <div style={{height:7,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${x.avg}%`,background:x.avg>=70?"#7c3aed":x.avg>=50?"#d97706":"#dc2626",borderRadius:4}}/>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </W>
          <W title="📅 Wifaq Readiness Guide">
            {[["Tajweed proficiency","70%+ recommended","#7c3aed"],["Manzil retention","75%+ strong","#2563eb"],["Hifz completion",">80% of assigned Juz","#1a6b3c"],["Attendance","90%+ ideal","#0d9488"]].map(([label,bench,col])=>(
              <div key={label} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #f3f4f6"}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:col,flexShrink:0}}/>
                <div style={{flex:1,fontSize:12,fontWeight:600,color:C.text}}>{label}</div>
                <div style={{fontSize:11,color:col,fontWeight:700}}>{bench}</div>
              </div>
            ))}
          </W>
        </div>
      )}

      {/* ── STUDENT TIMELINE + AI (merged) ── */}
      {subTab==="timeline"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* AI Readiness section */}
          <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1.5px solid #e0d7ff"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontWeight:900,fontSize:15,color:"#4c1d95",marginBottom:4}}>✨ AI Exam Readiness — whole school</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6,maxWidth:500}}>Scores each student 0–100. Covers BISE Matric, BISE FSc/FA, and Wifaq ul Madaris (Hifz).</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <select value={aiFilter} onChange={e=>setAiFilter(e.target.value)}
                  style={{border:"1.5px solid #e0d7ff",borderRadius:8,padding:"6px 10px",fontSize:12,background:"#f5f3ff",color:"#4c1d95",fontWeight:600}}>
                  <option value="all">All Students</option>
                  <option value="school">School (Matric)</option>
                  <option value="college">College (FSc/FA)</option>
                  <option value="hifz">Hifz (Wifaq)</option>
                </select>
                <button onClick={runAiReadiness} disabled={aiLoading}
                  style={{background:aiLoading?"#9ca3af":"#7c3aed",color:"white",border:"none",borderRadius:10,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:aiLoading?"not-allowed":"pointer"}}>
                  {aiLoading?"⏳ Analysing...":"✨ Run AI Readiness"}
                </button>
              </div>
            </div>
            {aiError&&<div style={{marginTop:12,background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"10px 14px",color:"#dc2626",fontSize:13}}>{aiError}</div>}
            {aiResults.length>0&&(
              <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:8,maxHeight:300,overflowY:"auto"}}>
                {aiResults.map((r,i)=>(
                  <div key={i} style={{background:"#f9fafb",borderRadius:10,padding:"10px 14px",border:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{r.cls} · {r.examType}</div>
                    </div>
                    <div style={{textAlign:"center",minWidth:60}}>
                      <div style={{fontWeight:900,fontSize:18,color:r.readinessColor}}>{r.readinessPct}%</div>
                      <div style={{fontSize:10,color:r.readinessColor,fontWeight:700}}>{r.readinessLabel}</div>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",maxWidth:240}}>
                      {r.factors.map((f,fi)=><span key={fi} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:f.s==="critical"?"#fef2f2":f.s==="warn"?"#fffbeb":"#f0fdf4",color:f.s==="critical"?"#dc2626":f.s==="warn"?"#d97706":"#15803d",border:`1px solid ${f.s==="critical"?"#fca5a5":f.s==="warn"?"#fde68a":"#bbf7d0"}`}}>{f.l}</span>)}
                    </div>
                    {r.advice&&<div style={{fontSize:11,color:"#4c1d95",background:"#f5f3ff",borderRadius:8,padding:"6px 10px",flex:"1 1 100%",lineHeight:1.5}}>{r.advice}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Per-student timeline */}
          <div style={{fontWeight:800,fontSize:14,color:C.text}}>📜 Per-Student Timeline + AI Grade Prediction</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <input value={tlSearch} onChange={e=>setTlSearch(e.target.value)} placeholder="Search student…"
              style={{flex:1,minWidth:200,border:"1.5px solid #e5e7eb",borderRadius:10,padding:"9px 14px",fontSize:13}}/>
            <select value={tlClass} onChange={e=>setTlClass(e.target.value)}
              style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:"9px 14px",fontSize:13,background:"white"}}>
              {allClasses.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,alignItems:"start"}}>
            {/* Student list */}
            <div style={{background:"white",borderRadius:14,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",overflow:"hidden",maxHeight:640,overflowY:"auto"}}>
              {tlStudents.length===0
                ?<div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:13}}>No students match.</div>
                :tlStudents.map(s=>{
                  const att=attForStudent(s.id);
                  const stuRes=results.filter(r=>String(r.student_id)===String(s.id));
                  const avgScore=stuRes.length>0?Math.round(stuRes.reduce((sum,r)=>sum+Number(r.marks_obtained||0)/Number(r.total_marks||1)*100,0)/stuRes.length):null;
                  const readiness=computeReadiness(s);
                  const isSelected=selStudent?.id===s.id;
                  return (
                    <div key={s.id} onClick={()=>{setSelStudent(s);setTlAiInsight("");setTlAiError("");}}
                      style={{padding:"12px 16px",cursor:"pointer",borderBottom:"1px solid #f3f4f6",background:isSelected?"#f0fdf4":"white",borderLeft:`3px solid ${isSelected?"#1a6b3c":"transparent"}`,transition:"all .15s"}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{s.full_name||s.name}</div>
                      <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>{s.class||s["class"]||"—"} · {s.stream||""}</div>
                      <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                        {att.total>0&&<span style={{fontSize:10,fontWeight:700,color:att.pct>=80?"#15803d":att.pct>=60?"#d97706":"#dc2626"}}>📅 {att.pct}%</span>}
                        {avgScore!==null&&<span style={{fontSize:10,fontWeight:700,color:avgScore>=75?"#15803d":avgScore>=50?"#d97706":"#dc2626"}}>📊 {avgScore}%</span>}
                        {readiness.readinessPct!==null&&<span style={{fontSize:10,fontWeight:700,color:readiness.readinessColor}}>✨ {readiness.readinessPct}%</span>}
                      </div>
                    </div>
                  );
                })
              }
            </div>

            {/* Timeline panel */}
            {selStudent?(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Student header */}
                <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",borderRadius:14,padding:"18px 22px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <div style={{width:52,height:52,borderRadius:12,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>
                    {selStudent.gender==="Female"?"👧":"👦"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:"white",fontWeight:900,fontSize:18}}>{selStudent.full_name||selStudent.name}</div>
                    <div style={{color:"rgba(255,255,255,0.75)",fontSize:12,marginTop:2}}>{selStudent.class||selStudent["class"]||"—"} · {selStudent.stream||""}</div>
                    {/* Readiness badge inline in header */}
                    {(()=>{const r=computeReadiness(selStudent);return r.readinessPct!==null&&(
                      <div style={{marginTop:6,display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.15)",borderRadius:8,padding:"4px 10px"}}>
                        <span style={{color:"white",fontSize:11,fontWeight:700}}>Readiness:</span>
                        <span style={{color:"white",fontSize:13,fontWeight:900}}>{r.readinessPct}%</span>
                        <span style={{color:"rgba(255,255,255,0.8)",fontSize:11}}>{r.readinessLabel}</span>
                      </div>
                    );})()}
                  </div>
                  <button onClick={()=>runTlAI(selStudent)} disabled={tlAiLoading}
                    style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.35)",borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:tlAiLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8}}>
                    {tlAiLoading?<><span style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite",display:"inline-block"}}/>Analyzing…</>:"✨ AI Grade Prediction"}
                  </button>
                </div>

                {/* Mini sparklines */}
                {(()=>{
                  const scoreHistory=getStuScoreHistory(selStudent.id);
                  const attHistory=getStuAttHistory(selStudent.id);
                  return (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div style={{background:"white",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
                        <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:8}}>📈 Score trend</div>
                        {scoreHistory.length<2
                          ?<div style={{fontSize:11,color:"#9ca3af",padding:"10px 0"}}>Need 2+ results for trend</div>
                          :<ResponsiveContainer width="100%" height={80}>
                            <LineChart data={scoreHistory} margin={{top:2,right:4,left:-30,bottom:0}}>
                              <YAxis domain={[0,100]} tick={false}/>
                              <Tooltip formatter={(v,_,p)=>[`${v}%`,p.payload.name]}/>
                              <Line type="monotone" dataKey="pct" stroke="#2563eb" strokeWidth={2} dot={{r:3,fill:"#2563eb"}} connectNulls/>
                            </LineChart>
                          </ResponsiveContainer>
                        }
                      </div>
                      <div style={{background:"white",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
                        <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:8}}>📅 Attendance (6mo)</div>
                        <ResponsiveContainer width="100%" height={80}>
                          <BarChart data={attHistory} margin={{top:2,right:4,left:-30,bottom:0}}>
                            <XAxis dataKey="month" tick={{fontSize:9}}/>
                            <Tooltip formatter={v=>[v!==null?`${v}%`:"No data","Att"]}/>
                            <Bar dataKey="pct" radius={[3,3,0,0]}>
                              {attHistory.map((e,i)=><Cell key={i} fill={e.pct===null?"#e5e7eb":e.pct>=80?"#1a6b3c":e.pct>=65?"#d97706":"#dc2626"}/>)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })()}

                {/* AI insight (combined: grade + readiness) */}
                {(tlAiInsight||tlAiError)&&(
                  <div style={{background:tlAiError?"#fef2f2":"#f0fdf4",borderRadius:12,padding:"16px 20px",border:`1px solid ${tlAiError?"#fecaca":"#bbf7d0"}`}}>
                    {tlAiError
                      ?<div style={{fontSize:13,color:"#dc2626"}}>{tlAiError}</div>
                      :<>
                        <div style={{fontWeight:700,color:"#15803d",fontSize:13,marginBottom:8}}>✨ AI Grade Prediction</div>
                        <div style={{fontSize:13,color:"#374151",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{tlAiInsight.text}</div>
                        {tlAiInsight.readiness&&(
                          <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid #bbf7d0",display:"flex",gap:16,flexWrap:"wrap"}}>
                            <div><div style={{fontSize:10,color:C.muted}}>Readiness score</div><div style={{fontWeight:900,fontSize:16,color:tlAiInsight.readiness.readinessColor}}>{tlAiInsight.readiness.readinessPct}%</div></div>
                            <div><div style={{fontSize:10,color:C.muted}}>Label</div><div style={{fontWeight:700,fontSize:13,color:tlAiInsight.readiness.readinessColor}}>{tlAiInsight.readiness.readinessLabel}</div></div>
                            <div><div style={{fontSize:10,color:C.muted}}>Exam</div><div style={{fontWeight:600,fontSize:12,color:C.text}}>{tlAiInsight.readiness.examType}</div></div>
                          </div>
                        )}
                      </>
                    }
                  </div>
                )}

                {/* Event timeline */}
                <div style={{background:"white",borderRadius:14,padding:"20px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                  <div style={{fontWeight:700,fontSize:14,color:"#111827",marginBottom:16}}>📜 Full History</div>
                  {(()=>{
                    const events=buildTimeline(selStudent);
                    if(events.length===0) return <div style={{textAlign:"center",padding:32,color:"#9ca3af",fontSize:13}}>No history yet for this student.</div>;
                    const TAG_COLORS={Milestone:{bg:"#f0fdf4",col:"#15803d"},Exam:{bg:"#eff6ff",col:"#2563eb"},Attendance:{bg:"#fff7ed",col:"#c2410c"},Finance:{bg:"#f0f9ff",col:"#0891b2"}};
                    return (
                      <div style={{position:"relative",paddingLeft:32}}>
                        <div style={{position:"absolute",left:12,top:8,bottom:8,width:2,background:"linear-gradient(to bottom,#1a6b3c22,#e5e7eb,#1a6b3c22)",borderRadius:1}}/>
                        {events.map((ev,i)=>(
                          <div key={i} style={{position:"relative",marginBottom:18}}>
                            <div style={{position:"absolute",left:-26,top:8,width:22,height:22,borderRadius:"50%",background:ev.bg,border:`2px solid ${ev.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,zIndex:1,boxShadow:"0 0 0 3px white"}}>{ev.icon}</div>
                            <div style={{background:"white",borderRadius:12,padding:"10px 14px",border:`1.5px solid ${ev.color}25`,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap",marginBottom:3}}>
                                <div style={{fontWeight:700,fontSize:13,color:ev.color}}>{ev.title}</div>
                                <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                                  {ev.tag&&TAG_COLORS[ev.tag]&&<span style={{fontSize:9,fontWeight:700,background:TAG_COLORS[ev.tag].bg,color:TAG_COLORS[ev.tag].col,borderRadius:20,padding:"1px 7px"}}>{ev.tag.toUpperCase()}</span>}
                                  <div style={{fontSize:10,color:"#9ca3af",whiteSpace:"nowrap"}}>{ev.date?new Date(ev.date.length===7?ev.date+"-01":ev.date).toLocaleDateString("en-PK",{day:"numeric",month:"short",year:"numeric"}):"—"}</div>
                                </div>
                              </div>
                              <div style={{fontSize:11,color:"#6b7280",lineHeight:1.5}}>{ev.detail}</div>
                            </div>
                          </div>
                        ))}
                        <div style={{position:"relative",paddingLeft:6}}>
                          <div style={{position:"absolute",left:-25,top:5,width:22,height:22,borderRadius:"50%",background:"#f3f4f6",border:"2px dashed #d1d5db",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>📍</div>
                          <div style={{fontSize:11,color:"#9ca3af",fontStyle:"italic"}}>Today — story continues…</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ):(
              <div style={{background:"white",borderRadius:14,padding:48,textAlign:"center",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:48,marginBottom:12}}>✨</div>
                <div style={{fontWeight:700,fontSize:15,color:"#374151",marginBottom:8}}>Select a student</div>
                <div style={{fontSize:13,color:"#9ca3af"}}>Choose from the list to view their full timeline and AI grade prediction.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── REPORT CARD AI ── */}
      {subTab==="reportcard"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{background:"white",borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,boxShadow:`0 2px 10px ${C.shadow}`}}>
            <div style={{fontWeight:900,fontSize:16,color:C.primary,marginBottom:4}}>📝 Report Card Comment Generator</div>
            <div style={{fontSize:12,color:C.muted}}>Select a student — AI reads their grades, attendance and conduct to write a personalised parent-facing comment. You review and copy.</div>
          </div>

          <div style={{background:"white",borderRadius:14,padding:"18px 22px",border:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:14}}>
            <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-end"}}>
              <div style={{flex:2,minWidth:200}}>
                <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:4,textTransform:"uppercase"}}>Student</label>
                <select value={rcStudentId} onChange={e=>{setRcStudentId(e.target.value);setRcComment("");}}
                  style={{width:"100%",padding:"9px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13}}>
                  <option value="">— Select student —</option>
                  {[...students].sort((a,b)=>(a.class||"").localeCompare(b.class||"")||(a.full_name||a.name||"").localeCompare(b.full_name||b.name||"")).map(s=>(
                    <option key={s.id} value={s.id}>{s.full_name||s.name} — Class {s.class||"?"}</option>
                  ))}
                </select>
              </div>
              <div style={{flex:1,minWidth:160}}>
                <label style={{fontSize:11,fontWeight:700,color:C.muted,display:"block",marginBottom:4,textTransform:"uppercase"}}>Tone</label>
                <select value={rcTone} onChange={e=>setRcTone(e.target.value)}
                  style={{width:"100%",padding:"9px 10px",borderRadius:8,border:`1px solid ${C.border}`,fontSize:13}}>
                  <option value="encouraging">🌟 Warm & Encouraging</option>
                  <option value="formal">📋 Formal & Professional</option>
                  <option value="detailed">🔍 Detailed & Analytical</option>
                </select>
              </div>
              <button onClick={runReportCardAI} disabled={!rcStudentId||rcGenerating}
                style={{background:rcStudentId&&!rcGenerating?`linear-gradient(135deg,${C.primary},#0d9488)`:"#9ca3af",color:"white",border:"none",borderRadius:10,padding:"10px 22px",fontWeight:800,fontSize:13,cursor:rcStudentId&&!rcGenerating?"pointer":"not-allowed",whiteSpace:"nowrap"}}>
                {rcGenerating?"🤖 Writing…":"✨ Generate Comment"}
              </button>
            </div>

            {/* Student data preview */}
            {rcStudentId&&(()=>{
              const stu=students.find(s=>String(s.id)===rcStudentId);
              if(!stu) return null;
              const att=attForStudent(stu.id);
              const res=results.filter(r=>String(r.student_id)===String(stu.id)&&!isHifzSub(r.subject));
              const avgM=res.length?Math.round(res.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1))*100,0)/res.length):null;
              const strong=res.filter(r=>(r.marks_obtained/(r.total_marks||1))*100>=80).map(r=>r.subject);
              const weak=res.filter(r=>(r.marks_obtained/(r.total_marks||1))*100<60).map(r=>r.subject);
              const conduct=conductData.filter(c=>String(c.student_id)===String(stu.id));
              return(
                <div style={{background:"#f9fafb",borderRadius:10,padding:"12px 16px",border:"1px solid #e5e7eb",display:"flex",gap:16,flexWrap:"wrap"}}>
                  <div><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Attendance</div><div style={{fontWeight:900,fontSize:18,color:att.pct>=85?C.primary:att.pct>=75?C.amber:C.rose}}>{att.total>0?`${att.pct}%`:"—"}</div></div>
                  <div><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Avg Score</div><div style={{fontWeight:900,fontSize:18,color:avgM>=70?C.primary:avgM>=50?C.amber:C.rose}}>{avgM!==null?`${avgM}%`:"—"}</div></div>
                  {strong.length>0&&<div><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Strong</div><div style={{fontSize:12,color:C.primary,fontWeight:600}}>{[...new Set(strong)].slice(0,3).join(", ")}</div></div>}
                  {weak.length>0&&<div><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Needs Work</div><div style={{fontSize:12,color:C.rose,fontWeight:600}}>{[...new Set(weak)].slice(0,3).join(", ")}</div></div>}
                  {conduct.length>0&&<div><div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase"}}>Conduct</div><div style={{fontSize:12,color:C.text,fontWeight:600}}>{conduct.filter(c=>c.type==="commendation").length}⭐ {conduct.filter(c=>["warning","incident"].includes(c.type)).length}⚠️</div></div>}
                </div>
              );
            })()}

            {/* Generated comment */}
            {rcComment&&(
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div style={{background:"#f0fdf4",borderRadius:10,padding:"16px 18px",border:"1px solid #bbf7d0",position:"relative"}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.primary,textTransform:"uppercase",marginBottom:8}}>Generated Comment — review before using</div>
                  <div style={{fontSize:14,color:C.text,lineHeight:1.75}}>{rcComment}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{navigator.clipboard?.writeText(rcComment);setRcCopied(true);setTimeout(()=>setRcCopied(false),2500);}}
                    style={{background:rcCopied?"#16a34a":C.primary,color:"white",border:"none",borderRadius:8,padding:"8px 18px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                    {rcCopied?"✅ Copied!":"📋 Copy to Clipboard"}
                  </button>
                  <button onClick={runReportCardAI}
                    style={{background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:8,padding:"8px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                    🔄 Regenerate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      )}
      {/*── AI INSIGHTS ── */}
      {subTab==="ai_insights"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* Class Health Report */}
          <div style={{background:C.white,borderRadius:14,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1.5px solid #e0d7ff"}}>
            <div style={{fontWeight:800,fontSize:14,color:"#4c1d95",marginBottom:10}}>🏥 Class Health Report</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:10}}>
              <select value={aiInsightClass} onChange={e=>setAiInsightClass(e.target.value)}
                style={{border:"1.5px solid #e0d7ff",borderRadius:8,padding:"7px 12px",fontSize:13,background:"#f5f3ff",color:"#4c1d95",fontWeight:600}}>
                {allClasses.map(c=><option key={c}>{c}</option>)}
              </select>
              <button disabled={healthLoading} onClick={async()=>{
                setHealthLoading(true);setHealthError("");setHealthReport("");
                const cls=aiInsightClass==="All"?students:students.filter(s=>(s.class||s["class"])===aiInsightClass);
                const ids=new Set(cls.map(s=>String(s.id)));
                const res=results.filter(r=>ids.has(String(r.student_id))&&!isHifzSub(r.subject));
                const avg=res.length?Math.round(res.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1)*100),0)/res.length):null;
                const attArr=cls.map(s=>attForStudent(s.id)).filter(a=>a.total>0);
                const avgAtt=attArr.length?Math.round(attArr.reduce((a,s)=>a+s.pct,0)/attArr.length):null;
                const prompt=`School performance data — Class: ${aiInsightClass} | Students: ${cls.length} | Avg Marks: ${avg!==null?avg+"%":"no data"} | Avg Attendance: ${avgAtt!==null?avgAtt+"%":"no data"}\n\nWrite a CRITICAL, honest class health report for the principal. Do NOT be encouraging if numbers are low. Use this exact format:\n\n**Overall Verdict:** [one blunt sentence on whether this class is performing well or poorly]\n\n**⚠️ Critical Issues:**\n• [specific problem 1 with reference to the data]\n• [specific problem 2]\n\n**📉 Root Causes:**\n• [likely root cause 1]\n• [likely root cause 2]\n\n**✅ Required Actions (Next 2 Weeks):**\n• [concrete action for teacher]\n• [concrete action for principal]`;
                try{const t=await groqGenerate(prompt,250);setHealthReport(t||"No response.");}
                catch(e){setHealthError("AI error: "+e.message);}
                setHealthLoading(false);
              }} style={{background:healthLoading?"#9ca3af":"#7c3aed",color:"white",border:"none",borderRadius:10,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:healthLoading?"not-allowed":"pointer"}}>
                {healthLoading?"⏳ Generating...":"🤖 Generate Report"}
              </button>
            </div>
            {healthError&&<div style={{background:"#fef2f2",borderRadius:8,padding:"10px 14px",color:"#dc2626",fontSize:13}}>{healthError}</div>}
            {healthReport&&renderAiReport(healthReport,"health",setAiCopied)}
          </div>

          {/* Subject Difficulty Detector */}
          <div style={{background:C.white,borderRadius:14,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontWeight:800,fontSize:14,color:C.text,marginBottom:10}}>📚 Subject Difficulty Detector</div>
            {subjectPerfData.length===0
              ?<div style={{color:C.muted,textAlign:"center",padding:20}}>No results data</div>
              :<>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={subjectPerfData} margin={{top:5,right:10,left:-10,bottom:5}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                    <XAxis dataKey="subject" tick={{fontSize:9}} interval={0}/>
                    <YAxis domain={[0,100]} tick={{fontSize:10}} tickFormatter={v=>`${v}%`}/>
                    <Tooltip formatter={(v,_,p)=>[`${v}% (${p.payload.count} records)`,"Avg"]}/>
                    <Bar dataKey="avg" name="Avg %" radius={[4,4,0,0]}>
                      {subjectPerfData.map((e,i)=><Cell key={i} fill={e.avg>=70?"#1a6b3c":e.avg>=50?"#d97706":"#dc2626"}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{marginTop:10}}>
                  <button disabled={subjAiLoading} onClick={async()=>{
                    setSubjAiLoading(true);setSubjAiReport("");
                    const worst=subjectPerfData.slice(0,3).map(x=>`${x.subject}: ${x.avg}%`).join(", ");
                    const best=subjectPerfData.slice(-2).map(x=>`${x.subject}: ${x.avg}%`).join(", ");
                    const prompt=`School subject performance data:\nWeakest subjects: ${worst}\nStrongest subjects: ${best}\n\nWrite a CRITICAL curriculum analysis. Be frank — if weak subjects are badly taught or poorly structured, say so. Use this exact format:\n\n**📉 Weak Subject Diagnosis:**\n• [what specifically is failing and why]\n• [whether this is a teaching, curriculum, or engagement problem]\n\n**⚠️ Urgent Interventions:**\n• [specific action for worst subject]\n• [teacher or timetable change needed]\n\n**📈 Leverage Strengths:**\n• [how strong subjects can be used to support weak ones]`;
                    try{const t=await groqGenerate(prompt,220);setSubjAiReport(t||"No response.");}
                    catch(e){setSubjAiReport("AI error: "+e.message);}
                    setSubjAiLoading(false);
                  }} style={{background:subjAiLoading?"#9ca3af":"#2563eb",color:"white",border:"none",borderRadius:10,padding:"8px 18px",fontWeight:700,fontSize:13,cursor:subjAiLoading?"not-allowed":"pointer"}}>
                    {subjAiLoading?"⏳ Analysing...":"🤖 AI Subject Insight"}
                  </button>
                </div>
                {subjAiReport&&renderAiReport(subjAiReport,"subj",setAiCopied)}
              </>
            }
          </div>

          {/* Early Warning System */}
          <div style={{background:C.white,borderRadius:14,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontWeight:800,fontSize:14,color:"#dc2626",marginBottom:10}}>⚠️ Early Warning System</div>
            {warnStudentsData.length===0
              ?<div style={{color:C.muted,textAlign:"center",padding:20}}>No students at risk (Att &lt;70% or Avg &lt;40%)</div>
              :<>
                <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:240,overflowY:"auto",marginBottom:10}}>
                  {warnStudentsData.map(s=>(
                    <div key={s.id} style={{background:"#fef2f2",borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",border:"1px solid #fca5a5"}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{s.full_name||s.name}</div>
                        <div style={{fontSize:11,color:C.muted}}>Class {s.class||"—"} · {s.stream||""}</div>
                      </div>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {s.flags.map((f,i)=><span key={i} style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#fee2e2",color:"#dc2626",border:"1px solid #fca5a5",fontWeight:600}}>{f}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
                <button disabled={warnAiLoading} onClick={async()=>{
                  setWarnAiLoading(true);setWarnAiReport("");
                  const top5=warnStudentsData.slice(0,5).map(s=>`${s.full_name||s.name} (${s.flags.join(", ")})`).join("; ");
                  const prompt=`${warnStudentsData.length} students flagged at risk. Top cases: ${top5}\n\nWrite a CRITICAL at-risk student assessment. Be direct about severity. Use this exact format:\n\n**🚨 Risk Level: [Critical / High / Moderate]**\n[one blunt sentence on how serious this situation is]\n\n**⚠️ Priority Cases (Immediate Action):**\n• [student/case 1 — specific risk and what must happen]\n• [student/case 2 — specific risk and what must happen]\n\n**🔧 Systemic Problems Observed:**\n• [root cause pattern across cases]\n\n**📋 Non-Negotiable Actions:**\n• [action 1 — who, what, by when]\n• [action 2 — who, what, by when]`;
                  try{const t=await groqGenerate(prompt,250);setWarnAiReport(t||"No response.");}
                  catch(e){setWarnAiReport("AI error: "+e.message);}
                  setWarnAiLoading(false);
                }} style={{background:warnAiLoading?"#9ca3af":"#dc2626",color:"white",border:"none",borderRadius:10,padding:"8px 18px",fontWeight:700,fontSize:13,cursor:warnAiLoading?"not-allowed":"pointer"}}>
                  {warnAiLoading?"⏳ Generating...":"🤖 AI Intervention Plan"}
                </button>
                {warnAiReport&&renderAiReport(warnAiReport,"warn",setAiCopied)}
              </>
            }
          </div>

          {/* Student Profile Generator */}
          <div style={{background:C.white,borderRadius:14,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontWeight:800,fontSize:14,color:C.text,marginBottom:10}}>👤 Student Profile Generator</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:10}}>
              <select value={profStudentId} onChange={e=>setProfStudentId(e.target.value)}
                style={{flex:1,minWidth:200,border:"1.5px solid #e5e7eb",borderRadius:8,padding:"7px 12px",fontSize:13}}>
                <option value="">— Select a student —</option>
                {students.map(s=><option key={s.id} value={s.id}>{s.full_name||s.name} ({s.class||"—"})</option>)}
              </select>
              <button disabled={profLoading||!profStudentId} onClick={async()=>{
                if(!profStudentId) return;
                setProfLoading(true);setProfReport("");
                const stu=students.find(s=>String(s.id)===String(profStudentId));
                if(!stu){setProfLoading(false);return;}
                const att=attForStudent(stu.id);
                const stuRes=results.filter(r=>String(r.student_id)===String(stu.id));
                const avg=stuRes.length?Math.round(stuRes.reduce((a,r)=>a+(r.marks_obtained/(r.total_marks||1)*100),0)/stuRes.length):null;
                const subjects=stuRes.slice(-6).map(r=>`${r.subject||"?"}: ${r.marks_obtained}/${r.total_marks}`).join(", ");
                const prompt=`Student profile data:\nName: ${stu.full_name||stu.name} | Class: ${stu.class||"?"} | Stream: ${stu.stream||"School"}\nAttendance: ${att.pct}% (${att.present}/${att.total} days)\nAvg Score: ${avg!==null?avg+"%":"no data"}\nRecent results: ${subjects||"none"}\n\nWrite a CRITICAL, frank student profile — do not sugarcoat weak performance. Use this exact format:\n\n**📊 Academic Standing: [Excellent / Good / At Risk / Critical]**\n• [direct assessment of where this student stands]\n\n**⚠️ Key Weaknesses:**\n• [weakest subject or skill with data reference]\n• [attendance or pattern concern if relevant]\n\n**✅ Strengths:**\n• [what the student genuinely does well]\n\n**🎯 Teacher Action Required:**\n• [one specific, immediate intervention]\n• [realistic grade trajectory if nothing changes]`;
                try{const t=await groqGenerate(prompt,280);setProfReport(t||"No response.");}
                catch(e){setProfReport("AI error: "+e.message);}
                setProfLoading(false);
              }} style={{background:profLoading||!profStudentId?"#9ca3af":"#1a6b3c",color:"white",border:"none",borderRadius:10,padding:"8px 20px",fontWeight:700,fontSize:13,cursor:profLoading||!profStudentId?"not-allowed":"pointer"}}>
                {profLoading?"⏳ Generating...":"🤖 Generate Profile"}
              </button>
            </div>
            {profReport&&renderAiReport(profReport,"prof",setAiCopied)}
          </div>

        </div>
      )}

      {/* ── STUDENT TIMELINE + AI (merged) ── */}
      {subTab==="timeline"&&(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>

          {/* AI Readiness section */}
          <div style={{background:C.white,borderRadius:16,padding:"20px 24px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1.5px solid #e0d7ff"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontWeight:900,fontSize:15,color:"#4c1d95",marginBottom:4}}>✨ AI Exam Readiness — whole school</div>
                <div style={{fontSize:12,color:C.muted,lineHeight:1.6,maxWidth:500}}>Scores each student 0–100. Covers BISE Matric, BISE FSc/FA, and Wifaq ul Madaris (Hifz).</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <select value={aiFilter} onChange={e=>setAiFilter(e.target.value)}
                  style={{border:"1.5px solid #e0d7ff",borderRadius:8,padding:"6px 10px",fontSize:12,background:"#f5f3ff",color:"#4c1d95",fontWeight:600}}>
                  <option value="all">All Students</option>
                  <option value="school">School (Matric)</option>
                  <option value="college">College (FSc/FA)</option>
                  <option value="hifz">Hifz (Wifaq)</option>
                </select>
                <button onClick={runAiReadiness} disabled={aiLoading}
                  style={{background:aiLoading?"#9ca3af":"#7c3aed",color:"white",border:"none",borderRadius:10,padding:"9px 20px",fontWeight:700,fontSize:13,cursor:aiLoading?"not-allowed":"pointer"}}>
                  {aiLoading?"⏳ Analysing...":"✨ Run AI Readiness"}
                </button>
              </div>
            </div>
            {aiError&&<div style={{marginTop:12,background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"10px 14px",color:"#dc2626",fontSize:13}}>{aiError}</div>}
            {aiResults.length>0&&(
              <div style={{marginTop:16,display:"flex",flexDirection:"column",gap:8,maxHeight:300,overflowY:"auto"}}>
                {aiResults.map((r,i)=>(
                  <div key={i} style={{background:"#f9fafb",borderRadius:10,padding:"10px 14px",border:"1px solid #e5e7eb",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</div>
                      <div style={{fontSize:11,color:C.muted}}>{r.cls} · {r.examType}</div>
                    </div>
                    <div style={{textAlign:"center",minWidth:60}}>
                      <div style={{fontWeight:900,fontSize:18,color:r.readinessColor}}>{r.readinessPct}%</div>
                      <div style={{fontSize:10,color:r.readinessColor,fontWeight:700}}>{r.readinessLabel}</div>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",maxWidth:240}}>
                      {r.factors.map((f,fi)=><span key={fi} style={{fontSize:10,padding:"2px 7px",borderRadius:10,background:f.s==="critical"?"#fef2f2":f.s==="warn"?"#fffbeb":"#f0fdf4",color:f.s==="critical"?"#dc2626":f.s==="warn"?"#d97706":"#15803d",border:`1px solid ${f.s==="critical"?"#fca5a5":f.s==="warn"?"#fde68a":"#bbf7d0"}`}}>{f.l}</span>)}
                    </div>
                    {r.advice&&<div style={{fontSize:11,color:"#4c1d95",background:"#f5f3ff",borderRadius:8,padding:"6px 10px",flex:"1 1 100%",lineHeight:1.5}}>{r.advice}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Per-student timeline */}
          <div style={{fontWeight:800,fontSize:14,color:C.text}}>📜 Per-Student Timeline + AI Grade Prediction</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <input value={tlSearch} onChange={e=>setTlSearch(e.target.value)} placeholder="Search student…"
              style={{flex:1,minWidth:200,border:"1.5px solid #e5e7eb",borderRadius:10,padding:"9px 14px",fontSize:13}}/>
            <select value={tlClass} onChange={e=>setTlClass(e.target.value)}
              style={{border:"1.5px solid #e5e7eb",borderRadius:10,padding:"9px 14px",fontSize:13,background:"white"}}>
              {allClasses.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:16,alignItems:"start"}}>
            {/* Student list */}
            <div style={{background:"white",borderRadius:14,boxShadow:"0 2px 12px rgba(0,0,0,0.06)",overflow:"hidden",maxHeight:640,overflowY:"auto"}}>
              {tlStudents.length===0
                ?<div style={{padding:24,textAlign:"center",color:"#9ca3af",fontSize:13}}>No students match.</div>
                :tlStudents.map(s=>{
                  const att=attForStudent(s.id);
                  const stuRes=results.filter(r=>String(r.student_id)===String(s.id));
                  const avgScore=stuRes.length>0?Math.round(stuRes.reduce((sum,r)=>sum+Number(r.marks_obtained||0)/Number(r.total_marks||1)*100,0)/stuRes.length):null;
                  const readiness=computeReadiness(s);
                  const isSelected=selStudent?.id===s.id;
                  return (
                    <div key={s.id} onClick={()=>{setSelStudent(s);setTlAiInsight("");setTlAiError("");}}
                      style={{padding:"12px 16px",cursor:"pointer",borderBottom:"1px solid #f3f4f6",background:isSelected?"#f0fdf4":"white",borderLeft:`3px solid ${isSelected?"#1a6b3c":"transparent"}`,transition:"all .15s"}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#111827"}}>{s.full_name||s.name}</div>
                      <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>{s.class||s["class"]||"—"} · {s.stream||""}</div>
                      <div style={{display:"flex",gap:8,marginTop:4,flexWrap:"wrap"}}>
                        {att.total>0&&<span style={{fontSize:10,fontWeight:700,color:att.pct>=80?"#15803d":att.pct>=60?"#d97706":"#dc2626"}}>📅 {att.pct}%</span>}
                        {avgScore!==null&&<span style={{fontSize:10,fontWeight:700,color:avgScore>=75?"#15803d":avgScore>=50?"#d97706":"#dc2626"}}>📊 {avgScore}%</span>}
                        {readiness.readinessPct!==null&&<span style={{fontSize:10,fontWeight:700,color:readiness.readinessColor}}>✨ {readiness.readinessPct}%</span>}
                      </div>
                    </div>
                  );
                })
              }
            </div>

            {/* Timeline panel */}
            {selStudent?(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Student header */}
                <div style={{background:"linear-gradient(135deg,#1a6b3c,#0a4a24)",borderRadius:14,padding:"18px 22px",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
                  <div style={{width:52,height:52,borderRadius:12,background:"rgba(255,255,255,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>
                    {selStudent.gender==="Female"?"👧":"👦"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:"white",fontWeight:900,fontSize:18}}>{selStudent.full_name||selStudent.name}</div>
                    <div style={{color:"rgba(255,255,255,0.75)",fontSize:12,marginTop:2}}>{selStudent.class||selStudent["class"]||"—"} · {selStudent.stream||""}</div>
                    {/* Readiness badge inline in header */}
                    {(()=>{const r=computeReadiness(selStudent);return r.readinessPct!==null&&(
                      <div style={{marginTop:6,display:"inline-flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.15)",borderRadius:8,padding:"4px 10px"}}>
                        <span style={{color:"white",fontSize:11,fontWeight:700}}>Readiness:</span>
                        <span style={{color:"white",fontSize:13,fontWeight:900}}>{r.readinessPct}%</span>
                        <span style={{color:"rgba(255,255,255,0.8)",fontSize:11}}>{r.readinessLabel}</span>
                      </div>
                    );})()}
                  </div>
                  <button onClick={()=>runTlAI(selStudent)} disabled={tlAiLoading}
                    style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.35)",borderRadius:10,padding:"10px 20px",fontWeight:700,fontSize:13,cursor:tlAiLoading?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:8}}>
                    {tlAiLoading?<><span style={{width:16,height:16,border:"2px solid rgba(255,255,255,0.4)",borderTopColor:"white",borderRadius:"50%",animation:"spin .8s linear infinite",display:"inline-block"}}/>Analyzing…</>:"✨ AI Grade Prediction"}
                  </button>
                </div>

                {/* Mini sparklines */}
                {(()=>{
                  const scoreHistory=getStuScoreHistory(selStudent.id);
                  const attHistory=getStuAttHistory(selStudent.id);
                  return (
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                      <div style={{background:"white",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
                        <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:8}}>📈 Score trend</div>
                        {scoreHistory.length<2
                          ?<div style={{fontSize:11,color:"#9ca3af",padding:"10px 0"}}>Need 2+ results for trend</div>
                          :<ResponsiveContainer width="100%" height={80}>
                            <LineChart data={scoreHistory} margin={{top:2,right:4,left:-30,bottom:0}}>
                              <YAxis domain={[0,100]} tick={false}/>
                              <Tooltip formatter={(v,_,p)=>[`${v}%`,p.payload.name]}/>
                              <Line type="monotone" dataKey="pct" stroke="#2563eb" strokeWidth={2} dot={{r:3,fill:"#2563eb"}} connectNulls/>
                            </LineChart>
                          </ResponsiveContainer>
                        }
                      </div>
                      <div style={{background:"white",borderRadius:12,padding:"14px 16px",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
                        <div style={{fontWeight:700,fontSize:12,color:C.muted,marginBottom:8}}>📅 Attendance (6mo)</div>
                        <ResponsiveContainer width="100%" height={80}>
                          <BarChart data={attHistory} margin={{top:2,right:4,left:-30,bottom:0}}>
                            <XAxis dataKey="month" tick={{fontSize:9}}/>
                            <Tooltip formatter={v=>[v!==null?`${v}%`:"No data","Att"]}/>
                            <Bar dataKey="pct" radius={[3,3,0,0]}>
                              {attHistory.map((e,i)=><Cell key={i} fill={e.pct===null?"#e5e7eb":e.pct>=80?"#1a6b3c":e.pct>=65?"#d97706":"#dc2626"}/>)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })()}

                {/* AI insight (combined: grade + readiness) */}
                {(tlAiInsight||tlAiError)&&(
                  <div style={{background:tlAiError?"#fef2f2":"#f0fdf4",borderRadius:12,padding:"16px 20px",border:`1px solid ${tlAiError?"#fecaca":"#bbf7d0"}`}}>
                    {tlAiError
                      ?<div style={{fontSize:13,color:"#dc2626"}}>{tlAiError}</div>
                      :<>
                        <div style={{fontWeight:700,color:"#15803d",fontSize:13,marginBottom:8}}>✨ AI Grade Prediction</div>
                        <div style={{fontSize:13,color:"#374151",lineHeight:1.7,whiteSpace:"pre-wrap"}}>{tlAiInsight.text}</div>
                        {tlAiInsight.readiness&&(
                          <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid #bbf7d0",display:"flex",gap:16,flexWrap:"wrap"}}>
                            <div><div style={{fontSize:10,color:C.muted}}>Readiness score</div><div style={{fontWeight:900,fontSize:16,color:tlAiInsight.readiness.readinessColor}}>{tlAiInsight.readiness.readinessPct}%</div></div>
                            <div><div style={{fontSize:10,color:C.muted}}>Label</div><div style={{fontWeight:700,fontSize:13,color:tlAiInsight.readiness.readinessColor}}>{tlAiInsight.readiness.readinessLabel}</div></div>
                            <div><div style={{fontSize:10,color:C.muted}}>Exam</div><div style={{fontWeight:600,fontSize:12,color:C.text}}>{tlAiInsight.readiness.examType}</div></div>
                          </div>
                        )}
                      </>
                    }
                  </div>
                )}

                {/* Event timeline */}
                <div style={{background:"white",borderRadius:14,padding:"20px 24px",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                  <div style={{fontWeight:700,fontSize:14,color:"#111827",marginBottom:16}}>📜 Full History</div>
                  {(()=>{
                    const events=buildTimeline(selStudent);
                    if(events.length===0) return <div style={{textAlign:"center",padding:32,color:"#9ca3af",fontSize:13}}>No history yet for this student.</div>;
                    const TAG_COLORS={Milestone:{bg:"#f0fdf4",col:"#15803d"},Exam:{bg:"#eff6ff",col:"#2563eb"},Attendance:{bg:"#fff7ed",col:"#c2410c"},Finance:{bg:"#f0f9ff",col:"#0891b2"}};
                    return (
                      <div style={{position:"relative",paddingLeft:32}}>
                        <div style={{position:"absolute",left:12,top:8,bottom:8,width:2,background:"linear-gradient(to bottom,#1a6b3c22,#e5e7eb,#1a6b3c22)",borderRadius:1}}/>
                        {events.map((ev,i)=>(
                          <div key={i} style={{position:"relative",marginBottom:18}}>
                            <div style={{position:"absolute",left:-26,top:8,width:22,height:22,borderRadius:"50%",background:ev.bg,border:`2px solid ${ev.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,zIndex:1,boxShadow:"0 0 0 3px white"}}>{ev.icon}</div>
                            <div style={{background:"white",borderRadius:12,padding:"10px 14px",border:`1.5px solid ${ev.color}25`,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap",marginBottom:3}}>
                                <div style={{fontWeight:700,fontSize:13,color:ev.color}}>{ev.title}</div>
                                <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                                  {ev.tag&&TAG_COLORS[ev.tag]&&<span style={{fontSize:9,fontWeight:700,background:TAG_COLORS[ev.tag].bg,color:TAG_COLORS[ev.tag].col,borderRadius:20,padding:"1px 7px"}}>{ev.tag.toUpperCase()}</span>}
                                  <div style={{fontSize:10,color:"#9ca3af",whiteSpace:"nowrap"}}>{ev.date?new Date(ev.date.length===7?ev.date+"-01":ev.date).toLocaleDateString("en-PK",{day:"numeric",month:"short",year:"numeric"}):"—"}</div>
                                </div>
                              </div>
                              <div style={{fontSize:11,color:"#6b7280",lineHeight:1.5}}>{ev.detail}</div>
                            </div>
                          </div>
                        ))}
                        <div style={{position:"relative",paddingLeft:6}}>
                          <div style={{position:"absolute",left:-25,top:5,width:22,height:22,borderRadius:"50%",background:"#f3f4f6",border:"2px dashed #d1d5db",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>📍</div>
                          <div style={{fontSize:11,color:"#9ca3af",fontStyle:"italic"}}>Today — story continues…</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ):(
              <div style={{background:"white",borderRadius:14,padding:48,textAlign:"center",boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                <div style={{fontSize:48,marginBottom:12}}>✨</div>
                <div style={{fontWeight:700,fontSize:15,color:"#374151",marginBottom:8}}>Select a student</div>
                <div style={{fontSize:13,color:"#9ca3af"}}>Choose from the list to view their full timeline and AI grade prediction.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function QKAcademy({ session, onSignOut, adminRole="admin" }) {
  const [tab,setTab] = useState("dashboard");
  const [mobPortalNav, setMobPortalNav] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [students,setStudents] = useState(INIT_STUDENTS);
  const [attendance,setAttendance] = useState(INIT_ATTENDANCE);
  const [fees,setFees] = useState([]);
  const [results,setResults] = useState([]);
  const [conductData,setConductData] = useState([]);
  const [dbStatus,setDbStatus] = useState("connecting");
  const [toast,setToast] = useState(null);
  // School settings — loaded once from DB
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYearsLoading, setAcademicYearsLoading] = useState(false);
  const [isOnline,setIsOnline] = useState(navigator.onLine);
  const [pendingCount,setPendingCount] = useState(0);
  const [syncing,setSyncing] = useState(false);

  const showToast = (msg,type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  };

  // ── Role-based access scoping ──────────────────────────────────
  // admin: full access | principal: full (for now) | vice_principal: full except settings/export
  // accountant: finance tabs only
  const ROLE_SCOPE = {
    admin:           { label:"Admin",          icon:"🔑", hideTabs:[] },
    principal:       { label:"Principal",      icon:"🏫", hideTabs:["teacherscorecard","assets"] },
    vice_principal:  { label:"Vice Principal", icon:"👔", hideTabs:["settings","export","parents","teacherscorecard","assets"] },
    accountant:      { label:"Accountant",     icon:"💼", hideTabs:["register","students","results","promotion","hifz","subjects","timetable","homework","conduct","reports","achievements","analytics","ai","attendance","leave","scholarship","admissions","calendar","timeline","insights","communications","teacherscorecard","weeklydigest","observations","staffattendance","assets","facility"] },
    facility_manager:{ label:"Facility Mgr",    icon:"🏗️", hideTabs:[] },
  };
  const myScope = ROLE_SCOPE[adminRole] || ROLE_SCOPE.admin;
  const canSeeTab = (tabId) => !myScope.hideTabs.includes(tabId);

  const loadAcademicYears = React.useCallback(async () => {
    setAcademicYearsLoading(true);
    try {
      const { data, error } = await supabase.from("academic_years").select("*").order("start_date", { ascending: false });
      if (error) throw error;
      const rows = (data || []).map((r) => ({
        ...r,
        year_label: r.year_label || normalizeAcademicYearLabel(r.year_name || r.year || academicYearValueFromDates(r.start_date, r.end_date)),
      }));
      setAcademicYears(rows);
    } catch (e) {
      console.warn("Academic year load failed:", e?.message || e);
      setAcademicYears([]);
    } finally {
      setAcademicYearsLoading(false);
    }
  }, []);

  React.useEffect(()=>{ loadAcademicYears(); }, [loadAcademicYears]);

  // Convert attendance object {date:{id:status}} to array [{date,student_id,status}]
  const activeAcademicYear = React.useMemo(() => getCurrentAcademicYear(academicYears, settings?.academic_year || DEFAULT_SETTINGS.academic_year), [academicYears, settings]);
  const activeAcademicYearLabel = activeAcademicYear?.year_label || normalizeAcademicYearLabel(settings?.academic_year || DEFAULT_SETTINGS.academic_year);

  const attArray = React.useMemo(()=>{
    if(Array.isArray(attendance)) return attendance;
    const arr=[];
    Object.entries(attendance||{}).forEach(([date,ids])=>{
      Object.entries(ids||{}).forEach(([student_id,status])=>{
        arr.push({date,student_id,status});
      });
    });
    return arr;
  },[attendance]);

  const refreshPending = useCallback(async () => {
    const n = await getPendingCount();
    setPendingCount(n);
  }, []);

  // Online/offline detection + auto-sync when internet returns
  useEffect(() => {
    const goOnline = async () => {
      setIsOnline(true);
      const pending = await getPendingCount();
      if (pending > 0) {
        setSyncing(true);
        showToast("🔄 Internet back! Syncing " + pending + " offline record(s)…", "saving");
        const count = await syncPending();
        setSyncing(false);
        setPendingCount(0);
        if (count > 0) showToast("" + count + " record(s) synced to database!", "success");
      }
    };
    const goOffline = () => {
      setIsOnline(false);
      showToast("📵 You're offline — data saves locally until internet returns", "warn");
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // Auto-sync on page load if online and records pending
    const syncOnLoad = async () => {
      await refreshPending();
      if (!navigator.onLine) return;
      const pending = await getPendingCount();
      if (pending > 0) {
        setSyncing(true);
        const count = await syncPending();
        setSyncing(false);
        await refreshPending();
        if (count > 0) showToast("✅ " + count + " pending record(s) synced!", "success");
      }
    };
    syncOnLoad();

    // Periodic auto-sync every 5 minutes
    const interval = setInterval(async () => {
      if (!navigator.onLine) return;
      const pending = await getPendingCount();
      if (pending > 0) {
        setSyncing(true);
        const count = await syncPending();
        setSyncing(false);
        await refreshPending();
        if (count > 0) showToast("✅ " + count + " record(s) auto-synced!", "success");
      }
    }, 5 * 60 * 1000);

    // Sync on tab becoming visible again
    const onVisible = async () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      const pending = await getPendingCount();
      if (pending > 0) {
        setSyncing(true);
        const count = await syncPending();
        setSyncing(false);
        await refreshPending();
        if (count > 0) showToast("✅ " + count + " record(s) synced!", "success");
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [refreshPending]);

  useEffect(()=>{
    const mapStudents = (sRes) => {
      if (sRes.status==="ok" && sRes.students?.length>0) {
        const mapped = sRes.students.map(s=>({
          id:s.id||"", name:s.full_name||s.name||"", father:s.father_name||s.father||"",
          stream:s.stream||"School Only", "class":s.class||"",
          dob:s.dob||"", contact:s.contact||"", address:s.address||"",
          cnic:s.cnic||"", occupation:s.occupation||"",
          enrolled:s.enrolled_date||s.enrolled||"", fees:s.fees_status||s.fees||"Unpaid",
          gender:s.gender||"",
          scholarship_status:s.scholarship_status||"None",
          monthly_fee:s.monthly_fee||0,
          is_boarding:s.is_boarding||false,
        })).filter(s=>s.name);
        if (mapped.length>0) setStudents(mapped);
      }
    };
    const mapAttendance = (aRes) => {
      if (aRes.status==="ok" && aRes.records?.length>0) {
        const attMap={};
        aRes.records.forEach(r=>{
          const date=r.date, id=r.studentId, status=r.status;
          if (date&&id&&status){if(!attMap[date])attMap[date]={};attMap[date][id]=status;}
        });
        if (Object.keys(attMap).length>0) setAttendance(attMap);
      }
    };
    const load = async () => {
      const [settRes, sRes, aRes, fRes, rRes, cRes] = await Promise.allSettled([
        supabase.from("school_settings").select("*"),
        sheetAPI({action:"getStudents"}),
        sheetAPI({action:"getAttendance"}),
        sheetAPI({action:"getFees"}),
        supabase.from("results").select("*").order("created_at",{ascending:false}),
        supabase.from("student_conduct").select("*").order("date",{ascending:false}),
      ]);
      if(settRes.status==="fulfilled" && settRes.value?.data?.length){
        const map={};
        settRes.value.data.forEach(r=>{ map[r.key]=r.value; });
        setSettings(prev=>({...prev,...map}));
      }
      if(sRes.status==="fulfilled") mapStudents(sRes.value);
      else console.error("❌ Students load error:",sRes.reason);
      if(aRes.status==="fulfilled") mapAttendance(aRes.value);
      else console.error("Attendance load error:",aRes.reason);
      if(fRes.status==="fulfilled" && fRes.value?.status==="ok") setFees(fRes.value.fees||[]);
      else if(fRes.status==="rejected") console.error("Fees load error:",fRes.reason);
      if(rRes.status==="fulfilled" && rRes.value?.data?.length) setResults(rRes.value.data);
      else if(rRes.status==="rejected") console.error("Results load error:",rRes.reason);
      if(cRes.status==="fulfilled" && cRes.value?.data?.length) setConductData(cRes.value.data);
      else if(cRes.status==="rejected") console.error("Conduct load error:",cRes.reason);
      setDbStatus("live");
    };
    load();
  },[]);

  const addStudent = async (student) => {
    setStudents(p=>[...p,student]);
    showToast("Saving student…","saving");
    const res = await sheetAPI({action:"addStudent",...student});
    const ok = res.status==="ok"||res.success===true;
    if (ok && res.offline) showToast("Saved locally — will sync when online","warn");
    else if (ok) showToast("Student saved to Database!","success");
    else showToast(`⚠️ Save failed: ${res.message||"Check Supabase"}`,"warn");
    refreshPending();
  };

  const saveAttendance = async (date,dayRecords,studentList) => {
    showToast("Saving attendance…","saving");
    const records = studentList.map(s=>({date,studentId:s.id,studentName:s.name,"class":s.class,status:dayRecords[s.id]||"P"}));
    const res = await sheetAPI({action:"saveAttendance",records:JSON.stringify(records)});
    const ok = res.status==="ok"||res.success===true;
    if (ok && res.offline) showToast("Attendance saved locally — will sync when online","warn");
    else if (ok) showToast("Attendance saved!","success");
    else showToast(`⚠️ Save failed: ${res.message||"Check Supabase"}`,"warn");
    refreshPending();
  };

  const statusColor = {connecting:C.accent,live:C.primary,error:C.rose};
  const statusDot = {connecting:"⏳",live:"🟢",error:"🔴"};

  const handlePromotionUpdate = (updated) => {
    setStudents(prev => prev.map(s => {
      const u = updated.find(x => x.id === s.id);
      return u ? {...s, "class": u.newClass} : s;
    }));
  };

  return (
    <SettingsContext.Provider value={settings}>
    <div style={{background:C.bg,minHeight:"100vh",fontFamily:"'Plus Jakarta Sans',sans-serif"}}>
      {toast && <Toast msg={toast.msg} type={toast.type}/>}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap');

        /* ── SIDEBAR LAYOUT ── */
        .qka-app-shell {
          display: flex;
          min-height: 100vh;
        }

        /* SIDEBAR */
        .qka-sidebar {
          width: 240px;
          min-width: 240px;
          background: linear-gradient(180deg, #0d3d1f 0%, #1a6b3c 60%, #0a4a24 100%);
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
          height: 100vh;
          overflow-y: auto;
          z-index: 200;
          transition: width 0.25s cubic-bezier(0.16,1,0.3,1), transform 0.25s cubic-bezier(0.16,1,0.3,1);
          scrollbar-width: thin;
          scrollbar-color: rgba(255,255,255,0.15) transparent;
          flex-shrink: 0;
        }
        .qka-sidebar.collapsed {
          width: 60px;
          min-width: 60px;
        }
        .qka-sidebar-logo {
          padding: 18px 16px 14px;
          border-bottom: 1px solid rgba(255,255,255,0.12);
          display: flex;
          align-items: center;
          gap: 10px;
          flex-shrink: 0;
        }
        .qka-sidebar.collapsed .qka-sidebar-logo-text { display: none; }
        .qka-sidebar.collapsed .qka-sidebar-logo { justify-content: center; padding: 14px 0; }

        .qka-sidebar-nav { padding: 10px 8px; flex: 1; }
        .qka-sidebar-section-title {
          font-size: 9px;
          font-weight: 800;
          color: rgba(255,255,255,0.4);
          letter-spacing: 1.2px;
          text-transform: uppercase;
          padding: 12px 10px 4px;
          white-space: nowrap;
          overflow: hidden;
        }
        .qka-sidebar.collapsed .qka-sidebar-section-title { opacity: 0; height: 0; padding: 0; }
        .qka-sidebar-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 9px 10px;
          border: none;
          border-radius: 10px;
          background: transparent;
          color: rgba(255,255,255,0.7);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s;
          white-space: nowrap;
          overflow: hidden;
          margin-bottom: 2px;
        }
        .qka-sidebar-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .qka-sidebar-btn.active {
          background: rgba(255,255,255,0.15);
          color: #fff;
          font-weight: 700;
          box-shadow: inset 3px 0 0 #f0a500;
        }
        .qka-sidebar-btn .tab-icon { font-size: 16px; flex-shrink: 0; }
        .qka-sidebar-btn .tab-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
        .qka-sidebar.collapsed .tab-label { display: none; }
        .qka-sidebar.collapsed .qka-sidebar-btn { justify-content: center; padding: 10px 0; }
        .qka-sidebar.collapsed .qka-sidebar-btn.active { box-shadow: inset 0 -3px 0 #f0a500; }

        /* Sidebar collapse toggle */
        .qka-collapse-btn {
          margin: 8px;
          padding: 8px;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 8px;
          background: rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all 0.15s;
          flex-shrink: 0;
        }
        .qka-collapse-btn:hover { background: rgba(255,255,255,0.12); color: #fff; }

        /* MAIN CONTENT AREA */
        .qka-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        /* TOP HERO HEADER */
        .qka-hero-header {
          background: linear-gradient(135deg, #1a6b3c 0%, #0d4a28 50%, #0a3d20 100%);
          border-bottom: 4px solid #f0a500;
          position: sticky;
          top: 0;
          z-index: 100;
          flex-shrink: 0;
        }
        .qka-hero-top {
          display: flex;
          align-items: center;
          padding: 16px 24px;
          gap: 20px;
        }
        .qka-hero-logo {
          width: 72px;
          height: 72px;
          object-fit: contain;
          filter: drop-shadow(0 3px 10px rgba(0,0,0,0.4));
          flex-shrink: 0;
        }
        .qka-hero-en {
          flex: 1;
          color: white;
        }
        .qka-hero-en-title {
          font-size: 20px;
          font-weight: 900;
          letter-spacing: 0.5px;
          line-height: 1.1;
          text-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .qka-hero-en-sub {
          font-size: 11px;
          font-weight: 600;
          opacity: 0.85;
          margin-top: 3px;
        }
        .qka-hero-en-affil {
          font-size: 10px;
          opacity: 0.7;
          margin-top: 2px;
        }
        .qka-hero-divider {
          width: 1px;
          height: 64px;
          background: rgba(255,255,255,0.2);
          flex-shrink: 0;
        }
        .qka-hero-ur {
          text-align: right;
          direction: rtl;
          color: white;
          font-family: 'Noto Nastaliq Urdu', serif;
          flex-shrink: 0;
        }
        .qka-hero-ur-title {
          font-size: 26px;
          font-weight: 700;
          line-height: 1.4;
          text-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .qka-hero-ur-sub {
          font-size: 14px;
          color: #ffd166;
          font-weight: 600;
          margin-top: 2px;
        }
        .qka-hero-ur-addr {
          font-size: 11px;
          opacity: 0.75;
          margin-top: 2px;
        }
        /* Accent strip below hero */
        .qka-hero-accent {
          height: 3px;
          background: linear-gradient(90deg, #2563eb, #0d9488, #f0a500, #0d9488, #2563eb);
          opacity: 0.7;
        }
        /* Subtitle bar */
        .qka-hero-subtitle {
          background: rgba(0,0,0,0.18);
          padding: 7px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .qka-hero-subtitle-left {
          font-size: 12px;
          font-weight: 700;
          color: #ffd166;
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .qka-hero-subtitle-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* CONTENT PAD */
        .qka-content-area {
          padding: 24px 28px;
          flex: 1;
        }

        /* MOBILE */
        @media(max-width: 768px) {
          .qka-sidebar {
            position: fixed;
            top: 0; left: 0; bottom: 0;
            transform: translateX(-100%);
            z-index: 300;
          }
          .qka-sidebar.mob-open {
            transform: translateX(0);
          }
          .qka-sidebar-overlay {
            display: none;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.4);
            z-index: 299;
          }
          .qka-sidebar-overlay.visible { display: block; }
          .qka-hero-logo { width: 52px; height: 52px; }
          .qka-hero-en-title { font-size: 15px; }
          .qka-hero-ur-title { font-size: 20px; }
          .qka-hero-divider { display: none; }
          .qka-hero-top { padding: 12px 16px; gap: 12px; }
          .qka-content-area { padding: 16px 14px; }
          .qka-hero-subtitle { padding: 6px 16px; }
          .mob-hide { display: none !important; }
        }
        @media(min-width: 769px) {
          .qka-mob-menu-btn { display: none !important; }
          .qka-sidebar-overlay { display: none !important; }
        }

        /* fade-up animation */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tab-content { animation: fadeUp 0.2s ease; }
      `}</style>

      {/* ── APP SHELL ── */}
      <div className="qka-app-shell">

        {/* Mobile sidebar overlay */}
        <div
          className={`qka-sidebar-overlay${mobPortalNav ? " visible" : ""}`}
          onClick={() => setMobPortalNav(false)}
        />

        {/* ── LEFT SIDEBAR ── */}
        <aside className={`qka-sidebar${sidebarCollapsed ? " collapsed" : ""}${mobPortalNav ? " mob-open" : ""}`}>

          {/* Sidebar Logo (mini) */}
          <div className="qka-sidebar-logo">
            <img src={SCHOOL_LOGO} alt="QKA" style={{width:36,height:36,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 2px 6px rgba(0,0,0,0.3))"}}/>
            <div className="qka-sidebar-logo-text">
              <div style={{fontSize:13,fontWeight:900,color:"white",lineHeight:1}}>QKA Portal</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.55)",marginTop:3,lineHeight:1}}>School Management</div>
            </div>
          </div>

          {/* Nav Groups */}
          <nav className="qka-sidebar-nav">

            {/* Overview */}
            <div className="qka-sidebar-section-title">Overview</div>
            {[
              {id:"dashboard", label:"Dashboard", icon:"🏠"},
            ].map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Quality & Performance */}
            {[{id:"teacherscorecard",label:"Teacher Scorecard",icon:"🏆"},{id:"weeklydigest",label:"Weekly Digest",icon:"📋"},{id:"observations",label:"Classroom Observations",icon:"👁"}].some(t=>canSeeTab(t.id))&&(
              <div className="qka-sidebar-section-title">Quality & Performance</div>
            )}
            {[
              {id:"teacherscorecard", label:"Teacher Scorecard",       icon:"🏆"},
              {id:"weeklydigest",     label:"Weekly Digest",           icon:"📋"},
              {id:"observations",     label:"Classroom Observations",  icon:"👁"},
            ].filter(t=>canSeeTab(t.id)).map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Admissions & Students */}
            <div className="qka-sidebar-section-title">Admissions & Students</div>
            {[
              {id:"admissions", label:"Online Applications", icon:"📩"},
              {id:"register",   label:"Register Student",    icon:"📋"},
              {id:"students",   label:"Student Records",     icon:"👥"},
              {id:"leave",      label:"Leave Requests",      icon:"📝"},
              {id:"attendance", label:"Attendance",          icon:"📅"},
              {id:"conduct",    label:"Conduct Log",         icon:"📌"},
            ].map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Academics */}
            <div className="qka-sidebar-section-title">Academics</div>
            {[
              {id:"timetable", label:"Timetable",         icon:"🗓️"},
              {id:"homework",  label:"Homework",          icon:"✏️"},
              {id:"hifz",      label:"Hifz Programme",   icon:"📖"},
              {id:"courseplans", label:"Course Plans",    icon:"📘"},
              {id:"library",   label:"Library",           icon:"📚"},
              {id:"results",   label:"Results & Reports", icon:"📊"},
            ].map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Finance */}
            <div className="qka-sidebar-section-title">Finance</div>
            {[
              {id:"fees",        label:"Fee Management",  icon:"💰"},
              {id:"scholarship", label:"Scholarships",    icon:"🎓"},
              {id:"export",      label:"Export Data",     icon:"📥"},
            ].filter(t=>canSeeTab(t.id)).map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Insights & Reports */}
            <div className="qka-sidebar-section-title">Insights & Reports</div>
            {[
              {id:"insights", label:"Reports & Analytics", icon:"📊"},
            ].map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Communications */}
            <div className="qka-sidebar-section-title">Communications</div>
            {[
              {id:"communications", label:"Communications", icon:"📢"},
            ].map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Operations */}
            {[{id:"staffattendance",label:"Staff Attendance",icon:"🧑‍💼"},{id:"assets",label:"Asset Register",icon:"🗄️"}].some(t=>canSeeTab(t.id))&&(
              <div className="qka-sidebar-section-title">Operations</div>
            )}
            {[
              {id:"staffattendance", label:"Staff Attendance", icon:"🧑‍💼"},
              {id:"assets",          label:"Asset Register",   icon:"🗄️"},
            ].filter(t=>canSeeTab(t.id)).map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

            {/* Administration */}
            {[{id:"promotion",label:"Promotion Wizard",icon:"🎓"},{id:"staff",label:"Staff Management",icon:"👨‍🏫"},{id:"parents",label:"Portal Access",icon:"🔐"},{id:"settings",label:"Settings",icon:"⚙️"},{id:"facility",label:"Facility Inspection",icon:"🏗️"}].some(t=>canSeeTab(t.id))&&(
              <div className="qka-sidebar-section-title">Administration</div>
            )}
            {[
              {id:"promotion",label:"Promotion Wizard",icon:"🎓"},
              {id:"staff",    label:"Staff Management", icon:"👨‍🏫"},
              {id:"responsibilities", label:"Staff Responsibilities", icon:"🗂️"},
              {id:"parents",  label:"Portal Access",    icon:"🔐"},
              {id:"facility", label:"Facility Inspection", icon:"🏗️"},
              {id:"settings", label:"Settings",         icon:"⚙️"},
            ].filter(t=>canSeeTab(t.id)).map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setMobPortalNav(false);}}
                className={`qka-sidebar-btn${tab===t.id?" active":""}`}>
                <span className="tab-icon">{t.icon}</span>
                <span className="tab-label">{t.label}</span>
              </button>
            ))}

          </nav>

          {/* Collapse toggle (desktop only) */}
          <button className="qka-collapse-btn mob-hide"
            onClick={()=>setSidebarCollapsed(v=>!v)}
            title={sidebarCollapsed?"Expand sidebar":"Collapse sidebar"}>
            {sidebarCollapsed ? "▶" : "◀"}
          </button>
        </aside>

        {/* ── MAIN AREA ── */}
        <main className="qka-main">

          {/* ── HERO HEADER ── */}
          <header className="qka-hero-header">
            <div className="qka-hero-top">

              {/* Mobile hamburger */}
              <button className="qka-mob-menu-btn"
                onClick={()=>setMobPortalNav(v=>!v)}
                style={{background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,padding:"7px 9px",cursor:"pointer",display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                <span style={{display:"block",width:18,height:2,background:"white",borderRadius:1,transition:"all .2s",transform:mobPortalNav?"rotate(45deg) translate(4px,4px)":"none"}}/>
                <span style={{display:"block",width:18,height:2,background:"white",borderRadius:1,transition:"all .2s",opacity:mobPortalNav?0:1}}/>
                <span style={{display:"block",width:mobPortalNav?18:12,height:2,background:"white",borderRadius:1,transition:"all .2s",transform:mobPortalNav?"rotate(-45deg) translate(4px,-4px)":"none"}}/>
              </button>

              {/* Logo */}
              <img src={SCHOOL_LOGO} alt="Quran Karim Academy" className="qka-hero-logo"/>

              {/* English info */}
              <div className="qka-hero-en">
                <div className="qka-hero-en-title">QURAN-E-KARIM ACADEMY</div>
                <div className="qka-hero-en-sub">Modern Islamic School System</div>
                <div className="qka-hero-en-affil mob-hide">Registered with Wifaq-ul Madaris Al-Arabia Pakistan</div>
                <div className="qka-hero-en-affil mob-hide">Gorsian, Jatli, Dist. Rawalpindi &nbsp;|&nbsp; Reg. No: CMR-1992015</div>
              </div>

              <div className="qka-hero-divider"/>

              {/* Urdu info */}
              <div className="qka-hero-ur">
                <div className="qka-hero-ur-title">قرآن کریم اکیڈمی</div>
                <div className="qka-hero-ur-sub">ماڈرن اسلامک سکول سسٹم</div>
                <div className="qka-hero-ur-addr mob-hide">رجسٹرڈ وفاق المدارس العربیہ پاکستان</div>
                <div className="qka-hero-ur-addr mob-hide">گورسیاں - جاتلی - ضلع راولپنڈی</div>
              </div>

              {/* Right actions */}
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0,marginLeft:8}}>
                <NotificationBell userEmail={session?.user?.email||"admin"} userRole="admin" targetClass={null}/>
                {!isOnline && <div style={{background:"#fef3c7",border:"1px solid #f0a500",borderRadius:20,padding:"5px 10px",fontSize:11,fontWeight:700,color:"#92400e"}}>📵</div>}
                {isOnline && pendingCount > 0 && (
                  <button
                    onClick={async()=>{
                      if(syncing) return;
                      setSyncing(true);
                      showToast("🔄 Syncing "+pendingCount+" record(s)…","saving");
                      const count = await syncPending();
                      setSyncing(false);
                      await refreshPending();
                      if(count>0) showToast("✅ "+count+" record(s) synced!","success");
                      else showToast("⚠️ Nothing synced — check console","warn");
                    }}
                    title="Click to sync pending records now"
                    style={{background:"#fef3c7",border:"1px solid #f0a500",borderRadius:20,padding:"5px 10px",fontSize:11,fontWeight:700,color:"#92400e",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                    {syncing?"🔄":"⏳"}{pendingCount}
                  </button>
                )}
                <div style={{background:dbStatus==="live"?C.primaryPale:C.accentPale,borderRadius:20,padding:"5px 10px",fontSize:11,fontWeight:700,color:dbStatus==="live"?C.primary:C.accent}}>
                  {isOnline?(dbStatus==="live"?"🟢":"⏳"):"💾"}
                </div>
                {onSignOut && (
                  <span style={{background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,color:"white"}}>
                    {myScope?.icon} {myScope?.label}
                  </span>
                )}
                {onSignOut && (
                  <button onClick={onSignOut} title="Sign Out"
                    style={{background:"rgba(225,29,72,0.12)",color:"#fda4af",border:"1px solid rgba(253,164,175,0.3)",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",gap:4,transition:"all .2s"}}>
                    🚪 <span className="mob-hide">Sign Out</span>
                  </button>
                )}
              </div>
            </div>

            <div className="qka-hero-accent"/>

            {/* Subtitle bar — shows active tab name */}
            <div className="qka-hero-subtitle">
              <div className="qka-hero-subtitle-left">
                <span style={{opacity:0.6}}>📍</span>
                <span>{TABS.find(t=>t.id===tab)?.icon} {TABS.find(t=>t.id===tab)?.label || "Dashboard"}</span>
              </div>
              <div className="qka-hero-subtitle-right">
                <span style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>
                  {new Date().toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}
                </span>
              </div>
            </div>
          </header>

          {/* Offline banner */}
          {!isOnline && (
            <div style={{background:"#fef3c7",borderBottom:"2px solid #f0a500",padding:"10px 16px",display:"flex",alignItems:"center",gap:10,fontSize:13,color:"#78350f",fontWeight:600,flexWrap:"wrap"}}>
              <span style={{fontSize:18,flexShrink:0}}>📵</span>
              <span style={{flex:1,minWidth:200}}>You're offline — all data is being saved to this device. It will automatically sync when internet returns.</span>
              {pendingCount > 0 && <span style={{background:"#f0a500",color:"#fff",borderRadius:12,padding:"2px 10px",fontSize:12,flexShrink:0}}>{pendingCount} record(s) waiting</span>}
            </div>
          )}

          {/* ── TAB CONTENT ── */}
          <div className="qka-content-area">
            <div key={tab} className="tab-content">
            {tab==="leave" && <LeaveRequestAdmin/>}
            {tab==="teacherscorecard" && (canSeeTab("teacherscorecard") ? <TeacherScorecardAdmin/> : <div style={{padding:48,textAlign:"center",color:"#9ca3af"}}><div style={{fontSize:48}}>🔒</div><div style={{fontWeight:700,marginTop:12}}>Access Restricted</div><div style={{fontSize:13,marginTop:6}}>Your role ({myScope.label}) does not have access to this section.</div></div>)}
            {tab==="weeklydigest"     && <PrincipalDigest adminRole={adminRole}/>}
            {tab==="observations"     && <ClassroomObservationLog adminRole={adminRole}/>}
            {tab==="staffattendance"  && (canSeeTab("staffattendance") ? <StaffAttendanceAdmin/> : <AccessRestricted/>)}
            {tab==="assets"           && (canSeeTab("assets") ? <AssetRegisterAdmin/> : <AccessRestricted/>)}
            {tab==="dashboard" && <DashboardErrorBoundary><Dashboard students={students} attendance={attendance} fees={fees} setTab={setTab}/></DashboardErrorBoundary>}
            {tab==="register" && <RegisterStudent onAdd={addStudent}/>}
            {tab==="scholarship" && <ScholarshipManagement sheetAPI={sheetAPI}/>}
            {tab==="attendance" && <Attendance students={students} attendance={attendance} setAttendance={setAttendance} onSave={saveAttendance}/>}
            {tab==="students" && <StudentRecords
              students={students}
              onUpdate={s=>setStudents(p=>p.map(x=>x.id===s.id?{...x,...s,name:s.full_name||s.name,father:s.father_name||s.father,enrolled:s.enrolled_date||s.enrolled,fees:s.fees_status||s.fees}:x))}
              onDelete={id=>setStudents(p=>p.filter(x=>x.id!==id))}
              onAdd={s=>setStudents(p=>[...p,s])}
            />}
            {tab==="fees" && <FeeManagement students={students} fees={fees} onFeesSaved={()=>{
              sheetAPI({action:"getFees"}).then(r=>{ if(r.status==="ok") setFees(r.fees||[]); });
            }} />}
            {tab==="fees" && !canSeeTab("fees") && <div style={{padding:48,textAlign:"center",color:"#9ca3af"}}><div style={{fontSize:48}}>🔒</div><div style={{fontWeight:700,marginTop:12}}>Access Restricted</div><div style={{fontSize:13,marginTop:6}}>Your role ({myScope.label}) does not have access to this section.</div></div>}
            {tab==="results" && !canSeeTab("results") && <div style={{padding:48,textAlign:"center",color:"#9ca3af"}}><div style={{fontSize:48}}>🔒</div><div style={{fontWeight:700,marginTop:12}}>Access Restricted</div></div>}
            {tab==="results" && <ResultsManagement students={students} attendance={attendance} resultsData={results} onResultsChange={r=>setResults(r)}/>}
            {tab==="promotion" && <PromotionWizard students={students} results={results} onStudentsUpdated={handlePromotionUpdate} showToast={showToast}/>}
            {tab==="hifz" && <HifzManagement students={students}/>}
            {tab==="courseplans" && <CoursePlanAdmin/>}
            {tab==="library" && <LibraryAdmin/>}
            {tab==="settings" && <SettingsManagement settings={settings} setSettings={setSettings} academicYears={academicYears} setAcademicYears={setAcademicYears} activeAcademicYear={activeAcademicYear} reloadAcademicYears={loadAcademicYears} students={students} fees={fees} attendance={attArray} results={results||[]}/>}
            {tab==="export" && <ExportDataManagement students={students} attendance={attArray} fees={fees} results={results||[]}/>}
            {tab==="staff" && <StaffManagement/>}
            {tab==="responsibilities" && <StaffResponsibilityAdmin/>}
            {tab==="timetable" && <TimetableManagement setSettings={setSettings} students={students} settings={settings}/>}
            {tab==="calendar"  && <CalendarManagement students={students}/>}
            {tab==="homework"  && <HomeworkTracker students={students}/>}
            {tab==="conduct"   && <ConductLog students={students}/>}
            {tab==="reports"      && <ReportsTab students={students} attendance={attendance} results={results}/>}
            {tab==="achievements" && <AchievementBoard students={students} results={results||[]} attendance={attArray} conductData={conductData||[]} onShowToast={showToast}/>}
            {tab==="analytics"    && <SchoolAnalyticsTab students={students} results={results||[]} attendance={attArray} fees={fees}/>}
            {tab==="timeline"     && <SchoolAnalyticsTab students={students} results={results||[]} attendance={attArray} fees={fees} initialSubTab="timeline"/>}
            {tab==="ai"           && <SchoolAnalyticsTab students={students} results={results||[]} attendance={attArray} fees={fees} initialSubTab="timeline"/>}
            {tab==="admissions" && <AdmissionsReceived setTab={setTab}/>}
            {tab==="announcements" && <AnnouncementsManagement/>}
            {tab==="parents" && <PortalAccessManagement students={students}/>}
            {tab==="insights" && <InsightsTab students={students} results={results||[]} attendance={attArray} fees={fees} conductData={conductData||[]} onShowToast={showToast}/>}
            {tab==="facility" && <FacilityInspectionAdmin/>}
            {tab==="communications" && <CommunicationsTab students={students}/>}
            </div>
          </div>

        </main>
      </div>
    </div>
    </SettingsContext.Provider>
  );
}

export default AppWithAuth;
